import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Users, FileText, Settings, LogOut, LayoutDashboard, ChevronDown, ChevronUp,
  Save, Trash2, Download, ChevronLeft, ChevronRight, Layout, FileCheck,
  Eye, EyeOff, Pencil, Check, X, AlertCircle, AlertTriangle, Clock, Archive, CheckCircle, PenTool, History, Briefcase, TrendingUp, MapPin, Search, Upload, Bell, Mail, ToggleLeft, ToggleRight, Send, ExternalLink, Lock, HelpCircle, Copy, Euro, Percent, Wallet,
  Layers, ClipboardList, CalendarDays, BookOpen,
  UserCircle, MessageCircle, Menu, CalendarCheck, KeyRound,
  UsersRound, UserPlus, UserMinus, Video, CalendarPlus
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { Buffer } from 'buffer';
import process from 'process';
import { createClient } from '@supabase/supabase-js';
import { supabase } from './supabaseClientConfig';
import { signalerErreur } from './erreurs';
import SetupOrganisationPage from './pages/SetupOrganisation';
import { CONDITIONS_VERSION, PAGES_LEGALES, LIENS_LEGAUX, documentsAAccepter } from './conditions';
import { DndContext as BaseDndContext, useDraggable, useDroppable, useSensors, useSensor, MouseSensor, TouchSensor, KeyboardSensor } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
// 2026-10-21 : les outils lourds (PDF, Word, graphiques, métiers ROME) sont chargés seulement au moment
// où on s'en sert (« await import(...) » dans les fonctions concernées) → ouverture de SkorUp plus rapide.
// Couleurs et polices pdf-lib : mêmes valeurs que pdf-lib (rgb() renvoie { type: 'RGB', red, green, blue }).
const rgb = (red, green, blue) => ({ type: 'RGB', red, green, blue });
const StandardFonts = { Helvetica: 'Helvetica', HelveticaBold: 'Helvetica-Bold' };
// Préchargement discret 4 s après l'ouverture : les outils PDF/Word sont prêts avant le premier clic.
if (typeof window !== 'undefined') {
  const prechargerOutils = () => { import('pdf-lib').catch(() => {}); import('jspdf').catch(() => {}); import('html2canvas').catch(() => {}); import('pizzip').catch(() => {}); import('docxtemplater').catch(() => {}); };
  const planifier = () => setTimeout(() => (window.requestIdleCallback ? window.requestIdleCallback(prechargerOutils, { timeout: 5000 }) : prechargerOutils()), 4000);
  if (document.readyState === 'complete') planifier(); else window.addEventListener('load', planifier, { once: true });
  // Après une mise en ligne, un onglet resté ouvert peut ne plus trouver un morceau de l'ancienne version.
  window.addEventListener('unhandledrejection', (e) => {
    const r = e && e.reason;
    if (r && (r.name === 'ChunkLoadError' || /Loading (CSS )?chunk/i.test(String(r.message || '')))) {
      toast.error('Une nouvelle version de SkorUp est en ligne : rechargez la page (F5) pour continuer.', { duration: 10000, id: 'nouvelle-version' });
    }
  });
}
// Référentiel ROME (260 Ko) : chargé à l'ouverture de « Fiches métiers » seulement.
const ROME_VIDE = { grandsDomaines: [] };
let _romePromesse = null;
const chargerRomeData = () => { if (!_romePromesse) _romePromesse = import('./data/romeData.json').then(m => m.default || m); return _romePromesse; };

// AJOUT (2026-10-07, téléphone) : glisser-déposer des séances compatible tactile. Sans réglage, toucher
// une ligne déclenchait aussitôt un glisser : sur téléphone, faire défiler la page ou toucher un champ
// pouvait déplacer un élément par erreur. Désormais : souris = glisser après 6 px de mouvement ;
// doigt = APPUI LONG (0,25 s) pour attraper l'élément, un simple balayage fait défiler normalement.
const DndContext = (props) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor)
  );
  return <BaseDndContext sensors={sensors} {...props} />;
};



// UX (2026-10-12) : les messages d'erreur techniques (en anglais, codes de base de données…) sont
// traduits en message clair pour l'utilisateur. Le détail technique reste affiché dans la console
// du navigateur pour le diagnostic. Les messages déjà rédigés en français ne sont jamais modifiés.
const TECH_ERROR_RULES = [
  [/row-level security|permission denied|not authorized|unauthorized|forbidden/i, "vous n'avez pas les droits nécessaires pour cette action."],
  [/jwt|token (has )?expired|invalid token|refresh token|session (expired|missing|not found)|not authenticated|auth session missing/i, 'votre session a expiré. Reconnectez-vous puis réessayez.'],
  [/failed to fetch|networkerror|network request failed|load failed|fetch failed|timed? ?out|econn/i, 'problème de connexion internet. Vérifiez votre connexion puis réessayez.'],
  [/duplicate key|already exists|unique constraint/i, 'cet élément existe déjà.'],
  [/payload too large|entity too large|maximum allowed size/i, 'fichier trop volumineux (50 Mo maximum).'],
  [/mime type|invalid file type|unsupported file/i, "ce type de fichier n'est pas accepté."],
  [/null value in column|violates not-null|violates check constraint|invalid input syntax|violates foreign key|out of range/i, 'certaines informations sont manquantes ou invalides.'],
  [/object not found|not found|does not exist|no rows/i, 'élément introuvable (il a peut-être été supprimé).'],
];
const hasNonAscii = (t) => /[\u0080-\uFFFF]/.test(t);
const looksTechnicalError = (t) => /[a-z]/i.test(t) && !hasNonAscii(t)
  && /\b(the|is|not|of|to|for|with|failed|error|invalid|column|function|violates|denied|unexpected|cannot|could|unable|undefined|null|status|request)\b/i.test(t);
const humanizeErrorMessage = (msg) => {
  if (typeof msg !== 'string' || !msg.trim()) return msg;
  const sep = msg.lastIndexOf(': ');
  const prefix = sep > 0 ? msg.slice(0, sep).trim() : '';
  const tail = sep > 0 ? msg.slice(sep + 2).trim() : msg.trim();
  if (!tail) return msg;
  const isAscii = !hasNonAscii(tail);
  const rule = isAscii ? TECH_ERROR_RULES.find(([re]) => re.test(tail)) : null;
  let friendly = rule ? rule[1] : null;
  if (!friendly && looksTechnicalError(tail)) {
    friendly = 'une erreur technique est survenue. Réessayez dans un instant ; si le problème continue, contactez le support.';
  }
  if (!friendly) return msg;
  console.warn('[erreur technique]', msg);
  if (prefix) return `${prefix} : ${friendly}`;
  return friendly.charAt(0).toUpperCase() + friendly.slice(1);
};
const _toastErrorOriginal = toast.error;
toast.error = (message, options) => _toastErrorOriginal(humanizeErrorMessage(message), options);

if (typeof window !== 'undefined') {
  window.Buffer = Buffer;
  window.process = process;
}

// --- Icônes Simplifiées (Lucide déjà importé) ---
const DownloadIcon = () => <FileText className="w-4 h-4 mr-2" />;

// --- Résolution d'URL de fichier Supabase ---
// Convertit un chemin relatif (ex: "modeling-imports/xxx.pdf") en URL publique complète
// Buckets autorisés : 'documents', 'ressources-pedagogiques', 'signed_documents'
const resolveFileUrl = (rawUrl) => {
  if (!rawUrl) return null;
  // Blob et data URLs sont des URLs locales → passer directement
  if (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:')) return rawUrl;
  if (rawUrl.startsWith('http')) return rawUrl;

  // Nettoyage : si le chemin commence par un nom de bucket connu + slash, on le retire
  // car supabase.storage.from(bucket) le rajoute déjà.
  let cleanPath = rawUrl;
  const knownBuckets = ['documents', 'ressources-pedagogiques', 'signed_documents', 'module_resources', 'client_files'];
  for (const b of knownBuckets) {
    if (cleanPath.startsWith(`${b}/`)) {
      cleanPath = cleanPath.substring(b.length + 1);
      break;
    }
  }

  // LOGIQUE DE BUCKET :
  const isRessource = cleanPath.startsWith('ressources/') || cleanPath.startsWith('modeling-imports/');
  const isSigned = cleanPath.startsWith('signed_');
  const bucket = isSigned ? 'signed_documents' : (isRessource ? 'ressources-pedagogiques' : 'documents');

  const { data } = supabase.storage.from(bucket).getPublicUrl(cleanPath);

  if (!data?.publicUrl) {
    console.error("Erreur de lien storage:", cleanPath, "dans le bucket:", bucket);
  }

  return data?.publicUrl || null;
};

// ─── DOCUMENTS AUTOMATIQUES (2026-10-20, lot 3) ───────────────────────────────────────────────────
// Date du jour à l'heure LOCALE (avant : toISOString = heure UTC → « hier » entre 0 h et 2 h en France).
const jourLocalISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// « 2026-11-03 » → « 03/11/2026 » sans passer par un objet Date (pas de décalage de fuseau).
const dateIsoVersFr = (iso) => { const m = String(iso || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}/${m[2]}/${m[1]}` : ''; };
// Valeurs « formation / dates / organisme » des modèles. Les dates viennent des VRAIES séances
// (numéro ≥ 1 : ni le bloc administratif, ni les exercices de début/fin).
const valeursFormationOrganisme = ({ moduleNom, seances, orgSettings }) => {
  const dates = (seances || []).filter(x => Number(x.numero_seance) >= 1 && x.date).map(x => String(x.date).slice(0, 10)).sort();
  return {
    formation_nom: moduleNom || '',
    date_debut: dates.length ? dateIsoVersFr(dates[0]) : '',
    date_fin: dates.length ? dateIsoVersFr(dates[dates.length - 1]) : '',
    org_nom: orgSettings?.nom || '',
    org_siret: orgSettings?.siret || '',
    org_nda: orgSettings?.nda || '',
    org_adresse: orgSettings?.adresse || '',
    org_code_postal: orgSettings?.code_postal || '',
    org_ville: orgSettings?.ville || '',
    org_region: orgSettings?.region || '',
    org_site_web: orgSettings?.site_web || '',
  };
};
// DÉCISION (20/10/2026) : les balises « date de signature » reçoivent la date à laquelle la personne
// SIGNE (et non plus la date de création du document) ; « date du jour » reste la date de création.
const TAGS_DATE_SIGNATURE = ['date_signature', 'date_signature_formateur', 'date_signature_organisme'];
const estDateSignature = (f) => TAGS_DATE_SIGNATURE.includes(f && f.tag);
const tagDateSignature = (role) => (role === 'client' ? 'date_signature' : `date_signature_${role}`);
const dateDuJourFr = () => new Date().toLocaleDateString('fr-FR');

// Document de FIN de parcours pas encore débloqué (le parcours n'est pas terminé).
const docDeFinVerrouille = (doc, parcoursTermine) => {
  if (parcoursTermine) return false;
  let m = doc?.metadata;
  if (typeof m === 'string') { try { m = JSON.parse(m); } catch (_) { m = {}; } }
  return !!(m && m.moment === 'fin');
};

// ─── CONSERVATION DES SIGNATURES (2026-10-20, lot 2) ──────────────────────────────────────────────
// Un document ou un élément de séance signé par QUI QUE CE SOIT (client, formateur, organisme) n'est
// jamais supprimé ni remplacé depuis l'application. Vérification faite sur la base (données à jour).
const MESSAGE_DOCUMENT_SIGNE = 'Ce document est signé : il est conservé dans le dossier et ne peut pas être supprimé.';
const documentEstSigne = (d) => !!(d && (d.signe_par_client || d.signe_par_formateur || d.signe_par_organisme
  || d.statut === 'Signé' || d.signed_pdf_url));
const elementSeanceEstSigne = (s) => {
  if (!s) return false;
  let m = s.metadata;
  if (typeof m === 'string') { try { m = JSON.parse(m); } catch (_) { m = {}; } }
  return !!(s.statut_client === 'Signé' || s.statut_formateur === 'Signé' || s.statut === 'Signé'
    || s.signed_pdf_url || s.signature_client || s.signature_formateur || (m && m.file_url_signed));
};
// Relit le document en base : true si on peut le supprimer, sinon message et false.
const peutSupprimerDocument = async (id) => {
  const { data, error } = await supabase.from('documents').select('*').eq('id', id).maybeSingle();
  if (error) { toast.error('Vérification impossible, suppression annulée : ' + error.message); return false; }
  if (documentEstSigne(data)) { toast.error(MESSAGE_DOCUMENT_SIGNE, { duration: 7000 }); return false; }
  return true;
};
const peutSupprimerElementSeance = async (id) => {
  const { data, error } = await supabase.from('sessions').select('*').eq('id', id).maybeSingle();
  if (error) { toast.error('Vérification impossible, suppression annulée : ' + error.message); return false; }
  if (elementSeanceEstSigne(data)) { toast.error('Cet élément a déjà été signé : il est conservé dans le dossier et ne peut pas être supprimé.', { duration: 7000 }); return false; }
  return true;
};

// ─── SIGNATURES (2026-10-20) : documents « à signer » placés dans une séance ─────────────────────
// Lit un fichier du stockage pour le retravailler (lien temporaire, sinon lien public).
const lireFichierStockage = async (rawUrl) => {
  let url = null;
  try {
    const ext = extractStorageBucketAndPath(rawUrl);
    if (ext) {
      const { data, error } = await supabase.storage.from(ext.bucket).createSignedUrl(ext.path, 300);
      if (!error && data?.signedUrl) url = data.signedUrl;
    }
  } catch (_) { /* repli sur le lien public */ }
  if (!url) url = resolveFileUrl(rawUrl);
  if (!url) throw new Error('fichier du document introuvable');
  const r = await fetch(url);
  if (!r.ok) throw new Error(`lecture du document impossible (HTTP ${r.status})`);
  return r.blob();
};

// Construit le PDF signé d'un document de séance pour UN signataire (role = 'client' | 'formateur').
// Part de la DERNIÈRE version (déjà signée par l'autre partie le cas échéant). La signature, les cases
// et les textes sont posés aux emplacements prévus (balises signature_/checkbox_/texte_<role>) ; sans
// emplacement de signature, la signature est ajoutée en bas de la dernière page (client à gauche,
// formateur à droite), avec le nom et la date. Lève une erreur si quoi que ce soit échoue.
const construirePdfSigneSeance = async ({ sourceUrl, meta, role, signatureDataUrl, checkedIds, textValues, nomSignataire }) => { /* 2026-10-21 : chargé à la demande */ const { PDFDocument } = await import('pdf-lib');
  let blob = await lireFichierStockage(sourceUrl);
  if (/\.(docx|doc)$/i.test(String(sourceUrl).split('?')[0])) {
    const docx = new Blob([await blob.arrayBuffer()], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    try { blob = await convertDocxBlobToPdf(docx); } catch (_) { blob = await convertDocxBlobToPdfLocal(docx); }
  } else {
    blob = new Blob([await blob.arrayBuffer()], { type: 'application/pdf' });
  }
  const m = meta || {};
  const champs = (Array.isArray(m.signature_fields) && m.signature_fields.length > 0) ? m.signature_fields
    : (Array.isArray(m.template_fields) && m.template_fields.length > 0) ? m.template_fields
    : (Array.isArray(m.fields) ? m.fields : []);
  const tags = [`signature_${role}`, `checkbox_${role}`, `texte_${role}`, tagDateSignature(role)];
  const mesChamps = champs.filter(f => tags.includes(f.tag));
  const coches = checkedIds instanceof Set ? checkedIds
    : new Set(Array.isArray(checkedIds) ? checkedIds : Object.keys(checkedIds || {}).filter(k => checkedIds[k]));
  const textes = textValues instanceof Map ? Object.fromEntries(textValues) : (textValues || {});
  let signaturePosee = false;
  if (mesChamps.length > 0) {
    const checkedMap = {};
    mesChamps.filter(f => f.tag === `checkbox_${role}`).forEach(f => { const k = fieldKey(f); if (coches.has(k)) checkedMap[k] = true; });
    const textMap = {};
    mesChamps.filter(f => f.tag === `texte_${role}`).forEach(f => { const k = fieldKey(f); if (textes[k]) textMap[k] = textes[k]; });
    blob = await overlayFieldsOnPdf(blob, mesChamps, { [tagDateSignature(role)]: dateDuJourFr() }, signatureDataUrl ? { [`signature_${role}`]: signatureDataUrl } : {}, checkedMap, textMap);
    signaturePosee = !!signatureDataUrl && mesChamps.some(f => f.tag === `signature_${role}`);
  }
  const pdfDoc = await PDFDocument.load(await blob.arrayBuffer(), { ignoreEncryption: true });
  if (signatureDataUrl && !signaturePosee) {
    const pages = pdfDoc.getPages();
    const page = pages[pages.length - 1];
    const { width } = page.getSize();
    const img = await pdfDoc.embedPng(Uint8Array.from(atob(signatureDataUrl.split(',')[1]), c => c.charCodeAt(0)));
    const k = Math.min(160 / img.width, 60 / img.height, 1);
    const x = role === 'client' ? 60 : Math.max(width / 2 + 40, 60), y = 110;
    page.drawImage(img, { x, y, width: img.width * k, height: img.height * k });
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    if (nomSignataire) page.drawText(String(nomSignataire).slice(0, 60), { x, y: y - 14, size: 8, font, color: rgb(0.2, 0.2, 0.2) });
    page.drawText(`Signé le ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, { x, y: y - 26, size: 7, font, color: rgb(0.4, 0.4, 0.4) });
  }
  return new Blob([await pdfDoc.save()], { type: 'application/pdf' });
};

// --- Ouverture sécurisée d'un fichier de stockage ---
// Au lieu d'ouvrir directement l'URL publique (permanente, devinable, partageable sans limite),
// on génère un lien signé à courte durée de vie (5 min) avant d'ouvrir l'onglet.
// Repli sur l'URL publique en cas d'échec (bucket/format non reconnu) pour ne jamais casser
// une fonctionnalité existante. Cf. audit sécurité — exposition des URLs de stockage.
const extractStorageBucketAndPath = (rawUrl) => {
  if (!rawUrl) return null;
  if (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:')) return null;

  // URL Supabase complète (publique ou déjà signée)
  const match = rawUrl.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+?)(?:\?|$)/);
  if (match) {
    const bucket = match[1];
    let path = decodeURIComponent(match[2]);
    if (path.startsWith(`${bucket}/`)) path = path.substring(bucket.length + 1);
    return { bucket, path };
  }
  if (rawUrl.startsWith('http')) return null; // URL externe non-Supabase

  // Chemin relatif
  let cleanPath = rawUrl;
  const knownBuckets = ['documents', 'ressources-pedagogiques', 'signed_documents', 'module_resources', 'client_files'];
  for (const b of knownBuckets) {
    if (cleanPath.startsWith(`${b}/`)) { cleanPath = cleanPath.substring(b.length + 1); break; }
  }
  const isRessource = cleanPath.startsWith('ressources/') || cleanPath.startsWith('modeling-imports/');
  const isSigned = cleanPath.startsWith('signed_');
  const bucket = isSigned ? 'signed_documents' : (isRessource ? 'ressources-pedagogiques' : 'documents');
  return { bucket, path: cleanPath };
};

const openSecureStorageFile = async (rawUrl) => {
  if (!rawUrl) return;
  if (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:')) { window.open(rawUrl, '_blank'); return; }
  try {
    const extracted = extractStorageBucketAndPath(rawUrl);
    if (extracted) {
      const { data, error } = await supabase.storage.from(extracted.bucket).createSignedUrl(extracted.path, 300);
      if (!error && data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
        return;
      }
    }
  } catch (e) {
    console.warn('[openSecureStorageFile] Repli sur URL publique :', e.message);
  }
  // Repli : comportement historique (URL publique) si la génération du lien signé échoue
  window.open(resolveFileUrl(rawUrl) || rawUrl, '_blank');
};

// AJOUT (2026-10-16) : nom des fichiers téléchargés = « Nom du document - Nom du client ».
// Caractères interdits par Windows retirés ; le nom de la personne n'est pas répété s'il figure
// déjà dans le nom du document.
const nettoyerNomFichier = (s) => String(s || '')
  .split('').filter(ch => ch.charCodeAt(0) >= 32).join('')
  .replace(/[\\/:*?"<>|]+/g, ' ')
  .replace(/\s+/g, ' ')
  .replace(/^[\s.]+|[\s.]+$/g, '');
const nomTelechargement = (nomDoc, nomPersonne) => {
  const base = nettoyerNomFichier(nomDoc) || 'Document';
  const personne = nettoyerNomFichier(nomPersonne);
  const simple = (x) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (!personne || simple(base).includes(simple(personne))) return base.slice(0, 150);
  return `${base.slice(0, 110).trim()} - ${personne.slice(0, 60)}`;
};

// ── FICHIERS PRIVÉS, étape 1 (audit du 04/11/2026) ───────────────────────────────────────────────
// Les espaces documents, exercice-returns, exercices-reponses et signed_documents vont passer en
// privé : un fichier ne s'ouvrira plus par son adresse publique, seulement avec les droits de la
// personne connectée (règles de stockage skorup_fichiers_*). Les adresses publiques déjà enregistrées
// partout dans la base restent la référence du fichier ; c'est au moment de l'ouvrir qu'on les convertit :
//   - fetch(adresse publique) → téléchargement avec la session (PDF, Word, images, signatures…) ;
//   - window.open(adresse publique) ou clic sur un lien → lien temporaire (1 h) fabriqué au clic.
// Tant que les espaces sont encore publics (étape 1), un refus n'empêche rien : on se rabat sur
// l'adresse publique et on note l'incident dans le journal d'erreurs (empreinte du chemin, jamais le
// nom du fichier), pour corriger les droits AVANT de passer en privé (étape 3).
const BUCKETS_PRIVES = ['documents', 'exercice-returns', 'exercices-reponses', 'signed_documents'];
const RE_ADRESSE_PUBLIQUE = /\/storage\/v1\/object\/public\/([^/?#]+)\/([^?#]+)/;
const fichierPrive = (adresse) => {
  if (typeof adresse !== 'string' || !adresse.includes('/storage/v1/object/public/')) return null;
  const base = String(process.env.REACT_APP_SUPABASE_URL || '').replace(/\/+$/, '');
  if (base && !adresse.startsWith(base + '/')) return null;
  const m = RE_ADRESSE_PUBLIQUE.exec(adresse);
  if (!m || !BUCKETS_PRIVES.includes(m[1])) return null;
  let chemin = m[2];
  try { chemin = decodeURIComponent(m[2]); } catch (_) { /* chemin laissé tel quel */ }
  return { bucket: m[1], path: chemin };
};
// SHA-256 (16 premiers caractères) du chemin : même calcul que le SQL « fichiers_prives_CONSULTER ».
const sha256Hex = (texte) => {
  const K = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2];
  const o = new TextEncoder().encode(texte);
  const l = o.length, n = ((l + 9 + 63) >> 6) << 6;
  const m = new Uint8Array(n); m.set(o); m[l] = 0x80;
  const dv = new DataView(m.buffer); dv.setUint32(n - 4, l * 8); dv.setUint32(n - 8, Math.floor(l / 0x20000000));
  let H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const r = (x, k) => (x >>> k) | (x << (32 - k)); const W = new Array(64);
  for (let i = 0; i < n; i += 64) {
    for (let t = 0; t < 16; t++) W[t] = dv.getUint32(i + t * 4);
    for (let t = 16; t < 64; t++) { const s0 = r(W[t - 15], 7) ^ r(W[t - 15], 18) ^ (W[t - 15] >>> 3); const s1 = r(W[t - 2], 17) ^ r(W[t - 2], 19) ^ (W[t - 2] >>> 10); W[t] = (W[t - 16] + s0 + W[t - 7] + s1) | 0; }
    let [a, b, c, d, e, f, g, h] = H;
    for (let t = 0; t < 64; t++) {
      const t1 = (h + (r(e, 6) ^ r(e, 11) ^ r(e, 25)) + ((e & f) ^ (~e & g)) + K[t] + W[t]) | 0;
      const t2 = ((r(a, 2) ^ r(a, 13) ^ r(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    H = [H[0] + a, H[1] + b, H[2] + c, H[3] + d, H[4] + e, H[5] + f, H[6] + g, H[7] + h].map(x => x | 0);
  }
  return H.map(x => (x >>> 0).toString(16).padStart(8, '0')).join('');
};
const empreinteFichier = async (f) => { try { return sha256Hex(`${f.bucket}/${f.path}`).slice(0, 16); } catch (_) { return 'inconnue'; } };
const noterRefusFichier = async (f, motif) => {
  try { signalerErreur(new Error(`Fichier privé refusé (${f.bucket}) ${await empreinteFichier(f)}`), { origine: `fichiers privés : ${String(motif || '').slice(0, 120)}` }); } catch (_) { /* jamais bloquant */ }
};
// Lien temporaire (1 h) pour une adresse publique d'un espace privé ; null si refusé.
const lienTemporaireFichier = async (f) => {
  try {
    const { data, error } = await supabase.storage.from(f.bucket).createSignedUrl(f.path, 3600);
    if (!error && data && data.signedUrl) return data.signedUrl;
    noterRefusFichier(f, error && error.message);
  } catch (e) { noterRefusFichier(f, e && e.message); }
  return null;
};
// Pour un <iframe>, une visionneuse ou un lien construit à la main : renvoie un lien temporaire si
// l'adresse est celle d'un espace privé, sinon l'adresse telle quelle.
const adresseOuvrable = async (adresse) => {
  const f = fichierPrive(adresse);
  if (!f) return adresse;
  return (await lienTemporaireFichier(f)) || adresse;
};
if (typeof window !== 'undefined' && !window.__skorupFichiersPrives) {
  window.__skorupFichiersPrives = true;
  const fetchOrigine = window.fetch.bind(window);
  window.fetch = async (entree, init) => {
    const adresse = typeof entree === 'string' ? entree : (entree && (entree.href || entree.url));
    const f = fichierPrive(adresse);
    const methode = String((init && init.method) || (entree && typeof entree === 'object' && entree.method) || 'GET').toUpperCase();
    if (!f || (methode !== 'GET' && methode !== 'HEAD')) return fetchOrigine(entree, init);
    try {
      const { data, error } = await supabase.storage.from(f.bucket).download(f.path);
      if (!error && data) {
        return new Response(methode === 'HEAD' ? null : data, { status: 200, headers: { 'Content-Type': data.type || 'application/octet-stream' } });
      }
      noterRefusFichier(f, error && error.message);
    } catch (e) { noterRefusFichier(f, e && e.message); }
    return fetchOrigine(entree, init); // espace encore public : comportement d'avant
  };
  const ouvrirOrigine = window.open.bind(window);
  window.open = (adresse, cible, options) => {
    const f = fichierPrive(typeof adresse === 'string' ? adresse : (adresse && adresse.href));
    if (!f) return ouvrirOrigine(adresse, cible, options);
    const href = typeof adresse === 'string' ? adresse : adresse.href;
    if (cible === '_self' || cible === '_top' || cible === '_parent') {
      lienTemporaireFichier(f).then(lien => { window.location.href = lien || href; });
      return null;
    }
    // La fenêtre est ouverte tout de suite (sinon le navigateur la bloque), puis reçoit le lien temporaire.
    const fenetre = ouvrirOrigine('', cible || '_blank', options);
    lienTemporaireFichier(f).then(lien => {
      const dest = lien || href;
      if (fenetre && !fenetre.closed) fenetre.location.href = dest;
      else ouvrirOrigine(dest, cible || '_blank', options);
    });
    return fenetre;
  };
  // Liens <a href="adresse publique"> : ouverts avec un lien temporaire.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    const lien = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!lien || !fichierPrive(lien.href)) return;
    e.preventDefault();
    window.open(lien.href, lien.target && lien.target !== '_self' ? lien.target : '_blank');
  }, true);
}

// Télécharge un fichier de stockage en forçant le nom donné par l'utilisateur (doc.nom), au lieu du nom
// technique généré à l'upload (horodatage, dossier, etc.) — utilisé partout où un document du "dossier
// client" (organisme + formateur) doit se télécharger sous le nom que la personne lui a donné.
// nomPersonne (2026-10-16) : nom du client (ou du formateur) ajouté à la fin du nom du fichier.
const handleDownloadNamedFile = async (doc, nomPersonne = '') => {
  if (!doc?.url) return;
  try {
    const extracted = extractStorageBucketAndPath(doc.url);
    if (!extracted) throw new Error('URL de fichier invalide.');
    let { data, error } = await supabase.storage.from(extracted.bucket).download(extracted.path);
    // SÉCURITÉ (2026-10-12) : les règles de stockage réservent désormais le téléchargement direct
    // aux fichiers de son organisme. Pour un ancien fichier sans auteur identifiable, on se rabat
    // sur son lien public (comportement d'avant) plutôt que d'échouer.
    if (error) {
      const publicUrl = supabase.storage.from(extracted.bucket).getPublicUrl(extracted.path)?.data?.publicUrl;
      const resp = publicUrl ? await fetch(publicUrl).catch(() => null) : null;
      if (!resp || !resp.ok) throw error;
      data = await resp.blob();
      error = null;
    }
    const ext = (extracted.path.split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    // Si le nom du document finit déjà par l'extension (« scan.pdf »), on la retire avant d'ajouter
    // le nom de la personne, puis on la remet à la fin.
    let nomDoc = String(doc.nom || 'document').trim() || 'document';
    if (ext && nomDoc.toLowerCase().endsWith('.' + ext)) nomDoc = nomDoc.slice(0, -(ext.length + 1));
    const baseName = nomTelechargement(nomDoc, nomPersonne);
    const fileName = ext && ext.length <= 5 ? `${baseName}.${ext}` : baseName;
    const blobUrl = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (e) {
    // Repli : on ouvre le fichier comme avant (nom technique) plutôt que de ne rien faire.
    console.warn('Téléchargement nommé impossible, ouverture du fichier :', e?.message);
    try { await openSecureStorageFile(doc.url); }
    catch (e2) { toast.error('Erreur lors du téléchargement : ' + (e?.message || e2?.message)); }
  }
};


// ==========================================
// MODALS
// ==========================================
const SignatureModal = ({ isOpen, onClose, onSave, requiredCheckboxes = [], requiredTextFields = [] }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#0f172a';
    }
    if (isOpen) { setCheckedIds(new Set()); setTextFieldValues({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Au moins UNE case cochée suffit à débloquer la signature (pas besoin de toutes les cocher).
  const allCheckboxesChecked = requiredCheckboxes.length === 0 || requiredCheckboxes.some(f => checkedIds.has(fieldKey(f)));
  const toggleCheckbox = (fieldId) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId); else next.add(fieldId);
      return next;
    });
  };

  // FIX (2026-09-07) : balises "texte libre" (ex: texte_organisme) exigées pour ce signataire —
  // même pattern que DocumentViewerModal (requiredTextFields / textFieldValues / blocage tant
  // que non rempli), absent jusqu'ici de cette modale générique de signature.
  const [textFieldValues, setTextFieldValues] = useState({});
  const setTextFieldValue = (fieldId, value) => {
    setTextFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };
  const allRequiredTextFilled = requiredTextFields.length === 0 || requiredTextFields.every(f => (textFieldValues[fieldKey(f)] || '').trim().length > 0);

  // FIX (2026-09-14) : même correctif que EmargementModal (getCoords) — voir le commentaire détaillé
  // là-bas. Ce composant n'est actuellement rendu nulle part dans l'app (remplacé le 2026-09-07 par
  // <DocumentViewerModal mode="sign">), mais corrigé quand même pour éviter de réintroduire ce bug
  // s'il est un jour réutilisé.
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        offsetX: (e.touches[0].clientX - rect.left) * sx,
        offsetY: (e.touches[0].clientY - rect.top) * sy
      };
    }
    return { offsetX: (e.clientX - rect.left) * sx, offsetY: (e.clientY - rect.top) * sy };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const { offsetX, offsetY } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext('2d');
    const { offsetX, offsetY } = getCoordinates(e);
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleSave = () => {
    if (!canvasRef.current || !allCheckboxesChecked || !allRequiredTextFilled) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl, checkedIds, textFieldValues);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 w-full max-w-lg border border-gray-100">
        <h3 className="text-xl font-extrabold text-gray-900 mb-2">Émargement Électronique</h3>
        <p className="text-sm text-gray-500 mb-6">Veuillez signer lisiblement dans le cadre ci-dessous pour valider votre présence ou votre accord.</p>
        {requiredCheckboxes.length > 0 && (
          <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm font-bold text-amber-800 mb-3">Avant de signer, cochez les cases suivantes :</p>
            {requiredCheckboxes.map((f, i) => (
              <label key={fieldKey(f) || i} className="flex items-center gap-3 cursor-pointer mb-2 select-none">
                <input
                  type="checkbox"
                  checked={checkedIds.has(fieldKey(f))}
                  onChange={() => toggleCheckbox(fieldKey(f))}
                  className="w-4 h-4 accent-violet-600 shrink-0"
                />
                <span className="text-sm text-gray-700 font-medium">Case à cocher {i + 1}</span>
              </label>
            ))}
            {!allCheckboxesChecked && (
              <p className="text-xs text-amber-700 mt-2 italic">Au moins une case doit être cochée pour pouvoir signer.</p>
            )}
          </div>
        )}
        {requiredTextFields.length > 0 && (
          <div className="mb-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-sm font-bold text-emerald-800 mb-3">Avant de signer, complétez le(s) champ(s) suivant(s) :</p>
            {requiredTextFields.map((f, i) => (
              <div key={fieldKey(f) || i} className="mb-2">
                <textarea
                  value={textFieldValues[fieldKey(f)] || ''}
                  onChange={e => setTextFieldValue(fieldKey(f), e.target.value)}
                  placeholder={`Texte ${i + 1}`}
                  rows={2}
                  className="w-full p-2.5 text-sm bg-white border border-emerald-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-400 transition-all"
                />
              </div>
            ))}
            {!allRequiredTextFilled && (
              <p className="text-xs text-emerald-700 mt-2 italic">Merci de compléter ce(s) champ(s) avant de signer.</p>
            )}
          </div>
        )}
        <div className="border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden bg-gray-50 touch-none mb-6 relative">
          <canvas
            ref={canvasRef}
            width={450}
            height={200}
            className="w-full h-[200px] cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseOut={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          <div className="absolute bottom-3 right-3 opacity-30 pointer-events-none text-xs font-bold uppercase tracking-widest text-gray-500">Zone de signature</div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between gap-3">
          <button onClick={clearCanvas} className="px-5 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors w-full sm:w-auto">Effacer</button>
          <div className="flex gap-3 w-full sm:w-auto flex-col sm:flex-row">
            <button onClick={onClose} className="px-5 py-3 text-gray-700 font-bold hover:bg-gray-100 rounded-xl transition-colors w-full sm:w-auto">Annuler</button>
            <button
              onClick={handleSave}
              disabled={!allCheckboxesChecked || !allRequiredTextFilled}
              className="px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors shadow-lg w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Valider
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddressInput = ({ value, onChange }) => {
  const parseAddress = (addr) => {
    if (!addr) return { rue: '', codePostal: '', ville: '' };
    const m = addr.match(/^(.*?),?\s*(\d{5})\s+(.+)$/);
    if (m) return { rue: m[1].trim().replace(/,$/, ''), codePostal: m[2], ville: m[3].trim() };
    return { rue: addr, codePostal: '', ville: '' };
  };
  const [parts, setParts] = React.useState(() => parseAddress(value));
  // FIX (2026-09-07) : l'initialiseur paresseux de useState ne s'exécute qu'AU MONTAGE — si
  // `value` change ensuite depuis l'extérieur (ex: AddressAutocomplete qui met à jour la
  // valeur combinée du parent après sélection d'une suggestion), `parts` ne se resynchronisait
  // jamais. Round-trip sûr : onChange(combined) plus bas repasse par le même parse/combine.
  React.useEffect(() => { setParts(parseAddress(value)); }, [value]);
  const updatePart = (field, val) => {
    const np = { ...parts, [field]: val };
    setParts(np);
    const combined = [np.rue, np.codePostal && np.ville ? `${np.codePostal} ${np.ville}` : (np.codePostal || np.ville)].filter(Boolean).join(', ');
    onChange(combined);
  };
  return (
    <div className="space-y-2">
      <div>
        <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">N° et Rue</label>
        <input
          className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-violet-400 transition-all text-sm"
          value={parts.rue}
          onChange={e => updatePart('rue', e.target.value)}
          placeholder="Ex: 12 Rue de la Paix"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Code Postal</label>
          <input
            className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-violet-400 transition-all text-sm"
            value={parts.codePostal}
            onChange={e => updatePart('codePostal', e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="75000"
            maxLength={5}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Ville</label>
          <input
            className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-violet-400 transition-all text-sm"
            value={parts.ville}
            onChange={e => updatePart('ville', e.target.value)}
            placeholder="Paris"
          />
        </div>
      </div>
    </div>
  );
};

// Découpe une adresse combinée "N° et rue, CP Ville" en parties distinctes.
// Utilisé pour dériver rue_formateur / code_postal_formateur / ville_formateur
// à partir du champ unique `adresse_formateur` lors de la résolution des balises documentaires.
const parseAddressString = (addr) => {
  if (!addr) return { rue: '', codePostal: '', ville: '' };
  const m = addr.match(/^(.*?),?\s*(\d{5})\s+(.+)$/);
  if (m) return { rue: m[1].trim().replace(/,$/, ''), codePostal: m[2], ville: m[3].trim() };
  return { rue: addr, codePostal: '', ville: '' };
};

// AJOUT (2026-09-16) : balises "initiales" (client, formateur, organisme), demandé par l'utilisateur.
// Prend la première lettre du PREMIER et du DERNIER mot d'un nom — plutôt que de dépendre de colonnes
// nom/prénom séparées, qui existent en base mais ne sont quasiment jamais renseignées en pratique
// (le formulaire de création ne demande qu'un seul champ "Nom Complet", ex. "Jean Dupont" ou
// "Marie LEROY" — voir InviteModal). Fonctionne donc quel que soit l'ordre (Nom Prénom ou Prénom Nom)
// et même sur un nom à un seul mot (ex. "Dupont" → "D"). Toujours en majuscules.
// AJOUT (2026-10-02) : chargement FIABLE d'une table Supabase — signalé par l'utilisateur ("parfois je ne
// vois plus mes séances dans mes modules, et en ajoutant une séance tout réapparaît"). Deux causes corrigées :
//  1. une erreur passagère (réseau, session en cours de renouvellement, incident Supabase) était ignorée en
//     silence : la liste restait vide jusqu'au prochain rechargement → on retente désormais 2 fois ;
//  2. Supabase ne renvoie JAMAIS plus de 1000 lignes par requête : au-delà, des lignes manquaient sans aucun
//     message → on lit maintenant la table page par page (buildQuery doit inclure un tri stable, ex. .order('id')).
// buildQuery est une fonction qui reconstruit la requête à chaque page/tentative (un builder Supabase ne se
// réutilise pas après avoir été exécuté).
// 2026-10-21 : colonnes « légères » des documents et des séances (toutes sauf les images de signature),
// fournies par la base (fonction skorup_colonnes_legeres) : une colonne ajoutée plus tard est prise en
// compte toute seule. Sans la fonction : chargement complet, comme avant.
let _colonnesLegeres = null;
const colonnesLegeres = async () => {
  if (_colonnesLegeres) return _colonnesLegeres;
  try {
    const { data, error } = await supabase.rpc('skorup_colonnes_legeres');
    if (!error && data && typeof data.documents === 'string' && typeof data.sessions === 'string' && data.documents && data.sessions) {
      _colonnesLegeres = data;
      return data;
    }
  } catch (_) { /* fonction absente : chargement complet */ }
  return { documents: '*', sessions: '*' };
};
// Images de signature lues à la demande (fabrication des PDF).
const chargerSignaturesSeances = async (clientId) => {
  try {
    const { data } = await supabase.from('sessions').select('id, signature_client, signature_formateur').eq('client_id', clientId);
    return new Map((data || []).map(r => [String(r.id), r]));
  } catch (_) { return new Map(); }
};
const chargerSignaturesDocument = async (docId) => {
  try {
    const { data } = await supabase.from('documents').select('signature_client, signature_formateur').eq('id', docId).maybeSingle();
    return data || {};
  } catch (_) { return {}; }
};

// LOT C (2026-10-22) : dossiers clients archivés (clients.archive_le). Leurs séances et documents restent
// en base mais ne sont plus affichés dans les écrans courants (seulement dans « Dossiers archivés »).
// Sans la colonne (SQL non lancé) : aucun dossier archivé, tout fonctionne comme avant.
let _archives = { org: null, at: 0, promesse: null };
const idsDossiersArchives = (orgId) => {
  if (!orgId) return Promise.resolve(new Set());
  if (_archives.org === orgId && _archives.promesse && Date.now() - _archives.at < 20000) return _archives.promesse;
  const promesse = (async () => {
    try {
      const { data, error } = await supabase.from('clients').select('id').eq('organisation_id', orgId).not('archive_le', 'is', null);
      if (error || !data) return new Set();
      return new Set(data.map(r => String(r.id)));
    } catch (_) { return new Set(); }
  })();
  _archives = { org: orgId, at: Date.now(), promesse };
  return promesse;
};
const oublierArchives = () => { _archives = { org: null, at: 0, promesse: null }; };
const sansArchives = (lignes, ids, cle) => (ids && ids.size ? lignes.filter(l => !ids.has(String(l[cle]))) : lignes);
// Nombre de dossiers clients actifs (limite de l'offre) : les dossiers archivés ne comptent pas.
const compterDossiersActifs = async (orgId) => {
  const r = await supabase.from('clients').select('id', { count: 'exact', head: true }).eq('organisation_id', orgId).is('archive_le', null);
  if (!r.error) return r;
  return supabase.from('clients').select('id', { count: 'exact', head: true }).eq('organisation_id', orgId);
};

const fetchAllRowsWithRetry = async (buildQuery, { label = 'données', pageSize = 1000, retries = 2 } = {}) => {
  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 700 * attempt));
    const rows = [];
    let failed = null;
    for (let from = 0; ; from += pageSize) {
      let result;
      try { result = await buildQuery().range(from, from + pageSize - 1); }
      catch (e) { result = { data: null, error: e }; }
      if (result.error) { failed = result.error; break; }
      rows.push(...(result.data || []));
      if (!result.data || result.data.length < pageSize) break;
    }
    if (!failed) return { data: rows, error: null };
    lastError = failed;
    console.warn(`[chargement ${label}] tentative ${attempt + 1} échouée :`, failed?.message || failed);
  }
  console.error(`[chargement ${label}] abandon après ${retries + 1} tentatives :`, lastError);
  return { data: null, error: lastError };
};

// AJOUT (2026-10-02) : prévient un client par email qu'un nouvel élément l'attend sur son espace (réutilise
// api/client/notify-new-document.js, qui revérifie côté serveur que l'appelant est admin/formateur de
// l'organisme du client). Fire-and-forget : un souci d'email n'annule jamais l'action déjà enregistrée.
// GROUPE (2026-10-23) : pendant un envoi groupé, les emails « nouveau document » du client sont
// regroupés en un seul (voir notifyDocumentRecipients et envoyerGroupeDocuments).
const _envoiGroupe = { actif: false, clientId: null, documents: [] };
const notifierDocumentsAuClient = (clientId, noms) => {
  if (!clientId || !noms || !noms.length) return;
  supabase.auth.getSession().then(({ data: sessionData }) => {
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) return;
    fetch('/api/client/notify-new-document', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      body: JSON.stringify({ clientId, documentName: noms.join(', '), documentNames: noms, origin: window.location.origin }),
    }).then(async (resp) => {
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || result.sent === false) console.warn('[notifierDocumentsAuClient] email non envoyé :', result.error || result.message || resp.status);
    }).catch(e => console.warn('[notifierDocumentsAuClient] erreur réseau :', e.message));
  }).catch(() => {});
};

// REGROUPEMENT DES EMAILS (2026-10-23) : les documents destinés à une même personne dans un court laps
// de temps partent dans UN seul email (15 s sans nouveau document, 60 s au plus). Si la page est fermée
// ou masquée avant, l'email part tout de suite (keepalive : la requête survit à la fermeture).
const _fileEmails = new Map();
const _envoyerFileEmail = (cle) => {
  const f = _fileEmails.get(cle);
  if (!f) return;
  _fileEmails.delete(cle);
  clearTimeout(f.minuteur);
  const noms = Array.from(new Set(f.noms)).filter(Boolean);
  if (!noms.length || !f.jeton) return;
  const origine = window.location.origin;
  const requete = f.type === 'client'
    ? { url: '/api/client/notify-new-document', corps: { clientId: f.id, documentName: noms.join(', '), documentNames: noms, origin: origine } }
    : { url: '/api/formateur/notify', corps: { type: 'nouveau_document', formateurId: f.id, documentName: noms.join(', '), documentNames: noms, origin: origine } };
  try {
    fetch(requete.url, {
      method: 'POST', keepalive: true,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${f.jeton}` },
      body: JSON.stringify(requete.corps),
    }).then(async (resp) => {
      const r = await resp.json().catch(() => ({}));
      if (!resp.ok || r.sent === false) console.warn('[emails regroupés] non envoyé :', r.error || r.message || resp.status);
    }).catch(e => console.warn('[emails regroupés] erreur réseau :', e.message));
  } catch (e) { console.warn('[emails regroupés]', e.message); }
};
const viderFileEmails = () => { Array.from(_fileEmails.keys()).forEach(_envoyerFileEmail); };
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', viderFileEmails);
  window.document.addEventListener('visibilitychange', () => { if (window.document.visibilityState === 'hidden') viderFileEmails(); });
}
const programmerEmailDocument = (type, id, nomDocument) => {
  if (!id || !nomDocument) return;
  supabase.auth.getSession().then(({ data }) => {
    const jeton = data?.session?.access_token;
    if (!jeton) return;
    const cle = `${type}:${id}`;
    let f = _fileEmails.get(cle);
    if (!f) { f = { type, id, noms: [], debut: Date.now(), jeton }; _fileEmails.set(cle, f); }
    f.jeton = jeton;
    f.noms.push(nomDocument);
    clearTimeout(f.minuteur);
    const reste = Math.max(0, 60000 - (Date.now() - f.debut));
    f.minuteur = setTimeout(() => _envoyerFileEmail(cle), Math.min(15000, reste));
  }).catch(() => {});
};

const notifyClientByEmail = (clientId, label) => {
  supabase.auth.getSession().then(({ data: sessionData }) => {
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) return;
    fetch('/api/client/notify-new-document', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      body: JSON.stringify({ clientId, documentName: label, origin: window.location.origin }),
    }).then(async (resp) => {
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || result.sent === false) console.warn('[notifyClientByEmail] email non envoyé :', result.error || result.message || resp.status);
    }).catch(e => console.warn('[notifyClientByEmail] erreur réseau :', e.message));
  }).catch(() => {});
};

// SÉCURITÉ (2026-10-12) : neutralise le texte inséré dans du HTML généré à la main (récapitulatif
// de séances, certificat de signature, autorisation de conservation). Sans cela, un nom ou un titre
// contenant du code pouvait s'exécuter dans le navigateur de l'admin/formateur qui génère le PDF.
const escHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
// Source d'image sûre pour <img src="…"> : image encodée (data:…;base64,…) ou lien http(s)/blob.
// Tout le reste est ignoré (renvoie '').
const safeImgSrc = (v) => {
  const s = String(v || '').trim();
  if (/^data:[a-z0-9.+/-]*(;[a-z0-9=._+-]+)*;base64,[a-z0-9+/=\s]+$/i.test(s)) return s.replace(/\s/g, '');
  if (/^(https?:|blob:)/i.test(s)) return escHtml(s);
  return '';
};

// UX (2026-10-12) : regroupe les éléments d'une même séance (document + émargement…) pour les
// afficher sur UNE seule ligne (tableau de bord, accueil formateur, calendrier) au lieu d'une
// ligne par élément.
const regrouperParSeance = (liste) => {
  const groupes = new Map();
  (liste || []).forEach(s => {
    const aUnNumero = s.numero_seance !== null && s.numero_seance !== undefined;
    const cle = aUnNumero ? `${s.client_id}|${s.numero_seance}|${s.date || ''}` : `seul|${s.id}`;
    if (!groupes.has(cle)) {
      groupes.set(cle, { cle, client_id: s.client_id, numero: aUnNumero ? s.numero_seance : null, date: s.date, heure_debut: s.heure_debut, heure_fin: s.heure_fin, items: [] });
    }
    const g = groupes.get(cle);
    if (!g.heure_debut && s.heure_debut) g.heure_debut = s.heure_debut;
    if (!g.heure_fin && s.heure_fin) g.heure_fin = s.heure_fin;
    g.items.push(s);
  });
  return [...groupes.values()].sort((a, b) =>
    (a.date || '').localeCompare(b.date || '') || String(a.heure_debut || '').localeCompare(String(b.heure_debut || '')));
};
const titreSeance = (g) => {
  if (g.numero === null || g.numero === undefined) return g.items[0]?.ressource_titre || g.items[0]?.nom || 'Séance';
  if (Number(g.numero) === 0) return 'Bloc administratif';
  return `Séance ${g.numero}`;
};
// AFFICHAGE (2026-10-20) : signatures réellement attendues sur un élément de séance. Un document à
// simple consultation, un exercice ou un dossier vide n'attend AUCUNE signature (avant, plusieurs
// écrans affichaient « Attente » sur tout, comme si chaque élément était un émargement). Même règle
// que les boutons « Signer » de l'espace client ; ce qui a déjà été signé reste toujours affiché.
const signaturesAttendues = (s) => {
  const m = lireMeta(s?.metadata);
  const type = s?.type_activite;
  const aSigner = type === 'signature' || !!m.isToSign
    || (type === 'document' && !!(m.requiresSignature || m.documentType === 'signature'));
  return {
    client: s?.statut_client === 'Signé' || (!s?.statut_client && s?.statut === 'Signé')
      || (aSigner && m.requiresClientSignature !== false),
    formateur: s?.statut_formateur === 'Signé' || m.requiresTrainerSignature === true
      || (type === 'signature' && m.requiresTrainerSignature !== false),
  };
};
const seanceAttendSignature = (g) => (g?.items || []).some(s => signaturesAttendues(s).client);
const seanceSignee = (g) => g.items.filter(s => signaturesAttendues(s).client).every(s => s.statut === 'Signé' || s.statut_client === 'Signé');
const horaireSeance = (g) => g.heure_debut ? `${g.heure_debut}${g.heure_fin ? '–' + g.heure_fin : ''}` : '';

// UX (2026-10-12) : guide « Bien démarrer » affiché sur le tableau de bord d'un nouvel admin,
// jusqu'à ce que les étapes essentielles soient faites (ou qu'il le masque).
const GuideDemarrage = ({ orgSettings, modules, documents, formateurs, clients, setActiveTab, currentOrgId }) => {
  const cleMasque = `skorup_guide_masque_${currentOrgId || 'org'}`;
  const [masque, setMasque] = React.useState(() => { try { return localStorage.getItem(cleMasque) === '1'; } catch { return false; } });
  const etapes = [
    { titre: 'Complétez les informations de votre organisme', detail: 'Raison sociale, SIRET, n° de déclaration d\'activité : elles apparaissent sur vos documents.', fait: !!(orgSettings?.siret && orgSettings?.nda), onglet: 'parametres_org', bouton: 'Compléter' },
    { titre: 'Créez votre premier module', detail: 'Le parcours type de vos accompagnements (ex. : bilan de compétences en 6 séances).', fait: (modules || []).length > 0, onglet: 'modules', bouton: 'Créer un module' },
    { titre: 'Ajoutez vos modèles de documents', detail: 'Convention, programme, émargement… remplis et signés automatiquement.', fait: (documents || []).some(d => !d.user_id && !d.assigned_formateur_id), onglet: 'gestion_documents', bouton: 'Ajouter' },
    { titre: 'Invitez vos formateurs', detail: 'Facultatif : seulement si vous travaillez avec des formateurs ou sous-traitants.', fait: (formateurs || []).filter(f => !f.estAdmin).length >= 1, onglet: 'formateurs', bouton: 'Inviter', facultatif: true },
    { titre: 'Ajoutez votre premier client', detail: 'Il reçoit un email pour accéder à son espace personnel.', fait: (clients || []).length > 0, onglet: 'clients', bouton: 'Ajouter un client' },
  ];
  const nbFaites = etapes.filter(e => e.fait).length;
  if (masque || etapes.filter(e => !e.facultatif).every(e => e.fait)) return null;
  const masquer = () => { try { localStorage.setItem(cleMasque, '1'); } catch { /* navigateur sans stockage */ } setMasque(true); };
  return (
    <div className="bg-white rounded-2xl border border-violet-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-violet-50 border-b border-violet-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-black text-violet-900">Bien démarrer avec SkorUp</h2>
          <p className="text-sm text-violet-700">{nbFaites} étape{nbFaites > 1 ? 's' : ''} sur {etapes.length} — quelques minutes suffisent.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32 h-2 bg-violet-100 rounded-full overflow-hidden">
            <div className="h-full bg-violet-600 rounded-full" style={{ width: `${Math.round(nbFaites / etapes.length * 100)}%` }} />
          </div>
          <button onClick={masquer} className="text-xs font-bold text-violet-500 hover:text-violet-800">Masquer</button>
        </div>
      </div>
      <div className="divide-y divide-gray-50">
        {etapes.map((e, i) => (
          <div key={i} className="px-5 py-3.5 flex items-center gap-4">
            {e.fait
              ? <CheckCircle size={22} className="text-emerald-500 shrink-0" />
              : <span className="w-[22px] h-[22px] rounded-full border-2 border-violet-200 text-violet-500 text-xs font-black flex items-center justify-center shrink-0">{i + 1}</span>}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${e.fait ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{e.titre}</p>
              {!e.fait && <p className="text-xs text-gray-500 mt-0.5">{e.detail}</p>}
            </div>
            {!e.fait && (
              <button onClick={() => setActiveTab(e.onglet)} className="shrink-0 text-xs font-bold px-3 py-2 rounded-xl bg-violet-600 text-white hover:bg-violet-700">{e.bouton}</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const computeInitials = (fullName) => {
  const words = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
};

// AJOUT (2026-09-18) : détermine si le parcours (module) d'un client est "terminé" — défini avec
// l'utilisateur comme : le client a au moins une séance de son parcours planifiée dans son calendrier,
// et TOUTES ces séances ont une date déjà passée (aucune à venir, aucune sans date). Sert à déclencher
// automatiquement l'accès aux documents/questionnaires/exercices de FIN de parcours (masqués tant que
// le parcours n'est pas terminé — voir ClientDocumentsView) sans action manuelle du formateur.
// Ne compte QUE les séances "réelles" du calendrier (numero_seance renseigné, issues des dossiers
// séance du module) — pas les exercices de fin nouvellement créés par ce mécanisme lui-même
// (numero_seance: null, voir plus bas), ce qui éviterait sinon un effet de bord où l'ajout d'un
// exercice de fin repasserait aussitôt le parcours à "non terminé".
//
// FIX (2026-09-30) : demandé par l'utilisateur — les documents de fin doivent être accessibles dès la
// FIN DU MODULE, « même si tout n'est pas signé ou autre ». L'ancienne règle exigeait que CHAQUE ligne
// de séance (émargement, document, exercice…) porte sa propre date strictement passée : une seule
// ligne sans date (ex. un document glissé dans un dossier de séance) pouvait suffire à bloquer la fin de
// parcours pour toujours (signalé sur le dossier Matthys Tessier : dernière séance signée la veille,
// documents de fin toujours verrouillés). Désormais on raisonne PAR SÉANCE (numero_seance) : la date d'une
// séance = la date renseignée sur l'une de ses lignes ; le parcours est terminé quand toutes les
// séances sont planifiées et que la date de la dernière est arrivée (le jour même inclus). Les
// statuts de signature / de rendu ne sont volontairement PAS pris en compte.
// LOT A (2026-10-21) : après un changement de module, les séances de l'ancien module restent en base
// (rien n'est perdu) mais ne comptent plus : seul le module ACTUEL du client décide de la fin de parcours
// et apparaît dans le planning. Une séance sans module (anciennes données) compte toujours.
const lireMetaSeance = (m) => (typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {}));
const estDuModuleActuel = (s, moduleId) => moduleId == null || moduleId === '' || s.module_id == null || String(s.module_id) === String(moduleId);
// ── FUSION_MODULE_DEBUT ──────────────────────────────────────────────────────────────────────
// 04/11/2026 : changement de module = fusion SÉANCE PAR SÉANCE (décision de l'utilisateur), au lieu de
// ranger toute l'ancienne séance à part. Rien n'est jamais supprimé :
//   - un élément signé / rendu de l'ancienne séance N prend la place de l'élément équivalent (pas encore
//     commencé) de la séance N du nouveau module ; l'élément remplacé est rangé avec l'ancien module ;
//   - un élément pas encore commencé est remplacé par le contenu du nouveau module ; si l'ancienne séance
//     était planifiée, sa date et ses horaires sont repris sur la séance du nouveau module (si elle n'en a pas) ;
//   - une ancienne séance signée ou planifiée qui n'existe pas dans le nouveau module se place selon sa
//     date (les séances suivantes sont renumérotées), sinon à la fin. Jamais de renumérotation pour un
//     participant de groupe (les séances du groupe sont liées par leur numéro) ;
//   - un élément ni commencé ni daté reste rangé avec l'ancien module (non affiché).
const elementSeanceCommence = (s) => elementSeanceEstSigne(s) || !!(s && s.reponse_url);
const estEmargementElement = (s) => /[ée]margement/i.test(`${s.ressource_titre || ''} ${s.nom || ''}`);
const titreElementNormalise = (s) => String(s.ressource_titre || s.nom || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/^seance\s*\d+\s*[-–:·]\s*/, '').replace(/\s*[-–]\s*\d+\s*$/, '').replace(/[^a-z0-9]+/g, ' ').trim();
const elementsEquivalents = (a, b) => {
  if (estEmargementElement(a) || estEmargementElement(b)) return estEmargementElement(a) && estEmargementElement(b);
  if (a.ressource_id && b.ressource_id && String(a.ressource_id) === String(b.ressource_id)) return true;
  const ta = titreElementNormalise(a);
  return !!ta && ta === titreElementNormalise(b);
};
const renumeroterTexte = (t, avant, apres) => (t ? String(t)
  .replace(new RegExp(`^(S[ée]ance\\s+)${avant}(?!\\d)`), `$1${apres}`)
  .replace(new RegExp(`(\\s[-–]\\s)${avant}$`), `$1${apres}`) : t);
const titreElementSeance = (s) => s.ressource_titre || s.nom || 'Élément';

const planFusionModule = (lignes, moduleId, { renumeroter = true } = {}) => {
  const mid = String(moduleId);
  const valeurModule = /^\d+$/.test(mid) ? Number(mid) : moduleId;
  const eligibles = (lignes || []).filter(s => s && s.numero_seance != null && Number(s.numero_seance) >= 1 && !s.is_administrative);
  const estActuel = (s) => s.module_id == null || String(s.module_id) === mid;
  const finaux = eligibles.filter(estActuel).map(s => ({ ...s, _initActuel: true }));
  const anciens = eligibles.filter(s => !estActuel(s));
  const sortants = [];
  const extras = [];
  const lignesDe = (n) => finaux.filter(s => !s._sortant && Number(s.numero_seance) === n);
  const aGarder = (s) => elementSeanceCommence(s) || !!s.date;
  const reporterDate = (rangees, src) => {
    rangees.forEach(s => {
      if (s.date || elementSeanceCommence(s)) return;
      s.date = src.date; s.heure_debut = s.heure_debut || src.heure_debut || null; s.heure_fin = s.heure_fin || src.heure_fin || null;
    });
  };

  [...new Set(anciens.map(s => Number(s.numero_seance)))].sort((a, b) => a - b).forEach(n => {
    const olds = anciens.filter(s => Number(s.numero_seance) === n);
    const cible = lignesDe(n);
    if (!olds.some(aGarder)) return; // ni signé ni planifié : rien à reprendre
    if (!cible.length) {
      const gardes = olds.filter(aGarder);
      if (gardes.length) extras.push({ numero: n, lignes: gardes });
      return;
    }
    const entrees = [];
    olds.filter(elementSeanceCommence).forEach(e => {
      const f = cible.find(x => !x._sortant && !elementSeanceCommence(x) && elementsEquivalents(e, x));
      if (f) { f._sortant = true; sortants.push({ ...f, module_id: e.module_id }); }
      entrees.push({ ...e, module_id: valeurModule, _repris: true });
    });
    entrees.forEach(e => finaux.push(e));
    const seance = lignesDe(n);
    seance.forEach(s => { s._touchee = true; });
    const src = seance.find(s => s._initActuel && s.date) || seance.find(s => s.date)
      || olds.filter(s => s.date).sort((a, b) => Number(elementSeanceCommence(b)) - Number(elementSeanceCommence(a)))[0];
    if (src) reporterDate(seance, src);
  });

  const dateSeance = (n) => lignesDe(n).map(s => s.date).filter(Boolean).sort()[0] || null;
  extras.forEach(x => { x.date = x.lignes.map(s => s.date).filter(Boolean).sort()[0] || null; });
  extras.sort((a, b) => ((a.date || '9999') < (b.date || '9999') ? -1 : (a.date || '9999') > (b.date || '9999') ? 1 : a.numero - b.numero));
  extras.forEach(x => {
    const nums = [...new Set(finaux.filter(s => !s._sortant).map(s => Number(s.numero_seance)))].sort((a, b) => a - b);
    const max = nums.length ? nums[nums.length - 1] : 0;
    const libre = !nums.includes(x.numero);
    const ordreOk = (k) => nums.every(n => { const d = dateSeance(n); if (!d || !x.date) return true; return n < k ? d <= x.date : d >= x.date; });
    let K = null;
    if (libre && (!renumeroter || ordreOk(x.numero))) K = Math.min(x.numero, max + 1);
    else if (renumeroter && x.date) {
      const suivant = nums.find(n => { const d = dateSeance(n); return d && d > x.date; });
      if (suivant != null) {
        K = suivant;
        finaux.filter(s => !s._sortant && Number(s.numero_seance) >= K).forEach(s => {
          const av = Number(s.numero_seance);
          s.numero_seance = av + 1;
          if (!elementSeanceCommence(s)) { s.nom = renumeroterTexte(s.nom, av, av + 1); s.ressource_titre = renumeroterTexte(s.ressource_titre, av, av + 1); }
        });
      }
    }
    if (K == null) K = max + 1;
    const src = x.lignes.find(s => s.date);
    x.lignes.forEach(e => {
      const n = { ...e, module_id: valeurModule, numero_seance: K, _repris: true, _touchee: true, _extra: x.numero };
      if (!n.date && src && !elementSeanceCommence(n)) { n.date = src.date; n.heure_debut = n.heure_debut || src.heure_debut || null; n.heure_fin = n.heure_fin || src.heure_fin || null; }
      finaux.push(n);
    });
  });

  // Différences à écrire (les lignes « virtuel-… » servent seulement à l'aperçu).
  const parId = new Map(eligibles.map(s => [String(s.id), s]));
  const CHAMPS = ['module_id', 'numero_seance', 'nom', 'ressource_titre', 'date', 'heure_debut', 'heure_fin'];
  const changements = [];
  [...finaux.filter(s => !s._sortant), ...sortants].forEach(s => {
    const o = parId.get(String(s.id));
    if (!o) return;
    const maj = {};
    CHAMPS.forEach(c => { const a = o[c] ?? null, b = s[c] ?? null; if (String(a) !== String(b)) maj[c] = b; });
    if (Object.keys(maj).length) changements.push({ id: s.id, maj });
  });

  // Récapitulatif pour la fenêtre de confirmation.
  const reprises = {};
  finaux.filter(s => s._repris).forEach(s => {
    const k = Number(s.numero_seance);
    (reprises[k] = reprises[k] || []).push({ titre: titreElementSeance(s), signe: elementSeanceCommence(s), ancienNumero: Number(parId.get(String(s.id))?.numero_seance), extra: s._extra != null });
  });
  const decales = {};
  finaux.filter(s => s._initActuel && !s._sortant).forEach(s => {
    const av = Number(parId.get(String(s.id)).numero_seance);
    if (av !== Number(s.numero_seance)) decales[av] = Number(s.numero_seance);
  });
  const dates = [];
  finaux.filter(s => !s._sortant && s._initActuel).forEach(s => {
    const o = parId.get(String(s.id));
    if (!o.date && s.date) { const k = Number(s.numero_seance); if (!dates.some(d => d.numero === k)) dates.push({ numero: k, date: s.date }); }
  });
  const nonRepris = anciens.filter(s => !finaux.some(f => String(f.id) === String(s.id))).length;
  return {
    changements,
    reprises: Object.keys(reprises).map(Number).sort((a, b) => a - b).map(n => ({ numero: n, elements: reprises[n] })),
    dates: dates.sort((a, b) => a.numero - b.numero),
    decales: Object.keys(decales).map(Number).sort((a, b) => a - b).map(av => ({ avant: av, apres: decales[av] })),
    remplaces: sortants.length,
    nonRepris,
    idsRepris: finaux.filter(s => s._repris).map(s => String(s.id)),
  };
};
// ── FUSION_MODULE_FIN ────────────────────────────────────────────────────────────────────────

// Fenêtre de confirmation d'un changement de module (ou d'une fusion d'un ancien module).
const ChangementModuleModal = ({ etat, onAnnuler, onConfirmer }) => {
  if (!etat) return null;
  const { plan, chargement, enCours, erreur, fusionSeule, moduleId, nomClient, ancien, nouveau, automatisations = [] } = etat;
  const dateFr = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
  const rien = plan && !plan.reprises.length && !plan.dates.length && !plan.decales.length;
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="titre-changement-module">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div>
          <h3 id="titre-changement-module" className="text-lg font-black text-gray-900">{fusionSeule ? 'Reprendre les séances de l\'ancien module' : `Changer le module de ${nomClient}`}</h3>
          <p className="text-sm text-gray-500 mt-1">{fusionSeule ? `Dans le parcours actuel : ${nouveau}` : `${ancien} → ${nouveau}`}</p>
        </div>
        {chargement && <p className="text-sm text-gray-500">Préparation du récapitulatif…</p>}
        {erreur && <p className="text-sm text-red-700 bg-red-50 rounded-xl p-3">{erreur}</p>}
        {!chargement && !erreur && !moduleId && (
          <p className="text-sm text-gray-700">Le module sera retiré du dossier. Les séances et les signatures restent dans le dossier.</p>
        )}
        {!chargement && !erreur && moduleId && plan && (
          <div className="space-y-3 text-sm text-gray-700">
            {rien && <p>{fusionSeule ? 'Rien à reprendre : aucun élément signé ou planifié dans l\'ancien module.' : 'Aucune séance signée ou planifiée à reprendre : le parcours du nouveau module est créé tel quel.'}</p>}
            {plan.reprises.length > 0 && (
              <div className="rounded-xl bg-emerald-50 p-3 space-y-1">
                <p className="font-bold text-emerald-800">Repris dans le nouveau parcours</p>
                {plan.reprises.map(r => (
                  <p key={r.numero}>Séance {r.numero} : {r.elements.map((e, i) => <span key={i}>{i > 0 ? ', ' : ''}« {e.titre} »{e.signe ? ' (signé)' : ' (planifié)'}{e.extra && e.ancienNumero !== r.numero ? ` — ancienne séance ${e.ancienNumero}` : ''}</span>)}</p>
                ))}
              </div>
            )}
            {plan.dates.length > 0 && (
              <div className="rounded-xl bg-violet-50 p-3 space-y-1">
                <p className="font-bold text-violet-800">Dates reprises</p>
                {plan.dates.map(d => <p key={d.numero}>Séance {d.numero} : {dateFr(d.date)}</p>)}
              </div>
            )}
            {plan.decales.length > 0 && (
              <div className="rounded-xl bg-amber-50 p-3 space-y-1">
                <p className="font-bold text-amber-800">Séances renumérotées (placement selon la date)</p>
                <p>{plan.decales.map(d => `${d.avant} → ${d.apres}`).join(' · ')}</p>
                {automatisations.length > 0 && (
                  <p className="text-amber-900">⚠ {automatisations.length} envoi(s) automatique(s) réglé(s) sur une de ces séances suivra(ont) le numéro : {automatisations.map(a => `« ${a.email_subject || 'sans objet'} » (séance ${a.target_numero_seance})`).join(', ')}.</p>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500">Rien n'est supprimé : les éléments signés gardent leur nom et leur PDF, et ce qui n'est pas repris reste rangé dans le dossier.</p>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button type="button" onClick={onAnnuler} disabled={enCours} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 disabled:opacity-40">Annuler</button>
          <button type="button" onClick={onConfirmer} disabled={chargement || enCours || !!erreur || (fusionSeule && rien)} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40">
            {enCours ? 'Enregistrement…' : (fusionSeule ? 'Reprendre dans le parcours' : 'Confirmer le changement')}
          </button>
        </div>
      </div>
    </div>
  );
};

const moduleDuClient = (clients, clientId) => {
  const c = (clients || []).find(x => String(x.id) === String(clientId));
  return c ? (c.module_id ?? null) : null;
};
// LOT B (2026-10-21) : progression = part des séances du module actuel réellement faites. Une séance
// est faite quand tout ce que le client doit y signer est signé ; sans signature attendue, quand sa date
// est passée. (Avant : compteur incrémenté à chaque signature, divisé par 10 par défaut.)
const progressionParcours = (clientId, sessions, moduleId = null) => {
  const lignes = (sessions || []).filter(s => String(s.client_id) === String(clientId) && s.numero_seance != null
    && Number(s.numero_seance) >= 1 && estDuModuleActuel(s, moduleId));
  const parSeance = new Map();
  lignes.forEach(s => { const k = String(s.numero_seance); if (!parSeance.has(k)) parSeance.set(k, { items: [] }); parSeance.get(k).items.push(s); });
  const total = parSeance.size;
  if (!total) return { faites: 0, total: 0, pct: 0 };
  const aujourdHui = jourLocalISO(new Date());
  let faites = 0;
  parSeance.forEach(g => {
    if (seanceAttendSignature(g)) { if (seanceSignee(g)) faites++; return; }
    const dates = g.items.map(s => (s.date ? String(s.date).slice(0, 10) : null)).filter(Boolean);
    if (dates.length && dates.every(d => d <= aujourdHui)) faites++;
  });
  return { faites, total, pct: Math.min(100, Math.round((faites / total) * 100)) };
};

const isModuleCompletedForClient = (clientId, sessions, moduleId = null) => {
  const realSessions = (sessions || []).filter(s => String(s.client_id) === String(clientId) && s.numero_seance != null && estDuModuleActuel(s, moduleId));
  if (realSessions.length === 0) return false;
  const seanceDates = new Map(); // numero_seance → date la plus tardive trouvée sur ses lignes (ou null)
  realSessions.forEach(s => {
    const key = String(s.numero_seance);
    const d = s.date ? String(s.date).slice(0, 10) : null;
    const prev = seanceDates.get(key) || null;
    seanceDates.set(key, d && (!prev || d > prev) ? d : prev);
  });
  const dates = Array.from(seanceDates.values());
  if (dates.some(d => !d)) return false; // une séance entière n'a encore aucune date → pas encore planifiée
  const now = new Date(); // date LOCALE (et non UTC) pour basculer à minuit heure française
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return dates.every(d => d <= todayStr);
};

// ═══════════════════════════════════════════════════════════════════════════
// RÉGION (2026-09-07) : liste officielle des 18 régions administratives françaises
// (13 métropole + 5 outre-mer), utilisée comme <datalist> pour un champ « Région » à
// autocomplétion native sur les fiches Client / Formateur / Organisme (taper « Bre » →
// suggère « Bretagne »).
// ═══════════════════════════════════════════════════════════════════════════
const FRENCH_REGIONS = [
  'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Bretagne', 'Centre-Val de Loire',
  'Corse', 'Grand Est', 'Guadeloupe', 'Guyane', 'Hauts-de-France', 'Île-de-France',
  'La Réunion', 'Martinique', 'Mayotte', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie',
  'Pays de la Loire', "Provence-Alpes-Côte d'Azur",
];

// Autocomplétion d'adresse française (API Adresse — api-adresse.data.gouv.fr — gratuite,
// sans clé, données BAN officielles). Au clic sur une suggestion, remonte via onSelect
// rue/codePostal/ville ET la région administrative correspondante (dernier segment de
// `properties.context`, ex: "75, Paris, Île-de-France"), pour pré-remplir automatiquement
// le champ Région à côté.
const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, className }) => {
  const [query, setQuery] = React.useState(value || '');
  const [suggestions, setSuggestions] = React.useState([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const debounceRef = React.useRef(null);

  React.useEffect(() => { setQuery(value || ''); }, [value]);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (val) => {
    setQuery(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val || val.trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(val)}&limit=5`);
        if (!resp.ok) return;
        const data = await resp.json();
        setSuggestions(data.features || []);
        setIsOpen(true);
      } catch (_) { /* best-effort : ne bloque jamais la saisie manuelle en cas d'échec réseau */ }
    }, 300);
  };

  const pick = (feature) => {
    const p = feature.properties || {};
    const label = p.label || '';
    setQuery(label);
    onChange(label);
    setIsOpen(false);
    setSuggestions([]);
    // properties.context est de la forme "<code dépt>, <nom dépt>, <nom région>" → la région
    // est toujours le DERNIER segment.
    const contextParts = (p.context || '').split(',').map(s => s.trim());
    const region = contextParts.length > 0 ? contextParts[contextParts.length - 1] : '';
    if (onSelect) onSelect({
      label,
      rue: [p.housenumber, p.street].filter(Boolean).join(' ') || p.name || '',
      codePostal: p.postcode || '',
      ville: p.city || '',
      region,
    });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        className={className || "w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-violet-400 transition-all text-sm"}
        value={query}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        placeholder={placeholder || 'Ex : 12 Rue de la Paix, 75001 Paris'}
        autoComplete="off"
      />
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto">
          {suggestions.map((f, i) => (
            <button
              type="button"
              key={f.properties?.id || i}
              onClick={() => pick(f)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 transition-colors border-b border-gray-50 last:border-b-0"
            >
              {f.properties?.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Identifiant stable d'un champ de balise (signature/case à cocher), utilisé pour savoir QUELLE
// case précise a été cochée par le signataire. Les champs venus d'une vraie ligne de la table
// `template_fields` ont un `id` de base de données unique. Mais les champs dupliqués en zéro-RLS
// dans metadata (module_step_resources / documents .template_fields / .signature_fields) n'ont
// historiquement PAS de `id` du tout (voir handleUploadVisualTemplate) → tous partagent `undefined`,
// ce qui fait que cocher UNE case en coche d'autres qui partagent la même clé "undefined".
// `client_key` est l'identifiant posé par l'éditeur de balises (toujours unique, généré à la pose) ;
// on le préfère quand il existe, sinon on retombe sur `id` (cas des lignes lues directement en DB).
// Dernier repli : des DOCUMENTS DÉJÀ GÉNÉRÉS avant l'ajout de client_key ont leur propre copie figée
// de metadata.signature_fields, sans client_key NI id — la regénérer n'est pas automatique (anti-
// doublon dans instantiateDocument), donc on dérive une clé à partir de la position exacte du champ
// (deux cases distinctes sont forcément à des coordonnées différentes) plutôt que de laisser
// plusieurs cases partager encore la clé "undefined".
const fieldKey = (f) => {
  if (!f) return undefined;
  if (f.client_key !== undefined && f.client_key !== null) return f.client_key;
  if (f.id !== undefined && f.id !== null) return f.id;
  return `pos_${f.tag || ''}_${f.page || 1}_${Number(f.x_percent).toFixed(2)}_${Number(f.y_percent).toFixed(2)}`;
};

// ─── Retour à la ligne des balises "texte libre" (2026-09-10) ──────────────────────────────────
// Découpe un texte pour tenir dans une case de largeur/hauteur données, à une taille de police
// donnée : sur "\n" (retours à la ligne tapés), puis mot par mot pour enrouler chaque ligne aux
// limites réelles de la case (avec découpage caractère par caractère d'un mot lui-même trop large,
// ex. texte tapé sans espaces). `widthOfTextAtSize` est injecté en paramètre plutôt que codé en dur
// (police pdf-lib Helvetica) : cette MÊME fonction sert à la fois PENDANT la saisie côté client
// (DocumentViewerModal, pour empêcher physiquement de taper plus que ce qui tiendra — avec une
// police Helvetica embarquée côté navigateur, voir measureFontRef) ET à la GRAVURE finale dans le
// PDF (overlayFieldsOnPdf). Avant ce correctif, ces deux endroits avaient chacun leur propre calcul
// de capacité (l'un une estimation par largeur moyenne de caractère, l'autre la vraie mesure
// pdf-lib) qui pouvaient diverger légèrement — d'où des coupures imprévisibles malgré un texte qui
// semblait tenir à l'écran. En partageant EXACTEMENT le même algorithme des deux côtés, un texte
// accepté à la saisie est GARANTI de tenir, sans coupure, dans le PDF final.
const wrapTextForBox = (text, widthOfTextAtSize, fs, maxTextW, maxLines) => {
  const breakLongWord = (word) => {
    if (widthOfTextAtSize(word, fs) <= maxTextW) return [word];
    const chunks = [];
    let cur = '';
    for (const ch of word) {
      const cand = cur + ch;
      if (cur && widthOfTextAtSize(cand, fs) > maxTextW) {
        chunks.push(cur);
        cur = ch;
      } else {
        cur = cand;
      }
    }
    if (cur) chunks.push(cur);
    return chunks.length > 0 ? chunks : [word];
  };
  const rawLines = String(text).split('\n');
  const wrappedLines = [];
  for (const rawLine of rawLines) {
    if (rawLine === '') { wrappedLines.push(''); continue; }
    const words = rawLine.split(' ');
    let current = '';
    for (const word of words) {
      const pieces = breakLongWord(word);
      pieces.forEach((piece, idx) => {
        const candidate = current ? (idx === 0 ? `${current} ${piece}` : `${current}${piece}`) : piece;
        if (current && widthOfTextAtSize(candidate, fs) > maxTextW) {
          wrappedLines.push(current);
          current = piece;
        } else {
          current = candidate;
        }
      });
    }
    wrappedLines.push(current);
  }
  const fits = wrappedLines.length <= maxLines;
  const visibleLines = wrappedLines.slice(0, maxLines);
  if (!fits) {
    let last = visibleLines[visibleLines.length - 1];
    while (last.length > 0 && widthOfTextAtSize(last + '…', fs) > maxTextW) {
      last = last.slice(0, -1);
    }
    visibleLines[visibleLines.length - 1] = (last || '') + '…';
  }
  return { lines: visibleLines, fits, totalLines: wrappedLines.length };
};

// ─── Signature séquentielle (Stage 3, 2026-07-24 ; généralisé à 3 parties le 2026-07-27) ────────
// Un modèle de document peut être configuré en 'sequentiel' (metadata.signing_mode, réglé une fois
// dans VisualTemplateEditor et copié sur chaque document instancié) : dans ce cas une partie ne peut
// signer qu'APRÈS celle qui la précède dans metadata.signing_order. Tant que ce n'est pas son tour,
// le document doit rester totalement invisible pour elle (masqué, pas juste désactivé) — décision
// produit du 2026-07-24. Fonction utilitaire partagée entre les espaces client/formateur/admin pour
// éviter de dupliquer cette règle à chaque endroit où une liste de documents à signer est construite.
//
// 2026-07-27 : un document peut désormais nécessiter la signature du client, du formateur ET/OU de
// l'organisme (n'importe quel admin de l'organisation), dans n'importe quelle combinaison. Les
// balises posées dans l'éditeur visuel suivent la convention <type>_<rôle> (signature_client,
// checkbox_formateur, texte_organisme...) ; ces helpers centralisent la correspondance balise/rôle
// et la lecture de destination/ordre de signature pour éviter des ternaires binaires client/
// formateur un peu partout (overlayFieldsOnPdf, handleSignDocument, instantiateDocument...).
const SIGNER_ROLES = ['client', 'formateur', 'organisme'];
const roleFromTag = (tag) => {
  if (!tag) return null;
  for (const r of SIGNER_ROLES) { if (tag.endsWith(`_${r}`)) return r; }
  return null;
};
const SIGNED_FLAG_COLUMN = { client: 'signe_par_client', formateur: 'signe_par_formateur', organisme: 'signe_par_organisme' };
const SIGNATURE_DATE_COLUMN = { client: 'date_signature_client', formateur: 'date_signature_formateur', organisme: 'date_signature_organisme' };
const SIGNATURE_IMAGE_COLUMN = { client: 'signature_client', formateur: 'signature_formateur', organisme: 'signature_organisme' };
// Couleur/libellé par rôle, utilisés à la fois dans overlayFieldsOnPdf (rendu PDF, couleurs rgb())
// et dans VisualTemplateEditor (rendu HTML, classes Tailwind) — bleu client / orange formateur /
// vert organisme, cohérent partout où une balise est affichée.
const ROLE_COLOR_RGB = { client: rgb(0.18, 0.42, 0.93), formateur: rgb(0.92, 0.49, 0.06), organisme: rgb(0.06, 0.6, 0.35) };
const ROLE_LABEL = { client: 'client', formateur: 'formateur', organisme: 'administrateur' };
// Libellé spécifique sous la signature (reprend le "bénéficiaire" déjà utilisé historiquement pour
// le client, plutôt que de changer ce texte sur les documents existants).
const ROLE_SIGNATURE_LABEL = { client: 'bénéficiaire', formateur: 'formateur', organisme: 'organisme' };

// destination stockée en base sous forme de chaîne "client,formateur,organisme" (ordre libre, rôles
// présents = destinataires). Valeurs héritées toujours comprises en lecture : 'client', 'formateur',
// 'both' (= client+formateur).
const parseDestinationRoles = (destination) => {
  if (!destination) return ['client'];
  if (destination === 'both') return ['client', 'formateur'];
  if (Array.isArray(destination)) return destination.filter(r => SIGNER_ROLES.includes(r));
  const roles = String(destination).split(',').map(s => s.trim()).filter(r => SIGNER_ROLES.includes(r));
  return roles.length ? roles : ['client'];
};
const stringifyDestinationRoles = (roles) => SIGNER_ROLES.filter(r => (roles || []).includes(r)).join(',');

// signing_order stocké en metadata sous forme de tableau de rôles dans l'ordre de signature (nouveau
// format) — ou chaîne héritée 'client_first' | 'formateur_first' (Stage 3, comprise en lecture seule).
const normalizeSigningOrder = (meta) => {
  if (Array.isArray(meta?.signing_order) && meta.signing_order.length) return meta.signing_order;
  if (meta?.signing_order === 'formateur_first') return ['formateur', 'client'];
  return ['client', 'formateur']; // valeur par défaut / 'client_first' hérité
};

const parseDocMetadata = (doc) => {
  const m = doc?.metadata;
  if (m && typeof m === 'object') return m;
  if (typeof m === 'string' && m.startsWith('{')) { try { return JSON.parse(m); } catch { return {}; } }
  return {};
};
const isBlockedBySigningOrder = (doc, role) => {
  const meta = parseDocMetadata(doc);
  if (meta.signing_mode !== 'sequentiel') return false; // mode simultané (défaut) : jamais masqué
  const order = normalizeSigningOrder(meta);
  const myIndex = order.indexOf(role);
  if (myIndex <= 0) return false; // pas concerné par l'ordre, ou premier de l'ordre : jamais masqué
  for (let i = 0; i < myIndex; i++) {
    const flagCol = SIGNED_FLAG_COLUMN[order[i]];
    if (flagCol && !doc[flagCol]) return true; // une partie précédente n'a pas encore signé
  }
  return false;
};

// FIX (2026-09-04) : détermine les rôles dont la signature est réellement requise sur CE document —
// d'après les balises signature_<rôle> posées dessus (même repli que handleSignDocument : metadata
// .signature_fields → metadata.template_fields → metadata.fields → table technique template_fields
// en tout dernier recours). Sert à savoir quand un document peut légitimement passer au statut
// "Signé" (= tous les rôles requis ont signé), plutôt que dès la toute première signature reçue.
const getRequiredSignerRoles = async (docLike, supabaseClient) => {
  const meta = parseDocMetadata(docLike);
  let candidateFields = (Array.isArray(meta.signature_fields) && meta.signature_fields.length > 0) ? meta.signature_fields
    : (Array.isArray(meta.template_fields) && meta.template_fields.length > 0) ? meta.template_fields
    : (Array.isArray(meta.fields) ? meta.fields : []);
  if (candidateFields.length === 0 && docLike?.template_id && supabaseClient) {
    try {
      const { data: dbFields } = await supabaseClient.from('template_fields').select('*').eq('template_id', docLike.template_id);
      if (dbFields && dbFields.length > 0) candidateFields = dbFields;
    } catch (e) { /* non-bloquant */ }
  }
  const roles = new Set();
  candidateFields.forEach(f => { if ((f.tag || '').startsWith('signature_')) { const r = roleFromTag(f.tag); if (r) roles.add(r); } });
  // Aucune balise signature du tout → document hors du système de balises visuelles (ancien format) :
  // comportement historique, une seule signature client attendue.
  if (roles.size === 0) roles.add('client');
  return Array.from(roles);
};
// mergedDoc = document avec les colonnes signe_par_* déjà mises à jour (état APRÈS la signature en cours).
const isDocFullySigned = (mergedDoc, requiredRoles) => (requiredRoles || []).every(r => !!mergedDoc[SIGNED_FLAG_COLUMN[r]]);

// Types "dossier" (documents administratifs simples, ajoutés librement, SANS signature) — catégorie
// PARTAGÉE entre le dossier d'un client (ClientDetailView) et celui d'un formateur (FormateurDetailView
// côté admin / FormateurView côté formateur) : un document affiché dans un "dossier" doit avoir l'un de
// ces types ET ne pas déjà être un document signé archivé (qui, lui, s'affiche dans "Documents Signés").
const DOSSIER_DOC_TYPES = ['Administratif', 'Contrat', 'Mission', 'Pièce justificative', 'Autre'];
const isDossierDoc = (d) => DOSSIER_DOC_TYPES.includes(d?.type_document) && !(d.statut === 'Signé') && !d?.visible_client;
// AJOUT (2026-10-05) : un document envoyé au client attend-il vraiment une signature ? Demandé : "j'envoie
// certains documents juste pour que le client les garde et les télécharge, mais ça met quand même 'en
// attente de signature'". Ordre de décision :
//  1. metadata.signature_requise (écrit à l'envoi depuis le 2026-10-05) fait foi ;
//  2. type "À signer" → oui ; type "Téléchargeable" → non ;
//  3. documents envoyés AVANT ce correctif : un document "Administratif" / "Pièce justificative" sans
//     aucune balise de signature ni modèle de signature → simple document à consulter / télécharger ;
//  4. dans le doute (anciens contrats, etc.) → oui, comme avant.
const docRequiresSignature = (doc) => {
  const meta = parseDocMetadata(doc);
  if (typeof meta.signature_requise === 'boolean') return meta.signature_requise;
  const fields = [
    ...(Array.isArray(meta.signature_fields) ? meta.signature_fields : []),
    ...(Array.isArray(meta.template_fields) ? meta.template_fields : []),
    ...(Array.isArray(meta.fields) ? meta.fields : []),
  ];
  const hasSignTag = fields.some(f => /^(signature|checkbox|texte)_/.test(f?.tag || ''));
  // FIX (2026-10-05, constaté en base) : les documents ajoutés via "Ajouter" (fiche client) gardaient la case
  // "Signature client" cochée PAR DÉFAUT → enregistrés "À signer" avec documentType = 'info'. Quand on coche
  // volontairement cette case, documentType passe à 'signature' : 'info' signale donc un simple document à
  // consulter, sauf s'il porte de vraies balises de signature ou est classé "à signer".
  if (meta.documentType === 'info' && meta.classification !== 'a_signer' && !hasSignTag) return false;
  if (doc?.type_document === 'À signer') return true;
  if (doc?.type_document === 'Téléchargeable') return false;
  if (['Administratif', 'Pièce justificative'].includes(doc?.type_document)) {
    if (!hasSignTag && !doc?.template_id && meta.requiresClientSignature !== true) return false;
  }
  return true;
};
// Le client doit-il encore signer ce document ?
const docAwaitsClientSignature = (doc) => !!doc?.visible_client && !doc?.signe_par_client && docRequiresSignature(doc);
// Le formateur doit-il encore signer ce document ?
const docAwaitsFormateurSignature = (doc) => !!doc?.visible_formateur && !doc?.signe_par_formateur && docRequiresSignature(doc);
// AJOUT (2026-10-02) : document INTERNE du dossier client (échangé entre l'organisme et le formateur :
// factures, justificatifs...) — jamais destiné au client. Depuis la migration
// documents_internes_client_migration.sql, la base ne les renvoie même plus au client ; ce test sert de
// seconde barrière côté écran (ex. ne jamais ouvrir un tel fichier dans la signature du client si son nom
// coïncide avec celui d'un document du module).
const isInternalDossierDoc = (d) => DOSSIER_DOC_TYPES.includes(d?.type_document) && !d?.visible_client;

// ─── Synchronisation Google Agenda (2026-07-27) ──────────────────────────────
// Déclenchée "best-effort" (non bloquante, sans afficher d'erreur) à chaque fois qu'une séance
// est créée/modifiée (date, heure, lien visio, note...). Le serveur (api/calendar/sync-seance)
// vérifie lui-même si le client et/ou son formateur ont connecté leur agenda Google — si
// personne n'est connecté, l'appel ne fait rien. Voir ProfileView pour l'écran de connexion.
const syncSeanceToCalendar = (clientId, numeroSeance) => {
  if (!clientId || numeroSeance === undefined || numeroSeance === null) return;
  // SÉCURITÉ (2026-10-12) : le serveur exige maintenant d'être connecté et autorisé sur ce client.
  supabase.auth.getSession().then(({ data }) => {
    const accessToken = data?.session?.access_token;
    if (!accessToken) return;
    fetch('/api/calendar/sync-seance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ clientId, numeroSeance }),
    }).catch(() => {});
  }).catch(() => {});
};

// LOT B (2026-10-21) : une date ou un horaire modifié met à jour Google Agenda. Les modifications
// rapprochées d'une même séance (date posée sur chaque élément, horaires tapés) partent en un seul envoi.
const _agendaEnAttente = {};
const planifierSyncAgenda = (clientId, numeroSeance) => {
  if (!clientId || numeroSeance === undefined || numeroSeance === null) return;
  const cle = `${clientId}|${numeroSeance}`;
  clearTimeout(_agendaEnAttente[cle]);
  _agendaEnAttente[cle] = setTimeout(() => { delete _agendaEnAttente[cle]; syncSeanceToCalendar(clientId, numeroSeance); }, 1500);
};

const EmargementModal = ({ isOpen, onClose, onSave, sessionTitle, signerRole = 'formateur' }) => {
  const fCanvasRef = useRef(null);
  const cCanvasRef = useRef(null);
  const [fDrawing, setFDrawing] = useState(false);
  const [cDrawing, setCDrawing] = useState(false);
  const isClient = signerRole === 'client';
  // SIGNATURES (2026-10-20) : un seul envoi à la fois (avant : double clic = séance comptée 2 fois).
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const envoiRef = useRef(false);
  useEffect(() => { envoiRef.current = false; setEnvoiEnCours(false); }, [isOpen]);

  // FIX (2026-09-14, v2) : la 1ère correction (ratio canvas.width/rect.width appliqué aux coordonnées
  // souris) n'a pas suffi — une formatrice a de nouveau signalé un décalage net et constant ("ma
  // souris était bien à 5 cm sur la gauche"). Le calcul du ratio était correct en théorie, mais cette
  // approche reste fragile (elle dépend de rect.width étant lu au bon moment, jamais 0, etc.).
  // Remplacé par la technique standard des pads de signature (ex. lib signature_pad) : on fait
  // correspondre la RÉSOLUTION INTERNE du canvas à sa taille RÉELLEMENT affichée à l'écran, en
  // pixels PHYSIQUES (× devicePixelRatio, pour un tracé net sur écran HiDPI/Retina — bonus : la
  // signature enregistrée est aussi en meilleure résolution qu'avant, qui était figée à 280×150),
  // puis on applique ctx.scale(dpr, dpr) sur le contexte. À partir de là, 1 unité de dessin = 1 pixel
  // CSS exactement : getCoords utilise directement (clientX/clientY - rect.left/top), sans AUCUN
  // calcul de ratio qui pourrait se tromper. On recalcule aussi si la fenêtre est redimensionnée
  // pendant que la modale est ouverte (le tracé en cours est alors effacé — cas rare, préférable à
  // un décalage silencieux).
  useEffect(() => {
    if (!isOpen) return;
    const setup = (canvas) => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#0f172a';
    };
    const setupAll = () => {
      if (!isClient) setup(fCanvasRef.current);
      setup(cCanvasRef.current);
    };
    setupAll();
    window.addEventListener('resize', setupAll);
    return () => window.removeEventListener('resize', setupAll);
  }, [isOpen, isClient]);

  const getCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches?.length > 0) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const fStart = (e) => { if (e.touches) e.preventDefault(); setFDrawing(true); const ctx = fCanvasRef.current.getContext('2d'); const { x, y } = getCoords(e, fCanvasRef.current); ctx.beginPath(); ctx.moveTo(x, y); };
  const fMove  = (e) => { if (!fDrawing) return; if (e.touches) e.preventDefault(); const ctx = fCanvasRef.current.getContext('2d'); const { x, y } = getCoords(e, fCanvasRef.current); ctx.lineTo(x, y); ctx.stroke(); };
  const fStop  = () => setFDrawing(false);

  const cStart = (e) => { if (e.touches) e.preventDefault(); setCDrawing(true); const ctx = cCanvasRef.current.getContext('2d'); const { x, y } = getCoords(e, cCanvasRef.current); ctx.beginPath(); ctx.moveTo(x, y); };
  const cMove  = (e) => { if (!cDrawing) return; if (e.touches) e.preventDefault(); const ctx = cCanvasRef.current.getContext('2d'); const { x, y } = getCoords(e, cCanvasRef.current); ctx.lineTo(x, y); ctx.stroke(); };
  const cStop  = () => setCDrawing(false);

  const clearCanvas = (ref) => { const c = ref.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); };

  const isEmpty = (canvas) => {
    if (!canvas) return true;
    return !Array.from(canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data).some(v => v !== 0);
  };

  const handleSave = async () => {
    if (envoiRef.current) return;
    let fSig = null, cSig = null;
    if (isClient) {
      if (isEmpty(cCanvasRef.current)) { toast.error('Votre signature est requise.'); return; }
      cSig = cCanvasRef.current.toDataURL('image/png');
    } else {
      if (isEmpty(fCanvasRef.current)) { toast.error('La signature du formateur est requise.'); return; }
      // Le formateur ne peut pas signer à la place du client (légal)
      fSig = fCanvasRef.current.toDataURL('image/png');
    }
    envoiRef.current = true; setEnvoiEnCours(true);
    try { await onSave(fSig, cSig); }
    finally { envoiRef.current = false; setEnvoiEnCours(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className={`bg-white rounded-3xl shadow-2xl p-6 md:p-8 w-full border border-gray-100 ${isClient ? 'max-w-lg' : 'max-w-2xl'}`}>
        <h3 className="text-xl font-extrabold text-gray-900 mb-1">{isClient ? 'Signer ma présence' : 'Émargement de présence'}</h3>
        {sessionTitle && <p className="text-sm text-indigo-600 font-bold mb-3">{sessionTitle}</p>}
        <p className="text-sm text-gray-500 mb-6">{isClient ? 'Signez ci-dessous pour valider votre présence.' : 'Signez dans les cadres ci-dessous pour valider la présence.'}</p>
        <div className="grid gap-6 mb-6 grid-cols-1">
          {!isClient && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-black uppercase tracking-wider text-gray-600">Signature du formateur <span className="text-violet-600">*</span></span>
                <button onClick={() => clearCanvas(fCanvasRef)} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Effacer</button>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden bg-gray-50 touch-none relative">
                <canvas ref={fCanvasRef} width={280} height={150} className="w-full h-[150px] cursor-crosshair"
                  onMouseDown={fStart} onMouseMove={fMove} onMouseUp={fStop} onMouseOut={fStop}
                  onTouchStart={fStart} onTouchMove={fMove} onTouchEnd={fStop} />
                <div className="absolute bottom-2 right-2 opacity-20 pointer-events-none text-xs font-bold uppercase tracking-widest text-gray-500">Signer ici</div>
              </div>
              <p className="text-xs text-amber-600 mt-2 font-medium">⚠️ Le bénéficiaire doit signer lui-même depuis son espace.</p>
            </div>
          )}
          {isClient && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-black uppercase tracking-wider text-gray-600">Votre signature <span className="text-violet-600">*</span></span>
                <button onClick={() => clearCanvas(cCanvasRef)} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Effacer</button>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden bg-gray-50 touch-none relative">
                <canvas ref={cCanvasRef} width={280} height={150} className="w-full h-[220px] sm:h-[170px] cursor-crosshair"
                  onMouseDown={cStart} onMouseMove={cMove} onMouseUp={cStop} onMouseOut={cStop}
                  onTouchStart={cStart} onTouchMove={cMove} onTouchEnd={cStop} />
                <div className="absolute bottom-2 right-2 opacity-20 pointer-events-none text-xs font-bold uppercase tracking-widest text-gray-500">Signer ici</div>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between gap-3">
          <button onClick={onClose} className="px-5 py-3 text-gray-700 font-bold hover:bg-gray-100 rounded-xl transition-colors">Annuler</button>
          <button onClick={handleSave} disabled={envoiEnCours} className="px-6 py-3 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-700 transition-colors shadow-lg disabled:opacity-50">{envoiEnCours ? 'Enregistrement…' : (isClient ? 'Valider ma signature' : "Valider l'émargement")}</button>
        </div>
      </div>
    </div>
  );
};

// Modale PDF polyvalente:
// - mode "view" => lecture simple (iframe)
// - mode "sign" => lecture obligatoire + canvas de signature en bas
/**
 * Convertit un Blob DOCX en Blob PDF via la fonction serverless /api/convert/docx-to-pdf.
 * Conversion 100% auto-hébergée côté serveur (mammoth + Chromium headless embarqué, voir
 * api/_lib/docxToPdf.js) — aucune plateforme externe, aucune clé API tierce à gérer (l'ancienne
 * intégration CloudConvert a été retirée le 2026-09-02, à la demande explicite de l'organisme de
 * ne dépendre d'aucun service tiers pour ses documents).
 * Lance une erreur si l'appel échoue (le fallback local convertDocxBlobToPdfLocal prend le relais).
 */
const convertDocxBlobToPdf = async (docxBlob) => {
  // Encoder le DOCX en base64 pour l'envoi inline au serverless (évite les problèmes CORS S3)
  const base64Data = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(docxBlob);
  });

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Session invalide — reconnectez-vous pour convertir ce document.');

  const response = await fetch('/api/convert/docx-to-pdf', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ docxBase64: base64Data }),
  });

  if (!response.ok) {
    let errMsg = `Conversion serveur échouée: ${response.status}`;
    try { const errJson = await response.json(); if (errJson?.error) errMsg = errJson.error; } catch (_) {}
    throw new Error(errMsg);
  }

  const pdfArrayBuffer = await response.arrayBuffer();
  return new Blob([pdfArrayBuffer], { type: 'application/pdf' });
};

/**
 * Conversion DOCX → PDF entièrement côté client (sans API externe).
 * docx-preview rend le DOCX en HTML, html2canvas capture en images,
 * jsPDF assemble le PDF page par page.
 */
const convertDocxBlobToPdfLocal = async (docxBlob) => { /* 2026-10-21 : chargé à la demande */ const html2canvas = (await import('html2canvas')).default; const { jsPDF } = await import('jspdf');
  const { renderAsync } = await import('docx-preview');

  // Conteneur caché — largeur auto pour que docx-preview respecte les marges Word
  const container = document.createElement('div');
  container.style.cssText = [
    'position:fixed', 'top:-99999px', 'left:-99999px',
    'background:#fff', 'overflow:visible', 'width:auto',
  ].join(';');

  // CSS correctifs pour améliorer le rendu docx-preview
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    .docx-wrapper { background: #fff !important; padding: 0 !important; }
    .docx-wrapper > article {
      background: #fff !important;
      box-shadow: none !important;
      margin: 0 !important;
    }
    table { border-collapse: collapse !important; table-layout: fixed !important; }
    td, th { word-wrap: break-word !important; overflow-wrap: break-word !important; }
    img { max-width: 100% !important; }
  `;
  container.appendChild(styleEl);
  document.body.appendChild(container);

  try {
    await renderAsync(docxBlob, container, null, {
      className: 'docx-preview',
      inWrapper: true,
      ignoreWidth: false,   // respecte la largeur de page définie dans le Word
      ignoreHeight: false,
      ignoreFonts: false,
      breakPages: true,
      useBase64URL: true,   // images embarquées
      renderChanges: false,
      renderHeaders: true,
      renderFooters: true,
    });

    // Attendre polices, images et mise en page
    await new Promise(r => setTimeout(r, 1200));

    const A4_W_MM = 210;
    const A4_H_MM = 297;
    const pdf = new jsPDF('p', 'mm', 'a4');

    // docx-preview v0.3.x : .docx-wrapper > article (une par page)
    const wrapper = container.querySelector('.docx-wrapper');
    const pageEls = wrapper
      ? Array.from(wrapper.querySelectorAll(':scope > article'))
      : Array.from(container.querySelectorAll('article'));
    const targets = pageEls.length > 0 ? pageEls : [container];

    for (let i = 0; i < targets.length; i++) {
      const el = targets[i];
      // Lire les dimensions réelles rendues par docx-preview
      const elW = Math.max(el.scrollWidth, el.offsetWidth, 794);
      const elH = Math.max(el.scrollHeight, el.offsetHeight, 100);

      const canvas = await html2canvas(el, {
        scale: 2,              // bon équilibre qualité/taille
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        allowTaint: true,
        width: elW,
        height: elH,
        windowWidth: elW,     // évite les débordements de tableaux
        scrollX: 0,
        scrollY: 0,
      });

      // JPEG qualité 0.88 — 5× plus léger que PNG, texte lisible
      const imgData = canvas.toDataURL('image/jpeg', 0.88);
      const ratio = canvas.height / canvas.width;
      const imgH = A4_W_MM * ratio;

      if (i > 0) pdf.addPage();

      if (imgH <= A4_H_MM) {
        pdf.addImage(imgData, 'JPEG', 0, 0, A4_W_MM, imgH);
      } else {
        // Page plus haute qu'une feuille A4 → découper en tranches
        const pixPerMm = canvas.width / A4_W_MM;
        const pagePixH = Math.round(A4_H_MM * pixPerMm);
        let yOff = 0;
        let firstSlice = true;
        while (yOff < canvas.height) {
          const sliceH = Math.min(pagePixH, canvas.height - yOff);
          const sc = document.createElement('canvas');
          sc.width = canvas.width; sc.height = sliceH;
          sc.getContext('2d').drawImage(canvas, 0, yOff, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
          const sliceImg = sc.toDataURL('image/jpeg', 0.88);
          const sliceHMm = sliceH / pixPerMm;
          if (i > 0 || !firstSlice) pdf.addPage();
          pdf.addImage(sliceImg, 'JPEG', 0, 0, A4_W_MM, sliceHMm);
          yOff += sliceH;
          firstSlice = false;
        }
      }
    }

    return new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
  } finally {
    document.body.removeChild(container);
  }
};

/**
 * overlayFieldsOnPdf — superpose les valeurs des balises sur un PDF avec pdf-lib.
 * @param {Blob} pdfBlob — PDF source
 * @param {Array} templateFields — [{page, x_percent, y_percent, font_size, tag}]
 * @param {Object} dataValues — {nomcomplet_client: "...", ...}
 * @returns {Blob} PDF modifié
 */
const overlayFieldsOnPdf = async (pdfBlob, templateFields, dataValues, signaturesMap = {}, checkedMap = {}, textInputMap = {}) => { /* 2026-10-21 : chargé à la demande */ const { PDFDocument } = await import('pdf-lib');
  // signaturesMap = { signature_client: dataUrl, signature_formateur: dataUrl }
  // checkedMap = { [field.id]: true } — état coché des cases à cocher, par identifiant de champ
  //   (une même balise checkbox_client/checkbox_formateur peut être posée plusieurs fois : on
  //   distingue donc chaque case par son id, pas par son tag).
  // textInputMap = { [fieldKey(field)]: 'texte saisi par le signataire' } — même principe que
  //   checkedMap : une balise texte_client/texte_formateur peut être posée plusieurs fois, donc
  //   on distingue chaque champ par fieldKey(field), pas par son tag.
  const pdfDoc = await PDFDocument.load(await pdfBlob.arrayBuffer(), { ignoreEncryption: true });
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const pages = pdfDoc.getPages();

  for (const field of templateFields) {
    const pageIndex = (field.page || 1) - 1;
    if (pageIndex >= pages.length) continue;
    const page = pages[pageIndex];
    const { width: pageW, height: pageH } = page.getSize();
    const cx = (field.x_percent / 100) * pageW;
    const cy = pageH - (field.y_percent / 100) * pageH; // PDF Y=0 en bas
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
      // Coordonnées invalides (x_percent/y_percent manquants ou corrompus) → on ignore ce champ
      // plutôt que de risquer de faire échouer tout le document pour un seul champ mal formé.
      console.warn(`[overlayFieldsOnPdf] Champ ${field.tag || field.id} ignoré — coordonnées invalides`, field.x_percent, field.y_percent);
      continue;
    }
    const isSignature = field.field_type === 'signature' || (field.tag || '').startsWith('signature_');
    const isCheckbox = field.field_type === 'checkbox' || (field.tag || '').startsWith('checkbox_');
    const isTextInput = field.field_type === 'text_input' || (field.tag || '').startsWith('texte_');

    if (isTextInput) {
      // Champ à remplir librement par le signataire (client ou formateur, façon Yousign) — la valeur
      // n'est connue qu'au moment de signer, transmise via textInputMap (une même balise texte_client/
      // texte_formateur peut être posée plusieurs fois, d'où l'indexation par fieldKey, pas par tag).
      // FIX (2026-09-04) : largeur/hauteur réglables depuis l'éditeur visuel (poignée de
      // redimensionnable) — repli sur les valeurs historiques (28% de la page / une ligne de texte)
      // quand le champ n'a pas encore été redimensionné (modèles existants, ou balise placée avant
      // ce correctif).
      const boxW = typeof field.width_percent === 'number' ? (field.width_percent / 100) * pageW : pageW * 0.28;
      // FIX (2026-09-10, round 2) : 12pt par défaut (au lieu de 10pt) pour correspondre exactement à
      // la taille utilisée par la zone de saisie à l'écran (pageTexts, fontSize: 12) — un texte tapé
      // ne doit pas apparaître "beaucoup plus petit" une fois gravé dans le PDF final (retour
      // utilisateur). `nominalFs` sert aussi de point de départ au "rétrécissement automatique"
      // ci-dessous quand le texte est trop long pour la case à cette taille.
      const nominalFs = field.font_size || 12;
      const boxH = typeof field.height_percent === 'number' ? (field.height_percent / 100) * pageH : Math.round(nominalFs * 1.35) + 2;
      // Ancrée à GAUCHE exactement sur le point cliqué dans l'éditeur (bx = cx), plutôt que centrée
      // autour de ce point (bx = cx - boxW/2 comme avant) : le texte tapé commence maintenant pile là
      // où la balise a été posée, ce qui était la source persistante de confusion sur "où le texte va
      // apparaître" (retour utilisateur du 2026-07-24, à deux reprises). Le marqueur dans l'éditeur
      // (VisualTemplateEditor) est ancré de la même façon pour que ce que l'admin voit corresponde
      // exactement à ce que produit ce rendu.
      const bx = Math.max(0, cx);
      const by = Math.max(0, cy - boxH / 2);
      const typedValue = textInputMap[fieldKey(field)];
      try {
        if (typedValue) {
          // FIX (2026-09-10) : la case de saisie à l'écran (pageTexts) accepte désormais plusieurs
          // lignes (touche Entrée) et enroule automatiquement le texte trop long — mais cette gravure
          // finale dans le PDF ne dessinait encore qu'UNE seule ligne, en mesurant la chaîne ENTIÈRE
          // (retours à la ligne "\n" compris) avec font.widthOfTextAtSize puis un unique page.drawText().
          // pdf-lib ne sait pas interpréter "\n" à l'intérieur d'une chaîne : dès que le texte tapé en
          // contenait un (donc dès qu'on écrivait plus d'une ligne), ça faisait échouer le calcul/dessin
          // — erreur silencieusement avalée par le catch ci-dessous, et le champ entier disparaissait du
          // PDF final. C'est exactement ce qui rendait les cases "Votre demande s'énonce ainsi"/"Nous
          // allons centrer notre travail sur" (larges, redimensionnées pour plusieurs lignes) invisibles
          // dans le document final, alors que les petites cases à une ligne (ex. le calendrier
          // prévisionnel, jamais redimensionnées) continuaient de fonctionner.
          const textStr = String(typedValue);
          const tx = bx + 3;
          const maxTextW = Math.max(4, boxW - 6);
          const fs = nominalFs;
          const lineHeight = fs * 1.25;
          // FIX (2026-09-10, round 4) : utilise désormais EXACTEMENT la même fonction de découpage
          // (wrapTextForBox, définie une seule fois en haut du fichier) que la zone de saisie côté
          // client — voir DocumentViewerModal/handleTextFieldChange. Les deux côtés utilisaient
          // auparavant des calculs de capacité légèrement différents (l'un une estimation par
          // largeur moyenne de caractère, l'autre la vraie mesure pdf-lib), ce qui provoquait des
          // coupures imprévisibles ici malgré un texte qui semblait tenir à l'écran. En partageant
          // le même algorithme ET la même police (Helvetica) des deux côtés, un texte accepté à la
          // saisie est garanti de tenir, sans coupure, dans ce rendu final.
          const maxLines = Math.max(1, Math.floor(boxH / lineHeight));
          const { lines: visibleLines } = wrapTextForBox(textStr, (s, sz) => font.widthOfTextAtSize(s, sz), fs, maxTextW, maxLines);
          const topY = by + boxH - fs;
          visibleLines.forEach((line, i) => {
            if (!line) return;
            page.drawText(line, {
              x: tx, y: topY - i * lineHeight,
              size: fs, font, color: rgb(0.1, 0.1, 0.1),
            });
          });
        }
        // Pas encore rempli (2026-09-03, suite) : on ne dessine plus rien du tout — plus de cadre ni de
        // libellé "Texte libre ..." gravé dans le PDF final. Champ optionnel non rempli = totalement
        // invisible pour le lecteur du document (retour utilisateur explicite).
      } catch (e) {
        console.warn('[overlayFieldsOnPdf] Erreur champ texte libre:', e.message);
      }
      continue;
    }

    if (isCheckbox) {
      // Case à cocher sans texte : simple carré, coché = rempli + coche blanche, décoché = contour seul.
      const boxSize = 12;
      const bx = cx - boxSize / 2;
      const by = cy - boxSize / 2;
      const bc = ROLE_COLOR_RGB[roleFromTag(field.tag) || 'client'];
      const isChecked = !!checkedMap[fieldKey(field)];
      try {
        if (isChecked) {
          page.drawRectangle({ x: bx, y: by, width: boxSize, height: boxSize, color: bc, borderColor: bc, borderWidth: 1 });
          // Coche (✓) en blanc par-dessus le carré rempli
          page.drawLine({ start: { x: bx + boxSize * 0.2, y: by + boxSize * 0.5 }, end: { x: bx + boxSize * 0.42, y: by + boxSize * 0.25 }, thickness: 1.6, color: rgb(1, 1, 1) });
          page.drawLine({ start: { x: bx + boxSize * 0.42, y: by + boxSize * 0.25 }, end: { x: bx + boxSize * 0.82, y: by + boxSize * 0.78 }, thickness: 1.6, color: rgb(1, 1, 1) });
        } else {
          page.drawRectangle({ x: bx, y: by, width: boxSize, height: boxSize, borderColor: bc, borderWidth: 1, opacity: 0.7 });
        }
      } catch (e) {
        console.warn('[overlayFieldsOnPdf] Erreur case à cocher:', e.message);
      }
      continue;
    }

    if (isSignature) {
      const sigW = pageW * 0.22;
      const sigH = 44;
      const bx = Math.max(0, cx - sigW / 2);
      const by = Math.max(0, cy - sigH / 2);
      const sigFieldRole = roleFromTag(field.tag) || 'client';
      // Couleur : bleu pour client, orange pour formateur, vert pour organisme
      const bc = ROLE_COLOR_RGB[sigFieldRole];

      const sigDataUrl = signaturesMap[field.tag];
      if (sigDataUrl) {
        // Signature réelle déjà disponible → on l'incrust
        try {
          const sigBytes = await fetch(sigDataUrl).then(r => r.arrayBuffer());
          const sigImg = await pdfDoc.embedPng(sigBytes).catch(async () => {
            // Si PNG échoue, essayer JPG
            return await pdfDoc.embedJpg(sigBytes);
          });
          const aspect = sigImg.width / sigImg.height;
          const drawW = Math.min(sigW, sigImg.width);
          const drawH = drawW / aspect;
          // Masque tout résidu (cadre placeholder d'un ancien passage) avant de dessiner la vraie signature
          page.drawRectangle({ x: bx - 2, y: by - 2, width: sigW + 4, height: sigH + 4, color: rgb(1, 1, 1), opacity: 1 });
          page.drawImage(sigImg, { x: bx, y: by + sigH - drawH, width: drawW, height: drawH });
          // Ligne de séparation + label
          page.drawLine({ start: { x: bx, y: by }, end: { x: bx + sigW, y: by }, thickness: 0.5, color: bc, opacity: 0.6 });
          page.drawText(`Signature ${ROLE_SIGNATURE_LABEL[sigFieldRole]}`, {
            x: bx, y: by - 9, size: 6.5, font, color: rgb(0.4, 0.4, 0.4),
          });
        } catch (e) {
          console.warn('[overlayFieldsOnPdf] Erreur incrust. signature:', e.message);
        }
      } else {
        // Placeholder : rectangle pointillé
        try {
          page.drawRectangle({ x: bx, y: by, width: sigW, height: sigH, borderColor: bc, borderWidth: 0.8, opacity: 0.5 });
          const label = `Signature ${ROLE_SIGNATURE_LABEL[sigFieldRole]}`;
          page.drawText(label, {
            x: bx + 4, y: by + sigH / 2 - 3, size: 7.5, font, color: bc, opacity: 0.7,
          });
        } catch (e) {
          console.warn('[overlayFieldsOnPdf] Erreur placeholder signature:', e.message);
        }
      }
    } else {
      // Balise texte (y compris date_du_jour)
      const value = String(dataValues[field.tag] || '');
      if (!value) continue;
      try {
        page.drawText(value, {
          x: Math.max(0, cx),
          y: Math.max(0, cy),
          size: field.font_size || 11,
          font,
          color: rgb(0, 0, 0),
          maxWidth: pageW * 0.85,
        });
      } catch (e) {
        console.warn(`[overlayFieldsOnPdf] Balise ${field.tag} ignorée :`, e.message);
      }
    }
  }
  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
};

const DocumentViewerModal = ({ isOpen, onClose, document, url, title, mode = 'view', onSave, isInteractiveConsent = false, requiredCheckboxes = [], requiredTextFields = [], requiresSignature = true, supabase: passedSupabase }) => {
  // On utilise le supabase passé en prop s'il existe, sinon le global
  const activeSupabase = passedSupabase || supabase;
  const [hasRead, setHasRead] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const scrollRef = useRef(null);
  const signatureSectionRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  // Détail technique gardé pour le diagnostic, dans la console uniquement (plus affiché à l'écran).
  useEffect(() => { if (debugInfo) console.warn('[DocumentViewerModal] diagnostic :', debugInfo); }, [debugInfo]);
  const sigCanvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef(null);
  const [hasSig, setHasSig] = useState(false);
  // SIGNATURES (2026-10-20) : un seul envoi à la fois (avant : double clic = double enregistrement).
  const [enregistrementSig, setEnregistrementSig] = useState(false);
  const enregistrementSigRef = useRef(false);
  useEffect(() => { enregistrementSigRef.current = false; setEnregistrementSig(false); }, [isOpen, url]);
  const [documentChoice, setDocumentChoice] = useState(null); // 'autorise' | 'refuse' | null
  const [checkedBoxIds, setCheckedBoxIds] = useState(() => new Set());
  // Au moins UNE case cochée suffit à débloquer la signature (pas besoin de toutes les cocher).
  const allRequiredChecked = requiredCheckboxes.length === 0 || requiredCheckboxes.some(f => checkedBoxIds.has(fieldKey(f)));
  const toggleRequiredCheckbox = (fieldId) => {
    setCheckedBoxIds(prev => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId); else next.add(fieldId);
      return next;
    });
  };
  // ── Balises "texte libre" appartenant à ce signataire : saisie directement sur le document ──
  const [textFieldValues, setTextFieldValues] = useState({});
  const setTextFieldValue = (fieldId, value) => {
    setTextFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };
  const allRequiredTextFilled = requiredTextFields.length === 0 || requiredTextFields.every(f => (textFieldValues[fieldKey(f)] || '').trim().length > 0);
  // FIX (2026-09-10, round 4) : police Helvetica embarquée côté navigateur (pdf-lib fonctionne aussi
  // en client, cette app l'utilise déjà pour bâtir le PDF final) — sert UNIQUEMENT à mesurer la
  // largeur du texte tapé (widthOfTextAtSize), avec la MÊME fonction wrapTextForBox que la gravure
  // finale (overlayFieldsOnPdf), pour savoir EXACTEMENT ce qui tiendra dans la case, sans estimation
  // approximative. Chargée une seule fois à l'ouverture en mode signature.
  const measureFontRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    if (mode !== 'sign' || !isOpen) return; // 2026-10-21 : pas au démarrage de l'application
    (async () => { /* 2026-10-21 : chargé à la demande */ const { PDFDocument } = await import('pdf-lib');
      try {
        const measureDoc = await PDFDocument.create();
        const f = await measureDoc.embedFont(StandardFonts.Helvetica);
        if (!cancelled) measureFontRef.current = f;
      } catch (e) {
        console.warn('[DocumentViewerModal] Police de mesure indisponible pour la saisie texte libre :', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, isOpen]);
  // Calcule, pour un champ texte libre donné (sur une page dont on connaît les dimensions RÉELLES
  // en points), si une nouvelle valeur tiendrait dans la case sans être tronquée dans le PDF final —
  // et si oui seulement, met à jour l'état. Une frappe qui ferait déborder la case est ignorée
  // plutôt qu'acceptée puis silencieusement coupée plus tard à la signature (retour utilisateur :
  // "il faut que ce soit exactement pareil quand le client reçoit le document").
  const handleTextFieldChange = (f, pg, newValue) => {
    const k = fieldKey(f);
    const font = measureFontRef.current;
    if (!font) { setTextFieldValue(k, newValue); return; }
    const pageWidthPt = pg.pageWidthPt || 595;
    const pageHeightPt = pg.pageHeightPt || 842;
    const fs = 12;
    const boxWpt = typeof f.width_percent === 'number' ? (f.width_percent / 100) * pageWidthPt : pageWidthPt * 0.28;
    const boxHpt = typeof f.height_percent === 'number' ? (f.height_percent / 100) * pageHeightPt : Math.round(fs * 1.35) + 2;
    const maxTextWpt = Math.max(4, boxWpt - 6);
    const maxLines = Math.max(1, Math.floor(boxHpt / (fs * 1.25)));
    const { fits } = wrapTextForBox(newValue, (s, sz) => font.widthOfTextAtSize(s, sz), fs, maxTextWpt, maxLines);
    if (!fits) return; // la case est pleine : on ignore cette frappe (rien à couper plus tard)
    setTextFieldValue(k, newValue);
  };
  // ── Rendu des cases à cocher directement SUR le document (au lieu d'une liste générique
  // "Case 1 / Case 2" sans contexte) : on rend chaque page en image (comme dans l'éditeur de
  // balises) et on superpose un carré cliquable exactement à la position (x_percent, y_percent)
  // enregistrée — le texte imprimé autour donne le contexte, pas besoin de libellé inventé.
  const [pageImages, setPageImages] = useState([]);
  const [pageImagesLoading, setPageImagesLoading] = useState(false);
  // FIX (2026-09-10, round 5) : corrige l'écart visuel signalé — "on voit bien la différence
  // d'écriture" entre l'écran du formateur qui tape et le PDF final reçu par le client. Cause
  // racine : la case (width/height en %) est bien proportionnelle à la page, mais la TAILLE DE
  // POLICE affichée pendant la saisie était fixée en dur à 12px CSS, quelle que soit la largeur
  // RÉELLE à l'écran de l'image de page (large sur un écran d'ordinateur, étroite sur mobile) —
  // alors que dans le PDF final, la police (12pt Helvetica) est TOUJOURS proportionnelle à la
  // largeur de la page (595pt pour une A4), quel que soit l'appareil qui l'affiche ensuite. Donc
  // plus la fenêtre du formateur est large, plus la case paraissait "vide" (police relativement
  // petite) par rapport à ce que ce même texte donnera, proportionnellement, dans le PDF.
  // Correctif : on mesure la largeur RÉELLE en pixels du conteneur de page (pagesContainerRef,
  // via ResizeObserver — se met à jour si la fenêtre/modale est redimensionnée), puis on calcule
  // une taille de police à l'écran proportionnelle à cette largeur, dans le MÊME ratio que 12pt
  // par rapport à la largeur de page en points du PDF (pg.pageWidthPt). Résultat : le texte
  // occupe, visuellement, exactement la même proportion de la case à l'écran (n'importe quel
  // appareil) que dans le document final — formateur et client voient la même chose.
  // NOTE (bugfix immédiat) : ce bloc DOIT rester après la déclaration de pageImages ci-dessus —
  // l'effet ci-dessous lit pageImages.length dans son tableau de dépendances, et le placer AVANT
  // (comme dans la toute première version de ce correctif) provoquait un plantage total de l'appli
  // ("Cannot access 'pageImages' before initialization", page blanche) car ce const est en TDZ tant
  // que sa ligne de déclaration n'a pas encore été exécutée dans le corps de la fonction.
  const pagesContainerRef = useRef(null);
  const [pagesContainerWidthPx, setPagesContainerWidthPx] = useState(0);
  useEffect(() => {
    if (mode !== 'sign') return;
    const el = pagesContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setPagesContainerWidthPx(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mode, pageImages.length]);
  const scaledFontSizePx = (pg) => {
    const pageWidthPt = pg && pg.pageWidthPt;
    if (!pageWidthPt || !pagesContainerWidthPx) return 12; // repli avant première mesure
    return Math.max(8, (12 / pageWidthPt) * pagesContainerWidthPx);
  };
  useEffect(() => {
    let cancelled = false;
    setPageImages([]);
    // FIX (2026-09-03, suite — bug mobile "une seule page visible en signature") : on ne restreint
    // plus ce rendu aux documents ayant des cases/textes interactifs requis — voir commentaire
    // détaillé au-dessus de ce useEffect.
    if (!isOpen || mode !== 'sign' || !blobUrl) return;
    (async () => {
      setPageImagesLoading(true);
      try {
        const loadPdfJs = () => new Promise((resolve, reject) => {
          if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
          const s = window.document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; resolve(window.pdfjsLib); };
          s.onerror = () => reject(new Error('PDF.js indisponible'));
          window.document.head.appendChild(s);
        });
        const pdfjsLib = await loadPdfJs();
        const resp = await fetch(blobUrl);
        const arrBuf = await resp.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrBuf }).promise;
        const pages = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const vp = page.getViewport({ scale: 1.5 });
          const canvas = window.document.createElement('canvas');
          canvas.width = vp.width; canvas.height = vp.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
          // FIX (2026-09-10, round 3) : on garde aussi les dimensions RÉELLES de la page PDF (en
          // points, indépendamment du zoom "scale: 1.5" utilisé ci-dessus juste pour la netteté de
          // l'image) — nécessaire pour calculer, au caractère près, combien de texte une case peut
          // réellement contenir dans le PDF final (voir pageTexts plus bas) et empêcher de taper plus
          // que ce qui tiendra, plutôt que de le découvrir après coup lors de la signature.
          pages.push({ dataUrl: canvas.toDataURL(), pageWidthPt: vp.width / 1.5, pageHeightPt: vp.height / 1.5 });
        }
        if (!cancelled) setPageImages(pages);
      } catch (e) {
        console.warn('[DocumentViewerModal] Rendu pages interactif impossible (repli sur la liste) :', e.message);
        if (!cancelled) setPageImages([]);
      } finally {
        if (!cancelled) setPageImagesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, blobUrl, requiredCheckboxes.length, requiredTextFields.length]);

  // resolveFileUrl est appliqué ici pour couvrir toutes les sources (relative path ou URL complète)
  const pdfUrl = resolveFileUrl(url || document?.url);
  const pdfTitle = title || document?.nom || 'Document';
  const isValidUrl = !!pdfUrl; // resolveFileUrl garantit une URL https si non null

  // Nouvelle approche : signed URL → iframe directe (pas de fetch, pas de CORS)
  // createSignedUrl génère un lien temporaire que l'iframe peut charger sans restriction
  useEffect(() => {
    let cancelled = false;

    // Extrait bucket + path depuis une URL Supabase publique ou un chemin relatif
    const extractBucketPath = (fullUrl) => {
      if (!fullUrl) return null;
      console.log('[DocumentViewerModal] Extraction depuis:', fullUrl);
      
      // Cas 1: URL complète Supabase (public ou sign|authenticated)
      const match = fullUrl.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+?)(?:\?|$)/);
      if (match) {
        let bucket = match[1];
        let rawPath = match[2];
        let path = decodeURIComponent(rawPath);
        
        // Nettoyage : si le path commence par le nom du bucket (redondance parfois constatée)
        if (path.startsWith(`${bucket}/`)) {
          path = path.substring(bucket.length + 1);
        }
        
        return { bucket, path };
      }
      
      // Cas 2: Chemin relatif ou simple nom de fichier
      let path = fullUrl;
      let bucket = 'documents'; // Default

      if (path.startsWith('ressources/') || path.startsWith('ressources-pedagogiques/')) {
        bucket = 'ressources-pedagogiques';
        path = path.replace('ressources/', '').replace('ressources-pedagogiques/', '');
      } else if (path.startsWith('modeling-imports/')) {
        bucket = 'ressources-pedagogiques';
      } else if (path.startsWith('documents/')) {
        bucket = 'documents';
        path = path.replace('documents/', '');
      }

      return { bucket, path };
    };

    const loadSignedUrl = async () => {
      if (!isOpen || !isValidUrl) return;
      setLoadingPdf(true);
      setPdfError(null);
      setBlobUrl(null);

      // Blob ou data URL = PDF local pré-rempli → afficher directement sans passer par Supabase
      if (pdfUrl.startsWith('blob:') || pdfUrl.startsWith('data:')) {
        setBlobUrl(pdfUrl);
        setLoadingPdf(false);
        return;
      }

      const initialExtracted = extractBucketPath(pdfUrl);
      if (!initialExtracted) {
        setPdfError('Chemin de fichier invalide');
        setLoadingPdf(false);
        return;
      }

      const { bucket: initialBucket, path: initialPath } = initialExtracted;
      const bucketsToTry = [initialBucket, 'documents', 'ressources-pedagogiques', 'signed_documents', 'module_resources', 'client_files'].filter((v, i, a) => a.indexOf(v) === i);
      const uniqueBuckets = [...new Set(bucketsToTry.filter(Boolean))];

      try {
        let finalSignedUrl = null;
        let lastError = null;
        let finalPath = initialPath;

        for (const bucket of uniqueBuckets) {
          console.log(`[DocumentViewerModal] Tentative dans le bucket: ${bucket} | Path: ${initialPath}`);
          let { data, error } = await activeSupabase.storage.from(bucket).createSignedUrl(initialPath, 3600);

          // Fallback 1: Si non trouvé, on tente le nom de fichier seul
          if (error?.message?.includes('not found') && initialPath.includes('/')) {
            const alternativePath = initialPath.split('/').pop();
            console.warn(`[DocumentViewerModal] Objet non trouvé à "${initialPath}" dans "${bucket}". Tentative à la racine: "${alternativePath}"...`);
            const fallbackResult = await activeSupabase.storage.from(bucket).createSignedUrl(alternativePath, 3600);
            data = fallbackResult.data;
            error = fallbackResult.error;
            if (!error && data?.signedUrl) {
              finalPath = alternativePath;
            }
          }

          if (!error && data?.signedUrl) {
            finalSignedUrl = data.signedUrl;
            console.log(`%c[DocumentViewerModal] ✅ Succès trouvé dans le bucket "${bucket}" !`, 'color: #10b981; font-weight: bold;');
            break;
          } else {
            lastError = error?.message || 'Inconnu';
          }
        }

        if (cancelled) return;

        if (!finalSignedUrl) {
          // Fallback final : utiliser directement la pdfUrl publique SAUF si le fichier est clairement absent
          let fileGenuinelyMissing = lastError && (
            lastError.toLowerCase().includes('not found') ||
            lastError.toLowerCase().includes('object not found')
          );
          // SÉCURITÉ (2026-10-12) : « not found » peut aussi vouloir dire « non autorisé » (règles de
          // stockage par organisme). Si le lien public répond quand même, on l'utilise.
          if (fileGenuinelyMissing && pdfUrl && pdfUrl.startsWith('http')) {
            const headResp = await fetch(pdfUrl, { method: 'HEAD' }).catch(() => null);
            if (headResp && headResp.ok) fileGenuinelyMissing = false;
          }
          if (!fileGenuinelyMissing && pdfUrl && pdfUrl.startsWith('http')) {
            // Signed URL indisponible pour raison de config (pas "not found") → tenter l'URL publique
            console.warn('[DocumentViewerModal] Signed URL indisponible (non-404) — fallback sur URL publique directe.');
            const fallbackPath = pdfUrl.split('?')[0].toLowerCase();
            const isFallbackWord = fallbackPath.endsWith('.docx') || fallbackPath.endsWith('.doc');
            const ouvrable = await adresseOuvrable(pdfUrl); // fichiers privés : lien temporaire si possible
            if (cancelled) return;
            if (isFallbackWord) {
              setBlobUrl(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(ouvrable)}`);
            } else {
              setBlobUrl(ouvrable);
            }
            setLoadingPdf(false);
            return;
          }
          // Fichier introuvable → afficher l'état d'erreur propre (pas de JSON brut dans iframe)
          setDebugInfo({ bucket: initialBucket, path: initialPath, error: lastError || 'Fichier introuvable dans tous les buckets testés.' });
          throw new Error(lastError || 'Fichier introuvable');
        }

        const isWord = finalPath.toLowerCase().endsWith('.docx') || finalPath.toLowerCase().endsWith('.doc');
        if (isWord) {
          // Microsoft Office Online Viewer — gratuit, sans clé API, rendu fidèle DOCX
          // Fichiers privés (audit 04/11/2026) : lien temporaire d'1 h (Microsoft lit le fichier avec).
          const docViewerSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(finalSignedUrl || pdfUrl)}`;
          if (!cancelled) setBlobUrl(docViewerSrc);
        } else {
          setBlobUrl(finalSignedUrl);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[DocumentViewerModal] Erreur critique:', err.message);
          setPdfError(err.message);
        }
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    };

    if (isOpen) {
      loadSignedUrl();
      setCheckedBoxIds(new Set());
    } else {
      setHasRead(false);
      setAgreed(false);
      setBlobUrl(null);
      setPdfError(null);
      setDocumentChoice(null);
      setCheckedBoxIds(new Set());
      setHasSig(false); // Sans ce reset, une signature dessinée sur un document restait "true" pour le document suivant.
    }

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pdfUrl]);

  useEffect(() => {
    if (!isOpen || mode !== 'sign' || hasRead) return;
    const el = signatureSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHasRead(true); },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen, mode, hasRead, blobUrl]);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setHasRead(true);
  };


  if (!isOpen) return null;

  const renderPdfZone = () => {
    if (loadingPdf) return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
        <div className="w-10 h-10 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin"></div>
        <p className="text-sm font-medium">Chargement du document en cours…</p>
      </div>
    );
    if (blobUrl) {
      // Les URLs du viewer Office Online ne doivent pas recevoir le suffixe PDF
      const isOfficeViewer = blobUrl.startsWith('https://view.officeapps.live.com');
      return (
        <iframe
          src={isOfficeViewer ? blobUrl : blobUrl + '#toolbar=0&navpanes=0'}
          title={pdfTitle}
          className="w-full h-full border-0"
          allowFullScreen
        />
      );
    }
    // Erreur ou pas d'URL
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-4 p-8">
        <div className="text-5xl">📄</div>
        <div className="text-center">
          <p className="font-semibold text-gray-600">
            {pdfError 
              ? (pdfError.includes('Object not found') ? 'Fichier source introuvable dans le stockage' : 'Impossible de charger le document') 
              : 'Aucun fichier joint à cette session.'}
          </p>
          {pdfError && (
            <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
              Le fichier a peut-être été supprimé ou remplacé. Si le problème continue, prévenez votre organisme de formation.
            </p>
          )}
        </div>
        {isValidUrl && (
          <button
            onClick={() => window.open(blobUrl || pdfUrl, '_blank')}
            className="flex items-center gap-2 bg-violet-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-violet-700 transition-colors shadow-lg"
          >
            <Download size={16} /> Télécharger pour lire le document
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-900/80 z-[100] flex items-center justify-center p-2 md:p-6 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col relative" style={{ maxHeight: '95vh' }}>
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white z-10 shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center text-violet-700 font-bold text-sm">
              {pdfUrl && (pdfUrl.toLowerCase().includes('.docx') || pdfUrl.toLowerCase().includes('.doc')) ? 'DOC' : 'PDF'}
            </div>
            <div>
              <h3 className="font-extrabold text-base text-gray-900">{pdfTitle}</h3>
              {mode === 'sign' && (
                <p className="text-[11px] font-bold" style={{ color: hasRead ? '#16a34a' : '#f97316' }}>
                  {hasRead ? '✅ Document parcouru — vous pouvez maintenant signer.' : '⬇️ Parcourez le document puis faites défiler pour déverrouiller la signature.'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isValidUrl && (
              <button
                onClick={() => window.open(pdfUrl, '_blank')}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-800 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                title="Ouvrir dans un nouvel onglet"
              >
                <Download size={14} /> Ouvrir ↗
              </button>
            )}
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors">✕</button>
          </div>
        </div>

        {/* Scrollable content zone */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto bg-gray-50 p-3 md:p-4 flex flex-col gap-6"
          onScroll={mode === 'sign' ? handleScroll : undefined}
          style={{ minHeight: 0 }}
        >
          {/* PDF Zone — mode interactif (cases directement sur le document) si des cases sont
              requises ET que le rendu page-par-page a réussi ; sinon le lecteur PDF classique. */}
          {mode === 'sign' && pageImages.length > 0 ? (
            <div className="w-full space-y-3" ref={pagesContainerRef}>
              {pageImages.map((pg, pi) => {
                const pageChecks = requiredCheckboxes.filter(f => (f.page || 1) === pi + 1);
                const pageTexts = requiredTextFields.filter(f => (f.page || 1) === pi + 1);
                return (
                  <div key={pi} className="relative w-full border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
                    <img src={pg.dataUrl} alt={`Page ${pi + 1}`} className="w-full block" />
                    {pageChecks.map((f, fi) => {
                      const k = fieldKey(f);
                      const isChecked = checkedBoxIds.has(k);
                      return (
                        <button
                          key={k || fi}
                          type="button"
                          onClick={() => toggleRequiredCheckbox(k)}
                          title="Cliquez pour cocher / décocher"
                          style={{ position: 'absolute', left: `${f.x_percent}%`, top: `${f.y_percent}%`, transform: 'translate(-50%, -50%)', width: 20, height: 20 }}
                          className={`rounded-sm border-2 shadow-lg transition-all flex items-center justify-center ${isChecked ? 'bg-violet-600 border-violet-700' : 'bg-white/80 border-violet-500 hover:bg-violet-50'}`}
                        >
                          {isChecked && <span className="text-white text-[11px] font-black leading-none">✓</span>}
                        </button>
                      );
                    })}
                    {pageTexts.map((f, fi) => {
                      const k = fieldKey(f);
                      const val = textFieldValues[k] || '';
                      return (
                        <textarea
                          key={k || `txt-${fi}`}
                          value={val}
                          onChange={e => handleTextFieldChange(f, pg, e.target.value)}
                          placeholder="Cliquez pour écrire…"
                          rows={1}
                          // FIX (2026-09-10, round 4) : remplace l'ancienne limite de caractères — une
                          // ESTIMATION (largeur moyenne de caractère) qui pouvait diverger de la
                          // capacité RÉELLE du PDF final et laissait encore passer des textes ensuite
                          // tronqués — par handleTextFieldChange, qui utilise la MÊME fonction de
                          // découpage ET la même police (Helvetica, mesurée par pdf-lib) que la
                          // gravure finale : une frappe qui ferait déborder la case dans le PDF est
                          // refusée ici, en temps réel, plutôt que découverte à la signature.
                          // FIX (2026-09-10) : remplace le <input type="text"> mono-ligne par une <textarea> —
                          // sur une case large, on ne pouvait écrire que sur une seule ligne et le texte trop
                          // long était coupé/débordait sans jamais revenir à la ligne. La <textarea> gère
                          // nativement "Entrée" (saut de ligne) et enroule le texte (whiteSpace/overflowWrap
                          // ci-dessous) sans jamais dépasser les contours de la case (overflow hidden + hauteur
                          // fixée sur field.height_percent, ignoré par l'ancien <input> qui ne lisait que
                          // width_percent — d'où le fait qu'agrandir la case dans l'éditeur de balises n'avait
                          // aucun effet ici). `resize: none` empêche l'utilisateur de re-déformer la case
                          // pendant la signature : la taille reste celle définie dans l'éditeur de modèle.
                          // Zone d'édition volontairement plus resserrée et en gris neutre (au lieu du violet/
                          // vert d'origine) : la balise posée dans l'éditeur de modèle est large pour laisser
                          // de la marge. Le rendu final (overlayFieldsOnPdf) souligne désormais uniquement la
                          // largeur réelle du texte, en gris — cette zone d'édition adopte la même sobriété
                          // (retour utilisateur 2026-07-24).
                          // Ancrée à GAUCHE (translate(0%, -50%), pas -50%/-50%) : le point posé dans
                          // l'éditeur de modèle est désormais le coin gauche exact où le texte démarre, à
                          // l'écran comme dans le PDF final — même correctif que overlayFieldsOnPdf.
                          style={{
                            position: 'absolute',
                            left: `${f.x_percent}%`,
                            top: `${f.y_percent}%`,
                            transform: 'translate(0%, -50%)',
                            ...(typeof f.width_percent === 'number' ? { width: `${f.width_percent}%` } : { minWidth: '10%', maxWidth: '26%' }),
                            height: `${typeof f.height_percent === 'number' ? f.height_percent : 3.5}%`,
                            minHeight: 20,
                            // FIX (round 5) : taille de police PROPORTIONNELLE à la largeur réelle à
                            // l'écran de la page (voir scaledFontSizePx ci-dessus) — plus fixée à 12px —
                            // pour que le texte occupe visuellement la même part de la case sur tous les
                            // écrans (ordinateur large ou mobile étroit), comme dans le PDF final.
                            fontSize: scaledFontSizePx(pg),
                            // Arial/Helvetica : la police la plus proche, en métriques de largeur, de
                            // l'Helvetica standard utilisée par pdf-lib pour graver le PDF final — pour
                            // que le texte affiché ici ressemble d'aussi près que possible à ce qui sera
                            // effectivement imprimé (retour utilisateur du 2026-09-10 : la police à
                            // l'écran devait "être exactement pareille" une fois le document reçu).
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            resize: 'none',
                            overflow: 'hidden',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                            lineHeight: 1.25,
                          }}
                          className={`px-0.5 bg-transparent outline-none text-gray-900 border rounded ${val ? 'border-gray-400' : 'border-gray-300 border-dashed'} focus:border-violet-500`}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white" style={{ height: mode === 'sign' ? '60vh' : '72vh', minHeight: 380 }}>
              {mode === 'sign' && pageImagesLoading ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
                  <div className="w-10 h-10 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin"></div>
                  <p className="text-sm font-medium">Préparation des champs interactifs sur le document…</p>
                </div>
              ) : renderPdfZone()}
            </div>
          )}

          {/* Section signature (mode sign uniquement) */}
          {mode === 'sign' && (
            <div ref={signatureSectionRef} className={`bg-white rounded-2xl border-2 transition-all ${hasRead ? 'border-gray-200' : 'border-dashed border-gray-200 opacity-50 pointer-events-none'}`}>
              <div className="p-5 border-b border-gray-100">
                <h4 className="font-extrabold text-gray-900 mb-1">{requiresSignature ? 'Signature électronique' : 'Validation'}</h4>
                <p className="text-sm text-gray-500">
                  {requiresSignature
                    ? 'Dessinez votre signature dans le cadre ci-dessous, puis cliquez sur "Signer ce document".'
                    : 'Ce document ne comporte pas de zone de signature — cliquez sur "Signer ce document" pour valider.'}
                </p>
              </div>
              <div className="p-5">
                {isInteractiveConsent && (
                  <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-sm font-bold text-amber-800 mb-3">Avant de signer, indiquez votre choix :</p>
                    <label className="flex items-center gap-3 cursor-pointer mb-2 select-none">
                      <input
                        type="radio"
                        name="documentChoice"
                        value="autorise"
                        checked={documentChoice === 'autorise'}
                        onChange={() => setDocumentChoice('autorise')}
                        className="w-4 h-4 accent-green-600 shrink-0"
                      />
                      <span className="text-sm text-gray-700 font-medium">J'autorise la conservation de mes documents personnels</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="documentChoice"
                        value="refuse"
                        checked={documentChoice === 'refuse'}
                        onChange={() => setDocumentChoice('refuse')}
                        className="w-4 h-4 accent-red-600 shrink-0"
                      />
                      <span className="text-sm text-gray-700 font-medium">Je n'autorise pas la conservation de mes documents personnels</span>
                    </label>
                    {!documentChoice && (
                      <p className="text-xs text-amber-700 mt-2 italic">Ce choix est obligatoire pour débloquer la signature.</p>
                    )}
                  </div>
                )}
                {requiredCheckboxes.length > 0 && pageImages.length > 0 && (
                  // Mode interactif actif : les cases se cochent directement sur le document ci-dessus,
                  // au plus près du texte qu'elles concernent — on affiche juste un état d'avancement.
                  <div className={`mb-4 p-3 rounded-xl border text-sm font-bold ${allRequiredChecked ? 'bg-green-50 border-green-200 text-green-700' : 'bg-violet-50 border-violet-200 text-violet-800'}`}>
                    {allRequiredChecked
                      ? `✅ ${requiredCheckboxes.filter(f => checkedBoxIds.has(fieldKey(f))).length} / ${requiredCheckboxes.length} case(s) cochée(s) sur le document ci-dessus.`
                      : `☐ 0 / ${requiredCheckboxes.length} case(s) cochée(s) — cochez au moins une case directement sur le document ci-dessus pour débloquer la signature.`}
                  </div>
                )}
                {requiredCheckboxes.length > 0 && pageImages.length === 0 && (
                  // Repli (rendu page-par-page indisponible) : liste générique, sans contexte visuel.
                  <div className="mb-4 p-4 bg-violet-50 border border-violet-200 rounded-xl">
                    <p className="text-sm font-bold text-violet-800 mb-3">Avant de signer, cochez la ou les cases suivantes :</p>
                    {requiredCheckboxes.map((f, i) => (
                      <label key={fieldKey(f) || i} className="flex items-center gap-3 cursor-pointer mb-2 last:mb-0 select-none">
                        <input
                          type="checkbox"
                          checked={checkedBoxIds.has(fieldKey(f))}
                          onChange={() => toggleRequiredCheckbox(fieldKey(f))}
                          className="w-4 h-4 rounded accent-violet-600 shrink-0"
                        />
                        <span className="text-sm text-gray-700 font-medium">Case à cocher {i + 1}</span>
                      </label>
                    ))}
                    {!allRequiredChecked && (
                      <p className="text-xs text-violet-700 mt-2 italic">Au moins une case doit être cochée pour débloquer la signature.</p>
                    )}
                  </div>
                )}
                {requiredTextFields.length > 0 && pageImages.length > 0 && (
                  // Mode interactif actif : les champs se remplissent directement sur le document ci-dessus.
                  // Purement informatif depuis le 2026-09-03 : ces champs ne bloquent plus la signature,
                  // il est normal que certains restent vides selon le document.
                  <div className={`mb-4 p-3 rounded-xl border text-sm font-bold ${allRequiredTextFilled ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                    {allRequiredTextFilled
                      ? `✅ ${requiredTextFields.length} champ(s) texte rempli(s) sur le document ci-dessus.`
                      : `✏️ ${requiredTextFields.filter(f => (textFieldValues[fieldKey(f)] || '').trim()).length} / ${requiredTextFields.length} champ(s) texte rempli(s) sur le document ci-dessus (facultatif — vous pouvez signer avec des champs vides).`}
                  </div>
                )}
                {requiredTextFields.length > 0 && pageImages.length === 0 && (
                  // Repli (rendu page-par-page indisponible) : liste générique, sans contexte visuel.
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-sm font-bold text-blue-800 mb-3">Champ(s) texte du document (facultatif) :</p>
                    {requiredTextFields.map((f, i) => (
                      <div key={fieldKey(f) || i} className="mb-2 last:mb-0">
                        <input
                          type="text"
                          value={textFieldValues[fieldKey(f)] || ''}
                          onChange={e => setTextFieldValue(fieldKey(f), e.target.value)}
                          placeholder={`Champ texte ${i + 1}`}
                          className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-start gap-3 cursor-pointer mb-4 select-none">
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-violet-600 shrink-0" />
                  <span className="text-sm text-gray-600">
                    Je certifie avoir <strong>lu et compris</strong> l'intégralité de ce document{requiresSignature ? " et j'accepte de le valider par ma signature électronique." : '.'}
                  </span>
                </label>
                {/* Canvas de signature manuscrite — uniquement si le document comporte une balise
                    signature pour ce signataire (sinon rien à signer, on ne l'exige pas). */}
                {requiresSignature && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Votre signature</span>
                    <button
                      type="button"
                      onClick={() => {
                        const canvas = sigCanvasRef.current;
                        if (canvas) { canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height); setHasSig(false); }
                      }}
                      className="text-xs text-gray-400 hover:text-violet-600 transition-colors"
                    >Effacer</button>
                  </div>
                  <canvas
                    ref={sigCanvasRef}
                    width={580}
                    height={140}
                    className="w-full border-2 border-dashed border-gray-300 rounded-xl bg-white cursor-crosshair"
                    style={{ touchAction: 'none' }}
                    onMouseDown={e => {
                      const c = sigCanvasRef.current;
                      const r = c.getBoundingClientRect();
                      isDrawingRef.current = true;
                      lastPosRef.current = { x: (e.clientX - r.left) * c.width / r.width, y: (e.clientY - r.top) * c.height / r.height };
                    }}
                    onMouseMove={e => {
                      if (!isDrawingRef.current) return;
                      const c = sigCanvasRef.current;
                      const r = c.getBoundingClientRect();
                      const x = (e.clientX - r.left) * c.width / r.width;
                      const y = (e.clientY - r.top) * c.height / r.height;
                      const ctx = c.getContext('2d');
                      ctx.beginPath(); ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y); ctx.lineTo(x, y);
                      ctx.strokeStyle = '#1a1a2e'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
                      lastPosRef.current = { x, y };
                      if (!hasSig) setHasSig(true);
                    }}
                    onMouseUp={() => { isDrawingRef.current = false; }}
                    onMouseLeave={() => { isDrawingRef.current = false; }}
                    onTouchStart={e => {
                      e.preventDefault();
                      const c = sigCanvasRef.current;
                      const r = c.getBoundingClientRect();
                      const t = e.touches[0];
                      isDrawingRef.current = true;
                      lastPosRef.current = { x: (t.clientX - r.left) * c.width / r.width, y: (t.clientY - r.top) * c.height / r.height };
                    }}
                    onTouchMove={e => {
                      e.preventDefault();
                      if (!isDrawingRef.current) return;
                      const c = sigCanvasRef.current;
                      const r = c.getBoundingClientRect();
                      const t = e.touches[0];
                      const x = (t.clientX - r.left) * c.width / r.width;
                      const y = (t.clientY - r.top) * c.height / r.height;
                      const ctx = c.getContext('2d');
                      ctx.beginPath(); ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y); ctx.lineTo(x, y);
                      ctx.strokeStyle = '#1a1a2e'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
                      lastPosRef.current = { x, y };
                      if (!hasSig) setHasSig(true);
                    }}
                    onTouchEnd={() => { isDrawingRef.current = false; }}
                  />
                  {!hasSig && <p className="text-xs text-gray-400 mt-1 text-center italic">Tracez votre signature ci-dessus</p>}
                </div>
                )}
                <div className="flex justify-end gap-3">
                  <button onClick={onClose} className="px-5 py-2.5 text-gray-700 font-bold hover:bg-gray-100 rounded-xl transition-colors text-sm">Annuler</button>
                  <button
                    onClick={async () => {
                      if (!onSave || enregistrementSigRef.current) return;
                      const canvas = sigCanvasRef.current;
                      let sigDataUrl = null;
                      if (canvas && hasSig) {
                        const tmp = window.document.createElement('canvas');
                        tmp.width = canvas.width; tmp.height = canvas.height;
                        const ctx = tmp.getContext('2d');
                        ctx.drawImage(canvas, 0, 0);
                        const imgData = ctx.getImageData(0, 0, tmp.width, tmp.height);
                        const d = imgData.data;
                        for (let i = 0; i < d.length; i += 4) {
                          if (d[i] > 220 && d[i+1] > 220 && d[i+2] > 220) d[i+3] = 0;
                        }
                        ctx.putImageData(imgData, 0, 0);
                        sigDataUrl = tmp.toDataURL('image/png');
                      }
                      enregistrementSigRef.current = true; setEnregistrementSig(true);
                      try { await onSave(sigDataUrl, isInteractiveConsent ? documentChoice : null, checkedBoxIds, textFieldValues); }
                      finally { enregistrementSigRef.current = false; setEnregistrementSig(false); }
                    }}
                    disabled={enregistrementSig || !agreed || (isInteractiveConsent && !documentChoice) || !allRequiredChecked || (requiresSignature && !hasSig)}
                    className={`px-6 py-2.5 font-bold rounded-xl transition-all text-sm shadow-lg ${(!enregistrementSig && agreed && (!isInteractiveConsent || documentChoice) && allRequiredChecked && (!requiresSignature || hasSig)) ? 'bg-violet-700 text-white hover:bg-violet-700' : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}
                  >
                    {enregistrementSig ? 'Enregistrement…' : 'Signer ce document'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer view mode */}
        {mode === 'view' && (
          <div className="p-4 border-t border-gray-100 shrink-0 flex justify-between items-center rounded-b-2xl bg-white">
            <span className="text-xs text-gray-400 italic">{isValidUrl ? pdfUrl.substring(0, 60) + '…' : 'Aucun fichier disponible'}</span>
            <button onClick={onClose} className="px-5 py-2 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors text-sm">Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
};




const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemName, title = "Confirmation de suppression", message = null }) => {

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[150] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 w-full max-w-md border border-gray-100 animate-slide-up">
        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mb-6">
          <Trash2 size={32} />
        </div>
        <h3 className="text-xl font-black text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-500 mb-8">
          Confirmez-vous la suppression définitive de <span className="font-bold text-gray-900">{itemName}</span> ? 
          {message || 'Toutes les données associées seront supprimées et cette action est irréversible.'}
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-5 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors">Annuler</button>
          <button onClick={onConfirm} className="flex-1 px-5 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-100">Confirmer</button>
        </div>
      </div>
    </div>
  );
};

// ─── Modale de Confirmation Générique SkorUp ───────────────────────────────
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel = 'Supprimer', danger = true }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center animate-slide-up">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${danger ? 'bg-red-50' : 'bg-violet-50'}`}>
          <Trash2 size={28} className={danger ? 'text-red-500' : 'text-violet-500'} />
        </div>
        <h3 className="text-lg font-black text-gray-900 mb-2">{title || 'Confirmer la suppression'}</h3>
        {message && <p className="text-sm text-gray-500 mb-7 leading-relaxed">{message}</p>}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-2xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-3 rounded-2xl text-sm font-bold text-white transition-all shadow-md ${danger ? 'bg-red-500 hover:bg-red-600 shadow-red-100' : 'bg-violet-600 hover:bg-violet-700 shadow-violet-100'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modale Paramètres d'un Document/Session ───────────────────────────────
const DocumentSettingsModal = ({ isOpen, session, onClose, onSave }) => {
  const [nom, setNom] = React.useState('');
  const [reqClient, setReqClient] = React.useState(true);
  const [reqFormateur, setReqFormateur] = React.useState(true);

  React.useEffect(() => {
    if (isOpen && session) {
      const meta = session.metadata || {};
      setNom(session.ressource_titre || session.titre || session.nom || '');
      setReqClient(meta.requiresClientSignature !== false);
      setReqFormateur(meta.requiresTrainerSignature === true);
    }
  }, [isOpen, session]);

  if (!isOpen || !session) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center">
            <Settings size={20} />
          </div>
          <div>
            <h3 className="font-extrabold text-gray-900 text-base">Paramètres du document</h3>
            <p className="text-xs text-gray-400">Configurer ce document dans le planning</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Renommer */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Titre du document</label>
            <input
              type="text"
              value={nom}
              onChange={e => setNom(e.target.value)}
              className="w-full p-3 rounded-xl border border-gray-200 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Ex : Engagement du bénéficiaire"
            />
          </div>

          {/* Qui doit signer */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">Qui doit signer ?</label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 cursor-pointer hover:border-indigo-200 transition-all">
                <input
                  type="checkbox"
                  checked={reqClient}
                  onChange={e => setReqClient(e.target.checked)}
                  className="w-4 h-4 accent-indigo-600 rounded"
                />
                <div>
                  <p className="font-bold text-gray-800 text-sm">Signature du Bénéficiaire (Client)</p>
                  <p className="text-xs text-gray-400">Le client devra signer ce document</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 cursor-pointer hover:border-violet-200 transition-all">
                <input
                  type="checkbox"
                  checked={reqFormateur}
                  onChange={e => setReqFormateur(e.target.checked)}
                  className="w-4 h-4 accent-violet-600 rounded"
                />
                <div>
                  <p className="font-bold text-gray-800 text-sm">Signature du Formateur</p>
                  <p className="text-xs text-gray-400">Le formateur devra contresigner</p>
                </div>
              </label>
            </div>
          </div>

          {/* Preview du statut */}
          <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-[11px] text-blue-700 font-medium">
            <span className="font-black uppercase tracking-wider">Aperçu : </span>
            {reqClient && reqFormateur && 'Signature client + formateur requises'}
            {reqClient && !reqFormateur && 'Signature client uniquement — Formateur N/A'}
            {!reqClient && reqFormateur && 'Signature formateur uniquement — Client N/A'}
            {!reqClient && !reqFormateur && 'Aucune signature requise — Document informatif'}
          </div>
        </div>

        <div className="flex gap-3 mt-7">
          <button onClick={onClose} className="flex-1 px-5 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors">
            Annuler
          </button>
          <button
            onClick={() => onSave({ nom, requiresClientSignature: reqClient, requiresTrainerSignature: reqFormateur })}
            className="flex-1 px-5 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

const ExerciceModal = ({ isOpen, onClose, session, onSubmit }) => {
  const [dragOver, setDragOver] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) { setSelectedFile(null); setDragOver(false); setIsSubmitting(false); }
  }, [isOpen]);

  if (!isOpen || !session) return null;

  const exerciceName = session.ressource_titre || session.nom || 'Exercice';
  const fileUrl = session.file_url || session.ressource_url;
  const hasSubmitted = !!session.reponse_url;

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    setIsSubmitting(true);
    await onSubmit(session.id, selectedFile);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fade-in">
      <div className="bg-white rounded-[32px] w-full max-w-lg shadow-2xl border border-gray-100 overflow-hidden animate-slide-up max-h-[90vh] flex flex-col">
        <div className="bg-emerald-600 p-8 text-white relative shrink-0">
          <p className="text-emerald-200 text-xs font-black uppercase tracking-widest mb-2">⚙️ Votre Exercice</p>
          <h2 className="text-2xl font-black leading-tight">{exerciceName}</h2>
          {session.instructions && (
            <p className="text-emerald-100 text-sm mt-3 leading-relaxed opacity-90">{session.instructions}</p>
          )}
          <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-all">✕</button>
        </div>

        <div className="p-8 space-y-6 overflow-y-auto">
          {hasSubmitted && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <span className="text-2xl">📬</span>
              <div>
                <p className="font-black text-amber-800 text-sm">{session.metadata?.deposeParFormateur ? 'Exercice réalisé en séance' : 'Terminé — En attente de correction'}</p>
                <p className="text-amber-600 text-xs mt-0.5">{session.metadata?.deposeParFormateur ? 'Votre formateur a déposé le document de cet exercice, fait ensemble pendant la séance.' : 'Votre formateur va corriger votre rendu prochainement.'}</p>
              </div>
            </div>
          )}
          {hasSubmitted && (
            <button
              onClick={() => openSecureStorageFile(session.reponse_url)}
              className="w-full flex items-center justify-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-2xl text-gray-700 font-bold hover:bg-gray-50 transition-all text-sm"
            >
              <Eye size={16} /> Voir le document déposé
            </button>
          )}

          {fileUrl && (
            <div>
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">1. Récupérer l'énoncé</p>
              <button
                onClick={() => openSecureStorageFile(fileUrl)}
                className="w-full flex items-center justify-center gap-3 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-2xl text-emerald-700 font-bold hover:bg-emerald-100 transition-all"
              >
                <Download size={18} /> Télécharger le modèle d'exercice
              </button>
            </div>
          )}

          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">{fileUrl ? '2.' : '1.'} Déposer votre rendu</p>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${dragOver ? 'border-emerald-500 bg-emerald-50 scale-[1.01]' : selectedFile ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 bg-gray-50 hover:border-emerald-300 hover:bg-emerald-50/30'}`}
            >
              <input
                type="file"
                className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                onChange={e => { const f = e.target.files[0]; if (f) setSelectedFile(f); }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
              />
              {selectedFile ? (
                <div className="flex flex-col items-center gap-2 pointer-events-none">
                  <span className="text-3xl">📎</span>
                  <p className="font-bold text-emerald-700 text-sm">{selectedFile.name}</p>
                  <p className="text-xs text-gray-400">{(selectedFile.size / 1024 / 1024).toFixed(2)} Mo</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 pointer-events-none">
                  <span className="text-3xl">📁</span>
                  <p className="font-bold text-gray-600 text-sm">Déposez votre exercice complété ici</p>
                  <p className="text-xs text-gray-400">ou cliquez pour parcourir vos fichiers</p>
                  <p className="text-xs text-gray-300 mt-1">PDF, Word, Excel, Images</p>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={!selectedFile || isSubmitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-4 rounded-2xl shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Envoi en cours...' : hasSubmitted ? 'Envoyer une nouvelle version' : 'Envoyer au formateur'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CorrectionModal = ({ isOpen, onClose, session, onSave }) => {
  const [statut, setStatut] = React.useState('');
  const [commentaire, setCommentaire] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (isOpen && session) {
      setStatut(session.correction_statut || '');
      setCommentaire(session.correction_commentaire || '');
    }
  }, [isOpen, session]);

  if (!isOpen || !session) return null;

  const handleSave = async () => {
    if (!statut) return;
    setSaving(true);
    try {
      await onSave(session.id, statut, commentaire);
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de l\'enregistrement : ' + (e.message || 'Erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-indigo-600 px-6 py-5">
          <h2 className="text-lg font-black text-white">Correction de l'exercice</h2>
          <p className="text-indigo-200 text-sm mt-0.5 truncate">{session.ressource_titre || session.nom}</p>
        </div>
        <div className="p-6 space-y-5">
          {session.reponse_url && (
            <button
              onClick={() => openSecureStorageFile(session.reponse_url)}
              className="flex items-center gap-3 w-full px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-700 font-bold text-sm hover:bg-emerald-100 transition-colors"
            >
              <FileCheck size={18} />
              Ouvrir le rendu du client
              <span className="ml-auto text-emerald-400">↗</span>
            </button>
          )}
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Statut de correction</p>
            <div className="flex gap-3">
              <button
                onClick={() => setStatut('Validé')}
                className={`flex-1 py-3 rounded-2xl font-bold text-sm border-2 transition-all ${statut === 'Validé' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-500 border-gray-200 hover:border-green-300'}`}
              >
                ✅ Validé
              </button>
              <button
                onClick={() => setStatut('À corriger')}
                className={`flex-1 py-3 rounded-2xl font-bold text-sm border-2 transition-all ${statut === 'À corriger' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-500 border-gray-200 hover:border-amber-300'}`}
              >
                📝 À corriger
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Commentaire (optionnel)</p>
            <textarea
              value={commentaire}
              onChange={e => setCommentaire(e.target.value)}
              rows={4}
              placeholder="Votre retour pour le client..."
              className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm resize-none"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-3 rounded-2xl font-bold text-sm bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors">
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={!statut || saving}
              className="flex-1 py-3 rounded-2xl font-bold text-sm bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Modale "Qui doit signer ?" pour un émargement DÉJÀ créé dans un modèle de module ──────────
// AJOUT (2026-09-25) : jusqu'ici, changer qui doit signer un émargement déjà créé dans un module
// obligeait à le supprimer et le recréer (StepResourceModal ne proposait ce choix qu'à la création).
// Demandé par l'utilisateur pour pouvoir ajuster ses modules sans "tout refaire" — voir
// handleUpdateStepResourceSignatures pour la logique de propagation aux dossiers clients existants.
const StepResourceSignatureModal = ({ isOpen, resource, onClose, onSave }) => {
  const [reqClient, setReqClient] = React.useState(true);
  const [reqFormateur, setReqFormateur] = React.useState(false);

  React.useEffect(() => {
    if (isOpen && resource) {
      const meta = (typeof resource.metadata === 'string' && resource.metadata.startsWith('{'))
        ? (() => { try { return JSON.parse(resource.metadata); } catch { return {}; } })()
        : (resource.metadata || {});
      setReqClient(meta.requiresClientSignature !== false);
      setReqFormateur(meta.requiresTrainerSignature === true);
    }
  }, [isOpen, resource]);

  if (!isOpen || !resource) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-violet-100 text-violet-600 rounded-2xl flex items-center justify-center">
            <Settings size={20} />
          </div>
          <div>
            <h3 className="font-extrabold text-gray-900 text-base">Qui doit signer ?</h3>
            <p className="text-xs text-gray-400">{resource.titre}</p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 cursor-pointer hover:border-indigo-200 transition-all">
            <input type="checkbox" checked={reqClient} onChange={e => setReqClient(e.target.checked)} className="w-4 h-4 accent-indigo-600 rounded" />
            <div>
              <p className="font-bold text-gray-800 text-sm">Signature du Bénéficiaire (Client)</p>
              <p className="text-xs text-gray-400">Le client devra signer cet émargement</p>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 cursor-pointer hover:border-violet-200 transition-all">
            <input type="checkbox" checked={reqFormateur} onChange={e => setReqFormateur(e.target.checked)} className="w-4 h-4 accent-violet-600 rounded" />
            <div>
              <p className="font-bold text-gray-800 text-sm">Signature du Formateur</p>
              <p className="text-xs text-gray-400">Le formateur devra aussi signer cet émargement</p>
            </div>
          </label>
        </div>

        <div className="p-3 mt-4 bg-blue-50 rounded-xl border border-blue-100 text-[11px] text-blue-700 font-medium">
          <span className="font-black uppercase tracking-wider">Aperçu : </span>
          {reqClient && reqFormateur && 'Signature client + formateur requises'}
          {reqClient && !reqFormateur && 'Signature client uniquement'}
          {!reqClient && reqFormateur && 'Signature formateur uniquement'}
          {!reqClient && !reqFormateur && 'Aucune signature requise'}
        </div>

        <p className="text-xs text-amber-600 mt-3 font-medium">
          ⚠️ Ajouter la signature formateur l'ajoutera aussi aux dossiers clients déjà créés avec cet émargement (sans toucher aux signatures déjà obtenues). La retirer ne modifie que les futurs dossiers.
        </p>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-5 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors">Annuler</button>
          <button
            onClick={() => onSave({ requiresClientSignature: reqClient, requiresTrainerSignature: reqFormateur })}
            className="flex-1 px-5 py-3 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-700 transition-colors shadow-lg"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

const StepResourceModal = ({ isOpen, onClose, onSave, pedagogicalResources, documentTemplates, supabase, momentLabel, currentOrgId }) => {
  const [type, setType] = useState('signature');
  const [title, setTitle] = useState('');
  const [metadata, setMetadata] = useState({
    requiresClientSignature: true,
    requiresTrainerSignature: false,
    documentType: 'signature'
  });
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [destination, setDestination] = useState('client');
  const [questions, setQuestions] = useState([]);
  // AJOUT (2026-09-30) : quiz noté (correction auto + seuil de réussite) et messages facultatifs
  // de début/fin, communs aux questionnaires et aux quiz — voir QuestionnaireFillerModal.
  const [isQuiz, setIsQuiz] = useState(false);
  const [seuilReussite, setSeuilReussite] = useState(50);
  const [qDescription, setQDescription] = useState('');
  const [qClosingMessage, setQClosingMessage] = useState('');
  // Reprendre un questionnaire déjà créé dans le menu « Questionnaires » (2026-10-13)
  const [qSource, setQSource] = useState('nouveau'); // 'nouveau' | 'existant'
  const [qExistants, setQExistants] = useState([]);
  const [qExistantId, setQExistantId] = useState('');
  React.useEffect(() => {
    if (!isOpen || type !== 'questionnaire' || !supabase) return;
    let q = supabase.from('module_step_resources').select('id, titre, metadata, module_id').eq('type', 'questionnaire');
    if (currentOrgId) q = q.eq('organisation_id', currentOrgId);
    q.then(({ data }) => {
      setQExistants((data || []).filter(r => r.module_id === null || r.module_id === undefined)
        .sort((a, b) => (a.titre || '').localeCompare(b.titre || '', 'fr', { sensitivity: 'base' })));
    });
  }, [isOpen, type, supabase, currentOrgId]);
  const qExistant = qExistants.find(x => String(x.id) === String(qExistantId)) || null;
  const qExistantMeta = qExistant ? parseQMeta(qExistant) : null;

  // Reset local state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setType('signature');
      setTitle('');
      setMetadata({
        requiresClientSignature: true,
        requiresTrainerSignature: false,
        documentType: 'signature'
      });
      setSelectedResourceId('');
      setIsUploading(false);
      setInstructions('');
      setDestination('client');
      setQuestions([]);
      setIsQuiz(false);
      setSeuilReussite(50);
      setQDescription('');
      setQClosingMessage('');
      setQSource('nouveau');
      setQExistantId('');
    }
  }, [isOpen]);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file, { cacheControl: '3600', upsert: true });

      let publicUrl;
      if (uploadError) {
        // Bibliothèque propre à l'organisme (20/10/2026) : rangé dans org_<organisme>/
        const cheminBiblio = currentOrgId ? `org_${currentOrgId}/${filePath}` : filePath;
        const { error: fallbackError } = await supabase.storage
          .from('ressources-pedagogiques')
          .upload(cheminBiblio, file, { cacheControl: '3600', upsert: true });
        if (fallbackError) throw fallbackError;
        ({ data: { publicUrl } } = supabase.storage.from('ressources-pedagogiques').getPublicUrl(cheminBiblio));
      } else {
        ({ data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(filePath));
      }

      setSelectedResourceId(publicUrl);
      if (!title) setTitle(file.name.replace(/\.[^/.]+$/, ""));
    } catch (err) {
      console.error('Error uploading file in modal:', err);
      toast.error('Erreur lors de l\'import du fichier : ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // obligatoire: true par défaut (2026-10-02) — même comportement qu'avant cette option ; décochable par question.
  const addQuestion = () => setQuestions(prev => [...prev, { id: Date.now().toString(), text: '', type: 'single', options: ['Option A', 'Option B'], correctAnswer: '', obligatoire: true }]);
  const removeQuestion = (id) => setQuestions(prev => prev.filter(q => q.id !== id));
  const updateQuestion = (id, field, value) => setQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: value } : q));
  // AJOUT (2026-09-30) : bascule le type d'une question en remettant correctAnswer dans le bon
  // format (chaîne pour "unique", tableau pour "multiple") — sinon un quiz noté peut se retrouver
  // avec un correctAnswer du mauvais format après un changement de type.
  const setQuestionType = (id, newType) => setQuestions(prev => prev.map(q => q.id === id ? { ...q, type: newType, correctAnswer: newType === 'multiple' ? [] : '' } : q));
  const addOption = (qId) => setQuestions(prev => prev.map(q => q.id === qId ? { ...q, options: [...q.options, ''] } : q));
  // AJOUT (2026-09-30) : correctAnswer est stocké par VALEUR (le texte de l'option) — on le
  // resynchronise ici si l'option qu'il désignait vient d'être renommée, sinon la bonne réponse
  // du quiz se désynchronise silencieusement dès qu'on corrige le texte d'une option.
  const updateOption = (qId, idx, value) => setQuestions(prev => prev.map(q => {
    if (q.id !== qId) return q;
    const oldVal = q.options[idx];
    const options = q.options.map((o, i) => i === idx ? value : o);
    let correctAnswer = q.correctAnswer;
    if (q.type === 'single' && correctAnswer === oldVal) correctAnswer = value;
    if (q.type === 'multiple' && Array.isArray(correctAnswer) && correctAnswer.includes(oldVal)) correctAnswer = correctAnswer.map(o => o === oldVal ? value : o);
    return { ...q, options, correctAnswer };
  }));
  const removeOption = (qId, idx) => setQuestions(prev => prev.map(q => {
    if (q.id !== qId || q.options.length <= 1) return q;
    const removedVal = q.options[idx];
    const options = q.options.filter((_, i) => i !== idx);
    let correctAnswer = q.correctAnswer;
    if (q.type === 'single' && correctAnswer === removedVal) correctAnswer = '';
    if (q.type === 'multiple' && Array.isArray(correctAnswer)) correctAnswer = correctAnswer.filter(o => o !== removedVal);
    return { ...q, options, correctAnswer };
  }));
  const toggleCorrectOption = (qId, opt) => setQuestions(prev => prev.map(q => {
    if (q.id !== qId) return q;
    if (q.type === 'single') return { ...q, correctAnswer: opt };
    const current = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
    return { ...q, correctAnswer: current.includes(opt) ? current.filter(o => o !== opt) : [...current, opt] };
  }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
      <div className="bg-white rounded-[32px] w-full max-w-lg shadow-2xl border border-gray-100 overflow-hidden animate-slide-up">
        <div className="bg-indigo-600 p-8 text-white relative">
          <h2 className="text-2xl font-black">Ajouter un élément</h2>
          <p className="text-indigo-100 text-sm mt-1 opacity-80">
            {momentLabel ? `Zone : ${momentLabel}` : 'Configurez les détails de l\'activité.'}
          </p>
          <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-all">✕</button>
        </div>

        <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            {['signature', 'document', 'exercice', 'questionnaire'].map(t => (
              <button
                key={t}
                onClick={() => {
                  setType(t);
                  // Update default title and metadata based on type
                  if (t === 'signature') {
                    setTitle('Émargement de présence');
                    setMetadata({ requiresClientSignature: true, requiresTrainerSignature: false, documentType: 'signature' });
                  } else if (t === 'exercice') {
                    setTitle('');
                    setMetadata({ documentType: 'info' });
                  } else if (t === 'questionnaire') {
                    setTitle('');
                    setMetadata({ documentType: 'questionnaire' });
                    if (questions.length === 0) setQuestions([{ id: Date.now().toString(), text: '', type: 'single', options: ['Option A', 'Option B'], correctAnswer: '' }]);
                  } else {
                    setTitle('');
                    // FIX (2026-10-05) : plus de signature client cochée par défaut pour un simple document —
                    // à cocher volontairement (ce qui passe documentType à 'signature').
                    setMetadata({ requiresClientSignature: false, requiresTrainerSignature: false, documentType: 'info' });
                  }
                }}
                className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all gap-2 ${type === t ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm' : 'border-gray-100 bg-gray-50 text-gray-400 hover:border-indigo-200'}`}
              >
                <span className="text-2xl">{t === 'signature' ? '✍️' : t === 'document' ? '📄' : t === 'questionnaire' ? '📝' : '⚙️'}</span>
                <span className="text-xs font-black uppercase tracking-widest">{t}</span>
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-widest">Libellé de l'activité</label>
            <input
              type="text"
              placeholder={type === 'signature' ? 'Émargement de présence' : type === 'exercice' ? 'Nom de l\'exercice' : type === 'questionnaire' ? 'Titre du questionnaire' : 'Titre du document'}
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-medium transition-all"
            />
          </div>

          {/* AJOUT (2026-09-25) : jusqu'ici ce choix n'existait que pour les documents (plus bas,
              {type === 'document'}) — jamais pour les émargements, qui étaient donc TOUJOURS créés en
              "client uniquement" sans aucun moyen de demander aussi la signature du formateur. */}
          {type === 'signature' && (
            <div className="bg-indigo-50/50 p-4 rounded-2xl space-y-3">
              <label className="block text-xs font-black text-indigo-800 uppercase tracking-widest mb-2">Qui doit signer ?</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={metadata.requiresClientSignature !== false} onChange={e => setMetadata({ ...metadata, requiresClientSignature: e.target.checked })} className="rounded text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-xs font-bold text-indigo-900 uppercase">Client</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={metadata.requiresTrainerSignature === true} onChange={e => setMetadata({ ...metadata, requiresTrainerSignature: e.target.checked })} className="rounded text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-xs font-bold text-indigo-900 uppercase">Formateur</span>
                </label>
              </div>
            </div>
          )}

          {type === 'exercice' && (
            <div>
              <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Énoncé / Consignes de l'exercice</label>
              <textarea
                className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-indigo-500 font-medium text-gray-800 text-sm transition-all resize-none"
                placeholder="Décrivez ici les consignes pour votre client..."
                rows={4}
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
              />
            </div>
          )}

          {type === 'questionnaire' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[['nouveau', '✏️ Créer un questionnaire'], ['existant', '📋 Reprendre un existant']].map(([val, lbl]) => (
                  <button key={val} type="button" onClick={() => setQSource(val)}
                    className={`py-2.5 px-3 rounded-xl text-xs font-black transition-all ${qSource === val ? 'bg-violet-600 text-white' : 'bg-white border border-violet-200 text-gray-600 hover:border-violet-400'}`}>{lbl}</button>
                ))}
              </div>
              {qSource === 'existant' && (
                <div className="space-y-3">
                  {qExistants.length === 0 ? (
                    <p className="text-sm text-gray-500 bg-gray-50 rounded-2xl p-4">Aucun questionnaire pour l'instant. Créez-le d'abord dans le menu « Questionnaires », ou choisissez « Créer un questionnaire ».</p>
                  ) : (
                    <select value={qExistantId}
                      onChange={e => {
                        setQExistantId(e.target.value);
                        const choisi = qExistants.find(x => String(x.id) === String(e.target.value));
                        if (choisi) setTitle(choisi.titre || '');
                      }}
                      className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-medium">
                      <option value="">Choisir un questionnaire ou un quiz…</option>
                      {qExistants.map(x => {
                        const m = parseQMeta(x);
                        const n = (m.questions || []).length;
                        return <option key={x.id} value={x.id}>{m.isQuiz ? '🎯 ' : '📝 '}{x.titre} ({n} question{n > 1 ? 's' : ''})</option>;
                      })}
                    </select>
                  )}
                  {qExistant && (
                    <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4 space-y-1.5">
                      <p className="text-sm font-black text-violet-900">{qExistantMeta.isQuiz ? '🎯 Quiz noté' : '📝 Questionnaire'} · {(qExistantMeta.questions || []).length} question{(qExistantMeta.questions || []).length > 1 ? 's' : ''}{qExistantMeta.isQuiz ? ` · réussite à ${qExistantMeta.seuilReussite ?? 50} %` : ''}</p>
                      <ul className="text-xs text-violet-800 space-y-0.5 max-h-28 overflow-y-auto">
                        {(qExistantMeta.questions || []).map((qq, i) => <li key={qq.id || i}>{i + 1}. {qq.text || '(sans titre)'}</li>)}
                      </ul>
                      <p className="text-xs text-violet-600 pt-1">Pour modifier ses questions, passez par le menu « Questionnaires » : le module sera mis à jour automatiquement.</p>
                    </div>
                  )}
                </div>
              )}
              {qSource === 'nouveau' && (<>
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5">Description (facultatif)</label>
                <textarea
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-400 text-sm resize-none"
                  placeholder="Expliquez au client le fonctionnement, avant qu'il commence..."
                  rows={2}
                  value={qDescription}
                  onChange={e => setQDescription(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-between bg-violet-50 border border-violet-100 rounded-2xl p-3">
                <div>
                  <p className="text-xs font-black text-violet-800 uppercase tracking-widest">🎯 Quiz noté</p>
                  <p className="text-xs text-violet-500 mt-0.5">Le client reçoit une note et un statut Acquis / Non acquis</p>
                </div>
                <button type="button" onClick={() => setIsQuiz(v => !v)}
                  className={`w-12 h-7 rounded-full transition-all relative shrink-0 ${isQuiz ? 'bg-violet-600' : 'bg-gray-200'}`}>
                  <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${isQuiz ? 'left-6' : 'left-1'}`}></span>
                </button>
              </div>

              {isQuiz && (
                <div>
                  <label className="block text-xs font-black text-violet-700 uppercase tracking-widest mb-1.5">Seuil de réussite (%)</label>
                  <input type="number" min="0" max="100" value={seuilReussite}
                    onChange={e => setSeuilReussite(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    className="w-32 p-2.5 bg-white border border-violet-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400" />
                </div>
              )}

              <div className="flex items-center justify-between">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">Questions ({questions.length})</label>
                <button type="button" onClick={addQuestion} className="flex items-center gap-1 text-xs font-black text-violet-600 hover:text-violet-800 uppercase tracking-wider">+ Ajouter une question</button>
              </div>
              {questions.length === 0 && (
                <div className="py-6 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  <p className="text-gray-400 text-xs">Cliquez sur "+ Ajouter une question" pour commencer</p>
                </div>
              )}
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {questions.map((q, qi) => (
                  <div key={q.id} className="bg-indigo-50 rounded-2xl p-4 space-y-3 border border-indigo-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-indigo-600 uppercase tracking-wider">Q{qi + 1}</span>
                      <button type="button" onClick={() => removeQuestion(q.id)} className="w-6 h-6 rounded-lg bg-red-50 text-red-400 text-xs flex items-center justify-center hover:bg-red-100 transition-colors">✕</button>
                    </div>
                    <input
                      type="text"
                      placeholder="Texte de la question..."
                      value={q.text}
                      onChange={e => updateQuestion(q.id, 'text', e.target.value)}
                      className="w-full p-3 bg-white border border-indigo-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-400 text-sm font-medium"
                    />
                    <div className="flex gap-1.5">
                      {[['single', '◉ Unique'], ['multiple', '☑ Multiple'], ['text', '✏️ Libre']].map(([val, lbl]) => (
                        <button key={val} type="button" onClick={() => setQuestionType(q.id, val)}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-black transition-all ${q.type === val ? 'bg-indigo-600 text-white' : 'bg-white border border-indigo-200 text-gray-500 hover:border-indigo-400'}`}>{lbl}</button>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                      <input type="checkbox" checked={q.obligatoire !== false} onChange={e => updateQuestion(q.id, 'obligatoire', e.target.checked)}
                        className="accent-violet-600 w-3.5 h-3.5" />
                      <span className="text-xs font-bold text-gray-500">Obligatoire — le client devra y répondre pour valider</span>
                    </label>
                    {isQuiz && (q.type === 'single' || q.type === 'multiple') && (
                      <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                        <input type="checkbox" checked={q.notee !== false} onChange={e => updateQuestion(q.id, 'notee', e.target.checked)}
                          className="accent-violet-600 w-3.5 h-3.5" />
                        <span className="text-xs font-bold text-gray-500">Question notée — compte dans la note du quiz</span>
                      </label>
                    )}
                    {isQuiz && (q.type === 'text' || q.notee === false) && (
                      <p className="text-[11px] font-bold text-gray-400">Non notée : la réponse est enregistrée mais ne compte pas dans la note.</p>
                    )}
                    {(q.type === 'single' || q.type === 'multiple') && (
                      <div className="space-y-1.5">
                        {qEstNotee(q, isQuiz) && <p className="text-[11px] font-black text-emerald-600 uppercase tracking-widest">Cochez la ou les bonne(s) réponse(s)</p>}
                        {q.options.map((opt, oi) => {
                          const isCorrect = q.type === 'single' ? (q.correctAnswer === opt && opt !== '') : (Array.isArray(q.correctAnswer) && q.correctAnswer.includes(opt) && opt !== '');
                          return (
                            <div key={oi} className="flex items-center gap-2">
                              {qEstNotee(q, isQuiz) ? (
                                <button type="button" onClick={() => toggleCorrectOption(q.id, opt)}
                                  title="Marquer comme bonne réponse"
                                  className={`w-4 h-4 shrink-0 flex items-center justify-center rounded${q.type === 'multiple' ? '' : '-full'} border-2 transition-all ${isCorrect ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 text-transparent hover:border-emerald-400'}`}>
                                  <span className="text-[11px] leading-none">✓</span>
                                </button>
                              ) : (
                                <span className="text-gray-400 text-xs">{q.type === 'single' ? '○' : '□'}</span>
                              )}
                              <input type="text" placeholder={`Option ${oi + 1}`} value={opt} onChange={e => updateOption(q.id, oi, e.target.value)}
                                className={`flex-1 p-2 bg-white border rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-400 ${qEstNotee(q, isQuiz) && isCorrect ? 'border-emerald-300' : 'border-indigo-100'}`} />
                              {q.options.length > 1 && (
                                <button type="button" onClick={() => removeOption(q.id, oi)} className="text-gray-300 hover:text-red-400 text-xs transition-colors">✕</button>
                              )}
                            </div>
                          );
                        })}
                        <button type="button" onClick={() => addOption(q.id)} className="text-xs text-indigo-500 hover:text-indigo-700 font-bold mt-1">+ Option</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5">Message de fin (facultatif)</label>
                <textarea
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-400 text-sm resize-none"
                  placeholder="Un mot pour remercier ou féliciter le client une fois terminé..."
                  rows={2}
                  value={qClosingMessage}
                  onChange={e => setQClosingMessage(e.target.value)}
                />
              </div>
              </>)}
            </div>
          )}

          {type !== 'signature' && type !== 'questionnaire' && (
            <div className="space-y-4 animate-slide-up">
              <div className="space-y-3">
                {/* Modélothèque dropdown */}
                {documentTemplates && Object.keys(documentTemplates).length > 0 && (
                  <select
                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                    value={selectedResourceId}
                    onChange={e => {
                      const url = e.target.value;
                      setSelectedResourceId(url);
                      if (url) {
                        const entry = Object.entries(documentTemplates).find(([, tpl]) => tpl.url === url);
                        if (entry && !title) setTitle(entry[0]);
                      }
                    }}
                  >
                    <option value="">Sélectionner dans la modélothèque...</option>
                    {Object.entries(documentTemplates).map(([nom, tpl]) => (
                      <option key={nom} value={tpl.url || ''}>{nom}</option>
                    ))}
                  </select>
                )}

                <div className="flex items-center gap-4">
                  <div className="h-px bg-gray-100 flex-1"></div>
                  <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">OU</span>
                  <div className="h-px bg-gray-100 flex-1"></div>
                </div>

                <label className={`w-full flex items-center justify-center gap-2 p-4 rounded-2xl border-2 border-dashed border-gray-200 text-gray-500 font-bold text-xs cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-all ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {isUploading ? 'Importation en cours...' : '📁 Importer un fichier (PDF, Word, Excel)'}
                  <input
                    type="file"
                    className="hidden"
                    disabled={isUploading}
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                </label>
                {selectedResourceId && !isUploading && (
                  <p className="text-xs text-green-600 font-bold flex items-center gap-1">✓ Fichier sélectionné</p>
                )}
              </div>

              {type === 'document' && (
                <div className="bg-indigo-50/50 p-4 rounded-2xl space-y-3">
                  {/* Destinataire */}
                  <div>
                    <label className="block text-xs font-black text-indigo-800 uppercase tracking-widest mb-2">Destinataire</label>
                    <div className="flex gap-4">
                      {[['client', 'Client'], ['formateur', 'Formateur'], ['both', 'Les deux']].map(([val, lbl]) => (
                        <label key={val} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" checked={destination === val} onChange={() => setDestination(val)} className="rounded-full text-indigo-600 focus:ring-indigo-500" />
                          <span className="text-sm font-bold text-gray-700">{lbl}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Signature requise */}
                  <div className="pt-3 border-t border-indigo-100">
                    <label className="block text-xs font-black text-indigo-800 uppercase tracking-widest mb-2">Signature</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={metadata.requiresClientSignature} onChange={e => setMetadata({ ...metadata, requiresClientSignature: e.target.checked, documentType: e.target.checked ? 'signature' : metadata.requiresTrainerSignature ? 'signature' : 'info' })} className="rounded text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-xs font-bold text-indigo-900 uppercase">Client</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={metadata.requiresTrainerSignature} onChange={e => setMetadata({ ...metadata, requiresTrainerSignature: e.target.checked, documentType: e.target.checked ? 'signature' : metadata.requiresClientSignature ? 'signature' : 'info' })} className="rounded text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-xs font-bold text-indigo-900 uppercase">Formateur</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pt-4 pb-2">
            <button
              onClick={() => {
                const questionsMeta = type !== 'questionnaire' ? {}
                  : (qSource === 'existant' && qExistant)
                    ? { ...qContenu(qExistantMeta), source_questionnaire_id: qExistant.id }
                    : { questions, isQuiz, ...(isQuiz ? { seuilReussite } : { seuilReussite: undefined }), description: qDescription.trim() || undefined, closingMessage: qClosingMessage.trim() || undefined };
                onSave({ type, title, metadata: { ...metadata, destination, ...questionsMeta }, resourceId: selectedResourceId, fileUrl: (selectedResourceId && selectedResourceId.includes('/')) ? selectedResourceId : null, destination, instructions: type === 'exercice' ? instructions : null });
                onClose();
              }}
              disabled={!title.trim() || isUploading || (type !== 'signature' && type !== 'questionnaire' && !selectedResourceId) || (type === 'questionnaire' && qSource === 'existant' && !qExistant) || (type === 'questionnaire' && qSource === 'nouveau' && (questions.length === 0 || (isQuiz && qQuizInvalide(questions))))}
              className="w-full bg-indigo-600 hover:bg-black text-white font-black py-4 rounded-2xl shadow-xl shadow-indigo-100 transition-all disabled:opacity-50"
            >
              {isUploading ? 'Veuillez patienter...' : 'Enregistrer l\'activité'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Composant Modal pour l'ajout d'éléments personnalisés par le Formateur ---
const SessionItemModal = ({ isOpen, onClose, onSave, pedagogicalResources, supabase, clientSessions = [], preSelectedSessionId = null, preSelectedLabel = null }) => {
  const [choice, setChoice] = useState('existing'); // 'existing' or 'new'
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [type, setType] = useState('signature');
  const [title, setTitle] = useState('');
  const [isToSign, setIsToSign] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [instructions, setInstructions] = useState('');

  // States for 'new' choice
  const [newDate, setNewDate] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');

  React.useEffect(() => {
    if (isOpen) {
      setChoice('existing');
      setType('signature');
      setTitle('');
      setIsToSign(false);
      setSelectedResourceId('');
      setIsUploading(false);
      setSelectedSessionId(preSelectedSessionId || '');
      setNewDate('');
      setNewStart('');
      setNewEnd('');
      setInstructions('');
    }
  }, [isOpen, preSelectedSessionId]);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      // Nom de fichier lisible (basé sur le nom réel du fichier) au lieu d'un simple horodatage +
      // suite aléatoire — c'est ce nom qui apparaît quand le formateur clique sur "Consulter" et
      // télécharge le fichier, donc un nom générique ne renseignait pas sur le contenu du document.
      const baseName = file.name.replace(/\.[^/.]+$/, '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'document';
      const uniqueSuffix = Math.random().toString(36).substring(2, 8);
      const fileName = `${baseName}-${uniqueSuffix}.${fileExt}`;
      const filePath = `custom-session-items/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(filePath);

      setSelectedResourceId(publicUrl);
    } catch (err) {
      toast.error('Erreur lors de l\'import du fichier.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  // AJOUT (2026-09-19) : exclut les exercices synthétiques de début/fin (numero_seance: null) de ce
  // sélecteur — on ne doit pouvoir rattacher un nouvel élément qu'à une vraie séance planifiée.
  const groupedSessions = clientSessions.filter(s => s.numero_seance !== null && s.numero_seance !== undefined).reduce((acc, s) => {
    if (!acc[s.numero_seance]) acc[s.numero_seance] = s;
    return acc;
  }, {});
  const uniqueSessionOptions = Object.values(groupedSessions).sort((a, b) => a.numero_seance - b.numero_seance);

  return (
    <div className="fixed inset-0 bg-gray-950/80 z-[200] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 animate-slide-up">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center shadow-lg"><Plus size={20} /></div>
            <h3 className="text-xl font-black text-gray-900 leading-none">Nouvel Élément</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-400">✕</button>
        </div>

        <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">

          {/* Contexte séance : badge si pré-sélectionné, sinon toggle + sélecteur */}
          {preSelectedSessionId ? (
            <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-2xl px-4 py-3">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-lg flex items-center justify-center shrink-0">
                <Layout size={14} />
              </div>
              <div>
                <p className="text-[11px] font-black text-indigo-400 uppercase tracking-widest">Ajout dans</p>
                <p className="text-sm font-black text-indigo-900">{preSelectedLabel || 'Séance sélectionnée'}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex p-1 bg-gray-100 rounded-2xl gap-1">
                <button
                  onClick={() => setChoice('existing')}
                  className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all ${choice === 'existing' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Séance Existante
                </button>
                <button
                  onClick={() => setChoice('new')}
                  className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all ${choice === 'new' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Nouvelle Séance
                </button>
              </div>

              {choice === 'existing' ? (
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Choisir la séance</label>
                  <select
                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-gray-800 outline-none focus:border-indigo-500"
                    value={selectedSessionId}
                    onChange={e => setSelectedSessionId(e.target.value)}
                  >
                    <option value="">-- Sélectionner --</option>
                    {uniqueSessionOptions.map(s => (
                      <option key={s.id} value={s.id}>SÉANCE {s.numero_seance} {s.nom ? `(${s.nom.split(' - ')[0]})` : ''}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-4 animate-fade-in">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Date de la séance</label>
                      <input
                        type="date"
                        className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-gray-800"
                        value={newDate}
                        onChange={e => setNewDate(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Début</label>
                        <input
                          type="time"
                          className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-gray-800"
                          value={newStart}
                          onChange={e => setNewStart(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Fin</label>
                        <input
                          type="time"
                          className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl font-bold text-gray-800"
                          value={newEnd}
                          onChange={e => setNewEnd(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Titre */}
          <div>
            <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Titre de l'élément</label>
            <input
              autoFocus
              className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-indigo-500 font-bold text-gray-800 transition-all"
              placeholder="Ex: Compte-rendu mi-parcours"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          {/* Type d'activité */}
          <div>
            <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Type d'activité</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'signature', label: 'Émargement', icon: '✍️' },
                { id: 'document', label: 'Document', icon: '📄' },
                { id: 'exercice', label: 'Exercice', icon: '⚙️' }
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={`p-3 rounded-2xl border-2 flex flex-col items-center gap-1 transition-all ${type === t.id ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-50 bg-gray-50 text-gray-400 hover:border-gray-200'}`}
                >
                  <span className="text-xl">{t.icon}</span>
                  <span className="text-[11px] font-black uppercase text-center">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Énoncé / Consignes — uniquement pour les exercices */}
          {type === 'exercice' && (
            <div>
              <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Énoncé / Consignes de l'exercice</label>
              <textarea
                className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-indigo-500 font-medium text-gray-800 text-sm transition-all resize-none"
                placeholder="Décrivez ici les consignes pour votre client..."
                rows={4}
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
              />
            </div>
          )}

          {/* Fichier / Ressource */}
          {(type === 'document' || type === 'exercice') && (
            <div className="space-y-4">
              <label className="block text-xs font-black text-gray-400 uppercase mb-2 tracking-widest">Fichier / Ressource</label>
              <div className="flex flex-col gap-3">
                <input
                  type="file"
                  accept={(type === 'signature' || isToSign) ? ".pdf" : undefined}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if ((type === 'signature' || isToSign) && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                        toast.error('Erreur : Seuls les fichiers PDF sont autorisés pour les documents nécessitant une signature.');
                        e.target.value = '';
                        return;
                      }
                      setTitle(file.name.replace(/\.[^/.]+$/, ""));
                      handleFileUpload(file);
                    }
                  }}
                  className="w-full text-xs p-3 bg-gray-50 border border-dashed border-gray-300 rounded-2xl"
                />
                <div className="text-center font-bold text-gray-300 text-xs">OU SÉLECTIONNER DANS LA BIBLIOTHÈQUE</div>
                <select
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-bold"
                  value={selectedResourceId}
                  onChange={e => setSelectedResourceId(e.target.value)}
                >
                  <option value="">-- Choisir une ressource existante --</option>
                  {pedagogicalResources.map(res => (
                    <option key={res.path || res.name} value={res.url || res.name}>{res.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {type === 'document' && (
            <div className="flex items-center gap-3 p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
              <input
                type="checkbox"
                id="isToSignCustom"
                checked={isToSign}
                onChange={e => setIsToSign(e.target.checked)}
                className="w-5 h-5 rounded-lg border-indigo-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="isToSignCustom" className="text-xs font-bold text-indigo-900 cursor-pointer">
                Nécessite une signature du client / formateur
              </label>
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 flex gap-3">
          <button onClick={onClose} className="flex-1 py-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors">Annuler</button>
          <button
            disabled={
              !title ||
              isUploading ||
              ((type === 'document' || type === 'exercice') && !selectedResourceId) ||
              (!preSelectedSessionId && choice === 'existing' && !selectedSessionId)
            }
            onClick={() => {
              const effectiveSessionId = preSelectedSessionId || selectedSessionId;
              const sessionData = (preSelectedSessionId || choice === 'existing')
                ? clientSessions.find(s => s.id === effectiveSessionId)
                : { date: newDate, heure_debut: newStart, heure_fin: newEnd, isNewSession: true };

              onSave({
                title,
                type,
                resourceId: selectedResourceId,
                isToSign: type === 'signature' ? true : isToSign,
                sessionChoice: preSelectedSessionId ? 'existing' : choice,
                sessionData,
                instructions: type === 'exercice' ? instructions : null
              });
              onClose();
            }}
            className="flex-[2] py-4 bg-indigo-600 text-white font-black text-sm rounded-2xl shadow-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95 transition-all"
          >
            {isUploading ? 'Chargement...' : 'Ajouter l\'élément'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// COMPOSANTS DE VUES EXTRAITS DE APP
// ==========================================

// REMARQUE (2026-07-28) : la création automatique d'un module de démarrage
// "Bilan de Compétences 24h" pour chaque nouvel organisme a été retirée à la
// demande explicite de l'utilisateur — chaque nouvel organisme doit désormais
// démarrer avec une page Modules totalement vide, sans rien créer par défaut.
// (Cette vue SignupView n'est de toute façon plus utilisée en production : le
// vrai flux d'inscription passe par pages/Signup.js + pages/SetupOrganisation.js
// et une fonction Edge Supabase "setup-organisation" — voir cette dernière si
// le même comportement de module par défaut doit aussi y être retiré.)

// ─── Vue publique : questionnaire d'entretien préalable envoyé à un prospect ──────────────────
// Accessible sans compte SkorUp, via /questionnaire?t=<token> (lien reçu par email — voir
// api/prospects.js action=envoyer). Demandé par l'utilisateur le 22/09/2026. Reprend le même
// rendu de questions (text/single/multiple) que QuestionnaireFillerModal plus bas dans ce
// fichier, adapté en page pleine plutôt qu'en modale puisqu'il n'y a ici ni app ni utilisateur
// connecté autour. Tout passe par api/prospects.js (clé service_role côté serveur) : cette vue
// n'appelle jamais directement Supabase.
const ProspectQuestionnaireView = () => {
  const token = React.useMemo(() => new URLSearchParams(window.location.search).get('t') || '', []);
  const [etat, setEtat] = useState('chargement'); // chargement | a_remplir | rempli | expire | erreur
  const [erreurMsg, setErreurMsg] = useState('');
  const [titre, setTitre] = useState('');
  const [questions, setQuestions] = useState([]);
  const [prenom, setPrenom] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setEtat('erreur'); setErreurMsg('Ce lien est incomplet.'); return; }
    fetch(`/api/prospects?action=lire&t=${encodeURIComponent(token)}`)
      .then(async (resp) => {
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) { setEtat('erreur'); setErreurMsg(data.error || 'Ce lien est invalide.'); return; }
        if (data.etat === 'rempli') { setEtat('rempli'); return; }
        if (data.etat === 'expire') { setEtat('expire'); return; }
        setTitre(data.titre || '');
        setQuestions(data.questions || []);
        setPrenom(data.prenom || '');
        setEtat('a_remplir');
      })
      .catch(() => { setEtat('erreur'); setErreurMsg('Impossible de charger le questionnaire pour le moment.'); });
  }, [token]);

  const setAnswer = (qId, value) => setAnswers(prev => ({ ...prev, [qId]: value }));
  const toggleMultiple = (qId, option) => {
    setAnswers(prev => {
      const current = prev[qId] || [];
      return { ...prev, [qId]: current.includes(option) ? current.filter(o => o !== option) : [...current, option] };
    });
  };

  // FIX (demande utilisateur, 2026-09-22) : seules les questions cochees "obligatoire" par
  // l'admin bloquent la soumission - un champ obligatoire absent (anciens modeles crees avant
  // cette option) est traite comme facultatif, pas comme obligatoire.
  const isComplete = questions.every(q => {
    if (!q.obligatoire) return true;
    if (q.type === 'text') return (answers[q.id] || '').trim().length > 0;
    if (q.type === 'single') return !!answers[q.id];
    if (q.type === 'multiple') return (answers[q.id] || []).length > 0;
    return true;
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    setErreurMsg('');
    try {
      const resp = await fetch(`/api/prospects?action=soumettre`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ t: token, reponses: answers }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) { setErreurMsg(data.error || 'Ce lien n\'est plus valable.'); setSubmitting(false); return; }
      setEtat('rempli');
    } catch (e) {
      setErreurMsg('Erreur réseau — vérifiez votre connexion et réessayez.');
      setSubmitting(false);
    }
  };

  if (etat === 'chargement') {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
        <div className="w-12 h-12 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin mb-4"></div>
        <div className="text-gray-400 font-bold uppercase tracking-widest text-xs animate-pulse">Chargement du questionnaire...</div>
      </div>
    );
  }

  if (etat === 'erreur') {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center border border-gray-100">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 text-red-500 flex items-center justify-center text-2xl">✕</div>
          <h1 className="text-xl font-black text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-500 text-sm">{erreurMsg}</p>
        </div>
      </div>
    );
  }

  if (etat === 'expire') {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center border border-gray-100">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center text-2xl">⏱</div>
          <h1 className="text-xl font-black text-gray-900 mb-2">Ce lien a expiré</h1>
          <p className="text-gray-500 text-sm">Contactez directement l'organisme qui vous l'a envoyé pour recevoir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  if (etat === 'rempli') {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center border border-gray-100">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center text-2xl">✓</div>
          <h1 className="text-xl font-black text-gray-900 mb-2">Merci !</h1>
          <p className="text-gray-500 text-sm">Vos réponses ont bien été transmises. Vous pouvez fermer cette page.</p>
        </div>
      </div>
    );
  }

  // etat === 'a_remplir'
  return (
    <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
      <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-violet-600 p-6 text-white shrink-0">
          <p className="text-violet-200 text-xs font-bold uppercase tracking-widest mb-1">SkorUp</p>
          <h1 className="text-xl font-black flex items-center gap-2">📝 {titre}</h1>
          <p className="text-violet-200 text-sm mt-1">
            {prenom ? `Bonjour ${prenom}, ` : ''}{questions.length} question{questions.length > 1 ? 's' : ''} à compléter
          </p>
        </div>
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {questions.map((q, qi) => (
            <div key={q.id} className="space-y-3">
              <p className="font-bold text-gray-900 text-sm leading-relaxed">
                <span className="text-violet-500 font-black mr-1">{qi + 1}.</span>
                {q.text || <span className="text-gray-400 italic">Question</span>}
                {q.obligatoire
                  ? <span className="text-red-500 ml-1">*</span>
                  : <span className="text-gray-400 font-normal text-xs ml-1.5">(facultatif)</span>}
              </p>
              {q.type === 'text' && (
                <textarea
                  placeholder="Votre réponse..."
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none outline-none focus:ring-2 focus:ring-violet-400 transition-all"
                  rows={3}
                />
              )}
              {q.type === 'single' && (
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => (
                    <label key={oi} onClick={() => setAnswer(q.id, opt)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${answers[q.id] === opt ? 'border-violet-500 bg-violet-50' : 'border-gray-100 hover:border-violet-200 bg-gray-50'}`}>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${answers[q.id] === opt ? 'border-violet-600' : 'border-gray-300'}`}>
                        {answers[q.id] === opt && <div className="w-2 h-2 bg-violet-600 rounded-full"></div>}
                      </div>
                      <span className="text-sm text-gray-700 select-none">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.type === 'multiple' && (
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => {
                    const checked = (answers[q.id] || []).includes(opt);
                    return (
                      <label key={oi} onClick={() => toggleMultiple(q.id, opt)}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${checked ? 'border-violet-500 bg-violet-50' : 'border-gray-100 hover:border-violet-200 bg-gray-50'}`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${checked ? 'border-violet-600 bg-violet-600' : 'border-gray-300'}`}>
                          {checked && <span className="text-white text-[11px] font-black leading-none">✓</span>}
                        </div>
                        <span className="text-sm text-gray-700 select-none">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-gray-100 shrink-0">
          {erreurMsg && <p className="text-[11px] text-red-500 text-center mb-3">{erreurMsg}</p>}
          <button onClick={handleSubmit} disabled={!isComplete || submitting}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white font-black py-4 rounded-2xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? '⏳ Envoi en cours…' : '✓ Soumettre mes réponses'}
          </button>
          {!isComplete && <p className="text-xs text-gray-400 text-center mt-2">Répondez à toutes les questions obligatoires (*) pour soumettre</p>}
        </div>
      </div>
    </div>
  );
};

const SignupView = ({ supabase, onComplete }) => {
  const [orgName, setOrgName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    setIsLoading(true);
    try {
      // 1. Créer le compte Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({ email: email.trim(), password });
      if (authError) throw authError;

      // 2. Créer l'organisation
      const { data: org, error: orgError } = await supabase.from('organisations')
        .insert([{ nom: orgName.trim() }]).select().single();
      if (orgError) throw orgError;

      // 3. Créer l'entrée admin dans utilisateurs — auth_uid explicitement lié à authData.user.id pour
      // que toute policy RLS qui vérifie auth.uid() = utilisateurs.auth_uid puisse s'appliquer ici aussi
      // (utilisateurs.id est un entier auto-incrémenté, PAS un UUID — voir le flux d'invitation formateur
      // un peu plus bas dans ce fichier qui utilise déjà auth_uid pour la même raison ; ne jamais y mettre
      // authData.user.id, qui casserait l'insert avec une erreur de type).
      // ATTENTION (signalé par l'audit, non entièrement corrigeable côté client) : cet insert crée un
      // organisme + un compte admin directement depuis le navigateur avec la clé anon. La seule vraie
      // protection contre un utilisateur qui s'auto-promouvrait admin d'un organisme existant (au lieu
      // du nouvel organisme tout juste créé ci-dessus) est une policy RLS côté Supabase sur l'INSERT de
      // 'utilisateurs' — à vérifier/durcir côté base (voir le message envoyé séparément à ce sujet).
      const { error: userError } = await supabase.from('utilisateurs').insert([{
        nom: adminName.trim(),
        email: email.trim(),
        role: 'admin',
        organisation_id: org.id,
        auth_uid: authData.user.id
      }]);
      if (userError) throw userError;

      // 4. (Ancienne étape "injecter les templates par défaut" retirée le 2026-07-28 —
      // un nouvel organisme démarre désormais avec une page Modules vide.)

      // 5. Si la session est directement disponible, connecter
      if (authData.session) {
        onComplete();
      } else {
        setNeedsConfirmation(true);
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
    }
    setIsLoading(false);
  };

  if (needsConfirmation) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center border border-gray-100">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-12 h-12 object-contain" /></div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-3">Confirmez votre email</h1>
          <p className="text-gray-500 mb-6">Un lien de confirmation a été envoyé à <strong>{email}</strong>. Cliquez dessus pour activer votre compte.</p>
          <button onClick={() => { window.history.replaceState(null, '', '/'); window.location.reload(); }} className="text-sm font-bold text-violet-600 hover:text-violet-700">Retour à la connexion</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
      <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md border border-gray-100 animate-fade-in">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-12 h-12 object-contain" /></div>
        <h1 className="text-2xl font-extrabold text-gray-900 mb-1 text-center">Créer votre espace</h1>
        <p className="text-gray-500 mb-8 text-center text-sm">Votre organisme de formation en quelques secondes.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Nom de l'organisme</label>
            <input type="text" required value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="ex : Mon Organisme Formation" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Votre nom complet</label>
            <input type="text" required value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="ex : Marie Dupont" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Adresse email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Mot de passe</label>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="8 caractères minimum" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Confirmer le mot de passe</label>
            <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Répéter le mot de passe" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>

          {error && <p className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl">{error}</p>}

          <button type="submit" disabled={isLoading} className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
            {isLoading ? 'Création en cours...' : 'Créer mon espace'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Déjà inscrit ?{' '}
          <button onClick={() => { window.history.replaceState(null, '', '/'); window.location.reload(); }} className="font-bold text-violet-600 hover:text-violet-700">Se connecter</button>
        </p>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// CONFORMITÉ (2026-10-23) : les textes légaux sont publiés sur le site (une seule version, à jour) ;
// l'application y renvoie. Les anciens textes intégrés (juillet 2026, périmés) ont été retirés.
// ──────────────────────────────────────────────────────────────
const LiensLegaux = ({ className = '', lienClassName = '' }) => (
  <div className={className}>
    {LIENS_LEGAUX.map(k => (
      <a key={k} href={PAGES_LEGALES[k].url} target="_blank" rel="noopener noreferrer" className={lienClassName}>
        {PAGES_LEGALES[k].court || PAGES_LEGALES[k].titre}
      </a>
    ))}
  </div>
);

// Enregistre l'acceptation (une ligne par texte ; déjà enregistrée = déjà acceptée).
const enregistrerAcceptation = async (documents, { role, organisationId, source }) => {
  for (const document of documents) {
    const { error } = await supabase.from('conditions_acceptees')
      .insert({ document, version: CONDITIONS_VERSION, role: role || null, organisation_id: organisationId ? String(organisationId) : null, source });
    if (error && error.code !== '23505') { console.error('[conditions] enregistrement impossible', document, error); return false; }
  }
  return true;
};

// Fenêtre affichée à la connexion tant que la version en vigueur des conditions n'a pas été acceptée.
// Sans la table conditions_acceptees (SQL non lancé) ou en cas d'erreur de lecture : rien n'est demandé.
const PorteConditions = ({ userRole, currentOrgId, onLogout }) => {
  const [aAccepter, setAAccepter] = useState([]);
  const [premiereFois, setPremiereFois] = useState(false);
  const [coche, setCoche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    let annule = false;
    setAAccepter([]); setCoche(false);
    if (!userRole) return undefined;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || annule) return;
        const { data, error } = await supabase.from('conditions_acceptees').select('document, version').eq('user_id', user.id);
        if (annule || error) return;
        const faits = new Set((data || []).filter(r => r.version === CONDITIONS_VERSION).map(r => r.document));
        let manquants = documentsAAccepter(userRole).filter(d => !faits.has(d));
        // Acceptation donnée à l'inscription (avant la confirmation de l'email) : enregistrée maintenant.
        const meta = user.user_metadata && user.user_metadata.conditions_acceptees;
        if (manquants.length && meta && meta.version === CONDITIONS_VERSION && Array.isArray(meta.documents)) {
          const dejaDonnes = manquants.filter(d => meta.documents.includes(d));
          if (dejaDonnes.length && await enregistrerAcceptation(dejaDonnes, { role: userRole, organisationId: currentOrgId, source: 'inscription' })) {
            manquants = manquants.filter(d => !dejaDonnes.includes(d));
          }
        }
        if (annule) return;
        // « Première fois » : compte créé après la mise en place des conditions ; sinon « Nos conditions évoluent ».
        setPremiereFois((data || []).length === 0 && String(user.created_at || '') >= CONDITIONS_VERSION);
        setAAccepter(manquants);
      } catch (_) { /* on ne bloque jamais l'accès sur une erreur technique */ }
    })();
    return () => { annule = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  if (!aAccepter.length) return null;
  const accepter = async () => {
    if (!coche || envoi) return;
    setEnvoi(true);
    const ok = await enregistrerAcceptation(aAccepter, { role: userRole, organisationId: currentOrgId, source: 'connexion' });
    setEnvoi(false);
    setAAccepter([]);
    if (ok) toast.success('Merci, votre acceptation est enregistrée.');
    else toast.error("Votre acceptation n'a pas pu être enregistrée : elle vous sera redemandée à votre prochaine connexion.", { duration: 8000 });
  };
  const estAdmin = userRole === 'admin';
  const noms = aAccepter.map(d => PAGES_LEGALES[d].titre);
  const liste = noms.length > 1 ? noms.slice(0, -1).join(', ') + ' et ' + noms[noms.length - 1] : noms[0];
  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[1000] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="porte-conditions-titre">
        <h2 id="porte-conditions-titre" className="text-xl font-black text-gray-900 mb-2">
          {premiereFois ? "Conditions d'utilisation de SkorUp" : 'Nos conditions évoluent'}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {estAdmin
            ? (premiereFois
              ? "Pour utiliser SkorUp, merci de prendre connaissance des documents ci-dessous et de les accepter."
              : "Nous avons mis à jour nos documents contractuels, notamment pour préciser nos engagements sur la protection des données de vos bénéficiaires (accord de traitement des données). Merci d'en prendre connaissance et de les accepter pour continuer.")
            : "SkorUp est le logiciel utilisé par votre organisme de formation. Pour y accéder, merci de prendre connaissance de ses conditions d'utilisation et de les accepter."}
        </p>
        <ul className="space-y-2 mb-4">
          {[...aAccepter, 'confidentialite'].map(d => (
            <li key={d}>
              <a href={PAGES_LEGALES[d].url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-violet-300 hover:bg-violet-50 text-sm font-bold text-gray-800">
                <span>{PAGES_LEGALES[d].titre}{d === 'confidentialite' ? ' (pour information)' : ''}</span>
                <span className="text-violet-600 text-xs shrink-0">Lire ↗</span>
              </a>
            </li>
          ))}
        </ul>
        <label className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 cursor-pointer mb-5">
          <input type="checkbox" checked={coche} onChange={e => setCoche(e.target.checked)} className="mt-0.5 w-4 h-4 accent-violet-600 shrink-0" />
          <span className="text-sm text-gray-700">J'ai lu et j'accepte les {liste}.</span>
        </label>
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button onClick={onLogout} className="sm:flex-1 px-5 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl">Me déconnecter</button>
          <button onClick={accepter} disabled={!coche || envoi} className="sm:flex-1 px-5 py-3 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {envoi ? 'Enregistrement…' : 'Accepter et continuer'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-4">Version du {CONDITIONS_VERSION.split('-').reverse().join('/')}. La date de votre acceptation est conservée.</p>
      </div>
    </div>
  );
};

// FIX (2026-10-09) : un administrateur existant tombait sur « Finaliser votre espace » (création d'un
// NOUVEL organisme) au lieu de son espace : son profil était recherché UNIQUEMENT par adresse email, et
// cette adresse avait été effacée en enregistrant la page Paramètres (champ email chargé vide puis
// sauvegardé tel quel). Le profil est maintenant retrouvé d'abord par le lien de connexion
// (auth_uid pour l'équipe, id pour un client), qui ne change jamais, puis seulement par email.
const escapeLike = (v) => String(v).replace(/[\\%_]/g, (m) => '\\' + m);
const findAccountProfile = async (authUser) => {
  if (!authUser) return null;
  const uid = authUser.id;
  const email = String(authUser.email || '').trim();
  if (uid) {
    const { data } = await supabase.from('utilisateurs').select('role, id, organisation_id').eq('auth_uid', uid).order('id', { ascending: true }).limit(1);
    if (data && data[0] && data[0].role) return data[0];
  }
  if (email) {
    const { data } = await supabase.from('utilisateurs').select('role, id, organisation_id').ilike('email', escapeLike(email)).order('id', { ascending: true }).limit(1);
    if (data && data[0] && data[0].role) return data[0];
  }
  if (uid) {
    const { data } = await supabase.from('clients').select('id, organisation_id').eq('id', uid).limit(1);
    if (data && data[0]) return { role: 'client', ...data[0] };
  }
  if (email) {
    const { data } = await supabase.from('clients').select('id, organisation_id').ilike('email_contact', escapeLike(email)).limit(1);
    if (data && data[0]) return { role: 'client', ...data[0] };
  }
  return null;
};

// Met à jour la fiche "utilisateurs" du compte connecté : retrouvée par auth_uid (lien de connexion),
// sinon par email (anciens comptes sans auth_uid). Une adresse email vide n'est JAMAIS enregistrée :
// c'est elle qui permet de retrouver le compte à la connexion.
const updateOwnStaffProfile = async (authUser, fields) => {
  const payload = { ...fields };
  if ('email' in payload) {
    const e = String(payload.email || '').trim();
    if (e) payload.email = e; else delete payload.email;
  }
  let result = await supabase.from('utilisateurs').update(payload).eq('auth_uid', authUser.id).select('id');
  if (!result.error && (!result.data || result.data.length === 0) && authUser.email) {
    result = await supabase.from('utilisateurs').update(payload).eq('email', authUser.email).select('id');
  }
  return result;
};

const LoginView = ({ handleLogin, supabase, successMessage, onNeedsSetup }) => {
  const [email, setEmail] = useState(() => {
    // Tenter de pré-remplir l'email depuis l'URL (#email=... ou ?email=...)
    const params = new URLSearchParams(window.location.hash.substring(1) || window.location.search);
    return params.get('email') || '';
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // AJOUT (2026-10-12) : « Première connexion » — des bénéficiaires sans mot de passe (lien d'invitation
  // expiré, perdu...) cliquaient sur « Créer votre espace » (réservé aux NOUVEAUX organismes) et se
  // retrouvaient bloqués. Ils ont désormais un bouton clair qui leur envoie un lien pour créer leur
  // mot de passe. Lien direct possible : /?premiere-connexion=1 (utilisé par la page d'inscription).
  const [showForgotPassword, setShowForgotPassword] = useState(() => {
    try { return new URLSearchParams(window.location.search).has('premiere-connexion'); } catch (e) { return false; }
  });
  const [forgotMode, setForgotMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).has('premiere-connexion') ? 'first' : 'forgot'; } catch (e) { return 'forgot'; }
  });
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setIsLoading(true);

    // 1. Authentification via Supabase
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password
    });

    if (authError) {
      console.error('Erreur connexion:', authError);
      if (authError.message.includes('Invalid login credentials')) {
        setErrorMsg('Email ou mot de passe incorrect.');
      } else if (authError.message.includes('Email not confirmed')) {
        setErrorMsg('Votre email n\'a pas encore été confirmé.');
      } else {
        setErrorMsg(authError.message);
      }
      setIsLoading(false);
      return;
    }

    // 2. Chercher le rôle dans la base de données
    const userEmail = authData.user?.email;
    if (userEmail) {
      // D'abord chercher dans utilisateurs (formateurs/admin)
      const userData = await findAccountProfile(authData.user);
      const dbError = null;

      if (userData && userData.role) {
        handleLogin(userData.role, userData.id, userData.organisation_id);
        setIsLoading(false);
        return;
      }

      // Si pas trouvé, chercher dans la table 'clients' (insensible à la casse)
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id, organisation_id')
        .ilike('email_contact', escapeLike(userEmail))
        .single();

      if (clientData) {
        handleLogin('client', clientData.id, clientData.organisation_id);
        setIsLoading(false);
        return;
      } else {
        const metaRole = authData.user?.user_metadata?.role;
        if (metaRole === 'client') {
          // La requête clients (par email, ilike) a été bloquée par RLS mais l'utilisateur est un client :
          // son clients.id est identique à son UUID Auth. On retente une lecture par id (auth.uid() = id
          // est en général autorisé par les policies RLS même quand la lecture par email ne l'est pas) afin
          // de récupérer organisation_id — sans quoi currentOrgId resterait null pour toute la session et
          // toutes les requêtes scopées par organisation_id ne renverraient plus rien pour ce client.
          const { data: selfClientData } = await supabase
            .from('clients')
            .select('organisation_id')
            .eq('id', authData.user.id)
            .maybeSingle();
          handleLogin('client', authData.user.id, selfClientData?.organisation_id || null);
          setIsLoading(false);
          return;
        } else if (metaRole === 'formateur') {
          setErrorMsg('Profil introuvable. Contactez votre administrateur.');
          setIsLoading(false);
          return;
        } else {
          // Admin sans organisation → setup
          console.error('Utilisateur non trouvé dans les DBs:', dbError || clientError);
          onNeedsSetup();
          setIsLoading(false);
          return;
        }
      }
    }

    setIsLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setIsLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin });

    if (error) {
      setErrorMsg(error.message);
    } else {
      setResetSent(true);
    }
    setIsLoading(false);
  };

  if (showForgotPassword) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center border border-gray-100 animate-fade-in">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-12 h-12 object-contain" /></div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">{forgotMode === 'first' ? 'Première connexion' : 'Mot de passe oublié'}</h1>
          <p className="text-gray-500 mb-8">
            {forgotMode === 'first'
              ? "Vous êtes accompagné(e) par un organisme de formation ? Saisissez l'adresse email à laquelle il vous a inscrit(e) : vous recevrez un lien pour créer votre mot de passe."
              : "Saisissez votre email pour réinitialiser l'accès."}
          </p>

          {resetSent ? (
            <div className="bg-green-50 text-green-700 p-4 rounded-xl text-sm font-medium mb-6 text-left">
              Si cette adresse est enregistrée, un email vient de vous être envoyé : cliquez sur le lien qu'il contient pour {forgotMode === 'first' ? 'créer' : 'choisir'} votre mot de passe.
              <span className="block mt-2 text-green-600 text-xs font-normal">Rien reçu après quelques minutes ? Vérifiez vos courriers indésirables, ou demandez à votre organisme de formation de vous renvoyer le lien.</span>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-5 text-left">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Adresse email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                />
              </div>

              {errorMsg && (
                <p className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isLoading ? 'Envoi en cours...' : (forgotMode === 'first' ? 'Recevoir mon lien de connexion' : 'Envoyer le lien')}
              </button>
            </form>
          )}

          <button
            type="button"
            onClick={() => { setShowForgotPassword(false); setResetSent(false); setErrorMsg(''); setForgotMode('forgot'); try { window.history.replaceState(null, '', window.location.pathname); } catch (e) { /* rien */ } }}
            className="mt-6 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors"
          >
            ← Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-dvh bg-gray-50 p-4">
      <div className="bg-white p-10 rounded-3xl shadow-xl w-full max-w-md text-center border border-gray-100 animate-fade-in">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-12 h-12 object-contain" /></div>
        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Connexion à SkorUp</h1>
        <p className="text-gray-500 mb-8">Connectez-vous avec vos identifiants.</p>

        {successMessage && (
          <div className="mb-6 p-4 rounded-xl text-sm font-medium bg-green-50 text-green-700">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 text-left">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Adresse email</label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="votre@email.com"
              className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 focus:border-transparent transition-all"
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-xs font-bold text-gray-400 uppercase">Mot de passe</label>
              <button
                type="button"
                onClick={() => { setForgotMode('forgot'); setShowForgotPassword(true); setErrorMsg(''); }}
                className="text-xs font-bold text-violet-600 hover:text-violet-700 transition-colors"
              >
                Oublié ?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 focus:border-transparent transition-all pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {errorMsg && (
            <p className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl">{errorMsg}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isLoading ? 'Connexion en cours...' : 'Se connecter'}
          </button>
        </form>

        <div className="mt-6 p-4 rounded-2xl bg-violet-50 border border-violet-100 text-left">
          <p className="text-sm font-bold text-gray-900">Première connexion ?</p>
          <p className="text-xs text-gray-600 mt-1">Vous êtes accompagné(e) par un organisme de formation et n'avez pas encore de mot de passe (ou le lien reçu ne fonctionne plus) ?</p>
          <button
            type="button"
            onClick={() => { setForgotMode('first'); setShowForgotPassword(true); setResetSent(false); setErrorMsg(''); }}
            className="mt-3 w-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold py-3 rounded-xl transition-all"
          >
            Recevoir mon lien de connexion
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Vous êtes un organisme de formation et souhaitez utiliser SkorUp ?{' '}
          <a href="/signup" className="font-bold text-gray-500 hover:text-violet-700 underline underline-offset-2">Créer l'espace de votre organisme</a>
        </p>
      </div>
      {/* Pied de page légal */}
      <LiensLegaux className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1.5"
        lienClassName="text-xs text-gray-400 hover:text-violet-600 transition-colors underline-offset-2 hover:underline" />
    </div>
  );
};

// --- dnd-kit: zone de dépôt séance (carte) ---
const SessDropZone = ({ zoneId, isAdmin, hasActive, children }) => {
  const { isOver, setNodeRef } = useDroppable({ id: String(zoneId) });
  const highlighted = isOver && hasActive;
  return (
    <div
      ref={setNodeRef}
      className={`border rounded-3xl bg-gray-50/50 overflow-hidden shadow-sm transition-all ${
        highlighted
          ? (isAdmin ? 'border-amber-400 bg-amber-50/50 shadow-amber-100' : 'border-indigo-400 bg-indigo-50/50 shadow-indigo-100')
          : 'border-gray-100'
      }`}
    >
      {children(highlighted)}
    </div>
  );
};

// --- dnd-kit: élément déplaçable séance (carte) ---
const SessDragItem = ({ itemId, activeId, children }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: String(itemId) });
  // CORRECTIF (2026-08-31) : useDraggable() calcule un "transform" mais ne déplace jamais l'élément
  // visuellement tout seul — il faut appliquer explicitement ce transform au style de l'élément
  // déplacé, sinon le glisser-déposer semble "ne rien faire du tout" (l'élément reste figé à l'écran)
  // bien que la logique de dépôt (onDragEnd) fonctionne en réalité normalement en dessous.
  const style = transform ? { transform: CSS.Translate.toString(transform), zIndex: 30, position: 'relative' } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`bg-white p-4 rounded-2xl border flex flex-wrap items-center justify-between gap-2 group transition-all shadow-sm cursor-grab active:cursor-grabbing ${String(activeId) === String(itemId) ? 'opacity-40 border-indigo-200' : 'border-gray-100 hover:border-indigo-200'}`}
    >
      {children}
    </div>
  );
};

// --- dnd-kit: ligne de dépôt (tableau formateur) ---
const FDropGroupRow = ({ groupNum, hasActive, children }) => {
  const { isOver, setNodeRef } = useDroppable({ id: `fg-${groupNum}` });
  const highlighted = isOver && hasActive;
  return (
    <tr ref={setNodeRef} className={`transition-all ${highlighted ? 'bg-indigo-100 outline outline-2 outline-indigo-400' : 'bg-indigo-50/30'}`}>
      {children(highlighted)}
    </tr>
  );
};

// --- dnd-kit: ligne déplaçable (tableau formateur) ---
const FDragItemRow = ({ sessionId, activeId, children }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: `fi-${sessionId}` });
  // CORRECTIF (2026-08-31) : même correctif que SessDragItem — le transform calculé par dnd-kit
  // n'était jamais appliqué, donc la ligne ne suivait pas le curseur pendant le glisser-déposer,
  // ce qui donnait l'impression que la fonctionnalité ne marchait pas du tout côté formateur.
  const style = transform ? { transform: CSS.Translate.toString(transform), position: 'relative', zIndex: 30 } : undefined;
  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`transition-colors border-l border-gray-100 cursor-grab active:cursor-grabbing ${activeId === `fi-${sessionId}` ? 'opacity-40 bg-indigo-50' : 'hover:bg-gray-50/30'}`}
    >
      {children}
    </tr>
  );
};

// FIX (2026-10-06) : « les dates des séances ne s'enregistrent pas ». Le champ date était "contrôlé"
// directement par la valeur en base : en tapant la date AU CLAVIER, le navigateur envoie une date dès
// le 1er chiffre de l'année (ex. 0002-10-15), React remettait aussitôt l'ancienne valeur dans le champ
// (la saisie s'effaçait sous les doigts) et une année erronée pouvait partir en base. Ce champ garde
// désormais la saisie localement et n'enregistre qu'une date COMPLÈTE (année >= 2000), 0,8 s après la
// saisie ou à la sortie du champ — et aussi si l'on quitte l'écran juste après.
// FIX (2026-10-09) : « quand je mets des horaires, soit ça ne s'enregistre pas, soit ça les modifie ».
// Une séance regroupe plusieurs lignes (un document, l'émargement...). Les dates/horaires étaient lus
// sur la PREMIÈRE ligne uniquement : si un élément avait été ajouté à la séance après coup sans
// horaires (ou avec d'autres), l'écran affichait ses valeurs vides ou différentes, et la saisie d'un
// seul horaire laissait l'autre horaire propre à chaque ligne → horaires « qui changent tout seuls ».
// Désormais : la séance affiche la première valeur RENSEIGNÉE parmi ses lignes, et une saisie
// enregistre début ET fin sur TOUTES les lignes de la séance, qui redeviennent identiques.
const fillGroupSchedule = (g, s) => {
  if (!g.date && s.date) g.date = s.date;
  if (!g.debut && s.heure_debut) g.debut = s.heure_debut;
  if (!g.fin && s.heure_fin) g.fin = s.heure_fin;
};
// AJOUT (2026-10-12) : une séance ne peut être émargée (signée) que LE JOUR de la séance ou APRÈS.
// Auparavant, une séance sans date pouvait être signée n'importe quand : une cliente avait ainsi signé
// toutes ses séances à l'avance. Règle : séance sans date → signature impossible (« Date à définir ») ;
// date future → « Disponible le … ». Le bloc administratif (séance 0) et les documents de début/fin de
// parcours (sans numéro de séance) gardent l'ancienne règle (seule une date future bloque).
// Le même verrou existe côté base de données (voir verrou_signature_seance.sql).
const localTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const signatureLockFor = (sessionDate, numero) => {
  const n = Number(numero);
  const isSeance = numero !== null && numero !== undefined && Number.isFinite(n) && n >= 1;
  if (!sessionDate) return isSeance ? { locked: true, label: 'Date à définir' } : { locked: false, label: '' };
  if (localTodayISO() < String(sessionDate).slice(0, 10)) {
    const [y, m, d] = String(sessionDate).slice(0, 10).split('-');
    return { locked: true, label: `Disponible le ${d}/${m}${y && y !== String(new Date().getFullYear()) ? '/' + y : ''}` };
  }
  return { locked: false, label: '' };
};

const scheduleOf = (items) => {
  const g = { date: null, debut: null, fin: null };
  (items || []).forEach(s => fillGroupSchedule(g, s));
  return g;
};

const SessionDateInput = ({ value, onCommit, className }) => {
  const saved = value || '';
  const [local, setLocal] = React.useState(saved);
  const [focused, setFocused] = React.useState(false);
  const timerRef = React.useRef(null);
  const pendingRef = React.useRef(null);
  const lastSentRef = React.useRef(saved);
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;
  React.useEffect(() => {
    if (!focused && pendingRef.current === null) { setLocal(saved); lastSentRef.current = saved; }
  }, [saved, focused]);
  const isComplete = (v) => v === '' || (/^\d{4}-\d{2}-\d{2}$/.test(v) && Number(v.slice(0, 4)) >= 2000);
  const commit = (v) => {
    clearTimeout(timerRef.current);
    pendingRef.current = null;
    if (!isComplete(v) || v === lastSentRef.current) return false;
    lastSentRef.current = v;
    onCommitRef.current(v);
    return true;
  };
  // Quitter l'écran moins d'une seconde après la saisie : on enregistre quand même.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => () => { if (pendingRef.current !== null) commit(pendingRef.current); }, []);
  return (
    <input
      type="date"
      value={local}
      className={className}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const v = e.target.value;
        setLocal(v);
        pendingRef.current = v;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => commit(v), 800);
      }}
      onBlur={(e) => {
        const v = e.target.value;
        if (!isComplete(v)) toast.error("Date incomplète : vérifiez l'année (4 chiffres). La date n'a pas été modifiée.", { id: 'session-date-incomplete' });
        commit(v);
        setFocused(false);
      }}
    />
  );
};

const ClientDetailView = ({
  client, formateurs, assignFormateur, handleModuleChange, modules,
  supabase, fetchUtilisateurs, onBack, sessions, fetchSessions, documents, fetchDocuments,
  handleGenerateDocx, documentTemplates, pedagogicalResources,
  handleDownloadResource, handleUploadExerciseResponse, generateSessions,
  handleDeleteClient, setIsSessionItemModalOpen, setTargetSessionForAddition,
  setViewingSession, handleDownloadPDF, updateSessionDate, updateSessionTime,
  onTimeChange, onSaveTimes, editedTimes, lastModifiedSessionId,
  setDocSettingsTarget, setIsDocSettingsOpen, setViewingDocId,
  handleMoveSessionItem, handleSaveCorrection, handleAddAdminBloc,
  handleRegenerateVisualDocs, instantiateDocument, currentOrgId,
  groupes = [], onOpenGroupe = null,
  depotsNonLus = [], estPro = false, nomUtilisateur = '', onDepotsVus = null, ongletInitial = null, onOngletInitialUtilise = null
}) => {
  const [activeTab, setActiveTab] = React.useState(ongletInitial || 'infos');
  React.useEffect(() => {
    if (ongletInitial) { setActiveTab(ongletInitial); if (onOngletInitialUtilise) onOngletInitialUtilise(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ongletInitial, client?.id]);
  const nbRecusNonLus = (depotsNonLus || []).filter(d => String(d.client_id) === String(client?.id)).length;
  const groupeDuClient = client?.groupe_id ? (groupes || []).find(g => String(g.id) === String(client.groupe_id)) : null;
  const [correctionModalSession, setCorrectionModalSession] = React.useState(null);
  const [activeId, setActiveId] = React.useState(null);
  const [isSavingInfo, setIsSavingInfo] = React.useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = React.useState(false);
  const [confirmState, setConfirmState] = React.useState({ open: false, title: '', message: '', onConfirm: null });
  const showDeleteConfirm = (title, message, onConfirmFn) => setConfirmState({ open: true, title, message, onConfirm: onConfirmFn });
  const hideDeleteConfirm = () => setConfirmState(prev => ({ ...prev, open: false, onConfirm: null }));
  const [assignedDocs, setAssignedDocs] = React.useState([]);
  const [moduleDocResources, setModuleDocResources] = React.useState([]);
  const [isLoadingAssigned, setIsLoadingAssigned] = React.useState(false);
  const [showAddDocModal, setShowAddDocModal] = React.useState(false);
  // Envoi programmé des documents "Ajouts personnalisés" (ajouté 2026-09-02) : au lieu de rester
  // en attente jusqu'à un clic manuel sur "Générer", un document peut être programmé pour être
  // généré + envoyé automatiquement à une date précise (ex : le jour de démarrage de la formation)
  // via le cron quotidien /api/automation/process-scheduled-documents.
  const [addDocSendMode, setAddDocSendMode] = React.useState('immediate');
  const [addDocScheduledDate, setAddDocScheduledDate] = React.useState('');
  // État pour la programmation d'un document DÉJÀ ajouté ("Ajouts personnalisés") — permet de
  // programmer/reprogrammer l'envoi après coup, pas seulement au moment de l'ajout (demande du
  // 2026-09-02 : "qu'une fois qu'il à été ajouté il puisse le programmé si besoin").
  const [scheduleEditDoc, setScheduleEditDoc] = React.useState(null); // ligne client_documents en cours d'édition
  const [scheduleEditMode, setScheduleEditMode] = React.useState('immediate');
  const [scheduleEditDate, setScheduleEditDate] = React.useState('');
  // Documents du dossier client (ajouté 2026-08-31, généralisé 2026-09-01) : section "Administratif"
  // partagée avec l'espace Formateur (voir handleUploadForClient plus bas dans le fichier) — l'organisme
  // ET le formateur peuvent tous les deux y déposer des documents pour le dossier du client (factures,
  // pièces envoyées par le client par un autre biais, etc.), et voient exactement la même liste.
  const [showAdminDocModal, setShowAdminDocModal] = React.useState(false);
  const [adminDocFile, setAdminDocFile] = React.useState(null);
  const [adminDocName, setAdminDocName] = React.useState('');
  const [adminDocType, setAdminDocType] = React.useState('Administratif');
  const [isUploadingAdminDoc, setIsUploadingAdminDoc] = React.useState(false);
  const [localDocGroups, setLocalDocGroups] = React.useState([]);
  const [showSendDocsModal, setShowSendDocsModal] = React.useState(false);
  const [selectedGroupId, setSelectedGroupId] = React.useState(null);
  const [isSendingDocs, setIsSendingDocs] = React.useState(false);
  const [groupeMode, setGroupeMode] = React.useState('immediate'); // GROUPE (2026-10-23)
  const [groupeDate, setGroupeDate] = React.useState('');
  const [groupeProgression, setGroupeProgression] = React.useState(null);
  const [clientQuestionnaireResponses, setClientQuestionnaireResponses] = React.useState([]);
  const [clientQuestionnaireResources, setClientQuestionnaireResources] = React.useState([]);
  // AJOUT (2026-10-02) : envoi d'un questionnaire / quiz directement à ce client, même s'il n'est pas dans
  // son module (table questionnaire_assignments — voir questionnaire_assignments_migration.sql).
  const [orgQuestionnaires, setOrgQuestionnaires] = React.useState([]);
  const [clientQAssignments, setClientQAssignments] = React.useState([]);
  const [showSendQ, setShowSendQ] = React.useState(false);
  const [qToSend, setQToSend] = React.useState('');
  const [qNotifyEmail, setQNotifyEmail] = React.useState(true);
  const [isSendingQ, setIsSendingQ] = React.useState(false);
  const fetchClientQAssignments = React.useCallback(async () => {
    if (!supabase || !client?.id) return;
    const { data, error } = await supabase.from('questionnaire_assignments').select('*').eq('client_id', client.id);
    if (error) { console.warn('[questionnaire_assignments] lecture impossible :', error.message); return; }
    setClientQAssignments(data || []);
  }, [supabase, client?.id]);
  React.useEffect(() => {
    if (!supabase || !currentOrgId) return;
    supabase.from('module_step_resources').select('id, titre, metadata, module_id').eq('type', 'questionnaire')
      .eq('organisation_id', currentOrgId).order('titre', { ascending: true })
      .then(({ data }) => { if (data) setOrgQuestionnaires(data); });
    fetchClientQAssignments();
  }, [supabase, currentOrgId, fetchClientQAssignments]);
  // Liste affichée : questionnaires du module + envoyés à la main + tout autre questionnaire déjà rempli
  // par ce client (ex. reçu via un groupe de documents), pour ne rien "perdre" de son historique.
  const displayedQuestionnaires = React.useMemo(() => {
    const byId = new Map();
    clientQuestionnaireResources.forEach(q => byId.set(String(q.id), { ...q, _source: 'module' }));
    clientQAssignments.forEach(a => {
      const q = orgQuestionnaires.find(o => String(o.id) === String(a.questionnaire_id));
      if (q && !byId.has(String(q.id))) byId.set(String(q.id), { ...q, _source: 'envoye', _assignment: a });
    });
    clientQuestionnaireResponses.forEach(r => {
      if (byId.has(String(r.questionnaire_id))) return;
      const q = orgQuestionnaires.find(o => String(o.id) === String(r.questionnaire_id));
      if (q) byId.set(String(q.id), { ...q, _source: 'autre' });
    });
    return Array.from(byId.values());
  }, [clientQuestionnaireResources, clientQAssignments, clientQuestionnaireResponses, orgQuestionnaires]);
  const sendableQuestionnaires = orgQuestionnaires.filter(q => !displayedQuestionnaires.some(d => String(d.id) === String(q.id)));
  const handleSendQuestionnaire = async () => {
    const q = orgQuestionnaires.find(o => String(o.id) === String(qToSend));
    if (!q) return;
    setIsSendingQ(true);
    const { error } = await supabase.from('questionnaire_assignments').insert([{
      client_id: client.id, questionnaire_id: q.id, organisation_id: client.organisation_id || currentOrgId,
    }]);
    setIsSendingQ(false);
    if (error) {
      if (error.code === '23505') toast.error('Ce questionnaire a déjà été envoyé à ce client.');
      else if (error.code === '42P01' || /questionnaire_assignments/.test(error.message || '')) toast.error("Fonction pas encore activée : lancez d'abord le script questionnaire_assignments_migration.sql dans Supabase.");
      else toast.error('Erreur : ' + error.message);
      return;
    }
    const qMeta = (() => { try { return typeof q.metadata === 'string' ? JSON.parse(q.metadata) : (q.metadata || {}); } catch { return {}; } })();
    toast.success(`« ${q.titre} » envoyé à ${client.nom || client.nom_complet || 'ce client'}.`);
    if (qNotifyEmail) notifyClientByEmail(client.id, `${qMeta.isQuiz ? 'Quiz' : 'Questionnaire'} « ${q.titre} »`);
    setQToSend(''); setShowSendQ(false);
    fetchClientQAssignments();
  };
  const handleRemoveQAssignment = async (assignment) => {
    const { error } = await supabase.from('questionnaire_assignments').delete().eq('id', assignment.id);
    if (error) { toast.error('Erreur : ' + error.message); return; }
    toast.success('Questionnaire retiré de l\'espace du client.');
    fetchClientQAssignments();
  };
  // --- Finances (2026-09-26) : historique des paiements reçus pour ce client ---
  const [clientPaiements, setClientPaiements] = React.useState([]);
  const [isLoadingPaiements, setIsLoadingPaiements] = React.useState(false);
  const [newPaiementMontant, setNewPaiementMontant] = React.useState('');
  const [newPaiementDate, setNewPaiementDate] = React.useState(() => jourLocalISO(new Date()));
  const [newPaiementMode, setNewPaiementMode] = React.useState('Virement');
  const [newPaiementNote, setNewPaiementNote] = React.useState('');
  const [isSavingPaiement, setIsSavingPaiement] = React.useState(false);
  const [clientInfo, setClientInfo] = React.useState({
    nomcomplet_client: client.nomcomplet_client || '',
    client_email: client.client_email || '',
    client_phone: client.client_phone || '',
    rue_client: '',
    code_postal_client: '',
    ville_client: '',
    region: '',
    numero_dossier: client.numero_dossier || '',
    modalite_formation: client.modalite_formation || 'Mixte',
    montant_prestation: client.montant_prestation || '',
    pourcentage_formateur: client.pourcentage_formateur || ''
  });

  // FIX (2026-09-04, corrigé) : remonte en haut de page à l'ouverture de la fiche client. Le vrai
  // conteneur défilant de l'appli est le <main overflow-y-auto> qui enveloppe tout le contenu des
  // onglets (la <nav> latérale, elle, est fixe) — window.scrollTo() était donc un no-op. On cible
  // directement ce <main> (repli sur window par sécurité s'il n'est pas trouvé).
  React.useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const fetchDetailedClient = async () => {
      const { data, error } = await supabase.from('clients').select('*').eq('id', client.id).single();
      if (data && !error) {
        setClientInfo({
          nomcomplet_client: data.nom_complet || '',
          client_email: data.email_contact || '',
          client_phone: data.telephone || '',
          rue_client: data.rue || '',
          code_postal_client: data.code_postal || '',
          ville_client: data.ville || '',
          region: data.region || '',
          numero_dossier: data.numero_dossier || '',
          modalite_formation: data.modalite_formation || 'Mixte',
          montant_prestation: data.montant_prestation || '',
          pourcentage_formateur: data.pourcentage_formateur || ''
        });
      }
    };
    fetchDetailedClient();
  }, [client.id, supabase]);

  // --- Finances (2026-09-26) ---
  const fetchClientPaiements = async () => {
    setIsLoadingPaiements(true);
    const { data, error } = await supabase.from('client_paiements').select('*').eq('client_id', client.id).order('date_paiement', { ascending: false });
    if (!error) setClientPaiements(data || []);
    setIsLoadingPaiements(false);
  };

  React.useEffect(() => {
    fetchClientPaiements();
  }, [client.id]);

  React.useEffect(() => {
    const fetchAssignedDocs = async () => {
      setIsLoadingAssigned(true);
      const [{ data: clientDocs, error: clientDocsError }, { data: moduleResources }] = await Promise.all([
        supabase.from('client_documents').select('*').eq('client_id', client.id).order('ordre', { ascending: true }),
        client.module_id
          ? supabase.from('module_step_resources').select('*').eq('module_id', client.module_id).eq('type', 'document')
          : Promise.resolve({ data: [] })
      ]);
      if (clientDocsError) {
        console.error('[fetchAssignedDocs] Erreur lecture client_documents:', clientDocsError);
        toast.error('Erreur lors du chargement des documents du client : ' + clientDocsError.message);
      }
      setAssignedDocs(clientDocs || []);
      setModuleDocResources(moduleResources || []);
      setIsLoadingAssigned(false);
    };
    fetchAssignedDocs();
    // Charger questionnaires du module + réponses du client
    if (client.module_id) {
      supabase.from('module_step_resources').select('*').eq('module_id', client.module_id).eq('type', 'questionnaire')
        .then(({ data }) => { if (data) setClientQuestionnaireResources(data); });
    }
    supabase.from('questionnaire_responses').select('*').eq('client_id', client.id)
      .then(({ data }) => { if (data) setClientQuestionnaireResponses(data); });
  }, [client.id, client.module_id, supabase]);

  React.useEffect(() => {
    if (!currentOrgId || !supabase) return;
    supabase.from('document_groups').select('*').eq('organisation_id', currentOrgId).order('nom', { ascending: true })
      .then(({ data }) => { if (data) setLocalDocGroups(data); });
  }, [currentOrgId, supabase]);

  // GROUPE (2026-10-23) : documents d'un groupe et leur situation pour CE client.
  const documentsDuGroupe = React.useMemo(() => {
    if (!selectedGroupId) return [];
    const gid = String(selectedGroupId);
    const vus = new Set();
    const lignes = [];
    (documents || []).forEach(d => {
      if (d.user_id || d.assigned_formateur_id || !d.nom) return;
      const ids = [d.group_id, ...(Array.isArray(d.group_ids) ? d.group_ids : [])].filter(v => v != null).map(String);
      if (!ids.includes(gid) || vus.has(d.nom)) return;
      vus.add(d.nom);
      const titre = d.nom;
      const dejaEnvoye = (documents || []).some(x => String(x.user_id) === String(client.id) && x.nom && (x.nom === titre || x.nom.startsWith(titre + ' - ')));
      const ligneAssignee = assignedDocs.find(a => a.template_titre === titre);
      const dejaProgramme = !!(ligneAssignee && ligneAssignee.send_mode === 'scheduled' && !ligneAssignee.sent_at);
      const modele = (documentTemplates || {})[titre];
      lignes.push({ titre, url: (modele && modele.url) || d.url, ligneAssignee, statut: dejaEnvoye ? 'envoye' : (dejaProgramme ? 'programme' : 'a_envoyer') });
    });
    return lignes;
  }, [selectedGroupId, documents, assignedDocs, documentTemplates, client.id]);
  const nbDocsGroupe = (gid) => {
    const g = String(gid);
    return new Set((documents || []).filter(d => !d.user_id && !d.assigned_formateur_id && d.nom
      && [d.group_id, ...(Array.isArray(d.group_ids) ? d.group_ids : [])].filter(v => v != null).map(String).includes(g)).map(d => d.nom)).size;
  };
  const fermerEnvoiGroupe = () => { setShowSendDocsModal(false); setSelectedGroupId(null); setGroupeMode('immediate'); setGroupeDate(''); setGroupeProgression(null); };

  // Une ligne « Ajouts personnalisés » par document (réutilisée si elle existe déjà).
  const ligneClientDocument = async (doc, champs, ordre = 0) => {
    if (doc.ligneAssignee) {
      const { data, error } = await supabase.from('client_documents').update(champs).eq('id', doc.ligneAssignee.id).select().single();
      if (error) throw error;
      return data;
    }
    const { data, error } = await supabase.from('client_documents').insert([{
      client_id: client.id, template_titre: doc.titre, template_url: doc.url, destination: 'client',
      organisation_id: client.organisation_id, ordre, ...champs,
    }]).select().single();
    if (error) throw error;
    return data;
  };

  const envoyerGroupeDocuments = async () => {
    const aTraiter = documentsDuGroupe.filter(d => d.statut === 'a_envoyer');
    if (!aTraiter.length || isSendingDocs) return;
    if (groupeMode === 'scheduled' && !groupeDate) { toast.error("Choisissez la date d'envoi."); return; }
    const sansFichier = aTraiter.filter(d => !d.url);
    setIsSendingDocs(true);
    const nomGroupe = (localDocGroups.find(g => String(g.id) === String(selectedGroupId)) || {}).nom || 'groupe';

    // ── Programmer : tout part automatiquement à la date choisie ──
    if (groupeMode === 'scheduled') {
      let ok = 0; const echecs = [];
      for (const doc of aTraiter) {
        if (!doc.url) { echecs.push(doc.titre); continue; }
        try {
          const ligne = await ligneClientDocument(doc, { send_mode: 'scheduled', scheduled_date: groupeDate, send_error: null }, assignedDocs.length + ok);
          setAssignedDocs(prev => prev.some(a => a.id === ligne.id) ? prev.map(a => a.id === ligne.id ? ligne : a) : [...prev, ligne]);
          ok++;
        } catch (e) { console.error('[envoi groupé] programmation', doc.titre, e); echecs.push(doc.titre); }
      }
      setIsSendingDocs(false);
      const jour = new Date(groupeDate + 'T00:00:00').toLocaleDateString('fr-FR');
      if (ok) toast.success(`${ok} document${ok > 1 ? 's' : ''} du groupe « ${nomGroupe} » programmé${ok > 1 ? 's' : ''} pour le ${jour}. Le client recevra un email ce jour-là.`, { duration: 8000 });
      if (echecs.length) toast.error(`Non programmé${echecs.length > 1 ? 's' : ''} : ${echecs.join(', ')}.`, { duration: 10000 });
      if (!echecs.length) fermerEnvoiGroupe();
      return;
    }

    // ── Maintenant : chaque document est préparé et envoyé, un seul email à la fin ──
    _envoiGroupe.actif = true; _envoiGroupe.clientId = client.id; _envoiGroupe.documents = [];
    const echecs = [...sansFichier.map(d => d.titre)];
    const idToast = 'envoi-groupe';
    try {
      const aEnvoyer = aTraiter.filter(d => d.url);
      for (let i = 0; i < aEnvoyer.length; i++) {
        const doc = aEnvoyer[i];
        setGroupeProgression({ fait: i, total: aEnvoyer.length, titre: doc.titre });
        toast.loading(`Envoi ${i + 1}/${aEnvoyer.length} : ${doc.titre}…`, { id: idToast });
        let ligne = null;
        try {
          ligne = await ligneClientDocument(doc, { send_mode: 'immediate', scheduled_date: null, send_error: null }, assignedDocs.length + i);
          setAssignedDocs(prev => prev.some(a => a.id === ligne.id) ? prev.map(a => a.id === ligne.id ? ligne : a) : [...prev, ligne]);
        } catch (e) { console.error('[envoi groupé] ajout', doc.titre, e); }
        const avant = _envoiGroupe.documents.length;
        try { await handleGenerateDocx(client, doc.titre, false, null, true); } catch (e) { console.error('[envoi groupé] génération', doc.titre, e); }
        if (_envoiGroupe.documents.length > avant) {
          if (ligne) {
            const { data: maj } = await supabase.from('client_documents').update({ sent_at: new Date().toISOString() }).eq('id', ligne.id).select().single();
            if (maj) setAssignedDocs(prev => prev.map(a => a.id === maj.id ? maj : a));
          }
        } else {
          echecs.push(doc.titre);
        }
      }
    } finally {
      const reussis = _envoiGroupe.documents.slice();
      _envoiGroupe.actif = false; _envoiGroupe.clientId = null; _envoiGroupe.documents = [];
      const aPrevenir = reussis.filter(r => r.prevenir).map(r => r.nom);
      notifierDocumentsAuClient(client.id, aPrevenir);
      setIsSendingDocs(false);
      setGroupeProgression(null);
      const n = reussis.length;
      if (n) toast.success(`${n} document${n > 1 ? 's' : ''} du groupe « ${nomGroupe} » envoyé${n > 1 ? 's' : ''}.${aPrevenir.length ? ' Le client a reçu un seul email récapitulatif.' : ''}`, { id: idToast, duration: 8000 });
      else toast.dismiss(idToast);
      if (echecs.length) toast.error(`Non envoyé${echecs.length > 1 ? 's' : ''} : ${echecs.join(', ')}. Vérifiez ${echecs.length > 1 ? 'ces modèles' : 'ce modèle'} dans Documents, puis utilisez « Envoyer » sur la ligne.`, { duration: 12000 });
      if (!echecs.length) fermerEnvoiGroupe();
    }
  };

  const handleRemoveAssignedDoc = async (docId) => {
    await supabase.from('client_documents').delete().eq('id', docId);
    setAssignedDocs(prev => prev.filter(d => d.id !== docId));
  };

  const handleAddAssignedDoc = async (titre, url, sendMode = 'immediate', scheduledDate = null) => {
    // Avant : une erreur d'insertion était silencieusement ignorée (rien ne s'affichait, aucun message) —
    // on l'affiche maintenant pour pouvoir diagnostiquer précisément ce qui bloque (ex : modèle sans
    // fichier associé, conflit de doublon, règle de sécurité Supabase, etc.).
    if (!url) {
      toast.error(`Le modèle "${titre}" n'a pas de fichier associé dans la modélothèque — impossible de l'ajouter.`);
      return;
    }
    if (sendMode === 'scheduled' && !scheduledDate) {
      toast.error('Choisissez une date pour programmer l\'envoi de ce document.');
      return;
    }
    const { data, error } = await supabase
      .from('client_documents')
      .insert([{
        client_id: client.id,
        template_titre: titre,
        template_url: url,
        destination: 'client',
        ordre: assignedDocs.length,
        organisation_id: client.organisation_id,
        send_mode: sendMode,
        scheduled_date: sendMode === 'scheduled' ? scheduledDate : null,
      }])
      .select()
      .single();
    if (error) {
      console.error('[handleAddAssignedDoc] Erreur insertion client_documents:', error);
      toast.error("Erreur lors de l'ajout du document : " + error.message);
      return;
    }
    if (data) setAssignedDocs(prev => [...prev, data]);
    setShowAddDocModal(false);
  };

  // Programmer/reprogrammer l'envoi d'un document déjà présent dans "Ajouts personnalisés" — sans
  // repasser par le modal d'ajout. sendMode 'immediate' annule toute programmation (le document
  // reste dans la liste, à générer manuellement comme avant).
  const handleUpdateAssignedDocSchedule = async (docId, sendMode, scheduledDate) => {
    if (sendMode === 'scheduled' && !scheduledDate) {
      toast.error("Choisissez une date pour programmer l'envoi de ce document.");
      return;
    }
    const { data, error } = await supabase
      .from('client_documents')
      .update({
        send_mode: sendMode,
        scheduled_date: sendMode === 'scheduled' ? scheduledDate : null,
        send_error: null,
      })
      .eq('id', docId)
      .select()
      .single();
    if (error) {
      console.error('[handleUpdateAssignedDocSchedule] Erreur mise à jour client_documents:', error);
      toast.error('Erreur lors de la programmation : ' + error.message);
      return;
    }
    if (data) setAssignedDocs(prev => prev.map(d => d.id === docId ? data : d));
    toast.success(sendMode === 'scheduled' ? 'Envoi programmé mis à jour.' : 'Le document sera envoyé manuellement.');
    setScheduleEditDoc(null);
  };

  // Bibliothèque complète pour le sélecteur "Ajouter un document" — fusionne documentTemplates
  // (issu de module_step_resources, la source habituelle) avec tous les documents de la table
  // "documents" qui ne sont pas déjà couverts (user_id null = modèle de bibliothèque, qu'il soit
  // rangé dans un groupe ou non — mêmes lignes que "Gestion des documents" > Bibliothèque de
  // modèles / Groupes de documents). Garantit que tout document ajouté par l'administrateur dans
  // la partie "Documents" apparaisse ici, groupé ou non (demande du 2026-09-02), y compris les cas
  // historiques jamais synchronisés vers module_step_resources.
  const allTemplatesForPicker = React.useMemo(() => {
    const merged = { ...(documentTemplates || {}) };
    (documents || [])
      .filter(d => !d.user_id && !d.assigned_formateur_id && d.nom && d.url)
      .forEach(d => {
        if (merged[d.nom]) return; // déjà présent via module_step_resources (métadonnées plus complètes)
        let parsedMeta = {};
        try { parsedMeta = typeof d.metadata === 'string' ? JSON.parse(d.metadata) : (d.metadata || {}); } catch { parsedMeta = {}; }
        merged[d.nom] = {
          id: d.id,
          url: d.url,
          name: d.nom,
          destination: (d.visible_formateur && !d.visible_client) ? 'formateur' : 'client',
          classification: parsedMeta.classification || 'telechargeable',
          document_group_id: d.group_id || null,
          metadata: parsedMeta,
        };
      });
    return merged;
  }, [documentTemplates, documents]);

  // DOSSIER_DOC_TYPES / isDossierDoc sont désormais définis au niveau module (voir plus haut dans le
  // fichier) — partagés avec FormateurDetailView et FormateurView.

  const handleUploadAdminDoc = async () => {
    if (!adminDocFile || !adminDocName.trim()) { toast.error('Veuillez renseigner un nom et choisir un fichier.'); return; }
    setIsUploadingAdminDoc(true);
    try {
      const ext = adminDocFile.name.split('.').pop();
      const safeN = adminDocName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `dossier_client/${client.id}/${Date.now()}_${safeN}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, adminDocFile);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);

      // visible_client: false — section à usage interne (organisme + formateur), jamais visible du client.
      // visible_formateur: true — le formateur assigné à ce client doit voir tout ce que l'organisme dépose ici,
      // exactement comme l'organisme voit ce que le formateur dépose depuis son propre espace.
      const { error: insertError } = await supabase.from('documents').insert([{
        nom: adminDocName.trim(),
        type_document: adminDocType,
        url: publicUrl,
        user_id: client.id,
        organisation_id: client.organisation_id || currentOrgId,
        visible_client: false,
        visible_formateur: true,
        signe_par_client: false,
        signe_par_formateur: false,
      }]);
      if (insertError) throw insertError;

      toast.success('Document ajouté au dossier du client.');
      setShowAdminDocModal(false);
      setAdminDocFile(null);
      setAdminDocName('');
      setAdminDocType('Administratif');
      await fetchDocuments();
    } catch (e) {
      console.error('Erreur upload document dossier client:', e);
      toast.error("Erreur lors de l'ajout : " + e.message);
    }
    setIsUploadingAdminDoc(false);
  };

  const handleDeleteAdminDoc = async (docId) => {
    if (!(await peutSupprimerDocument(docId))) return; // lot 2 : jamais un document signé
    const { error } = await supabase.from('documents').delete().eq('id', docId);
    if (error) { toast.error('Erreur lors de la suppression : ' + error.message); return; }
    toast.success('Document supprimé.');
    await fetchDocuments();
  };

  // AJOUT (2026-09-19) : on distingue les "vraies" séances calendrier (numero_seance renseigné) des
  // exercices synthétiques de début/fin de module (numero_seance: null, voir instantiateExerciceSession).
  // `clientSessions` ne garde que les vraies séances : c'est cette liste qui alimente tous les écrans de
  // planification (onglet "Planning & Supervision", ajout d'item, etc.) pour que ces exercices n'y
  // apparaissent plus jamais comme une "séance" à dater. `allClientSessions` garde tout, pour que les
  // badges/compteurs d'exercices (et l'onglet "Exercices" du dossier client) continuent d'inclure ces
  // exercices de début/fin normalement.
  const allClientSessions = sessions ? sessions.filter(s => s.client_id === client.id) : [];
  const clientSessions = allClientSessions.filter(s => s.numero_seance !== null && s.numero_seance !== undefined && estDuModuleActuel(s, client.module_id)).sort((a, b) => a.numero_seance - b.numero_seance);
  // LOT A (2026-10-21) : séances d'un ancien module — conservées, affichées à part en lecture seule.
  const seancesAncienModule = allClientSessions.filter(s => s.numero_seance !== null && s.numero_seance !== undefined && !estDuModuleActuel(s, client.module_id))
    .sort((a, b) => (Number(a.numero_seance) - Number(b.numero_seance)) || String(a.nom || '').localeCompare(String(b.nom || '')));
  // 04/11/2026 : on n'affiche que ce qui compte (signé, rendu, ou à reprendre dans le parcours) ; le reste
  // (éléments jamais commencés ni planifiés, éléments remplacés) reste dans le dossier sans encombrer.
  const planAncienModule = client.module_id && seancesAncienModule.length ? planFusionModule(allClientSessions, client.module_id, { renumeroter: !client.groupe_id }) : null;
  const idsAReprendre = new Set(planAncienModule ? planAncienModule.idsRepris : []);
  const seancesAncienModuleVisibles = seancesAncienModule.filter(s => elementSeanceCommence(s) || idsAReprendre.has(String(s.id)));
  const repriseAncienPossible = !!(planAncienModule && planAncienModule.changements.length);
  const clientDocs = documents ? documents.filter(d => d.user_id === client.id) : [];
  const clientExercises = allClientSessions.filter(s => s.type_activite === 'exercice' || s.type_activite === 'Exercice');
  const clientPendingCorrections = clientExercises.filter(s =>
    s.reponse_url && s.correction_statut !== 'Validé' && s.correction_statut !== 'À corriger'
  ).length;

  const handleResendInvite = async () => {
    await renvoyerLienAcces({ ...client, email: client.email || client.email_contact || clientInfo.client_email });
  };

  // FIX (2026-10-07) : « Erreur lors de la sauvegarde : invalid input syntax for type numeric: "" ».
  // Un champ chiffré laissé vide (Montant de la prestation, Part reversée au formateur) partait en
  // texte vide "" et la base refusait TOUT l'enregistrement. Désormais un champ chiffré vide est
  // enregistré comme "non renseigné" (null), la virgule décimale est acceptée (1500,50), et la fiche
  // s'enregistre toujours — avec un simple avertissement si des informations ne sont pas remplies.
  const CLIENT_INFO_LABELS = [
    ['nomcomplet_client', 'Nom complet'],
    ['client_email', 'Email'],
    ['client_phone', 'Téléphone'],
    ['rue_client', 'Rue'],
    ['code_postal_client', 'Code postal'],
    ['ville_client', 'Ville'],
    ['region', 'Région'],
    ['numero_dossier', 'N° de dossier'],
    ['montant_prestation', 'Montant de la prestation'],
    ['pourcentage_formateur', 'Part reversée au formateur'],
  ];
  const toNumberOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const t = String(v).replace(/[\s\u00a0€%]/g, '').replace(',', '.');
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const handleSaveClientInfo = async () => {
    const missingFields = CLIENT_INFO_LABELS
      .filter(([key]) => String(clientInfo[key] ?? '').trim() === '')
      .map(([, label]) => label);
    setIsSavingInfo(true);
    const fullAddress = [clientInfo.rue_client, clientInfo.code_postal_client, clientInfo.ville_client].filter(Boolean).join(', ');
    const { error } = await supabase.from('clients').update({
      nom_complet: clientInfo.nomcomplet_client,
      ...(String(clientInfo.client_email || '').trim() ? { email_contact: String(clientInfo.client_email).trim() } : {}),
      telephone: clientInfo.client_phone,
      rue: clientInfo.rue_client,
      code_postal: clientInfo.code_postal_client,
      ville: clientInfo.ville_client,
      adresse_postale: fullAddress,
      region: clientInfo.region,
      numero_dossier: clientInfo.numero_dossier,
      modalite_formation: clientInfo.modalite_formation,
      montant_prestation: toNumberOrNull(clientInfo.montant_prestation),
      pourcentage_formateur: toNumberOrNull(clientInfo.pourcentage_formateur),
    }).eq('id', client.id);

    if (error) {
      toast.error("Erreur lors de la sauvegarde : " + error.message);
    } else {
      await fetchUtilisateurs();
      // LOT A (2026-10-21) : enregistrer les informations ne touche plus aux séances (avant : les
      // éléments supprimés ou renommés étaient recréés à chaque « Enregistrer »).
      toast.success("Informations personnelles sauvegardées !");
      if (missingFields.length > 0) {
        toast(`Attention : toutes les informations ne sont pas remplies (${missingFields.join(', ')}).`, {
          icon: '⚠️',
          duration: 6000,
          id: 'client-info-incomplete',
          style: { background: '#FFFBEB', color: '#92400E', border: '1px solid #FCD34D' },
        });
      }
    }
    setIsSavingInfo(false);
  };

  // --- Finances (2026-09-26) ---
  const handleAddPaiement = async () => {
    const montant = parseFloat(newPaiementMontant);
    if (!montant || montant <= 0) { toast.error("Merci d'indiquer un montant valide."); return; }
    setIsSavingPaiement(true);
    const { error } = await supabase.from('client_paiements').insert({
      client_id: client.id,
      organisation_id: currentOrgId,
      montant,
      date_paiement: newPaiementDate || jourLocalISO(new Date()),
      mode_paiement: newPaiementMode,
      note: newPaiementNote || null,
    });
    if (error) {
      toast.error("Erreur lors de l'ajout du paiement : " + error.message);
    } else {
      toast.success("Paiement ajouté !");
      setNewPaiementMontant('');
      setNewPaiementNote('');
      await fetchClientPaiements();
    }
    setIsSavingPaiement(false);
  };

  const handleDeletePaiement = async (paiementId) => {
    const { error } = await supabase.from('client_paiements').delete().eq('id', paiementId);
    if (error) {
      toast.error("Erreur lors de la suppression : " + error.message);
    } else {
      toast.success("Paiement supprimé.");
      await fetchClientPaiements();
    }
  };

  const updateSession = async (id, payload) => {
    await supabase.from('sessions').update(payload).eq('id', id);
    if (fetchSessions) fetchSessions();
    if (payload.date || payload.heure_debut || payload.heure_fin) {
      const target = clientSessions.find(s => s.id === id);
      if (target) syncSeanceToCalendar(client.id, target.numero_seance);
    }
  };

  const handleAddCustomSession = async () => {
    const nextNum = clientSessions.length + 1;
    const { error } = await supabase.from('sessions').insert([{
      client_id: client.id,
      module_id: client.module_id,
      numero_seance: nextNum,
      titre: `Séance personnalisée ${nextNum}`,
      statut: 'À venir'
    }]);
    if (!error && fetchSessions) fetchSessions();
  };

  const handleDeleteSession = (id) => {
    showDeleteConfirm(
      'Supprimer cette séance ?',
      'Cette action est irréversible. La séance sera définitivement supprimée.',
      async () => {
        hideDeleteConfirm();
        if (!(await peutSupprimerElementSeance(id))) return; // lot 2 : jamais un élément signé
        const { error } = await supabase.from('sessions').delete().eq('id', id);
        if (error) { toast.error('Erreur suppression : ' + error.message); return; }
        if (fetchSessions) fetchSessions();
      }
    );
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <button onClick={onBack} className="text-gray-500 hover:text-gray-900 font-bold flex items-center mb-4 transition-colors">
        <ChevronLeft className="w-5 h-5 mr-1" /> Retour à la supervision
      </button>

      <div className="bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between md:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 break-words">{client.nomcomplet_client || client.nom || "Client sans nom"}</h2>
          <p className="text-gray-500 break-all">{client.email || "Aucun email"} - N° Dossier: {client.numero_dossier || "Non défini"}</p>
          {groupeDuClient && (
            <button onClick={() => onOpenGroupe && onOpenGroupe(groupeDuClient.id)} disabled={!onOpenGroupe}
              className="mt-2 inline-flex items-start gap-1.5 text-left text-xs font-black px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 max-w-full">
              <UsersRound size={13} className="mt-0.5 shrink-0" /> <span>Groupe : {groupeDuClient.nom}</span>
            </button>
          )}
        </div>
        <div className="mt-4 md:mt-0 flex flex-wrap items-center gap-2 md:gap-3">
          <button
            onClick={() => handleDownloadPDF(client, 'emargement')}
            className="bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-bold py-2 px-4 rounded-xl shadow-sm transition-all flex items-center gap-2 text-sm"
          >
            <Download size={16} /> Récap Émargements
          </button>
          <button
            onClick={() => handleDownloadPDF(client)}
            className="bg-violet-600 hover:bg-violet-700 text-white font-bold py-2 px-4 rounded-xl shadow-lg transition-all flex items-center gap-2 text-sm"
          >
            <Download size={16} /> Récapitulatif Général
          </button>
          <MasquerSiSansCompte clientId={client.id}>
          <button
            onClick={handleResendInvite}
            title="Renvoyer le lien d'invitation par email"
            className="bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-bold py-2 px-4 rounded-xl shadow-sm transition-all flex items-center gap-2 text-sm"
          >
            <Mail size={16} /> Renvoyer le lien
          </button>
          </MasquerSiSansCompte>
          <BadgeCompteClient clientId={client.id} detail={false} fallback={
            <span className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${client.status === 'Nouveau' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
              {client.status || 'Actif'}
            </span>
          } />
        </div>
      </div>

      <div className="flex gap-1 xl:gap-2 border-b border-gray-200 overflow-x-auto xl:flex-wrap xl:overflow-visible">
        <button onClick={() => setActiveTab('infos')} className={`shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'infos' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-800'}`}>Infos & Modalités</button>
        <button onClick={() => setActiveTab('seances')} className={`shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'seances' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-800'}`}>Supervision Séances</button>
        <button onClick={() => setActiveTab('administratif')} className={`shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'administratif' ? 'border-b-2 border-violet-700 text-violet-700' : 'text-gray-500 hover:text-gray-800'}`}>Administratif</button>
        <button onClick={() => setActiveTab('docs_signes')} className={`shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'docs_signes' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-800'}`}>Documents Signés</button>
        <button onClick={() => setActiveTab('exercices')} className={`relative shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'exercices' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-500 hover:text-gray-800'}`}>
          Exercices{clientExercises.length > 0 ? ` (${clientExercises.length})` : ''}
          {clientPendingCorrections > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[11px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-md">
              {clientPendingCorrections}
            </span>
          )}
        </button>
        <button onClick={() => setActiveTab('docs')} className={`shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'docs' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-800'}`}>Documents liés</button>
        <button onClick={() => setActiveTab('questionnaires')} className={`shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'questionnaires' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-800'}`}>Questionnaires{displayedQuestionnaires.length > 0 ? ` (${displayedQuestionnaires.length})` : ''}</button>
        <button onClick={() => setActiveTab('recus')} className={`relative shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'recus' ? 'border-b-2 border-violet-600 text-violet-700' : 'text-gray-500 hover:text-gray-800'}`}>
          Reçus du client
          {nbRecusNonLus > 0 && <span className="absolute -top-1 -right-1 bg-violet-600 text-white text-[11px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-md">{nbRecusNonLus}</span>}
        </button>
        <button onClick={() => setActiveTab('finances')} className={`shrink-0 px-4 py-3 font-bold text-sm ${activeTab === 'finances' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-500 hover:text-gray-800'}`}>Finances</button>
      </div>

      {activeTab === 'infos' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
          <BlocAccesCompte client={client} onRenvoyer={handleResendInvite} />
          <h3 className="text-lg font-bold text-gray-800">Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Module Assigné</label>
              <select value={client.module_id || ''} onChange={(e) => handleModuleChange(client.id, e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-900 text-sm font-bold rounded-xl focus:ring-indigo-500 block w-full p-3 outline-none">
                <option value="">Aucun module</option>
                {modules.map(m => (<option key={m.id} value={m.id}>{m.nom}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">Formateur Assigné</label>
              <select value={client.formateur_id || ''} onChange={(e) => assignFormateur(client.id, e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-900 text-sm font-bold rounded-xl focus:ring-indigo-500 block w-full p-3 outline-none">
                <option value="">Non assigné</option>
                {formateurs.map(f => (<option key={f.id} value={f.id}>{f.nom}</option>))}
              </select>
            </div>
          </div>

          <h3 className="text-lg font-bold text-gray-800 mt-8">Informations Personnelles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Nom Complet</label>
              <input className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.nomcomplet_client} onChange={e => setClientInfo({ ...clientInfo, nomcomplet_client: e.target.value })} placeholder="Nom Complet (Génération Docs)" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Email Contact</label>
              <input className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.client_email} onChange={e => setClientInfo({ ...clientInfo, client_email: e.target.value })} placeholder="Email Contact" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Téléphone</label>
              <input className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.client_phone} onChange={e => setClientInfo({ ...clientInfo, client_phone: e.target.value })} placeholder="Téléphone" />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <label className="block text-xs font-bold text-gray-400 mb-1">Rue / N° de voie</label>
                <AddressAutocomplete
                  value={clientInfo.rue_client}
                  onChange={val => setClientInfo({ ...clientInfo, rue_client: val })}
                  onSelect={({ rue, codePostal, ville, region }) => setClientInfo(prev => ({ ...prev, rue_client: rue, code_postal_client: codePostal || prev.code_postal_client, ville_client: ville || prev.ville_client, region: region || prev.region }))}
                  placeholder="Ex : 245 rue Jeanine"
                  className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Code Postal</label>
                <input className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.code_postal_client} onChange={e => setClientInfo({ ...clientInfo, code_postal_client: e.target.value })} placeholder="Ex : 75001" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-gray-400 mb-1">Ville</label>
                <input className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.ville_client} onChange={e => setClientInfo({ ...clientInfo, ville_client: e.target.value })} placeholder="Ex : Paris" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-bold text-gray-400 mb-1">Région</label>
                <input list="region-list-client" className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.region} onChange={e => setClientInfo({ ...clientInfo, region: e.target.value })} placeholder="Ex : Bretagne" autoComplete="off" />
                <datalist id="region-list-client">{FRENCH_REGIONS.map(r => <option key={r} value={r} />)}</datalist>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">N° de Dossier</label>
              <input className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.numero_dossier} onChange={e => setClientInfo({ ...clientInfo, numero_dossier: e.target.value })} placeholder="N° de Dossier" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Montant de la Prestation (€)</label>
              <input type="number" className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.montant_prestation} onChange={e => setClientInfo({ ...clientInfo, montant_prestation: e.target.value })} placeholder="Montant en euros (ex: 1500)" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Part reversée au formateur (%)</label>
              <input type="number" min="0" max="100" className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.pourcentage_formateur} onChange={e => setClientInfo({ ...clientInfo, pourcentage_formateur: e.target.value })} placeholder="Ex: 50" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Modalités de la formation</label>
              <select className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={clientInfo.modalite_formation} onChange={e => setClientInfo({ ...clientInfo, modalite_formation: e.target.value })}>
                <option value="Mixte">Mixte (Présentiel & Distanciel)</option>
                <option value="Présentiel">Présentiel</option>
                <option value="Distanciel">Distanciel</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap justify-end items-center gap-3 pt-4 mb-4 border-b border-gray-100 pb-8">
            <button
              onClick={() => setIsConfirmDeleteOpen(true)}
              className="px-6 py-3 text-red-600 font-bold hover:bg-red-50 rounded-xl transition-colors flex items-center"
            >
              <Trash2 size={18} className="mr-2" />
              Supprimer le client
            </button>
            <button
              onClick={handleSaveClientInfo}
              disabled={isSavingInfo}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all flex items-center disabled:opacity-50"
            >
              <Save size={18} className="mr-2" />
              {isSavingInfo ? 'Enregistrement...' : 'Enregistrer les informations'}
            </button>
          </div>

          <DeleteConfirmationModal
            isOpen={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onConfirm={() => {
              setIsConfirmDeleteOpen(false);
              handleDeleteClient(client.id);
            }}
            itemName={clientInfo.nomcomplet_client || client.nom || "ce client"}
            title="Supprimer ce client ?"
            message="Toutes ses données seront supprimées définitivement. S'il a déjà signé un document ou un émargement, son dossier sera archivé à la place : son accès à SkorUp sera fermé, il ne recevra plus d'emails et ses documents signés seront conservés (Clients → Dossiers archivés)."
          />
        </div>
      )}

      {activeTab === 'docs_signes' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6 animate-fade-in">
          <div className="grid grid-cols-1 gap-4">
            {/* Sessions signées */}
            {sessions
              .filter(s => s.client_id === client.id && (s.signed_pdf_url || s.file_url_signed || s.metadata?.file_url_signed))
              .map(session => {
                const signedUrl = session.signed_pdf_url || session.file_url_signed || session.metadata?.file_url_signed;
                return (
                  <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:border-green-200 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 text-green-600 rounded-xl flex items-center justify-center">
                        <FileCheck size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{session.ressource_titre || session.nom || session.titre || 'Document Signé'}</h4>
                        <p className="text-xs text-gray-500 uppercase font-black">
                          Signé le {new Date(session.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewingSession && setViewingSession({ session: { ...session, file_url: signedUrl }, mode: 'view' })}
                        className="flex items-center gap-2 bg-white text-indigo-700 px-4 py-2 rounded-xl text-xs font-bold border border-indigo-200 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                      >
                        <Eye size={14} /> Voir
                      </button>
                      <button
                        onClick={() => handleDownloadNamedFile({ url: signedUrl, nom: session.ressource_titre || session.nom || session.titre || 'Document signé' }, client.nom_complet || client.nom)}
                        className="flex items-center gap-2 bg-white text-green-700 px-4 py-2 rounded-xl text-xs font-bold border border-green-200 hover:bg-green-600 hover:text-white transition-all shadow-sm"
                      >
                        <Download size={14} /> Télécharger
                      </button>
                    </div>
                  </div>
                );
              })}

            {/* Documents administratifs signés */}
            {documents
              .filter(d => d.user_id === client.id && (d.statut === 'Signé'))
              .map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-4 bg-emerald-50/30 rounded-2xl border border-emerald-100 hover:border-emerald-300 transition-all group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center">
                      <FileCheck size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{doc.nom}</h4>
                      <p className="text-xs text-emerald-600 font-bold uppercase tracking-widest">
                        Document Administratif Signé
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewingDocId && setViewingDocId(doc.id)}
                      className="flex items-center gap-2 bg-white text-indigo-700 px-4 py-2 rounded-xl text-xs font-bold border border-indigo-200 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                    >
                      <Eye size={14} /> Voir
                    </button>
                    <button
                      onClick={() => handleDownloadNamedFile({ url: doc.url || doc.file_url, nom: doc.nom }, client.nom_complet || client.nom)}
                      className="flex items-center gap-2 bg-white text-emerald-700 px-4 py-2 rounded-xl text-xs font-bold border border-emerald-200 hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                    >
                      <Download size={14} /> Télécharger
                    </button>
                  </div>
                </div>
              ))}

            {(sessions.filter(s => s.client_id === client.id && (s.signed_pdf_url || s.file_url_signed || s.metadata?.file_url_signed)).length === 0 &&
              documents.filter(d => d.user_id === client.id && (d.statut === 'Signé')).length === 0) && (
              <div className="text-center py-8 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                <p className="text-gray-400 text-sm italic">Aucun document signé n'est archivé pour ce client.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'administratif' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6 animate-fade-in">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-bold text-gray-800">Documents du dossier client</h3>
              <p className="text-xs text-gray-400 mt-0.5">Espace partagé avec le formateur assigné : factures, justificatifs, documents envoyés par le client par un autre biais, etc. Usage interne — jamais visible par le client.</p>
            </div>
            <button
              onClick={() => setShowAdminDocModal(true)}
              className="bg-violet-700 hover:bg-violet-800 text-white text-xs font-bold px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all shadow-sm shrink-0"
            >
              <Plus className="w-4 h-4" /> Ajouter un document
            </button>
          </div>

          {documents.filter(d => d.user_id === client.id && isDossierDoc(d)).length === 0 ? (
            <div className="text-center py-8 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
              <Archive className="mx-auto mb-3 text-gray-300" size={32} />
              <p className="text-gray-400 text-sm italic">Aucun document dans le dossier de ce client.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.filter(d => d.user_id === client.id && isDossierDoc(d)).map(doc => (
                <div key={doc.id} className="flex items-center justify-between bg-violet-50/30 rounded-2xl px-4 py-3 border border-violet-100 group">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-gray-700 truncate block">{doc.nom}</span>
                      <span className="text-[11px] font-black uppercase tracking-widest text-violet-600">{doc.type_document || 'Autre'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openSecureStorageFile(doc.url)}
                      className="bg-white text-violet-700 hover:bg-violet-600 hover:text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-violet-200 transition-all"
                    >
                      Ouvrir
                    </button>
                    <button
                      onClick={() => handleDownloadNamedFile(doc, client.nom_complet || client.nom)}
                      className="text-gray-400 hover:text-violet-700 hover:bg-violet-50 p-1.5 rounded-lg transition-all"
                      title="Télécharger sous le nom indiqué"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => showDeleteConfirm(
                        `Supprimer "${doc.nom}" ?`,
                        'Ce document sera définitivement retiré du dossier du client.',
                        async () => { hideDeleteConfirm(); await handleDeleteAdminDoc(doc.id); }
                      )}
                      className="text-gray-300 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100"
                      title="Supprimer ce document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAdminDocModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
                <h4 className="text-base font-black text-gray-900 mb-1">Ajouter un document au dossier</h4>
                <p className="text-xs text-gray-400 mb-4">Ce fichier sera archivé dans le dossier de {client.nom_complet || client.nom || 'ce client'}, visible par vous et le formateur assigné (pas par le client).</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Fichier</label>
                    <input
                      type="file"
                      onChange={(e) => {
                        const f = e.target.files[0] || null;
                        setAdminDocFile(f);
                        // Pré-remplit le nom avec celui du fichier (sans l'extension) — modifiable ensuite ; on ne
                        // remplace pas un nom déjà saisi à la main.
                        if (f && !adminDocName.trim()) {
                          setAdminDocName(f.name.replace(/\.[^/.]+$/, ''));
                        }
                      }}
                      className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Nom du document</label>
                    <input
                      type="text"
                      value={adminDocName}
                      onChange={(e) => setAdminDocName(e.target.value)}
                      placeholder="Ex : Facture finale"
                      className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Type de document</label>
                    <select
                      value={adminDocType}
                      onChange={(e) => setAdminDocType(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                    >
                      <option value="Administratif">Administratif</option>
                      <option value="Contrat">Contrat</option>
                      <option value="Mission">Mission</option>
                      <option value="Pièce justificative">Pièce justificative</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button
                    onClick={() => { setShowAdminDocModal(false); setAdminDocFile(null); setAdminDocName(''); setAdminDocType('Administratif'); }}
                    className="flex-1 text-gray-500 hover:text-gray-800 font-bold text-sm py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleUploadAdminDoc}
                    disabled={isUploadingAdminDoc || !adminDocFile || !adminDocName.trim()}
                    className="flex-1 bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white font-bold text-sm py-2.5 rounded-xl transition-all"
                  >
                    {isUploadingAdminDoc ? 'Envoi...' : 'Ajouter au dossier'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'exercices' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm animate-fade-in">
          {clientExercises.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
              <FileCheck className="mx-auto mb-3 text-gray-300" size={32} />
              <p className="text-gray-400 text-sm italic">Aucun exercice assigné à ce client pour le moment.</p>
            </div>
          ) : (() => {
            // Priorité d'affichage : rendu et en attente de correction d'abord, puis pas encore rendu, puis déjà traité.
            const rank = (s) => {
              if (s.reponse_url && s.correction_statut !== 'Validé' && s.correction_statut !== 'À corriger') return 0;
              if (!s.reponse_url) return 1;
              return 2;
            };
            const sortedExercises = [...clientExercises].sort((a, b) => rank(a) - rank(b));
            return (
              <div className="overflow-hidden rounded-2xl border border-gray-100">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-gray-400 font-bold uppercase text-xs tracking-widest">
                    <tr>
                      <th className="px-4 py-3">Exercice</th>
                      <th className="px-4 py-3">Séance</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {sortedExercises.map(session => {
                      const docUrl = session.file_url_signed || session.metadata?.file_url_signed || session.file_url || session.ressource_url;
                      let badge;
                      if (!session.reponse_url) {
                        badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-gray-100 text-gray-400">Pas encore rendu</span>;
                      } else if (session.correction_statut === 'Validé') {
                        badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-green-100 text-green-700">✅ Validé</span>;
                      } else if (session.correction_statut === 'À corriger') {
                        badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-700">📝 À corriger (envoyé)</span>;
                      } else {
                        badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">🟢 Rendu — à corriger</span>;
                      }
                      return (
                        <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-semibold text-gray-800 text-xs">{session.ressource_titre || session.titre || session.nom}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400">{session.numero_seance != null ? `Séance ${session.numero_seance}` : (session.metadata?.moment === 'fin' ? 'Doc. de fin' : 'Doc. de début')}</td>
                          <td className="px-4 py-3">{badge}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end items-center gap-2">
                              <button
                                onClick={() => setViewingSession({ session: { ...session, file_url: docUrl }, mode: 'view' })}
                                className="text-xs font-bold px-3 py-1.5 rounded-lg border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 transition-all"
                              >
                                Consulter
                              </button>
                              {session.reponse_url && (
                                <button
                                  onClick={() => setCorrectionModalSession(session)}
                                  className={`text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all ${session.correction_statut === 'Validé' ? 'bg-green-600 text-white hover:bg-green-700' : session.correction_statut === 'À corriger' ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                                >
                                  {session.correction_statut === 'Validé' ? 'Validé' : session.correction_statut === 'À corriger' ? 'Modifier' : 'Corriger'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'docs' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-800">Documents de ce client</h3>
              <p className="text-xs text-gray-400 mt-0.5">Documents issus du module + ajouts personnalisés.</p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* "Régénérer PDF" et "Envoyer pour signature" retirés du 02/09/2026 : ces deux
                  boutons ne concernaient pas les "Ajouts personnalisés" (le premier régénère les
                  documents visuels du module, le second ouvre un envoi par groupe de documents
                  distinct) et créaient une confusion de doublon avec les boutons Envoyer/Consulter
                  disponibles sur chaque ligne d'"Ajouts personnalisés" ci-dessous. */}
              <button
                onClick={() => { setSelectedGroupId(null); setGroupeMode('immediate'); setGroupeDate(''); setShowSendDocsModal(true); }}
                className="bg-white hover:bg-indigo-50 text-indigo-700 text-xs font-bold px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all border border-indigo-200"
                title="Envoyer tous les documents d'un groupe en une fois"
              >
                📁 Envoyer un groupe
              </button>
              <button
                onClick={() => { setAddDocSendMode('immediate'); setAddDocScheduledDate(''); setShowAddDocModal(true); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all shadow-sm"
              >
                <Plus className="w-4 h-4" /> Ajouter
              </button>
            </div>
          </div>

          {isLoadingAssigned ? (
            <p className="text-gray-400 text-sm italic">Chargement...</p>
          ) : (moduleDocResources.length === 0 && assignedDocs.length === 0) ? (
            <div className="text-center py-10 text-gray-400">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm italic">Aucun document configuré pour ce client.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {moduleDocResources.length > 0 && (
                <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">Depuis le module</p>
              )}
              {moduleDocResources.map(res => {
                // "à signer" détecté via classification/requiresClientSignature/documentType — mêmes
                // trois signaux que needsSignature ailleurs dans le fichier (ex: renderResourceCard),
                // avec analyse défensive du JSON (metadata peut arriver en chaîne ou déjà en objet).
                const resMeta = typeof res.metadata === 'string' && res.metadata.startsWith('{')
                  ? (() => { try { return JSON.parse(res.metadata); } catch { return {}; } })()
                  : (res.metadata || {});
                const needsSignatureRes = resMeta.classification === 'a_signer' || resMeta.requiresClientSignature === true || resMeta.documentType === 'signature';
                return (
                <div key={res.id} className="flex items-center justify-between bg-indigo-50/40 rounded-2xl px-4 py-3 border border-indigo-100">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-indigo-400 shrink-0" />
                    <div>
                      <span className="text-sm font-bold text-gray-700">{res.titre}</span>
                      {needsSignatureRes && (
                        <span className="ml-2 text-[11px] font-black bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full uppercase">à signer</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {needsSignatureRes && (
                      <button
                        onClick={() => { const previewWin = window.open('', '_blank'); handleGenerateDocx(client, res.titre, false, null, false, 'preview', previewWin); }}
                        className="bg-gray-50 text-gray-600 hover:bg-gray-100 text-xs font-bold px-3 py-1.5 rounded-lg border border-gray-200 transition-all"
                        title="Ouvrir un aperçu dans un nouvel onglet, sans l'envoyer"
                      >
                        Consulter
                      </button>
                    )}
                    <button
                      onClick={() => handleGenerateDocx(client, res.titre)}
                      className="bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-indigo-100 transition-all"
                      title={needsSignatureRes ? 'Générer et envoyer pour signature' : undefined}
                    >
                      {needsSignatureRes ? 'Envoyer' : 'Générer'}
                    </button>
                  </div>
                </div>
                );
              })}
              {assignedDocs.length > 0 && (
                <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1 mt-3">Ajouts personnalisés</p>
              )}
              {assignedDocs.map(doc => (
                <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-2xl px-4 py-3 border border-gray-100 group">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-indigo-400 shrink-0" />
                    <div>
                      <span className="text-sm font-bold text-gray-700">{doc.template_titre}</span>
                      {doc.send_mode === 'scheduled' && !doc.sent_at && (
                        <span
                          className={`ml-2 text-[11px] font-black px-2 py-0.5 rounded-full uppercase ${doc.send_error ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}
                          title={doc.send_error ? `Erreur lors du dernier essai : ${doc.send_error}` : "Ce document sera généré et envoyé automatiquement à la date indiquée."}
                        >
                          {doc.send_error ? '⚠ Erreur — nouvel essai auto.' : `📅 Programmé le ${doc.scheduled_date ? new Date(doc.scheduled_date + 'T00:00:00').toLocaleDateString('fr-FR') : ''}`}
                        </span>
                      )}
                      {doc.send_mode === 'scheduled' && doc.sent_at && (
                        <span className="ml-2 text-[11px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase" title={`Envoyé automatiquement le ${new Date(doc.sent_at).toLocaleDateString('fr-FR')}`}>
                          ✓ Envoyé auto.
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Mêmes boutons pour tous les documents ajoutés depuis le 02/09/2026, qu'ils
                        nécessitent une signature ou non (avant : bouton "Générer" seul si le
                        document n'était pas détecté comme "à signer"). */}
                    <button
                      onClick={() => { const previewWin = window.open('', '_blank'); handleGenerateDocx(client, doc.template_titre, false, null, false, 'preview', previewWin); }}
                      className="bg-gray-50 text-gray-600 hover:bg-gray-100 text-xs font-bold px-3 py-1.5 rounded-lg border border-gray-200 transition-all"
                      title="Ouvrir un aperçu dans un nouvel onglet, sans l'envoyer"
                    >
                      Consulter
                    </button>
                    <button
                      onClick={() => handleGenerateDocx(client, doc.template_titre)}
                      className="bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-indigo-100 transition-all"
                      title="Générer et envoyer"
                    >
                      Envoyer
                    </button>
                    <button
                      onClick={() => { setScheduleEditDoc(doc); setScheduleEditMode(doc.send_mode || 'immediate'); setScheduleEditDate(doc.scheduled_date || ''); }}
                      className="text-gray-400 hover:text-indigo-600 transition-colors p-1.5 rounded-lg hover:bg-indigo-50"
                      title="Programmer l'envoi de ce document"
                    >
                      📅
                    </button>
                    <button
                      onClick={() => showDeleteConfirm(
                        `Retirer "${doc.template_titre}" ?`,
                        'Ce document personnalisé ne sera plus assigné à ce client.',
                        async () => { hideDeleteConfirm(); await handleRemoveAssignedDoc(doc.id); }
                      )}
                      className="text-gray-300 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100"
                      title="Retirer ce document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAddDocModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
                <h4 className="text-base font-black text-gray-900 mb-1">Ajouter un document</h4>
                <p className="text-xs text-gray-400 mb-3">Choisissez un modèle de la modélothèque à ajouter.</p>

                <div className="bg-gray-50 rounded-xl border border-gray-100 p-3 mb-3 space-y-2">
                  <p className="text-xs font-black uppercase tracking-widest text-gray-400">Envoi</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAddDocSendMode('immediate')}
                      className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg border transition-all ${addDocSendMode === 'immediate' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                    >
                      Maintenant
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddDocSendMode('scheduled')}
                      className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg border transition-all ${addDocSendMode === 'scheduled' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                    >
                      📅 Programmer
                    </button>
                  </div>
                  {addDocSendMode === 'scheduled' && (
                    <div>
                      <label className="text-[11px] font-bold text-gray-500 block mb-1">Date d'envoi automatique</label>
                      <input
                        type="date"
                        value={addDocScheduledDate}
                        onChange={(e) => setAddDocScheduledDate(e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <p className="text-xs text-gray-400 mt-1">Le document sera généré et envoyé au client automatiquement ce jour-là (ex : jour de démarrage de la formation), sans action de votre part.</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {Object.entries(allTemplatesForPicker || {})
                    .filter(([titre]) =>
                      !assignedDocs.some(d => d.template_titre === titre) &&
                      !moduleDocResources.some(r => r.titre === titre)
                    )
                    .map(([titre, tpl]) => (
                      <button
                        key={titre}
                        onClick={() => handleAddAssignedDoc(titre, tpl.url, addDocSendMode, addDocScheduledDate)}
                        className="w-full flex items-center gap-3 text-left bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 text-gray-700 font-bold text-sm px-4 py-3 rounded-xl border border-gray-100 hover:border-indigo-200 transition-all"
                      >
                        <FileText className="w-4 h-4 shrink-0 text-indigo-300" />
                        {titre}
                      </button>
                    ))
                  }
                  {Object.entries(allTemplatesForPicker || {}).filter(([titre]) =>
                    !assignedDocs.some(d => d.template_titre === titre) &&
                    !moduleDocResources.some(r => r.titre === titre)
                  ).length === 0 && (
                    <p className="text-gray-400 text-sm italic text-center py-6">Tous les modèles disponibles sont déjà dans la liste.</p>
                  )}
                </div>
                <button
                  onClick={() => setShowAddDocModal(false)}
                  className="mt-4 w-full text-gray-500 hover:text-gray-800 font-bold text-sm py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {scheduleEditDoc && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
                <h4 className="text-base font-black text-gray-900 mb-1">Programmer l'envoi</h4>
                <p className="text-xs text-gray-400 mb-3">« {scheduleEditDoc.template_titre} »</p>
                <div className="bg-gray-50 rounded-xl border border-gray-100 p-3 mb-3 space-y-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setScheduleEditMode('immediate')}
                      className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg border transition-all ${scheduleEditMode === 'immediate' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                    >
                      Manuel (comme avant)
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleEditMode('scheduled')}
                      className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg border transition-all ${scheduleEditMode === 'scheduled' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                    >
                      📅 Programmer
                    </button>
                  </div>
                  {scheduleEditMode === 'scheduled' && (
                    <div>
                      <label className="text-[11px] font-bold text-gray-500 block mb-1">Date d'envoi automatique</label>
                      <input
                        type="date"
                        value={scheduleEditDate}
                        onChange={(e) => setScheduleEditDate(e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <p className="text-xs text-gray-400 mt-1">Le document sera généré et envoyé au client automatiquement ce jour-là, sans action de votre part.</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setScheduleEditDoc(null)}
                    className="flex-1 text-gray-500 hover:text-gray-800 font-bold text-sm py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleUpdateAssignedDocSchedule(scheduleEditDoc.id, scheduleEditMode, scheduleEditDate)}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm py-2.5 rounded-xl transition-all"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}

          {showSendDocsModal && (() => {
            const aEnvoyer = documentsDuGroupe.filter(d => d.statut === 'a_envoyer');
            const auj = jourLocalISO(new Date());
            return (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
                <h4 className="text-base font-black text-gray-900 mb-1">Envoyer un groupe de documents</h4>
                <p className="text-xs text-gray-400 mb-4">Tous les documents du groupe sont envoyés en une fois à ce client. Ceux qu'il a déjà reçus sont ignorés.</p>
                {!selectedGroupId ? (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1 mb-4">
                    {localDocGroups.length === 0 ? (
                      <p className="text-gray-400 text-sm italic text-center py-6">Aucun groupe de documents. Créez-en un dans Documents › Groupes de documents.</p>
                    ) : localDocGroups.map(grp => {
                      const n = nbDocsGroupe(grp.id);
                      return (
                        <button key={grp.id} onClick={() => setSelectedGroupId(grp.id)} disabled={n === 0}
                          className="w-full flex items-center justify-between gap-3 text-left font-bold text-sm px-4 py-3 rounded-xl border bg-gray-50 hover:bg-indigo-50 text-gray-700 border-gray-100 hover:border-indigo-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                          <span className="flex items-center gap-2 min-w-0"><span className="text-base">📁</span><span className="truncate">{grp.nom}</span></span>
                          <span className="text-xs font-bold text-gray-400 shrink-0">{n} document{n > 1 ? 's' : ''}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <button onClick={() => setSelectedGroupId(null)} disabled={isSendingDocs} className="text-xs font-bold text-indigo-600 hover:underline mb-2">← Changer de groupe</button>
                    <p className="text-sm font-black text-gray-800 mb-2">📁 {(localDocGroups.find(g => String(g.id) === String(selectedGroupId)) || {}).nom}</p>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1 mb-4">
                      {documentsDuGroupe.map(d => (
                        <div key={d.titre} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border text-sm ${d.statut === 'a_envoyer' ? 'bg-white border-gray-100 text-gray-800' : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
                          <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 shrink-0 text-indigo-300" /><span className="truncate">{d.titre}</span></span>
                          <span className="text-[11px] font-bold shrink-0">
                            {d.statut === 'envoye' ? 'déjà envoyé' : d.statut === 'programme' ? 'déjà programmé' : (groupeProgression && groupeProgression.titre === d.titre ? 'envoi…' : '')}
                          </span>
                        </div>
                      ))}
                    </div>
                    {aEnvoyer.length > 0 ? (
                      <div className="bg-gray-50 rounded-xl border border-gray-100 p-3 mb-4 space-y-2">
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setGroupeMode('immediate')} disabled={isSendingDocs}
                            className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg border transition-all ${groupeMode === 'immediate' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}>Maintenant</button>
                          <button type="button" onClick={() => setGroupeMode('scheduled')} disabled={isSendingDocs}
                            className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg border transition-all ${groupeMode === 'scheduled' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}>📅 Programmer</button>
                        </div>
                        {groupeMode === 'scheduled' && (
                          <input type="date" min={auj} value={groupeDate} onChange={e => setGroupeDate(e.target.value)} aria-label="Date d'envoi"
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                        )}
                        <p className="text-xs text-gray-400">
                          {groupeMode === 'scheduled'
                            ? "Les documents seront générés et envoyés automatiquement ce jour-là, le matin. Le client recevra un seul email."
                            : 'Le client recevra un seul email listant les documents.'}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-3 mb-4">Ce client a déjà reçu (ou va recevoir) tous les documents de ce groupe.</p>
                    )}
                    {groupeProgression && (
                      <div className="mb-4">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-600 transition-all" style={{ width: `${Math.round(groupeProgression.fait / Math.max(1, groupeProgression.total) * 100)}%` }} /></div>
                        <p className="text-xs text-gray-500 mt-1">Envoi {groupeProgression.fait + 1}/{groupeProgression.total}…</p>
                      </div>
                    )}
                  </>
                )}
                <div className="flex gap-2">
                  <button onClick={fermerEnvoiGroupe} disabled={isSendingDocs}
                    className="flex-1 text-gray-500 hover:text-gray-800 font-bold text-sm py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all disabled:opacity-40">
                    {isSendingDocs ? 'Patientez…' : 'Fermer'}
                  </button>
                  {selectedGroupId && aEnvoyer.length > 0 && (
                    <button onClick={envoyerGroupeDocuments} disabled={isSendingDocs || (groupeMode === 'scheduled' && !groupeDate)}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm py-2.5 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                      {isSendingDocs ? 'Envoi…' : `${groupeMode === 'scheduled' ? 'Programmer' : 'Envoyer'} ${aEnvoyer.length} document${aEnvoyer.length > 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'questionnaires' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">📝 Questionnaires & Quiz du parcours</h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">{displayedQuestionnaires.length} questionnaire{displayedQuestionnaires.length > 1 ? 's' : ''}</span>
              <button type="button" onClick={() => setShowSendQ(v => !v)}
                className="flex items-center gap-1.5 bg-violet-700 hover:bg-violet-800 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm transition-all">
                <Send size={13} /> {showSendQ ? 'Annuler' : 'Envoyer un questionnaire'}
              </button>
            </div>
          </div>
          {showSendQ && (
            <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4 space-y-3">
              <p className="text-xs text-violet-800">Choisissez un questionnaire ou un quiz : il apparaîtra dans « Mes Documents » de ce client, même s'il ne fait pas partie de son module.</p>
              {sendableQuestionnaires.length === 0 ? (
                <p className="text-xs text-gray-500 italic">Tous vos questionnaires sont déjà disponibles pour ce client. Créez-en d'autres dans l'onglet « Questionnaires & Quiz ».</p>
              ) : (
                <>
                  <select value={qToSend} onChange={e => setQToSend(e.target.value)} className="w-full p-2.5 bg-white border border-violet-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400">
                    <option value="">— Sélectionner —</option>
                    {sendableQuestionnaires.map(q => {
                      const m = (() => { try { return typeof q.metadata === 'string' ? JSON.parse(q.metadata) : (q.metadata || {}); } catch { return {}; } })();
                      const modName = q.module_id ? (modules || []).find(md => String(md.id) === String(q.module_id))?.nom : null;
                      return <option key={q.id} value={String(q.id)}>{m.isQuiz ? '🎯 ' : '📝 '}{q.titre}{modName ? ` (module ${modName})` : ''}</option>;
                    })}
                  </select>
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={qNotifyEmail} onChange={e => setQNotifyEmail(e.target.checked)} className="accent-violet-600" />
                    Prévenir le client par email
                  </label>
                  <button type="button" onClick={handleSendQuestionnaire} disabled={!qToSend || isSendingQ}
                    className="w-full bg-violet-700 hover:bg-violet-800 text-white text-sm font-bold py-2.5 rounded-xl disabled:opacity-50">
                    {isSendingQ ? 'Envoi…' : 'Envoyer au client'}
                  </button>
                </>
              )}
            </div>
          )}
          {displayedQuestionnaires.length === 0 ? (
            <div className="py-10 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
              <p className="text-2xl mb-2">📝</p>
              <p className="text-gray-400 text-sm">Aucun questionnaire pour ce client.</p>
              <p className="text-gray-300 text-xs mt-1">Ajoutez-en dans son module, ou utilisez « Envoyer un questionnaire ».</p>
            </div>
          ) : (
            <div className="space-y-3">
              {displayedQuestionnaires.map(qResource => {
                const meta = (() => { try { return typeof qResource.metadata === 'string' ? JSON.parse(qResource.metadata) : (qResource.metadata || {}); } catch { return {}; } })();
                const questions = meta.questions || [];
                const isQuizResource = !!meta.isQuiz;
                const response = clientQuestionnaireResponses.find(r => String(r.questionnaire_id) === String(qResource.id));
                return (
                  <div key={qResource.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0 ${isQuizResource ? 'bg-violet-50 text-violet-600' : 'bg-amber-50 text-amber-600'}`}>{isQuizResource ? '🎯' : '📝'}</div>
                        <div>
                          <p className="font-bold text-gray-900 text-sm">{qResource.titre}</p>
                          <p className="text-xs text-gray-500">{questions.length} question{questions.length > 1 ? 's' : ''} · {isQuizResource ? 'Quiz noté' : 'Questionnaire'} · {qResource._source === 'envoye' ? `envoyé le ${new Date(qResource._assignment.assigned_at).toLocaleDateString('fr-FR')}` : qResource._source === 'module' ? 'dans le module' : 'hors module'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {response && isQuizResource && response.score_percent != null && (
                          <span className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${response.passed ? 'text-green-600 bg-green-50 border-green-100' : 'text-red-600 bg-red-50 border-red-100'}`}>
                            {response.passed ? '✅ Acquis' : '❌ Non acquis'} · {Math.round(response.score_percent)}%
                          </span>
                        )}
                        {response ? (
                          <span className="text-xs font-bold text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-100">✓ Complété le {new Date(response.completed_at).toLocaleDateString('fr-FR')}</span>
                        ) : (
                          <span className="text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100">⏳ En attente</span>
                        )}
                        {qResource._source === 'envoye' && !response && (
                          <button type="button" onClick={() => handleRemoveQAssignment(qResource._assignment)} title="Retirer ce questionnaire de l'espace du client"
                            className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </div>
                    {response && response.responses && (
                      <div className="border-t border-gray-50 p-4 bg-gray-50/50 space-y-3">
                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Réponses</p>
                        {questions.map((q, qi) => {
                          const answer = response.responses[q.id];
                          const hasAnswer = answer !== undefined && answer !== '' && (!Array.isArray(answer) || answer.length > 0);
                          const isQuestionCorrect = qEstNotee(q, isQuizResource)
                            ? (q.type === 'single'
                                ? answer === q.correctAnswer
                                : (Array.isArray(q.correctAnswer) && Array.isArray(answer) && q.correctAnswer.length === answer.length && q.correctAnswer.every(o => answer.includes(o))))
                            : null;
                          return (
                            <div key={q.id} className="space-y-1">
                              <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                                <span>{qi + 1}. {q.text}</span>
                                {isQuestionCorrect !== null && (
                                  <span className={`text-xs font-black ${isQuestionCorrect ? 'text-emerald-600' : 'text-red-500'}`}>{isQuestionCorrect ? '✓' : '✗'}</span>
                                )}
                              </p>
                              {hasAnswer ? (
                                <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                                  {Array.isArray(answer) ? (
                                    <ul className="space-y-0.5">
                                      {answer.map((a, i) => <li key={i} className="text-xs text-gray-700 flex items-center gap-1"><span className="text-violet-500">✓</span>{a}</li>)}
                                    </ul>
                                  ) : (
                                    <p className="text-xs text-gray-700">{answer}</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 italic px-3">—</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}


      {activeTab === 'seances' && (
        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-8 px-2">
            <div>
              <h3 className="text-xl font-black text-gray-900">Planning & Supervision</h3>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-1">Vue par Blocs de Séances</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setTargetSessionForAddition({ clientId: client.id, nextNum: clientSessions.length + 1 });
                  setIsSessionItemModalOpen(true);
                }}
                className="bg-indigo-600 hover:bg-black text-white text-xs font-black px-6 py-3.5 rounded-2xl flex items-center gap-2 shadow-lg transition-all"
              >
                <Plus size={16} /> Ajouter une étape
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {(() => {
              const grouped = clientSessions.reduce((acc, s) => {
                const key = (s.numero_seance === null || s.numero_seance === undefined) ? 'Sans numéro' : s.numero_seance;
                if (!acc[key]) acc[key] = { numero: s.numero_seance, items: [] };
                acc[key].items.push(s);
                return acc;
              }, {});

              return (
                <DndContext
                  onDragStart={({ active }) => setActiveId(active.id)}
                  onDragEnd={({ active, over }) => {
                    if (over && handleMoveSessionItem) {
                      const targetNum = over.id === '__admin' ? 0 : Number(over.id);
                      const src = sessions.find(s => String(s.id) === String(active.id));
                      if (src && src.numero_seance !== targetNum) {
                        const targetGroup = Object.values(grouped).find(g => {
                          const zId = g.numero === 0 ? '__admin' : String(g.numero ?? '__null');
                          return zId === over.id;
                        });
                        const planCible = scheduleOf(targetGroup?.items);
                        handleMoveSessionItem(active.id, targetNum, planCible.date, planCible.debut, planCible.fin);
                      }
                    }
                    setActiveId(null);
                  }}
                >
                  {Object.values(grouped).sort((a, b) => (a.numero ?? 999) - (b.numero ?? 999)).map((group, gIdx) => {
                    const isAdminGroup = group.numero === 0 || group.items.some(s => s.is_administrative);
                    const zoneId = group.numero === 0 ? '__admin' : String(group.numero ?? '__null');
                    return (
                      <SessDropZone key={gIdx} zoneId={zoneId} isAdmin={isAdminGroup} hasActive={activeId != null}>
                        {(isGroupOver) => (
                          <>
                            <div className="bg-white p-4 md:p-6 flex flex-wrap items-center justify-between gap-4 border-b border-gray-50">
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl shadow-lg ${isAdminGroup ? 'bg-amber-500 text-white' : 'bg-indigo-600 text-white'}`}>
                                  {isAdminGroup ? <Archive size={22} /> : (group.numero ?? '?')}
                                </div>
                                <div>
                                  <h4 className="font-black text-gray-900 text-lg uppercase tracking-tight">
                                    {isAdminGroup ? 'BLOC ADMINISTRATIF' : `SÉANCE ${group.numero}`}
                                    {isGroupOver && activeId && (
                                      <span className={`ml-3 text-[11px] font-black px-2 py-0.5 rounded-full animate-pulse ${isAdminGroup ? 'text-amber-600 bg-amber-100' : 'text-indigo-500 bg-indigo-100'}`}>Déposer ici</span>
                                    )}
                                  </h4>
                                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{isAdminGroup ? '7 jours avant la Séance 1' : 'Planification globale'}</p>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-4 bg-gray-50 p-3 rounded-2xl border border-gray-100">
                                <div className="flex flex-col">
                                  <label className="text-[11px] font-black text-gray-400 uppercase mb-1 ml-1">Date</label>
                                  <SessionDateInput
                                    value={scheduleOf(group.items).date || ''}
                                    onCommit={(v) => { group.items.forEach(s => updateSessionDate(s.id, v)); }}
                                    className="bg-white border border-gray-200 rounded-xl px-3 py-1.5 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                  />
                                </div>
                                <div className="flex flex-col">
                                  <label className="text-[11px] font-black text-gray-400 uppercase mb-1 ml-1">Début</label>
                                  <input
                                    type="time"
                                    value={editedTimes[group.items[0]?.id]?.start ?? scheduleOf(group.items).debut ?? ''}
                                    onChange={(e) => { const g = scheduleOf(group.items); group.items.forEach(s => onTimeChange(s.id, 'start', e.target.value, { start: g.debut, end: g.fin })); }}
                                    onBlur={() => group.items.forEach(s => onSaveTimes(s.id))}
                                    className="bg-white border border-gray-200 rounded-xl px-3 py-1.5 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 transition-all w-24"
                                  />
                                </div>
                                <div className="flex flex-col">
                                  <label className="text-[11px] font-black text-gray-400 uppercase mb-1 ml-1">Fin</label>
                                  <input
                                    type="time"
                                    value={editedTimes[group.items[0]?.id]?.end ?? scheduleOf(group.items).fin ?? ''}
                                    onChange={(e) => { const g = scheduleOf(group.items); group.items.forEach(s => onTimeChange(s.id, 'end', e.target.value, { start: g.debut, end: g.fin })); }}
                                    onBlur={() => group.items.forEach(s => onSaveTimes(s.id))}
                                    className="bg-white border border-gray-200 rounded-xl px-3 py-1.5 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 transition-all w-24"
                                  />
                                </div>
                                {lastModifiedSessionId && group.items.some(s => s.id === lastModifiedSessionId) && (
                                  <div className="flex items-center text-green-500 animate-pulse">
                                    <Check size={16} strokeWidth={3} />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="p-4 space-y-3">
                              {group.items.map(s => {
                                // AFFICHAGE (2026-10-20) : seulement les signatures réellement attendues (voir signaturesAttendues).
                                const _sig = signaturesAttendues(s);
                                const clientRequired = _sig.client;
                                const coachRequired = _sig.formateur;
                                return (
                                  <SessDragItem key={s.id} itemId={s.id} activeId={activeId}>
                                    <div className="flex items-center gap-4">
                                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                        s.type_activite === 'Exercice' ? 'bg-amber-50 text-amber-600' :
                                        s.type_activite === 'Document PDF' ? 'bg-blue-50 text-blue-600' : 'bg-indigo-50 text-indigo-600'
                                      }`}>
                                        {s.type_activite === 'Exercice' ? <PenTool size={20} /> : <FileText size={20} />}
                                      </div>
                                      <div>
                                        <h5 className="font-bold text-gray-900 text-sm">{s.ressource_titre || s.titre || s.nom || 'Activité'}</h5>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                          <span className="text-[11px] font-black uppercase tracking-widest text-gray-400">{s.type_activite === 'signature' ? 'Émargement' : (s.type_activite || 'Activité')}</span>
                                          {!clientRequired && !coachRequired ? (
                                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-gray-50 text-gray-400">Aucune signature</span>
                                          ) : (
                                          <div className="flex flex-wrap items-center gap-2">
                                            {clientRequired ? (
                                              <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-md ${s.statut_client === 'Signé' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                Client: {s.statut_client === 'Signé' ? 'OK ✓' : 'Attente'}
                                              </span>
                                            ) : (
                                              <span className="text-[11px] font-black uppercase px-2 py-0.5 rounded-md bg-gray-100 text-gray-400">Client: N/A</span>
                                            )}
                                            {coachRequired ? (
                                              <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-md ${s.statut_formateur === 'Signé' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                Formateur: {s.statut_formateur === 'Signé' ? 'OK ✓' : 'Attente'}
                                              </span>
                                            ) : (
                                              <span className="text-[11px] font-black uppercase px-2 py-0.5 rounded-md bg-gray-100 text-gray-400">Formateur: N/A</span>
                                            )}
                                          </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-1">
                                      <button
                                        onPointerDown={e => e.stopPropagation()}
                                        onClick={() => { setDocSettingsTarget(s); setIsDocSettingsOpen(true); }}
                                        className="p-2.5 text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                                        title="Paramètres du document"
                                      >
                                        <Settings size={17} />
                                      </button>
                                      <button
                                        onPointerDown={e => e.stopPropagation()}
                                        onClick={() => {
                                          const docUrl = s.file_url || s.ressource_url;
                                          if (docUrl) {
                                            setViewingSession({ session: { ...s, file_url: docUrl }, mode: 'view' });
                                          } else {
                                            toast.error("Aucun document lié.");
                                          }
                                        }}
                                        className="p-2.5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                                        title="Consulter"
                                      >
                                        <Eye size={20} />
                                      </button>
                                      {(s.type_activite === 'Exercice' || s.type_activite === 'exercice') && (
                                        s.reponse_url ? (
                                          <button
                                            onPointerDown={e => e.stopPropagation()}
                                            onClick={() => setCorrectionModalSession(s)}
                                            className={`relative p-2.5 rounded-xl transition-all ${s.correction_statut === 'Validé' ? 'text-green-600 hover:bg-green-50' : s.correction_statut === 'À corriger' ? 'text-amber-500 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                                            title="Corriger le rendu"
                                          >
                                            <FileCheck size={18} />
                                            <span className={`absolute top-1.5 right-1.5 block h-2 w-2 rounded-full ring-2 ring-white ${s.correction_statut === 'Validé' ? 'bg-green-500' : s.correction_statut === 'À corriger' ? 'bg-amber-400' : 'bg-emerald-500 animate-pulse'}`}></span>
                                          </button>
                                        ) : (
                                          <span className="relative p-2.5 text-gray-300 rounded-xl" title="Aucun rendu">
                                            <FileCheck size={18} />
                                          </span>
                                        )
                                      )}
                                      <button
                                        onPointerDown={e => e.stopPropagation()}
                                        onClick={() => handleDeleteSession(s.id)}
                                        className="p-2.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                                        title="Supprimer"
                                      >
                                        <Trash2 size={18} />
                                      </button>
                                    </div>
                                  </SessDragItem>
                                );
                              })}
                              {isAdminGroup && (
                                <button
                                  onClick={() => {
                                    const adminItem = group.items[0];
                                    setTargetSessionForAddition({ clientId: client.id, preSelectedSessionId: adminItem?.id, adminBloc: true });
                                    setIsSessionItemModalOpen(true);
                                  }}
                                  className="text-xs font-black bg-amber-500 text-white px-4 py-2 rounded-xl hover:bg-amber-600 transition-all shadow-sm flex items-center gap-2 mt-2"
                                >
                                  <Plus size={14} /> Ajouter un élément
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </SessDropZone>
                    );
                  })}
                </DndContext>
              );
            })()}
            {clientSessions.length === 0 && (
              <div className="py-20 text-center bg-gray-50 rounded-[32px] border-2 border-dashed border-gray-100">
                <History className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-400 italic text-sm">Aucune séance n'est encore programmée.</p>
              </div>
            )}
            {seancesAncienModuleVisibles.length > 0 && (
              <details open={repriseAncienPossible} className="mt-6 rounded-2xl border border-gray-200 bg-gray-50/60 p-4">
                <summary className="cursor-pointer text-sm font-bold text-gray-600">
                  📁 Éléments d'un ancien module ({seancesAncienModuleVisibles.length}) — conservés
                </summary>
                <p className="mt-2 text-xs text-gray-500">Ce client a changé de module. Ces éléments restent dans son dossier (preuves, signatures).{repriseAncienPossible ? ' Ils peuvent être repris dans son parcours actuel, séance par séance.' : ''}</p>
                {repriseAncienPossible && (
                  <button type="button" onClick={() => handleModuleChange(client.id, client.module_id, { fusionSeule: true })} className="mt-3 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold">
                    Reprendre dans le parcours actuel
                  </button>
                )}
                <div className="mt-3 divide-y divide-gray-100 rounded-xl bg-white border border-gray-100">
                  {seancesAncienModuleVisibles.map(s => (
                    <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
                      <span className="font-semibold text-gray-700">Séance {s.numero_seance} · {s.ressource_titre || s.nom}</span>
                      <span className="text-xs text-gray-500">
                        {s.date ? new Date(String(s.date).slice(0, 10) + 'T12:00:00').toLocaleDateString('fr-FR') : 'sans date'}
                        {elementSeanceEstSigne(s) ? ' · ✓ signé' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}

      {activeTab === 'docs' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-800 mb-6">Documents envoyés</h3>
          <div className="grid grid-cols-1 gap-3">
            {clientDocs.length > 0 ? clientDocs.map(doc => {
              const clientSigned = doc.signe_par_client;
              const formateurSigned = doc.signe_par_formateur;
              const organismeSigned = doc.signe_par_organisme;
              const _docMetaForBadge = (() => { try { return typeof doc.metadata === 'string' ? JSON.parse(doc.metadata) : (doc.metadata || {}); } catch { return {}; } })();
              const _docFieldsForBadge = Array.isArray(_docMetaForBadge.template_fields) ? _docMetaForBadge.template_fields : [];
              // FIX (2026-09-07) : quand le document porte des destination_roles (écrits à la
              // génération — voir instantiateDocument / handleGenerateDocx), ce sont EUX qui
              // déterminent les signatures réellement attendues. Avant ce correctif, needsClientSign
              // valait "vrai" par défaut pour TOUT document de cette liste — le badge "en attente de
              // signature client" s'affichait donc même pour un document formateur+administrateur
              // uniquement, où le client n'est jamais destinataire (bug remonté le 2026-09-07). Sans
              // destination_roles (anciens documents), on garde l'ancien comportement (client par défaut).
              const _destRolesForBadge = Array.isArray(_docMetaForBadge.destination_roles) && _docMetaForBadge.destination_roles.length > 0 ? _docMetaForBadge.destination_roles : null;
              // FIX (2026-10-05) : un document simplement transmis (sans signature) n'attend plus de signature.
              const _sigRequired = docRequiresSignature(doc);
              const sentToClientOnly = !_sigRequired && (_destRolesForBadge ? _destRolesForBadge.includes('client') : !!doc.visible_client);
              const needsClientSign = _sigRequired && (_destRolesForBadge ? _destRolesForBadge.includes('client') : (doc.requiresClientSignature !== false));
              const needsFormateurSign = !_sigRequired ? false : _destRolesForBadge
                ? _destRolesForBadge.includes('formateur')
                : (_docFieldsForBadge.some(f => ['signature_formateur', 'checkbox_formateur', 'texte_formateur'].includes(f.tag)) || doc.requiresTrainerSignature === true);
              const needsOrganismeSign = _sigRequired && !!(_destRolesForBadge && _destRolesForBadge.includes('organisme'));
              const fullySignedByAll = (!needsClientSign || clientSigned) && (!needsFormateurSign || formateurSigned) && (!needsOrganismeSign || organismeSigned);
              return (
              <div key={doc.id} className="p-4 border border-gray-100 rounded-2xl flex items-center justify-between group hover:border-indigo-200 transition-all shadow-sm">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${fullySignedByAll ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}><FileText size={20} /></div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm truncate max-w-[200px]">{doc.nom}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {sentToClientOnly && (
                        <span className="text-[11px] font-black uppercase px-2 py-0.5 rounded-md bg-sky-100 text-sky-700">📥 Transmis au client · sans signature</span>
                      )}
                      {needsClientSign && (
                        <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-md ${clientSigned ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {clientSigned ? '✓ Signature client reçue' : '⏳ En attente de signature client'}
                        </span>
                      )}
                      {(needsFormateurSign || (_sigRequired && formateurSigned)) && (
                        <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-md ${formateurSigned ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {formateurSigned ? '✓ Signature formateur reçue' : '⏳ En attente de signature formateur'}
                        </span>
                      )}
                      {(needsOrganismeSign || (_sigRequired && organismeSigned)) && (
                        <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-md ${organismeSigned ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {organismeSigned ? '✓ Signature administrateur reçue' : '⏳ En attente de signature administrateur'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setViewingDocId && setViewingDocId(doc.id)} className="p-2 text-indigo-500 hover:bg-indigo-50 bg-gray-50 rounded-lg transition-colors" title="Voir"><Eye size={18} /></button>
                  <button onClick={() => handleDownloadNamedFile({ url: doc.url || doc.file_url, nom: doc.nom }, client.nom_complet || client.nom)} className="p-2 text-gray-400 hover:text-indigo-600 bg-gray-50 rounded-lg" title="Télécharger"><Download size={18} /></button>
                  <button onClick={() => showDeleteConfirm(`Supprimer "${doc.nom}" ?`, 'Ce document sera définitivement supprimé.', async () => { hideDeleteConfirm(); if (!(await peutSupprimerDocument(doc.id))) return; await supabase.from('documents').delete().eq('id', doc.id); fetchDocuments && await fetchDocuments(); })} className="p-2 text-gray-300 hover:text-red-500 bg-gray-50 rounded-lg transition-colors" title="Supprimer"><Trash2 size={18} /></button>
                </div>
              </div>
              );
            }) : (
              <div className="text-center py-10 text-gray-400">
                <p className="text-2xl mb-2">✉️</p>
                <p className="text-sm italic">Aucun document envoyé pour signature.</p>
                <p className="text-xs text-gray-300 mt-1">Utilisez le bouton "Envoyer" sur un document ci-dessus (onglet "Documents de ce client").</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'recus' && (
        <DepotsClientPanel client={client} currentOrgId={currentOrgId} userRole="admin" estPro={estPro} nomUtilisateur={nomUtilisateur} onVu={onDepotsVus} />
      )}

      {activeTab === 'finances' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Euro size={20} className="text-emerald-600" /> Finances</h3>
          <BlocFinancements client={client} currentOrgId={currentOrgId} montantPrestation={clientInfo.montant_prestation} />
          <div className="border-t border-gray-100 pt-5"><BlocConventionsEntreprise client={client} /></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Montant total de la prestation</label>
              <div className="p-3 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-700 font-bold">
                {(parseFloat(clientInfo.montant_prestation) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € <span className="text-xs font-normal text-gray-400">(modifiable dans l'onglet "Infos &amp; Modalités")</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">Part reversée au formateur</label>
              <div className="p-3 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-700 font-bold">
                {clientInfo.pourcentage_formateur || 0}% <span className="text-xs font-normal text-gray-400">(modifiable dans l'onglet "Infos &amp; Modalités")</span>
              </div>
            </div>
          </div>

          {(() => {
            const montantTotal = parseFloat(clientInfo.montant_prestation) || 0;
            const montantPaye = clientPaiements.reduce((sum, p) => sum + (parseFloat(p.montant) || 0), 0);
            const resteDu = montantTotal - montantPaye;
            const pourcentageFormateur = parseFloat(clientInfo.pourcentage_formateur) || 0;
            const partFormateur = montantTotal * pourcentageFormateur / 100;
            const partOrganisme = montantTotal - partFormateur;
            const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
                  <p className="text-xs font-black uppercase text-emerald-700 tracking-widest mb-1">Payé</p>
                  <p className="text-lg font-black text-emerald-800">{fmt(montantPaye)}</p>
                </div>
                <div className={`rounded-2xl p-4 border ${resteDu > 0 ? 'bg-orange-50 border-orange-100' : 'bg-gray-50 border-gray-100'}`}>
                  <p className={`text-xs font-black uppercase tracking-widest mb-1 ${resteDu > 0 ? 'text-orange-700' : 'text-gray-500'}`}>Reste dû</p>
                  <p className={`text-lg font-black ${resteDu > 0 ? 'text-orange-800' : 'text-gray-600'}`}>{fmt(resteDu)}</p>
                </div>
                {pourcentageFormateur > 0 && (
                  <>
                    <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
                      <p className="text-xs font-black uppercase text-indigo-700 tracking-widest mb-1">Part formateur</p>
                      <p className="text-lg font-black text-indigo-800">{fmt(partFormateur)}</p>
                    </div>
                    <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4">
                      <p className="text-xs font-black uppercase text-violet-700 tracking-widest mb-1">Part organisme</p>
                      <p className="text-lg font-black text-violet-800">{fmt(partOrganisme)}</p>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-sm font-bold text-gray-700 mb-3">Ajouter un paiement reçu</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Montant (€)</label>
                <input type="number" step="0.01" className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={newPaiementMontant} onChange={e => setNewPaiementMontant(e.target.value)} placeholder="Ex: 500" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Date</label>
                <input type="date" className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={newPaiementDate} onChange={e => setNewPaiementDate(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1">Mode</label>
                <select className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={newPaiementMode} onChange={e => setNewPaiementMode(e.target.value)}>
                  <option value="Virement">Virement</option>
                  <option value="Carte bancaire">Carte bancaire</option>
                  <option value="Chèque">Chèque</option>
                  <option value="Espèces">Espèces</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <button
                  onClick={handleAddPaiement}
                  disabled={isSavingPaiement || !newPaiementMontant}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition-all disabled:opacity-50"
                >
                  {isSavingPaiement ? 'Ajout...' : '+ Ajouter'}
                </button>
              </div>
            </div>
            <div className="mt-3">
              <input className="w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-indigo-500 rounded-xl outline-none transition-colors" value={newPaiementNote} onChange={e => setNewPaiementNote(e.target.value)} placeholder="Note (optionnel, ex: 1er versement)" />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-sm font-bold text-gray-700 mb-3">Historique des paiements</h4>
            {isLoadingPaiements ? (
              <p className="text-sm text-gray-400 italic">Chargement...</p>
            ) : clientPaiements.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucun paiement enregistré pour ce client.</p>
            ) : (
              <div className="space-y-2">
                {clientPaiements.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div>
                      <span className="font-bold text-gray-800 text-sm">{(parseFloat(p.montant) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                      <span className="text-xs text-gray-400 ml-3">{p.date_paiement ? new Date(p.date_paiement).toLocaleDateString('fr-FR') : ''}</span>
                      {p.mode_paiement && <span className="text-xs text-gray-400 ml-3">{p.mode_paiement}</span>}
                      {p.note && <span className="text-xs text-gray-400 ml-3 italic">{p.note}</span>}
                    </div>
                    <button onClick={() => handleDeletePaiement(p.id)} className="p-2 text-gray-300 hover:text-red-500 rounded-lg transition-colors" title="Supprimer">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <CorrectionModal
        isOpen={!!correctionModalSession}
        onClose={() => setCorrectionModalSession(null)}
        session={correctionModalSession}
        onSave={handleSaveCorrection}
      />
      <ConfirmModal
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={hideDeleteConfirm}
      />
    </div>
  );
};

const SqueletteChargement = ({ texte = 'Chargement…' }) => (
  <div className="space-y-3" aria-busy="true">
    <p className="text-sm font-bold text-gray-500 flex items-center gap-2">
      <span className="w-4 h-4 border-2 border-gray-200 border-t-violet-600 rounded-full animate-spin"></span> {texte}
    </p>
    {[0, 1, 2].map(i => (
      <div key={i} className="h-20 rounded-3xl bg-gray-100 animate-pulse" />
    ))}
  </div>
);

// LOT C (2026-10-22) : dossiers archivés (clients qui avaient déjà signé), consultables et restaurables.
const DossiersArchives = ({ archives = [], onRestaurer = null }) => {
  const [ouvert, setOuvert] = React.useState(false);
  const [detail, setDetail] = React.useState({});
  const [enCours, setEnCours] = React.useState(null);
  if (!archives.length) return null;
  const jour = (v) => { try { return new Date(v).toLocaleDateString('fr-FR'); } catch (_) { return ''; } };
  const voir = async (id) => {
    if (detail[id] && !detail[id].chargement) { setDetail(d => { const n = { ...d }; delete n[id]; return n; }); return; }
    setDetail(d => ({ ...d, [id]: { chargement: true } }));
    const cols = await colonnesLegeres();
    const [dR, sR] = await Promise.all([
      supabase.from('documents').select(cols.documents).eq('user_id', id),
      supabase.from('sessions').select(cols.sessions).eq('client_id', id),
    ]);
    setDetail(d => ({ ...d, [id]: {
      chargement: false,
      erreur: !!(dR.error || sR.error),
      docs: (dR.data || []).filter(documentEstSigne),
      seances: (sR.data || []).filter(elementSeanceEstSigne)
        .sort((a, b) => (Number(a.numero_seance) || 0) - (Number(b.numero_seance) || 0)),
    } }));
  };
  const restaurer = async (c) => {
    if (!onRestaurer || enCours) return;
    setEnCours(c.id);
    try { await onRestaurer(c); } finally { setEnCours(null); }
  };
  return (
    <div className="mb-4">
      <button onClick={() => setOuvert(o => !o)} className="text-sm font-bold text-gray-500 hover:text-gray-900 flex items-center gap-2">
        🗄️ Dossiers archivés ({archives.length}) <span className="text-xs">{ouvert ? '▲' : '▼'}</span>
      </button>
      {ouvert && (
        <div className="mt-3 border border-gray-100 rounded-2xl divide-y divide-gray-100 bg-gray-50/60">
          <p className="px-4 py-3 text-xs text-gray-500">
            Clients supprimés alors qu'ils avaient déjà signé : leur accès à SkorUp est fermé et ils ne reçoivent plus d'emails,
            mais leurs documents signés et émargements sont conservés. Ils ne comptent pas dans la limite de votre offre.
          </p>
          {archives.map(c => {
            const d = detail[c.id];
            return (
              <div key={c.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-sm">{c.nom_complet || 'Client sans nom'}</p>
                    <p className="text-xs text-gray-500 break-all">{c.email_contact || ''}{c.archive_le ? `${c.email_contact ? ' · ' : ''}archivé le ${jour(c.archive_le)}` : ''}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => voir(c.id)} className="text-xs font-bold px-3 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-100">
                      {d && !d.chargement ? 'Masquer' : 'Documents signés'}
                    </button>
                    {onRestaurer && (
                      <button onClick={() => restaurer(c)} disabled={!!enCours} className="text-xs font-bold px-3 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                        {enCours === c.id ? 'Restauration…' : 'Restaurer'}
                      </button>
                    )}
                  </div>
                </div>
                {d && (
                  <div className="mt-3 bg-white rounded-xl border border-gray-100 p-3 text-sm">
                    {d.chargement ? <p className="text-gray-400 text-xs">Chargement…</p> : (
                      <>
                        {d.erreur && <p className="text-xs text-red-600 mb-2">Une partie des éléments n'a pas pu être chargée. Réessayez.</p>}
                        {d.docs.length === 0 && d.seances.length === 0 && <p className="text-xs text-gray-500">Aucun élément signé trouvé.</p>}
                        {d.docs.map(doc => {
                          const lien = doc.signed_pdf_url || doc.url;
                          return (
                            <div key={'d' + doc.id} className="flex items-center justify-between gap-2 py-1.5">
                              <span className="text-gray-800 min-w-0 break-words">📄 {doc.nom || 'Document'}</span>
                              {lien && <a href={lien} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-indigo-600 hover:underline shrink-0">Ouvrir</a>}
                            </div>
                          );
                        })}
                        {d.seances.map(s => {
                          const lien = lireMetaSeance(s.metadata).file_url_signed || s.signed_pdf_url;
                          return (
                            <div key={'s' + s.id} className="flex items-center justify-between gap-2 py-1.5">
                              <span className="text-gray-800 min-w-0 break-words">✍️ Séance {s.numero_seance ?? '–'}{s.date ? ` (${dateIsoVersFr(s.date)})` : ''} — {s.ressource_titre || s.nom || 'Élément'}</span>
                              {lien
                                ? <a href={lien} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-indigo-600 hover:underline shrink-0">Ouvrir</a>
                                : <span className="text-xs text-emerald-600 font-bold shrink-0">Signé</span>}
                            </div>
                          );
                        })}
                        {d.seances.length > 0 && (
                          <p className="text-xs text-gray-400 mt-2">Pour éditer le récapitulatif des émargements en PDF, restaurez le dossier (sans renvoyer d'accès), puis archivez-le de nouveau.</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── FINANCEMENT DES DOSSIERS (2026-10-23) ─────────────────────────────────────────────────────────
// Table « financements » (SQL financements) : une ligne par financeur d'un dossier client. Types alignés
// sur les lignes du BPF pour pouvoir le calculer plus tard. Sans la table : rien ne s'affiche.
const TYPES_FINANCEMENT = [
  ['personnel', 'Personnel (le client paie lui-même)'],
  ['cpf', 'CPF (Mon Compte Formation)'],
  ['opco', 'OPCO'],
  ['entreprise', 'Entreprise / employeur'],
  ['france_travail', 'France Travail'],
  ['region', 'Région'],
  ['public', 'Autre financement public (État, collectivité…)'],
  ['organisme_formation', 'Autre organisme de formation (sous-traitance)'],
  ['autre', 'Autre'],
];
const LIBELLES_COURTS_FINANCEMENT = { personnel: 'Personnel', cpf: 'CPF', opco: 'OPCO', entreprise: 'Entreprise', france_travail: 'France Travail', region: 'Région', public: 'Financement public', organisme_formation: 'Organisme de formation', autre: 'Autre' };
const STATUTS_FINANCEMENT = [['', '—'], ['a_demander', 'À demander'], ['demande_envoyee', 'Demande envoyée'], ['accord_recu', 'Accord reçu'], ['refuse', 'Refusé'], ['paye', 'Payé']];
const libelleFinancement = (t) => LIBELLES_COURTS_FINANCEMENT[t] || 'Autre';
const libelleStatutFinancement = (s) => (STATUTS_FINANCEMENT.find(([v]) => v === s) || [null, ''])[1];
// Espaces fines insécables remplacées par des espaces simples : la police des PDF ne les connaît pas.
const nombreFr = (n) => { const x = Math.round((Number(n) || 0) * 100) / 100; return x.toLocaleString('fr-FR', { minimumFractionDigits: Number.isInteger(x) ? 0 : 2, maximumFractionDigits: 2 }).replace(/[\u202f\u00a0]/g, ' '); };
const montantRenseigne = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));

// Valeurs des balises de documents à partir des lignes de financement d'un dossier.
const valeursBalisesFinancement = (lignes, montantPrestation) => {
  const L = (lignes || []).filter(Boolean);
  const vide = { financement_type: '', financeur_nom: '', montant_financeur: '', reste_a_charge: '', numero_prise_en_charge: '' };
  if (!L.length) return vide;
  const tiers = L.filter(l => l.type !== 'personnel');
  const perso = L.filter(l => l.type === 'personnel');
  const somme = (arr) => arr.reduce((s, l) => s + (montantRenseigne(l.montant) ? Number(l.montant) : 0), 0);
  const aMontant = (arr) => arr.some(l => montantRenseigne(l.montant));
  const types = [];
  L.forEach(l => { const x = libelleFinancement(l.type); if (!types.includes(x)) types.push(x); });
  const noms = [];
  tiers.forEach(l => { const x = String(l.financeur_nom || '').trim() || libelleFinancement(l.type); if (!noms.includes(x)) noms.push(x); });
  const total = Number(montantPrestation) || 0;
  let reste = '';
  if (perso.length && aMontant(perso)) reste = nombreFr(somme(perso));
  else if (total > 0 && tiers.length && aMontant(tiers)) reste = nombreFr(Math.max(0, total - somme(tiers)));
  return {
    financement_type: types.join(' + '),
    financeur_nom: noms.length ? noms.join(' / ') : 'Financement personnel',
    montant_financeur: tiers.length && aMontant(tiers) ? nombreFr(somme(tiers)) : '',
    reste_a_charge: reste,
    numero_prise_en_charge: L.map(l => String(l.numero_prise_en_charge || '').trim()).filter(Boolean).join(' / '),
  };
};
// Lecture du financement d'un dossier : table directe (administrateur), sinon fonction limitée
// (formateur : type + financeur ; client : son propre financement). Jamais bloquant.
const chargerFinancementsDossier = async (clientId) => {
  if (!clientId) return [];
  try {
    const { data, error } = await supabase.from('financements').select('*').eq('client_id', clientId)
      .order('position', { ascending: true }).order('created_at', { ascending: true });
    if (!error && Array.isArray(data) && data.length) return data;
    const r = await supabase.rpc('skorup_financements_dossier', { p_client: String(clientId) });
    return (!r.error && Array.isArray(r.data)) ? r.data : [];
  } catch (_) { return []; }
};
const valeursFinancement = async (clientId, montantPrestation) => valeursBalisesFinancement(await chargerFinancementsDossier(clientId), montantPrestation);

// Financements de tout l'organisme (liste des clients, administrateur).
const useFinancementsOrg = (orgId, rafraichir) => {
  const [etat, setEtat] = React.useState({ dispo: false, parClient: {} });
  React.useEffect(() => {
    if (!orgId) return undefined;
    let annule = false;
    fetchAllRowsWithRetry(() => supabase.from('financements').select('client_id, type, financeur_nom').eq('organisation_id', orgId).order('position', { ascending: true }), { label: 'financements', retries: 0 })
      .then(({ data, error }) => {
        if (annule) return;
        if (error || !Array.isArray(data)) { setEtat({ dispo: false, parClient: {} }); return; }
        const m = {};
        data.forEach(f => { const k = String(f.client_id); (m[k] = m[k] || []).push(f); });
        setEtat({ dispo: true, parClient: m });
      })
      .catch(() => {});
    return () => { annule = true; };
  }, [orgId, rafraichir]);
  return etat;
};
const typesFinancementDe = (lignes) => {
  const t = [];
  (lignes || []).forEach(f => { const x = libelleFinancement(f.type); if (!t.includes(x)) t.push(x); });
  return t;
};
const FiltreFinancement = ({ clients = [], parClient = {}, valeur, onChange }) => {
  if (!clients.length) return null;
  const typesDe = (c) => (parClient[String(c.id)] || []).map(f => f.type);
  const compte = (t) => clients.filter(c => (t === 'aucun' ? typesDe(c).length === 0 : typesDe(c).includes(t))).length;
  const options = [['tous', `Tous les financements (${clients.length})`]];
  TYPES_FINANCEMENT.forEach(([t]) => { const n = compte(t); if (n > 0 || valeur === t) options.push([t, `${libelleFinancement(t)} (${n})`]); });
  options.push(['aucun', `Non renseigné (${compte('aucun')})`]);
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      <label htmlFor="filtre-financement" className="text-xs font-bold text-gray-500">💶 Financement</label>
      <select id="filtre-financement" value={valeur} onChange={e => onChange(e.target.value)}
        className={`text-xs font-bold px-3 py-1.5 rounded-full border outline-none ${valeur !== 'tous' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
};

// ─── BPF : BILAN PÉDAGOGIQUE ET FINANCIER (2026-10-26) ───────────────────────────────────────────────
// Calcule les cadres du cerfa 10443 à partir des dossiers : financements (cadre C), séances datées
// de l'exercice (heures, cadres E et F), réglages bpf_reglages (objectif et spécialité des modules,
// formateurs internes / externes, situation des stagiaires, dispositif OPCO). Montants comptés au
// prorata des heures faites dans l'exercice (produits constatés « au fur et à mesure de l'exécution »).
// Saisies de l'organisme par exercice : bpf_exercices. Tables : SQL « bpf ».
const BPF_LIGNES_C = [
  ['c1', '1', 'Entreprises pour la formation de leurs salariés'],
  ['c2', '2', 'Organismes gestionnaires des fonds de la formation (total a à h)'],
  ['c2a', '2 a', "Contrats d'apprentissage"],
  ['c2b', '2 b', 'Contrats de professionnalisation'],
  ['c2c', '2 c', 'Promotion ou reconversion par alternance'],
  ['c2d', '2 d', 'Projets de transition professionnelle'],
  ['c2e', '2 e', 'Compte personnel de formation'],
  ['c2f', '2 f', "Dispositifs pour les personnes en recherche d'emploi"],
  ['c2g', '2 g', 'Dispositifs pour les travailleurs non-salariés'],
  ['c2h', '2 h', 'Plan de développement des compétences ou autres dispositifs'],
  ['c3', '3', 'Pouvoirs publics pour la formation de leurs agents'],
  ['c4', '4', 'Instances européennes'],
  ['c5', '5', 'État'],
  ['c6', '6', 'Conseils régionaux'],
  ['c7', '7', 'France Travail'],
  ['c8', '8', 'Autres ressources publiques'],
  ['c9', '9', 'Personnes à titre individuel et à leurs frais'],
  ['c10', '10', "Contrats avec d'autres organismes de formation (y compris CFA)"],
  ['c11', '11', 'Autres produits au titre de la formation professionnelle'],
];
const BPF_DISPOSITIFS_OPCO = [
  ['c2h', 'Plan de développement des compétences ou autre'],
  ['c2b', 'Contrat de professionnalisation'],
  ['c2c', 'Promotion ou reconversion par alternance (Pro-A)'],
  ['c2d', 'Projet de transition professionnelle'],
  ['c2a', "Contrat d'apprentissage"],
  ['c2f', "Dispositif pour personnes en recherche d'emploi"],
  ['c2g', 'Dispositif pour travailleurs non-salariés'],
];
const BPF_LIGNES_PUBLIQUES = [['c8', 'Autres ressources publiques'], ['c5', 'État'], ['c3', 'Pouvoirs publics pour leurs agents'], ['c4', 'Instances européennes']];
const BPF_SITUATIONS = [
  ['a', "Salarié(e) d'un employeur privé"],
  ['b', 'Apprenti(e)'],
  ['c', "Personne en recherche d'emploi"],
  ['d', 'Particulier à ses propres frais'],
  ['e', 'Autre (agent public, indépendant, bénévole…)'],
];
const BPF_OBJECTIFS = [
  ['e', 'Bilan de compétences'],
  ['f', 'Accompagnement à la VAE'],
  ['a', 'Diplôme ou titre RNCP (ou CQP enregistré au RNCP)'],
  ['b', 'Certification ou habilitation au répertoire spécifique (RS)'],
  ['c', 'CQP non enregistré au RNCP ni au RS'],
  ['d', 'Autre formation professionnelle'],
];
const BPF_NIVEAUX_RNCP = [['6_8', 'Niveau 6 à 8'], ['5', 'Niveau 5'], ['4', 'Niveau 4'], ['3', 'Niveau 3'], ['2', 'Niveau 2'], ['cqp', 'CQP sans niveau']];
const BPF_NSF = [
  ['415', "Développement des capacités d'orientation, d'insertion ou de réinsertion sociales et professionnelles"],
  ['413', 'Développement des capacités comportementales et relationnelles'],
  ['414', "Développement des capacités individuelles d'organisation"],
  ['410', 'Spécialités concernant plusieurs capacités'],
  ['333', 'Enseignement, formation'],
  ['315', "Ressources humaines, gestion du personnel, gestion de l'emploi"],
  ['310', 'Spécialités plurivalentes des échanges et de la gestion'],
  ['312', 'Commerce, vente'],
  ['314', 'Comptabilité, gestion'],
  ['320', 'Spécialités plurivalentes de la communication'],
  ['326', "Informatique, traitement de l'information, réseaux de transmission des données"],
  ['330', 'Spécialités plurivalentes sanitaires et sociales'],
  ['331', 'Santé'],
  ['332', 'Travail social'],
  ['334', 'Accueil, hôtellerie, tourisme'],
  ['336', 'Coiffure, esthétique et autres spécialités des services aux personnes'],
  ['136', 'Langues vivantes, civilisations étrangères et régionales'],
  ['100', 'Formations générales'],
];
const libelleNsf = (code) => (BPF_NSF.find(([c]) => c === String(code || '').trim()) || [null, ''])[1];
const libelleDe = (liste, v) => (liste.find(([k]) => k === v) || [null, ''])[1];

const minutesHeure = (s) => { const x = /^(\d{1,2})\s*[:hH]\s*(\d{2})/.exec(String(s || '').trim()); return x ? Number(x[1]) * 60 + Number(x[2]) : null; };
const heuresEntre = (debut, fin) => { const a = minutesHeure(debut), b = minutesHeure(fin); return (a !== null && b !== null && b > a) ? (b - a) / 60 : 0; };
const arrondiH = (h) => Math.round((Number(h) || 0) * 100) / 100;
const fmtH = (h) => `${nombreFr(Math.round(Number(h) || 0))} h`;
const fmtEur = (n) => `${nombreFr(Math.round(Number(n) || 0))} €`;

const ligneCDeFinancement = (f, reglage = {}) => {
  switch (f && f.type) {
    case 'personnel': return 'c9';
    case 'cpf': return 'c2e';
    case 'opco': return BPF_DISPOSITIFS_OPCO.some(([k]) => k === reglage.dispositif) ? reglage.dispositif : 'c2h';
    case 'entreprise': return 'c1';
    case 'france_travail': return 'c7';
    case 'region': return 'c6';
    case 'public': return BPF_LIGNES_PUBLIQUES.some(([k]) => k === reglage.ligne) ? reglage.ligne : 'c8';
    case 'organisme_formation': return 'c10';
    default: return 'c11';
  }
};
const suggestionObjectif = (module) => {
  const n = String((module && module.nom) || '').toLowerCase();
  if (/bilan/.test(n)) return 'e';
  if (/\bvae\b|validation des acquis/.test(n)) return 'f';
  return null;
};
const suggestionSituation = (fins) => {
  const t = (fins || []).map(f => f.type);
  if (t.includes('france_travail') || t.includes('region')) return 'c';
  if (t.includes('entreprise') || t.includes('opco')) return 'a';
  if (t.length && t.every(x => x === 'personnel')) return 'd';
  return null;
};
const suggestionStatutFormateur = (u) => {
  if (!u) return 'interne';
  if (u.role === 'admin') return 'interne';
  return String(u.formateur_nda || u.nda || '').trim() ? 'externe' : 'interne';
};

// Calcul pur (testable) : renvoie les cadres, le détail par dossier et les points à vérifier.
const calculerBpf = ({ debut, fin, clients = [], sessions = [], groupes = [], groupeSeances = [], financements = [], modules = [], utilisateurs = [], reglages = {} }) => {
  const reg = (cible, id) => reglages[`${cible}|${id}`] || {};
  const dans = (d) => !!d && d >= debut && d <= fin;
  // Séances par dossier : une séance = un numéro ; date la plus tôt, durée la plus longue de ses éléments.
  const parClient = new Map();
  sessions.forEach(s => {
    const n = Number(s.numero_seance);
    if (!s.client_id || !(n >= 1)) return;
    const k = String(s.client_id);
    if (!parClient.has(k)) parClient.set(k, new Map());
    const m = parClient.get(k);
    const e = m.get(n) || { numero: n, date: null, h: 0, heure_debut: null };
    const d = s.date ? String(s.date).slice(0, 10) : null;
    if (d && (!e.date || d < e.date)) e.date = d;
    const h = heuresEntre(s.heure_debut, s.heure_fin);
    if (h > e.h) { e.h = h; e.heure_debut = s.heure_debut; }
    m.set(n, e);
  });
  // 30/10/2026 : feuille d'émargement papier d'un groupe — absent = 0 h, présent en partie = heures saisies.
  groupeSeances.forEach(gs => {
    const pr = gs && gs.presences_papier;
    if (!pr || typeof pr !== 'object') return;
    Object.entries(pr).forEach(([cid, v]) => {
      const m = parClient.get(String(cid));
      const e = m && m.get(Number(gs.numero));
      if (!e || !v) return;
      if (e.hPrevu === undefined) e.hPrevu = e.h; // durée de la séance (heures du formateur)
      if (v.presence === 'absent') e.h = 0;
      else if (v.presence === 'partiel' && Number(v.heures) >= 0) e.h = Math.min(e.h || Infinity, Number(v.heures));
    });
  });
  const userParId = new Map(utilisateurs.map(u => [String(u.id), u]));
  const statutFormateur = (id) => {
    if (id === null || id === undefined || id === '') return { statut: 'interne', confirme: true, inconnu: true };
    const r = reg('formateur', id);
    return r.statut ? { statut: r.statut, confirme: true } : { statut: suggestionStatutFormateur(userParId.get(String(id))), confirme: false };
  };
  const groupeParId = new Map(groupes.map(g => [String(g.id), g]));
  const verifs = [];
  const heuresFormateurs = new Map(); // clé de séance → { formateurId, h }
  const formateursUtilises = new Set();
  const modulesUtilises = new Map();

  const dossiers = clients.map(c => {
    const seances = Array.from((parClient.get(String(c.id)) || new Map()).values()).filter(e => e.date);
    const hTot = arrondiH(seances.reduce((s, e) => s + e.h, 0));
    const ex = seances.filter(e => dans(e.date));
    const hEx = arrondiH(ex.reduce((s, e) => s + e.h, 0));
    const sansHoraire = ex.filter(e => !e.h).length;
    const fins = financements.filter(f => String(f.client_id) === String(c.id));
    const prorata = hTot > 0 ? hEx / hTot : 0;
    const produits = fins.map(f => ({
      id: f.id, type: f.type, financeur: f.financeur_nom || '', ligne: ligneCDeFinancement(f, reg('financement', f.id)),
      montantTotal: montantRenseigne(f.montant) ? Number(f.montant) : null,
      montant: montantRenseigne(f.montant) ? Math.round(Number(f.montant) * prorata * 100) / 100 : 0,
    }));
    const produitsTotal = produits.reduce((s, p) => s + p.montant, 0);
    const module = modules.find(m => String(m.id) === String(c.module_id)) || null;
    const rm = module ? reg('module', module.id) : {};
    const objectif = rm.objectif || suggestionObjectif(module);
    const nsf = String(rm.nsf || (objectif === 'e' ? '415' : '') || '').trim();
    const groupe = c.groupe_id ? groupeParId.get(String(c.groupe_id)) : null;
    const formateurId = (groupe && groupe.formateur_id) || c.formateur_id || null;
    const rc = reg('client', c.id);
    const situation = rc.situation || suggestionSituation(fins);
    const g = fins.some(f => f.type === 'organisme_formation');
    return {
      id: c.id, nom: c.nom_complet || c.nom || 'Sans nom', archive: !!c.archive_le, modalite: c.modalite_formation || '',
      pourcentageFormateur: Number(c.pourcentage_formateur) || 0,
      hTot, hEx, sansHoraire, nbSeancesEx: ex.length, seancesEx: ex, groupe,
      fins, produits, produitsTotal,
      module, moduleNom: module ? module.nom : '', objectif, objectifConfirme: !!rm.objectif, niveau: rm.niveau || null, nsf, nsfConfirme: !!rm.nsf,
      formateurId, formateur: statutFormateur(formateurId),
      situation, situationConfirmee: !!rc.situation, g,
    };
  }).filter(d => d.hEx > 0 || d.nbSeancesEx > 0 || d.produitsTotal > 0 || (d.fins.length && d.hTot === 0 && d.fins.some(f => {
    const cree = String(f.created_at || '').slice(0, 10); return dans(cree);
  })));

  // Heures des formateurs (cadre E) : une séance de groupe ne compte qu'une fois.
  dossiers.forEach(d => {
    d.seancesEx.forEach(e => {
      const hSeance = e.hPrevu !== undefined ? e.hPrevu : e.h; // 30/10/2026 : un stagiaire absent ne change pas les heures du formateur
      if (!hSeance) return;
      let cle, fId;
      if (d.groupe) {
        const gs = groupeSeances.find(x => String(x.groupe_id) === String(d.groupe.id) && String(x.date || '').slice(0, 10) === e.date);
        fId = (gs && gs.formateur_id) || d.groupe.formateur_id || d.formateurId;
        cle = `g|${d.groupe.id}|${e.date}|${e.heure_debut || ''}`;
      } else {
        fId = d.formateurId;
        cle = `c|${d.id}|${e.numero}`;
      }
      if (!heuresFormateurs.has(cle)) heuresFormateurs.set(cle, { formateurId: fId, h: hSeance });
    });
  });

  const C = Object.fromEntries(BPF_LIGNES_C.map(([k]) => [k, { montant: 0, dossiers: [] }]));
  const E = { interne: { ids: new Set(), h: 0 }, externe: { ids: new Set(), h: 0 } };
  const F1 = Object.fromEntries(BPF_SITUATIONS.map(([k]) => [k, { n: 0, h: 0, dossiers: [] }]));
  const F2 = { n: 0, h: 0, dossiers: [] };
  const F3 = Object.fromEntries(BPF_OBJECTIFS.map(([k]) => [k, { n: 0, h: 0, dossiers: [] }]));
  const F3a = Object.fromEntries(BPF_NIVEAUX_RNCP.map(([k]) => [k, { n: 0, h: 0 }]));
  const nsfMap = new Map();
  const G = { n: 0, h: 0, dossiers: [] };
  let distance = false;
  let repereHonoraires = 0;

  dossiers.forEach(d => {
    d.produits.forEach(p => {
      if (!p.montant) return;
      C[p.ligne].montant += p.montant;
      if (!C[p.ligne].dossiers.includes(d)) C[p.ligne].dossiers.push(d);
    });
    if (/distan|mixte/i.test(d.modalite)) distance = true;
    if (d.formateur.statut === 'externe') repereHonoraires += d.produitsTotal * d.pourcentageFormateur / 100;
    if (d.hEx <= 0) return;
    if (d.formateurId !== null && d.formateurId !== undefined) formateursUtilises.add(String(d.formateurId));
    if (d.g) { G.n += 1; G.h += d.hEx; G.dossiers.push(d); return; }
    const sit = BPF_SITUATIONS.some(([k]) => k === d.situation) ? d.situation : 'e';
    F1[sit].n += 1; F1[sit].h += d.hEx; F1[sit].dossiers.push(d);
    if (d.formateur.statut === 'externe') { F2.n += 1; F2.h += d.hEx; F2.dossiers.push(d); }
    const obj = BPF_OBJECTIFS.some(([k]) => k === d.objectif) ? d.objectif : 'd';
    F3[obj].n += 1; F3[obj].h += d.hEx; F3[obj].dossiers.push(d);
    if (obj === 'a') { const nv = BPF_NIVEAUX_RNCP.some(([k]) => k === d.niveau) ? d.niveau : null; if (nv) { F3a[nv].n += 1; F3a[nv].h += d.hEx; } }
    const code = d.nsf || '';
    if (!nsfMap.has(code)) nsfMap.set(code, { code, n: 0, h: 0, dossiers: [] });
    const x = nsfMap.get(code); x.n += 1; x.h += d.hEx; x.dossiers.push(d);
    if (d.module) modulesUtilises.set(String(d.module.id), d.module);
  });
  BPF_DISPOSITIFS_OPCO.forEach(([k]) => { C.c2.montant += C[k].montant; C[k].dossiers.forEach(d => { if (!C.c2.dossiers.includes(d)) C.c2.dossiers.push(d); }); });
  C.c2.montant += C.c2e.montant; C.c2e.dossiers.forEach(d => { if (!C.c2.dossiers.includes(d)) C.c2.dossiers.push(d); });
  const totalC = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'c10', 'c11'].reduce((s, k) => s + C[k].montant, 0);

  heuresFormateurs.forEach(({ formateurId, h }) => {
    const st = statutFormateur(formateurId).statut === 'externe' ? 'externe' : 'interne';
    E[st].ids.add(String(formateurId ?? 'sans')); E[st].h += h;
  });
  const codes = Array.from(nsfMap.values()).sort((a, b) => b.h - a.h || b.n - a.n);
  const avecCode = codes.filter(x => x.code);
  const F4 = avecCode.slice(0, 5);
  const autres = { code: '', n: 0, h: 0, dossiers: [] };
  [...avecCode.slice(5), ...codes.filter(x => !x.code)].forEach(x => { autres.n += x.n; autres.h += x.h; autres.dossiers.push(...x.dossiers); });

  // Points à vérifier.
  const sansFin = dossiers.filter(d => d.hEx > 0 && !d.fins.length);
  if (sansFin.length) verifs.push({ cle: 'sans_financement', niveau: 'alerte', titre: `${sansFin.length} dossier${sansFin.length > 1 ? 's' : ''} sans financement`, aide: 'Leurs heures comptent, mais pas de montant au cadre C.', dossiers: sansFin, action: 'client' });
  const sansMontant = dossiers.filter(d => d.fins.some(f => !montantRenseigne(f.montant)));
  if (sansMontant.length) verifs.push({ cle: 'sans_montant', niveau: 'alerte', titre: `${sansMontant.length} financement${sansMontant.length > 1 ? 's' : ''} sans montant`, aide: 'Ajoutez le montant dans le bloc Financement du dossier.', dossiers: sansMontant, action: 'client' });
  const sansSeance = dossiers.filter(d => d.fins.length && d.hTot === 0);
  if (sansSeance.length) verifs.push({ cle: 'sans_seance', niveau: 'alerte', titre: `${sansSeance.length} dossier${sansSeance.length > 1 ? 's' : ''} financé${sansSeance.length > 1 ? 's' : ''} sans séance datée`, aide: 'Sans séance datée, le montant ne peut pas être réparti : il n\'est pas compté.', dossiers: sansSeance, action: 'client' });
  const sansModule = dossiers.filter(d => d.hEx > 0 && !d.g && !d.module);
  if (sansModule.length) verifs.push({ cle: 'sans_module', niveau: 'alerte', titre: `${sansModule.length} dossier${sansModule.length > 1 ? 's' : ''} sans module`, aide: 'Comptés en « autre formation » et sans spécialité.', dossiers: sansModule, action: 'client' });
  const modulesARegler = Array.from(modulesUtilises.values()).filter(m => { const r = reg('module', m.id); return !(r.objectif || suggestionObjectif(m)) || !String(r.nsf || ((r.objectif || suggestionObjectif(m)) === 'e' ? '415' : '')).trim(); });
  if (modulesARegler.length) verifs.push({ cle: 'modules', niveau: 'alerte', titre: `${modulesARegler.length} module${modulesARegler.length > 1 ? 's' : ''} sans objectif ou spécialité`, aide: modulesARegler.map(m => m.nom).join(', '), action: 'reglages' });
  const sansSituation = dossiers.filter(d => d.hEx > 0 && !d.g && !BPF_SITUATIONS.some(([k]) => k === d.situation));
  if (sansSituation.length) verifs.push({ cle: 'situation', niveau: 'alerte', titre: `${sansSituation.length} stagiaire${sansSituation.length > 1 ? 's' : ''} sans situation`, aide: 'Comptés en « autres stagiaires » tant que ce n\'est pas choisi.', dossiers: sansSituation, action: 'reglages' });
  const aConfirmer = dossiers.filter(d => d.hEx > 0 && !d.g && BPF_SITUATIONS.some(([k]) => k === d.situation) && !d.situationConfirmee).length
    + Array.from(formateursUtilises).filter(id => !reg('formateur', id).statut).length
    + Array.from(modulesUtilises.values()).filter(m => !reg('module', m.id).objectif).length;
  if (aConfirmer) verifs.push({ cle: 'a_confirmer', niveau: 'info', titre: `${aConfirmer} valeur${aConfirmer > 1 ? 's' : ''} proposée${aConfirmer > 1 ? 's' : ''} par SkorUp à confirmer`, aide: 'Situation des stagiaires, formateurs internes / externes, objectif des modules.', action: 'reglages' });
  const nbSansHoraire = dossiers.reduce((s, d) => s + d.sansHoraire, 0);
  if (nbSansHoraire) verifs.push({ cle: 'sans_horaire', niveau: 'info', titre: `${nbSansHoraire} séance${nbSansHoraire > 1 ? 's' : ''} datée${nbSansHoraire > 1 ? 's' : ''} sans horaires`, aide: 'Comptées 0 h : ajoutez l\'heure de début et de fin dans le planning du dossier.', dossiers: dossiers.filter(d => d.sansHoraire), action: 'client' });

  const totF1 = BPF_SITUATIONS.reduce((s, [k]) => ({ n: s.n + F1[k].n, h: s.h + F1[k].h }), { n: 0, h: 0 });
  const totF3 = BPF_OBJECTIFS.reduce((s, [k]) => ({ n: s.n + F3[k].n, h: s.h + F3[k].h }), { n: 0, h: 0 });
  const totF4 = [...F4, autres].reduce((s, x) => ({ n: s.n + x.n, h: s.h + x.h }), { n: 0, h: 0 });
  const coherent = totF1.n === totF3.n && totF1.n === totF4.n && Math.abs(totF1.h - totF3.h) < 0.01 && Math.abs(totF1.h - totF4.h) < 0.01;
  return {
    dossiers, verifs, C, totalC, distance, repereHonoraires: Math.round(repereHonoraires * 100) / 100,
    E: { interne: { n: E.interne.ids.size, h: E.interne.h }, externe: { n: E.externe.ids.size, h: E.externe.h } },
    F1, totF1, F2, F3, F3a, totF3, F4, F4autres: autres, totF4, G, coherent,
    formateursUtilises: Array.from(formateursUtilises), modulesUtilises: Array.from(modulesUtilises.values()),
  };
};

const exercicesProposes = () => {
  const a = new Date().getFullYear();
  return [a - 1, a, a - 2, a - 3].map(y => ({ debut: `${y}-01-01`, fin: `${y}-12-31`, libelle: `${y} · du 01/01/${y} au 31/12/${y}` }));
};
const dateFrCourte = (iso) => { const [y, m, d] = String(iso || '').split('-'); return y && m && d ? `${d}/${m}/${y}` : ''; };
const lireParMorceaux = async (table, colonnes, colonneFiltre, ids) => {
  const sortie = [];
  for (let i = 0; i < ids.length; i += 100) {
    const morceau = ids.slice(i, i + 100);
    const { data, error } = await fetchAllRowsWithRetry(() => supabase.from(table).select(colonnes).in(colonneFiltre, morceau), { label: table, retries: 1 });
    if (error) throw error;
    sortie.push(...(data || []));
  }
  return sortie;
};

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// VEILLE LÉGALE ET RÉGLEMENTAIRE (03/11/2026) — offre Pro / Illimité, administrateur.
// Les informations sont collectées et publiées chaque lundi par le serveur (api/prospects.js,
// veilleHebdomadaire, tables veille_infos / veille_suivi / veille_reglages / veille_collectes).
// L'organisme note ce qu'il en a fait (preuve Qualiopi, critère 6) et exporte la preuve en PDF.
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const THEMES_VEILLE_APP = { qualiopi: 'Qualiopi', cpf: 'CPF', financement: 'Financement', controle: 'Contrôle', apprentissage: 'Apprentissage', certification: 'Certification', reconversion: 'Reconversion', reglementation: 'Réglementation' };
const PRIORITES_VEILLE_APP = {
  action: ['Action requise', 'bg-red-50 text-red-700'],
  surveiller: ['À surveiller', 'bg-amber-50 text-amber-800'],
  info: ['Pour information', 'bg-blue-50 text-blue-700'],
};
const ACTIONS_VEILLE_RAPIDES = ['Information lue : pas d\'impact pour nous.', 'Information partagée avec l\'équipe.', 'Procédure ou document mis à jour.', 'Clients ou bénéficiaires informés.'];
const veilleTableAbsente = (err) => !!err && (err.code === '42P01' || err.code === 'PGRST205' || /(does not exist|schema cache|could not find)/i.test(err.message || ''));
const dateSemaineVeille = (iso) => { if (!iso) return ''; const d = new Date(`${String(iso).slice(0, 10)}T12:00:00Z`); return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }); };
const libelleSemaineVeille = (lundi) => {
  if (!lundi) return '';
  const l = new Date(`${lundi}T12:00:00Z`);
  const debut = new Date(l.getTime() - 7 * 86400000), fin = new Date(l.getTime() - 86400000);
  const f = (d, annee) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(annee ? { year: 'numeric' } : {}), timeZone: 'UTC' });
  return `${f(debut)} – ${f(fin, true)}`;
};
const appelVeilleApi = async (action, body = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(`/api/prospects?action=${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Erreur ${r.status}`);
  return d;
};

// Nombre d'informations de la dernière semaine pas encore traitées (badge du menu).
const compterVeilleNonTraitees = async (orgId) => {
  try {
    const { data: infos, error } = await supabase.from('veille_infos').select('id, semaine').lte('publie_le', new Date().toISOString()).order('semaine', { ascending: false }).limit(40);
    if (error || !infos || !infos.length) return 0;
    const derniere = infos[0].semaine;
    const ids = infos.filter(i => i.semaine === derniere).map(i => i.id);
    const { data: suivi } = await supabase.from('veille_suivi').select('info_id').eq('organisation_id', orgId).in('info_id', ids);
    const faits = new Set((suivi || []).map(s => String(s.info_id)));
    return ids.filter(id => !faits.has(String(id))).length;
  } catch (_) { return 0; }
};

// PDF de preuve : toutes les informations reçues sur la période, avec le traitement noté.
const genererPreuveVeillePdf = async ({ orgNom, infos, suivi, debut, fin }) => {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const gras = await pdf.embedFont(StandardFonts.HelveticaBold);
  const sur = (t, f = font) => Array.from(String(t ?? '').replace(/[  \t\r\n]/g, ' ')).map(ch => { try { f.encodeText(ch); return ch; } catch (_) { return '?'; } }).join('');
  const lignes = (t, f, taille, largeur) => {
    const mots = sur(t, f).split(' '); const out = []; let l = '';
    mots.forEach(m => { const e = l ? `${l} ${m}` : m; if (f.widthOfTextAtSize(e, taille) > largeur && l) { out.push(l); l = m; } else l = e; });
    if (l) out.push(l);
    return out.length ? out : [''];
  };
  const W = 841.89, H = 595.28, M = 32;
  const cols = [['Semaine', 70], ['Information et source', 300], ['Priorité', 70], ['Traitée le', 70], ['Action menée', 210], ['Par', 58]];
  let page, y;
  const entete = () => {
    page = pdf.addPage([W, H]); y = H - M;
    page.drawText(sur(orgNom || 'Organisme de formation', gras), { x: M, y, size: 10, font: gras, color: rgb(0.43, 0.16, 0.85) }); y -= 20;
    page.drawText(sur('Veille légale et réglementaire — preuve de suivi (Qualiopi, critère 6)', gras), { x: M, y, size: 15, font: gras }); y -= 16;
    page.drawText(sur(`Période : ${debut.split('-').reverse().join('/')} au ${fin.split('-').reverse().join('/')} · éditée le ${new Date().toLocaleDateString('fr-FR')} · ${infos.length} information(s), ${infos.filter(i => suivi[i.id]).length} traitée(s)`), { x: M, y, size: 9, font, color: rgb(0.35, 0.35, 0.4) }); y -= 18;
    let x = M;
    cols.forEach(([t, w]) => { page.drawRectangle({ x, y: y - 16, width: w, height: 16, color: rgb(0.95, 0.93, 0.99) }); page.drawText(sur(t, gras), { x: x + 4, y: y - 11, size: 8, font: gras }); x += w; });
    y -= 16;
  };
  entete();
  infos.forEach(i => {
    const s = suivi[i.id];
    const cellules = [
      [dateSemaineVeille(i.semaine)],
      [...lignes(i.titre, gras, 8, cols[1][1] - 8), ...lignes(`${i.source_nom} · ${i.url}`, font, 7, cols[1][1] - 8)],
      [(PRIORITES_VEILLE_APP[i.priorite] || ['', ''])[0]],
      [s ? new Date(s.traitee_le).toLocaleDateString('fr-FR') : 'Non traitée'],
      lignes(s ? (s.action_menee || '—') : '', font, 8, cols[4][1] - 8),
      lignes(s ? (s.traite_par || '') : '', font, 7, cols[5][1] - 8),
    ];
    const h = Math.max(...cellules.map(c => c.length)) * 10 + 8;
    if (y - h < M) entete();
    let x = M;
    cols.forEach(([, w], j) => {
      page.drawRectangle({ x, y: y - h, width: w, height: h, borderColor: rgb(0.85, 0.85, 0.88), borderWidth: 0.5 });
      cellules[j].forEach((t, k) => page.drawText(t, { x: x + 4, y: y - 11 - k * 10, size: j === 1 && k === 0 ? 8 : (j === 1 ? 7 : 8), font: j === 1 && k < lignes(i.titre, gras, 8, cols[1][1] - 8).length ? gras : font, color: j === 3 && !s ? rgb(0.7, 0.2, 0.1) : rgb(0.1, 0.1, 0.12) }));
      x += w;
    });
    y -= h;
  });
  if (!infos.length) page.drawText(sur('Aucune information sur cette période.'), { x: M, y: y - 20, size: 10, font });
  return new Blob([await pdf.save()], { type: 'application/pdf' });
};

const VeilleView = ({ currentOrgId, orgSettings, nomUtilisateur, currentUserId, onNonLus }) => {
  const [etat, setEtat] = React.useState('chargement'); // chargement | ok | absent | erreur
  const [infos, setInfos] = React.useState([]);
  const [suivi, setSuivi] = React.useState({});
  const [semaine, setSemaine] = React.useState(null);
  const [filtre, setFiltre] = React.useState('tout');
  const [resumeLundi, setResumeLundi] = React.useState(true);
  const [edition, setEdition] = React.useState(null); // { info, texte }
  const [export_, setExport] = React.useState(null); // { debut, fin }
  const [proprio, setProprio] = React.useState(null);
  const [essai, setEssai] = React.useState(null);
  const [occupe, setOccupe] = React.useState('');
  const [rangement, setRangement] = React.useState(null); // audit 04/11/2026

  const charger = React.useCallback(async () => {
    if (!currentOrgId) return;
    const [ri, rs, rr] = await Promise.all([
      supabase.from('veille_infos').select('*').lte('publie_le', new Date().toISOString()).order('semaine', { ascending: false }).order('date_article', { ascending: false }).limit(500),
      supabase.from('veille_suivi').select('*').eq('organisation_id', currentOrgId),
      supabase.from('veille_reglages').select('*').eq('organisation_id', currentOrgId).maybeSingle(),
    ]);
    if (ri.error || rs.error) { setEtat(veilleTableAbsente(ri.error || rs.error) ? 'absent' : 'erreur'); return; }
    setInfos(ri.data || []);
    setSuivi(Object.fromEntries((rs.data || []).map(s => [s.info_id, s])));
    setResumeLundi(!(rr.data && rr.data.resume_lundi === false));
    setSemaine(prev => prev || (ri.data && ri.data[0] ? ri.data[0].semaine : null));
    setEtat('ok');
  }, [currentOrgId]);
  React.useEffect(() => { charger(); }, [charger]);
  const chargerProprio = React.useCallback(async () => {
    try { const d = await appelVeilleApi('veille_statut'); setProprio(d.proprietaire ? d : null); } catch (_) { setProprio(null); }
  }, []);
  React.useEffect(() => { chargerProprio(); }, [chargerProprio]);

  const semaines = Array.from(new Set(infos.map(i => i.semaine)));
  const deLaSemaine = infos.filter(i => i.semaine === semaine);
  const themes = Array.from(new Set(deLaSemaine.map(i => i.theme)));
  const affichees = deLaSemaine.filter(i => filtre === 'tout' || i.theme === filtre)
    .sort((a, b) => ['action', 'surveiller', 'info'].indexOf(a.priorite) - ['action', 'surveiller', 'info'].indexOf(b.priorite));
  const nb = (p) => deLaSemaine.filter(i => i.priorite === p).length;
  const nbTraitees = deLaSemaine.filter(i => suivi[i.id]).length;
  React.useEffect(() => {
    if (etat !== 'ok' || !onNonLus) return;
    const derniere = semaines[0];
    onNonLus(infos.filter(i => i.semaine === derniere && !suivi[i.id]).length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etat, infos, suivi]);

  const enregistrer = async () => {
    const { info, texte } = edition;
    setOccupe('enregistrement');
    const ligne = { organisation_id: currentOrgId, info_id: info.id, action_menee: texte.trim() || null, traite_par: nomUtilisateur || null, traite_par_id: currentUserId ? String(currentUserId) : null, updated_at: new Date().toISOString(), ...(suivi[info.id] ? {} : { traitee_le: new Date().toISOString() }) };
    const { data, error } = await supabase.from('veille_suivi').upsert([ligne], { onConflict: 'organisation_id,info_id' }).select().maybeSingle();
    setOccupe('');
    if (error) { toast.error('Enregistrement impossible : ' + error.message); return; }
    setSuivi(prev => ({ ...prev, [info.id]: data || { ...ligne, traitee_le: prev[info.id]?.traitee_le || ligne.updated_at } }));
    setEdition(null);
    toast.success('Action notée');
  };
  const annulerTraitement = async (info) => {
    const { error } = await supabase.from('veille_suivi').delete().eq('organisation_id', currentOrgId).eq('info_id', info.id);
    if (error) { toast.error('Impossible : ' + error.message); return; }
    setSuivi(prev => { const n = { ...prev }; delete n[info.id]; return n; });
    setEdition(null);
  };
  const changerResume = async (v) => {
    setResumeLundi(v);
    const { error } = await supabase.from('veille_reglages').upsert([{ organisation_id: currentOrgId, resume_lundi: v, updated_at: new Date().toISOString() }], { onConflict: 'organisation_id' });
    if (error) { setResumeLundi(!v); toast.error('Réglage non enregistré : ' + error.message); }
    else toast.success(v ? 'Vous recevrez le résumé chaque lundi' : 'Résumé du lundi désactivé');
  };
  const exporter = async () => {
    setOccupe('export');
    try {
      const periode = infos.filter(i => i.semaine >= export_.debut && i.semaine <= export_.fin);
      const blob = await genererPreuveVeillePdf({ orgNom: orgSettings?.nom, infos: periode, suivi, debut: export_.debut, fin: export_.fin });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Preuve_veille_${export_.debut}_${export_.fin}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setExport(null);
    } catch (e) { toast.error('Le PDF n\'a pas pu être créé : ' + (e.message || e)); }
    setOccupe('');
  };
  const lancer = async (modeEssai) => {
    setOccupe(modeEssai ? 'essai' : 'lancer');
    try {
      const d = await appelVeilleApi('veille_lancer', { essai: modeEssai });
      if (modeEssai) setEssai(d);
      else { toast.success(`Collecte terminée : ${d.publiees || 0} nouvelle(s) information(s) publiée(s)${d.aVenir ? `, ${d.aVenir.nb} prête(s) pour lundi prochain` : ''}.`); setEssai(null); await charger(); }
      await chargerProprio();
    } catch (e) { toast.error(e.message || String(e)); }
    setOccupe('');
  };
  const rangerConventions = async () => {
    setOccupe('ranger');
    try {
      const d = await appelVeilleApi('ranger_conventions');
      setRangement(d);
      if (d.erreurs && d.erreurs.length) toast.error(`${d.ranges} convention(s) rangée(s), ${d.erreurs.length} à revoir.`);
      else toast.success(d.trouves ? `${d.ranges} convention(s) signée(s) rangée(s) dans le dossier de leur organisme.` : 'Rien à ranger : toutes les conventions sont déjà à leur place.');
    } catch (e) { toast.error(e.message || String(e)); }
    setOccupe('');
  };
  const masquer = async (info, masquee) => {
    try {
      await appelVeilleApi('veille_masquer', { infoId: info.id, masquee });
      toast.success(masquee ? 'Information retirée de SkorUp et du site' : 'Information remise');
      await Promise.all([charger(), chargerProprio()]);
    } catch (e) { toast.error(e.message || String(e)); }
  };

  if (etat === 'chargement') return <div className="p-10 text-center text-gray-500">Chargement de la veille…</div>;
  if (etat === 'absent') return <div className="bg-white p-8 rounded-3xl border border-gray-100 max-w-2xl mx-auto text-center text-gray-600">La veille n'est pas encore activée : lancez le SQL « veille » dans Supabase.</div>;
  if (etat === 'erreur') return <div className="bg-white p-8 rounded-3xl border border-red-100 max-w-2xl mx-auto text-center text-red-700">La veille n'a pas pu être chargée. Réessayez dans un instant.</div>;
  const derniereCollecte = proprio && (proprio.collectes || []).find(c => c.termine_le);
  const prochaineVeille = proprio && (proprio.collectes || []).find(c => !c.termine_le);
  const ordreAVenir = { action: 0, surveiller: 1, info: 2 };
  const aVenirTriees = ((proprio && proprio.aVenir) || []).slice().sort((x, y) => (ordreAVenir[x.priorite] - ordreAVenir[y.priorite]) || String(y.date_article).localeCompare(String(x.date_article)));
  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-widest text-violet-600">Veille légale et réglementaire</p>
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 mt-1">{semaine ? 'Ce qui a changé dans la formation' : 'Votre veille arrive lundi'}</h1>
          <p className="text-sm text-gray-500 mt-1">{semaine ? `Publiée le lundi ${dateSemaineVeille(semaine)} · semaine du ${libelleSemaineVeille(semaine)} · ${deLaSemaine.length} information${deLaSemaine.length > 1 ? 's' : ''}` : 'Chaque lundi matin, les nouveautés de la semaine passée (Qualiopi, CPF, financement, réglementation) arrivent ici avec leur source.'}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {semaines.length > 0 && (
            <div>
              <label htmlFor="veille-semaine" className="block text-[11px] font-bold text-gray-500 mb-1">Semaine</label>
              <select id="veille-semaine" value={semaine || ''} onChange={e => { setSemaine(e.target.value); setFiltre('tout'); }} className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm">
                {semaines.map(s => <option key={s} value={s}>{libelleSemaineVeille(s)}</option>)}
              </select>
            </div>
          )}
          <button type="button" onClick={() => { const f = new Date(); const d = new Date(f.getTime() - 365 * 86400000); setExport({ debut: jourLocalISO(d), fin: jourLocalISO(f) }); }} disabled={!infos.length}
            className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-40 inline-flex items-center gap-2"><Download size={16} /> Exporter la preuve (PDF)</button>
        </div>
      </div>

      {semaine && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[[nb('action'), 'action requise', 'text-red-700'], [nb('surveiller'), 'à surveiller', 'text-amber-700'], [nb('info'), 'pour information', 'text-blue-700'], [`${nbTraitees} / ${deLaSemaine.length}`, 'traitées', 'text-gray-900']].map(([v, l, c]) => (
            <div key={l} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm"><p className={`text-2xl font-black tabular-nums ${c}`}>{v}</p><p className="text-xs text-gray-500 mt-0.5">{l}</p></div>
          ))}
        </div>
      )}

      {themes.length > 1 && (
        <div role="group" aria-label="Filtrer par thème" className="flex flex-wrap gap-2">
          {['tout', ...themes].map(t => (
            <button key={t} type="button" aria-pressed={filtre === t} onClick={() => setFiltre(t)}
              className={`px-3.5 py-2 rounded-xl text-sm font-bold border ${filtre === t ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300'}`}>{t === 'tout' ? 'Tout' : THEMES_VEILLE_APP[t] || t}</button>
          ))}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0 space-y-4 w-full">
          {!semaine && <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">Aucune information publiée pour l'instant. La première veille arrivera lundi matin.</div>}
          {affichees.map(i => {
            const s = suivi[i.id];
            const [lp, cp] = PRIORITES_VEILLE_APP[i.priorite] || PRIORITES_VEILLE_APP.info;
            return (
              <article key={i.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 md:p-6 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[11px] font-black px-2 py-1 rounded-lg ${cp}`}>{lp}</span>
                  <span className="text-[11px] font-black px-2 py-1 rounded-lg bg-violet-50 text-violet-700">{THEMES_VEILLE_APP[i.theme] || i.theme}</span>
                  {i.date_article && <span className="text-xs text-gray-500">{new Date(i.date_article).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</span>}
                  {s && <span className="ml-auto text-xs font-black text-emerald-700">✓ Traitée le {new Date(s.traitee_le).toLocaleDateString('fr-FR')}</span>}
                </div>
                <h2 className="text-base md:text-lg font-black text-gray-900 leading-snug break-words">{i.titre}</h2>
                {i.resume && <p className="text-sm text-gray-700 leading-relaxed break-words">{i.resume}</p>}
                {s && <p className="text-sm text-gray-700 bg-emerald-50 rounded-xl px-3 py-2 break-words"><b>Action menée :</b> {s.action_menee || '—'}{s.traite_par ? <span className="text-gray-500"> · {s.traite_par}</span> : null}</p>}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <a href={i.url} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-violet-700 hover:underline inline-flex items-center gap-1.5 break-all"><ExternalLink size={14} className="shrink-0" /> Source : {i.source_nom}</a>
                  <div className="flex flex-wrap gap-2">
                    {proprio && <button type="button" onClick={() => masquer(i, true)} className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-500 hover:text-red-600 hover:border-red-200">Retirer (propriétaire)</button>}
                    <button type="button" onClick={() => setEdition({ info: i, texte: s?.action_menee || '' })}
                      className={s ? 'px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 hover:border-violet-300' : 'px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700'}>{s ? 'Modifier' : 'Traitée : noter l\'action menée'}</button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <aside className="w-full lg:w-80 shrink-0 space-y-4">
          <div className="bg-gray-900 text-white rounded-3xl p-5 space-y-3">
            <p className="text-[11px] font-black uppercase tracking-widest text-violet-300">Preuve Qualiopi</p>
            <p className="font-black leading-snug">Votre veille est tracée pour l'audit</p>
            <div className="flex justify-between text-sm"><span>Traitées cette semaine</span><b>{nbTraitees}/{deLaSemaine.length}</b></div>
            <div className="h-2 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-violet-400 rounded-full" style={{ width: `${deLaSemaine.length ? Math.round((nbTraitees / deLaSemaine.length) * 100) : 0}%` }} /></div>
            <p className="text-xs text-violet-100 leading-relaxed">Le PDF liste chaque information reçue, sa source, la date de traitement, l'action menée et qui l'a notée.</p>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 p-5 space-y-2">
            <label className="flex items-center justify-between gap-3 font-black text-gray-900 cursor-pointer">Résumé du lundi par e-mail
              <input type="checkbox" checked={resumeLundi} onChange={e => changerResume(e.target.checked)} className="w-5 h-5 accent-violet-600" />
            </label>
            <p className="text-xs text-gray-500 leading-relaxed">Chaque lundi matin, les nouvelles informations de la semaine, triées par priorité, envoyées aux administrateurs de l'organisme.</p>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 p-5 text-xs text-gray-500 leading-relaxed">
            Informations collectées automatiquement chaque jour et publiées chaque lundi, auprès de sources officielles et spécialisées (service-public.fr, France compétences, Centre Inffo…). Le résumé reprend le début de l'article : le texte officiel fait foi, vérifiez-le avant d'agir.
          </div>
          {proprio && (
            <div className="bg-white rounded-3xl border-2 border-violet-200 p-5 space-y-3">
              <p className="font-black text-gray-900">Propriétaire de SkorUp</p>
              {derniereCollecte ? (
                <div className="text-xs text-gray-600 space-y-1">
                  <p>Dernière veille publiée : {libelleSemaineVeille(derniereCollecte.semaine)} · {derniereCollecte.nb_publies} information(s) · {derniereCollecte.nb_resumes || 0} résumé(s) envoyé(s)</p>
                  <ul className="space-y-0.5">
                    {(derniereCollecte.rapport || []).map(r => <li key={r.cle} className={r.etat === 'erreur' ? 'text-red-700' : r.etat === 'vide' ? 'text-amber-700' : ''}>{r.etat === 'erreur' ? '✗' : r.etat === 'vide' ? '⚠' : '✓'} {r.nom} : {r.etat === 'erreur' ? r.erreur : r.etat === 'vide' ? 'aucun article trouvé (adresse à vérifier)' : `${r.articles} lus, ${r.retenus} retenus`}</li>)}
                  </ul>
                </div>
              ) : <p className="text-xs text-gray-500">Aucune veille publiée pour l'instant.</p>}
              {prochaineVeille && (
                <div className="text-xs text-gray-600 space-y-1 bg-violet-50 rounded-xl p-3">
                  <p className="font-bold text-violet-800">Prochaine veille (lundi {dateSemaineVeille(prochaineVeille.semaine)}) : {aVenirTriees.length} information(s) prête(s)</p>
                  <p className="text-gray-500">Les sources sont lues chaque jour à 8 h ; ces informations restent invisibles jusqu'au lundi.</p>
                  {aVenirTriees.map(m => <p key={m.id} className="flex items-start justify-between gap-2"><span className="break-words">{m.titre} <span className="text-gray-400">({m.source_nom})</span></span><button type="button" onClick={() => masquer(m, true)} className="text-red-600 font-bold shrink-0">Retirer</button></p>)}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => lancer(true)} disabled={!!occupe} className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 disabled:opacity-40">{occupe === 'essai' ? 'Essai…' : 'Tester les sources (sans publier)'}</button>
                <button type="button" onClick={() => lancer(false)} disabled={!!occupe} className="px-3 py-2 rounded-xl bg-violet-600 text-white text-xs font-bold disabled:opacity-40">{occupe === 'lancer' ? 'Collecte…' : 'Lancer la collecte maintenant'}</button>
              </div>
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <p className="text-xs font-bold text-gray-700">Sécurité des fichiers</p>
                <p className="text-xs text-gray-500">Range dans le dossier de leur organisme les conventions signées enregistrées avant le 04/11/2026. Rien n'est supprimé.</p>
                <button type="button" onClick={rangerConventions} disabled={!!occupe} className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 disabled:opacity-40">{occupe === 'ranger' ? 'Rangement…' : 'Ranger les conventions signées'}</button>
                {rangement && (
                  <div className="text-xs text-gray-600 space-y-0.5">
                    <p>{rangement.trouves ? `${rangement.ranges} / ${rangement.trouves} fichier(s) rangé(s), dont ${rangement.references} convention(s) mise(s) à jour.` : 'Rien à ranger.'}</p>
                    {(rangement.erreurs || []).map((e, i) => <p key={i} className="text-red-700 break-words">{e}</p>)}
                  </div>
                )}
              </div>
              {essai && (
                <div className="text-xs text-gray-700 bg-gray-50 rounded-xl p-3 space-y-1">
                  <p className="font-bold">Essai : {essai.infos.length} information(s) pour la semaine passée ({essai.nbLus} articles lus).</p>
                  {essai.aVenir && <p>Semaine en cours, pour le lundi {dateSemaineVeille(essai.aVenir.semaine)} : {essai.aVenir.nb} information(s) déjà trouvée(s).</p>}
                  <ul className="list-disc pl-4 space-y-0.5">{essai.infos.map(x => <li key={x.url} className="break-words">{x.titre} <span className="text-gray-500">({x.source_nom})</span></li>)}</ul>
                  <ul className="space-y-0.5 pt-1">{(essai.rapport || []).map(r => <li key={r.cle} className={r.etat === 'erreur' ? 'text-red-700' : r.etat === 'vide' ? 'text-amber-700' : ''}>{r.etat === 'erreur' ? '✗' : r.etat === 'vide' ? '⚠' : '✓'} {r.nom} : {r.etat === 'erreur' ? r.erreur : r.etat === 'vide' ? 'aucun article trouvé (adresse à vérifier)' : `${r.articles} lus, ${r.retenus} retenus`}</li>)}</ul>
                </div>
              )}
              {(proprio.masquees || []).length > 0 && (
                <div className="text-xs text-gray-600 space-y-1">
                  <p className="font-bold">Informations retirées</p>
                  {proprio.masquees.map(m => <p key={m.id} className="flex items-start justify-between gap-2"><span className="break-words">{m.titre}</span><button type="button" onClick={() => masquer(m, false)} className="text-violet-700 font-bold shrink-0">Remettre</button></p>)}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {edition && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-3 sm:p-4" onClick={() => !occupe && setEdition(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="veille-action-titre" className="bg-white rounded-3xl w-full max-w-lg shadow-2xl p-5 sm:p-6 space-y-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 id="veille-action-titre" className="text-lg font-black text-gray-900">Qu'avez-vous fait de cette information ?</h2>
            <p className="text-sm text-gray-600 break-words">{edition.info.titre}</p>
            <div className="flex flex-wrap gap-2">
              {ACTIONS_VEILLE_RAPIDES.map(a => <button key={a} type="button" onClick={() => setEdition(p => ({ ...p, texte: a }))} className="px-3 py-1.5 rounded-lg bg-violet-50 text-violet-800 text-xs font-bold text-left">{a}</button>)}
            </div>
            <div>
              <label htmlFor="veille-action" className="block text-xs font-bold text-gray-600 mb-1">Action menée</label>
              <textarea id="veille-action" rows={4} maxLength={2000} value={edition.texte} onChange={e => setEdition(p => ({ ...p, texte: e.target.value }))} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm resize-y" placeholder="Ex : modalités vérifiées auprès de l'OPCO, mention ajoutée dans nos devis." />
            </div>
            <div className="flex flex-wrap justify-between gap-2">
              {suivi[edition.info.id] ? <button type="button" onClick={() => annulerTraitement(edition.info)} className="px-4 py-2.5 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50">Marquer comme non traitée</button> : <span />}
              <div className="flex gap-2">
                <button type="button" onClick={() => setEdition(null)} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Annuler</button>
                <button type="button" onClick={enregistrer} disabled={!!occupe} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40">{occupe === 'enregistrement' ? 'Enregistrement…' : 'Enregistrer'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {export_ && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-3 sm:p-4" onClick={() => !occupe && setExport(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="veille-export-titre" className="bg-white rounded-3xl w-full max-w-md shadow-2xl p-5 sm:p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 id="veille-export-titre" className="text-lg font-black text-gray-900">Preuve de veille pour l'audit</h2>
            <p className="text-sm text-gray-600">Choisissez la période (semaines de publication).</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label htmlFor="veille-du" className="block text-xs font-bold text-gray-600 mb-1">Du</label><input id="veille-du" type="date" value={export_.debut} onChange={e => setExport(p => ({ ...p, debut: e.target.value }))} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" /></div>
              <div><label htmlFor="veille-au" className="block text-xs font-bold text-gray-600 mb-1">Au</label><input id="veille-au" type="date" value={export_.fin} onChange={e => setExport(p => ({ ...p, fin: e.target.value }))} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setExport(null)} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Annuler</button>
              <button type="button" onClick={exporter} disabled={!!occupe || !export_.debut || !export_.fin || export_.debut > export_.fin} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40 inline-flex items-center gap-2"><Download size={15} /> {occupe === 'export' ? 'Création…' : 'Télécharger le PDF'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BpfView = ({ currentOrgId, orgSettings, onOuvrirClient }) => {
  const exos = React.useMemo(() => exercicesProposes(), []);
  const [exo, setExo] = React.useState(exos[0]);
  const [perso, setPerso] = React.useState(false);
  const [etat, setEtat] = React.useState('chargement'); // chargement | ok | absent | erreur
  const [donnees, setDonnees] = React.useState(null);
  const [reglages, setReglages] = React.useState({});
  const [saisies, setSaisies] = React.useState({}); // clé "debut|fin" → valeurs
  const [brouillon, setBrouillon] = React.useState({});
  const [modifie, setModifie] = React.useState(false);
  const [onglet, setOnglet] = React.useState('bilan');
  const [detail, setDetail] = React.useState(null); // { titre, dossiers, montants }
  const [enregistrement, setEnregistrement] = React.useState(false);

  const charger = React.useCallback(async () => {
    if (!currentOrgId) return;
    setEtat('chargement');
    try {
      const [rg, ex] = await Promise.all([
        fetchAllRowsWithRetry(() => supabase.from('bpf_reglages').select('cible, cible_id, valeurs').eq('organisation_id', currentOrgId), { label: 'réglages BPF', retries: 0 }),
        supabase.from('bpf_exercices').select('*').eq('organisation_id', currentOrgId),
      ]);
      if (rg.error || ex.error) {
        const err = rg.error || ex.error;
        setEtat(err.code === '42P01' || err.code === 'PGRST205' || /(does not exist|schema cache|could not find)/i.test(err.message || '') ? 'absent' : 'erreur');
        return;
      }
      const map = {};
      (rg.data || []).forEach(r => { map[`${r.cible}|${r.cible_id}`] = r.valeurs || {}; });
      const sx = {};
      (ex.data || []).forEach(r => { sx[`${r.debut}|${r.fin}`] = { ...(r.valeurs || {}), _maj: r.updated_at }; });
      const cl = await fetchAllRowsWithRetry(() => supabase.from('clients').select('*').eq('organisation_id', currentOrgId), { label: 'clients (BPF)' });
      if (cl.error) throw cl.error;
      const ids = (cl.data || []).map(c => c.id);
      const [se, gr, gs, fi, ut, mo] = await Promise.all([
        lireParMorceaux('sessions', 'client_id, numero_seance, date, heure_debut, heure_fin', 'client_id', ids),
        supabase.from('groupes').select('*').eq('organisation_id', currentOrgId),
        supabase.from('groupe_seances').select('*').eq('organisation_id', currentOrgId),
        fetchAllRowsWithRetry(() => supabase.from('financements').select('*').eq('organisation_id', currentOrgId), { label: 'financements (BPF)', retries: 0 }),
        supabase.from('utilisateurs').select('*').eq('organisation_id', currentOrgId),
        supabase.from('modules').select('id, nom').eq('organisation_id', currentOrgId),
      ]);
      setReglages(map);
      setSaisies(sx);
      setDonnees({
        clients: cl.data || [], sessions: se, groupes: gr.error ? [] : (gr.data || []), groupeSeances: gs.error ? [] : (gs.data || []),
        financements: fi.error ? [] : (fi.data || []), utilisateurs: ut.data || [], modules: mo.data || [],
      });
      setEtat('ok');
    } catch (e) {
      console.error('[BPF]', e);
      setEtat('erreur');
    }
  }, [currentOrgId]);
  React.useEffect(() => { charger(); }, [charger]);

  // Saisies de l'exercice : celles enregistrées, sinon les plus récentes (forme juridique, NAF, dirigeant).
  React.useEffect(() => {
    const cle = `${exo.debut}|${exo.fin}`;
    const propre = saisies[cle];
    if (propre) { setBrouillon(propre); setModifie(false); return; }
    const derniere = Object.values(saisies).sort((a, b) => String(b._maj || '').localeCompare(String(a._maj || '')))[0] || {};
    setBrouillon({ forme_juridique: derniere.forme_juridique || '', naf: derniere.naf || '', dirigeant_nom: derniere.dirigeant_nom || '', dirigeant_qualite: derniere.dirigeant_qualite || '', part_ca: derniere.part_ca ?? '' });
    setModifie(false);
  }, [exo, saisies]);

  const bpf = React.useMemo(() => (donnees ? calculerBpf({ ...donnees, debut: exo.debut, fin: exo.fin, reglages }) : null), [donnees, exo, reglages]);

  const enregistrerReglage = async (cible, cibleId, valeurs) => {
    const cle = `${cible}|${cibleId}`;
    const avant = reglages[cle];
    const nouveau = { ...(avant || {}), ...valeurs };
    setReglages(prev => ({ ...prev, [cle]: nouveau }));
    const { error } = await supabase.from('bpf_reglages').upsert([{ organisation_id: currentOrgId, cible, cible_id: String(cibleId), valeurs: nouveau, updated_at: new Date().toISOString() }], { onConflict: 'organisation_id,cible,cible_id' });
    if (error) {
      setReglages(prev => { const s = { ...prev }; if (avant) s[cle] = avant; else delete s[cle]; return s; });
      toast.error("Réglage non enregistré : " + error.message);
    }
  };
  const champ = (k, v) => { setBrouillon(prev => ({ ...prev, [k]: v })); setModifie(true); };
  const enregistrerSaisies = async () => {
    setEnregistrement(true);
    const { _maj, ...valeurs } = brouillon;
    const { error } = await supabase.from('bpf_exercices').upsert([{ organisation_id: currentOrgId, debut: exo.debut, fin: exo.fin, valeurs, updated_at: new Date().toISOString() }], { onConflict: 'organisation_id,debut,fin' });
    setEnregistrement(false);
    if (error) { toast.error('Enregistrement impossible : ' + error.message); return; }
    setSaisies(prev => ({ ...prev, [`${exo.debut}|${exo.fin}`]: { ...valeurs, _maj: new Date().toISOString() } }));
    setModifie(false);
    toast.success('Saisies du BPF enregistrées.');
  };

  const nomFormateur = (id) => { const u = donnees && donnees.utilisateurs.find(x => String(x.id) === String(id)); return u ? (u.nom || u.email || `Formateur ${id}`) : 'Sans formateur'; };
  const ouvrirDetail = (titre, dossiers, avecMontants = null) => setDetail({ titre, dossiers, avecMontants });

  const exporterCsv = () => {
    if (!bpf) return;
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const nb = (n) => String(Math.round((Number(n) || 0) * 100) / 100).replace('.', ',');
    const lignes = [['Dossier', 'Archivé', 'Module', 'Objectif (F-3)', 'Spécialité (F-4)', 'Formateur', 'Interne / externe', 'Situation (F-1)', "Heures dans l'exercice", 'Heures au total', 'Confié par un autre OF (G)', 'Financements (montant total)', "Montant compté dans l'exercice"].map(q).join(';')];
    bpf.dossiers.forEach(d => {
      lignes.push([
        d.nom, d.archive ? 'oui' : '', d.moduleNom, libelleDe(BPF_OBJECTIFS, d.objectif), d.nsf ? `${d.nsf} ${libelleNsf(d.nsf)}`.trim() : '',
        d.formateurId ? nomFormateur(d.formateurId) : '', d.formateur.statut, libelleDe(BPF_SITUATIONS, d.situation),
        nb(d.hEx), nb(d.hTot), d.g ? 'oui' : '',
        d.produits.map(p => `${libelleFinancement(p.type)}${p.financeur ? ' ' + p.financeur : ''} (${p.montantTotal === null ? 'sans montant' : nb(p.montantTotal)}) → ligne ${(BPF_LIGNES_C.find(([k]) => k === p.ligne) || [])[1]}`).join(' | '),
        nb(d.produitsTotal),
      ].map(q).join(';'));
    });
    const blob = new Blob(['﻿' + lignes.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `BPF ${exo.debut.slice(0, 4)} - détail par dossier.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const exporterPdf = async () => {
    if (!bpf) return;
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const mx = 46, larg = 595.28 - 92, bas = 841.89 - 46;
    let y = 54;
    const place = (h) => { if (y + h > bas) { pdf.addPage(); y = 54; } };
    const titre = (t) => { place(40); y += 10; pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12); pdf.setTextColor(76, 29, 149); pdf.text(t, mx, y); y += 16; pdf.setTextColor(30); };
    const ligne = (g, d1 = '', d2 = '', gras = false) => {
      pdf.setFont('helvetica', gras ? 'bold' : 'normal'); pdf.setFontSize(9.5);
      const l = pdf.splitTextToSize(String(g), larg - 170); place(l.length * 12 + 4);
      pdf.text(l, mx, y); if (d1 !== '') pdf.text(String(d1), mx + larg - 90, y, { align: 'right' }); if (d2 !== '') pdf.text(String(d2), mx + larg, y, { align: 'right' });
      y += l.length * 12 + 3;
    };
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16); pdf.text('Bilan pédagogique et financier', mx, y); y += 20;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(90);
    pdf.text(`${orgSettings?.nom || ''} — exercice du ${dateFrCourte(exo.debut)} au ${dateFrCourte(exo.fin)} — préparé avec SkorUp le ${new Date().toLocaleDateString('fr-FR')}`, mx, y); y += 12;
    pdf.text('Document d\'aide : à vérifier puis à recopier sur Mon Activité Formation.', mx, y); y += 6; pdf.setTextColor(30);
    titre('A. Identification');
    ligne('N° de déclaration', '', orgSettings?.nda || '—'); ligne('SIRET', '', orgSettings?.siret || '—'); ligne('Forme juridique', '', brouillon.forme_juridique || '—'); ligne('Code NAF', '', brouillon.naf || '—');
    titre('B. Informations générales');
    ligne('Exercice comptable', '', `du ${dateFrCourte(exo.debut)} au ${dateFrCourte(exo.fin)}`); ligne('Formation en tout ou partie à distance', '', bpf.distance ? 'Oui' : 'Non');
    titre('C. Origine des produits (HT)');
    BPF_LIGNES_C.forEach(([k, n, l]) => ligne(`${n}  ${l}`, '', fmtEur(bpf.C[k].montant), !/^c2./.test(k)));
    ligne('Total des produits réalisés au titre de la formation professionnelle', '', fmtEur(bpf.totalC), true);
    ligne('Part du chiffre d\'affaires global (%)', '', brouillon.part_ca !== '' && brouillon.part_ca !== undefined ? `${brouillon.part_ca} %` : '—');
    titre('D. Charges (HT)');
    ligne('Total des charges liées à la formation', '', brouillon.charges_total ? fmtEur(brouillon.charges_total) : '—');
    ligne('dont salaires des formateurs', '', brouillon.charges_salaires ? fmtEur(brouillon.charges_salaires) : '—');
    ligne('dont achats de prestations et honoraires de formation', '', brouillon.charges_achats ? fmtEur(brouillon.charges_achats) : '—');
    titre('E. Personnes dispensant des heures de formation'); ligne('', 'Nombre', 'Heures', true);
    ligne('Personnes de votre organisme', bpf.E.interne.n, fmtH(bpf.E.interne.h)); ligne('Personnes extérieures (sous-traitance)', bpf.E.externe.n, fmtH(bpf.E.externe.h));
    titre('F-1. Type de stagiaires'); ligne('', 'Stagiaires', 'Heures', true);
    BPF_SITUATIONS.forEach(([k, l]) => ligne(`${k}. ${l}`, bpf.F1[k].n, fmtH(bpf.F1[k].h))); ligne('Total (1)', bpf.totF1.n, fmtH(bpf.totF1.h), true);
    titre('F-2. Dont activité sous-traitée'); ligne('Confiés par votre organisme à un autre organisme', bpf.F2.n, fmtH(bpf.F2.h));
    titre('F-3. Objectif général des prestations'); ligne('', 'Stagiaires', 'Heures', true);
    [['a'], ['b'], ['c'], ['d'], ['e'], ['f']].forEach(([k]) => {
      ligne(`${k}. ${libelleDe(BPF_OBJECTIFS, k)}`, bpf.F3[k].n, fmtH(bpf.F3[k].h));
      if (k === 'a') BPF_NIVEAUX_RNCP.forEach(([nv, l]) => ligne(`      dont ${l}`, bpf.F3a[nv].n, fmtH(bpf.F3a[nv].h)));
    });
    ligne('Total (3)', bpf.totF3.n, fmtH(bpf.totF3.h), true);
    titre('F-4. Spécialités de formation'); ligne('', 'Stagiaires', 'Heures', true);
    bpf.F4.forEach(x => ligne(`${x.code} ${libelleNsf(x.code)}`, x.n, fmtH(x.h))); ligne('Autres spécialités', bpf.F4autres.n, fmtH(bpf.F4autres.h)); ligne('Total (4)', bpf.totF4.n, fmtH(bpf.totF4.h), true);
    titre('G. Formations confiées par un autre organisme'); ligne('Stagiaires et heures', bpf.G.n, fmtH(bpf.G.h));
    titre('H. Dirigeant'); ligne('Nom et prénom', '', brouillon.dirigeant_nom || '—'); ligne('Qualité', '', brouillon.dirigeant_qualite || '—');
    if (bpf.verifs.length) { titre('Points à vérifier'); bpf.verifs.forEach(v => ligne(`• ${v.titre}${v.aide ? ' — ' + v.aide : ''}`)); }
    pdf.save(`BPF ${exo.debut.slice(0, 4)} - ${nettoyerNomFichier(orgSettings?.nom || 'organisme')}.pdf`);
  };

  const carte = 'bg-white border border-gray-100 rounded-3xl p-5 md:p-6 shadow-sm space-y-3 min-w-0';
  const h2 = 'text-base font-black text-gray-900';
  const lien = 'text-xs font-bold text-violet-700 hover:underline';
  const inputCls = 'w-full p-2.5 text-sm border bg-gray-50 border-gray-200 focus:border-violet-500 rounded-xl outline-none';
  const LigneNH = ({ libelle, n, h, onVoir, gris = false, gras = false }) => (
    <div className={`grid grid-cols-[minmax(0,1fr)_70px_80px] gap-2 items-center text-sm py-1.5 border-b border-gray-50 ${gris ? 'text-gray-400' : ''} ${gras ? 'font-black' : ''}`}>
      <span className="min-w-0 break-words">{libelle}{onVoir && n > 0 && <> <button type="button" onClick={onVoir} className={lien}>voir</button></>}</span>
      <span className="text-right font-bold">{n}</span>
      <span className="text-right font-bold">{fmtH(h)}</span>
    </div>
  );
  const EnteteNH = ({ a = 'Stagiaires' }) => (
    <div className="grid grid-cols-[minmax(0,1fr)_70px_80px] gap-2 text-[11px] font-bold text-gray-500"><span></span><span className="text-right">{a}</span><span className="text-right">Heures</span></div>
  );

  const entete = (
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
      <div className="min-w-0">
        <h2 className="text-2xl font-black text-gray-900">Bilan pédagogique et financier</h2>
        <p className="text-sm text-gray-500 mt-1">Préparé automatiquement à partir de vos dossiers. À vérifier puis à recopier sur Mon Activité Formation (avant le 30 avril).</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="bpf-exercice" className="text-xs font-bold text-gray-500">Exercice</label>
        <select id="bpf-exercice" value={perso ? 'perso' : `${exo.debut}|${exo.fin}`}
          onChange={e => { if (e.target.value === 'perso') { setPerso(true); return; } setPerso(false); const [d, f] = e.target.value.split('|'); setExo({ debut: d, fin: f }); }}
          className="text-sm font-bold p-2.5 rounded-xl border border-gray-200 bg-white">
          {exos.map(x => <option key={x.debut} value={`${x.debut}|${x.fin}`}>{x.libelle}</option>)}
          <option value="perso">Autres dates…</option>
        </select>
        {perso && (
          <span className="flex items-center gap-1">
            <input type="date" aria-label="Début de l'exercice" value={exo.debut} onChange={e => e.target.value && setExo(prev => ({ ...prev, debut: e.target.value }))} className="text-sm p-2 rounded-xl border border-gray-200" />
            <input type="date" aria-label="Fin de l'exercice" value={exo.fin} onChange={e => e.target.value && setExo(prev => ({ ...prev, fin: e.target.value }))} className="text-sm p-2 rounded-xl border border-gray-200" />
          </span>
        )}
        <button type="button" onClick={exporterCsv} disabled={!bpf} className="text-sm font-bold px-4 py-2.5 rounded-xl border border-violet-200 text-violet-700 bg-white hover:bg-violet-50 disabled:opacity-40">Export Excel (détail)</button>
        <button type="button" onClick={exporterPdf} disabled={!bpf} className="text-sm font-bold px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40">Télécharger le PDF</button>
      </div>
    </div>
  );

  if (etat === 'chargement') return <div className="max-w-7xl mx-auto space-y-6">{entete}<SqueletteChargement texte="Préparation de votre BPF…" /></div>;
  if (etat === 'absent') return (
    <div className="max-w-3xl mx-auto bg-white p-8 rounded-3xl border border-gray-100 text-center space-y-2">
      <h2 className="text-lg font-black text-gray-900">BPF pas encore activé</h2>
      <p className="text-sm text-gray-500">Lancez le SQL « bpf » dans Supabase (SQL Editor), puis rechargez la page.</p>
    </div>
  );
  if (etat === 'erreur' || !bpf) return (
    <div className="max-w-3xl mx-auto bg-amber-50 p-6 rounded-3xl border border-amber-100 text-center space-y-3">
      <p className="text-sm font-bold text-amber-800">Le BPF n'a pas pu être préparé.</p>
      <button type="button" onClick={charger} className="text-sm font-bold underline text-amber-800">Réessayer</button>
    </div>
  );

  const alertes = bpf.verifs.filter(v => v.niveau === 'alerte');
  const infos = bpf.verifs.filter(v => v.niveau === 'info');
  const nbAConfirmer = (bpf.verifs.find(v => v.cle === 'a_confirmer') || {}).titre;
  const dossiersF = bpf.dossiers.filter(d => d.hEx > 0 && !d.g);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-vue="bpf">
      {entete}
      <div className="flex gap-2 border-b border-gray-200" role="tablist">
        {[['bilan', 'Bilan'], ['reglages', `Réglages${nbAConfirmer ? ' (à confirmer)' : ''}`]].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)}
            className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px ${onglet === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{l}</button>
        ))}
      </div>

      {onglet === 'bilan' && (<>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-4">
          <div className={`rounded-2xl p-4 border ${alertes.length ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`} data-bloc="bpf-verifs">
            <p className={`text-sm font-black ${alertes.length ? 'text-amber-900' : 'text-emerald-800'}`}>
              {alertes.length ? `${alertes.length} point${alertes.length > 1 ? 's' : ''} à vérifier avant de déclarer` : 'Aucun point bloquant'}
            </p>
            <div className="mt-2 space-y-2">
              {[...alertes, ...infos].map(v => (
                <div key={v.cle} className="text-sm">
                  <p className={`font-bold ${v.niveau === 'alerte' ? 'text-amber-900' : 'text-gray-700'}`}>{v.niveau === 'alerte' ? '• ' : 'À noter : '}{v.titre}</p>
                  {v.aide && <p className="text-xs text-gray-600 break-words">{v.aide}</p>}
                  {v.dossiers && v.dossiers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {v.dossiers.slice(0, 12).map(d => (
                        <button key={d.id} type="button" onClick={() => (v.action === 'reglages' ? setOnglet('reglages') : onOuvrirClient && onOuvrirClient(d.id))}
                          className="text-xs font-bold bg-white border border-amber-200 text-amber-900 px-2 py-1 rounded-full hover:bg-amber-100">{d.nom}</button>
                      ))}
                      {v.dossiers.length > 12 && <span className="text-xs text-gray-500">+ {v.dossiers.length - 12}</span>}
                    </div>
                  )}
                  {v.action === 'reglages' && (!v.dossiers || !v.dossiers.length) && <button type="button" onClick={() => setOnglet('reglages')} className={lien}>Ouvrir les réglages</button>}
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-2xl p-4 border ${bpf.coherent ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-sm font-black ${bpf.coherent ? 'text-emerald-800' : 'text-red-800'}`}>{bpf.coherent ? 'Totaux cohérents' : 'Totaux à revoir'}</p>
            <p className={`text-xs mt-1 ${bpf.coherent ? 'text-emerald-800' : 'text-red-800'}`}>F-1 = F-3 = F-4 : {bpf.totF1.n} stagiaire{bpf.totF1.n > 1 ? 's' : ''} · {fmtH(bpf.totF1.h)}</p>
            <p className="text-xs text-gray-500 mt-2">{bpf.dossiers.length} dossier{bpf.dossiers.length > 1 ? 's' : ''} concerné{bpf.dossiers.length > 1 ? 's' : ''} par l'exercice (archivés compris).</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">A · B</span> Identification et informations générales</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-gray-500">N° de déclaration</p><p className="font-bold break-words">{orgSettings?.nda || <span className="text-amber-700">À compléter dans Paramètres</span>}</p></div>
              <div><p className="text-xs text-gray-500">SIRET</p><p className="font-bold break-words">{orgSettings?.siret || <span className="text-amber-700">À compléter dans Paramètres</span>}</p></div>
              <div><label htmlFor="bpf-fj" className="text-xs text-gray-500">Forme juridique</label><input id="bpf-fj" className={inputCls} value={brouillon.forme_juridique || ''} onChange={e => champ('forme_juridique', e.target.value)} placeholder="ex. Entreprise individuelle, SAS…" maxLength={80} /></div>
              <div><label htmlFor="bpf-naf" className="text-xs text-gray-500">Code NAF</label><input id="bpf-naf" className={inputCls} value={brouillon.naf || ''} onChange={e => champ('naf', e.target.value)} placeholder="ex. 8559A" maxLength={10} /></div>
              <div><p className="text-xs text-gray-500">Exercice comptable</p><p className="font-bold">{dateFrCourte(exo.debut)} → {dateFrCourte(exo.fin)}</p></div>
              <div><p className="text-xs text-gray-500">Formation en tout ou partie à distance</p><p className="font-bold">{bpf.distance ? 'Oui' : 'Non'}</p></div>
            </div>
          </section>

          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">D</span> Charges (HT) — depuis votre comptabilité</h3>
            {[['charges_total', 'Total des charges liées à la formation'], ['charges_salaires', 'dont salaires des formateurs'], ['charges_achats', 'dont achats de prestations et honoraires']].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between gap-3 text-sm">
                <label htmlFor={`bpf-${k}`} className="min-w-0">{l}</label>
                <input id={`bpf-${k}`} inputMode="decimal" className="w-36 p-2 text-sm text-right border bg-gray-50 border-gray-200 rounded-xl outline-none focus:border-violet-500" value={brouillon[k] ?? ''} onChange={e => champ(k, e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))} placeholder="€" />
              </div>
            ))}
            {bpf.repereHonoraires > 0 && (
              <p className="text-xs text-violet-800 bg-violet-50 rounded-xl px-3 py-2">Repère SkorUp : part reversée aux formateurs externes ≈ {fmtEur(bpf.repereHonoraires)}{' '}
                <button type="button" className={lien} onClick={() => champ('charges_achats', String(Math.round(bpf.repereHonoraires)))}>Reprendre ce montant</button></p>
            )}
          </section>

          <section className={`${carte} lg:row-span-2`}>
            <h3 className={h2}><span className="text-violet-600">C</span> Origine des produits (HT)</h3>
            <p className="text-xs text-gray-500">Depuis le bloc « Financement » des dossiers. Une formation sur deux exercices est comptée au prorata de ses heures faites dans l'exercice.</p>
            <div className="text-sm">
              {BPF_LIGNES_C.map(([k, n, l]) => {
                const sous = k.length > 3; const v = bpf.C[k];
                return (
                  <div key={k} className={`flex justify-between gap-3 py-1.5 border-b border-gray-50 ${sous ? 'pl-5 text-gray-700' : ''} ${!v.montant ? 'text-gray-400' : ''}`}>
                    <span className="min-w-0 break-words"><b>{n}</b> {l}</span>
                    <span className="shrink-0 text-right"><b>{fmtEur(v.montant)}</b>{v.dossiers.length > 0 && <> <button type="button" className={lien} onClick={() => ouvrirDetail(`C · ${n} — ${l}`, v.dossiers, k)}>{v.dossiers.length} dossier{v.dossiers.length > 1 ? 's' : ''}</button></>}</span>
                  </div>
                );
              })}
              <div className="flex justify-between gap-3 pt-3 text-base font-black"><span>Total des produits de la formation</span><span className="text-emerald-700">{fmtEur(bpf.totalC)}</span></div>
              <div className="flex justify-between items-center gap-3 pt-2">
                <label htmlFor="bpf-partca" className="text-xs text-gray-600">Part du chiffre d'affaires global réalisée en formation (%)</label>
                <input id="bpf-partca" inputMode="decimal" className="w-24 p-2 text-sm text-right border bg-gray-50 border-gray-200 rounded-xl outline-none focus:border-violet-500" value={brouillon.part_ca ?? ''} onChange={e => champ('part_ca', e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))} placeholder="%" />
              </div>
            </div>
          </section>

          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">E</span> Personnes dispensant la formation</h3>
            <EnteteNH a="Nombre" />
            <LigneNH libelle="Personnes de votre organisme" n={bpf.E.interne.n} h={bpf.E.interne.h} />
            <LigneNH libelle="Personnes extérieures (sous-traitance)" n={bpf.E.externe.n} h={bpf.E.externe.h} />
            <p className="text-xs text-gray-500">Heures des séances datées de l'exercice ; une séance de groupe compte une seule fois.</p>
          </section>

          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">F-2 · G</span> Sous-traitance</h3>
            <EnteteNH />
            <LigneNH libelle="F-2 · confiés par vous à un autre organisme" n={bpf.F2.n} h={bpf.F2.h} onVoir={() => ouvrirDetail('F-2 — Activité sous-traitée', bpf.F2.dossiers)} />
            <LigneNH libelle="G · confiés à vous par un autre organisme" n={bpf.G.n} h={bpf.G.h} onVoir={() => ouvrirDetail('G — Formations confiées par un autre organisme', bpf.G.dossiers)} />
          </section>

          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">F-1</span> Type de stagiaires</h3>
            <EnteteNH />
            {BPF_SITUATIONS.map(([k, l]) => <LigneNH key={k} libelle={`${k} · ${l}`} n={bpf.F1[k].n} h={bpf.F1[k].h} gris={!bpf.F1[k].n} onVoir={() => ouvrirDetail(`F-1 ${k} — ${l}`, bpf.F1[k].dossiers)} />)}
            <LigneNH libelle="Total" n={bpf.totF1.n} h={bpf.totF1.h} gras />
          </section>

          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">F-3</span> Objectif des prestations</h3>
            <EnteteNH />
            {['a', 'b', 'c', 'd', 'e', 'f'].map(k => (
              <React.Fragment key={k}>
                <LigneNH libelle={`${k} · ${libelleDe(BPF_OBJECTIFS, k)}`} n={bpf.F3[k].n} h={bpf.F3[k].h} gris={!bpf.F3[k].n} onVoir={() => ouvrirDetail(`F-3 ${k} — ${libelleDe(BPF_OBJECTIFS, k)}`, bpf.F3[k].dossiers)} />
                {k === 'a' && bpf.F3.a.n > 0 && BPF_NIVEAUX_RNCP.map(([nv, l]) => <LigneNH key={nv} libelle={`      dont ${l}`} n={bpf.F3a[nv].n} h={bpf.F3a[nv].h} gris={!bpf.F3a[nv].n} />)}
              </React.Fragment>
            ))}
            <LigneNH libelle="Total" n={bpf.totF3.n} h={bpf.totF3.h} gras />
          </section>

          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">F-4</span> Spécialités de formation</h3>
            <EnteteNH />
            {bpf.F4.map(x => <LigneNH key={x.code} libelle={`${x.code} · ${libelleNsf(x.code) || 'Spécialité'}`} n={x.n} h={x.h} onVoir={() => ouvrirDetail(`F-4 — ${x.code} ${libelleNsf(x.code)}`, x.dossiers)} />)}
            <LigneNH libelle="Autres spécialités (ou non renseignées)" n={bpf.F4autres.n} h={bpf.F4autres.h} gris={!bpf.F4autres.n} onVoir={() => ouvrirDetail('F-4 — Autres spécialités', bpf.F4autres.dossiers)} />
            <LigneNH libelle="Total" n={bpf.totF4.n} h={bpf.totF4.h} gras />
          </section>

          <section className={carte}>
            <h3 className={h2}><span className="text-violet-600">H</span> Dirigeant</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label htmlFor="bpf-dn" className="text-xs text-gray-500">Nom et prénom</label><input id="bpf-dn" className={inputCls} value={brouillon.dirigeant_nom || ''} onChange={e => champ('dirigeant_nom', e.target.value)} maxLength={120} /></div>
              <div><label htmlFor="bpf-dq" className="text-xs text-gray-500">Qualité</label><input id="bpf-dq" className={inputCls} value={brouillon.dirigeant_qualite || ''} onChange={e => champ('dirigeant_qualite', e.target.value)} placeholder="Gérant(e), Président(e)…" maxLength={80} /></div>
            </div>
          </section>
        </div>

        <div className={`${modifie ? 'sticky bottom-3 z-10 bg-violet-50 border-violet-200 shadow-lg' : 'bg-white border-gray-100'} flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl p-4 border`}>
          <p className="text-xs text-gray-600">Les chiffres se recalculent à chaque ouverture. Vos saisies (forme juridique, NAF, charges, part du CA, dirigeant) sont gardées pour cet exercice.</p>
          <button type="button" onClick={enregistrerSaisies} disabled={!modifie || enregistrement} className="shrink-0 text-sm font-bold px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40">{enregistrement ? 'Enregistrement…' : 'Enregistrer mes saisies'}</button>
        </div>
      </>)}

      {onglet === 'reglages' && (
        <div className="space-y-5" data-bloc="bpf-reglages">
          <p className="text-sm text-gray-600">SkorUp propose une valeur (marquée « proposé ») ; choisissez-la pour la confirmer. Chaque choix est enregistré tout de suite.</p>

          <section className={carte}>
            <h3 className={h2}>Modules — objectif (F-3) et spécialité (F-4)</h3>
            {(donnees.modules || []).length === 0 && <p className="text-sm text-gray-400">Aucun module.</p>}
            <div className="divide-y divide-gray-100">
              {(donnees.modules || []).map(m => {
                const r = reglages[`module|${m.id}`] || {};
                const sObj = suggestionObjectif(m);
                const obj = r.objectif || '';
                const objEff = obj || sObj;
                const nsfProp = objEff === 'e' ? '415' : '';
                return (
                  <div key={m.id} className="py-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)] gap-3 items-start" data-module={m.id}>
                    <p className="text-sm font-bold text-gray-900 break-words">{m.nom}{bpf.modulesUtilises.some(x => String(x.id) === String(m.id)) && <span className="ml-2 text-[11px] font-bold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">dans l'exercice</span>}</p>
                    <div>
                      <select aria-label={`Objectif du module ${m.nom}`} className={inputCls} value={obj} onChange={e => enregistrerReglage('module', m.id, { objectif: e.target.value || null })}>
                        <option value="">{sObj ? `Proposé : ${libelleDe(BPF_OBJECTIFS, sObj)}` : '— Choisir l\'objectif —'}</option>
                        {BPF_OBJECTIFS.map(([k, l]) => <option key={k} value={k}>{k} · {l}</option>)}
                      </select>
                      {objEff === 'a' && (
                        <select aria-label={`Niveau RNCP du module ${m.nom}`} className={`${inputCls} mt-2`} value={r.niveau || ''} onChange={e => enregistrerReglage('module', m.id, { niveau: e.target.value || null })}>
                          <option value="">— Niveau du diplôme —</option>
                          {BPF_NIVEAUX_RNCP.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                      )}
                    </div>
                    <div>
                      <input aria-label={`Code de spécialité du module ${m.nom}`} list="bpf-nsf" className={inputCls} defaultValue={r.nsf || ''} key={`${m.id}-${r.nsf || ''}`}
                        placeholder={nsfProp ? `Proposé : ${nsfProp}` : 'Code (ex. 415)'}
                        onBlur={e => { const v = e.target.value.trim().slice(0, 12); if (v !== (r.nsf || '')) enregistrerReglage('module', m.id, { nsf: v || null }); }} />
                      <p className="text-[11px] text-gray-500 mt-1 break-words">{libelleNsf(r.nsf || nsfProp) || 'Choisissez dans la liste ou saisissez le code officiel.'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <datalist id="bpf-nsf">{BPF_NSF.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</datalist>
          </section>

          <section className={carte}>
            <h3 className={h2}>Formateurs — internes ou externes (E, F-2)</h3>
            <div className="divide-y divide-gray-100">
              {(donnees.utilisateurs || []).filter(u => u.role === 'formateur' || u.role === 'admin' || bpf.formateursUtilises.includes(String(u.id))).map(u => {
                const r = reglages[`formateur|${u.id}`] || {};
                const prop = suggestionStatutFormateur(u);
                return (
                  <div key={u.id} className="py-3 flex flex-col md:flex-row md:items-center justify-between gap-2" data-formateur={u.id}>
                    <p className="text-sm font-bold text-gray-900 break-words">{u.nom || u.email}{u.role === 'admin' && <span className="ml-2 text-[11px] text-gray-500">(administrateur)</span>}{bpf.formateursUtilises.includes(String(u.id)) && <span className="ml-2 text-[11px] font-bold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">dans l'exercice</span>}</p>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Statut de ${u.nom || u.email}`}>
                      {[['interne', 'Interne (salarié, dirigeant, vous-même)'], ['externe', 'Externe — sous-traitant (son propre n° de déclaration)']].map(([k, l]) => (
                        <label key={k} className={`text-xs font-bold px-3 py-2 rounded-xl border cursor-pointer ${(r.statut || '') === k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-200'}`}>
                          <input type="radio" className="sr-only" name={`statut-${u.id}`} checked={(r.statut || '') === k} onChange={() => enregistrerReglage('formateur', u.id, { statut: k })} />
                          {l}{!r.statut && prop === k ? ' · proposé' : ''}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className={carte}>
            <h3 className={h2}>Stagiaires de l'exercice — situation (F-1) et dispositif OPCO (C 2)</h3>
            {dossiersF.length === 0 && <p className="text-sm text-gray-400">Aucun stagiaire avec des heures dans cet exercice.</p>}
            <div className="divide-y divide-gray-100">
              {dossiersF.map(d => {
                const r = reglages[`client|${d.id}`] || {};
                const prop = suggestionSituation(d.fins);
                const opcos = d.fins.filter(f => f.type === 'opco' || f.type === 'public');
                return (
                  <div key={d.id} className="py-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.2fr)] gap-3 items-start" data-stagiaire={d.id}>
                    <div className="min-w-0">
                      <button type="button" onClick={() => onOuvrirClient && onOuvrirClient(d.id)} className="text-sm font-bold text-gray-900 hover:text-violet-700 hover:underline text-left break-words">{d.nom}</button>
                      <p className="text-[11px] text-gray-500 break-words">{d.fins.length ? typesFinancementDe(d.fins).join(' + ') : 'Sans financement'} · {fmtH(d.hEx)}</p>
                    </div>
                    <select aria-label={`Situation de ${d.nom}`} className={inputCls} value={r.situation || ''} onChange={e => enregistrerReglage('client', d.id, { situation: e.target.value || null })}>
                      <option value="">{prop ? `Proposé : ${libelleDe(BPF_SITUATIONS, prop)}` : '— Choisir la situation —'}</option>
                      {BPF_SITUATIONS.map(([k, l]) => <option key={k} value={k}>{k} · {l}</option>)}
                    </select>
                    <div className="space-y-2">
                      {opcos.map(f => {
                        const rf = reglages[`financement|${f.id}`] || {};
                        return f.type === 'opco' ? (
                          <select key={f.id} aria-label={`Dispositif OPCO de ${d.nom}`} className={inputCls} value={rf.dispositif || 'c2h'} onChange={e => enregistrerReglage('financement', f.id, { dispositif: e.target.value })}>
                            {BPF_DISPOSITIFS_OPCO.map(([k, l]) => <option key={k} value={k}>OPCO {f.financeur_nom ? `(${f.financeur_nom}) ` : ''}· {l}</option>)}
                          </select>
                        ) : (
                          <select key={f.id} aria-label={`Financeur public de ${d.nom}`} className={inputCls} value={rf.ligne || 'c8'} onChange={e => enregistrerReglage('financement', f.id, { ligne: e.target.value })}>
                            {BPF_LIGNES_PUBLIQUES.map(([k, l]) => <option key={k} value={k}>Public {f.financeur_nom ? `(${f.financeur_nom}) ` : ''}· {l}</option>)}
                          </select>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-gray-900/60 z-[150] flex items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label={detail.titre}>
          <div className="bg-white w-full sm:max-w-4xl h-full sm:h-auto sm:max-h-[90vh] sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <h3 className="font-black text-gray-900 break-words min-w-0">{detail.titre}</h3>
              <button type="button" onClick={() => setDetail(null)} className="shrink-0 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500" aria-label="Fermer">✕</button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-left text-xs text-gray-500">
                  <th className="py-2 pr-3">Dossier</th><th className="py-2 pr-3">Module</th><th className="py-2 pr-3 text-right">Heures (exercice / total)</th>
                  {detail.avecMontants && <><th className="py-2 pr-3 text-right">Financé</th><th className="py-2 text-right">Compté</th></>}
                </tr></thead>
                <tbody>
                  {detail.dossiers.map(d => {
                    const p = detail.avecMontants ? d.produits.filter(x => x.ligne === detail.avecMontants || (detail.avecMontants === 'c2' && /^c2/.test(x.ligne))) : [];
                    const partiel = d.hTot > 0 && d.hEx < d.hTot;
                    return (
                      <tr key={d.id} className="border-t border-gray-100">
                        <td className="py-2 pr-3"><button type="button" onClick={() => { setDetail(null); if (onOuvrirClient) onOuvrirClient(d.id); }} className="font-bold text-violet-700 hover:underline text-left">{d.nom}</button>{d.archive && <span className="ml-1 text-[11px] text-gray-400">(archivé)</span>}</td>
                        <td className="py-2 pr-3 text-gray-600">{d.moduleNom || '—'}</td>
                        <td className={`py-2 pr-3 text-right ${partiel ? 'text-amber-700 font-bold' : ''}`}>{fmtH(d.hEx)} / {fmtH(d.hTot)}</td>
                        {detail.avecMontants && <><td className="py-2 pr-3 text-right">{fmtEur(p.reduce((s, x) => s + (x.montantTotal || 0), 0))}</td><td className="py-2 text-right font-bold">{fmtEur(p.reduce((s, x) => s + x.montant, 0))}</td></>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {detail.avecMontants && <p className="text-xs text-gray-500 mt-3">Heures en orange : formation sur deux exercices, montant compté au prorata des heures faites dans celui-ci.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── QUESTIONNAIRE AU FINANCEUR (2026-10-26) ───────────────────────────────────────────────────────
// Envoi par email d'un questionnaire de l'organisme au contact d'un financeur, qui répond sans compte
// (même page publique que les prospects, via api/prospects.js). Table financeur_questionnaire_envois.
const TITRE_MODELE_FINANCEUR = 'Satisfaction du financeur';
const QUESTIONS_MODELE_FINANCEUR = () => {
  const n = Date.now();
  const satis = ['Très satisfait', 'Satisfait', 'Peu satisfait', 'Pas satisfait'];
  return [
    { id: `${n}-1`, text: 'Globalement, êtes-vous satisfait(e) de la prestation que vous avez financée ?', type: 'single', options: satis, obligatoire: true },
    { id: `${n}-2`, text: 'La prestation a-t-elle répondu aux objectifs fixés ?', type: 'single', options: ['Oui, totalement', 'En partie', 'Non'], obligatoire: true },
    { id: `${n}-3`, text: 'Comment jugez-vous nos échanges (réactivité, disponibilité, clarté des informations) ?', type: 'single', options: satis, obligatoire: true },
    { id: `${n}-4`, text: 'Les documents administratifs (convention, feuilles d\'émargement, attestations…) vous ont-ils été transmis dans les délais ?', type: 'single', options: ['Oui', 'Non', 'Je ne sais pas'], obligatoire: true },
    { id: `${n}-5`, text: 'Quels sont, selon vous, les points forts de la prestation ?', type: 'multiple', options: ['Qualité de l\'accompagnement', 'Suivi administratif', 'Respect des délais', 'Communication', 'Résultats obtenus'], obligatoire: false },
    { id: `${n}-6`, text: 'Recommanderiez-vous notre organisme ?', type: 'single', options: ['Oui', 'Peut-être', 'Non'], obligatoire: true },
    { id: `${n}-7`, text: 'Avez-vous des remarques ou des suggestions d\'amélioration ?', type: 'text', obligatoire: false },
  ];
};
// Crée le modèle dans les questionnaires de l'organisme (ou retrouve celui qui existe déjà).
const creerModeleSatisfactionFinanceur = async (orgId) => {
  if (!orgId) throw new Error('Organisme introuvable.');
  const { data: existants } = await supabase.from('module_step_resources').select('id, titre, module_id')
    .eq('type', 'questionnaire').eq('organisation_id', orgId).eq('titre', TITRE_MODELE_FINANCEUR);
  const deja = (existants || []).find(r => r.module_id === null || r.module_id === undefined);
  if (deja) return { id: deja.id, existait: true };
  const { data, error } = await supabase.from('module_step_resources').insert([{
    titre: TITRE_MODELE_FINANCEUR,
    type: 'questionnaire',
    metadata: JSON.stringify({ questions: QUESTIONS_MODELE_FINANCEUR(), isQuiz: false, description: 'Questionnaire envoyé au financeur (OPCO, entreprise…) pour recueillir son appréciation — Qualiopi, indicateur 30.' }),
    module_id: null,
    organisation_id: orgId,
  }]).select('id').single();
  if (error) throw error;
  return { id: data.id, existait: false };
};
const appelQuestionnaireFinanceur = async (action, corps) => {
  const { data: s } = await supabase.auth.getSession();
  const jeton = s?.session?.access_token;
  if (!jeton) throw new Error('Session expirée, reconnectez-vous.');
  const resp = await fetch(`/api/prospects?action=${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}` },
    body: JSON.stringify({ ...corps, origin: window.location.origin }),
  });
  const r = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(r.error || 'Erreur lors de l\'envoi.');
  return r;
};
const texteReponse = (q, rep) => {
  if (Array.isArray(rep)) return rep.length ? rep.join(', ') : 'Sans réponse';
  return (rep !== undefined && rep !== null && String(rep).trim()) ? String(rep) : 'Sans réponse';
};
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const telechargerReponsesFinanceurPdf = async (envoi, nomClient) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const mx = 50, largeur = 595.28 - 100, bas = 841.89 - 50, lh = 15;
  let y = 60;
  const place = (h) => { if (y + h > bas) { pdf.addPage(); y = 60; } };
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16); pdf.setTextColor(20);
  const t = pdf.splitTextToSize(envoi.titre || 'Questionnaire', largeur); pdf.text(t, mx, y); y += t.length * 20 + 6;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(100);
  [
    `Financeur : ${envoi.financeur_nom || '—'}${envoi.destinataire_nom ? ' — ' + envoi.destinataire_nom : ''} (${envoi.destinataire_email || ''})`,
    `Dossier : ${nomClient || '—'}`,
    `Envoyé le ${dateFr(envoi.created_at)} — répondu le ${dateFr(envoi.rempli_at)}`,
  ].forEach(l => { const x = pdf.splitTextToSize(l, largeur); pdf.text(x, mx, y); y += x.length * 14; });
  y += 6; pdf.setDrawColor(220); pdf.line(mx, y, mx + largeur, y); y += 24;
  (envoi.questions || []).forEach((q, i) => {
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(20);
    const ql = pdf.splitTextToSize(`${i + 1}. ${q.text || ''}`, largeur); place(ql.length * lh + 20); pdf.text(ql, mx, y); y += ql.length * lh + 4;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(80);
    const al = pdf.splitTextToSize(texteReponse(q, envoi.reponses && envoi.reponses[q.id]), largeur); place(al.length * lh + 20); pdf.text(al, mx, y); y += al.length * lh + 18;
  });
  const nom = nomTelechargement(`${envoi.titre || 'Questionnaire'} - ${envoi.financeur_nom || 'financeur'}`, nomClient || '');
  pdf.save(`${nom}.pdf`);
};
const FenetreReponsesFinanceur = ({ envoi, nomClient, onClose }) => (
  <div className="fixed inset-0 bg-gray-900/60 z-[150] flex items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Réponses du financeur">
    <div className="bg-white w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-black text-gray-900 break-words">📝 {envoi.titre}</h3>
          <p className="text-xs text-gray-500 break-words">{[envoi.financeur_nom, envoi.destinataire_nom, envoi.destinataire_email].filter(Boolean).join(' · ')} — répondu le {dateFr(envoi.rempli_at)}</p>
        </div>
        <button type="button" onClick={onClose} className="shrink-0 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500" aria-label="Fermer">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {(envoi.questions || []).map((q, i) => (
          <div key={q.id || i}>
            <p className="text-sm font-bold text-gray-900">{i + 1}. {q.text}</p>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words bg-gray-50 rounded-xl px-3 py-2">{texteReponse(q, envoi.reponses && envoi.reponses[q.id])}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-100 p-4 flex justify-end gap-2">
        <button type="button" onClick={() => telechargerReponsesFinanceurPdf(envoi, nomClient)} className="px-4 py-2.5 text-sm font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white">Télécharger en PDF</button>
        <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-bold rounded-xl text-gray-600 hover:bg-gray-100">Fermer</button>
      </div>
    </div>
  </div>
);
// Sous une ligne de financeur : envoi d'un questionnaire et suivi des envois.
const QuestionnairesFinanceur = ({ ligne, envois = [], orgId, nomClient, onChange, verrouille = false }) => {
  const [ouvert, setOuvert] = React.useState(false);
  const [liste, setListe] = React.useState(null); // questionnaires de l'organisme
  const [choix, setChoix] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [vu, setVu] = React.useState(null);
  const email = String(ligne.contact_email || '').trim();
  const chargerListe = async (selection = null) => {
    const { data } = await supabase.from('module_step_resources').select('id, titre, module_id')
      .eq('type', 'questionnaire').eq('organisation_id', orgId).order('titre', { ascending: true });
    const l = (data || []).filter(r => r.module_id === null || r.module_id === undefined);
    setListe(l);
    const modele = l.find(r => r.titre === TITRE_MODELE_FINANCEUR);
    setChoix(String(selection ?? (modele ? modele.id : (l[0] ? l[0].id : ''))));
  };
  const ouvrir = () => { setOuvert(true); if (!liste) chargerListe(); };
  const creerModele = async () => {
    setEnCours(true);
    try {
      const r = await creerModeleSatisfactionFinanceur(orgId);
      toast.success(r.existait ? 'Le modèle « Satisfaction du financeur » existe déjà : il est sélectionné.' : 'Modèle « Satisfaction du financeur » créé (modifiable dans Questionnaires).');
      await chargerListe(r.id);
    } catch (e) { toast.error('Création impossible : ' + (e?.message || 'erreur inconnue')); }
    finally { setEnCours(false); }
  };
  const envoyer = async () => {
    if (!choix || enCours) return;
    setEnCours(true);
    try {
      const r = await appelQuestionnaireFinanceur('envoyer_financeur', { financementId: ligne.id, questionnaireId: choix });
      if (r.simulated) toast('Questionnaire enregistré, mais l\'envoi d\'emails n\'est pas configuré sur le serveur.', { icon: '⚠️' });
      else toast.success(`Questionnaire envoyé à ${email}.`);
      setOuvert(false);
      if (onChange) await onChange();
    } catch (e) { toast.error(e.message); }
    finally { setEnCours(false); }
  };
  const relancer = async (envoi) => {
    setEnCours(true);
    try {
      await appelQuestionnaireFinanceur('relancer_financeur', { envoiId: envoi.id });
      toast.success(`Rappel envoyé à ${envoi.destinataire_email}.`);
      if (onChange) await onChange();
    } catch (e) { toast.error(e.message); }
    finally { setEnCours(false); }
  };
  if (ligne.type === 'personnel' && !envois.length) return null;
  const aModele = (liste || []).some(r => r.titre === TITRE_MODELE_FINANCEUR);
  return (
    <div className="border-t border-gray-100 px-3 py-2.5 space-y-2" data-bloc="questionnaire-financeur">
      {envois.map(e => (
        <div key={e.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
          <p className="text-gray-700 break-words min-w-0">
            <span className="font-bold">📝 {e.titre}</span>{' — '}
            {e.statut === 'rempli'
              ? <span className="font-bold text-emerald-700">✅ Répondu le {dateFr(e.rempli_at)}</span>
              : <span className="text-amber-700">⏳ Envoyé le {dateFr(e.created_at)}{e.nb_relances ? ` · relancé ${e.nb_relances} fois` : ''}</span>}
          </p>
          <div className="flex gap-2 shrink-0">
            {e.statut === 'rempli' ? (<>
              <button type="button" onClick={() => setVu(e)} className="font-bold text-emerald-700 hover:underline">Voir les réponses</button>
              <button type="button" onClick={() => telechargerReponsesFinanceurPdf(e, nomClient)} className="font-bold text-gray-500 hover:underline">PDF</button>
            </>) : (
              <button type="button" onClick={() => relancer(e)} disabled={enCours || verrouille} className="font-bold text-amber-700 hover:underline disabled:opacity-40">Relancer</button>
            )}
          </div>
        </div>
      ))}
      {ligne.type !== 'personnel' && !ouvert && (
        email
          ? <button type="button" onClick={ouvrir} disabled={verrouille} className="text-xs font-bold text-violet-700 hover:underline disabled:opacity-40">📝 Envoyer un questionnaire au financeur</button>
          : <p className="text-[11px] text-gray-400">Ajoutez l'email du contact (crayon ✏️) pour lui envoyer un questionnaire.</p>
      )}
      {ouvert && (
        <div className="bg-violet-50 border border-violet-100 rounded-xl p-3 space-y-2">
          <p className="text-xs text-gray-600">Le contact reçoit un email avec un lien personnel (valable 30 jours, sans compte) : <strong className="break-all">{email}</strong></p>
          {liste === null ? <p className="text-xs text-gray-400">Chargement des questionnaires…</p> : (
            <>
              {liste.length > 0 && (
                <select aria-label="Questionnaire à envoyer" value={choix} onChange={e => setChoix(e.target.value)} className="w-full p-2.5 text-sm border bg-white border-gray-200 rounded-xl outline-none focus:border-violet-500">
                  {liste.map(r => <option key={r.id} value={String(r.id)}>{r.titre}</option>)}
                </select>
              )}
              {!aModele && (
                <button type="button" onClick={creerModele} disabled={enCours} className="text-xs font-bold text-violet-700 hover:underline disabled:opacity-40">✨ Créer le modèle « {TITRE_MODELE_FINANCEUR} »</button>
              )}
            </>
          )}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <button type="button" onClick={() => setOuvert(false)} disabled={enCours} className="px-3 py-2 text-xs font-bold text-gray-500 hover:text-gray-800 rounded-xl">Annuler</button>
            <button type="button" onClick={envoyer} disabled={enCours || !choix} className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-4 py-2 rounded-xl disabled:opacity-50">
              {enCours ? 'Envoi…' : 'Envoyer le questionnaire'}
            </button>
          </div>
        </div>
      )}
      {vu && <FenetreReponsesFinanceur envoi={vu} nomClient={nomClient} onClose={() => setVu(null)} />}
    </div>
  );
};

// Bloc « Financement » de la fiche client (administrateur).
const FINANCEMENT_VIDE = { type: 'opco', financeur_nom: '', montant: '', numero_prise_en_charge: '', statut: '', contact_nom: '', contact_email: '', contact_telephone: '' };
const BlocFinancements = ({ client, currentOrgId, montantPrestation }) => {
  const [lignes, setLignes] = React.useState([]);
  const [etat, setEtat] = React.useState('chargement'); // chargement | ok | absent | erreur
  const [edition, setEdition] = React.useState(null); // { id | null, ...champs }
  const [enCours, setEnCours] = React.useState(false);
  const [aSupprimer, setASupprimer] = React.useState(null);
  const [envois, setEnvois] = React.useState(null); // 2026-10-26 : questionnaires aux financeurs (null = fonction pas encore activée)
  const clientId = client && client.id;
  const charger = React.useCallback(async () => {
    if (!clientId) return;
    const { data, error } = await supabase.from('financements').select('*').eq('client_id', clientId)
      .order('position', { ascending: true }).order('created_at', { ascending: true });
    if (error) {
      const absente = error.code === '42P01' || error.code === 'PGRST205' || /(does not exist|schema cache|could not find)/i.test(error.message || '');
      setEtat(absente ? 'absent' : 'erreur'); setLignes([]); return;
    }
    setLignes(data || []); setEtat('ok');
    // 2026-10-26 : questionnaires envoyés aux financeurs (table absente → rien).
    const env = await supabase.from('financeur_questionnaire_envois').select('*').eq('client_id', clientId).order('created_at', { ascending: true });
    setEnvois(!env.error && Array.isArray(env.data) ? env.data : null);
  }, [clientId]);
  React.useEffect(() => { setEdition(null); setEtat('chargement'); charger(); }, [charger]);
  const nomClient = (client && (client.nom_complet || client.nomcomplet_client || client.nom)) || '';
  const envoisOrphelins = (envois || []).filter(e => !e.financement_id || !lignes.some(l => String(l.id) === String(e.financement_id)));

  if (etat === 'absent' || etat === 'chargement') return null;
  if (etat === 'erreur') {
    return (
      <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm text-amber-800 flex items-center justify-between gap-3">
        <span>Le financement de ce dossier n'a pas pu être chargé.</span>
        <button type="button" onClick={charger} className="font-bold underline">Réessayer</button>
      </div>
    );
  }

  const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const total = lignes.reduce((s, l) => s + (montantRenseigne(l.montant) ? Number(l.montant) : 0), 0);
  const prestation = Number(montantPrestation) || 0;
  const ecart = prestation > 0 && lignes.some(l => montantRenseigne(l.montant)) ? Math.round((total - prestation) * 100) / 100 : null;
  const champ = (k, v) => setEdition(prev => {
    const suite = { ...prev, [k]: v };
    if (k === 'type' && !String(prev.financeur_nom || '').trim()) {
      if (v === 'cpf') suite.financeur_nom = 'Mon Compte Formation';
      if (v === 'france_travail') suite.financeur_nom = 'France Travail';
    }
    return suite;
  });
  const enregistrer = async () => {
    if (!edition || enCours) return;
    const montantTxt = String(edition.montant ?? '').replace(/\s/g, '').replace(',', '.');
    if (montantTxt !== '' && (Number.isNaN(Number(montantTxt)) || Number(montantTxt) < 0)) { toast.error('Le montant doit être un nombre positif.'); return; }
    const email = String(edition.contact_email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("L'email du contact n'est pas valide."); return; }
    const perso = edition.type === 'personnel';
    const ligne = {
      type: edition.type,
      financeur_nom: perso ? null : (String(edition.financeur_nom || '').trim() || null),
      montant: montantTxt === '' ? null : Math.round(Number(montantTxt) * 100) / 100,
      numero_prise_en_charge: perso ? null : (String(edition.numero_prise_en_charge || '').trim() || null),
      statut: edition.statut || null,
      contact_nom: perso ? null : (String(edition.contact_nom || '').trim() || null),
      contact_email: perso ? null : (email || null),
      contact_telephone: perso ? null : (String(edition.contact_telephone || '').trim() || null),
      updated_at: new Date().toISOString(),
    };
    setEnCours(true);
    try {
      const { error } = edition.id
        ? await supabase.from('financements').update(ligne).eq('id', edition.id)
        : await supabase.from('financements').insert([{ ...ligne, client_id: clientId, organisation_id: currentOrgId || client.organisation_id, position: lignes.length }]);
      if (error) throw error;
      toast.success('Financement enregistré.');
      setEdition(null);
      await charger();
    } catch (e) {
      toast.error("Le financement n'a pas pu être enregistré : " + (e?.message || 'erreur inconnue'));
    } finally { setEnCours(false); }
  };
  const supprimer = async () => {
    const l = aSupprimer; setASupprimer(null);
    if (!l) return;
    const { error } = await supabase.from('financements').delete().eq('id', l.id);
    if (error) { toast.error('Suppression impossible : ' + error.message); return; }
    toast.success('Financement supprimé.');
    await charger();
  };
  const cls = 'w-full p-3 text-sm border bg-gray-50 border-gray-200 focus:border-emerald-500 rounded-xl outline-none transition-colors';
  const placeholderNom = { opco: 'ex. OPCO Atlas, AKTO, Constructys…', entreprise: "Nom de l'entreprise", region: 'ex. Région Bretagne', public: "ex. État, Département…", organisme_formation: "Nom de l'organisme donneur d'ordre", autre: 'Nom du financeur' };

  return (
    <div className="border border-emerald-100 bg-emerald-50/40 rounded-2xl p-4 md:p-5 space-y-4" data-bloc="financement">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-black text-gray-800">💶 Financement du dossier</h4>
          <p className="text-xs text-gray-500">Qui paie la prestation (une ligne par financeur, ex. CPF + reste à charge du client).</p>
        </div>
        {!edition && (
          <button type="button" onClick={() => setEdition({ ...FINANCEMENT_VIDE, type: lignes.length ? 'personnel' : 'opco', id: null })}
            className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm">
            + Ajouter un financeur
          </button>
        )}
      </div>

      {lignes.length === 0 && !edition && <p className="text-sm text-gray-400 italic">Aucun financement renseigné pour ce dossier.</p>}

      {lignes.length > 0 && (
        <div className="space-y-2">
          {lignes.map(l => (
            <div key={l.id} className="bg-white rounded-xl border border-gray-100">
            <div className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 break-words">
                  <span className="text-xs font-black uppercase tracking-wider text-emerald-700 mr-2">{libelleFinancement(l.type)}</span>
                  {l.type !== 'personnel' && (l.financeur_nom || '')}
                  {montantRenseigne(l.montant) && <span className="ml-2 text-gray-700">{fmt(l.montant)}</span>}
                </p>
                <p className="text-xs text-gray-500 break-words">
                  {[l.statut ? libelleStatutFinancement(l.statut) : '', l.numero_prise_en_charge ? `N° ${l.numero_prise_en_charge}` : '', [l.contact_nom, l.contact_email, l.contact_telephone].filter(Boolean).join(' · ')].filter(Boolean).join(' — ')}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button type="button" onClick={() => setEdition({ ...FINANCEMENT_VIDE, ...l, montant: montantRenseigne(l.montant) ? String(l.montant) : '', statut: l.statut || '' })} disabled={!!edition}
                  className="p-2 text-gray-400 hover:text-emerald-700 rounded-lg disabled:opacity-40" title="Modifier" aria-label={`Modifier le financement ${libelleFinancement(l.type)}`}><Pencil size={16} /></button>
                <button type="button" onClick={() => setASupprimer(l)} disabled={!!edition}
                  className="p-2 text-gray-300 hover:text-red-500 rounded-lg disabled:opacity-40" title="Supprimer" aria-label={`Supprimer le financement ${libelleFinancement(l.type)}`}><Trash2 size={16} /></button>
              </div>
            </div>
            {envois !== null && (
              <QuestionnairesFinanceur ligne={l} envois={envois.filter(e => String(e.financement_id) === String(l.id))} orgId={currentOrgId || client.organisation_id}
                nomClient={nomClient} onChange={charger} verrouille={!!edition} />
            )}
            </div>
          ))}
          {envoisOrphelins.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100">
              <p className="px-3 pt-2.5 text-xs font-bold text-gray-500">Questionnaires d'anciens financeurs</p>
              <QuestionnairesFinanceur ligne={{ id: null, type: 'personnel' }} envois={envoisOrphelins} orgId={currentOrgId || client.organisation_id} nomClient={nomClient} onChange={charger} verrouille />
            </div>
          )}
          {ecart !== null && (
            ecart === 0
              ? <p className="text-xs font-bold text-emerald-700">✓ Total des financements : {fmt(total)} — correspond au montant de la prestation.</p>
              : <p className="text-xs font-bold text-amber-800 bg-amber-50 rounded-lg px-3 py-2">⚠️ Total des financements : {fmt(total)} — différent du montant de la prestation ({fmt(prestation)}).</p>
          )}
        </div>
      )}

      {edition && (
        <div className="bg-white rounded-2xl border border-emerald-200 p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1" htmlFor="fin-type">Type de financement</label>
              <select id="fin-type" className={cls} value={edition.type} onChange={e => champ('type', e.target.value)}>
                {TYPES_FINANCEMENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {edition.type !== 'personnel' && (
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1" htmlFor="fin-nom">Nom du financeur</label>
                <input id="fin-nom" className={cls} value={edition.financeur_nom || ''} onChange={e => champ('financeur_nom', e.target.value)} placeholder={placeholderNom[edition.type] || ''} maxLength={150} />
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1" htmlFor="fin-montant">Montant (€)</label>
              <input id="fin-montant" type="text" inputMode="decimal" className={cls} value={edition.montant ?? ''} onChange={e => champ('montant', e.target.value)} placeholder="ex. 1500" />
            </div>
            {edition.type !== 'personnel' && (
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-1" htmlFor="fin-numero">N° de dossier / prise en charge</label>
                <input id="fin-numero" className={cls} value={edition.numero_prise_en_charge || ''} onChange={e => champ('numero_prise_en_charge', e.target.value)} maxLength={100} />
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1" htmlFor="fin-statut">Statut</label>
              <select id="fin-statut" className={cls} value={edition.statut || ''} onChange={e => champ('statut', e.target.value)}>
                {STATUTS_FINANCEMENT.map(([v, l]) => <option key={v || 'aucun'} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          {edition.type !== 'personnel' && (
            <div>
              <p className="text-xs font-bold text-gray-500 mb-2">Contact chez le financeur (facultatif)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input aria-label="Nom du contact" className={cls} value={edition.contact_nom || ''} onChange={e => champ('contact_nom', e.target.value)} placeholder="Nom du contact" maxLength={150} />
                <input aria-label="Email du contact" type="email" className={cls} value={edition.contact_email || ''} onChange={e => champ('contact_email', e.target.value)} placeholder="Email" maxLength={200} />
                <input aria-label="Téléphone du contact" type="tel" className={cls} value={edition.contact_telephone || ''} onChange={e => champ('contact_telephone', e.target.value)} placeholder="Téléphone" maxLength={40} />
              </div>
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <button type="button" onClick={() => setEdition(null)} disabled={enCours} className="px-4 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-800 rounded-xl">Annuler</button>
            <button type="button" onClick={enregistrer} disabled={enCours} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl shadow-sm disabled:opacity-50">
              {enCours ? 'Enregistrement…' : 'Enregistrer le financement'}
            </button>
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400">
        Dans vos modèles de documents : {'{financement_type}'}, {'{financeur_nom}'}, {'{montant_financeur}'}, {'{reste_a_charge}'}, {'{numero_prise_en_charge}'}.
      </p>
      <ConfirmModal
        isOpen={!!aSupprimer}
        title="Supprimer ce financement ?"
        message={aSupprimer ? `${libelleFinancement(aSupprimer.type)}${aSupprimer.financeur_nom ? ' — ' + aSupprimer.financeur_nom : ''} sera retiré de ce dossier. Les documents déjà envoyés ne changent pas et les réponses aux questionnaires sont conservées.` : ''}
        onConfirm={supprimer}
        onCancel={() => setASupprimer(null)}
      />
    </div>
  );
};

// Formateur : rappel du financement de son client (type + financeur seulement).
const FinancementResume = ({ clientId }) => {
  const [lignes, setLignes] = React.useState([]);
  React.useEffect(() => {
    if (!clientId) return undefined;
    let annule = false;
    supabase.rpc('skorup_financements_dossier', { p_client: String(clientId) })
      .then(({ data, error }) => { if (!annule) setLignes(!error && Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { annule = true; };
  }, [clientId]);
  if (!lignes.length) return null;
  return (
    <p className="mb-5 text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2.5 break-words" data-bloc="financement-formateur">
      <span className="font-bold text-emerald-800">💶 Financement : </span>
      {lignes.map(l => (l.type !== 'personnel' && l.financeur_nom ? `${libelleFinancement(l.type)} (${l.financeur_nom})` : libelleFinancement(l.type))).join(' + ')}
    </p>
  );
};

const AdminClientsView = ({
  chargement = false,
  clientsArchives = [], onRestaurerDossier = null,
  handleAddUser, newUserName, setNewUserName,
  newUserEmail, setNewUserEmail,
  newUserRole, setNewUserRole, isAddingUser,
  clientPhone, setClientPhone,
  clientEmail, setClientEmail,
  clients, formateurs, assignFormateur, handleModuleChange,
  modules, handleGenerateDocx, sessions, documentTemplates, supabase,
  expandedClientId, setExpandedClientId, fetchUtilisateurs, fetchDocuments,
  activeTab, setActiveTab, setIsInviteModalOpen, setInviteDefaultRole, fetchSessions, documents,
  pedagogicalResources, handleDownloadResource, handleUploadExerciseResponse,
  generateSessions, handleDeleteClient, setIsSessionItemModalOpen,
  setTargetSessionForAddition, setViewingSession,
  handleDownloadPDF, updateSessionDate, updateSessionTime, onTimeChange, onSaveTimes,
  editedTimes, lastModifiedSessionId,
  setDocSettingsTarget, setIsDocSettingsOpen, setViewingDocId,
  handleMoveSessionItem, handleSaveCorrection, handleAddAdminBloc,
  handleRegenerateVisualDocs, instantiateDocument, currentOrgId,
  groupes = [], onOpenGroupe = null,
  depotsNonLus = [], estPro = false, nomUtilisateur = '', onDepotsVus = null, ongletInitial = null, onOngletInitialUtilise = null
}) => {
  const [clientSearchTerm, setClientSearchTerm] = React.useState('');

  const filteredClients = clientSearchTerm.trim()
    ? clients.filter(c => {
        const term = clientSearchTerm.toLowerCase();
        const fullName = `${c.prenom || ''} ${c.nom || ''}`.toLowerCase();
        const reverseName = `${c.nom || ''} ${c.prenom || ''}`.toLowerCase();
        return fullName.includes(term) || reverseName.includes(term) || (c.email || '').toLowerCase().includes(term);
      })
    : clients;

  // AJOUT (2026-10-19) : filtre par statut du compte (activé / en attente / sans compte).
  const ctxComptes = React.useContext(ComptesClientsContext);
  const [filtreCompte, setFiltreCompte] = React.useState('tous');
  const clientsFiltresCompte = (!ctxComptes?.dispo || filtreCompte === 'tous')
    ? filteredClients
    : filteredClients.filter(c => {
        const st = statutCompteDe(ctxComptes.statuts[String(c.id)], !!ctxComptes.differes?.has(String(c.id)));
        return filtreCompte === 'a_activer' ? (st === 'attente' || st === 'aucun') : st === filtreCompte;
      });

  // 2026-10-23 : filtre par financement (table « financements » ; absente → pas de filtre).
  const financementsOrg = useFinancementsOrg(currentOrgId, expandedClientId);
  const [filtreFinancement, setFiltreFinancement] = React.useState('tous');
  const clientsFiltres = (!financementsOrg.dispo || filtreFinancement === 'tous')
    ? clientsFiltresCompte
    : clientsFiltresCompte.filter(c => {
        const t = (financementsOrg.parClient[String(c.id)] || []).map(f => f.type);
        return filtreFinancement === 'aucun' ? t.length === 0 : t.includes(filtreFinancement);
      });

  const clientsGroupedByFormateur = clientsFiltres.reduce((acc, client) => {
    const fId = client.formateur_id || 'unassigned';
    if (!acc[fId]) acc[fId] = [];
    acc[fId].push(client);
    return acc;
  }, {});

  if (expandedClientId) {
    const selectedClient = clients.find(c => c.id === expandedClientId);
    if (selectedClient) {
      return (
        <ClientDetailView
          client={selectedClient} formateurs={formateurs} assignFormateur={assignFormateur}
          handleModuleChange={handleModuleChange} modules={modules} supabase={supabase}
          fetchUtilisateurs={fetchUtilisateurs} onBack={() => setExpandedClientId(null)}
          sessions={sessions} fetchSessions={fetchSessions} documents={documents} fetchDocuments={fetchDocuments}
          handleGenerateDocx={handleGenerateDocx} documentTemplates={documentTemplates}
          pedagogicalResources={pedagogicalResources}
          handleDownloadResource={handleDownloadResource}
          handleUploadExerciseResponse={handleUploadExerciseResponse}
          generateSessions={generateSessions}
          handleDeleteClient={handleDeleteClient}
          setIsSessionItemModalOpen={setIsSessionItemModalOpen}
          setTargetSessionForAddition={setTargetSessionForAddition}
          setViewingSession={setViewingSession}
          handleDownloadPDF={handleDownloadPDF}
          updateSessionDate={updateSessionDate}
          updateSessionTime={updateSessionTime}
          onTimeChange={onTimeChange}
          onSaveTimes={onSaveTimes}
          editedTimes={editedTimes}
          lastModifiedSessionId={lastModifiedSessionId}
          setDocSettingsTarget={setDocSettingsTarget}
          setIsDocSettingsOpen={setIsDocSettingsOpen}
          setViewingDocId={setViewingDocId}
          handleMoveSessionItem={handleMoveSessionItem}
          handleSaveCorrection={handleSaveCorrection}
          handleAddAdminBloc={handleAddAdminBloc}
          handleRegenerateVisualDocs={handleRegenerateVisualDocs}
          instantiateDocument={instantiateDocument}
          currentOrgId={currentOrgId}
          groupes={groupes}
          onOpenGroupe={onOpenGroupe}
          depotsNonLus={depotsNonLus}
          estPro={estPro}
          nomUtilisateur={nomUtilisateur}
          onDepotsVus={onDepotsVus}
          ongletInitial={ongletInitial}
          onOngletInitialUtilise={onOngletInitialUtilise}
        />
      );
    }
  }

  return (
    <div className="space-y-8 animate-fade-in max-w-7xl mx-auto">
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-2 h-6 bg-indigo-600 rounded-full mr-3"></span> Clients
        </h2>

        <div className="bg-indigo-50 border border-indigo-100 p-5 md:p-6 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-lg">
              <Plus size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 leading-tight">Nouveau Client</h3>
              <p className="text-sm text-gray-500">Invitez un nouveau client (bénéficiaire) par email.</p>
            </div>
          </div>
          <button
            onClick={() => { setInviteDefaultRole('client'); setIsInviteModalOpen(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-8 py-3.5 rounded-2xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 transform active:scale-95"
          >
            <Plus size={20} /> Ajouter un client
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100 mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <span className="w-2 h-6 bg-gray-900 rounded-full mr-3"></span> Vue d'ensemble (Tri par Formateur)
          </h2>
          <div className="relative w-full md:w-72">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input
              type="text"
              placeholder="Rechercher un client (nom, prénom)..."
              value={clientSearchTerm}
              onChange={e => setClientSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50"
            />
            {clientSearchTerm && (
              <button onClick={() => setClientSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
        </div>
        <BandeauComptesAActiver clientIds={clients.map(c => c.id)} onVoir={() => setFiltreCompte('a_activer')} />
        <FiltresComptes clients={filteredClients} valeur={filtreCompte} onChange={setFiltreCompte} />
        {financementsOrg.dispo && <FiltreFinancement clients={clientsFiltresCompte} parClient={financementsOrg.parClient} valeur={filtreFinancement} onChange={setFiltreFinancement} />}
        <DossiersArchives archives={clientsArchives} onRestaurer={onRestaurerDossier} />
        {clientSearchTerm && filteredClients.length === 0 && (
          <p className="text-sm text-gray-500 italic py-4">Aucun client trouvé pour "{clientSearchTerm}".</p>
        )}
        {filtreFinancement !== 'tous' && clientsFiltresCompte.length > 0 && clientsFiltres.length === 0 && (
          <p className="text-sm text-gray-500 italic py-4">Aucun client avec ce financement.</p>
        )}
        {filtreCompte !== 'tous' && filteredClients.length > 0 && clientsFiltresCompte.length === 0 && (
          <p className="text-sm text-gray-500 italic py-4">Aucun client dans cette catégorie.</p>
        )}
        {chargement && clients.length === 0 && <SqueletteChargement texte="Chargement de vos clients…" />}
        <div className="space-y-8">
          {Object.keys(clientsGroupedByFormateur).map(fId => {
            const formateur = formateurs.find(f => String(f.id) === String(fId));
            const formateurName = formateur ? `${formateur.nom || ''} ${formateur.prenom || ''}`.trim() : 'NON ASSIGNÉ';
            return (
              <div key={fId} className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-xs mr-3">{formateurName.charAt(0)}</span>
                  <h3 className="font-bold text-gray-900 uppercase tracking-wider text-sm">{formateurName}</h3>
                  <span className="ml-auto text-xs font-bold text-gray-500">{clientsGroupedByFormateur[fId].length} bénéficiaire(s)</span>
                </div>
                <ul className="divide-y divide-gray-100">
                  {clientsGroupedByFormateur[fId].map(client => (
                    <li key={client.id} className="p-4 hover:bg-gray-50/50 transition-colors flex items-center justify-between cursor-pointer" onClick={() => setExpandedClientId(client.id)}>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white border border-gray-200 text-gray-600 rounded-xl flex items-center justify-center mr-4 font-bold shadow-sm">{client.nom ? client.nom.charAt(0) : '?'}</div>
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900 leading-tight">{client.nom || client.nomcomplet_client || 'Sans Nom'}</span>
                          <span className="text-xs text-gray-500">{client.email || 'Email non renseigné'}</span>
                          {typesFinancementDe(financementsOrg.parClient[String(client.id)]).length > 0 && (
                            <span className="text-[11px] font-bold text-emerald-700">💶 {typesFinancementDe(financementsOrg.parClient[String(client.id)]).join(' + ')}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 md:gap-4">
                        <ActionCompteClient client={client} />
                        <BadgeCompteClient clientId={client.id} fallback={
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${client.status === 'Nouveau' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                            {client.status || 'Actif'}
                          </span>
                        } />
                        <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const FormateurDetailView = ({
  formateur, onBack, supabase, fetchUtilisateurs, modules, clients,
  handleDeleteFormateur, documents, documentTemplates, handleGenerateDocx,
  setViewingDocId, fetchDocuments, isSelfAdmin = false, currentOrgId
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = React.useState(false);
  const [docToDelete, setDocToDelete] = React.useState(null);
  // NOUVEAU (2026-09-04) : "Documents du dossier formateur" — ajout libre d'un document par l'admin,
  // sans passer par un modèle de signature (ex: carte d'identité, diplôme scanné, attestation...).
  const [showFormateurDocModal, setShowFormateurDocModal] = React.useState(false);
  const [formateurDocFile, setFormateurDocFile] = React.useState(null);
  const [formateurDocName, setFormateurDocName] = React.useState('');
  const [formateurDocType, setFormateurDocType] = React.useState('Administratif');
  const [isUploadingFormateurDoc, setIsUploadingFormateurDoc] = React.useState(false);

  // FIX (2026-09-04, corrigé) : remonte en haut de page à l'ouverture de la fiche formateur — même
  // correctif que ClientDetailView (voir commentaire là-bas) : on cible le vrai conteneur défilant
  // (<main overflow-y-auto>), pas window.
  React.useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, []);
  React.useEffect(() => { fetchDocuments(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteDoc = async () => {
    if (!docToDelete) return;
    // Scopé strictement à l'organisme courant — plus aucune tolérance pour les lignes historiques
    // sans organisation_id (elles ont été rattachées à leur organisme d'origine par migration SQL).
    if (!currentOrgId) { toast.error('Organisme introuvable — suppression annulée par sécurité.'); return; }
    if (!(await peutSupprimerDocument(docToDelete.id))) { setDocToDelete(null); return; } // lot 2
    const delQuery = supabase.from('documents').delete().eq('id', docToDelete.id).eq('organisation_id', currentOrgId);
    const { error } = await delQuery;
    if (error) { toast.error('Erreur lors de la suppression : ' + error.message); console.error('[handleDeleteDoc]', error); }
    else { toast.success('Document supprimé.'); }
    await fetchDocuments();
    setDocToDelete(null);
  };

  // NOUVEAU (2026-09-04) : ajoute un document librement au dossier de CE formateur, sans signature —
  // même logique que handleUploadAdminDoc (ClientDetailView), adaptée à assigned_formateur_id.
  const handleUploadFormateurDoc = async () => {
    if (!formateurDocFile || !formateurDocName.trim()) { toast.error('Veuillez renseigner un nom et choisir un fichier.'); return; }
    setIsUploadingFormateurDoc(true);
    try {
      const ext = formateurDocFile.name.split('.').pop();
      const safeN = formateurDocName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `dossier_formateur/${formateur.id}/${Date.now()}_${safeN}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, formateurDocFile);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);

      // user_id volontairement absent : ce document n'est lié à aucun client, seulement au formateur
      // lui-même (assigned_formateur_id) — c'est ce qui le distingue des documents de dossier client.
      const { error: insertError } = await supabase.from('documents').insert([{
        nom: formateurDocName.trim(),
        type_document: formateurDocType,
        url: publicUrl,
        assigned_formateur_id: formateur.id,
        organisation_id: formateur.organisation_id || currentOrgId,
        visible_client: false,
        visible_formateur: true,
        signe_par_client: false,
        signe_par_formateur: false,
      }]);
      if (insertError) throw insertError;

      toast.success('Document ajouté au dossier du formateur.');
      setShowFormateurDocModal(false);
      setFormateurDocFile(null);
      setFormateurDocName('');
      setFormateurDocType('Administratif');
      await fetchDocuments();
    } catch (e) {
      console.error('Erreur upload document dossier formateur:', e);
      toast.error("Erreur lors de l'ajout : " + e.message);
    }
    setIsUploadingFormateurDoc(false);
  };
  const [legalInfo, setLegalInfo] = React.useState({
    nom: formateur.nom || '',
    formateur_siret: formateur.formateur_siret || formateur.siret || '',
    formateur_nda: formateur.formateur_nda || formateur.nda || '',
    adresse_formateur: formateur.adresse_formateur || formateur.adresse_pro || formateur.adresse_client || '',
    adresse_session: formateur.adresse_session || '',
    region: formateur.region || '',
    email: formateur.email || '',
    telephone: formateur.telephone || '',
    compagnie_assurance: formateur.compagnie_assurance || '',
    numero_assurance_rcp: formateur.numero_assurance_rcp || ''
  });
  const [sameAddress, setSameAddress] = React.useState(
    !formateur.adresse_session || formateur.adresse_session === (formateur.adresse_formateur || formateur.adresse_pro || formateur.adresse_client || '')
  );

  // FIX (2026-09-04) : exclut les documents des CLIENTS de ce formateur (ils ont un user_id, jamais
  // les documents personnels du formateur — voir handleGenerateDocx) — cette section est réservée à
  // ses propres documents administratifs (contrat, NDA...), pas à ceux de ses clients.
  const trainerDocs = documents ? documents.filter(d => d.assigned_formateur_id === formateur.id && !d.user_id && !isDossierDoc(d)) : [];

  const handleSave = async () => {
    setIsSaving(true);
    const { error } = await supabase
      .from('utilisateurs')
      .update({
        nom: legalInfo.nom,
        formateur_siret: legalInfo.formateur_siret,
        formateur_nda: legalInfo.formateur_nda,
        adresse_formateur: legalInfo.adresse_formateur,
        adresse_session: sameAddress ? legalInfo.adresse_formateur : legalInfo.adresse_session,
        region: legalInfo.region,
        ...(String(legalInfo.email || '').trim() ? { email: String(legalInfo.email).trim() } : {}),
        telephone: legalInfo.telephone,
        compagnie_assurance: legalInfo.compagnie_assurance,
        numero_assurance_rcp: legalInfo.numero_assurance_rcp
      })
      .eq('id', formateur.id);

    if (error) {
      toast.error("Erreur lors de la sauvegarde : " + error.message);
    } else {
      await fetchUtilisateurs();
      toast.success("Informations légales enregistrées !");
    }
    setIsSaving(false);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <button onClick={onBack} className="text-gray-500 hover:text-gray-900 font-bold flex items-center mb-4 transition-colors">
        <ChevronLeft className="w-5 h-5 mr-1" /> Retour à la liste des formateurs
      </button>

      <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-6 mb-8 pb-8 border-b border-gray-100">
          <div className="w-20 h-20 bg-violet-100 text-violet-700 rounded-3xl flex items-center justify-center font-bold text-3xl shadow-inner">
            {formateur.nom ? formateur.nom.charAt(0) : '?'}
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{formateur.nom}</h2>
            <p className="text-gray-500 text-lg">Profil Formateur & Informations Légales</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="text-violet-600" /> Identité Professionnelle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Raison Sociale / Nom complet</label>
                <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all font-medium"
                  value={legalInfo.nom} onChange={e => setLegalInfo({ ...legalInfo, nom: e.target.value })} placeholder="Ex: Matthys Coaching EURL" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">SIRET</label>
                  <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                    value={legalInfo.formateur_siret} onChange={e => setLegalInfo({ ...legalInfo, formateur_siret: e.target.value })} placeholder="14 chiffres" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">NDA (Qualiopi)</label>
                  <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                    value={legalInfo.formateur_nda} onChange={e => setLegalInfo({ ...legalInfo, formateur_nda: e.target.value })} placeholder="N° Déclaration" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Assurance RCP — Compagnie</label>
                <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                  value={legalInfo.compagnie_assurance} onChange={e => setLegalInfo({ ...legalInfo, compagnie_assurance: e.target.value })} placeholder="Ex: AXA, MAIF, Hiscox..." />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Assurance RCP — N° de contrat</label>
                <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                  value={legalInfo.numero_assurance_rcp} onChange={e => setLegalInfo({ ...legalInfo, numero_assurance_rcp: e.target.value })} placeholder="N° de contrat RCP" />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Plus className="text-indigo-500" /> Coordonnées & Contact
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Adresse Siège Social</label>
                <AddressAutocomplete
                  value={legalInfo.adresse_formateur}
                  onChange={val => setLegalInfo({ ...legalInfo, adresse_formateur: val })}
                  onSelect={({ label, region }) => setLegalInfo(prev => ({ ...prev, adresse_formateur: label, region: region || prev.region }))}
                  placeholder="Ex : 12 Rue de la Paix, 75001 Paris"
                  className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sameAddress"
                  checked={sameAddress}
                  onChange={e => setSameAddress(e.target.checked)}
                  className="w-4 h-4 accent-violet-600 cursor-pointer rounded"
                />
                <label htmlFor="sameAddress" className="text-sm text-gray-600 cursor-pointer select-none">
                  Même adresse pour les sessions de formation
                </label>
              </div>
              {!sameAddress && (
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Adresse de Pratique</label>
                  <AddressAutocomplete
                    value={legalInfo.adresse_session}
                    onChange={val => setLegalInfo({ ...legalInfo, adresse_session: val })}
                    placeholder="Ex : 12 Rue de la Paix, 75001 Paris"
                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Région</label>
                <input list="region-list-formateur" className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" value={legalInfo.region} onChange={e => setLegalInfo({ ...legalInfo, region: e.target.value })} placeholder="Ex : Bretagne" autoComplete="off" />
                <datalist id="region-list-formateur">{FRENCH_REGIONS.map(r => <option key={r} value={r} />)}</datalist>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Email</label>
                  <input
                    className={`w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all ${isSelfAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                    value={legalInfo.email}
                    disabled={isSelfAdmin}
                    title={isSelfAdmin ? "Cet email est lié à votre connexion — modifiez-le depuis vos paramètres de compte." : undefined}
                    onChange={e => setLegalInfo({ ...legalInfo, email: e.target.value })}
                    placeholder="Email pro"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Téléphone</label>
                  <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
                    value={legalInfo.telephone} onChange={e => setLegalInfo({ ...legalInfo, telephone: e.target.value })} placeholder="06..." />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap justify-end items-center gap-4">
          {isSelfAdmin ? (
            <p className="text-xs text-gray-400 italic max-w-xs text-right">
              Ceci est votre propre compte administrateur — il ne peut pas être supprimé depuis cet écran.
            </p>
          ) : (
            <button
              onClick={() => setIsConfirmDeleteOpen(true)}
              className="px-6 py-4 text-red-600 font-bold hover:bg-red-50 rounded-2xl transition-all flex items-center gap-2"
            >
              <Trash2 size={20} />
              Supprimer le formateur
            </button>
          )}
          <button onClick={handleSave} disabled={isSaving} className="bg-violet-700 hover:bg-violet-700 text-white font-bold py-4 px-10 rounded-2xl shadow-xl transition-all flex items-center gap-3 disabled:opacity-50">
            <Save size={20} />
            {isSaving ? 'Enregistrement...' : 'Enregistrer les informations légales'}
          </button>
        </div>

        {!isSelfAdmin && (
          <DeleteConfirmationModal
            isOpen={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onConfirm={() => {
              setIsConfirmDeleteOpen(false);
              handleDeleteFormateur(formateur.id);
              onBack();
            }}
            itemName={legalInfo.nom || "ce formateur"}
            title="Supprimer ce formateur ?"
          />
        )}
        <div className="mt-12 pt-12 border-t border-gray-100">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Archive className="text-violet-600" /> Documents Administratifs (Contrats / NDA...)
          </h3>
          
          <div className="bg-gray-50 rounded-[32px] p-6 border border-gray-100 space-y-8">

            {/* ── Bibliothèque de modèles disponibles ── */}
            {(() => {
              // Filtre : seuls les modèles avec destination='formateur' apparaissent dans la fiche formateur
              // FIX (2026-09-04) : la destination est stockée en chaîne "role1,role2..." dès qu'il y a
              // plusieurs destinataires (ex: "formateur,organisme" pour un contrat signé par les deux) —
              // une égalité stricte avec 'formateur' ratait donc tout modèle combinant formateur +
              // organisme. On vérifie maintenant l'appartenance du rôle 'formateur', en excluant les
              // modèles qui concernent aussi le client (ceux-là relèvent du dossier client, pas de
              // cette section réservée aux documents formateur ↔ organisme).
              const availableTpls = Object.entries(documentTemplates || {}).filter(([, tpl]) => {
                const tplRoles = parseDestinationRoles(tpl.destination);
                return tplRoles.includes('formateur') && !tplRoles.includes('client');
              });
              return (
                <div>
                  <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Envoyer un document pour signature</h4>
                  {availableTpls.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Aucun modèle disponible — ajoutez-en un depuis l'onglet Formateurs.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {availableTpls.map(([key]) => {
                        // Le doc envoyé est nommé "{key} - {nom_formateur}" — on cherche par préfixe
                        const alreadySent = trainerDocs.some(d => d.nom && (d.nom === key || d.nom.startsWith(key + ' - ')));
                        return (
                          <div key={key} className="flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-violet-50 text-violet-600 rounded-xl flex items-center justify-center shrink-0">
                                <FileText size={16} />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900 text-sm">{key}</p>
                                {alreadySent && <p className="text-xs text-amber-600 font-bold">⚡ Déjà envoyé — renvoyer créera un nouvel exemplaire</p>}
                              </div>
                            </div>
                            <button
                              onClick={() => handleGenerateDocx(null, key, true, formateur.id)}
                              className="flex items-center gap-2 bg-violet-700 hover:bg-violet-700 text-white px-4 py-2 rounded-xl text-xs font-black shadow-sm transition-all transform active:scale-95"
                            >
                              <Send size={13} /> Envoyer pour signature
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Suivi des documents envoyés ── */}
            <div className="space-y-3">
              <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Suivi des documents envoyés</h4>
              {trainerDocs.length > 0 ? trainerDocs.map(doc => {
                const isSigned = doc.signe_par_formateur || doc.statut === 'Signé';
                return (
                <div key={doc.id} className={`bg-white p-4 rounded-2xl border flex items-center justify-between group transition-all shadow-sm ${isSigned ? 'border-green-100 hover:border-green-300' : 'border-gray-100 hover:border-violet-200'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSigned ? 'bg-green-50 text-green-600' : 'bg-violet-50 text-violet-700'}`}>
                      {isSigned ? <FileCheck size={20} /> : <FileText size={20} />}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{doc.nom}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-400 font-bold uppercase">{doc.created_at ? (() => { const d = new Date(doc.created_at); return isNaN(d) ? '—' : d.toLocaleDateString('fr-FR'); })() : '—'}</span>
                        <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded-md ${isSigned ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {isSigned ? 'Signé' : 'En attente de signature'}
                        </span>
                      </div>
                      {isSigned && doc.date_signature_formateur && (
                        <p className="text-xs text-green-600 mt-0.5">
                          Signé le {new Date(doc.date_signature_formateur).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setViewingDocId(doc.id)}
                      className="p-2.5 text-gray-400 hover:text-violet-700 hover:bg-violet-50 rounded-xl transition-all"
                      title="Consulter le document original"
                    >
                      <Eye size={20} />
                    </button>
                    {isSigned && doc.signed_pdf_url && (
                      <button
                        onClick={() => handleDownloadNamedFile({ url: doc.signed_pdf_url, nom: doc.nom }, formateur.nom)}
                        className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl text-xs font-bold transition-all shadow-sm"
                        title="Télécharger le PDF signé"
                      >
                        <Download size={14} /> PDF signé
                      </button>
                    )}
                    <button
                      onClick={() => setDocToDelete(doc)}
                      className="p-2 text-red-400 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all border border-red-200 hover:border-red-400"
                      title="Supprimer ce document"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                );
              }) : (
                <p className="text-gray-400 italic text-sm py-4">Aucun document généré pour le moment.</p>
              )}

              <DeleteConfirmationModal
                isOpen={!!docToDelete}
                onClose={() => setDocToDelete(null)}
                onConfirm={handleDeleteDoc}
                itemName={docToDelete?.nom || 'ce document'}
                title="Supprimer ce document ?"
              />
            </div>

            {/* ── NOUVEAU (2026-09-04) : Documents du dossier formateur (ajout libre, sans signature) ── */}
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Documents du dossier formateur</h4>
                  <p className="text-[11px] text-gray-400 mt-0.5">Pièces administratives archivées sans demande de signature (assurance, diplôme...).</p>
                </div>
                <button
                  onClick={() => setShowFormateurDocModal(true)}
                  className="bg-violet-700 hover:bg-violet-800 text-white text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 transition-all shadow-sm shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" /> Ajouter un document
                </button>
              </div>
              {documents.filter(d => d.assigned_formateur_id === formateur.id && !d.user_id && isDossierDoc(d)).length === 0 ? (
                <p className="text-sm text-gray-400 italic">Aucun document dans le dossier de ce formateur.</p>
              ) : (
                <div className="space-y-2">
                  {documents.filter(d => d.assigned_formateur_id === formateur.id && !d.user_id && isDossierDoc(d)).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-100 group">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                        <div className="min-w-0">
                          <span className="text-sm font-bold text-gray-700 truncate block">{doc.nom}</span>
                          <span className="text-[11px] font-black uppercase tracking-widest text-violet-600">{doc.type_document || 'Autre'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openSecureStorageFile(doc.url)}
                          className="bg-white text-violet-700 hover:bg-violet-600 hover:text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-violet-200 transition-all"
                        >
                          Ouvrir
                        </button>
                        <button
                          onClick={() => handleDownloadNamedFile(doc, formateur.nom)}
                          className="text-gray-400 hover:text-violet-700 hover:bg-violet-50 p-1.5 rounded-lg transition-all"
                          title="Télécharger sous le nom indiqué"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDocToDelete(doc)}
                          className="text-gray-300 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100"
                          title="Supprimer ce document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showFormateurDocModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
                    <h4 className="text-base font-black text-gray-900 mb-1">Ajouter un document au dossier</h4>
                    <p className="text-xs text-gray-400 mb-4">Ce fichier sera archivé dans le dossier administratif de {formateur.nom || 'ce formateur'}, sans demande de signature.</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Fichier</label>
                        <input
                          type="file"
                          onChange={(e) => {
                            const f = e.target.files[0] || null;
                            setFormateurDocFile(f);
                            if (f && !formateurDocName.trim()) {
                              setFormateurDocName(f.name.replace(/\.[^/.]+$/, ''));
                            }
                          }}
                          className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Nom du document</label>
                        <input
                          type="text"
                          value={formateurDocName}
                          onChange={(e) => setFormateurDocName(e.target.value)}
                          placeholder="Ex : Attestation d'assurance RCP"
                          className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Type de document</label>
                        <select
                          value={formateurDocType}
                          onChange={(e) => setFormateurDocType(e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                        >
                          <option value="Administratif">Administratif</option>
                          <option value="Contrat">Contrat</option>
                          <option value="Mission">Mission</option>
                          <option value="Pièce justificative">Pièce justificative</option>
                          <option value="Autre">Autre</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-5">
                      <button
                        onClick={() => { setShowFormateurDocModal(false); setFormateurDocFile(null); setFormateurDocName(''); setFormateurDocType('Administratif'); }}
                        className="flex-1 text-gray-500 hover:text-gray-800 font-bold text-sm py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleUploadFormateurDoc}
                        disabled={isUploadingFormateurDoc || !formateurDocFile || !formateurDocName.trim()}
                        className="flex-1 bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white font-bold text-sm py-2.5 rounded-xl transition-all"
                      >
                        {isUploadingFormateurDoc ? 'Envoi...' : 'Ajouter au dossier'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Liste des clients assignés */}
      <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm mt-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Users className="text-violet-600" /> Clients Assignés
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.filter(c => c.formateur_id === formateur.id).map(client => {
            const assignedModule = modules.find(m => String(m.id) === String(client.module_id));
            return (
              <div key={client.id} className="p-4 border border-gray-50 rounded-2xl bg-gray-50/50 hover:bg-white hover:border-violet-200 transition-all group">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center font-bold text-gray-700 shadow-sm group-hover:bg-violet-50 transition-colors">
                    {client.nom ? client.nom.charAt(0) : '?'}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{client.nom}</h4>
                    <p className="text-xs text-gray-500 font-medium">{client.email}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <span className={`px-2 py-1 rounded-lg text-xs font-bold ${assignedModule ? 'bg-violet-50 text-violet-700' : 'bg-gray-100 text-gray-400'}`}>
                    {assignedModule ? assignedModule.nom : 'Pas de module assigné'}
                  </span>
                </div>
              </div>
            );
          })}
          {clients.filter(c => c.formateur_id === formateur.id).length === 0 && (
            <p className="text-gray-400 italic text-sm py-4">Aucun client n'est encore assigné à ce formateur.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminFormateursView = ({
  clients, formateurs, documents, expandedClientId, setExpandedClientId,
  supabase, fetchUtilisateurs, fetchDocuments, activeTab, setActiveTab,
  modules, sessions, handleDownloadResource, handleDeleteFormateur,
  documentTemplates, handleGenerateDocx, setViewingDocId,
  handleUploadDocxTemplate, newTemplateName, setNewTemplateName, adminSelfId,
  currentOrgId, setIsInviteModalOpen, setInviteDefaultRole
}) => {
  const [selectedFormateurId, setSelectedFormateurId] = React.useState(null);
  const [selectedClientSummary, setSelectedClientSummary] = React.useState(null);
  // Recherche + tri alphabétique de la liste des formateurs (ajouté 2026-08-05) — la liste
  // était auparavant affichée dans l'ordre brut renvoyé par la base (ordre de création), sans
  // moyen de retrouver rapidement un formateur une fois qu'il y en a beaucoup.
  const [formateurSearchQuery, setFormateurSearchQuery] = React.useState('');
  const sortedFilteredFormateurs = React.useMemo(() => {
    const q = formateurSearchQuery.trim().toLowerCase();
    return [...(formateurs || [])]
      .filter(f => !q || (f.nom || '').toLowerCase().includes(q) || (f.email || '').toLowerCase().includes(q))
      .sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }));
  }, [formateurs, formateurSearchQuery]);

  if (selectedFormateurId) {
    const formateur = formateurs.find(f => f.id === selectedFormateurId);
    if (formateur) {
      return (
        <FormateurDetailView
          formateur={formateur}
          onBack={() => setSelectedFormateurId(null)}
          supabase={supabase}
          fetchUtilisateurs={fetchUtilisateurs}
          fetchDocuments={fetchDocuments}
          modules={modules}
          clients={clients}
          handleDeleteFormateur={handleDeleteFormateur}
          documents={documents}
          documentTemplates={documentTemplates}
          handleGenerateDocx={handleGenerateDocx}
          currentOrgId={currentOrgId}
          setViewingDocId={setViewingDocId}
          isSelfAdmin={adminSelfId != null && String(formateur.id) === String(adminSelfId)}
        />
      );
    }
  }

  // --- Modal de Résumé de Planning pour l'Admin ---
  const renderClientSummary = () => {
    if (!selectedClientSummary) return null;
    // AJOUT (2026-09-19) : exclut les exercices synthétiques de début/fin (numero_seance: null) de ce
    // récapitulatif d'émargements Qualiopi — ce ne sont pas des séances planifiées à émarger.
    const clientSessions = sessions.filter(s => s.client_id === selectedClientSummary.id && s.numero_seance !== null && s.numero_seance !== undefined).sort((a, b) => a.numero_seance - b.numero_seance);

    return (
      <div className="fixed inset-0 bg-gray-950/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 text-indigo-700 rounded-2xl flex items-center justify-center font-black text-xl">
                {selectedClientSummary.nom?.charAt(0)}
              </div>
              <div>
                <h3 className="text-xl font-black text-gray-900 leading-none">Supervision : {selectedClientSummary.nom}</h3>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-1.5">Planning & Émargements Qualiopi</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedClientSummary(null)}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/30">
            {clientSessions.length > 0 ? (
              <table className="w-full text-left border-collapse bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 text-sm">
                <thead>
                  <tr className="bg-gray-100/50 text-xs font-black uppercase text-gray-400 tracking-widest">
                    <th className="p-4">Séance</th>
                    <th className="p-4">Date & Heures</th>
                    <th className="p-4">Activité</th>
                    <th className="p-4 text-center">Émargement Client</th>
                    <th className="p-4 text-center">Émargement Formateur</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {clientSessions.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-4">
                        <span className="font-black text-gray-900">S{s.numero_seance}</span>
                        <span className="ml-2 text-gray-500 font-medium">{s.nom}</span>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-gray-700">{s.date ? new Date(s.date).toLocaleDateString() : 'Non planifié'}</div>
                        <div className="text-xs text-gray-400 font-bold">{s.heure_debut || '--:--'} - {s.heure_fin || '--:--'}</div>
                      </td>
                      <td className="p-4">
                        <span className="text-xs px-2 py-0.5 rounded-lg bg-gray-100 font-black uppercase text-gray-400 tracking-tighter">
                          {s.type_activite}
                        </span>
                      </td>
                      {/* AFFICHAGE (2026-10-20) : « — » quand l'élément n'attend pas de signature. */}
                      <td className="p-4 text-center">
                        {signaturesAttendues(s).client ? (
                        <span className={`px-2 py-1 rounded-lg text-[11px] font-black uppercase ${s.statut_client === 'Signé' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {s.statut_client === 'Signé' ? 'OK ✓' : 'Attente'}
                        </span>
                        ) : <span className="text-gray-300 font-bold">—</span>}
                      </td>
                      <td className="p-4 text-center">
                        {signaturesAttendues(s).formateur ? (
                        <span className={`px-2 py-1 rounded-lg text-[11px] font-black uppercase ${s.statut_formateur === 'Signé' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {s.statut_formateur === 'Signé' ? 'OK ✓' : 'Attente'}
                        </span>
                        ) : <span className="text-gray-300 font-bold">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
                <p className="text-gray-400 italic">Aucune séance n'a été générée pour ce client.</p>
              </div>
            )}
          </div>
          <div className="p-4 border-t border-gray-100 bg-gray-50 text-right shrink-0">
            <button
               onClick={() => setSelectedClientSummary(null)}
               className="bg-gray-900 text-white font-bold py-2.5 px-8 rounded-xl hover:bg-black transition-all"
            >
              Fermer la supervision
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-7xl mx-auto">

      {/* Ajouté 2026-08-05 : bouton d'invitation dédié aux formateurs, pré-sélectionnant le rôle
          "Formateur" — auparavant, le seul bouton d'invitation ("Nouveau Membre") se trouvait sur
          l'onglet Clients, avec un sélecteur de rôle à changer manuellement. */}
      <div className="bg-violet-50 border border-violet-100 p-5 md:p-6 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-violet-600 text-white rounded-2xl flex items-center justify-center shadow-lg">
            <Plus size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 leading-tight">Nouveau Formateur</h3>
            <p className="text-sm text-gray-500">Invitez un nouveau formateur par email.</p>
          </div>
        </div>
        <button
          onClick={() => { setInviteDefaultRole('formateur'); setIsInviteModalOpen(true); }}
          className="bg-violet-600 hover:bg-violet-700 text-white font-bold px-8 py-3.5 rounded-2xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 transform active:scale-95"
        >
          <Plus size={20} /> Ajouter un formateur
        </button>
      </div>

      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-2 h-6 bg-violet-600 rounded-full mr-3"></span> Liste des Formateurs
          <span className="ml-3 text-sm font-bold text-violet-600 bg-violet-50 px-2.5 py-0.5 rounded-full">
            {formateurs.length}
          </span>
        </h2>
        <div className="flex border-b border-gray-200 mb-6 font-sans">
          <button onClick={() => setActiveTab('clients')} className={`px-6 py-3 font-bold text-sm transition-all border-b-2 ${activeTab === 'clients' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Clients</button>
          <button onClick={() => setActiveTab('formateurs')} className={`px-6 py-3 font-bold text-sm transition-all border-b-2 ${activeTab === 'formateurs' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Formateurs</button>
        </div>

        <div className="relative mb-6">
          <Search className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={formateurSearchQuery}
            onChange={e => setFormateurSearchQuery(e.target.value)}
            placeholder="Rechercher un formateur par nom ou email..."
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-violet-300 bg-white"
          />
        </div>

        {sortedFilteredFormateurs.length === 0 && (
          <div className="py-10 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-sm text-gray-400 italic">Aucun formateur ne correspond à "{formateurSearchQuery}".</p>
          </div>
        )}

        <ul className="space-y-6">
          {sortedFilteredFormateurs.map(f => {
            const sesClients = clients.filter(c => c.formateur_id === f.id);
            const isExpanded = expandedClientId === f.id;
            return (
              <li key={f.id} className={`p-6 border rounded-2xl transition-all ${isExpanded ? 'border-violet-200 bg-violet-50/10' : 'border-gray-100 bg-gray-50'}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                  <div className="flex items-center cursor-pointer hover:bg-white p-2 rounded-xl transition-all flex-1" onClick={() => setSelectedFormateurId(f.id)}>
                    <div className="w-12 h-12 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center mr-4 font-bold text-xl shadow-sm">{f.nom ? f.nom.charAt(0) : '?'}</div>
                    <div className="flex flex-col flex-1">
                      <div className="flex items-center justify-between pr-4">
                        <div>
                          <span className="font-bold text-gray-900 text-lg hover:text-violet-700 transition-colors">{f.nom}</span>
                          {adminSelfId != null && String(f.id) === String(adminSelfId) && (
                            <span className="ml-2 text-xs font-black uppercase px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 align-middle">Vous — Admin</span>
                          )}
                          <span className="text-sm text-gray-500 block">{f.email}</span>
                        </div>
                        <span className="text-violet-400 bg-violet-50 px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Voir profil <ChevronRight size={14} />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 md:mt-0 ml-4 border-l border-gray-100 pl-4">
                    <button onClick={() => setExpandedClientId(isExpanded ? null : f.id)} className="text-gray-400 hover:text-gray-600 transition-colors">
                      {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-6 pt-6 border-t border-gray-200 animate-slide-up space-y-6">
                    <div>
                      <h4 className="text-xs font-bold text-gray-500 mb-4 uppercase tracking-wider flex items-center">
                        <Users className="w-3.5 h-3.5 mr-2" /> Clients Assignés
                      </h4>
                      {sesClients.length > 0 ? (
                        <div className="overflow-hidden border border-gray-100 rounded-xl shadow-sm">
                          <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase tracking-widest">Client</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase tracking-widest">Module</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase tracking-widest text-center">Statut</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 bg-white">
                              {sesClients.map(client => {
                                const clientModule = modules?.find(m => String(m.id) === String(client.module_id));
                                return (
                                  <tr key={client.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="p-3">
                                      <div 
                                        className="font-bold text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer underline underline-offset-2 transition-colors"
                                        onClick={(e) => { e.stopPropagation(); setSelectedClientSummary(client); }}
                                      >
                                        {client.nom || client.nom_complet || "Client"}
                                      </div>
                                      <div className="text-xs text-gray-400">{client.email}</div>
                                    </td>
                                    <td className="p-3">
                                      <span className="text-xs font-medium text-gray-600">{clientModule?.nom || 'Non assigné'}</span>
                                    </td>
                                    <td className="p-3 text-center">
                                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${client.status === 'Nouveau' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'}`}>
                                        {client.status || 'Actif'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="py-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                          <p className="text-sm text-gray-400 italic">Aucun client assigné à ce formateur.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {renderClientSummary()}
    </div>
  );
};
const DroppableMomentZone = ({ id, children, className }) => {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`${className} transition-all ${isOver ? 'ring-2 ring-indigo-500 scale-[1.01]' : ''}`}>
      {children}
    </div>
  );
};

const DraggableGroupBlock = ({ resourceId, group, onDelete }) => {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({ id: `drag-grp-${resourceId}` });
  // CORRECTIF (2026-08-31) : même correctif que SessDragItem/FDragItemRow (transform jamais appliqué).
  const style = transform ? { transform: CSS.Translate.toString(transform), zIndex: 30, position: 'relative' } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`flex items-center justify-between p-3 rounded-xl border border-indigo-200 text-sm hover:bg-indigo-100 transition-all shadow-sm ${isDragging ? 'opacity-40 bg-indigo-50' : 'bg-white'}`}
    >
      {/* Drag handle — only this area activates drag */}
      <div className="flex items-center gap-3 flex-1 cursor-grab active:cursor-grabbing" {...listeners}>
        <Layout size={16} className="text-indigo-600" />
        <span className="font-bold text-indigo-900">Groupe de documents : {group?.nom || 'Groupe Inconnu'}</span>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(resourceId); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="text-gray-300 hover:text-red-500 transition-colors p-1 z-10 relative"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

// AJOUT (2026-09-30) : ligne d'un document/émargement/questionnaire à l'intérieur d'un dossier de
// séance, rendue glissable vers un autre dossier — même correctif transform que DraggableGroupBlock
// ci-dessus (sinon l'élément reste visuellement figé à l'écran pendant le glisser).
const DraggableStepResourceRow = ({ res, editingId, setEditingId, editValue, setEditValue, handleRenameResource, setSignatureSettingsTarget, setIsSignatureSettingsOpen, handleDeleteStepResource }) => {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({ id: `drag-res-${res.id}` });
  const style = transform ? { transform: CSS.Translate.toString(transform), zIndex: 30, position: 'relative' } : undefined;
  return (
    <div ref={setNodeRef} style={style} className={`flex items-center justify-between bg-gray-50/50 p-3 rounded-xl border border-gray-100 text-[11px] transition-all ${isDragging ? 'opacity-40' : ''}`}>
      <div className="flex items-center gap-2">
        <span {...attributes} {...listeners} className="text-gray-300 hover:text-indigo-500 cursor-grab active:cursor-grabbing" title="Glisser pour déplacer vers une autre séance">⠿</span>
        <span className="text-gray-400">
          {res.type === 'signature' ? '✍️' : res.type === 'document' ? '📄' : res.type === 'questionnaire' ? '📝' : '⚙️'}
        </span>
        <div className="flex flex-col flex-1">
          {editingId === res.id ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                className="bg-white border border-indigo-200 rounded text-[11px] p-0.5 font-bold text-gray-800 w-full outline-none"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleRenameResource(res.id, editValue);
                    setEditingId(null);
                  } else if (e.key === 'Escape') setEditingId(null);
                }}
              />
              <button onClick={() => { handleRenameResource(res.id, editValue); setEditingId(null); }} className="text-green-600 hover:text-green-700"><Check size={12} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group/restitle leading-none">
              <span className="font-bold text-gray-800">{res.titre}</span>
              <button
                onClick={() => { setEditingId(res.id); setEditValue(res.titre); }}
                className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/restitle:opacity-100 transition-opacity"
              >
                <Pencil size={10} />
              </button>
            </div>
          )}
          <span className="text-[11px] text-gray-400 uppercase">{res.type} {res.ressource_id ? `(${res.ressource_id})` : ''}</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {res.type === 'signature' && (
          <button
            onClick={() => { setSignatureSettingsTarget(res); setIsSignatureSettingsOpen(true); }}
            className="text-gray-300 hover:text-violet-600"
            title="Qui doit signer ?"
          >
            <Settings size={12} />
          </button>
        )}
        <button onClick={() => handleDeleteStepResource(res.id)} className="text-gray-300 hover:text-red-400">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
};

const IngenierieView = ({
  modules, handleAddModule, handleDeleteModule,
  newModuleName, setNewModuleName, newModuleSeances, setNewModuleSeances,
  handleUploadDocxTemplate, newTemplateName, setNewTemplateName,
  handleUploadResource, newResourceName, setNewResourceName, isUploadingResource,
  modelingModuleId, setModelingModuleId, moduleSessionTemplates, moduleStepResources, fetchModules,
  newStepTitle, setNewStepTitle, newStepActivity, setNewStepActivity, currentOrgId,
  selectedResourceId, setSelectedResourceId, pedagogicalResources, isAddingStep,
  setIsAddingStep, isAddingStepResource, setIsAddingStepResource, supabase,
  createSessionFolder, handleDeleteFolder, handleDeleteStepResource, handleAddStepResource,
  handleRenameFolder, handleRenameResource, handleRenameModule, handleAddModuleMomentResource, handleRedistributeModuleDocs, documentTemplates,
  handleUpdateStepResourceSignatures
}) => {
  const [isResourceModalOpen, setIsResourceModalOpen] = React.useState(false);
  const [activeFolderId, setActiveFolderId] = React.useState(null);
  // AJOUT (2026-09-25) : modale "Qui doit signer ?" pour éditer un émargement de module déjà créé.
  const [isSignatureSettingsOpen, setIsSignatureSettingsOpen] = React.useState(false);
  const [signatureSettingsTarget, setSignatureSettingsTarget] = React.useState(null);
  // AJOUT (2026-09-17) : édition inline du nom d'un module (même principe que editingId/editValue
  // plus bas pour les "dossiers de séance", mais état séparé pour éviter toute collision d'id entre
  // un module et un dossier de séance — les deux sont des entités différentes en base).
  const [editingModuleId, setEditingModuleId] = React.useState(null);
  const [editModuleValue, setEditModuleValue] = React.useState('');
  const [activeMoment, setActiveMoment] = React.useState(null);
  const [activeMomentModuleId, setActiveMomentModuleId] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');

  const [documentGroups, setDocumentGroups] = React.useState([]);

  React.useEffect(() => {
    const fetchGroups = async () => {
      if (!currentOrgId) return;
      const { data, error } = await supabase.from('document_groups').select('*').eq('organisation_id', currentOrgId).order('nom', { ascending: true });
      if (!error && data) setDocumentGroups(data);
    };
    fetchGroups();
  }, [supabase, currentOrgId]);

  const handleGroupDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;
    if (!currentOrgId) return;
    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    // AJOUT (2026-09-30) : déplace un document/émargement/questionnaire d'un dossier de séance vers
    // un autre (over.id = `drop-tpl-{templateId}`) — jusqu'ici seul le déplacement de groupes de
    // documents entre Début/Fin existait.
    if (activeIdStr.startsWith('drag-res-')) {
      if (overIdStr.startsWith('drop-tpl-')) {
        const resourceId = activeIdStr.replace('drag-res-', '');
        const targetTemplateId = overIdStr.replace('drop-tpl-', '');
        const { error } = await supabase.from('module_step_resources').update({ template_id: targetTemplateId }).eq('id', resourceId).eq('organisation_id', currentOrgId);
        if (!error) fetchModules();
      }
      return;
    }

    // Déplacement d'un groupe de documents entre les zones Début/Fin de parcours (comportement
    // existant, inchangé) — ignore explicitement un dépôt sur un dossier de séance (drop-tpl-),
    // qui n'a pas de sens pour un groupe de documents.
    if (activeIdStr.startsWith('drag-grp-') && overIdStr.startsWith('drop-') && !overIdStr.startsWith('drop-tpl-')) {
      const resourceId = activeIdStr.replace('drag-grp-', '');
      const overIdParts = overIdStr.split('-'); // expected 'drop-{moduleId}-{moment}'
      if (overIdParts[0] === 'drop' && overIdParts.length >= 3) {
        const targetMoment = overIdParts[2]; // 'debut' ou 'fin'
        // Scopé à l'organisme courant (+ lignes historiques sans organisation_id) : sans ce filtre, un
        // glisser-déposer aurait pu modifier la ressource de module d'un AUTRE organisme si l'id était deviné.
        const { error } = await supabase.from('module_step_resources').update({ moment: targetMoment }).eq('id', resourceId).eq('organisation_id', currentOrgId);
        if (!error) fetchModules();
      }
    }
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-7xl mx-auto">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Ingénierie Formation</h1>
          <p className="text-gray-500 text-lg mt-1">Gérez la structure de vos modules et séances.</p>
        </div>
      </div>

      {/* Configuration Modules */}
      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100">
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-2 h-6 bg-purple-500 rounded-full mr-3"></span> Gestion des Modules
        </h2>
        <form onSubmit={handleAddModule} className="flex flex-col md:flex-row gap-4 items-end mb-8 bg-gray-50 p-4 rounded-xl border border-gray-200">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du nouveau module</label>
            <input required type="text" value={newModuleName} onChange={e => setNewModuleName(e.target.value)} placeholder="Ex: Bilan 24h" className="w-full p-2.5 rounded-lg border outline-none text-sm" />
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 mb-1">Séances prévues</label>
            <input required type="number" min="1" value={newModuleSeances} onChange={e => setNewModuleSeances(e.target.value)} className="w-full p-2.5 rounded-lg border outline-none text-sm" />
          </div>
          <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg px-6 py-2.5 shadow-sm transition-colors">Créer Module</button>
        </form>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {modules.map(mod => {
            const templates = moduleSessionTemplates.filter(t => String(t.module_id) === String(mod.id));

            return (
              <div key={mod.id} className="border border-purple-100 bg-purple-50/20 p-5 rounded-2xl relative shadow-sm h-fit">
                {editingModuleId === mod.id ? (
                  <div className="flex items-center gap-2 pr-24">
                    <input
                      autoFocus
                      className="bg-white border border-purple-300 rounded text-lg font-bold text-gray-900 p-1 w-full outline-none"
                      value={editModuleValue}
                      onChange={e => setEditModuleValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { handleRenameModule(mod.id, editModuleValue); setEditingModuleId(null); }
                        else if (e.key === 'Escape') setEditingModuleId(null);
                      }}
                    />
                    <button onClick={() => { handleRenameModule(mod.id, editModuleValue); setEditingModuleId(null); }} className="text-green-600 hover:text-green-700 shrink-0"><Check size={16} /></button>
                    <button onClick={() => setEditingModuleId(null)} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={16} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 pr-24 group/modtitle">
                    <h3 className="font-bold text-gray-900 text-lg">{mod.nom}</h3>
                    <button
                      onClick={() => { setEditingModuleId(mod.id); setEditModuleValue(mod.nom); }}
                      className="text-gray-300 hover:text-purple-600 opacity-0 group-hover/modtitle:opacity-100 transition-opacity"
                      title="Renommer ce module"
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                )}
                <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-1.5 rounded-xl absolute top-5 right-5">{templates.length > 0 ? templates.filter(t => Number(t.ordre) >= 1).length : (Number(mod.seances_prevues) || 0)} Séance(s)</span>

                <div className="flex gap-2 flex-wrap mt-6">
                  <button onClick={() => setModelingModuleId(modelingModuleId === mod.id ? null : mod.id)} className="text-xs font-bold text-indigo-600 hover:text-white hover:bg-indigo-600 flex items-center bg-white border border-indigo-200 px-4 py-2 rounded-lg transition-all">⚙️ Modéliser Parcours</button>
                  <button onClick={() => handleRedistributeModuleDocs(mod.id)} className="text-xs font-bold text-emerald-600 hover:text-white hover:bg-emerald-600 flex items-center bg-white border border-emerald-200 px-4 py-2 rounded-lg transition-all" title="Envoyer les documents de début/fin aux clients déjà assignés à ce module">🔄 Sync documents clients</button>
                  <button onClick={() => handleDeleteModule(mod.id, mod.nom)} className="text-xs font-bold text-red-600 hover:text-white hover:bg-red-600 flex items-center gap-1.5 bg-white border border-red-200 px-4 py-2 rounded-lg transition-all ml-auto" title="Supprimer ce module">
                    <Trash2 size={14} /> Supprimer
                  </button>
                </div>

                {/* Interface de Modélisation du Parcours */}
                {modelingModuleId === mod.id && (
                  <DndContext onDragEnd={handleGroupDragEnd}>
                    <div className="mt-6 pt-6 border-t border-purple-100 animate-fade-in space-y-6">
                    <h4 className="text-sm font-bold text-indigo-700 flex items-center gap-2">
                      <Layout size={16} /> Modélisation du Parcours
                    </h4>

                    {/* Zone Documents de Début de Parcours */}
                    {(() => {
                      const debutResources = moduleStepResources.filter(r => r.module_id === mod.id && r.moment === 'debut');
                      return (
                        <DroppableMomentZone id={`drop-${mod.id}-debut`} className="bg-emerald-50 border border-emerald-200 rounded-2xl overflow-hidden">
                          <div className="bg-emerald-100 px-4 py-3 flex flex-wrap items-center justify-between border-b border-emerald-200 gap-2">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-5 bg-emerald-500 rounded-full"></span>
                              <span className="text-xs font-black text-emerald-800 uppercase tracking-wider">Documents de Début de Parcours</span>
                              <span className="text-[11px] font-bold text-emerald-600 bg-emerald-200 px-2 py-0.5 rounded-full">Accessibles immédiatement</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <select 
                                onChange={async (e) => {
                                  if(!e.target.value) return;
                                  const grpId = e.target.value;
                                  const grpName = e.target.options[e.target.selectedIndex].text;
                                  await handleAddModuleMomentResource(mod.id, 'debut', { type: 'document_group', title: grpName, document_group_id: grpId });
                                  e.target.value = '';
                                }}
                                className="text-xs font-bold text-emerald-800 bg-white border border-emerald-300 px-2 py-1.5 rounded-lg outline-none cursor-pointer"
                              >
                                <option value="">+ Groupe...</option>
                                {documentGroups.map(g => <option key={g.id} value={g.id}>{g.nom}</option>)}
                              </select>
                              <button
                                onClick={() => { setActiveMoment('debut'); setActiveMomentModuleId(mod.id); setActiveFolderId(null); setIsResourceModalOpen(true); }}
                                className="text-xs font-black bg-emerald-600 text-white px-3 py-1.5 rounded-xl hover:bg-emerald-700 transition-all flex items-center gap-1.5"
                              >
                                <Plus size={12} /> Fichier
                              </button>
                            </div>
                          </div>
                          <div className="p-3 space-y-2">
                            {debutResources.length === 0 && <p className="text-[11px] text-emerald-600/60 italic px-1">Aucun document de début.</p>}
                            {debutResources.map(res => {
                              if (res.type === 'document_group') {
                                return <DraggableGroupBlock key={res.id} resourceId={res.id} group={documentGroups.find(g => g.id === res.document_group_id)} onDelete={handleDeleteStepResource} />;
                              }
                              return (
                                <div key={res.id} className="flex items-center justify-between bg-white p-2.5 rounded-xl border border-emerald-100 text-[11px]">
                                  <div className="flex items-center gap-2">
                                    <span>{res.type === 'signature' ? '✍️' : '📄'}</span>
                                    <span className="font-bold text-gray-800">{res.titre}</span>
                                    <span className="text-[11px] text-gray-400 uppercase">{res.type}</span>
                                  </div>
                                  <button onClick={() => handleDeleteStepResource(res.id)} className="text-gray-300 hover:text-red-400"><Trash2 size={12} /></button>
                                </div>
                              );
                            })}
                          </div>
                        </DroppableMomentZone>
                      );
                    })()}

                    <div className="space-y-4">
                      {templates.map((template, idx) => {
                        const resources = moduleStepResources.filter(r => r.template_id === template.id);
                        return (
                          <div key={template.id} className="bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden">
                            <div className="bg-indigo-50/50 p-4 flex items-center justify-between border-b border-indigo-50">
                              <div className="flex items-center gap-3">
                                <span className="w-8 h-8 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-bold shadow-sm shrink-0">{idx + 1}</span>
                                <div className="flex-1">
                                  {editingId === template.id ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        autoFocus
                                        className="bg-white border border-indigo-300 rounded text-sm p-1 font-bold text-gray-900 w-full outline-none"
                                        value={editValue}
                                        onChange={e => setEditValue(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') {
                                            handleRenameFolder(template.id, editValue);
                                            setEditingId(null);
                                          } else if (e.key === 'Escape') setEditingId(null);
                                        }}
                                      />
                                      <button onClick={() => { handleRenameFolder(template.id, editValue); setEditingId(null); }} className="text-green-600 hover:text-green-700"><Check size={16} /></button>
                                      <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 group/title">
                                      <p className="font-bold text-gray-900">{template.titre}</p>
                                      <button 
                                        onClick={() => { setEditingId(template.id); setEditValue(template.titre); }}
                                        className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/title:opacity-100 transition-opacity"
                                      >
                                        <Pencil size={12} />
                                      </button>
                                    </div>
                                  )}
                                  <p className="text-xs text-gray-400 uppercase font-black">Dossier Séance</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteFolder(template.id)}
                                className="text-gray-300 hover:text-red-500 bg-white w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
                              >✕</button>
                            </div>

                            <DroppableMomentZone id={`drop-tpl-${template.id}`} className="p-4 space-y-2">
                              {resources.map((res) => (
                                <DraggableStepResourceRow
                                  key={res.id}
                                  res={res}
                                  editingId={editingId}
                                  setEditingId={setEditingId}
                                  editValue={editValue}
                                  setEditValue={setEditValue}
                                  handleRenameResource={handleRenameResource}
                                  setSignatureSettingsTarget={setSignatureSettingsTarget}
                                  setIsSignatureSettingsOpen={setIsSignatureSettingsOpen}
                                  handleDeleteStepResource={handleDeleteStepResource}
                                />
                              ))}

                              <button
                                onClick={() => { setActiveFolderId(template.id); setIsResourceModalOpen(true); }}
                                className="text-xs font-black bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-black transition-all shadow-lg flex items-center gap-2 mt-2"
                              >
                                <Plus size={14} /> Ajouter un élément
                              </button>
                            </DroppableMomentZone>
                          </div>
                        );
                      })}
                      {templates.length === 0 && (
                        <div className="bg-gray-50 border border-dashed border-gray-200 p-8 rounded-[32px] text-center">
                          <p className="text-gray-400 text-sm italic">Aucun dossier créé.</p>
                        </div>
                      )}
                    </div>

                    {/* Zone Documents de Fin de Parcours */}
                    {(() => {
                      const finResources = moduleStepResources.filter(r => r.module_id === mod.id && r.moment === 'fin');
                      return (
                        <DroppableMomentZone id={`drop-${mod.id}-fin`} className="bg-violet-50 border border-violet-200 rounded-2xl overflow-hidden">
                          <div className="bg-violet-100 px-4 py-3 flex flex-wrap items-center justify-between border-b border-violet-200 gap-2">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-5 bg-violet-600 rounded-full"></span>
                              <span className="text-xs font-black text-violet-800 uppercase tracking-wider">Documents de Fin de Parcours</span>
                              <span className="text-[11px] font-bold text-violet-700 bg-violet-200 px-2 py-0.5 rounded-full">Débloqués après le dernier RDV</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <select 
                                onChange={async (e) => {
                                  if(!e.target.value) return;
                                  const grpId = e.target.value;
                                  const grpName = e.target.options[e.target.selectedIndex].text;
                                  await handleAddModuleMomentResource(mod.id, 'fin', { type: 'document_group', title: grpName, document_group_id: grpId });
                                  e.target.value = '';
                                }}
                                className="text-xs font-bold text-violet-800 bg-white border border-violet-300 px-2 py-1.5 rounded-lg outline-none cursor-pointer"
                              >
                                <option value="">+ Groupe...</option>
                                {documentGroups.map(g => <option key={g.id} value={g.id}>{g.nom}</option>)}
                              </select>
                              <button
                                onClick={() => { setActiveMoment('fin'); setActiveMomentModuleId(mod.id); setActiveFolderId(null); setIsResourceModalOpen(true); }}
                                className="text-xs font-black bg-violet-700 text-white px-3 py-1.5 rounded-xl hover:bg-violet-700 transition-all flex items-center gap-1.5"
                              >
                                <Plus size={12} /> Fichier
                              </button>
                            </div>
                          </div>
                          <div className="p-3 space-y-2">
                            {finResources.length === 0 && <p className="text-[11px] text-violet-700/60 italic px-1">Aucun document de fin.</p>}
                            {finResources.map(res => {
                              if (res.type === 'document_group') {
                                return <DraggableGroupBlock key={res.id} resourceId={res.id} group={documentGroups.find(g => g.id === res.document_group_id)} onDelete={handleDeleteStepResource} />;
                              }
                              return (
                                <div key={res.id} className="flex items-center justify-between bg-white p-2.5 rounded-xl border border-violet-100 text-[11px]">
                                  <div className="flex items-center gap-2">
                                    <span>{res.type === 'signature' ? '✍️' : '📄'}</span>
                                    <span className="font-bold text-gray-800">{res.titre}</span>
                                    <span className="text-[11px] text-gray-400 uppercase">{res.type}</span>
                                  </div>
                                  <button onClick={() => handleDeleteStepResource(res.id)} className="text-gray-300 hover:text-violet-400"><Trash2 size={12} /></button>
                                </div>
                              );
                            })}
                          </div>
                        </DroppableMomentZone>
                      );
                    })()}

                    <form
                      onSubmit={async (e) => { e.preventDefault(); await createSessionFolder(mod.id, newStepTitle); }}
                      className="bg-indigo-900 p-4 rounded-2xl shadow-xl space-y-3"
                    >
                      <div className="flex gap-2">
                        <input required type="text" placeholder="Nouveau Dossier..." value={newStepTitle} onChange={e => setNewStepTitle(e.target.value)} className="flex-1 text-xs p-2.5 bg-indigo-800 border-none text-white rounded-xl outline-none" />
                        <button type="submit" disabled={isAddingStep} className="bg-white text-indigo-900 font-bold px-4 rounded-xl text-xs hover:bg-indigo-50 disabled:opacity-50">
                          {isAddingStep ? '...' : 'Créer Dossier'}
                        </button>
                      </div>
                    </form>
                  </div>
                  </DndContext>
                )}
              </div>
            );
          })}
          {modules.length === 0 && <div className="text-gray-500 italic col-span-2">Créez votre premier module.</div>}
        </div>
      </div>

      {/* Sections Ressources Pédagogiques et Bibliothèque de Modèles Word supprimées :
          - Les ressources s'ajoutent directement dans les dossiers des modules via "+ Ajouter"
          - Les modèles formateurs sont gérés dans la section Formateurs */}

      <StepResourceModal
        isOpen={isResourceModalOpen}
        onClose={() => { setIsResourceModalOpen(false); setActiveFolderId(null); setActiveMoment(null); setActiveMomentModuleId(null); }}
        pedagogicalResources={pedagogicalResources}
        documentTemplates={documentTemplates}
        supabase={supabase}
        currentOrgId={currentOrgId}
        momentLabel={activeMoment === 'debut' ? '🟢 Début de Parcours (Onboarding)' : activeMoment === 'fin' ? '🔴 Fin de Parcours (Outro)' : null}
        onSave={(data) => {
          if (activeMoment && activeMomentModuleId) {
            handleAddModuleMomentResource(activeMomentModuleId, activeMoment, data);
            setIsResourceModalOpen(false); setActiveMoment(null); setActiveMomentModuleId(null);
          } else {
            handleAddStepResource(activeFolderId, data);
          }
        }}
      />

      <StepResourceSignatureModal
        isOpen={isSignatureSettingsOpen}
        resource={signatureSettingsTarget}
        onClose={() => { setIsSignatureSettingsOpen(false); setSignatureSettingsTarget(null); }}
        onSave={async (data) => {
          if (signatureSettingsTarget) {
            await handleUpdateStepResourceSignatures(signatureSettingsTarget.id, data);
          }
          setIsSignatureSettingsOpen(false);
          setSignatureSettingsTarget(null);
        }}
      />
    </div>
  );
};

const FormateurView = ({
  clients, formateurs, sessions, generateSessions,
  updateSessionDate, signSession, modules, currentUserId,
  expandedClientId, setExpandedClientId, userRole,
  handleAddSession, handleDeleteSession, updateSessionTime,
  handleGenerateDocx, documents, fetchUtilisateurs, documentTemplates,
  pedagogicalResources, handleDownloadResource, handleUploadExerciseResponse,
  setIsSessionItemModalOpen, setTargetSessionForAddition, setViewingSession,
  handleSignDocument, setViewingDocId,
  clientSkills, fetchClientSkills, supabase, fetchDocuments,
  handleMoveSessionItem, handleSaveCorrection, currentOrgId, handleAddCompletedExercise,
  depotsNonLus = [], estPro = false, nomUtilisateur = '', onDepotsVus = null, ongletInitial = null, onOngletInitialUtilise = null, chargement = false
}) => {
  const [editedTimes, setEditedTimes] = React.useState({});
  const [savingId, setSavingId] = React.useState(null);
  const [correctionModalSession, setCorrectionModalSession] = React.useState(null);
  const [confirmState, setConfirmState] = React.useState({ open: false, title: '', message: '', onConfirm: null });
  const showDeleteConfirm = (title, message, onConfirmFn) => setConfirmState({ open: true, title, message, onConfirm: onConfirmFn });
  const hideDeleteConfirm = () => setConfirmState(prev => ({ ...prev, open: false, onConfirm: null }));
  const [formateurClientTab, setFormateurClientTab] = React.useState('seances');
  const [formateurMainSection, setFormateurMainSection] = React.useState('clients'); // 'clients' | 'mes_docs'
  const [uploadingClientId, setUploadingClientId] = React.useState(null);
  const [clientDocFile, setClientDocFile] = React.useState(null);
  const [clientDocName, setClientDocName] = React.useState('');
  const [isUploadingClientDoc, setIsUploadingClientDoc] = React.useState(false);
  // NOUVEAU (2026-09-04) : "Mon dossier administratif" — le formateur ajoute lui-même un document qui
  // le concerne personnellement (assurance, diplôme...), en lien avec l'organisme, sans passer par le
  // dossier d'un client (jusqu'ici il ne pouvait ajouter un document QUE dans le dossier d'un client).
  const [showOwnDocModal, setShowOwnDocModal] = React.useState(false);
  const [ownDocFile, setOwnDocFile] = React.useState(null);
  const [ownDocName, setOwnDocName] = React.useState('');
  const [ownDocType, setOwnDocType] = React.useState('Administratif');
  const [isUploadingOwnDoc, setIsUploadingOwnDoc] = React.useState(false);
  const [fActiveId, setFActiveId] = React.useState(null);
  // Exercices déposés par le formateur (2026-10-09)
  const [exoUploadingId, setExoUploadingId] = React.useState(null);
  const [showAddExo, setShowAddExo] = React.useState(false);
  const [addExoForm, setAddExoForm] = React.useState({ title: '', numero: '', file: null });
  const [isAddingExo, setIsAddingExo] = React.useState(false);
  const EXO_ACCEPT = 'image/*,.pdf,.doc,.docx,.odt,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.txt,.rtf';
  const assignedClients = clients.filter(c => c.formateur_id === currentUserId);

  // AJOUT (2026-09-30) : résultats de quiz des propres clients du formateur — jamais ceux des
  // autres formateurs (myClientIds ci-dessous est dérivé du même filtre formateur_id === currentUserId
  // qu'assignedClients juste au-dessus).
  const [formateurQuizResources, setFormateurQuizResources] = React.useState([]);
  const [formateurQuizResponses, setFormateurQuizResponses] = React.useState([]);
  React.useEffect(() => {
    if (!currentOrgId || !supabase) return;
    supabase.from('module_step_resources').select('id, titre, metadata, module_id').eq('type', 'questionnaire').eq('organisation_id', currentOrgId)
      .then(({ data }) => { if (data) setFormateurQuizResources(data); });
    const myClientIds = clients.filter(c => c.formateur_id === currentUserId).map(c => c.id);
    if (myClientIds.length > 0) {
      supabase.from('questionnaire_responses').select('*').in('client_id', myClientIds)
        .then(({ data }) => { if (data) setFormateurQuizResponses(data); });
    } else {
      setFormateurQuizResponses([]);
    }
  }, [currentOrgId, supabase, clients, currentUserId]);

  // Documents à signer par le formateur : uniquement ceux qui lui sont explicitement assignés
  // (assigned_formateur_id = ce formateur). Le champ est posé par instantiateDocument quand
  // la destination inclut le formateur (visFormateur=true).
  const myAdminDocs = React.useMemo(() =>
    (documents || []).filter(d => d.assigned_formateur_id === currentUserId && d.visible_formateur !== false && !isBlockedBySigningOrder(d, 'formateur') && !isDossierDoc(d)),
    [documents, currentUserId]
  );
  const pendingDocsCount = myAdminDocs.filter(d => !d.signe_par_formateur).length;
  const nomsGroupesDocs = useNomsGroupes(currentOrgId); // signature en lot (2026-10-23)

  React.useEffect(() => {
    setFormateurClientTab(ongletInitial || 'seances');
    if (ongletInitial && onOngletInitialUtilise) onOngletInitialUtilise();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedClientId]);

  // FIX (2026-10-02) : les horaires n'étaient enregistrés qu'en cliquant sur la petite icône disquette —
  // sans ce clic, ils étaient perdus en quittant la page. Ils s'enregistrent désormais AUTOMATIQUEMENT
  // 0,8 s après la saisie (et à la sortie du champ) ; l'icône reste disponible pour forcer l'enregistrement.
  const fEditedTimesRef = React.useRef({});
  const fPendingSavesRef = React.useRef({});
  const onSaveTimes = async (sessionId) => {
    clearTimeout(fPendingSavesRef.current[sessionId]);
    delete fPendingSavesRef.current[sessionId];
    const times = fEditedTimesRef.current[sessionId];
    if (!times) return;
    setSavingId(sessionId);
    const okStart = await updateSessionTime(sessionId, 'heure_debut', times.start);
    const okEnd = await updateSessionTime(sessionId, 'heure_fin', times.end);
    if (okStart !== false && okEnd !== false && fEditedTimesRef.current[sessionId] === times) {
      const next = { ...fEditedTimesRef.current };
      delete next[sessionId];
      fEditedTimesRef.current = next;
      setEditedTimes(next);
    }
    setTimeout(() => setSavingId(null), 1500);
  };

  const onTimeChange = (sessionId, field, value, groupDefaults) => {
    const current = fEditedTimesRef.current[sessionId] || (groupDefaults ? {
      start: groupDefaults.start || '',
      end: groupDefaults.end || ''
    } : {
      start: sessions.find(s => s.id === sessionId)?.heure_debut || '',
      end: sessions.find(s => s.id === sessionId)?.heure_fin || ''
    });
    const next = { ...fEditedTimesRef.current, [sessionId]: { ...current, [field]: value } };
    fEditedTimesRef.current = next;
    setEditedTimes(next);
    clearTimeout(fPendingSavesRef.current[sessionId]);
    fPendingSavesRef.current[sessionId] = setTimeout(() => { onSaveTimes(sessionId); }, 800);
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    const [h1, m1] = start.split(':').map(Number);
    const [h2, m2] = end.split(':').map(Number);
    let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff <= 0) return null;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}h${m > 0 ? ` ${m}min` : ''}`;
  };

  const handleUploadForClient = async (clientId) => {
    if (!clientDocFile || !clientDocName.trim()) {
      toast.error('Veuillez renseigner un nom et choisir un fichier.');
      return;
    }
    // Vérification d'appartenance : le client ciblé doit être un client réel visible par ce formateur
    // (liste déjà scopée par organisme) — avant ce correctif, un clientId arbitraire aurait été accepté
    // sans aucune vérification.
    const targetClient = (clients || []).find(c => String(c.id) === String(clientId));
    if (!targetClient) {
      toast.error("Client introuvable — impossible d'ajouter le document.");
      return;
    }
    setIsUploadingClientDoc(true);
    const ext = clientDocFile.name.split('.').pop();
    const safeN = clientDocName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const fileName = `formateur_${currentUserId}/${Date.now()}_${safeN}.${ext}`;
    const { error: upErr } = await supabase.storage.from('documents').upload(fileName, clientDocFile);
    if (upErr) {
      toast.error('Erreur upload : ' + upErr.message);
      setIsUploadingClientDoc(false);
      return;
    }
    const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);
    const { error: dbErr } = await supabase.from('documents').insert({
      nom: clientDocName.trim(),
      type_document: 'Administratif',
      user_id: clientId,
      assigned_formateur_id: currentUserId,
      url: publicUrl,
      visible_client: false,
      visible_formateur: true,
      // organisation_id manquait ici : sans lui, ce document échappait au cloisonnement par organisme
      // appliqué partout ailleurs.
      ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
    });
    if (!dbErr) {
      toast.success('Document ajouté au dossier client !');
      setClientDocFile(null);
      setClientDocName('');
      setUploadingClientId(null);
      if (fetchDocuments) fetchDocuments();
    } else {
      toast.error('Erreur base de données : ' + dbErr.message);
    }
    setIsUploadingClientDoc(false);
  };

  const handleDeleteDossierDoc = async (docId) => {
    if (!(await peutSupprimerDocument(docId))) return; // lot 2 : jamais un document signé
    const { error } = await supabase.from('documents').delete().eq('id', docId);
    if (error) { toast.error('Erreur lors de la suppression : ' + error.message); return; }
    toast.success('Document supprimé.');
    if (fetchDocuments) await fetchDocuments();
  };

  // NOUVEAU (2026-09-04) : ajoute un document à SON PROPRE dossier administratif — pas de client visé
  // (assigned_formateur_id = soi-même, user_id absent), visible uniquement par l'organisme.
  const handleUploadOwnAdminDoc = async () => {
    if (!ownDocFile || !ownDocName.trim()) {
      toast.error('Veuillez renseigner un nom et choisir un fichier.');
      return;
    }
    setIsUploadingOwnDoc(true);
    const ext = ownDocFile.name.split('.').pop();
    const safeN = ownDocName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const fileName = `formateur_${currentUserId}/dossier/${Date.now()}_${safeN}.${ext}`;
    const { error: upErr } = await supabase.storage.from('documents').upload(fileName, ownDocFile);
    if (upErr) {
      toast.error('Erreur upload : ' + upErr.message);
      setIsUploadingOwnDoc(false);
      return;
    }
    const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);
    const { error: dbErr } = await supabase.from('documents').insert({
      nom: ownDocName.trim(),
      type_document: ownDocType,
      assigned_formateur_id: currentUserId,
      url: publicUrl,
      visible_client: false,
      visible_formateur: true,
      ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
    });
    if (!dbErr) {
      toast.success('Document ajouté à votre dossier administratif !');
      setOwnDocFile(null);
      setOwnDocName('');
      setShowOwnDocModal(false);
      if (fetchDocuments) fetchDocuments();
    } else {
      toast.error('Erreur base de données : ' + dbErr.message);
    }
    setIsUploadingOwnDoc(false);
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-7xl mx-auto">
      {/* Navigation principale du formateur */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {formateurMainSection === 'clients' ? 'Mes Clients Assignés' : 'Mes Documents Administratifs'}
          </h1>
          <p className="text-gray-500 text-lg mt-1">
            {formateurMainSection === 'clients' ? "Suivez l'avancement et gérez les émargements." : 'Documents à consulter et à signer.'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setFormateurMainSection('clients')}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              formateurMainSection === 'clients' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Mes Clients
          </button>
          <button
            onClick={() => setFormateurMainSection('mes_docs')}
            className={`relative px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              formateurMainSection === 'mes_docs' ? 'bg-violet-700 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Docs Administratifs
            {pendingDocsCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-black w-5 h-5 rounded-full flex items-center justify-center shadow-md">
                {pendingDocsCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Section : Mes Documents Administratifs */}
      {formateurMainSection === 'mes_docs' && (() => {
        // Grouper les docs par client (user_id) — les docs sans client vont dans "Sans client"
        const grouped = {};
        myAdminDocs.forEach(doc => {
          const clientId = doc.user_id || '__none__';
          if (!grouped[clientId]) grouped[clientId] = [];
          grouped[clientId].push(doc);
        });
        const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
          if (a === '__none__') return 1;
          if (b === '__none__') return -1;
          const ca = clients.find(c => c.id === a);
          const cb = clients.find(c => c.id === b);
          return (ca?.nom || '').localeCompare(cb?.nom || '');
        });

        const renderDocRow = (doc) => {
          const signed = doc.signe_par_formateur;
          return (
            <div key={doc.id} className={`flex items-center justify-between p-4 rounded-2xl border transition-all group ${signed ? 'border-green-100 bg-green-50/30' : 'border-gray-100 hover:border-violet-200 bg-white'}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${signed ? 'bg-green-100 text-green-600' : 'bg-violet-50 text-violet-600'}`}>
                  {signed ? <Check size={16} strokeWidth={2.5} /> : <FileText size={16} />}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 text-sm truncate">{doc.nom}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${signed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {signed ? '✓ Signé' : '⚠ À signer'}
                    </span>
                    {doc.created_at && <span className="text-xs text-gray-400">Reçu le {new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button onClick={() => setViewingDocId && setViewingDocId(doc.id)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="Consulter">
                  <Eye size={18} />
                </button>
                {!signed ? (
                  <button
                    onClick={() => setViewingSession && setViewingSession({ session: { ...doc, file_url: doc.url || doc.file_url }, mode: 'sign' })}
                    className="bg-violet-700 hover:bg-violet-800 text-white px-4 py-2 rounded-xl text-xs font-black shadow-sm flex items-center gap-1.5 transition-all"
                  >
                    <PenTool size={13} /> Signer
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-2 rounded-xl text-xs font-black border border-green-100">
                    <Check size={13} strokeWidth={3} /> Signé
                  </div>
                )}
              </div>
            </div>
          );
        };

        const myDossierDocs = (documents || []).filter(d => d.assigned_formateur_id === currentUserId && !d.user_id && isDossierDoc(d));

        return (
          <div className="space-y-4">
            {/* ── NOUVEAU (2026-09-04) : Mon dossier administratif — documents personnels, sans client, sans signature ── */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
              <div className="flex flex-wrap items-center justify-between mb-3 gap-3">
                <div>
                  <h3 className="font-black text-gray-900 text-sm">Mon dossier administratif</h3>
                  <p className="text-[11px] text-gray-400 mt-0.5">Vos propres documents partagés avec l'organisme (assurance, diplôme...) — sans lien avec un client.</p>
                </div>
                <button
                  onClick={() => setShowOwnDocModal(true)}
                  className="bg-violet-700 hover:bg-violet-800 text-white text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 transition-all shadow-sm shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" /> Ajouter un document
                </button>
              </div>
              {myDossierDocs.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Aucun document dans votre dossier.</p>
              ) : (
                <div className="space-y-2">
                  {myDossierDocs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-2xl px-4 py-3 border border-gray-100 group">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                        <div className="min-w-0">
                          <span className="text-sm font-bold text-gray-700 truncate block">{doc.nom}</span>
                          <span className="text-[11px] font-black uppercase tracking-widest text-violet-600">{doc.type_document || 'Autre'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openSecureStorageFile(doc.url)}
                          className="bg-white text-violet-700 hover:bg-violet-600 hover:text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-violet-200 transition-all"
                        >
                          Ouvrir
                        </button>
                        <button
                          onClick={() => handleDeleteDossierDoc(doc.id)}
                          className="text-gray-300 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100"
                          title="Supprimer ce document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {showOwnDocModal && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
                  <h4 className="text-base font-black text-gray-900 mb-1">Ajouter un document à mon dossier</h4>
                  <p className="text-xs text-gray-400 mb-4">Ce fichier sera visible par l'organisme, sans lien avec un client précis.</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Fichier</label>
                      <input
                        type="file"
                        onChange={(e) => {
                          const f = e.target.files[0] || null;
                          setOwnDocFile(f);
                          if (f && !ownDocName.trim()) {
                            setOwnDocName(f.name.replace(/\.[^/.]+$/, ''));
                          }
                        }}
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Nom du document</label>
                      <input
                        type="text"
                        value={ownDocName}
                        onChange={(e) => setOwnDocName(e.target.value)}
                        placeholder="Ex : Attestation d'assurance RCP"
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Type de document</label>
                      <select
                        value={ownDocType}
                        onChange={(e) => setOwnDocType(e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-violet-300"
                      >
                        <option value="Administratif">Administratif</option>
                        <option value="Contrat">Contrat</option>
                        <option value="Mission">Mission</option>
                        <option value="Pièce justificative">Pièce justificative</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-5">
                    <button
                      onClick={() => { setShowOwnDocModal(false); setOwnDocFile(null); setOwnDocName(''); setOwnDocType('Administratif'); }}
                      className="flex-1 text-gray-500 hover:text-gray-800 font-bold text-sm py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleUploadOwnAdminDoc}
                      disabled={isUploadingOwnDoc || !ownDocFile || !ownDocName.trim()}
                      className="flex-1 bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white font-bold text-sm py-2.5 rounded-xl transition-all"
                    >
                      {isUploadingOwnDoc ? 'Envoi...' : 'Ajouter au dossier'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {myAdminDocs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm">
                <Archive className="mx-auto mb-4 text-gray-300" size={40} />
                <p className="text-gray-400 italic">Aucun document ne vous a été assigné.</p>
              </div>
            ) : sortedGroups.map(([clientId, docs]) => {
              const client = clientId !== '__none__' ? clients.find(c => c.id === clientId) : null;
              const pendingCount = docs.filter(d => !d.signe_par_formateur).length;
              const allSigned = pendingCount === 0;
              return (
                <div key={clientId} className={`bg-white rounded-3xl border shadow-sm overflow-hidden ${allSigned ? 'border-green-100' : 'border-amber-100'}`}>
                  {/* En-tête client */}
                  <div className={`flex items-center justify-between px-6 py-4 ${allSigned ? 'bg-green-50/40' : 'bg-amber-50/40'} border-b ${allSigned ? 'border-green-100' : 'border-amber-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-base ${allSigned ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {client ? (client.nom?.charAt(0) || '?') : '📄'}
                      </div>
                      <div>
                        <p className="font-black text-gray-900 text-sm">
                          {client ? (client.nom_complet || client.nom || client.email) : 'Documents généraux'}
                        </p>
                        <p className="text-[11px] text-gray-400">{docs.length} document{docs.length > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-black uppercase px-3 py-1.5 rounded-full ${allSigned ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {allSigned ? '✓ Tout signé' : `${pendingCount} à signer`}
                    </span>
                  </div>
                  {/* Signature en une fois (2026-10-23) */}
                  {client && (
                    <div className="px-4 pt-4 empty:hidden">
                      <PanneauSignatureEnLot docs={docs} role="formateur" tousDocuments={documents} nomsGroupes={nomsGroupesDocs}
                        handleSignDocument={handleSignDocument} fetchDocuments={fetchDocuments} />
                    </div>
                  )}
                  {/* Liste des docs */}
                  <div className="p-4 space-y-2">
                    {docs.map(renderDocRow)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Section : Mes Clients */}
      {formateurMainSection === 'clients' && <BandeauComptesAActiver clientIds={assignedClients.map(c => c.id)} />}
      {formateurMainSection === 'clients' && (
      <div className="grid grid-cols-1 gap-6">
        {assignedClients.length > 0 ? assignedClients.map(client => {
          // AJOUT (2026-09-19) : voir le même commentaire dans ClientDetailView — `clientSessions` ne
          // garde que les vraies séances calendrier (numero_seance renseigné) pour que les exercices
          // synthétiques de début/fin de module n'apparaissent plus comme une "séance" à dater ; les
          // compteurs d'exercices utilisent `allClientSessions` pour continuer à tout inclure.
          const allClientSessions = sessions.filter(s => s.client_id === client.id);
          const clientSessions = allClientSessions.filter(s => s.numero_seance !== null && s.numero_seance !== undefined && estDuModuleActuel(s, client.module_id));
          const clientExercisesCount = allClientSessions.filter(s => s.type_activite === 'exercice' || s.type_activite === 'Exercice').length;
          const clientPendingCorrections = allClientSessions.filter(s =>
            (s.type_activite === 'exercice' || s.type_activite === 'Exercice') &&
            s.reponse_url && s.correction_statut !== 'Validé' && s.correction_statut !== 'À corriger'
          ).length;
          const isExpanded = expandedClientId === client.id;
          const assignedModule = modules?.find(m => String(m.id) === String(client.module_id));
          const progress = progressionParcours(client.id, sessions, client.module_id).pct; // LOT B

          return (
            <div key={client.id} className={`bg-white rounded-3xl p-6 shadow-sm border ${isExpanded ? 'border-indigo-200' : 'border-gray-100'} transition-all`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center">
                  <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center font-bold text-2xl mr-4">{client.nom ? client.nom.charAt(0) : '?'}</div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xl">{client.nom}</h3>
                    <p className="text-sm text-gray-500 font-medium">{client.email}</p>
                    <div className="mt-1.5"><BadgeCompteClient clientId={client.id} align="left" /></div>
                  </div>
                </div>

                <div className="flex flex-1 max-w-xs flex-col">
                  <div className="flex justify-between text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">
                    <span>Progression</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all duration-500" style={{ width: `${progress}%` }}></div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <BoutonCreerCompte client={client} />
                  <MasquerSiSansCompte clientId={client.id}>
                  <button
                    onClick={() => renvoyerLienAcces(client)}
                    title="Renvoyer le lien de connexion à ce client"
                    className="px-4 py-2.5 rounded-xl text-sm font-bold transition-all bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 flex items-center gap-1.5"
                  >
                    <Mail size={15} /> Renvoyer le lien
                  </button>
                  </MasquerSiSansCompte>
                  <button
                    onClick={() => setExpandedClientId(isExpanded ? null : client.id)}
                    className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${isExpanded ? 'bg-gray-100 text-gray-700' : 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'}`}
                  >
                    {isExpanded ? "Réduire" : "Gérer les séances"}
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-8 pt-8 border-t border-gray-100 animate-slide-up">
                  <FinancementResume clientId={client.id} />
                  <div className="tabs-scroll flex gap-4 border-b border-gray-200 mb-6 font-sans">
                    <button onClick={() => setFormateurClientTab('seances')} className={`px-4 py-3 font-bold text-sm transition-all border-b-2 ${formateurClientTab === 'seances' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Planning des Séances</button>
                    <button onClick={() => setFormateurClientTab('administratif')} className={`px-4 py-3 font-bold text-sm transition-all border-b-2 ${formateurClientTab === 'administratif' ? 'border-violet-700 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Administratif</button>
                    <button onClick={() => setFormateurClientTab('docs_signes')} className={`px-4 py-3 font-bold text-sm transition-all border-b-2 ${formateurClientTab === 'docs_signes' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Documents Signés</button>
                    <button onClick={() => setFormateurClientTab('exercices')} className={`relative px-4 py-3 font-bold text-sm transition-all border-b-2 ${formateurClientTab === 'exercices' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                      Exercices
                      {clientPendingCorrections > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[11px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-md">
                          {clientPendingCorrections}
                        </span>
                      )}
                    </button>
                    <button onClick={() => setFormateurClientTab('quiz')} className={`px-4 py-3 font-bold text-sm transition-all border-b-2 ${formateurClientTab === 'quiz' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Quiz</button>
                    <button onClick={() => setFormateurClientTab('recus')} className={`relative px-4 py-3 font-bold text-sm transition-all border-b-2 ${formateurClientTab === 'recus' ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                      Reçus du client
                      {(depotsNonLus || []).filter(d => String(d.client_id) === String(client.id) && d.destinataire !== 'organisme').length > 0 && (
                        <span className="ml-1.5 inline-flex bg-violet-600 text-white text-[11px] font-black w-4 h-4 rounded-full items-center justify-center align-middle">
                          {(depotsNonLus || []).filter(d => String(d.client_id) === String(client.id) && d.destinataire !== 'organisme').length}
                        </span>
                      )}
                    </button>
                  </div>

                  {formateurClientTab === 'recus' && (
                    <DepotsClientPanel client={client} currentOrgId={currentOrgId} userRole="formateur" estPro={estPro} nomUtilisateur={nomUtilisateur} onVu={onDepotsVus} />
                  )}

                  {formateurClientTab === 'quiz' && (
                    <div className="space-y-3">
                      {(() => {
                        const quizItems = formateurQuizResources
                          .filter(r => String(r.module_id) === String(client.module_id))
                          .map(r => {
                            const meta = (() => { try { return typeof r.metadata === 'string' ? JSON.parse(r.metadata) : (r.metadata || {}); } catch { return {}; } })();
                            return { resource: r, meta };
                          })
                          .filter(({ meta }) => meta.isQuiz);
                        if (quizItems.length === 0) {
                          return (
                            <div className="py-10 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                              <p className="text-2xl mb-2">🎯</p>
                              <p className="text-gray-400 text-sm">Aucun quiz dans le module de ce client.</p>
                            </div>
                          );
                        }
                        return quizItems.map(({ resource, meta }) => {
                          const response = formateurQuizResponses.find(r => r.questionnaire_id === resource.id && r.client_id === client.id);
                          return (
                            <div key={resource.id} className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between">
                              <div>
                                <p className="font-bold text-gray-900 text-sm">{resource.titre}</p>
                                <p className="text-xs text-gray-500">Seuil de réussite : {meta.seuilReussite ?? 50}%</p>
                              </div>
                              {response && response.score_percent != null ? (
                                <span className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${response.passed ? 'text-green-600 bg-green-50 border-green-100' : 'text-red-600 bg-red-50 border-red-100'}`}>
                                  {response.passed ? '✅ Acquis' : '❌ Non acquis'} · {Math.round(response.score_percent)}%
                                </span>
                              ) : (
                                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100">⏳ En attente</span>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  {formateurClientTab === 'administratif' && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="w-2 h-5 bg-violet-600 rounded-full"></span>
                        <h4 className="font-black text-gray-800 text-sm uppercase tracking-tight">Documents du dossier client</h4>
                      </div>
                      
                      {(() => {
                        // Liste partagée avec l'onglet "Administratif" de l'espace organisme (ClientDetailView) —
                        // les mêmes types de documents et la même exclusion des documents déjà signés par le client
                        // (qui, eux, s'affichent dans l'onglet "Documents Signés").
                        const DOSSIER_DOC_TYPES = ['Administratif', 'Contrat', 'Mission', 'Pièce justificative', 'Autre'];
                        const adminDocs = documents.filter(d =>
                          d.user_id === client.id &&
                          DOSSIER_DOC_TYPES.includes(d.type_document) &&
                          !(d.statut === 'Signé') &&
                          !d.visible_client // FIX (2026-10-05) : un document transmis AU client n'est pas un document interne du dossier
                        );
                        
                        if (adminDocs.length === 0) return (
                          <div className="py-12 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                            <Archive className="mx-auto mb-3 text-gray-300" size={32} />
                            <p className="text-gray-400 text-sm italic">Aucun document dans le dossier de ce client.</p>
                          </div>
                        );

                        // Seuls les documents générés depuis un modèle (template_id renseigné) demandent
                        // une signature du formateur — un document simplement déposé ici (upload manuel,
                        // organisme ou formateur) n'en a jamais besoin : il propose juste Ouvrir/Télécharger.
                        const formatDocDate = (d) => {
                          if (!d) return null;
                          const parsed = new Date(d);
                          return isNaN(parsed) ? null : parsed.toLocaleDateString('fr-FR');
                        };

                        return (
                          <div className="grid grid-cols-1 gap-3">
                            {adminDocs.map(doc => {
                              const needsSignature = !!doc.template_id;
                              const dateLabel = formatDocDate(doc.created_at);
                              return (
                                <div key={doc.id} className="bg-white p-4 rounded-2xl border border-gray-100 flex items-center justify-between group hover:border-violet-200 transition-all shadow-sm">
                                  <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-violet-50 text-violet-700 rounded-xl flex items-center justify-center">
                                      <FileText size={20} />
                                    </div>
                                    <div>
                                      <p className="font-bold text-gray-900 text-sm">{doc.nom}</p>
                                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                        {dateLabel ? `${needsSignature ? 'Généré' : 'Ajouté'} le ${dateLabel}` : (doc.type_document || 'Document')}
                                      </p>
                                    </div>
                                  </div>
                                  {needsSignature ? (
                                    <div className="flex items-center gap-2">
                                      <button 
                                        onClick={() => setViewingSession({ session: { ...doc, file_url: doc.url }, mode: 'view' })}
                                        className="p-2.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                                        title="Consulter"
                                      >
                                        <Eye size={20} />
                                      </button>
                                      {!doc.signe_par_formateur ? (
                                        <button 
                                          onClick={() => setViewingSession({ session: { ...doc, file_url: doc.url }, mode: 'sign' })}
                                          className="bg-violet-700 hover:bg-violet-700 text-white px-5 py-2.5 rounded-xl text-xs font-black shadow-lg shadow-violet-100 flex items-center gap-2 transition-all transform active:scale-95"
                                        >
                                          <PenTool size={14} /> Signer le document
                                        </button>
                                      ) : (
                                        <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-4 py-2 rounded-xl text-xs font-black border border-green-100">
                                          <Check size={14} strokeWidth={4} /> Signé
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => openSecureStorageFile(doc.url)}
                                        className="bg-white text-violet-700 hover:bg-violet-600 hover:text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-violet-200 transition-all"
                                      >
                                        Ouvrir
                                      </button>
                                      <button
                                        onClick={() => handleDownloadNamedFile(doc, client.nom_complet || client.nom)}
                                        className="text-gray-400 hover:text-violet-700 hover:bg-violet-50 p-2 rounded-lg transition-all"
                                        title="Télécharger sous le nom indiqué"
                                      >
                                        <Download size={18} />
                                      </button>
                                      <button
                                        onClick={() => showDeleteConfirm(
                                          `Supprimer "${doc.nom}" ?`,
                                          'Ce document sera définitivement retiré du dossier du client.',
                                          async () => { hideDeleteConfirm(); await handleDeleteDossierDoc(doc.id); }
                                        )}
                                        className="text-gray-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                                        title="Supprimer ce document"
                                      >
                                        <Trash2 size={18} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Upload formateur — ajouter un document au dossier client */}
                      <div className="mt-6 pt-5 border-t border-dashed border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-2 h-4 bg-indigo-400 rounded-full"></span>
                          <h4 className="font-black text-gray-700 text-xs uppercase tracking-tight">Ajouter un document au dossier client</h4>
                        </div>
                        {uploadingClientId === client.id ? (
                          <div className="bg-indigo-50 rounded-2xl p-4 space-y-3 border border-indigo-100">
                            <input
                              type="text"
                              value={clientDocName}
                              onChange={e => setClientDocName(e.target.value)}
                              placeholder="Nom du document (ex: Facture finale)"
                              className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl p-3 outline-none focus:ring-2 focus:ring-indigo-300"
                            />
                            <input
                              type="file"
                              onChange={e => {
                                const f = e.target.files[0] || null;
                                setClientDocFile(f);
                                // Pré-remplit le nom avec celui du fichier (sans l'extension) — le formateur peut
                                // ensuite le modifier librement ; on ne remplace pas un nom déjà saisi à la main.
                                if (f && !clientDocName.trim()) {
                                  setClientDocName(f.name.replace(/\.[^/.]+$/, ''));
                                }
                              }}
                              className="w-full bg-white border border-gray-200 text-sm rounded-xl p-2 outline-none"
                              accept=".pdf,.doc,.docx,image/*"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleUploadForClient(client.id)}
                                disabled={isUploadingClientDoc}
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black py-2.5 rounded-xl transition-all disabled:opacity-50"
                              >
                                {isUploadingClientDoc ? 'Envoi en cours...' : 'Envoyer le document'}
                              </button>
                              <button
                                onClick={() => { setUploadingClientId(null); setClientDocFile(null); setClientDocName(''); }}
                                className="px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-50 transition-all"
                              >
                                Annuler
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setUploadingClientId(client.id)}
                            className="flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-black px-4 py-2.5 rounded-xl border border-indigo-100 transition-all"
                          >
                            <Upload size={14} /> Insérer un document (facture, justificatif...)
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {formateurClientTab === 'docs_signes' && (
                    <div className="mb-4 space-y-6">

                      {/* --- Section 1 : Suivi des documents du client --- */}
                      {(() => {
                        const clientDocs = (documents || []).filter(d =>
                          String(d.user_id) === String(client.id) &&
                          d.visible_formateur !== false
                        );
                        if (clientDocs.length === 0) return null;
                        return (
                          <div>
                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Suivi des documents</h4>
                            <div className="overflow-hidden rounded-2xl border border-gray-100">
                              <table className="w-full text-left text-sm">
                                <thead className="bg-gray-50 text-gray-400 font-bold uppercase text-xs tracking-widest">
                                  <tr>
                                    <th className="px-4 py-3">Document</th>
                                    <th className="px-4 py-3">Statut</th>
                                    <th className="px-4 py-3 text-right">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 bg-white">
                                  {clientDocs.map(doc => {
                                    const isSigned = !!doc.signe_par_client;
                                    const noSignatureNeeded = !docRequiresSignature(doc); // FIX (2026-10-05)
                                    const fileUrl = doc.url || doc.file_url;
                                    return (
                                      <tr key={doc.id} className={`hover:bg-gray-50 transition-colors ${!isSigned ? 'bg-amber-50/30' : ''}`}>
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSigned ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                                              {isSigned ? <FileCheck size={16} /> : <FileText size={16} />}
                                            </div>
                                            <span className="font-semibold text-gray-800 text-xs">{doc.nom || 'Document'}</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${noSignatureNeeded ? 'bg-sky-100 text-sky-700' : isSigned ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {noSignatureNeeded ? '📥 Transmis' : isSigned ? '✓ Signé' : '⚠ En attente'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          {fileUrl && (
                                            <button
                                              onClick={() => openSecureStorageFile(fileUrl)}
                                              className="inline-flex items-center gap-1.5 bg-white text-indigo-700 px-3 py-1.5 rounded-xl text-xs font-bold border border-indigo-200 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                                            >
                                              <Eye size={13} /> Consulter
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}

                      {/* --- Section 2 : Émargements de séances signés --- */}
                      {(() => {
                        const signedSessions = clientSessions.filter(s =>
                          (s.statut === 'Signé' || s.statut_client === 'Signé') &&
                          (s.signed_pdf_url || s.file_url_signed || s.metadata?.file_url_signed)
                        );
                        if (signedSessions.length === 0) return null;
                        return (
                          <div>
                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Émargements de séances</h4>
                            <div className="overflow-hidden rounded-2xl border border-gray-100">
                              <table className="w-full text-left text-sm">
                                <thead className="bg-gray-50 text-gray-400 font-bold uppercase text-xs tracking-widest">
                                  <tr>
                                    <th className="px-4 py-3">Séance</th>
                                    <th className="px-4 py-3">Date de signature</th>
                                    <th className="px-4 py-3 text-right">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 bg-white">
                                  {signedSessions.map(session => {
                                    const signedUrl = session.signed_pdf_url || session.file_url_signed || session.metadata?.file_url_signed;
                                    const dateSign = session.date_signature_client || session.date_signature_formateur || session.date_signature || session.updated_at;
                                    return (
                                      <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-green-100 text-green-600 rounded-lg flex items-center justify-center shrink-0">
                                              <FileCheck size={16} />
                                            </div>
                                            <span className="font-semibold text-gray-800 text-xs">{session.ressource_titre || session.nom || session.titre || 'Émargement Signé'}</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          {dateSign ? new Date(dateSign).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          <button
                                            onClick={() => handleDownloadNamedFile({ url: signedUrl, nom: session.ressource_titre || session.nom || session.titre || 'Émargement signé' }, client.nom_complet || client.nom)}
                                            className="inline-flex items-center gap-1.5 bg-white text-green-700 px-3 py-1.5 rounded-xl text-xs font-bold border border-green-200 hover:bg-green-600 hover:text-white transition-all shadow-sm"
                                          >
                                            <Download size={13} /> Télécharger
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}

                      {/* --- État vide global --- */}
                      {(() => {
                        const hasClientDocs = (documents || []).some(d => String(d.user_id) === String(client.id) && d.visible_formateur !== false);
                        const hasSignedSessions = clientSessions.some(s =>
                          (s.statut === 'Signé' || s.statut_client === 'Signé') &&
                          (s.signed_pdf_url || s.file_url_signed || s.metadata?.file_url_signed)
                        );
                        if (!hasClientDocs && !hasSignedSessions) return (
                          <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                            <FileCheck className="mx-auto mb-3 text-gray-300" size={32} />
                            <p className="text-gray-400 text-sm italic">Aucun document disponible pour ce client.</p>
                          </div>
                        );
                        return null;
                      })()}

                    </div>
                  )}

                  {formateurClientTab === 'seances' && (
                    <>
                      <div className="flex flex-wrap items-center gap-3">
                        <h4 className="font-bold text-gray-800 flex items-center">
                          <span className="w-2 h-5 bg-indigo-500 rounded-full mr-2"></span>
                          Planning des Séances - {assignedModule?.nom || 'Sans module'}
                        </h4>

                        {(userRole === 'admin' || userRole === 'formateur') && (
                          <button
                            onClick={() => handleAddSession(client)}
                            className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border border-indigo-100 flex items-center"
                            title="Ajouter une séance"
                          >
                            <span className="mr-1.5">➕</span> Ajouter une séance
                          </button>
                        )}
                      </div>

                      <div className="mb-4 flex flex-wrap gap-2"></div>

                      {clientSessions.length > 0 ? (
                        <div className="overflow-hidden rounded-2xl border border-gray-100">
                          <table className="mst w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-400 font-bold uppercase text-xs tracking-widest">
                              <tr>
                                <th className="px-4 py-3 text-left">N° & Séance</th>
                                <th className="px-4 py-3 text-left">Date</th>
                                <th className="px-4 py-3 text-left">Horaires (Début/Fin)</th>
                                <th className="px-4 py-3 text-left">Statut</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {(() => {
                                const grouped = clientSessions.reduce((acc, s) => {
                                  const key = s.numero_seance;
                                  if (!acc[key]) acc[key] = { numero: s.numero_seance, nom: (s.nom || '').split(' - ')[0], date: s.date, debut: s.heure_debut, fin: s.heure_fin, items: [] };
                                  acc[key].items.push(s);
                                  fillGroupSchedule(acc[key], s);
                                  return acc;
                                }, {});

                                return (
                                  <DndContext
                                    onDragStart={({ active }) => setFActiveId(active.id)}
                                    onDragEnd={({ active, over }) => {
                                      if (over && handleMoveSessionItem) {
                                        const itemId = String(active.id).replace('fi-', '');
                                        const src = sessions.find(s => String(s.id) === itemId);
                                        const targetGroup = Object.values(grouped).find(g => `fg-${g.numero}` === over.id);
                                        if (src && targetGroup && src.numero_seance !== targetGroup.numero) {
                                          handleMoveSessionItem(src.id, targetGroup.numero, targetGroup.date, targetGroup.debut, targetGroup.fin);
                                        }
                                      }
                                      setFActiveId(null);
                                    }}
                                  >
                                    {Object.values(grouped).sort((a, b) => a.numero - b.numero).map((group, gIdx) => (
                                  <React.Fragment key={gIdx}>
                                    <FDropGroupRow groupNum={group.numero} hasActive={fActiveId != null}>
                                      {(isGroupOver) => (
                                        <>
                                          <td className="px-4 py-3 font-black text-indigo-900 border-l-4 border-indigo-500">
                                            <div className="flex items-center gap-2 text-xs">
                                              <Layout size={12} className="text-indigo-600" />
                                              <span>SÉANCE {group.numero}{group.nom && !/^s[ée]ance\s*\d+$/i.test(String(group.nom).trim()) ? ` : ${group.nom}` : ''}</span>
                                              {isGroupOver && fActiveId && (
                                                <span className="ml-2 text-[11px] font-black text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded-full animate-pulse">Déposer ici</span>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-4 py-3" data-label="Date de la séance">
                                            <SessionDateInput
                                              value={group.date || ''}
                                              onCommit={(v) => { group.items.forEach(s => updateSessionDate(s.id, v)); }}
                                              className="border-none bg-transparent font-bold text-indigo-700 text-xs focus:ring-0 outline-none w-full"
                                            />
                                          </td>
                                          <td className="px-4 py-3" data-label="Horaires (début / fin)">
                                            <div className="flex items-center gap-2">
                                              <div className="mst-times flex flex-col gap-0.5">
                                                <input
                                                  type="time"
                                                  value={editedTimes[group.items[0]?.id]?.start ?? group.debut ?? ''}
                                                  onChange={(e) => group.items.forEach(s => onTimeChange(s.id, 'start', e.target.value, { start: group.debut, end: group.fin }))}
                                                  onBlur={() => group.items.forEach(s => onSaveTimes(s.id))}
                                                  className="bg-transparent border-none text-xs w-24 font-bold text-indigo-600 focus:ring-0"
                                                />
                                                <input
                                                  type="time"
                                                  value={editedTimes[group.items[0]?.id]?.end ?? group.fin ?? ''}
                                                  onChange={(e) => group.items.forEach(s => onTimeChange(s.id, 'end', e.target.value, { start: group.debut, end: group.fin }))}
                                                  onBlur={() => group.items.forEach(s => onSaveTimes(s.id))}
                                                  className="bg-transparent border-none text-xs w-24 font-bold text-indigo-600 focus:ring-0"
                                                />
                                              </div>
                                              <button onClick={() => group.items.forEach(s => onSaveTimes(s.id))} className="text-indigo-400 hover:text-indigo-600">
                                                <Save size={14} />
                                              </button>
                                            </div>
                                          </td>
                                          <td colSpan="2" className="mst-hide px-4 py-3 text-right">

                                          </td>
                                        </>
                                      )}
                                    </FDropGroupRow>

                                {/* Nested Items — Draggable */}
                                {group.items.map(session => (
                                  <FDragItemRow key={session.id} sessionId={session.id} activeId={fActiveId}>
                                    <td className="px-8 py-3 italic text-gray-600 text-[11px] flex items-center gap-2">
                                      <span className="text-[11px] text-gray-300 mr-0.5" title="Glisser pour déplacer">⠿</span>
                                      <span className="text-[14px]">
                                        {session.type_activite === 'signature' ? '✍️' : session.type_activite === 'document' ? '📄' : '⚙️'}
                                      </span>
                                      <span className="truncate">{session.ressource_titre || session.nom}</span>
                                    </td>
                                    <td colSpan="2" className="mst-hide px-4 py-3 text-xs text-gray-400 italic">Mêmes date et horaires que la séance</td>
                                    <td className="px-4 py-3">
                                      <div className="flex flex-col gap-1">
                                        {/* AFFICHAGE (2026-10-20) : seulement les signatures réellement attendues. */}
                                        {signaturesAttendues(session).client && (
                                        <div className="flex items-center gap-1.5">
                                          <span className={`w-1.5 h-1.5 rounded-full ${session.statut_client === 'Signé' ? 'bg-green-500' : 'bg-orange-400'}`}></span>
                                          <span className="text-[11px] font-black uppercase text-gray-500">Client : {session.statut_client || (session.statut === 'Signé' ? 'Signé' : 'À venir')}</span>
                                        </div>
                                        )}
                                        {signaturesAttendues(session).formateur && (
                                        <div className="flex items-center gap-1.5">
                                          <span className={`w-1.5 h-1.5 rounded-full ${session.statut_formateur === 'Signé' ? 'bg-green-500' : 'bg-orange-400'}`}></span>
                                          <span className="text-[11px] font-black uppercase text-gray-500">Formateur : {session.statut_formateur || (session.type_activite === 'signature' && session.statut === 'Signé' ? 'Signé' : 'À venir')}</span>
                                        </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="flex justify-end items-center gap-2">
                                        {(() => {
                                          const sessionDate = session.date || group.date;
                                          const signLock = signatureLockFor(sessionDate, session.numero_seance ?? group.numero);
                                          const isDateLocked = signLock.locked;
                                          const metadata = session.metadata || {};
                                          const signedUrl = session.file_url_signed || metadata.file_url_signed;
                                          const isToSign = metadata.isToSign || session.type_activite === 'signature';
                                          // FIX (2026-09-25 bis) : le bouton "Signer" du formateur ne doit refléter QUE
                                          // sa propre signature (statut_formateur), jamais le statut global "statut" —
                                          // sinon il apparaît "Signé ✓" dès que le CLIENT a signé, alors que le
                                          // formateur lui-même n'a rien signé (bug repéré le 25/09/2026, séance déjà
                                          // corrigée côté écriture mais toujours affichée à tort ici côté lecture).
                                          const isSigned = session.statut_formateur === 'Signé';

                                          return (
                                            <div className="flex gap-2 items-center">
                                              {(session.type_activite === 'document' || session.type_activite === 'exercice' || session.type_activite === 'Exercice') && (
                                                <button
                                                  onPointerDown={e => e.stopPropagation()}
                                                  onClick={() => {
                                                    const docUrl = signedUrl || session.file_url || session.ressource_url;
                                                    setViewingSession({ session: { ...session, file_url: docUrl }, mode: 'view' });
                                                  }}
                                                  className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all ${signedUrl ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'}`}
                                                >
                                                  {signedUrl ? 'Voir Signé ↗' : 'Consulter'}
                                                </button>
                                              )}

                                              {isToSign && (
                                                <button
                                                  disabled={isSigned || isDateLocked}
                                                  onPointerDown={e => e.stopPropagation()}
                                                  onClick={() => signSession(session)}
                                                  className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all ${isSigned ? 'bg-green-50 text-green-600 border-green-200' : isDateLocked ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'bg-violet-600 text-white border-violet-700 hover:bg-violet-700'}`}
                                                >
                                                  {isSigned ? 'Signé ✓' : isDateLocked ? signLock.label : 'Signer'}
                                                </button>
                                              )}

                                              {signedUrl && (
                                                <button
                                                  onPointerDown={e => e.stopPropagation()}
                                                  onClick={() => handleDownloadNamedFile({ url: signedUrl, nom: session.ressource_titre || session.nom || 'Document signé' }, client.nom_complet || client.nom)}
                                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                  title="Télécharger"
                                                >
                                                  <FileCheck size={16} />
                                                </button>
                                              )}
                                                {userRole === 'client' && (
                                                  <label className="text-xs font-bold px-3 py-1.5 rounded-lg bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700 transition-colors">
                                                    Soumettre Réponse
                                                    <input
                                                      type="file"
                                                      className="hidden"
                                                      onChange={(e) => e.target.files[0] && handleUploadExerciseResponse(session.id, e.target.files[0])}
                                                    />
                                                  </label>
                                                )}
                                              {(userRole === 'admin' || userRole === 'formateur') && (session.type_activite === 'exercice' || session.type_activite === 'Exercice') && (
                                                session.reponse_url ? (
                                                  <button
                                                    onPointerDown={e => e.stopPropagation()}
                                                    onClick={() => setCorrectionModalSession(session)}
                                                    className={`relative text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1.5 ${session.correction_statut === 'Validé' ? 'bg-green-600 text-white hover:bg-green-700' : session.correction_statut === 'À corriger' ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                                                  >
                                                    {session.correction_statut === 'Validé' ? (
                                                      <>✅ Validé</>
                                                    ) : session.correction_statut === 'À corriger' ? (
                                                      <>📝 À corriger</>
                                                    ) : (
                                                      <>
                                                        <span className="relative flex h-2 w-2">
                                                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                                                        </span>
                                                        Corriger
                                                      </>
                                                    )}
                                                  </button>
                                                ) : (
                                                  <span className="text-xs font-bold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-400 flex items-center gap-1.5">
                                                    Aucun rendu
                                                  </span>
                                                )
                                              )}
                                              <button
                                                onPointerDown={e => e.stopPropagation()}
                                                onClick={() => handleDeleteSession(session)}
                                                className="text-gray-300 hover:text-red-500 transition-colors p-1"
                                              >
                                                <Trash2 size={14} />
                                              </button>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                  </FDragItemRow>
                                ))}
                                {/* Footer row with Add button */}
                                <tr className="bg-gray-50/20">
                                  <td colSpan="5" className="px-8 py-2 text-left border-l border-gray-100">
                                    <button
                                      onClick={() => {
                                        setTargetSessionForAddition({
                                          ...group,
                                          clientId: client.id,
                                          preSelectedSessionId: group.items[0]?.id
                                        });
                                        setIsSessionItemModalOpen(true);
                                      }}
                                      className="text-xs font-black bg-white text-indigo-600 px-4 py-1.5 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm border border-indigo-100 flex items-center gap-2"
                                    >
                                      <Plus size={12} /> Ajouter un élément
                                    </button>
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </DndContext>
                        );
                      })()}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                      <p className="text-gray-400 text-sm italic">Aucune séance n'est encore enregistrée pour ce client.</p>
                    </div>
                  )}
                  </>
                )}

                  {formateurClientTab === 'exercices' && (() => {
                    const clientExercises = allClientSessions.filter(s => s.type_activite === 'exercice' || s.type_activite === 'Exercice');
                    const seanceNumbers = [...new Set(allClientSessions.map(s => s.numero_seance).filter(n => n != null && Number(n) > 0).map(Number))].sort((a, b) => a - b);
                    const todayStr = jourLocalISO(new Date());
                    const defaultNumero = (() => {
                      const past = allClientSessions.filter(s => s.numero_seance && s.date && s.date <= todayStr).map(s => Number(s.numero_seance));
                      return past.length ? Math.max(...past) : (seanceNumbers[0] || 1);
                    })();
                    const openAddExo = () => { setAddExoForm({ title: '', numero: String(defaultNumero), file: null }); setShowAddExo(true); };
                    const submitAddExo = async () => {
                      if (!addExoForm.title.trim()) { toast.error("Indiquez le nom de l'exercice."); return; }
                      if (!addExoForm.file) { toast.error('Choisissez la photo ou le fichier de l\'exercice.'); return; }
                      if (!handleAddCompletedExercise) return;
                      setIsAddingExo(true);
                      const ok = await handleAddCompletedExercise({ client, numero: Number(addExoForm.numero || defaultNumero), title: addExoForm.title.trim(), file: addExoForm.file });
                      setIsAddingExo(false);
                      if (ok) { setShowAddExo(false); setAddExoForm({ title: '', numero: '', file: null }); }
                    };
                    const depositRendu = async (session, file) => {
                      if (!file) return;
                      setExoUploadingId(session.id);
                      await handleUploadExerciseResponse(session.id, file, { byTrainer: true });
                      setExoUploadingId(null);
                    };
                    const addExoBlock = (
                      <div className="space-y-3">
                        {!showAddExo ? (
                          <button
                            onClick={openAddExo}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all"
                          >
                            <Plus size={14} /> Ajouter un exercice réalisé en séance
                          </button>
                        ) : (
                          <div className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4 space-y-3">
                            <p className="text-xs font-black text-emerald-800 uppercase tracking-tight">Exercice réalisé pendant une séance</p>
                            <p className="text-[11px] text-emerald-700">Déposez la photo, le PDF ou le document de l'exercice fait avec le client : il sera rangé dans ses exercices (visible par lui) comme « rendu ».</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="sm:col-span-2">
                                <label className="block text-xs font-black text-gray-500 uppercase mb-1">Nom de l'exercice</label>
                                <input
                                  value={addExoForm.title}
                                  onChange={e => setAddExoForm(f => ({ ...f, title: e.target.value }))}
                                  placeholder="Ex : Ligne de vie, Test des valeurs..."
                                  className="w-full p-3 text-sm border bg-white border-gray-200 focus:border-emerald-500 rounded-xl outline-none"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-black text-gray-500 uppercase mb-1">Séance</label>
                                <select
                                  value={addExoForm.numero}
                                  onChange={e => setAddExoForm(f => ({ ...f, numero: e.target.value }))}
                                  className="w-full p-3 text-sm border bg-white border-gray-200 focus:border-emerald-500 rounded-xl outline-none"
                                >
                                  {(seanceNumbers.length ? seanceNumbers : [1]).map(n => <option key={n} value={n}>Séance {n}</option>)}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-black text-gray-500 uppercase mb-1">Photo ou fichier</label>
                              <input
                                type="file"
                                accept={EXO_ACCEPT}
                                onChange={e => setAddExoForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                                className="block w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-white file:text-emerald-700 file:font-bold"
                              />
                              <p className="text-xs text-gray-400 mt-1">Sur téléphone, vous pouvez prendre la photo directement.</p>
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                              <button onClick={() => setShowAddExo(false)} disabled={isAddingExo} className="px-4 py-2 text-xs font-bold text-gray-500 hover:bg-white rounded-xl">Annuler</button>
                              <button onClick={submitAddExo} disabled={isAddingExo} className="px-4 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl disabled:opacity-50">
                                {isAddingExo ? 'Envoi en cours…' : "Enregistrer l'exercice"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                    if (clientExercises.length === 0) return (
                      <div className="space-y-4">
                        {addExoBlock}
                        <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                          <FileCheck className="mx-auto mb-3 text-gray-300" size={32} />
                          <p className="text-gray-400 text-sm italic">Aucun exercice assigné à ce client pour le moment.</p>
                        </div>
                      </div>
                    );

                    // Priorité d'affichage : rendu et en attente de correction d'abord, puis pas encore rendu, puis déjà traité.
                    const rank = (s) => {
                      if (s.reponse_url && s.correction_statut !== 'Validé' && s.correction_statut !== 'À corriger') return 0;
                      if (!s.reponse_url) return 1;
                      return 2;
                    };
                    const sortedExercises = [...clientExercises].sort((a, b) => rank(a) - rank(b));

                    return (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-5 bg-emerald-500 rounded-full"></span>
                          <h4 className="font-black text-gray-800 text-sm uppercase tracking-tight">Tous les exercices — rendus et en attente</h4>
                        </div>
                        {addExoBlock}
                        <div className="overflow-hidden rounded-2xl border border-gray-100">
                          <table className="mst w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-400 font-bold uppercase text-xs tracking-widest">
                              <tr>
                                <th className="px-4 py-3">Exercice</th>
                                <th className="px-4 py-3">Séance</th>
                                <th className="px-4 py-3">Statut</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 bg-white">
                              {sortedExercises.map(session => {
                                const docUrl = session.file_url_signed || session.metadata?.file_url_signed || session.file_url || session.ressource_url || session.reponse_url;
                                let badge;
                                if (!session.reponse_url) {
                                  badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-gray-100 text-gray-400">Pas encore rendu</span>;
                                } else if (session.correction_statut === 'Validé') {
                                  badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-green-100 text-green-700">✅ Validé</span>;
                                } else if (session.correction_statut === 'À corriger') {
                                  badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-700">📝 À corriger (envoyé)</span>;
                                } else {
                                  badge = <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">🟢 Rendu — à corriger</span>;
                                }
                                return (
                                  <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                      <span className="font-semibold text-gray-800 text-xs">{session.ressource_titre || session.nom}</span>
                                      {session.metadata?.deposeParFormateur && (
                                        <span className="block text-xs text-emerald-600 font-bold mt-0.5">Déposé par le formateur</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-400">{session.numero_seance != null ? `Séance ${session.numero_seance}` : (session.metadata?.moment === 'fin' ? 'Doc. de fin' : 'Doc. de début')}</td>
                                    <td className="px-4 py-3">{badge}</td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="flex justify-end items-center gap-2">
                                        <button
                                          onClick={() => setViewingSession({ session: { ...session, file_url: docUrl }, mode: 'view' })}
                                          className="text-xs font-bold px-3 py-1.5 rounded-lg border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 transition-all"
                                        >
                                          Consulter
                                        </button>
                                        {!session.reponse_url && (
                                          <label className={`text-xs font-bold px-3 py-1.5 rounded-lg border cursor-pointer transition-all ${exoUploadingId === session.id ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`}>
                                            {exoUploadingId === session.id ? 'Envoi…' : 'Déposer le rendu'}
                                            <input
                                              type="file"
                                              accept={EXO_ACCEPT}
                                              className="hidden"
                                              disabled={exoUploadingId === session.id}
                                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; depositRendu(session, f); }}
                                            />
                                          </label>
                                        )}
                                        {session.reponse_url && (
                                          <button
                                            onClick={() => openSecureStorageFile(session.reponse_url)}
                                            className="text-xs font-bold px-3 py-1.5 rounded-lg border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 transition-all"
                                          >
                                            Voir le rendu
                                          </button>
                                        )}
                                        {session.reponse_url && (
                                          <button
                                            onClick={() => setCorrectionModalSession(session)}
                                            className={`text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all ${session.correction_statut === 'Validé' ? 'bg-green-600 text-white hover:bg-green-700' : session.correction_statut === 'À corriger' ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                                          >
                                            {session.correction_statut === 'Validé' ? 'Validé' : session.correction_statut === 'À corriger' ? 'Modifier' : 'Corriger'}
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        }) : (
          <div className="col-span-full py-12 text-center bg-white rounded-3xl border-2 border-dashed border-gray-200">
            <p className="text-gray-400 font-medium italic">{chargement ? 'Chargement de vos clients…' : 'Aucun client ne vous est assigné actuellement.'}</p>
          </div>
        )}
      </div>
      )}
      <CorrectionModal
        isOpen={!!correctionModalSession}
        onClose={() => setCorrectionModalSession(null)}
        session={correctionModalSession}
        onSave={handleSaveCorrection}
      />
      <ConfirmModal
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={hideDeleteConfirm}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   VisualTemplateEditor — éditeur style Yousign
   Upload DOCX → aperçu PDF → drag-drop balises sur le document
   ───────────────────────────────────────────────────────── */
const VisualTemplateEditor = ({ isOpen, onClose, onSave, initialData }) => {
  const [step, setStep] = React.useState('upload'); // 'upload' | 'converting' | 'editor'
  const [file, setFile] = React.useState(null);
  const [pdfPages, setPdfPages] = React.useState([]); // [{dataUrl, nativeW, nativeH}]
  const [currentPage, setCurrentPage] = React.useState(0);
  const [fields, setFields] = React.useState([]); // [{id, tag, page, xPct, yPct}]
  const [dragTag, setDragTag] = React.useState(null);
  const [draggingFieldId, setDraggingFieldId] = React.useState(null); // repositionnement d'un champ existant
  // FIX (2026-09-04) : redimensionnement d'une case "texte libre" (largeur/hauteur, en % de la page,
  // comme x/y) via une poignée glissable — voir le rendu isTxt plus bas et l'effet ci-dessous.
  const [resizingFieldId, setResizingFieldId] = React.useState(null);
  const resizeStartRef = React.useRef(null);
  const [clickPlaceTag, setClickPlaceTag] = React.useState(null); // { tag } ou { fieldId } — mode clic-pour-placer
  const [hoverPos, setHoverPos] = React.useState(null); // position survol pour ghost cursor
  // AJOUT (2026-09-18) : sélection multiple par "lasso" (cliquer-glisser sur le fond de page pour
  // entourer plusieurs balises) + déplacement groupé — demandé par l'utilisateur pour pouvoir bouger
  // toute une ligne (ex. les 3 balises d'un calendrier prévisionnel) d'un seul geste au lieu de les
  // glisser une par une. Voir startRubberBand/handleDuplicateRow (déplacement groupé) plus bas.
  const [selectedFieldIds, setSelectedFieldIds] = React.useState(() => new Set());
  const [rubberBandActive, setRubberBandActive] = React.useState(false);
  const [rubberBandRect, setRubberBandRect] = React.useState(null); // {minX, maxX, minY, maxY} en % — pour le rectangle affiché
  const rubberBandStartRef = React.useRef(null); // {xPct, yPct} figé au mousedown
  // FIX (2026-09-18) : remplace l'ancien glissement natif du navigateur (draggable/onDragStart/onDrop)
  // pour repositionner une balise déjà posée — ce dernier ne montrait AUCUN aperçu pendant le geste (la
  // balise "sautait" à sa nouvelle position seulement au relâchement), ce qui était gênant pour juger où
  // atterrirait un déplacement, surtout en groupe. On suit désormais la souris nous-mêmes (mousedown +
  // mousemove + mouseup, comme la poignée de redimensionnement ou le lasso) et on met à jour la position
  // réelle de la ou des balises à CHAQUE mouvement, aimantation comprise — déplacement 100% visible en
  // direct. Ne concerne que le déplacement d'une balise déjà posée (seule ou en groupe sélectionné) ;
  // le dépôt d'une NOUVELLE balise depuis le panneau de droite reste en glissement natif (dragTag).
  const moveDragRef = React.useRef(null); // { fieldId, origins: Map<id,{xPct,yPct}>, startClientX, startClientY }
  const [isMoveDragging, setIsMoveDragging] = React.useState(false);
  const justDraggedRef = React.useRef(false); // évite qu'un vrai glissement ne déclenche aussi le clic (mode clic-pour-placer) au relâchement
  const [templateName, setTemplateName] = React.useState('');
  // Destinataires (2026-07-27) : un ou plusieurs parmi client/formateur/organisme, cochables librement
  // (remplace l'ancien choix unique 'client' | 'formateur' | 'both'). Stocké en base sous forme de
  // chaîne "client,formateur,organisme" via stringifyDestinationRoles/parseDestinationRoles (module-level).
  const [destinationRoles, setDestinationRoles] = React.useState(['client']);
  // Mode de signature (Stage 3, 2026-07-24 ; généralisé à 3 parties le 2026-07-27) : réglé une fois sur
  // le modèle, propagé à chaque document instancié (voir instantiateDocument) et lu par
  // isBlockedBySigningOrder pour masquer un document à la partie qui doit attendre son tour.
  const [signingMode, setSigningMode] = React.useState('simultane'); // 'simultane' | 'sequentiel'
  const [signingOrder, setSigningOrder] = React.useState(['client', 'formateur']); // tableau de rôles, dans l'ordre de signature
  const [isSaving, setIsSaving] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const pageRef = React.useRef(null);
  // AJOUT (2026-09-18) : map id de balise → élément DOM réel, pour tester l'intersection du lasso avec
  // la vraie zone affichée de chaque balise (et non un simple point) — voir startRubberBand plus bas.
  const fieldRefs = React.useRef({});
  const pdfBlobRef = React.useRef(null); // stocke le blob PDF pour prévisualisation

  // Garde signingOrder cohérent avec les destinataires cochés : retire les rôles décochés, ajoute
  // les rôles nouvellement cochés à la fin de l'ordre existant (plutôt que de tout réinitialiser).
  React.useEffect(() => {
    setSigningOrder(prev => {
      const kept = prev.filter(r => destinationRoles.includes(r));
      const added = destinationRoles.filter(r => !kept.includes(r));
      return [...kept, ...added];
    });
  }, [destinationRoles]);

  // AJOUT (2026-09-15) : 'date_signature' existait déjà pour le Client, mais pas d'équivalent pour
  // le Formateur ni l'Organisme (Administrateur) — demandé par l'utilisateur le 15/09/2026. On ajoute
  // 'date_signature_formateur' et 'date_signature_organisme' (même valeur : la date du jour où le
  // document est généré, exactement comme 'date_signature' pour le client — voir dataValues/
  // resolvedValues/dataToMerge plus bas, tous mis à jour en conséquence). On NE renomme PAS
  // 'date_signature' en 'date_signature_client' pour ne pas casser les modèles déjà créés qui
  // utilisent cette balise.
  // AJOUT (2026-09-16) : balises "initiales" (client, formateur, organisme), demandé par l'utilisateur
  // — voir computeInitials() en haut du fichier. Pour 'initiales_organisme', confirmé avec
  // l'utilisateur (16/09/2026) : basé sur le NOM DE L'ORGANISME (org_nom, ex. "VB Coaching" → "VC"),
  // pas sur une personne admin précise — cohérent avec les autres balises Organisme (toutes au niveau
  // société), et fonctionne pour tout organisme sans configuration supplémentaire.
  const ALL_TAGS = {
    'Client': ['nomcomplet_client', 'numero_dossier_client', 'client_email', 'client_phone', 'adresse_client', 'rue_client', 'code_postal_client', 'ville_client', 'region_client', 'adresse_session', 'prix_prestation', 'formation_nom', 'modalite_formation', 'date_debut', 'date_fin', 'date_signature', 'initiales_client'],
    'Formateur': ['nom_formateur', 'email_formateur', 'tel_formateur', 'adresse_formateur', 'rue_formateur', 'code_postal_formateur', 'ville_formateur', 'region_formateur', 'formateur_siret', 'formateur_nda', 'compagnie_assurance', 'numero_assurance_rcp', 'date_signature_formateur', 'initiales_formateur'],
    'Organisme': ['org_nom', 'org_siret', 'org_nda', 'org_adresse', 'org_code_postal', 'org_ville', 'org_region', 'org_site_web', 'date_signature_organisme', 'initiales_organisme'],
    'Financement': ['financement_type', 'financeur_nom', 'montant_financeur', 'reste_a_charge', 'numero_prise_en_charge'],
    'Groupe / entreprise': ['entreprise_nom', 'entreprise_adresse', 'entreprise_signataire', 'entreprise_signataire_fonction', 'groupe_nom', 'liste_stagiaires', 'nombre_stagiaires', 'duree_heures', 'lieu_formation'],
    'Divers': ['date_du_jour'],
  };
  const SIGNATURE_TAGS = [
    { tag: 'signature_client', label: 'Signature client', color: 'blue' },
    { tag: 'signature_formateur', label: 'Signature formateur', color: 'orange' },
    { tag: 'signature_organisme', label: 'Signature administrateur', color: 'emerald' },
  ];
  const isSignatureTag = (tag) => !!roleFromTag(tag) && tag.startsWith('signature_');
  const sigTagColor = (tag) => {
    const role = roleFromTag(tag) || 'client';
    return role === 'client'
      ? { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-600', badgeTxt: 'text-white' }
      : role === 'formateur'
      ? { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-500', badgeTxt: 'text-white' }
      : { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-600', badgeTxt: 'text-white' };
  };

  // ── Cases à cocher (sans texte — le texte est déjà imprimé dans le document) ──
  // Peuvent être posées plusieurs fois, comme n'importe quelle balise, pour ajouter plusieurs cases distinctes.
  const CHECKBOX_TAGS = [
    { tag: 'checkbox_client', label: 'Case à cocher client', color: 'blue' },
    { tag: 'checkbox_formateur', label: 'Case à cocher formateur', color: 'orange' },
    { tag: 'checkbox_organisme', label: 'Case à cocher administrateur', color: 'emerald' },
  ];
  const isCheckboxTag = (tag) => !!roleFromTag(tag) && tag.startsWith('checkbox_');

  // ── Champs texte libre (rempli par le signataire au moment de signer, façon Yousign) ──
  // Comme les cases à cocher : peuvent être posés plusieurs fois, et sont identifiés par fieldKey()
  // (pas par leur tag) puisqu'un même tag peut désigner plusieurs champs distincts sur un document.
  const TEXT_INPUT_TAGS = [
    { tag: 'texte_client', label: 'Texte libre client', color: 'blue' },
    { tag: 'texte_formateur', label: 'Texte libre formateur', color: 'orange' },
    { tag: 'texte_organisme', label: 'Texte libre administrateur', color: 'emerald' },
  ];
  const isTextInputTag = (tag) => !!roleFromTag(tag) && tag.startsWith('texte_');

  // Pré-chargement quand on édite un template existant (initialData fourni)
  React.useEffect(() => {
    if (!isOpen || !initialData?.url) return;
    setTemplateName(initialData.name || '');
    setDestinationRoles(parseDestinationRoles(initialData.destination));
    setSigningMode(initialData.signingMode || 'simultane');
    setSigningOrder(normalizeSigningOrder({ signing_order: initialData.signingOrder }));
    setStep('converting');
    (async () => {
      try {
        const resp = await fetch(initialData.url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        pdfBlobRef.current = blob; // stocker pour prévisualisation
        // Créer un File fictif pour que handleSave puisse le réutiliser
        const fakeFile = new File([blob], (initialData.name || 'template') + '.pdf', { type: 'application/pdf' });
        setFile(fakeFile);
        const pdfjsLib = await loadPdfJs();
        const arr = await blob.arrayBuffer();
        const pdfjsDoc = await pdfjsLib.getDocument({ data: new Uint8Array(arr) }).promise;
        const pages = [];
        const SCALE = 1.5;
        for (let i = 1; i <= pdfjsDoc.numPages; i++) {
          const page = await pdfjsDoc.getPage(i);
          const vp = page.getViewport({ scale: SCALE });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(vp.width);
          canvas.height = Math.floor(vp.height);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
          pages.push({ dataUrl: canvas.toDataURL('image/png'), nativeW: canvas.width, nativeH: canvas.height });
        }
        setPdfPages(pages);
        setCurrentPage(0);
        // Pré-charger les champs existants
        setFields(initialData.fields || []);
        setStep('editor');
      } catch(e) {
        toast.error('Chargement du template impossible : ' + e.message);
        setStep('upload');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  // Chargement dynamique de PDF.js depuis le CDN
  const loadPdfJs = () => new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Impossible de charger PDF.js'));
    document.head.appendChild(script);
  });

  const handleFileSelect = async (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext !== 'docx' && ext !== 'pdf') {
      toast.error('Seuls les fichiers .docx ou .pdf sont acceptés pour l\'éditeur visuel.');
      return;
    }
    setFile(f);
    if (!templateName) setTemplateName(f.name.replace(/\.[^/.]+$/, ''));
    setStep('converting');
    try {
      // 1. Obtenir un PDF — si PDF directement uploadé, pas de conversion nécessaire
      let pdfBlob;
      if (ext === 'pdf') {
        // PDF direct : aucune conversion, design préservé à 100%
        pdfBlob = new Blob([await f.arrayBuffer()], { type: 'application/pdf' });
      } else {
        // DOCX : convertir (ConvertAPI en priorité, local en fallback)
        try { pdfBlob = await convertDocxBlobToPdf(f); }
        catch (_) { pdfBlob = await convertDocxBlobToPdfLocal(f); }
      }
      pdfBlobRef.current = pdfBlob; // stocker pour prévisualisation

      // 2. Charger PDF.js et rendre chaque page en canvas
      const pdfjsLib = await loadPdfJs();
      const arr = await pdfBlob.arrayBuffer();
      const pdfjsDoc = await pdfjsLib.getDocument({ data: new Uint8Array(arr) }).promise;
      const pages = [];
      const SCALE = 1.5;
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        const page = await pdfjsDoc.getPage(i);
        const vp = page.getViewport({ scale: SCALE });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        pages.push({ dataUrl: canvas.toDataURL('image/png'), nativeW: canvas.width, nativeH: canvas.height });
      }
      setPdfPages(pages);
      setCurrentPage(0);
      setFields([]);
      setStep('editor');
    } catch (err) {
      toast.error('Chargement impossible : ' + err.message);
      setStep('upload');
    }
  };

  // FIX (2026-09-04) : suit la souris pendant le glissement de la poignée de redimensionnement
  // (mousedown posé directement sur la poignée, voir le rendu isTxt plus bas) et met à jour
  // width_percent/height_percent du champ en cours de redimensionnement en temps réel.
  React.useEffect(() => {
    if (!resizingFieldId) return;
    const onMove = (e) => {
      const st = resizeStartRef.current;
      if (!st || st.fieldId !== resizingFieldId) return;
      const dxPct = ((e.clientX - st.startClientX) / st.rectWidth) * 100;
      const dyPct = ((e.clientY - st.startClientY) / st.rectHeight) * 100;
      const newWidth = Math.max(6, Math.min(90, st.startWidthPct + dxPct));
      const newHeight = Math.max(1.5, Math.min(40, st.startHeightPct + dyPct));
      setFields(prev => prev.map(f => f.id === st.fieldId ? { ...f, width_percent: newWidth, height_percent: newHeight } : f));
    };
    const onUp = () => { setResizingFieldId(null); resizeStartRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizingFieldId]);

  // FIX (2026-09-18) : ne gère plus QUE le dépôt d'une NOUVELLE balise depuis le panneau de droite
  // (dragTag, toujours en glissement natif du navigateur) — le repositionnement d'une balise déjà
  // posée (seule ou en groupe) est désormais géré en direct par le glissement "maison" (voir
  // moveDragRef / handleFieldMouseDown plus bas), qui affiche la balise bouger en temps réel au lieu
  // d'attendre le dépôt pour la faire apparaître à sa nouvelle position.
  const handlePageDrop = (e) => {
    e.preventDefault();
    if (!pageRef.current || !dragTag) return;
    const rect = pageRef.current.getBoundingClientRect();
    const xPct = Math.max(2, Math.min(95, ((e.clientX - rect.left) / rect.width) * 100));
    const yPct = Math.max(2, Math.min(97, ((e.clientY - rect.top) / rect.height) * 100));
    // Nouveau champ depuis la sidebar — taille par défaut posée sur les champs texte libre
    // (redimensionnable ensuite via la poignée, voir le rendu isTxt plus bas), avec alignement
    // magnétique (2026-09-18) sur une balise existante proche.
    const snappedY = findSnappedY(yPct, currentPage + 1, []);
    setFields(prev => [...prev, {
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      tag: dragTag,
      page: currentPage + 1,
      xPct,
      yPct: snappedY,
      ...(isTextInputTag(dragTag) ? { width_percent: 28, height_percent: 3.5 } : {}),
    }]);
    setDragTag(null);
  };

  // Touche Escape pour annuler le mode clic-pour-placer
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') { setClickPlaceTag(null); setSelectedFieldIds(new Set()); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Clic sur la page : placer ou déplacer une balise (mode clic-pour-placer)
  const handlePageClick = (e) => {
    if (!clickPlaceTag || !pageRef.current) return;
    e.stopPropagation();
    const rect = pageRef.current.getBoundingClientRect();
    const xPct = Math.max(0, Math.min(98, ((e.clientX - rect.left) / rect.width) * 100));
    const yPct = Math.max(2, Math.min(98, ((e.clientY - rect.top) / rect.height) * 100));

    if (clickPlaceTag.fieldId) {
      // Repositionner un champ déjà posé — alignement magnétique (2026-09-18).
      const movedField = fields.find(f => f.id === clickPlaceTag.fieldId);
      const snappedY = movedField ? findSnappedY(yPct, movedField.page, [clickPlaceTag.fieldId]) : yPct;
      setFields(prev => prev.map(f => f.id === clickPlaceTag.fieldId ? { ...f, xPct, yPct: snappedY } : f));
    } else if (clickPlaceTag.tag) {
      // Chaque clic ajoute une nouvelle occurrence de la balise — une même balise peut être
      // posée plusieurs fois (ex: signature sur plusieurs pages, adresse rappelée 2x, etc.).
      // Pour repositionner une occurrence déjà posée, on clique directement dessus (mode fieldId ci-dessus).
      // Alignement magnétique (2026-09-18) sur une balise existante proche.
      const snappedY = findSnappedY(yPct, currentPage + 1, []);
      setFields(prev => [...prev, {
        id: `f_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        tag: clickPlaceTag.tag,
        page: currentPage + 1,
        xPct, yPct: snappedY,
        ...(isTextInputTag(clickPlaceTag.tag) ? { width_percent: 28, height_percent: 3.5 } : {}),
      }]);
    }
    setClickPlaceTag(null);
  };

  // AJOUT (2026-09-18) : alignement magnétique — quand on pose ou déplace une balise et qu'elle
  // s'approche du Y d'une autre balise déjà posée sur la même page (tolérance SNAP_Y_TOLERANCE_PCT),
  // elle se cale automatiquement sur cette même ligne au lieu de rester légèrement décalée. Demandé par
  // l'utilisateur pour obtenir des lignes parfaitement alignées sans réglage manuel au pixel près.
  const SNAP_Y_TOLERANCE_PCT = 1.4;
  const findSnappedY = (yPct, page, excludeIds = []) => {
    let best = null;
    let bestDist = SNAP_Y_TOLERANCE_PCT;
    for (const f of fields) {
      if (f.page !== page || excludeIds.includes(f.id)) continue;
      const d = Math.abs(f.yPct - yPct);
      if (d <= bestDist) { bestDist = d; best = f.yPct; }
    }
    return best !== null ? best : yPct;
  };

  // AJOUT (2026-09-18) : sélection multiple par "lasso" — cliquer-glisser sur une zone vide de la page
  // dessine un rectangle de sélection ; toutes les balises qu'il survole (au moment du relâchement)
  // deviennent sélectionnées (selectedFieldIds). Un clic simple (rectangle minuscule) désélectionne tout.
  // On démarre le lasso uniquement sur le fond de la page — chaque balise a son propre onMouseDown qui
  // stoppe la propagation pour ne pas déclencher de lasso quand on clique/glisse dessus.
  const rubberBandRectRef = React.useRef(null); // valeur "live" du rectangle, lue par onUp (évite de faire dépendre l'effet des coordonnées qui changent à chaque mousemove)
  const startRubberBand = (e) => {
    if (clickPlaceTag || !pageRef.current || e.button !== 0) return;
    const rect = pageRef.current.getBoundingClientRect();
    const xPct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const yPct = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    rubberBandStartRef.current = { xPct, yPct };
    const initRect = { minX: xPct, maxX: xPct, minY: yPct, maxY: yPct };
    rubberBandRectRef.current = initRect;
    setRubberBandRect(initRect);
    setRubberBandActive(true);
  };
  React.useEffect(() => {
    if (!rubberBandActive) return;
    const onMove = (e) => {
      if (!pageRef.current || !rubberBandStartRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      const xPct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const yPct = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
      const start = rubberBandStartRef.current;
      const next = {
        minX: Math.min(start.xPct, xPct), maxX: Math.max(start.xPct, xPct),
        minY: Math.min(start.yPct, yPct), maxY: Math.max(start.yPct, yPct),
      };
      rubberBandRectRef.current = next;
      setRubberBandRect(next);
    };
    const onUp = () => {
      const r = rubberBandRectRef.current;
      setRubberBandActive(false);
      rubberBandStartRef.current = null;
      rubberBandRectRef.current = null;
      setRubberBandRect(null);
      if (!r || (r.maxX - r.minX < 0.6 && r.maxY - r.minY < 0.6)) {
        // Rectangle minuscule = simple clic sur le fond → on désélectionne tout.
        setSelectedFieldIds(new Set());
        return;
      }
      if (!pageRef.current) return;
      // FIX (2026-09-18) : sélectionner une balise si le lasso touche sa zone RÉELLEMENT AFFICHÉE
      // (intersection de rectangles, via son élément DOM réel), et non plus seulement son point
      // d'ancrage (xPct/yPct) — ce dernier est parfois le bord gauche ou le coin bas d'une balise
      // (texte libre, balise de fusion...), donc un lasso qui recouvre visuellement toute une balise
      // pouvait la manquer si son point d'ancrage précis tombait juste hors du rectangle.
      const pageRect = pageRef.current.getBoundingClientRect();
      const selRect = {
        left: pageRect.left + (r.minX / 100) * pageRect.width,
        right: pageRect.left + (r.maxX / 100) * pageRect.width,
        top: pageRect.top + (r.minY / 100) * pageRect.height,
        bottom: pageRect.top + (r.maxY / 100) * pageRect.height,
      };
      const ids = fields
        .filter(f => f.page === currentPage + 1)
        .filter(f => {
          const el = fieldRefs.current[f.id];
          if (!el) return false;
          const b = el.getBoundingClientRect();
          return selRect.left < b.right && selRect.right > b.left && selRect.top < b.bottom && selRect.bottom > b.top;
        })
        .map(f => f.id);
      setSelectedFieldIds(new Set(ids));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rubberBandActive, fields, currentPage]);

  // Les deux modes de placement/sélection sont mutuellement exclusifs.
  React.useEffect(() => {
    if (clickPlaceTag) setSelectedFieldIds(new Set());
  }, [clickPlaceTag]);

  // Une sélection multiple ne doit pas survivre à un changement de page (les balises sélectionnées
  // n'existent plus visuellement sur la nouvelle page).
  React.useEffect(() => {
    setSelectedFieldIds(new Set());
  }, [currentPage]);

  // FIX (2026-09-18) : démarre le glissement "maison" d'une balise déjà posée (voir moveDragRef
  // ci-dessus) — remplace le glissement natif du navigateur qui ne montrait aucun aperçu pendant le
  // geste. Appelé au mousedown sur une balise (seule ou faisant partie d'une sélection multiple).
  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    if (e.button !== 0 || clickPlaceTag) return; // le mode clic-pour-placer gère son propre déplacement
    justDraggedRef.current = false;
    const isGroup = selectedFieldIds.size > 1 && selectedFieldIds.has(field.id);
    if (isGroup) {
      const origins = new Map();
      fields.forEach(f => { if (selectedFieldIds.has(f.id)) origins.set(f.id, { xPct: f.xPct, yPct: f.yPct }); });
      moveDragRef.current = { fieldId: field.id, page: field.page, startClientX: e.clientX, startClientY: e.clientY, origins };
    } else {
      // Glisser une balise non sélectionnée annule toute sélection multiple en cours.
      if (selectedFieldIds.size > 0) setSelectedFieldIds(new Set());
      moveDragRef.current = {
        fieldId: field.id,
        page: field.page,
        startClientX: e.clientX,
        startClientY: e.clientY,
        origins: new Map([[field.id, { xPct: field.xPct, yPct: field.yPct }]]),
      };
    }
    setDraggingFieldId(field.id);
    setIsMoveDragging(true);
  };

  React.useEffect(() => {
    if (!isMoveDragging) return;
    const onMove = (e) => {
      const st = moveDragRef.current;
      if (!st || !pageRef.current) return;
      const dxClient = e.clientX - st.startClientX;
      const dyClient = e.clientY - st.startClientY;
      if (Math.abs(dxClient) > 3 || Math.abs(dyClient) > 3) justDraggedRef.current = true;
      const rect = pageRef.current.getBoundingClientRect();
      const rawDeltaXPct = (dxClient / rect.width) * 100;
      const rawDeltaYPct = (dyClient / rect.height) * 100;
      const leaderOrigin = st.origins.get(st.fieldId);
      if (!leaderOrigin) return;
      // Alignement magnétique (2026-09-18) : calculé sur la nouvelle position du champ "meneur" (celui
      // réellement saisi), en excluant du calcul toutes les balises qui bougent avec lui — puis la même
      // correction est appliquée à tout le groupe pour garder les positions relatives.
      const leaderNewYRaw = leaderOrigin.yPct + rawDeltaYPct;
      const excludeIds = Array.from(st.origins.keys());
      const snappedLeaderY = findSnappedY(leaderNewYRaw, st.page, excludeIds);
      const snapCorrection = snappedLeaderY - leaderNewYRaw;
      setFields(prev => prev.map(f => {
        const origin = st.origins.get(f.id);
        if (!origin) return f;
        return {
          ...f,
          xPct: Math.max(2, Math.min(95, origin.xPct + rawDeltaXPct)),
          yPct: Math.max(2, Math.min(97, origin.yPct + rawDeltaYPct + snapCorrection)),
        };
      }));
    };
    const onUp = () => {
      moveDragRef.current = null;
      setIsMoveDragging(false);
      setDraggingFieldId(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoveDragging]);

  // AJOUT (2026-09-18) : "Dupliquer la ligne" — demandé par l'utilisateur pour les documents avec
  // beaucoup de balises répétées côte à côte (ex. un calendrier prévisionnel où chaque séance a sa
  // propre ligne "date | durée | lieu" en texte libre) : jusqu'ici il fallait reposer et redimensionner
  // une balise à chaque case, puis l'aligner à la main sur la même ligne que ses voisines — fastidieux
  // dès qu'il y a beaucoup de lignes. Un clic sur ce bouton repère TOUTES les balises de la page dont
  // le Y est proche de celle cliquée (= "la même ligne", tolérance ROW_Y_TOLERANCE_PCT) et en pose une
  // copie identique (même X, même largeur/hauteur) juste en dessous — la ligne suivante est donc déjà
  // parfaitement alignée avec la précédente, sans aucun réglage manuel.
  // FIX (2026-09-18) : tolérance resserrée (était 2.5) — elle ne doit repérer QUE les balises
  // réellement alignées sur la même ligne (même Y au pixel près), jamais une autre ligne juste
  // au-dessus ou en dessous. Avec l'ancienne tolérance plus large, si l'utilisateur cliquait plusieurs
  // fois sur le bouton "dupliquer" de la ligne D'ORIGINE (au lieu de celui de la toute dernière ligne
  // créée), la nouvelle ligne recalculée à partir de cette même origine atterrissait exactement au même
  // endroit que la précédente copie — les deux lignes se retrouvaient empilées l'une sur l'autre, puis
  // un clic sur l'une d'elles les repérait TOUTES comme "la même ligne" et les dupliquait d'un coup
  // (signalé par l'utilisateur : "ça duplique tout ce qu'il y a au-dessus aussi").
  const ROW_Y_TOLERANCE_PCT = 0.5;
  const handleDuplicateRow = (fieldId) => {
    const ref = fields.find(f => f.id === fieldId);
    if (!ref) return;
    const rowFields = fields.filter(f => f.page === ref.page && Math.abs(f.yPct - ref.yPct) <= ROW_Y_TOLERANCE_PCT);
    // Décalage vertical = hauteur de la balise la plus haute de la ligne (+ une petite marge) ; 4%
    // par défaut pour les balises sans hauteur propre (signature, case à cocher, balise de fusion).
    const rowHeightPct = Math.max(...rowFields.map(f => (typeof f.height_percent === 'number' ? f.height_percent : 4)), 4);
    const offsetPct = rowHeightPct + 1.5;
    // FIX (2026-09-18) : si l'emplacement calculé tombe déjà sur une ligne existante, on ne l'empile
    // plus dessus — on descend automatiquement d'un cran de plus jusqu'à trouver un emplacement libre,
    // pour empêcher à la source la superposition de lignes décrite ci-dessus.
    let newYPct = ref.yPct + offsetPct;
    const isRowOccupied = (y) => fields.some(f => f.page === ref.page && Math.abs(f.yPct - y) <= ROW_Y_TOLERANCE_PCT);
    let guard = 0;
    while (isRowOccupied(newYPct) && guard < 40) {
      newYPct += offsetPct;
      guard++;
    }
    if (newYPct > 97) {
      toast.error("Plus de place en bas de la page pour une nouvelle ligne — repositionnez-la manuellement ou passez à la page suivante.");
      return;
    }
    const newFields = rowFields.map((f, i) => ({
      ...f,
      id: `f_${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`,
      yPct: newYPct,
    }));
    setFields(prev => [...prev, ...newFields]);
    toast.success(`Ligne dupliquée (${newFields.length} balise${newFields.length > 1 ? 's' : ''}) !`);
  };

  const handleReset = () => {
    setFile(null); setPdfPages([]); setFields([]);
    setCurrentPage(0); setStep('upload');
    setClickPlaceTag(null); setHoverPos(null);
    setSelectedFieldIds(new Set()); setRubberBandActive(false); setRubberBandRect(null);
    moveDragRef.current = null; setIsMoveDragging(false); setDraggingFieldId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    handleReset();
    setTemplateName(''); setDestinationRoles(['client']);
    setSigningMode('simultane'); setSigningOrder(['client', 'formateur']);
    onClose();
  };

  const handleSave = async () => {
    if (!file || !templateName.trim()) { toast.error('Nom requis.'); return; }
    if (fields.length === 0) { toast.error('Placez au moins une balise sur le document.'); return; }
    if (destinationRoles.length === 0) { toast.error('Choisissez au moins un destinataire (client, formateur ou organisme).'); return; }
    setIsSaving(true);
    // Classification automatique selon les balises posées
    const INTERACTIVE_TAGS = ['signature_client', 'signature_formateur', 'signature_organisme', 'checkbox_client', 'checkbox_formateur', 'checkbox_organisme', 'texte_client', 'texte_formateur', 'texte_organisme'];
    const hasDataFields = fields.some(f => !INTERACTIVE_TAGS.includes(f.tag));
    const hasSignature = fields.some(f => INTERACTIVE_TAGS.includes(f.tag));
    const autoClassification = hasDataFields ? 'a_generer' : hasSignature ? 'a_signer' : 'telechargeable';
    // L'ordre n'a de sens qu'entre les rôles effectivement sélectionnés — on filtre pour ne jamais
    // envoyer un rôle décoché dans signingOrder (ex: organisme décoché après avoir réglé un ordre).
    const effectiveOrder = signingOrder.filter(r => destinationRoles.includes(r));
    const signingConfig = { mode: signingMode, order: signingMode === 'sequentiel' && destinationRoles.length > 1 ? effectiveOrder : null };
    try {
      await onSave(file, templateName.trim(), stringifyDestinationRoles(destinationRoles), fields, autoClassification, initialData?.templateId || null, signingConfig);
      handleClose();
    } catch (err) {
      toast.error('Erreur : ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Prévisualisation avec valeurs test — génère le vrai PDF pour vérifier le positionnement
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const handlePreview = async () => {
    if (!pdfBlobRef.current || fields.length === 0) {
      toast.error('Aucune balise à prévisualiser.'); return;
    }
    setIsPreviewing(true);
    try {
      const testValues = {
        nomcomplet_client: 'Jean DUPONT', numero_dossier_client: 'DOS-2026-001', ville_client: 'Vannes', date_du_jour: new Date().toLocaleDateString('fr-FR'),
        client_email: 'jean@exemple.fr', client_phone: '06 12 34 56 78',
        rue_client: '12 rue de la Paix', code_postal_client: '56000',
        adresse_client: '12 rue de la Paix, 56000 Vannes',
        adresse_session: '56000 Vannes', prix_prestation: '1 500 €',
        financement_type: 'CPF + Personnel', financeur_nom: 'Mon Compte Formation', montant_financeur: '1 300', reste_a_charge: '200', numero_prise_en_charge: 'CPF-2026-0001',
        formation_nom: 'Bilan de compétences BC 24h', modalite_formation: 'présentiel',
        date_debut: '01/09/2026', date_fin: '30/11/2026', date_signature: new Date().toLocaleDateString('fr-FR'),
        initiales_client: computeInitials('Jean DUPONT'),
        nom_formateur: 'Marie LEROY', email_formateur: 'marie@formateur.fr', tel_formateur: '06 00 00 00 00',
        adresse_formateur: '5 av. Victor Hugo, 75008 Paris', rue_formateur: '5 av. Victor Hugo', code_postal_formateur: '75008', ville_formateur: 'Paris',
        formateur_siret: '123 456 789 00012', formateur_nda: '75 12 34567 89',
        compagnie_assurance: 'AXA', numero_assurance_rcp: 'RCP-2026-001',
        date_signature_formateur: new Date().toLocaleDateString('fr-FR'),
        initiales_formateur: computeInitials('Marie LEROY'),
        org_nom: 'VB Coaching', org_siret: '399 146 067 00034', org_nda: '53560969356',
        org_adresse: '2 rue du Général Baron Fabre', org_code_postal: '56000', org_ville: 'Vannes',
        org_site_web: 'www.vbcoaching56.com',
        date_signature_organisme: new Date().toLocaleDateString('fr-FR'),
        initiales_organisme: computeInitials('VB Coaching'),
      };
      const tplFields = fields.map(f => ({
        template_id: 0, client_key: f.id, tag: f.tag, page: f.page || 1,
        x_percent: f.xPct, y_percent: f.yPct,
        width_percent: f.width_percent, height_percent: f.height_percent,
        field_type: (f.tag === 'signature_client' || f.tag === 'signature_formateur' || f.tag === 'signature_organisme') ? 'signature' : (f.tag === 'checkbox_client' || f.tag === 'checkbox_formateur' || f.tag === 'checkbox_organisme') ? 'checkbox' : (f.tag === 'texte_client' || f.tag === 'texte_formateur' || f.tag === 'texte_organisme') ? 'text_input' : 'text',
        font_size: 11,
      }));
      // Aperçu des champs texte libre avec une valeur d'exemple (le vrai contenu n'est saisi
      // qu'au moment de la signature — voir handleSignSave/handleSignDocument).
      const previewTextInputMap = {};
      tplFields.forEach(f => {
        if (f.field_type === 'text_input') {
          previewTextInputMap[fieldKey(f)] = f.tag === 'texte_client' ? 'Exemple : réponse du client…' : f.tag === 'texte_organisme' ? 'Exemple : réponse de l\'administrateur…' : 'Exemple : réponse du formateur…';
        }
      });
      const resultBlob = await overlayFieldsOnPdf(pdfBlobRef.current, tplFields, testValues, {}, {}, previewTextInputMap);
      const url = URL.createObjectURL(resultBlob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch(e) {
      toast.error('Erreur prévisualisation : ' + e.message);
    } finally {
      setIsPreviewing(false);
    }
  };

  const fieldsOnPage = fields.filter(f => f.page === currentPage + 1);
  const placedTags = new Set(fields.map(f => f.tag));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            {step === 'editor' && (
              <button onClick={handleReset} className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-xl transition-all">
                <ChevronLeft size={18} />
              </button>
            )}
            <div>
              <h2 className="text-xl font-black text-gray-900">
                {step === 'upload' ? 'Importer un modèle de document'
                 : step === 'converting' ? 'Préparation de l\'aperçu…'
                 : `Éditeur de balises — ${pdfPages.length} page${pdfPages.length > 1 ? 's' : ''}`}
              </h2>
              <p className="text-sm text-gray-400 mt-0.5">
                {step === 'upload' ? 'Votre fichier PDF reste intact · les balises sont positionnées par dessus en overlay'
                 : step === 'converting' ? 'Conversion du DOCX en aperçu PDF (10-20 secondes)…'
                 : `${fields.length} balise${fields.length !== 1 ? 's' : ''} posée${fields.length !== 1 ? 's' : ''} · glissez une balise depuis le panneau droit`}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl">
            <X size={20} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* Upload / Converting */}
          {(step === 'upload' || step === 'converting') && (
            <div className="flex-1 flex items-center justify-center p-8">
              {step === 'converting' ? (
                <div className="text-center space-y-4">
                  <div className="relative w-16 h-16 mx-auto">
                    <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-violet-100" />
                    <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-violet-600 border-t-transparent animate-spin" />
                  </div>
                  <p className="font-bold text-gray-700">Conversion du document…</p>
                  <p className="text-sm text-gray-400">Préparation de l'aperçu visuel page par page</p>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-gray-200 rounded-3xl p-12 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/20 transition-all max-w-lg w-full"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]); }}
                >
                  <input ref={fileInputRef} type="file" className="hidden" accept=".docx,.pdf" onChange={e => e.target.files[0] && handleFileSelect(e.target.files[0])} />
                  <div className="w-16 h-16 bg-violet-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <Upload size={28} className="text-violet-500" />
                  </div>
                  <p className="font-black text-gray-800 text-lg mb-2">Importez votre document</p>
                  <p className="text-gray-400 text-sm mb-6">Glissez un <strong>.pdf</strong> ou .docx ici, ou cliquez pour sélectionner</p>
                  <div className="grid grid-cols-3 gap-3 text-left">
                    {[
                      ['📄', 'Fichier intact', 'Pas de modification du Word'],
                      ['🎯', 'Balises visuelles', 'Glissez à l\'endroit exact'],
                      ['⚡', 'Génération auto', 'Les données se remplissent seules'],
                    ].map(([icon, title, desc]) => (
                      <div key={title} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <span className="text-xl">{icon}</span>
                        <p className="text-xs font-bold text-gray-700 mt-1">{title}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Éditeur visuel */}
          {step === 'editor' && (
            <>
              {/* ── Visionneuse centrale ── */}
              <div className="flex-1 bg-[#e8e8e8] overflow-auto flex flex-col items-center gap-3 p-4">
                {/* Navigation pages */}
                {pdfPages.length > 1 && (
                  <div className="shrink-0 flex items-center gap-2 bg-white rounded-xl px-4 py-2 shadow-sm border border-gray-200">
                    <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}
                      className="text-gray-400 hover:text-violet-600 disabled:opacity-30 transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    {pdfPages.map((_, i) => (
                      <button key={i} onClick={() => setCurrentPage(i)}
                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${currentPage === i ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}>
                        {i + 1}
                      </button>
                    ))}
                    <button onClick={() => setCurrentPage(p => Math.min(pdfPages.length - 1, p + 1))} disabled={currentPage === pdfPages.length - 1}
                      className="text-gray-400 hover:text-violet-600 disabled:opacity-30 transition-colors">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}

                {/* Page avec overlay de balises */}
                <div
                  ref={pageRef}
                  className="relative shadow-2xl shrink-0"
                  style={{
                    width: Math.min(pdfPages[currentPage]?.nativeW || 800, 680),
                    cursor: clickPlaceTag ? 'crosshair' : 'default',
                    outline: clickPlaceTag ? '3px dashed #7C3AED' : '',
                    outlineOffset: clickPlaceTag ? '-3px' : '',
                  }}
                  onClick={handlePageClick}
                  onMouseDown={startRubberBand}
                  onMouseMove={e => {
                    if (!clickPlaceTag || !pageRef.current) { if (hoverPos) setHoverPos(null); return; }
                    const rect = pageRef.current.getBoundingClientRect();
                    setHoverPos({
                      xPct: Math.max(1, Math.min(98, ((e.clientX - rect.left) / rect.width) * 100)),
                      yPct: Math.max(2, Math.min(97, ((e.clientY - rect.top) / rect.height) * 100)),
                    });
                  }}
                  onMouseLeave={() => setHoverPos(null)}
                  onDragOver={e => {
                    // FIX (2026-09-18) : ne concerne plus que le dépôt d'une NOUVELLE balise depuis la
                    // sidebar (dragTag) — le repositionnement d'une balise déjà posée ne passe plus par
                    // le glissement natif du navigateur, voir handleFieldMouseDown/moveDragRef plus haut.
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                    e.currentTarget.style.outline = '3px dashed #7C3AED';
                    e.currentTarget.style.outlineOffset = '-3px';
                  }}
                  onDragLeave={e => {
                    if (!clickPlaceTag) { e.currentTarget.style.outline = ''; e.currentTarget.style.outlineOffset = ''; }
                  }}
                  onDrop={e => {
                    if (!clickPlaceTag) { e.currentTarget.style.outline = ''; e.currentTarget.style.outlineOffset = ''; }
                    handlePageDrop(e);
                  }}
                >
                  <img
                    src={pdfPages[currentPage]?.dataUrl}
                    style={{ width: '100%', display: 'block' }}
                    alt={`Page ${currentPage + 1}`}
                    draggable={false}
                  />

                  {/* AJOUT (2026-09-18) : rectangle de sélection "lasso" — visible pendant le glisser */}
                  {rubberBandActive && rubberBandRect && (
                    <div
                      className="absolute border-2 border-violet-500 bg-violet-500/10 pointer-events-none"
                      style={{
                        left: `${rubberBandRect.minX}%`,
                        top: `${rubberBandRect.minY}%`,
                        width: `${rubberBandRect.maxX - rubberBandRect.minX}%`,
                        height: `${rubberBandRect.maxY - rubberBandRect.minY}%`,
                        zIndex: 40,
                      }}
                    />
                  )}

                  {/* Ghost cursor — balise fantôme qui suit la souris en mode clic-pour-placer */}
                  {clickPlaceTag && hoverPos && (() => {
                    const tag = clickPlaceTag.tag || fields.find(f => f.id === clickPlaceTag.fieldId)?.tag;
                    if (!tag) return null;
                    const isSig = isSignatureTag(tag);
                    const isChk = isCheckboxTag(tag);
                    const isTxt = isTextInputTag(tag);
                    // Ancrage réel dans le PDF : signature/case = centré sur le point ; texte libre = point
                    // cliqué = coin gauche exact où le texte commencera (corrigé le 2026-07-24, voir
                    // overlayFieldsOnPdf) ; balise texte de fusion = le point est le coin bas-gauche de la
                    // ligne de base (comportement de drawText dans pdf-lib). On sépare donc la croix
                    // (toujours centrée sur le point exact) de l'étiquette (ancrée différemment selon le
                    // type) pour que l'aperçu corresponde au rendu réel.
                    const labelTransform = (isSig || isChk) ? 'translate(-50%, -50%)' : isTxt ? 'translate(0%, -50%)' : 'translate(0%, -100%)';
                    return (
                      <div
                        key="ghost"
                        style={{ position: 'absolute', left: `${hoverPos.xPct}%`, top: `${hoverPos.yPct}%`, zIndex: 30, opacity: 0.6, pointerEvents: 'none' }}
                      >
                        <div style={{ position: 'absolute', top: 0, left: 0, transform: labelTransform }}>
                          {isSig ? (
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 whitespace-nowrap shadow-xl ring-2 ring-white ${roleFromTag(tag) === 'organisme' ? 'bg-emerald-100 border-emerald-500' : roleFromTag(tag) === 'formateur' ? 'bg-orange-100 border-orange-500' : 'bg-blue-100 border-blue-500'}`} style={{ minWidth: 140 }}>
                              <span className="text-base">✍️</span>
                              <p className={`text-xs font-black ${roleFromTag(tag) === 'organisme' ? 'text-emerald-800' : roleFromTag(tag) === 'formateur' ? 'text-orange-800' : 'text-blue-800'}`}>Signature {ROLE_LABEL[roleFromTag(tag) || 'client']}</p>
                            </div>
                          ) : isChk ? (
                            // Carré compact — même taille que le rendu final (~12pt) pour un cadrage précis
                            <div
                              className={`rounded-sm shadow-lg ring-2 ring-white ${roleFromTag(tag) === 'organisme' ? 'border-2 border-emerald-500 bg-emerald-500/20' : roleFromTag(tag) === 'formateur' ? 'border-2 border-orange-500 bg-orange-500/20' : 'border-2 border-blue-500 bg-blue-500/20'}`}
                              style={{ width: 16, height: 16 }}
                            />
                          ) : isTxt ? (
                            <div className="flex items-center select-none">
                              <div className={`w-0.5 self-stretch rounded-full shrink-0 ${roleFromTag(tag) === 'organisme' ? 'bg-emerald-500' : roleFromTag(tag) === 'formateur' ? 'bg-orange-500' : 'bg-blue-500'}`} style={{ minHeight: 18 }} />
                              <div className={`flex items-center gap-1.5 pl-2 pr-2 py-1.5 rounded-r-lg border border-l-0 shadow-xl ring-2 ring-white whitespace-nowrap ${roleFromTag(tag) === 'organisme' ? 'bg-emerald-100 border-emerald-500' : roleFromTag(tag) === 'formateur' ? 'bg-orange-100 border-orange-500' : 'bg-blue-100 border-blue-500'}`}>
                                <span className="text-sm">📝</span>
                                <p className={`text-xs font-black ${roleFromTag(tag) === 'organisme' ? 'text-emerald-800' : roleFromTag(tag) === 'formateur' ? 'text-orange-800' : 'text-blue-800'}`}>Texte {ROLE_LABEL[roleFromTag(tag) || 'client']}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-violet-700 text-white text-[11px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap shadow-xl ring-2 ring-white">
                              <span className="font-mono">{tag === 'date_du_jour' ? '📅 date_du_jour' : `{${tag}}`}</span>
                            </div>
                          )}
                        </div>
                        {/* Croix de centrage — marque le point exact (xPct, yPct) tel qu'enregistré */}
                        <div style={{ position: 'absolute', top: 0, left: 0, transform: 'translate(-50%,-50%)', width: 10, height: 10, pointerEvents: 'none' }}>
                          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1.5, background: '#7C3AED', transform: 'translateY(-50%)' }} />
                          <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1.5, background: '#7C3AED', transform: 'translateX(-50%)' }} />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Balises posées sur cette page */}
                  {fieldsOnPage.map(field => {
                    const isSig = isSignatureTag(field.tag);
                    const isChk = isCheckboxTag(field.tag);
                    const isTxt = isTextInputTag(field.tag);
                    const isBeingMoved = draggingFieldId === field.id;
                    const isSelectedForMove = clickPlaceTag?.fieldId === field.id;
                    // AJOUT (2026-09-18) : sélection multiple (lasso) — surbrillance distincte du mode
                    // "clic-pour-déplacer" ci-dessus, pour bien montrer quelles balises bougeront ensemble.
                    const isLassoSelected = selectedFieldIds.has(field.id);
                    return (
                      <div
                        key={field.id}
                        ref={el => { if (el) fieldRefs.current[field.id] = el; else delete fieldRefs.current[field.id]; }}
                        onMouseDown={e => handleFieldMouseDown(e, field)}
                        onClick={e => {
                          e.stopPropagation();
                          // FIX (2026-09-18) : un vrai glissement (voir handleFieldMouseDown) ne doit pas
                          // en plus armer le mode clic-pour-placer au relâchement.
                          if (justDraggedRef.current) { justDraggedRef.current = false; return; }
                          // Clic sur balise posée → activer mode repositionnement
                          if (!clickPlaceTag) setClickPlaceTag({ fieldId: field.id });
                          else if (clickPlaceTag.fieldId === field.id) setClickPlaceTag(null);
                        }}
                        style={{
                          position: 'absolute',
                          left: `${field.xPct}%`,
                          top: `${field.yPct}%`,
                          // FIX (2026-09-04) : la case "texte libre" occupe désormais sa taille réelle
                          // (width_percent/height_percent, redimensionnable via la poignée) au lieu d'un
                          // petit repère à taille fixe — les autres types de balises gardent leur taille
                          // intrinsèque (pas de width/height forcée).
                          ...(isTxt ? { width: `${field.width_percent || 28}%`, height: `${field.height_percent || 3.5}%` } : {}),
                          // Signature/case = centrées sur le point ; texte libre = point cliqué = coin
                          // gauche exact où le texte commencera (ancrage aligné sur overlayFieldsOnPdf,
                          // corrigé le 2026-07-24 pour ne plus centrer la balise autour du point) ; balise
                          // texte de fusion = le point est le coin bas-gauche de la ligne de base
                          // (comportement réel de drawText/pdf-lib).
                          transform: (isSig || isChk) ? 'translate(-50%, -50%)' : isTxt ? 'translate(0%, -50%)' : 'translate(0%, -100%)',
                          // FIX (2026-09-18) : la balise reste pleinement visible pendant le glissement
                          // "maison" (elle EST désormais ce qui bouge réellement à l'écran, plus besoin de
                          // l'estomper comme du temps du glissement natif) — juste une légère ombre + un
                          // z-index relevé pour bien la distinguer des autres pendant qu'on la tient.
                          zIndex: isBeingMoved ? 25 : 10,
                          cursor: isBeingMoved ? 'grabbing' : isSelectedForMove ? 'crosshair' : 'grab',
                          opacity: 1,
                          boxShadow: isBeingMoved ? '0 8px 20px rgba(0,0,0,0.28)' : undefined,
                          outline: isSelectedForMove ? '2px solid #7C3AED' : isLassoSelected ? '2px dashed #2563EB' : 'none',
                          outlineOffset: isLassoSelected ? 2 : 0,
                          borderRadius: 4,
                        }}
                        title={(isSig || isChk || isTxt) ? 'Glissez pour repositionner — centré exactement sur le point choisi' : 'Glissez pour repositionner — le coin bas-gauche indique la position exacte du texte'}
                      >
                        {isSig ? (
                          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg ring-2 ring-white border-2 whitespace-nowrap select-none ${roleFromTag(field.tag) === 'organisme' ? 'bg-emerald-50 border-emerald-400' : roleFromTag(field.tag) === 'formateur' ? 'bg-orange-50 border-orange-400' : 'bg-blue-50 border-blue-400'}`} style={{ minWidth: 140 }}>
                            <span className="text-base select-none">✍️</span>
                            <div className="flex-1 select-none">
                              <p className={`text-xs font-black ${roleFromTag(field.tag) === 'organisme' ? 'text-emerald-700' : roleFromTag(field.tag) === 'formateur' ? 'text-orange-700' : 'text-blue-700'}`}>
                                Signature {ROLE_LABEL[roleFromTag(field.tag) || 'client']}
                              </p>
                              <p className="text-[11px] text-gray-400">Centré sur le point choisi</p>
                            </div>
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); handleDuplicateRow(field.id); }}
                              title="Dupliquer la ligne (toutes les balises alignées avec celle-ci) juste en dessous"
                              className="w-4 h-4 rounded-full bg-gray-200 hover:bg-violet-500 hover:text-white flex items-center justify-center transition-colors shrink-0"
                            >
                              <Copy size={8} />
                            </button>
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); setFields(prev => prev.filter(f => f.id !== field.id)); }}
                              className="w-4 h-4 rounded-full bg-gray-200 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors shrink-0"
                            >
                              <X size={8} />
                            </button>
                          </div>
                        ) : isChk ? (
                          // Carré compact — même taille que le rendu final (~12pt) pour un cadrage précis.
                          // Le bouton de suppression n'apparaît qu'au survol pour ne pas gêner l'alignement.
                          <div className="group/chk relative select-none" style={{ width: 16, height: 16 }}>
                            <div
                              className={`rounded-sm shadow-lg ring-2 ring-white ${roleFromTag(field.tag) === 'organisme' ? 'border-2 border-emerald-500 bg-emerald-500/20' : roleFromTag(field.tag) === 'formateur' ? 'border-2 border-orange-500 bg-orange-500/20' : 'border-2 border-blue-500 bg-blue-500/20'}`}
                              style={{ width: 16, height: 16 }}
                            />
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); handleDuplicateRow(field.id); }}
                              title="Dupliquer la ligne juste en dessous"
                              className="absolute -top-2 -left-2 w-4 h-4 rounded-full bg-gray-200 hover:bg-violet-500 hover:text-white flex items-center justify-center transition-opacity opacity-0 group-hover/chk:opacity-100 shrink-0"
                            >
                              <Copy size={8} />
                            </button>
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); setFields(prev => prev.filter(f => f.id !== field.id)); }}
                              className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-500 hover:text-white flex items-center justify-center transition-opacity opacity-0 group-hover/chk:opacity-100 shrink-0"
                            >
                              <X size={8} />
                            </button>
                          </div>
                        ) : isTxt ? (
                          // FIX (2026-09-04) : la case est désormais affichée à sa taille RÉELLE
                          // (width_percent/height_percent du champ, en % de la page — comme x/y) et
                          // peut être étirée directement depuis la poignée en bas à droite, au lieu
                          // d'un petit repère à taille fixe. Le point posé reste le coin haut-gauche
                          // de la case (ancrage identique à overlayFieldsOnPdf).
                          //
                          // FIX (2026-09-10) : la croix de suppression et la poignée de redimensionnement
                          // étaient auparavant DANS la div `overflow-hidden` (nécessaire pour tronquer le
                          // texte qui dépasserait de la case) — or elles sont volontairement positionnées
                          // À CHEVAL sur le bord (-top-2.5/-right-2.5, -bottom-1.5/-right-1.5), donc
                          // `overflow-hidden` les rognait aux 3/4. On sort maintenant ces deux éléments de
                          // la div tronquée pour en faire des frères positionnés par rapport au conteneur
                          // extérieur (celui avec `position: 'absolute'`, sans overflow-hidden) : la case
                          // continue de tronquer son propre contenu, mais la croix et la poignée restent
                          // entièrement visibles.
                          <>
                            <div
                              className={`w-full h-full min-w-[60px] min-h-[20px] rounded-lg border-2 shadow-lg ring-2 ring-white flex items-start gap-1 px-1.5 py-1 select-none overflow-hidden ${
                                roleFromTag(field.tag) === 'organisme' ? 'bg-emerald-50/90 border-emerald-400' : field.tag === 'texte_formateur' ? 'bg-orange-50/90 border-orange-400' : 'bg-blue-50/90 border-blue-400'
                              }`}
                            >
                              <span className="text-xs select-none shrink-0">📝</span>
                              <p className={`text-[11px] font-black leading-tight truncate ${
                                roleFromTag(field.tag) === 'organisme' ? 'text-emerald-700' : field.tag === 'texte_formateur' ? 'text-orange-700' : 'text-blue-700'
                              }`}>
                                Texte {ROLE_LABEL[roleFromTag(field.tag) || 'client']}
                              </p>
                            </div>
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); handleDuplicateRow(field.id); }}
                              title="Dupliquer la ligne (toutes les balises alignées avec celle-ci) juste en dessous — pratique pour un calendrier prévisionnel, une liste de séances, etc."
                              className="absolute -top-2.5 -left-2.5 w-6 h-6 rounded-full bg-white border-2 border-violet-300 text-violet-500 hover:bg-violet-500 hover:text-white hover:border-violet-500 flex items-center justify-center transition-colors shrink-0 shadow-md"
                              style={{ zIndex: 30 }}
                            >
                              <Copy size={12} strokeWidth={2.5} />
                            </button>
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); setFields(prev => prev.filter(f => f.id !== field.id)); }}
                              title="Supprimer cette balise"
                              className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-white border-2 border-red-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 flex items-center justify-center transition-colors shrink-0 shadow-md"
                              style={{ zIndex: 30 }}
                            >
                              <X size={13} strokeWidth={3} />
                            </button>
                            {/* Poignée de redimensionnement — glisser pour étirer largeur/hauteur */}
                            <div
                              draggable={false}
                              onDragStart={e => e.preventDefault()}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (!pageRef.current) return;
                                const rect = pageRef.current.getBoundingClientRect();
                                resizeStartRef.current = {
                                  fieldId: field.id,
                                  startClientX: e.clientX,
                                  startClientY: e.clientY,
                                  startWidthPct: field.width_percent || 28,
                                  startHeightPct: field.height_percent || 3.5,
                                  rectWidth: rect.width,
                                  rectHeight: rect.height,
                                };
                                setResizingFieldId(field.id);
                              }}
                              title="Glisser pour redimensionner la case"
                              className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 rounded-sm bg-white border-2 border-gray-400 hover:border-violet-600 hover:bg-violet-50 cursor-nwse-resize shadow-sm"
                              style={{ zIndex: 20 }}
                            />
                          </>
                        ) : (
                          <div className="group relative inline-flex select-none">
                            <div className="flex items-center gap-1.5 bg-violet-600 text-white text-xs font-bold px-1.5 py-0.5 rounded whitespace-nowrap shadow-lg ring-2 ring-white">
                              <span className="font-mono">{field.tag === 'date_du_jour' ? '📅 date_du_jour' : `{${field.tag}}`}</span>
                            </div>
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); handleDuplicateRow(field.id); }}
                              title="Dupliquer la ligne juste en dessous"
                              className="absolute -top-2 -left-2 w-3.5 h-3.5 rounded-full bg-gray-200 hover:bg-violet-500 hover:text-white flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 shrink-0"
                            >
                              <Copy size={8} />
                            </button>
                            <button
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); setFields(prev => prev.filter(f => f.id !== field.id)); }}
                              className="absolute -top-2 -right-2 w-3.5 h-3.5 rounded-full bg-gray-200 hover:bg-red-500 hover:text-white flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 shrink-0"
                            >
                              <X size={8} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* AJOUT (2026-09-18) : bannière de confirmation de sélection multiple — indique
                      combien de balises sont sélectionnées et qu'on peut les glisser ensemble. */}
                  {!clickPlaceTag && selectedFieldIds.size > 1 && (
                    <div className="absolute inset-x-0 top-2 pointer-events-none flex items-center justify-center">
                      <div className="bg-blue-600/95 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                        <span>🔗</span>
                        <span>{selectedFieldIds.size} balises sélectionnées — glissez-en une pour déplacer le groupe · Échap pour désélectionner</span>
                      </div>
                    </div>
                  )}

                  {/* Bannière mode clic-pour-placer */}
                  {clickPlaceTag && (
                    <div className="absolute inset-0 pointer-events-none flex items-end justify-center pb-4">
                      <div className="bg-violet-700/95 text-white text-sm font-bold px-5 py-2.5 rounded-xl shadow-xl flex items-center gap-3">
                        <span>🎯</span>
                        <span>
                          {clickPlaceTag.fieldId
                            ? 'Cliquez sur la page pour déplacer la balise · Échap pour annuler'
                            : <>Cliquez sur la page pour placer <span className="font-mono bg-white/20 px-1 rounded">{clickPlaceTag.tag}</span> · Échap pour annuler</>
                          }
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Hint zone de dépôt — ne concerne que le dépôt d'une NOUVELLE balise depuis la
                      sidebar (dragTag) : le repositionnement d'une balise déjà posée (draggingFieldId)
                      n'en a plus besoin depuis le 2026-09-18, la balise elle-même bouge en direct sous
                      le curseur et indique déjà clairement où elle va atterrir. */}
                  {dragTag && (
                    <div className="absolute inset-0 pointer-events-none bg-gray-900/5 flex items-end justify-center pb-4">
                      <div className={`text-white text-sm font-bold px-5 py-2.5 rounded-xl shadow-xl ${
                        dragTag === 'signature_client' || dragTag === 'checkbox_client' || dragTag === 'texte_client' ? 'bg-blue-600/90' :
                        dragTag === 'signature_formateur' || dragTag === 'checkbox_formateur' || dragTag === 'texte_formateur' ? 'bg-orange-500/90' :
                        'bg-violet-700/90'
                      }`}>
                        {isSignatureTag(dragTag)
                          ? `✍️ Déposez la zone de ${dragTag === 'signature_client' ? 'signature client' : 'signature formateur'}`
                          : isCheckboxTag(dragTag)
                            ? `☑️ Déposez la case à cocher ${dragTag === 'checkbox_client' ? 'client' : 'formateur'}`
                            : isTextInputTag(dragTag)
                              ? `📝 Déposez le champ texte libre ${dragTag === 'texte_client' ? 'client' : 'formateur'}`
                              : <>Déposez ici → <span className="font-mono">{dragTag === 'date_du_jour' ? '📅 date_du_jour' : `{${dragTag}}`}</span></>
                        }
                      </div>
                    </div>
                  )}
                </div>

                {pdfPages.length > 1 && (
                  <p className="text-xs text-gray-400 shrink-0">Page {currentPage + 1} / {pdfPages.length}</p>
                )}
              </div>

              {/* ── Panneau droit ── */}
              <div className="w-60 shrink-0 border-l border-gray-100 bg-white flex flex-col overflow-hidden">
                {/* Liste des balises */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div>
                    <h3 className="text-xs font-black text-gray-500 uppercase tracking-widest mb-1">Balises disponibles</h3>
                    <p className="text-[11px] text-gray-400 leading-snug">Glissez une balise et déposez-la sur la page à l'endroit souhaité</p>
                  </div>

                  {/* ── Zones de signature ── */}
                  <div>
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1.5">— Signatures —</p>
                    <div className="flex flex-col gap-1.5">
                      {SIGNATURE_TAGS.map(({ tag, label, color }) => {
                        const isPlaced = placedTags.has(tag);
                        const isDraggingThis = dragTag === tag;
                        const isClickSelected = clickPlaceTag?.tag === tag;
                        const isBlue = color === 'blue';
                        return (
                          <div
                            key={tag}
                            draggable
                            onDragStart={e => { setDragTag(tag); setClickPlaceTag(null); e.dataTransfer.effectAllowed = 'copy'; }}
                            onDragEnd={() => setDragTag(null)}
                            onClick={() => setClickPlaceTag(isClickSelected ? null : { tag })}
                            className={`flex items-center gap-2 px-2.5 py-2.5 rounded-lg border-2 select-none cursor-pointer transition-all ${
                              isClickSelected ? 'bg-violet-600 text-white border-violet-600 scale-95 shadow-inner' :
                              isDraggingThis ? 'opacity-40 scale-95' :
                              isBlue
                                ? 'bg-blue-50 border-blue-200 text-blue-700 hover:border-blue-400'
                                : 'bg-orange-50 border-orange-200 text-orange-700 hover:border-orange-400'
                            }`}
                          >
                            <span className="text-base shrink-0">✍️</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold truncate">{label}</p>
                              <p className="text-[11px] opacity-60">Zone de signature</p>
                            </div>
                            {isPlaced && !isDraggingThis && <Check size={10} className="shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Cases à cocher (sans texte) ── */}
                  <div>
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1.5">— Cases à cocher —</p>
                    <div className="flex flex-col gap-1.5">
                      {CHECKBOX_TAGS.map(({ tag, label, color }) => {
                        const isPlaced = placedTags.has(tag);
                        const isDraggingThis = dragTag === tag;
                        const isClickSelected = clickPlaceTag?.tag === tag;
                        const isBlue = color === 'blue';
                        return (
                          <div
                            key={tag}
                            draggable
                            onDragStart={e => { setDragTag(tag); setClickPlaceTag(null); e.dataTransfer.effectAllowed = 'copy'; }}
                            onDragEnd={() => setDragTag(null)}
                            onClick={() => setClickPlaceTag(isClickSelected ? null : { tag })}
                            className={`flex items-center gap-2 px-2.5 py-2.5 rounded-lg border-2 select-none cursor-pointer transition-all ${
                              isClickSelected ? 'bg-violet-600 text-white border-violet-600 scale-95 shadow-inner' :
                              isDraggingThis ? 'opacity-40 scale-95' :
                              isBlue
                                ? 'bg-blue-50 border-blue-200 text-blue-700 hover:border-blue-400'
                                : 'bg-orange-50 border-orange-200 text-orange-700 hover:border-orange-400'
                            }`}
                          >
                            <span className="text-base shrink-0">☑️</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold truncate">{label}</p>
                              <p className="text-[11px] opacity-60">Case à cocher · peut être posée plusieurs fois</p>
                            </div>
                            {isPlaced && !isDraggingThis && <Check size={10} className="shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Champs texte libre (remplis par le signataire, façon Yousign) ── */}
                  <div>
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1.5">— Texte libre —</p>
                    <div className="flex flex-col gap-1.5">
                      {TEXT_INPUT_TAGS.map(({ tag, label, color }) => {
                        const isPlaced = placedTags.has(tag);
                        const isDraggingThis = dragTag === tag;
                        const isClickSelected = clickPlaceTag?.tag === tag;
                        const isBlue = color === 'blue';
                        return (
                          <div
                            key={tag}
                            draggable
                            onDragStart={e => { setDragTag(tag); setClickPlaceTag(null); e.dataTransfer.effectAllowed = 'copy'; }}
                            onDragEnd={() => setDragTag(null)}
                            onClick={() => setClickPlaceTag(isClickSelected ? null : { tag })}
                            className={`flex items-center gap-2 px-2.5 py-2.5 rounded-lg border-2 border-dashed select-none cursor-pointer transition-all ${
                              isClickSelected ? 'bg-violet-600 text-white border-violet-600 scale-95 shadow-inner' :
                              isDraggingThis ? 'opacity-40 scale-95' :
                              isBlue
                                ? 'bg-blue-50 border-blue-200 text-blue-700 hover:border-blue-400'
                                : 'bg-orange-50 border-orange-200 text-orange-700 hover:border-orange-400'
                            }`}
                          >
                            <span className="text-base shrink-0">📝</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold truncate">{label}</p>
                              <p className="text-[11px] opacity-60">Champ à remplir · peut être posé plusieurs fois</p>
                            </div>
                            {isPlaced && !isDraggingThis && <Check size={10} className="shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Balises texte ── */}
                  {Object.entries(ALL_TAGS).map(([group, tags]) => (
                    <div key={group}>
                      <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1.5">— {group} —</p>
                      <div className="flex flex-col gap-1">
                        {tags.map(tag => {
                          const isPlaced = placedTags.has(tag);
                          const isDraggingThis = dragTag === tag;
                          const isClickSelected = clickPlaceTag?.tag === tag;
                          const isDate = tag === 'date_du_jour';
                          return (
                            <div
                              key={tag}
                              draggable
                              onDragStart={e => { setDragTag(tag); setClickPlaceTag(null); e.dataTransfer.effectAllowed = 'copy'; }}
                              onDragEnd={() => setDragTag(null)}
                              onClick={() => setClickPlaceTag(isClickSelected ? null : { tag })}
                              className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[11px] font-mono border select-none cursor-pointer transition-all ${
                                isClickSelected ? 'bg-violet-600 text-white border-violet-600 shadow-inner' :
                                isDraggingThis ? 'opacity-40 scale-95' :
                                isDate ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-400' :
                                isPlaced ? 'bg-violet-50 border-violet-200 text-violet-700' :
                                'bg-white border-gray-100 text-gray-600 hover:border-violet-200 hover:bg-violet-50/50 hover:text-violet-600'
                              }`}
                            >
                              <span className="text-gray-300 shrink-0 text-xs">{isDate ? '📅' : '⠿'}</span>
                              <span className="truncate flex-1">{isDate ? 'date_du_jour' : `{${tag}}`}</span>
                              {isPlaced && !isDraggingThis && <Check size={10} className="shrink-0 text-violet-500" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Paramètres + Enregistrer */}
                <div className="p-4 border-t border-gray-100 space-y-3 shrink-0">
                  <div>
                    <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Nom du modèle</label>
                    <input
                      type="text"
                      value={templateName}
                      onChange={e => setTemplateName(e.target.value)}
                      className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-400"
                      placeholder="Ex : Convention de formation"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Destinataires (un ou plusieurs)</label>
                    <div className="flex gap-2">
                      {[['client', '📁 Client'], ['formateur', '📋 Formateur'], ['organisme', '🏢 Administrateur']].map(([val, label]) => {
                        const checked = destinationRoles.includes(val);
                        return (
                          <button key={val} onClick={() => setDestinationRoles(prev => {
                              const next = checked ? prev.filter(r => r !== val) : [...prev, val];
                              return next.length ? next : prev; // au moins un destinataire doit rester coché
                            })}
                            className={`flex-1 py-2 rounded-xl text-[11px] font-bold border transition-all ${checked ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-500 border-gray-100 hover:border-violet-200'}`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Mode de signature (Stage 3, 2026-07-24 ; généralisé à 3 parties le 2026-07-27) —
                      n'a de sens que si plusieurs destinataires sont cochés : sinon il n'y a qu'un
                      seul signataire, rien à séquencer. */}
                  {destinationRoles.length > 1 && (
                    <div>
                      <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Mode de signature</label>
                      <div className="flex gap-2 mb-2">
                        {[['simultane', '⇄ Simultané'], ['sequentiel', '→ Séquentiel']].map(([val, label]) => (
                          <button key={val} onClick={() => setSigningMode(val)}
                            className={`flex-1 py-2 rounded-xl text-[11px] font-bold border transition-all ${signingMode === val ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-500 border-gray-100 hover:border-violet-200'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mb-2">
                        {signingMode === 'sequentiel'
                          ? "Chaque partie ne verra le document qu'une fois son tour arrivé, dans l'ordre ci-dessous."
                          : 'Toutes les parties cochées peuvent signer indépendamment, dans n\'importe quel ordre.'}
                      </p>
                      {signingMode === 'sequentiel' && (
                        <div className="space-y-1.5">
                          <p className="text-[11px] text-gray-400 mb-0.5">Ordre de signature (▲▼ pour réordonner) :</p>
                          {signingOrder.filter(r => destinationRoles.includes(r)).map((role, idx, arr) => (
                            <div key={role} className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                              <span className="text-xs font-black text-gray-400 w-4">{idx + 1}.</span>
                              <span className="flex-1 text-[11px] font-bold text-gray-700">
                                {role === 'client' ? '📁 Client' : role === 'formateur' ? '📋 Formateur' : '🏢 Administrateur'}
                              </span>
                              <button type="button" disabled={idx === 0}
                                onClick={() => setSigningOrder(prev => {
                                  const cur = prev.filter(r => destinationRoles.includes(r));
                                  const i = cur.indexOf(role);
                                  if (i <= 0) return prev;
                                  const next = [...cur]; [next[i - 1], next[i]] = [next[i], next[i - 1]];
                                  return next;
                                })}
                                className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-violet-600 disabled:opacity-30">▲</button>
                              <button type="button" disabled={idx === arr.length - 1}
                                onClick={() => setSigningOrder(prev => {
                                  const cur = prev.filter(r => destinationRoles.includes(r));
                                  const i = cur.indexOf(role);
                                  if (i === -1 || i >= cur.length - 1) return prev;
                                  const next = [...cur]; [next[i + 1], next[i]] = [next[i], next[i + 1]];
                                  return next;
                                })}
                                className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-violet-600 disabled:opacity-30">▼</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handlePreview}
                    disabled={isPreviewing || fields.length === 0 || !pdfBlobRef.current}
                    className="w-full py-2.5 rounded-xl text-sm font-bold bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 transition-all disabled:opacity-40"
                    title="Génère le PDF avec des valeurs test pour vérifier le positionnement exact"
                  >
                    {isPreviewing ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-3.5 h-3.5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                        Génération…
                      </span>
                    ) : '👁 Prévisualiser le résultat'}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !templateName.trim() || fields.length === 0}
                    className="w-full py-3 rounded-xl text-sm font-bold bg-violet-600 hover:bg-violet-700 text-white transition-all disabled:opacity-40 shadow-sm"
                  >
                    {isSaving ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Enregistrement…
                      </span>
                    ) : `Enregistrer (${fields.length} balise${fields.length !== 1 ? 's' : ''})`}
                  </button>
                  {fields.length === 0 && (
                    <p className="text-[11px] text-amber-600 text-center">Glissez au moins une balise sur le document</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// AJOUT (2026-10-01) : onglet admin "Questionnaires & Quiz" (demande utilisateur, besoin Qualiopi).
// La création / modification / rattachement aux groupes des questionnaires et quiz est DÉPLACÉE
// ici depuis la page Documents (même code qu'avant, voir DocumentsView), et chaque questionnaire
// ou quiz ouvre désormais une page de STATISTIQUES (QuestionnaireStatsPanel) calculée à partir
// de la table questionnaire_responses : nombre de réponses, répartition des réponses question par
// question, réponses libres (verbatims), note moyenne et taux de réussite pour les quiz — avec
// filtres période / formateur / module et export PDF (preuve pour l'audit Qualiopi).
// Réservé à l'administrateur (voir ROLE_TABS et le menu latéral).
// ═══════════════════════════════════════════════════════════════════════════
// QUIZ (2026-10-13) : questions non notées (ex. « Nom et prénom », réponse libre). Une question compte
// dans la note seulement si c'est un choix unique/multiple et qu'elle n'est pas marquée notee === false.
// Les questions créées avant restent notées (notee absent = notée).
const qEstNotee = (q, isQuiz = true) => !!isQuiz && (q?.type === 'single' || q?.type === 'multiple') && q?.notee !== false;
const qManqueBonneReponse = (q) => qEstNotee(q) && (
  (q.type === 'single' && !q.correctAnswer) ||
  (q.type === 'multiple' && (!Array.isArray(q.correctAnswer) || q.correctAnswer.length === 0)));
// Quiz enregistrable : au moins une question notée, et chaque question notée a sa bonne réponse.
const qQuizInvalide = (questions) => (questions || []).some(qManqueBonneReponse) || !(questions || []).some(q => qEstNotee(q));

// QUIZ (2026-10-13) : un questionnaire du menu « Questionnaires » peut être repris dans un module
// (Documents de début / fin). Le module en garde une copie liée (metadata.source_questionnaire_id),
// remise à jour à chaque modification du questionnaire d'origine.
const Q_CONTENU_KEYS = ['questions', 'isQuiz', 'seuilReussite', 'description', 'closingMessage'];
const qContenu = (meta) => Object.fromEntries(Q_CONTENU_KEYS.filter(k => meta && meta[k] !== undefined).map(k => [k, meta[k]]));

const parseQMeta = (q) => {
  try { return typeof q?.metadata === 'string' ? JSON.parse(q.metadata) : (q?.metadata || {}); }
  catch { return {}; }
};
const parseQAnswers = (r) => {
  try { return typeof r?.responses === 'string' ? JSON.parse(r.responses) : (r?.responses || {}); }
  catch { return {}; }
};
// Date LOCALE au format AAAA-MM-JJ (comparaisons de filtres) et JJ/MM/AAAA (affichage).
const qLocalDay = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value).slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const qFrDate = (value) => {
  const day = qLocalDay(value);
  if (!day) return '—';
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
};
// Une réponse "unique" ou "multiple" est-elle correcte ? (même règle que QuestionnaireFillerModal :
// tout ou rien par question)
const qIsAnswerCorrect = (q, answer) => {
  if (q.type === 'single') return answer !== undefined && answer !== null && answer !== '' && answer === q.correctAnswer;
  if (q.type === 'multiple') {
    const correct = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
    const given = Array.isArray(answer) ? answer : [];
    return correct.length > 0 && correct.length === given.length && correct.every(o => given.includes(o));
  }
  return false;
};
// Note d'une réponse de quiz : on reprend la note enregistrée au moment du passage (score_percent /
// passed, enregistrés depuis le 2026-09-30) ; à défaut (anciennes réponses), on la recalcule.
const qQuizResult = (response, questions, seuil) => {
  if (typeof response.score_percent === 'number') {
    return { score: response.score_percent, passed: typeof response.passed === 'boolean' ? response.passed : response.score_percent >= seuil };
  }
  const gradable = questions.filter(q => qEstNotee(q));
  if (gradable.length === 0) return { score: null, passed: null };
  const answers = parseQAnswers(response);
  const correctCount = gradable.filter(q => qIsAnswerCorrect(q, answers[q.id])).length;
  const score = Math.round((correctCount / gradable.length) * 100);
  return { score, passed: score >= seuil };
};

// Calcule toutes les statistiques d'un questionnaire pour une liste de réponses déjà filtrées.
const computeQuestionnaireStats = (questionnaire, responses) => {
  const meta = parseQMeta(questionnaire);
  const questions = meta.questions || [];
  const isQuiz = !!meta.isQuiz;
  const seuil = meta.seuilReussite ?? 50;
  const parsed = responses.map(r => ({ ...r, _answers: parseQAnswers(r), _quiz: isQuiz ? qQuizResult(r, questions, seuil) : null }));

  const questionStats = questions.map((q, qi) => {
    const answered = parsed.filter(r => {
      const a = r._answers[q.id];
      if (q.type === 'text') return typeof a === 'string' && a.trim().length > 0;
      if (q.type === 'multiple') return Array.isArray(a) && a.length > 0;
      return a !== undefined && a !== null && a !== '';
    });
    const base = { index: qi + 1, question: q, answeredCount: answered.length };
    if (q.type === 'text') {
      return { ...base, texts: answered.map(r => ({ text: String(r._answers[q.id]).trim(), clientId: r.client_id, date: r.completed_at || r.created_at })) };
    }
    const counts = new Map();
    (q.options || []).filter(o => o !== '').forEach(o => counts.set(o, 0));
    answered.forEach(r => {
      const a = r._answers[q.id];
      (Array.isArray(a) ? a : [a]).forEach(v => counts.set(v, (counts.get(v) || 0) + 1));
    });
    const notee = qEstNotee(q, isQuiz);
    const correctSet = new Set(q.type === 'multiple' ? (Array.isArray(q.correctAnswer) ? q.correctAnswer : []) : (q.correctAnswer ? [q.correctAnswer] : []));
    const options = Array.from(counts.entries()).map(([label, count]) => ({
      label,
      count,
      percent: answered.length > 0 ? Math.round((count / answered.length) * 100) : 0,
      isCorrect: notee && correctSet.has(label),
      isLegacy: !(q.options || []).includes(label), // option supprimée/renommée depuis
    }));
    const correctCount = notee ? answered.filter(r => qIsAnswerCorrect(q, r._answers[q.id])).length : null;
    return {
      ...base,
      options,
      correctPercent: notee && answered.length > 0 ? Math.round((correctCount / answered.length) * 100) : null,
    };
  });

  const days = parsed.map(r => qLocalDay(r.completed_at || r.created_at)).filter(Boolean).sort();
  const quizScores = parsed.map(r => r._quiz).filter(x => x && typeof x.score === 'number');
  const passedCount = quizScores.filter(x => x.passed).length;
  return {
    meta, questions, isQuiz, seuil, parsed, questionStats,
    total: parsed.length,
    distinctClients: new Set(parsed.map(r => String(r.client_id))).size,
    firstDay: days[0] || null,
    lastDay: days[days.length - 1] || null,
    avgScore: quizScores.length > 0 ? Math.round(quizScores.reduce((s, x) => s + x.score, 0) / quizScores.length) : null,
    minScore: quizScores.length > 0 ? Math.min(...quizScores.map(x => x.score)) : null,
    maxScore: quizScores.length > 0 ? Math.max(...quizScores.map(x => x.score)) : null,
    passedCount,
    failedCount: quizScores.length - passedCount,
    passRate: quizScores.length > 0 ? Math.round((passedCount / quizScores.length) * 100) : null,
  };
};

// Export PDF "natif" (jsPDF, texte vectoriel) — volontairement sans capture d'écran html2canvas :
// rendu net, texte sélectionnable, et aucune dépendance aux couleurs CSS de Tailwind.
// NB : polices standard PDF → pas d'émojis ni de caractères hors Latin-1 dans les libellés fixes.
const exportQuestionnaireStatsPdf = async ({ titre, stats, filtersLabel, clientName, formateurOfClient, moduleOfClient, orgName }) => { /* 2026-10-21 : chargé à la demande */ const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF('p', 'mm', 'a4');
  const W = 210, H = 297, M = 15, CW = W - 2 * M;
  const VIOLET = [109, 40, 217], GREEN = [5, 150, 105], RED = [220, 38, 38], DARK = [31, 41, 55], GREY = [107, 114, 128], LIGHT = [237, 233, 254];
  let y = M;
  const clean = (s) => String(s ?? '')
    .replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"').replace(/[\u2013\u2014]/g, '-')
    .replace(/\u0153/g, 'oe').replace(/\u0152/g, 'OE').replace(/\u2026/g, '...').replace(/\u202F/g, ' ')
    .replace(/[^\x20-\xFF\n]/g, '');
  const ensure = (h) => { if (y + h > H - M - 8) { pdf.addPage(); y = M; } };
  const write = (str, { size = 10, bold = false, color = DARK, indent = 0, width = CW - indent, gap = 0 } = {}) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
    const lineH = size * 0.45;
    pdf.splitTextToSize(clean(str), width).forEach(line => {
      ensure(lineH);
      pdf.text(line, M + indent, y + lineH * 0.75);
      y += lineH;
    });
    y += gap;
  };

  // En-tête
  write(orgName ? `${orgName} - Statistiques questionnaire` : 'Statistiques questionnaire', { size: 9, color: GREY, gap: 1 });
  write(titre, { size: 16, bold: true, color: VIOLET, gap: 1 });
  write(`${stats.isQuiz ? `Quiz noté (seuil de réussite : ${stats.seuil} %)` : 'Questionnaire'} - ${stats.questions.length} question(s)`, { size: 9, color: GREY });
  write(`Filtres : ${filtersLabel}`, { size: 9, color: GREY });
  write(`Édité le ${qFrDate(new Date())}`, { size: 9, color: GREY, gap: 4 });

  // Indicateurs clés
  const kpis = [
    ['Réponses', String(stats.total)],
    ['Bénéficiaires', String(stats.distinctClients)],
    ['Période des réponses', stats.firstDay ? `${qFrDate(stats.firstDay)} au ${qFrDate(stats.lastDay)}` : '-'],
  ];
  if (stats.isQuiz) {
    kpis.push(['Note moyenne', stats.avgScore != null ? `${stats.avgScore} %` : '-']);
    kpis.push(['Taux de réussite', stats.passRate != null ? `${stats.passRate} % (${stats.passedCount} acquis / ${stats.failedCount} non acquis)` : '-']);
    kpis.push(['Note min / max', stats.minScore != null ? `${stats.minScore} % / ${stats.maxScore} %` : '-']);
  }
  ensure(8 + kpis.length * 6);
  pdf.setFillColor(...LIGHT);
  pdf.roundedRect(M, y, CW, 4 + kpis.length * 6, 2, 2, 'F');
  y += 2;
  kpis.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(...GREY);
    pdf.text(clean(label), M + 4, y + 4);
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK);
    pdf.text(clean(value), M + 60, y + 4);
    y += 6;
  });
  y += 6;

  // Détail question par question
  write('Détail par question', { size: 12, bold: true, color: VIOLET, gap: 2 });
  stats.questionStats.forEach(qs => {
    ensure(18);
    pdf.setDrawColor(229, 231, 235);
    pdf.line(M, y, M + CW, y);
    y += 3;
    write(`Q${qs.index}. ${qs.question.text || '(question sans titre)'}`, { size: 10.5, bold: true, gap: 0.5 });
    const typeLabel = (qs.question.type === 'text' ? 'Réponse libre' : qs.question.type === 'multiple' ? 'Choix multiples (plusieurs réponses possibles)' : 'Choix unique')
      + (stats.isQuiz && !qEstNotee(qs.question) ? ' - non notée' : '');
    write(`${typeLabel} - ${qs.answeredCount} réponse(s)${qs.correctPercent != null ? ` - ${qs.correctPercent} % de bonnes réponses` : ''}`, { size: 8.5, color: GREY, gap: 2 });

    if (qs.question.type === 'text') {
      if (qs.texts.length === 0) write('Aucune réponse.', { size: 9, color: GREY, indent: 3, gap: 2 });
      qs.texts.forEach(t => {
        write(`"${t.text}"`, { size: 9, indent: 3 });
        write(`- ${clientName(t.clientId)}, le ${qFrDate(t.date)}`, { size: 8, color: GREY, indent: 3, gap: 1.5 });
      });
      y += 2;
      return;
    }
    const labelW = 88, barX = M + 3 + labelW + 2, barW = 52;
    qs.options.forEach(o => {
      pdf.setFont('helvetica', o.isCorrect ? 'bold' : 'normal'); pdf.setFontSize(9);
      const lines = pdf.splitTextToSize(clean(o.label + (o.isCorrect ? '  (bonne réponse)' : '') + (o.isLegacy ? '  (option retirée)' : '')), labelW);
      const rowH = Math.max(lines.length * 4.1, 5);
      ensure(rowH + 1);
      pdf.setTextColor(...(o.isCorrect ? GREEN : DARK));
      lines.forEach((line, li) => pdf.text(line, M + 3, y + 3.4 + li * 4.1));
      pdf.setFillColor(243, 244, 246);
      pdf.roundedRect(barX, y + 0.8, barW, 3.4, 1, 1, 'F');
      if (o.percent > 0) {
        pdf.setFillColor(...(o.isCorrect ? GREEN : VIOLET));
        pdf.roundedRect(barX, y + 0.8, Math.max(1.5, (barW * o.percent) / 100), 3.4, 1, 1, 'F');
      }
      pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...DARK);
      pdf.text(`${o.percent} %`, barX + barW + 3, y + 3.4);
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...GREY);
      pdf.text(`(${o.count})`, barX + barW + 15, y + 3.4);
      y += rowH + 1;
    });
    y += 3;
  });

  // Liste des répondants
  y += 2;
  write('Liste des réponses', { size: 12, bold: true, color: VIOLET, gap: 2 });
  const cols = stats.isQuiz
    ? [['Bénéficiaire', 44], ['Formateur', 36], ['Module', 44], ['Date', 22], ['Résultat', 34]]
    : [['Bénéficiaire', 52], ['Formateur', 44], ['Module', 58], ['Date', 26]];
  const header = () => {
    ensure(7);
    pdf.setFillColor(...LIGHT);
    pdf.rect(M, y, CW, 6, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5); pdf.setTextColor(...VIOLET);
    let x = M + 2;
    cols.forEach(([label, w]) => { pdf.text(label, x, y + 4.2); x += w; });
    y += 7;
  };
  header();
  if (stats.parsed.length === 0) write('Aucune réponse pour ces filtres.', { size: 9, color: GREY });
  [...stats.parsed]
    .sort((a, b) => String(b.completed_at || b.created_at || '').localeCompare(String(a.completed_at || a.created_at || '')))
    .forEach(r => {
      if (y + 6 > H - M - 8) { pdf.addPage(); y = M; header(); }
      const values = [clientName(r.client_id), formateurOfClient(r.client_id), moduleOfClient(r.client_id), qFrDate(r.completed_at || r.created_at)];
      if (stats.isQuiz) values.push(r._quiz && r._quiz.score != null ? `${r._quiz.score} % - ${r._quiz.passed ? 'Acquis' : 'Non acquis'}` : '-');
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
      let x = M + 2;
      cols.forEach(([, w], ci) => {
        const isResult = stats.isQuiz && ci === cols.length - 1 && r._quiz && r._quiz.score != null;
        pdf.setTextColor(...(isResult ? (r._quiz.passed ? GREEN : RED) : DARK));
        pdf.text(pdf.splitTextToSize(clean(values[ci]), w - 3)[0] || '', x, y + 3.8);
        x += w;
      });
      pdf.setDrawColor(243, 244, 246);
      pdf.line(M, y + 5.5, M + CW, y + 5.5);
      y += 6;
    });

  // Pied de page : numérotation
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(...GREY);
    pdf.text(clean(`${titre} - page ${p}/${pages}`), W / 2, H - 8, { align: 'center' });
  }
  const safeName = clean(titre).replace(/[\\/:*?"<>|]/g, '-').trim() || 'questionnaire';
  pdf.save(`Statistiques - ${safeName}.pdf`);
};

const QuestionnaireStatsPanel = ({ questionnaire, responses, loadingResponses, clients, formateurs, modules, orgName, onBack, onEdit, recipients = [], questionnaireIds = null }) => {
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [formateurFilter, setFormateurFilter] = React.useState('');
  const [moduleFilter, setModuleFilter] = React.useState('');
  const [expandedTexts, setExpandedTexts] = React.useState({});

  const clientById = React.useMemo(() => {
    const m = new Map();
    (clients || []).forEach(c => m.set(String(c.id), c));
    return m;
  }, [clients]);
  const formateurNameById = (id) => (formateurs || []).find(f => String(f.id) === String(id))?.nom || '—';
  const moduleNameById = (id) => (modules || []).find(m => String(m.id) === String(id))?.nom || '—';
  const clientName = (clientId) => clientById.get(String(clientId))?.nom || 'Bénéficiaire supprimé';
  const formateurOfClient = (clientId) => { const c = clientById.get(String(clientId)); return c?.formateur_id ? formateurNameById(c.formateur_id) : '—'; };
  const moduleOfClient = (clientId) => { const c = clientById.get(String(clientId)); return c?.module_id ? moduleNameById(c.module_id) : '—'; };

  const allForQ = React.useMemo(
    () => {
      // 20/10/2026 : le questionnaire d'origine et toutes ses copies (voir idsDuQuestionnaire).
      const ids = new Set((questionnaireIds && questionnaireIds.length ? questionnaireIds : [questionnaire.id]).map(String));
      return (responses || []).filter(r => ids.has(String(r.questionnaire_id)));
    },
    [responses, questionnaire.id, questionnaireIds]
  );
  const filtered = React.useMemo(() => allForQ.filter(r => {
    const c = clientById.get(String(r.client_id));
    const day = qLocalDay(r.completed_at || r.created_at);
    if (dateFrom && (!day || day < dateFrom)) return false;
    if (dateTo && (!day || day > dateTo)) return false;
    if (formateurFilter && String(c?.formateur_id ?? '') !== formateurFilter) return false;
    if (moduleFilter && String(c?.module_id ?? '') !== moduleFilter) return false;
    return true;
  }), [allForQ, clientById, dateFrom, dateTo, formateurFilter, moduleFilter]);
  const stats = React.useMemo(() => computeQuestionnaireStats(questionnaire, filtered), [questionnaire, filtered]);
  // AJOUT (2026-10-05) : destinataires (filtres formateur / module appliqués) et ceux qui n'ont pas encore répondu
  const respondedClientIds = new Set(allForQ.map(r => String(r.client_id)));
  const filteredRecipients = (recipients || []).filter(rc => {
    const c = clientById.get(String(rc.clientId));
    if (!c) return false;
    if (formateurFilter && String(c.formateur_id ?? '') !== formateurFilter) return false;
    if (moduleFilter && String(c.module_id ?? '') !== moduleFilter) return false;
    return true;
  });
  const pendingRecipients = filteredRecipients.filter(rc => !respondedClientIds.has(String(rc.clientId)));

  // Ne proposer dans les filtres que les formateurs / modules réellement concernés par des réponses
  const formateurOptions = React.useMemo(() => {
    const ids = new Set(allForQ.map(r => clientById.get(String(r.client_id))?.formateur_id).filter(Boolean).map(String));
    return (formateurs || []).filter(f => ids.has(String(f.id)));
  }, [allForQ, clientById, formateurs]);
  const moduleOptions = React.useMemo(() => {
    const ids = new Set(allForQ.map(r => clientById.get(String(r.client_id))?.module_id).filter(Boolean).map(String));
    return (modules || []).filter(m => ids.has(String(m.id)));
  }, [allForQ, clientById, modules]);

  const hasFilters = !!(dateFrom || dateTo || formateurFilter || moduleFilter);
  const filtersLabel = [
    dateFrom || dateTo ? `période ${dateFrom ? 'du ' + qFrDate(dateFrom) : ''}${dateTo ? ' au ' + qFrDate(dateTo) : ''}`.trim() : null,
    formateurFilter ? `formateur : ${formateurNameById(formateurFilter)}` : null,
    moduleFilter ? `module : ${moduleNameById(moduleFilter)}` : null,
  ].filter(Boolean).join(' · ') || 'aucun (toutes les réponses)';

  const handleExport = async () => {
    try {
      await exportQuestionnaireStatsPdf({ titre: questionnaire.titre, stats, filtersLabel: filtersLabel.replace(/·/g, '-'), clientName, formateurOfClient, moduleOfClient, orgName });
    } catch (e) {
      console.error('[QuestionnaireStatsPanel] export PDF :', e);
      toast.error('Erreur export PDF : ' + e.message);
    }
  };

  const Kpi = ({ label, value, sub, tone = 'violet' }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
      <p className="text-xs font-black uppercase tracking-widest text-gray-400">{label}</p>
      <p className={`text-2xl font-black mt-1 ${tone === 'green' ? 'text-emerald-600' : tone === 'red' ? 'text-red-500' : 'text-violet-700'}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={onBack} className="text-sm text-gray-500 hover:text-violet-700 font-bold flex items-center gap-1 mb-2">
            <ChevronLeft size={16} /> Retour aux questionnaires
          </button>
          <h2 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
            <span>{stats.isQuiz ? '🎯' : '📝'}</span> {questionnaire.titre}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {stats.isQuiz ? `Quiz noté · seuil de réussite ${stats.seuil} %` : 'Questionnaire'} · {stats.questions.length} question{stats.questions.length > 1 ? 's' : ''}
            {questionnaire.module_id ? ` · intégré au module « ${moduleNameById(questionnaire.module_id)} »` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {onEdit && (
            <button onClick={onEdit} className="px-4 py-2 rounded-xl border border-violet-200 bg-white text-violet-700 text-sm font-bold hover:bg-violet-50">✏️ Modifier</button>
          )}
          <button onClick={handleExport} disabled={loadingResponses}
            className="px-4 py-2 rounded-xl bg-violet-700 text-white text-sm font-bold hover:bg-violet-800 shadow-sm flex items-center gap-2 disabled:opacity-50">
            <Download size={15} /> Exporter en PDF
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div>
          <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Du</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full p-2 border border-gray-200 rounded-xl text-sm" />
        </div>
        <div>
          <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Au</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full p-2 border border-gray-200 rounded-xl text-sm" />
        </div>
        <div>
          <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Formateur</label>
          <select value={formateurFilter} onChange={e => setFormateurFilter(e.target.value)} className="w-full p-2 border border-gray-200 rounded-xl text-sm bg-white">
            <option value="">Tous</option>
            {formateurOptions.map(f => <option key={f.id} value={String(f.id)}>{f.nom}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Module</label>
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="w-full p-2 border border-gray-200 rounded-xl text-sm bg-white">
            <option value="">Tous</option>
            {moduleOptions.map(m => <option key={m.id} value={String(m.id)}>{m.nom}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { const y = new Date().getFullYear(); setDateFrom(`${y}-01-01`); setDateTo(`${y}-12-31`); }}
            className="flex-1 p-2 rounded-xl bg-violet-50 text-violet-700 text-xs font-bold hover:bg-violet-100">Année en cours</button>
          {hasFilters && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setFormateurFilter(''); setModuleFilter(''); }}
              className="p-2 rounded-xl bg-gray-100 text-gray-600 text-xs font-bold hover:bg-gray-200">Effacer</button>
          )}
        </div>
      </div>

      {loadingResponses ? (
        <div className="py-16 text-center text-gray-400 text-sm">Chargement des réponses…</div>
      ) : (
        <>
          {/* AJOUT (2026-10-05) : destinataires et réponses en attente — demandé : "on voit le nombre de
              questionnaires en attente de réponse ?". Destinataires = envois directs (fiche client) + clients
              dont le module contient ce questionnaire (directement ou via un groupe de documents). */}
          <div className={`grid grid-cols-2 ${stats.isQuiz ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-3`}>
            <Kpi label="Réponses" value={stats.total} sub={hasFilters ? `sur ${allForQ.length} au total` : null} />
            <Kpi label="En attente de réponse" value={pendingRecipients.length} tone={pendingRecipients.length > 0 ? 'red' : 'green'}
              sub={filteredRecipients.length > 0 ? `sur ${filteredRecipients.length} destinataire${filteredRecipients.length > 1 ? 's' : ''}` : 'aucun destinataire connu'} />
            <Kpi label="Bénéficiaires" value={stats.distinctClients} />
            <Kpi label="Période" value={stats.firstDay ? qFrDate(stats.lastDay) : '—'} sub={stats.firstDay ? `1re réponse le ${qFrDate(stats.firstDay)}` : 'aucune réponse'} />
            {stats.isQuiz && <Kpi label="Note moyenne" value={stats.avgScore != null ? `${stats.avgScore} %` : '—'} sub={stats.minScore != null ? `min ${stats.minScore} % · max ${stats.maxScore} %` : null} />}
            {stats.isQuiz && <Kpi label="Taux de réussite" value={stats.passRate != null ? `${stats.passRate} %` : '—'} tone={stats.passRate == null ? 'violet' : stats.passRate >= 50 ? 'green' : 'red'} sub={`${stats.passedCount} acquis · ${stats.failedCount} non acquis`} />}
          </div>

          {pendingRecipients.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
              <p className="font-bold text-amber-800 text-sm mb-2">⏳ En attente de réponse ({pendingRecipients.length})</p>
              <div className="flex flex-wrap gap-2">
                {pendingRecipients.map(rc => (
                  <span key={String(rc.clientId)} className="text-xs bg-white border border-amber-200 text-amber-800 px-2.5 py-1 rounded-full">
                    {clientName(rc.clientId)} · {rc.via === 'envoi' ? `envoyé le ${qFrDate(rc.date)}` : 'via son module'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {stats.total === 0 ? (
            <div className="py-12 text-center bg-white rounded-2xl border border-dashed border-gray-200">
              <p className="text-3xl mb-2">📭</p>
              <p className="text-gray-500 text-sm font-bold">Aucune réponse {hasFilters ? 'pour ces filtres' : 'pour le moment'}.</p>
            </div>
          ) : (
            <>
              {/* Détail par question */}
              <div className="space-y-4">
                {stats.questionStats.map(qs => (
                  <div key={qs.question.id || qs.index} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div>
                        <p className="text-xs font-black text-violet-600 uppercase tracking-widest">Question {qs.index}</p>
                        <p className="font-bold text-gray-900">{qs.question.text || '(question sans titre)'}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {qs.question.type === 'text' ? 'Réponse libre' : qs.question.type === 'multiple' ? 'Choix multiples — plusieurs réponses possibles' : 'Choix unique'}{stats.isQuiz && !qEstNotee(qs.question) ? ' · non notée' : ''} · {qs.answeredCount} réponse{qs.answeredCount > 1 ? 's' : ''}
                        </p>
                      </div>
                      {qs.correctPercent != null && (
                        <span className={`text-xs font-black px-3 py-1 rounded-full ${qs.correctPercent >= 50 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                          {qs.correctPercent} % de bonnes réponses
                        </span>
                      )}
                    </div>

                    {qs.question.type === 'text' ? (
                      qs.texts.length === 0 ? <p className="text-sm text-gray-400 italic">Aucune réponse.</p> : (
                        <div className="space-y-2">
                          {(expandedTexts[qs.index] ? qs.texts : qs.texts.slice(0, 5)).map((t, i) => (
                            <div key={i} className="bg-gray-50 rounded-xl p-3">
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">« {t.text} »</p>
                              <p className="text-xs text-gray-400 mt-1">{clientName(t.clientId)} · {qFrDate(t.date)}</p>
                            </div>
                          ))}
                          {qs.texts.length > 5 && (
                            <button onClick={() => setExpandedTexts(prev => ({ ...prev, [qs.index]: !prev[qs.index] }))} className="text-xs font-bold text-violet-600 hover:underline">
                              {expandedTexts[qs.index] ? 'Réduire' : `Voir les ${qs.texts.length} réponses`}
                            </button>
                          )}
                        </div>
                      )
                    ) : (
                      <div className="space-y-2">
                        {qs.options.map(o => (
                          <div key={o.label} className="grid grid-cols-12 items-center gap-3">
                            <p className={`col-span-12 sm:col-span-5 text-sm ${o.isCorrect ? 'font-bold text-emerald-700' : 'text-gray-700'}`}>
                              {o.isCorrect && '✓ '}{o.label}{o.isLegacy && <span className="text-xs text-gray-400 ml-1">(option retirée)</span>}
                            </p>
                            <div className="col-span-9 sm:col-span-5 h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${o.isCorrect ? 'bg-emerald-500' : 'bg-violet-500'}`} style={{ width: `${o.percent}%` }} />
                            </div>
                            <p className="col-span-3 sm:col-span-2 text-sm text-right">
                              <span className="font-black text-gray-900">{o.percent} %</span> <span className="text-gray-400 text-xs">({o.count})</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Liste des réponses */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-bold text-gray-900">Liste des réponses ({stats.total})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-400">
                      <tr>
                        <th className="text-left px-4 py-2">Bénéficiaire</th>
                        <th className="text-left px-4 py-2">Formateur</th>
                        <th className="text-left px-4 py-2">Module</th>
                        <th className="text-left px-4 py-2">Date</th>
                        {stats.isQuiz && <th className="text-left px-4 py-2">Résultat</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {[...stats.parsed]
                        .sort((a, b) => String(b.completed_at || b.created_at || '').localeCompare(String(a.completed_at || a.created_at || '')))
                        .map(r => (
                          <tr key={r.id} className="border-t border-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-800">{clientName(r.client_id)}</td>
                            <td className="px-4 py-2 text-gray-600">{formateurOfClient(r.client_id)}</td>
                            <td className="px-4 py-2 text-gray-600">{moduleOfClient(r.client_id)}</td>
                            <td className="px-4 py-2 text-gray-600">{qFrDate(r.completed_at || r.created_at)}</td>
                            {stats.isQuiz && (
                              <td className="px-4 py-2">
                                {r._quiz && r._quiz.score != null ? (
                                  <span className={`text-xs font-black px-2 py-1 rounded-full ${r._quiz.passed ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                    {r._quiz.score} % · {r._quiz.passed ? 'Acquis' : 'Non acquis'}
                                  </span>
                                ) : '—'}
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const QuestionnairesView = ({ supabase, currentOrgId, clients, formateurs, modules, orgName }) => {
  // Modale de confirmation suppression (même mécanisme que DocumentsView)
  const [confirmState, setConfirmState] = React.useState({ open: false, title: '', message: '', onConfirm: null });
  const showDeleteConfirm = (title, message, onConfirmFn) => setConfirmState({ open: true, title, message, onConfirm: onConfirmFn });
  const hideDeleteConfirm = () => setConfirmState(prev => ({ ...prev, open: false, onConfirm: null }));

  // Groupes de documents (pour rattacher un questionnaire à un groupe, comme avant dans Documents)
  const [documentGroups, setDocumentGroups] = React.useState([]);
  React.useEffect(() => {
    if (!currentOrgId || !supabase) return;
    supabase.from('document_groups').select('*').eq('organisation_id', currentOrgId).order('nom', { ascending: true })
      .then(({ data }) => { if (data) setDocumentGroups(data); });
  }, [currentOrgId, supabase]);

  // Questionnaires intégrés directement à un module (créés depuis l'éditeur de module, module_id
  // renseigné) — leurs statistiques sont consultables ici, leur modification reste dans Modules.
  const [moduleQuestionnaires, setModuleQuestionnaires] = React.useState([]);
  const [selectedStatsId, setSelectedStatsId] = React.useState(null);

  // ── Réponses de tous les bénéficiaires de l'organisme ──
  const [responses, setResponses] = React.useState([]);
  const [loadingResponses, setLoadingResponses] = React.useState(true);
  const clientIdsKey = React.useMemo(() => (clients || []).map(c => c.id).join(','), [clients]);
  React.useEffect(() => {
    const ids = clientIdsKey ? clientIdsKey.split(',') : [];
    if (!supabase || ids.length === 0) { setResponses([]); setLoadingResponses(false); return; }
    let cancelled = false;
    (async () => {
      setLoadingResponses(true);
      const all = [];
      // Par paquets de 100 clients (longueur d'URL) et pages de 1000 lignes (limite Supabase)
      for (let i = 0; i < ids.length; i += 100) {
        const chunk = ids.slice(i, i + 100);
        for (let from = 0; ; from += 1000) {
          const { data, error } = await supabase.from('questionnaire_responses').select('*')
            .in('client_id', chunk).order('id', { ascending: true }).range(from, from + 999);
          if (error) { console.error('[QuestionnairesView] réponses :', error); toast.error('Erreur chargement des réponses : ' + error.message); break; }
          all.push(...(data || []));
          if (!data || data.length < 1000) break;
        }
      }
      if (!cancelled) { setResponses(all); setLoadingResponses(false); }
    })();
    return () => { cancelled = true; };
  }, [supabase, clientIdsKey]);

  const responseCountById = React.useMemo(() => {
    const m = new Map();
    responses.forEach(r => m.set(String(r.questionnaire_id), (m.get(String(r.questionnaire_id)) || 0) + 1));
    return m;
  }, [responses]);

  // AJOUT (2026-10-05) : qui a reçu chaque questionnaire ? — envois directs (questionnaire_assignments) et
  // clients dont le module contient le questionnaire (directement, ou via un groupe de documents du module).
  const [orgAssignments, setOrgAssignments] = React.useState([]);
  const [moduleGroupLinks, setModuleGroupLinks] = React.useState([]);
  React.useEffect(() => {
    if (!supabase || !currentOrgId) return;
    supabase.from('questionnaire_assignments').select('client_id, questionnaire_id, assigned_at').eq('organisation_id', currentOrgId)
      .then(({ data, error }) => { if (!error && data) setOrgAssignments(data); });
    supabase.from('module_step_resources').select('module_id, document_group_id').eq('type', 'document_group').eq('organisation_id', currentOrgId)
      .then(({ data }) => { if (data) setModuleGroupLinks(data.filter(l => l.module_id != null && l.document_group_id != null)); });
  }, [supabase, currentOrgId]);
  const getRecipients = (q) => {
    if (!q) return [];
    const byClient = new Map();
    orgAssignments.filter(a => String(a.questionnaire_id) === String(q.id))
      .forEach(a => byClient.set(String(a.client_id), { clientId: a.client_id, via: 'envoi', date: a.assigned_at }));
    const meta = parseQMeta(q);
    const moduleIds = new Set();
    if (q.module_id != null) moduleIds.add(String(q.module_id));
    const groupIds = (Array.isArray(meta.group_ids) && meta.group_ids.length > 0 ? meta.group_ids : (q.document_group_id ? [q.document_group_id] : [])).map(String);
    moduleGroupLinks.filter(l => groupIds.includes(String(l.document_group_id))).forEach(l => moduleIds.add(String(l.module_id)));
    (clients || []).forEach(c => {
      if (c.module_id != null && moduleIds.has(String(c.module_id)) && !byClient.has(String(c.id))) byClient.set(String(c.id), { clientId: c.id, via: 'module', date: null });
    });
    return Array.from(byClient.values());
  };
  // DÉCISION (20/10/2026) : un questionnaire de la bibliothèque regroupe ses réponses ET celles de toutes
  // ses copies placées dans des modules (metadata.source_questionnaire_id).
  const idsDuQuestionnaire = (q) => {
    if (!q) return [];
    if (q.module_id != null) return [String(q.id)];
    return [String(q.id), ...moduleQuestionnaires
      .filter(c => String(parseQMeta(c).source_questionnaire_id) === String(q.id)).map(c => String(c.id))];
  };
  const destinatairesRegroupes = (q) => {
    const ids = idsDuQuestionnaire(q);
    const parClient = new Map();
    [q, ...moduleQuestionnaires.filter(x => ids.includes(String(x.id)))].forEach(x =>
      getRecipients(x).forEach(rc => { if (!parClient.has(String(rc.clientId))) parClient.set(String(rc.clientId), rc); }));
    return Array.from(parClient.values());
  };
  const nbReponsesRegroupees = (q) => idsDuQuestionnaire(q).reduce((n, id) => n + (responseCountById.get(id) || 0), 0);
  const pendingCountFor = (q) => {
    const ids = idsDuQuestionnaire(q);
    const answered = new Set(responses.filter(r => ids.includes(String(r.questionnaire_id))).map(r => String(r.client_id)));
    return destinatairesRegroupes(q).filter(rc => !answered.has(String(rc.clientId))).length;
  };

  // ── États Questionnaires (déplacés depuis DocumentsView le 2026-10-01) ────────────────────────────────
  const [questionnaireTemplates, setQuestionnaireTemplates] = React.useState([]);
  const [showQBuilder, setShowQBuilder] = React.useState(false);
  const [qName, setQName] = React.useState('');
  const [qQuestions, setQQuestions] = React.useState([]);
  const [editingQId, setEditingQId] = React.useState(null);
  const [expandedQGroupId, setExpandedQGroupId] = React.useState(null);
  // AJOUT (2026-09-30) : quiz noté (correction auto + seuil de réussite) et messages facultatifs
  // de début/fin, communs aux questionnaires et aux quiz — voir QuestionnaireFillerModal.
  const [qIsQuiz, setQIsQuiz] = React.useState(false);
  const [qSeuilReussite, setQSeuilReussite] = React.useState(50);
  const [qDescription, setQDescription] = React.useState('');
  const [qClosingMessage, setQClosingMessage] = React.useState('');

  const fetchQTemplates = React.useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const { data, error } = await supabase
        .from('module_step_resources')
        .select('id, titre, metadata, document_group_id, module_id')
        .eq('type', 'questionnaire')
        .eq('organisation_id', currentOrgId)
        .order('titre', { ascending: true });
      if (error) { console.error('[fetchQTemplates] error:', error); toast.error('Erreur chargement questionnaires : ' + error.message); return; }
      const templates = (data || []).filter(r => r.module_id === null || r.module_id === undefined);
      setQuestionnaireTemplates(templates);
      setModuleQuestionnaires((data || []).filter(r => r.module_id !== null && r.module_id !== undefined));
    } catch(e) { console.error('[fetchQTemplates] exception:', e); }
  }, [currentOrgId]);

  React.useEffect(() => {
    fetchQTemplates();
  }, [fetchQTemplates]);

  const qAddQuestion = () => {
    // obligatoire: true par défaut (2026-10-02) — même comportement qu'avant cette option ; décochable par question.
    setQQuestions(prev => [...prev, { id: Date.now(), text: '', type: 'single', options: ['', ''], correctAnswer: '', obligatoire: true }]);
  };
  const qRemoveQuestion = (id) => setQQuestions(prev => prev.filter(q => q.id !== id));
  const qUpdateQuestion = (id, field, val) => setQQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: val } : q));
  // AJOUT (2026-09-30) : bascule le type d'une question en remettant correctAnswer dans le bon
  // format (chaîne pour "unique", tableau pour "multiple").
  const qSetQuestionType = (id, newType) => setQQuestions(prev => prev.map(q => q.id === id ? { ...q, type: newType, correctAnswer: newType === 'multiple' ? [] : '' } : q));
  const qAddOption = (qId) => setQQuestions(prev => prev.map(q => q.id === qId ? { ...q, options: [...q.options, ''] } : q));
  // AJOUT (2026-09-30) : correctAnswer est stocké par VALEUR (le texte de l'option) — on le
  // resynchronise ici si l'option qu'il désignait vient d'être renommée.
  const qUpdateOption = (qId, oi, val) => setQQuestions(prev => prev.map(q => {
    if (q.id !== qId) return q;
    const oldVal = q.options[oi];
    const options = q.options.map((o, i) => i === oi ? val : o);
    let correctAnswer = q.correctAnswer;
    if (q.type === 'single' && correctAnswer === oldVal) correctAnswer = val;
    if (q.type === 'multiple' && Array.isArray(correctAnswer) && correctAnswer.includes(oldVal)) correctAnswer = correctAnswer.map(o => o === oldVal ? val : o);
    return { ...q, options, correctAnswer };
  }));
  const qRemoveOption = (qId, oi) => setQQuestions(prev => prev.map(q => {
    if (q.id !== qId) return q;
    const removedVal = q.options[oi];
    const options = q.options.filter((_, i) => i !== oi);
    let correctAnswer = q.correctAnswer;
    if (q.type === 'single' && correctAnswer === removedVal) correctAnswer = '';
    if (q.type === 'multiple' && Array.isArray(correctAnswer)) correctAnswer = correctAnswer.filter(o => o !== removedVal);
    return { ...q, options, correctAnswer };
  }));
  const qToggleCorrectOption = (qId, opt) => setQQuestions(prev => prev.map(q => {
    if (q.id !== qId) return q;
    if (q.type === 'single') return { ...q, correctAnswer: opt };
    const current = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
    return { ...q, correctAnswer: current.includes(opt) ? current.filter(o => o !== opt) : [...current, opt] };
  }));

  const handleSaveQTemplate = async () => {
    if (!qName.trim() || qQuestions.length === 0) return;
    try {
      // Préserver les group_ids existants lors d'une mise à jour
      const existingMeta = editingQId
        ? (() => { const tpl = questionnaireTemplates.find(t => t.id === editingQId); try { return typeof tpl?.metadata === 'string' ? JSON.parse(tpl.metadata) : (tpl?.metadata || {}); } catch { return {}; } })()
        : {};
      const payload = {
        titre: qName.trim(),
        type: 'questionnaire',
        metadata: JSON.stringify({
          ...existingMeta,
          questions: qQuestions,
          isQuiz: qIsQuiz,
          seuilReussite: qIsQuiz ? qSeuilReussite : undefined,
          description: qDescription.trim() || undefined,
          closingMessage: qClosingMessage.trim() || undefined,
        }),
        module_id: null,
        organisation_id: currentOrgId,
      };
      let nbModulesMaj = 0;
      if (editingQId) {
        const { error } = await supabase.from('module_step_resources').update(payload).eq('id', editingQId);
        if (error) { toast.error('Erreur mise à jour : ' + error.message); return; }
        // Copies reprises dans des modules (Documents de début / fin) : même contenu.
        const { data: copies } = await supabase.from('module_step_resources').select('id, metadata, module_id')
          .eq('type', 'questionnaire').eq('organisation_id', currentOrgId);
        const liees = (copies || []).filter(c => c.module_id != null && String(parseQMeta(c).source_questionnaire_id) === String(editingQId));
        const contenu = qContenu(JSON.parse(payload.metadata));
        for (const c of liees) {
          const m = parseQMeta(c);
          const { error: e2 } = await supabase.from('module_step_resources')
            .update({ metadata: { ...m, questions: undefined, isQuiz: undefined, seuilReussite: undefined, description: undefined, closingMessage: undefined, ...contenu } }).eq('id', c.id);
          if (!e2) nbModulesMaj++;
        }
      } else {
        const { data: inserted, error } = await supabase.from('module_step_resources').insert([payload]).select();
        if (error) { toast.error('Erreur création : ' + error.message); return; }
      }
      toast.success(editingQId ? `Questionnaire mis à jour${nbModulesMaj ? ` (et dans ${nbModulesMaj} module${nbModulesMaj > 1 ? 's' : ''})` : ''}.` : 'Questionnaire créé.');
      setQName(''); setQQuestions([]); setEditingQId(null); setShowQBuilder(false);
      setQIsQuiz(false); setQSeuilReussite(50); setQDescription(''); setQClosingMessage('');
      await fetchQTemplates();
    } catch (e) { console.error('[handleSaveQTemplate] exception:', e); toast.error('Erreur inattendue : ' + e.message); }
  };

  const handleEditQTemplate = (q) => {
    const meta = (() => { try { return typeof q.metadata === 'string' ? JSON.parse(q.metadata) : (q.metadata || {}); } catch { return {}; } })();
    setQName(q.titre || '');
    setQQuestions((meta.questions || []).map(qq => ({ ...qq, id: qq.id || Date.now() + Math.random() })));
    setQIsQuiz(!!meta.isQuiz);
    setQSeuilReussite(meta.seuilReussite ?? 50);
    setQDescription(meta.description || '');
    setQClosingMessage(meta.closingMessage || '');
    setEditingQId(q.id);
    setShowQBuilder(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteQTemplate = (id) => {
    showDeleteConfirm(
      'Supprimer ce questionnaire ?',
      'Ce questionnaire sera définitivement supprimé.',
      async () => {
        hideDeleteConfirm();
        const { error } = await supabase.from('module_step_resources').delete().eq('id', id);
        if (error) { toast.error('Erreur : ' + error.message); } else { toast.success('Questionnaire supprimé.'); fetchQTemplates(); }
      }
    );
  };

  const handleQSetGroups = async (qId, selectedGroupIds, currentMeta) => {
    const newMeta = { ...(currentMeta || {}), group_ids: selectedGroupIds };
    const primaryGroup = selectedGroupIds.length > 0 ? selectedGroupIds[0] : null;
    const { error } = await supabase.from('module_step_resources').update({
      document_group_id: primaryGroup,
      metadata: JSON.stringify(newMeta),
    }).eq('id', qId);
    if (error) { toast.error('Erreur : ' + error.message); } else { fetchQTemplates(); }
  };


  const allQuestionnaires = [...questionnaireTemplates, ...moduleQuestionnaires];
  const selectedQuestionnaire = selectedStatsId != null ? allQuestionnaires.find(q => String(q.id) === String(selectedStatsId)) : null;
  const moduleNameById = (id) => (modules || []).find(m => String(m.id) === String(id))?.nom || 'Module supprimé';

  if (selectedQuestionnaire) {
    return (
      <div className="max-w-6xl mx-auto animate-fade-in">
        <QuestionnaireStatsPanel
          questionnaire={selectedQuestionnaire}
          responses={responses}
          loadingResponses={loadingResponses}
          clients={clients}
          formateurs={formateurs}
          modules={modules}
          orgName={orgName}
          onBack={() => setSelectedStatsId(null)}
          recipients={destinatairesRegroupes(selectedQuestionnaire)}
          questionnaireIds={idsDuQuestionnaire(selectedQuestionnaire)}
          onEdit={selectedQuestionnaire.module_id == null ? () => { setSelectedStatsId(null); handleEditQTemplate(selectedQuestionnaire); } : null}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Questionnaires & Quiz</h1>
        <p className="text-gray-500 mt-1">Créez vos questionnaires et quiz, puis cliquez sur l'un d'eux pour consulter ses statistiques (indicateurs Qualiopi) et les exporter en PDF.</p>
      </div>

          {/* ── Section Questionnaires & Quiz ── */}
          <div className="p-5 bg-violet-50 rounded-2xl border border-violet-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">📝 Questionnaires & Quiz</h3>
              <div className="flex flex-wrap items-center justify-end gap-2">
              {!questionnaireTemplates.some(t => t.titre === TITRE_MODELE_FINANCEUR) && (
                <button type="button"
                  onClick={async () => {
                    try { const r = await creerModeleSatisfactionFinanceur(currentOrgId); toast.success(r.existait ? 'Ce modèle existe déjà.' : 'Modèle « Satisfaction du financeur » créé : vous pouvez le modifier.'); await fetchQTemplates(); }
                    catch (e) { toast.error('Création impossible : ' + (e?.message || 'erreur inconnue')); }
                  }}
                  className="flex items-center gap-1.5 bg-white border border-violet-200 text-violet-700 text-xs font-bold px-3 py-2 rounded-xl hover:bg-violet-100 transition-all"
                  title="Questionnaire type à envoyer aux financeurs (Qualiopi, indicateur 30)">
                  ✨ Modèle « Satisfaction du financeur »
                </button>
              )}
              <button
                onClick={() => { setShowQBuilder(v => !v); if (editingQId) { setEditingQId(null); setQName(''); setQQuestions([]); setQIsQuiz(false); setQSeuilReussite(50); setQDescription(''); setQClosingMessage(''); } }}
                className="flex items-center gap-2 bg-violet-700 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm hover:bg-violet-800 transition-all"
              >
                <Plus size={13} /> {showQBuilder && !editingQId ? 'Annuler' : 'Nouveau questionnaire'}
              </button>
              </div>
            </div>

            {showQBuilder && (
              <div className="bg-white rounded-2xl p-5 border border-violet-200 space-y-4 mb-4">
                <div>
                  <label className="block text-xs font-black text-violet-700 uppercase tracking-widest mb-1.5">Titre du questionnaire</label>
                  <input type="text" placeholder="Ex : Questionnaire de satisfaction"
                    value={qName} onChange={e => setQName(e.target.value)}
                    className="w-full p-3 bg-gray-50 border border-violet-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400" />
                </div>

                <div>
                  <label className="block text-xs font-black text-violet-700 uppercase tracking-widest mb-1.5">Description (facultatif)</label>
                  <textarea
                    className="w-full p-3 bg-gray-50 border border-violet-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                    placeholder="Expliquez au client le fonctionnement, avant qu'il commence..."
                    rows={2}
                    value={qDescription} onChange={e => setQDescription(e.target.value)} />
                </div>

                <div className="flex items-center justify-between bg-violet-100/60 border border-violet-200 rounded-2xl p-3">
                  <div>
                    <p className="text-xs font-black text-violet-800 uppercase tracking-widest">🎯 Quiz noté</p>
                    <p className="text-xs text-violet-500 mt-0.5">Le client reçoit une note et un statut Acquis / Non acquis</p>
                  </div>
                  <button type="button" onClick={() => setQIsQuiz(v => !v)}
                    className={`w-12 h-7 rounded-full transition-all relative shrink-0 ${qIsQuiz ? 'bg-violet-600' : 'bg-gray-200'}`}>
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${qIsQuiz ? 'left-6' : 'left-1'}`}></span>
                  </button>
                </div>

                {qIsQuiz && (
                  <div>
                    <label className="block text-xs font-black text-violet-700 uppercase tracking-widest mb-1.5">Seuil de réussite (%)</label>
                    <input type="number" min="0" max="100" value={qSeuilReussite}
                      onChange={e => setQSeuilReussite(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                      className="w-32 p-2.5 bg-white border border-violet-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400" />
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-black text-violet-700 uppercase tracking-widest">Questions ({qQuestions.length})</label>
                    <button type="button" onClick={qAddQuestion} className="text-xs font-black text-violet-600 hover:text-violet-800 uppercase">+ Question</button>
                  </div>
                  {qQuestions.length === 0 && (
                    <div className="py-5 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <p className="text-gray-400 text-xs">Cliquez sur "+ Question" pour commencer</p>
                    </div>
                  )}
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {qQuestions.map((q, qi) => (
                      <div key={q.id} className="bg-violet-50 rounded-xl p-3 border border-violet-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-violet-600 uppercase">Q{qi + 1}</span>
                          <button type="button" onClick={() => qRemoveQuestion(q.id)} className="w-5 h-5 rounded bg-red-50 text-red-400 text-xs flex items-center justify-center hover:bg-red-100">✕</button>
                        </div>
                        <input type="text" placeholder="Texte de la question..." value={q.text} onChange={e => qUpdateQuestion(q.id, 'text', e.target.value)}
                          className="w-full p-2.5 bg-white border border-violet-200 rounded-lg text-sm outline-none focus:ring-1 focus:ring-violet-400" />
                        <div className="flex gap-1.5">
                          {[['single', '◉ Unique'], ['multiple', '☑ Multiple'], ['text', '✏️ Libre']].map(([val, lbl]) => (
                            <button key={val} type="button" onClick={() => qSetQuestionType(q.id, val)}
                              className={`flex-1 py-1 rounded text-xs font-black transition-all ${q.type === val ? 'bg-violet-600 text-white' : 'bg-white border border-violet-200 text-gray-500 hover:border-violet-400'}`}>{lbl}</button>
                          ))}
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                          <input type="checkbox" checked={q.obligatoire !== false} onChange={e => qUpdateQuestion(q.id, 'obligatoire', e.target.checked)}
                            className="accent-violet-600 w-3.5 h-3.5" />
                          <span className="text-xs font-bold text-gray-500">Obligatoire — le client devra y répondre pour valider</span>
                        </label>
                        {qIsQuiz && (q.type === 'single' || q.type === 'multiple') && (
                          <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                            <input type="checkbox" checked={q.notee !== false} onChange={e => qUpdateQuestion(q.id, 'notee', e.target.checked)}
                              className="accent-violet-600 w-3.5 h-3.5" />
                            <span className="text-xs font-bold text-gray-500">Question notée — compte dans la note du quiz</span>
                          </label>
                        )}
                        {qIsQuiz && (q.type === 'text' || q.notee === false) && (
                          <p className="text-[11px] font-bold text-gray-400">Non notée : la réponse est enregistrée mais ne compte pas dans la note.</p>
                        )}
                        {(q.type === 'single' || q.type === 'multiple') && (
                          <div className="space-y-1">
                            {qEstNotee(q, qIsQuiz) && <p className="text-[11px] font-black text-emerald-600 uppercase tracking-widest">Cochez la ou les bonne(s) réponse(s)</p>}
                            {q.options.map((opt, oi) => {
                              const isCorrect = q.type === 'single' ? (q.correctAnswer === opt && opt !== '') : (Array.isArray(q.correctAnswer) && q.correctAnswer.includes(opt) && opt !== '');
                              return (
                                <div key={oi} className="flex items-center gap-2">
                                  {qEstNotee(q, qIsQuiz) ? (
                                    <button type="button" onClick={() => qToggleCorrectOption(q.id, opt)}
                                      title="Marquer comme bonne réponse"
                                      className={`w-4 h-4 shrink-0 flex items-center justify-center rounded${q.type === 'multiple' ? '' : '-full'} border-2 transition-all ${isCorrect ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 text-transparent hover:border-emerald-400'}`}>
                                      <span className="text-[11px] leading-none">✓</span>
                                    </button>
                                  ) : (
                                    <span className="text-gray-400 text-xs">{q.type === 'single' ? '○' : '□'}</span>
                                  )}
                                  <input type="text" placeholder={`Option ${oi + 1}`} value={opt} onChange={e => qUpdateOption(q.id, oi, e.target.value)}
                                    className={`flex-1 p-1.5 bg-white border rounded-lg text-xs outline-none focus:ring-1 focus:ring-violet-400 ${qEstNotee(q, qIsQuiz) && isCorrect ? 'border-emerald-300' : 'border-violet-100'}`} />
                                  {q.options.length > 1 && <button type="button" onClick={() => qRemoveOption(q.id, oi)} className="text-gray-300 hover:text-red-400 text-xs">✕</button>}
                                </div>
                              );
                            })}
                            <button type="button" onClick={() => qAddOption(q.id)} className="text-xs text-violet-500 hover:text-violet-700 font-bold">+ Option</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-black text-violet-700 uppercase tracking-widest mb-1.5">Message de fin (facultatif)</label>
                  <textarea
                    className="w-full p-3 bg-gray-50 border border-violet-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                    placeholder="Un mot pour remercier ou féliciter le client une fois terminé..."
                    rows={2}
                    value={qClosingMessage} onChange={e => setQClosingMessage(e.target.value)} />
                </div>

                {qIsQuiz && qQuestions.length > 0 && qQuizInvalide(qQuestions) && (
                  <p className="text-xs font-bold text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
                    {!qQuestions.some(q => qEstNotee(q))
                      ? 'Un quiz noté doit avoir au moins une question notée (choix unique ou multiple).'
                      : `Indiquez la bonne réponse de ${qQuestions.map((q, i) => (qManqueBonneReponse(q) ? `Q${i + 1}` : null)).filter(Boolean).join(', ')}, ou décochez « Question notée ».`}
                  </p>
                )}
                <button onClick={handleSaveQTemplate} disabled={!qName.trim() || qQuestions.length === 0 || (qIsQuiz && qQuizInvalide(qQuestions))}
                  className="w-full bg-violet-700 hover:bg-violet-800 text-white font-black py-3 rounded-xl transition-all disabled:opacity-50">
                  {editingQId ? '✓ Mettre à jour le questionnaire' : '✓ Enregistrer le questionnaire'}
                </button>
              </div>
            )}

            {/* Grille des questionnaires */}
            {questionnaireTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {questionnaireTemplates.map(q => {
                  const qMeta = (() => { try { return typeof q.metadata === 'string' ? JSON.parse(q.metadata) : (q.metadata || {}); } catch { return {}; } })();
                  const nbQ = (qMeta.questions || []).length;
                  const rawGroupIds = qMeta.group_ids && qMeta.group_ids.length > 0 ? qMeta.group_ids : (q.document_group_id ? [q.document_group_id] : []);
                  // FIX (2026-09-30) : ignore les ids de groupes qui n'existent plus (groupe supprimé
                  // depuis), sinon le badge affiche un nombre de groupes dont aucun n'est coché.
                  const qGroupIds = rawGroupIds.filter(id => documentGroups.some(g => g.id === id));
                  return (
                        <div key={q.id} className="bg-white p-4 rounded-2xl border border-violet-100 shadow-sm hover:border-violet-300 transition-all">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{qMeta.isQuiz ? '🎯' : '📝'}</span>
                              <div>
                                <p className="font-bold text-gray-900 text-sm">{q.titre}</p>
                                <p className="text-xs text-gray-500">{nbQ} question{nbQ > 1 ? 's' : ''} · {qMeta.isQuiz ? 'Quiz noté' : 'Questionnaire'}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => handleEditQTemplate(q)} title="Modifier" className="p-1.5 text-violet-400 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-all text-sm">✏️</button>
                              <button onClick={() => handleDeleteQTemplate(q.id)} title="Supprimer" className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={13} /></button>
                            </div>
                          </div>
                          <button type="button" onClick={() => setSelectedStatsId(q.id)}
                            className="w-full flex items-center justify-between mt-1 px-3 py-2 rounded-lg bg-violet-700 text-white hover:bg-violet-800 transition-colors">
                            <span className="text-[11px] font-black uppercase tracking-widest">📊 Voir les statistiques</span>
                            <span className="text-[11px] font-bold">{loadingResponses ? '…' : `${nbReponsesRegroupees(q)} réponse${nbReponsesRegroupees(q) > 1 ? 's' : ''} · ${pendingCountFor(q)} en attente`}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setExpandedQGroupId(expandedQGroupId === q.id ? null : q.id)}
                            className="w-full flex items-center justify-between mt-2 px-2 py-1.5 rounded-lg bg-violet-50 hover:bg-violet-100 transition-colors"
                          >
                            <span className="text-xs font-black text-violet-700 uppercase tracking-widest flex items-center gap-1.5">
                              📁 {qGroupIds.length > 0 ? `${qGroupIds.length} groupe${qGroupIds.length > 1 ? 's' : ''}` : 'Aucun groupe'}
                            </span>
                            <span className="text-violet-400 text-xs">{expandedQGroupId === q.id ? '▲' : '▼'}</span>
                          </button>
                          {expandedQGroupId === q.id && (
                            <div className="space-y-1 mt-1 max-h-40 overflow-y-auto pr-1 border border-violet-100 rounded-xl p-2 bg-white">
                              {documentGroups.length === 0 && <p className="text-xs text-gray-400 italic">Aucun groupe créé</p>}
                              {documentGroups.map(g => {
                                const checked = qGroupIds.includes(g.id);
                                return (
                                  <label key={g.id} className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-violet-100' : 'hover:bg-gray-50'}`}>
                                    <input type="checkbox" checked={checked} className="accent-violet-600 w-3.5 h-3.5"
                                      onChange={() => {
                                        const next = checked ? qGroupIds.filter(id => id !== g.id) : [...qGroupIds, g.id];
                                        handleQSetGroups(q.id, next, qMeta);
                                      }} />
                                    <span className="text-xs text-gray-700">{g.nom}</span>
                                    {checked && <span className="ml-auto text-violet-500 text-xs font-bold">✓</span>}
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                  );
                })}
              </div>
            ) : !showQBuilder && (
              <div className="py-8 text-center bg-white rounded-2xl border border-dashed border-violet-200">
                <p className="text-2xl mb-1">📝</p>
                <p className="text-gray-400 text-sm">Aucun questionnaire créé.</p>
                <p className="text-gray-300 text-xs mt-0.5">Cliquez sur "Nouveau questionnaire" pour commencer.</p>
              </div>
            )}
          </div>


      {moduleQuestionnaires.length > 0 && (
        <div className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="font-bold text-gray-900 mb-1">Questionnaires intégrés aux modules</h3>
          <p className="text-xs text-gray-400 mb-4">Créés directement dans un module (onglet Modules, où ils se modifient). Cliquez pour voir leurs statistiques.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {moduleQuestionnaires.map(q => {
              const qMeta = parseQMeta(q);
              const nbQ = (qMeta.questions || []).length;
              const nbR = responseCountById.get(String(q.id)) || 0;
              return (
                <button key={q.id} type="button" onClick={() => setSelectedStatsId(q.id)}
                  className="text-left bg-white p-4 rounded-2xl border border-gray-100 hover:border-violet-300 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{qMeta.isQuiz ? '🎯' : '📝'}</span>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 text-sm truncate">{q.titre}</p>
                      <p className="text-xs text-gray-500">{moduleNameById(q.module_id)} · {nbQ} question{nbQ > 1 ? 's' : ''} · {qMeta.isQuiz ? 'Quiz noté' : 'Questionnaire'}</p>
                    </div>
                    <span className="ml-auto shrink-0 text-xs font-black text-violet-700 bg-violet-50 px-2 py-1 rounded-full">📊 {loadingResponses ? '…' : `${nbR} réponse${nbR > 1 ? 's' : ''} · ${pendingCountFor(q)} en attente`}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={hideDeleteConfirm}
      />
    </div>
  );
};

const DocumentsView = ({
  sessions, documents, clients, formateurs, userRole, currentUserId, currentOrgId,
  handleSignDocument, handleDownloadPDF, handleAddDocument,
  updateDateSeance, newDocName, setNewDocName,
  newDocType, setNewDocType, newDocUrl, setNewDocUrl,
  newDocFile, setNewDocFile, newDocClientId, setNewDocClientId,
  newDocVisClient, setNewDocVisClient, newDocVisFormateur, setNewDocVisFormateur,
  isAddingDoc, selectedClientForDocs, setSelectedClientForDocs,
  signingDocId, setSigningDocId, viewingDocId, setViewingDocId,
  handleSignatureSave, documentTemplates, handleUploadDocxTemplate, handleUploadVisualTemplate,
  newTemplateName, setNewTemplateName, setIsDeleteModalOpen, setTargetToDelete,
  newTemplateDestination, setNewTemplateDestination, supabase,
  onUpdateTemplateDestination,
  newTemplateClassification, setNewTemplateClassification, fetchDocuments,
  onOpenQuestionnaires
}) => {
  const [expandedId, setExpandedId] = React.useState(null);
  const [clientDocTab, setClientDocTab] = React.useState('avant');
  // FIX (2026-09-07) : "historique" remplace les anciens onglets séparés "client"/"formateur" —
  // un seul onglet listant tous les documents envoyés, quel que soit le destinataire.
  const [docAudienceTab, setDocAudienceTab] = React.useState('historique');
  // FIX (2026-09-07) : pagination de la grille de documents émis (15 par page), pour que
  // l'historique reste lisible même avec beaucoup de documents.
  const [docsPage, setDocsPage] = React.useState(1);
  const DOCS_PER_PAGE = 15;
  const isAdmin = userRole === 'admin';
  const isClient = userRole === 'client';
  const isFormateur = userRole === 'formateur';
  const nomsGroupesDocs = useNomsGroupes(currentOrgId); // signature en lot (2026-10-23)

  // Renommer un modèle inline
  const [editingName, setEditingName] = React.useState(null); // nom actuel en cours d'édition
  const [editingNameValue, setEditingNameValue] = React.useState('');
  const handleRenameSubmit = async (oldName) => {
    const newName = editingNameValue.trim();
    if (!newName || newName === oldName) { setEditingName(null); return; }
    try {
      // Scopé strictement à l'organisme courant — un modèle du même nom appartenant à un AUTRE
      // organisme ne doit jamais pouvoir être renommé (plus de tolérance pour organisation_id NULL,
      // toutes les lignes historiques ont été rattachées à leur organisme d'origine par migration SQL).
      if (!currentOrgId) { setEditingName(null); return; }
      const msrRenameQuery = supabase.from('module_step_resources').update({ titre: newName }).eq('titre', oldName).eq('organisation_id', currentOrgId);
      await msrRenameQuery;

      const docRenameQuery = supabase.from('documents').update({ nom: newName }).eq('nom', oldName).is('user_id', null).eq('organisation_id', currentOrgId);
      await docRenameQuery;

      if (fetchDocuments) await fetchDocuments();
      toast.success(`Renommé en "${newName}"`);
    } catch(e) { toast.error('Erreur renommage : ' + e.message); }
    setEditingName(null);
  };

  // Modifier les balises d'un template visuel existant
  const [editingTemplate, setEditingTemplate] = React.useState(null); // { url, fields, name, destination }

  // TemplateEditorModal
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = React.useState(false);
  // Upload simple (sans balises)
  const [isUploadingSimple, setIsUploadingSimple] = React.useState(false);
  const simpleUploadRef = React.useRef(null);
  const handleUploadSimpleTemplate = async (file) => {
    if (!file) return;
    const name = file.name.replace(/\.[^/.]+$/, '');
    const fileExt = (file.name.split('.').pop() || 'pdf').toLowerCase();
    setIsUploadingSimple(true);
    const toastId = 'simple-upload';
    toast.loading('Envoi du document…', { id: toastId });
    try {
      const safeName = name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `template_simple_${safeName}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);
      const metadataObj = { has_visual_fields: false, classification: 'a_signer' };
      // Scoping toujours appliqué (avant : uniquement si currentOrgId était renseigné, sinon la
      // recherche portait silencieusement sur TOUS les organismes).
      let msrQuery = supabase.from('module_step_resources').select('id').eq('titre', name).eq('type', 'document');
      msrQuery = currentOrgId ? msrQuery.eq('organisation_id', currentOrgId) : msrQuery.limit(0);
      const { data: existingMsr } = await msrQuery;
      if (existingMsr && existingMsr.length > 0) {
        const { error: eMaj } = await supabase.from('module_step_resources').update({
          file_url: publicUrl, destination: 'client', metadata: JSON.stringify(metadataObj),
        }).eq('id', existingMsr[0].id);
        if (eMaj) throw eMaj; // LOT B
      } else {
        const { error: msrErr } = await supabase.from('module_step_resources').insert([{
          titre: name, file_url: publicUrl, type: 'document', destination: 'client',
          metadata: JSON.stringify(metadataObj),
          ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
        }]);
        if (msrErr) throw msrErr;
      }
      let docQuery = supabase.from('documents').select('id').eq('nom', name).is('user_id', null).is('assigned_formateur_id', null); // lot 2
      docQuery = currentOrgId ? docQuery.eq('organisation_id', currentOrgId) : docQuery.limit(0);
      const { data: existingDoc } = await docQuery;
      if (existingDoc && existingDoc.length > 0) {
        const { error: eDoc } = await supabase.from('documents').update({ url: publicUrl, metadata: JSON.stringify(metadataObj) }).eq('id', existingDoc[0].id);
        if (eDoc) throw eDoc; // LOT B
      } else {
        const { error: eDoc } = await supabase.from('documents').insert([{
          nom: name, type_action: 'Modèle Référence', url: publicUrl,
          metadata: JSON.stringify(metadataObj), user_id: null,
          ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
        }]);
        if (eDoc) throw eDoc; // LOT B
      }
      if (fetchDocuments) await fetchDocuments();
      toast.success(`"${name}" ajouté comme document à signer !`, { id: toastId });
    } catch (e) {
      console.error('[handleUploadSimpleTemplate]', e);
      toast.error('Erreur : ' + e.message, { id: toastId });
    } finally {
      setIsUploadingSimple(false);
      if (simpleUploadRef.current) simpleUploadRef.current.value = '';
    }
  };
  // Prévisualisation des balises d'un template
  const [previewTemplate, setPreviewTemplate] = React.useState(null); // { key, tpl }
  const [previewPages, setPreviewPages] = React.useState([]);
  const [previewFields, setPreviewFields] = React.useState([]);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  const openTemplatePreview = async (key, tpl) => {
    setPreviewTemplate({ key, tpl });
    setPreviewLoading(true);
    setPreviewPages([]);
    setPreviewFields([]);
    try {
      // 1. Charger les champs visuels
      if (tpl.id) {
        const { data: fields } = await supabase.from('template_fields').select('*').eq('template_id', tpl.id).order('page', { ascending: true });
        setPreviewFields(fields || []);
      }
      // 2. Obtenir le PDF — si déjà PDF pas de conversion, sinon convertir DOCX
      const resp = await fetch(tpl.url);
      const blob = await resp.blob();
      let pdfBlob;
      const tplIsPdf = /\.pdf$/i.test((tpl.url || '').split('?')[0]);
      if (tplIsPdf) {
        pdfBlob = blob; // PDF direct : aucune conversion nécessaire
      } else {
        try { pdfBlob = await convertDocxBlobToPdf(blob); }
        catch(_) { pdfBlob = await convertDocxBlobToPdfLocal(blob); }
      }
      // 3. Rendre avec PDF.js
      const loadPdfJs = () => new Promise((resolve, reject) => {
        if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; resolve(window.pdfjsLib); };
        s.onerror = () => reject(new Error('PDF.js indisponible'));
        document.head.appendChild(s);
      });
      const pdfjsLib = await loadPdfJs();
      const arrBuf = await pdfBlob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrBuf }).promise;
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const vp = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        canvas.width = vp.width; canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        pages.push({ dataUrl: canvas.toDataURL(), w: vp.width, h: vp.height });
      }
      setPreviewPages(pages);
    } catch(e) {
      toast.error('Impossible de charger la prévisualisation : ' + e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Modale de confirmation suppression
  const [confirmState, setConfirmState] = React.useState({ open: false, title: '', message: '', onConfirm: null });
  const showDeleteConfirm = (title, message, onConfirmFn) => setConfirmState({ open: true, title, message, onConfirm: onConfirmFn });
  const hideDeleteConfirm = () => setConfirmState(prev => ({ ...prev, open: false, onConfirm: null }));

  // ── Source fusionnée pour la grille de modèles (documents table + MSR via documentTemplates) ──
  // Assure l'affichage même si l'insert dans la table documents échoue silencieusement
  const displayTemplates = React.useMemo(() => {
    const fromDocs = (documents || []).filter(d => !d.user_id && !d.assigned_formateur_id);
    const fromDocsNoms = new Set(fromDocs.map(d => d.nom));
    const fromMsr = Object.entries(documentTemplates || {})
      .filter(([nom]) => !fromDocsNoms.has(nom))
      .map(([nom, tpl]) => {
        const ext = tpl.url ? tpl.url.split('.').pop().split('?')[0].toLowerCase() : 'pdf';
        return {
          id: tpl.id || nom,
          nom,
          url: tpl.url,
          type_action: 'Modèle Référence',
          metadata: tpl.metadata || {},
          extension: ext,
          visible_formateur: parseDestinationRoles(tpl.destination).includes('formateur'),
          group_ids: [],
          group_id: null,
          _fromMsrOnly: true,
        };
      });
    return [...fromDocs, ...fromMsr];
  }, [documents, documentTemplates]);

  // États pour la bibliothèque de modèles (onglet Formateurs)
  const [fplShowUpload, setFplShowUpload] = React.useState(false);
  const [fplName, setFplName] = React.useState('');
  const [fplFile, setFplFile] = React.useState(null);
  const [fplUploading, setFplUploading] = React.useState(false);

  // Groupes de documents
  const [documentGroups, setDocumentGroups] = React.useState([]);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [isCreatingGroup, setIsCreatingGroup] = React.useState(false);
  const [selectedTemplateFile, setSelectedTemplateFile] = React.useState(null);
  const [expandedGroupId, setExpandedGroupId] = React.useState(null);
  const [issuedDocToDelete, setIssuedDocToDelete] = React.useState(null);

  const handleDeleteIssuedDoc = async () => {
    if (!issuedDocToDelete) return;
    // LOT 2 (2026-10-20) : un document signé n'est jamais supprimé, et le FICHIER n'est plus effacé du
    // stockage — c'était souvent le modèle partagé par tous les autres clients, ou un PDF signé. Les
    // fichiers devenus inutiles seront nettoyés à part.
    if (!(await peutSupprimerDocument(issuedDocToDelete.id))) { setIssuedDocToDelete(null); return; }
    const { error } = await supabase.from('documents').delete().eq('id', issuedDocToDelete.id);
    if (error) { toast.error('Erreur suppression : ' + error.message); }
    else { toast.success('Document supprimé.'); if (fetchDocuments) fetchDocuments(); }
    setIssuedDocToDelete(null);
  };

  const fetchDocumentGroups = React.useCallback(async () => {
    if (!currentOrgId) return;
    const { data, error } = await supabase.from('document_groups').select('*').eq('organisation_id', currentOrgId).order('nom', { ascending: true });
    if (!error && data) setDocumentGroups(data);
  }, [supabase, currentOrgId]);

  React.useEffect(() => {
    if (isAdmin) fetchDocumentGroups();
  }, [isAdmin, fetchDocumentGroups]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setIsCreatingGroup(true);
    const { error } = await supabase.from('document_groups').insert([{ nom: newGroupName.trim(), organisation_id: currentOrgId }]);
    setIsCreatingGroup(false);
    if (!error) {
      setNewGroupName('');
      fetchDocumentGroups();
      toast.success("Groupe créé avec succès.");
    } else {
      toast.error("Erreur de création du groupe : " + error.message);
    }
  };

  const handleDeleteGroup = (groupId) => {
    showDeleteConfirm(
      'Supprimer ce groupe ?',
      'Les documents associés seront simplement détachés, pas supprimés.',
      async () => {
        hideDeleteConfirm();
        await supabase.from('module_step_resources').update({ document_group_id: null }).eq('document_group_id', groupId);
        // Détacher les documents dont group_id = ce groupe (évite FK orpheline)
        await supabase.from('documents').update({ group_id: null }).eq('group_id', groupId).is('user_id', null);
        // FIX (2026-09-30) : nettoie aussi metadata.group_ids des questionnaires/quiz rattachés à ce
        // groupe — sinon leur badge "X groupes" restait gonflé indéfiniment après cette suppression
        // (voir qGroupIds plus bas, qui filtre déjà les ids orphelins à l'affichage, mais autant
        // nettoyer la donnée elle-même pour les prochaines fois).
        const { data: questTemplatesWithGroup } = await supabase.from('module_step_resources').select('id, metadata').eq('type', 'questionnaire').eq('organisation_id', currentOrgId);
        if (questTemplatesWithGroup) {
          for (const tpl of questTemplatesWithGroup) {
            const meta = (() => { try { return typeof tpl.metadata === 'string' ? JSON.parse(tpl.metadata) : (tpl.metadata || {}); } catch { return {}; } })();
            if (Array.isArray(meta.group_ids) && meta.group_ids.includes(groupId)) {
              const newGroupIds = meta.group_ids.filter(id => id !== groupId);
              await supabase.from('module_step_resources').update({ metadata: JSON.stringify({ ...meta, group_ids: newGroupIds }) }).eq('id', tpl.id);
            }
          }
        }
        const { error } = await supabase.from('document_groups').delete().eq('id', groupId);
        if (!error) {
          fetchDocumentGroups();
          if (fetchDocuments) fetchDocuments();
          fetchQTemplates();
          toast.success("Groupe supprimé.");
        } else {
          toast.error("Erreur lors de la suppression : " + error.message);
        }
      }
    );
  };

  const handleToggleDocumentGroup = async (docId, nextGroupId) => {
    if (!docId) return;
    const { error } = await supabase.from('documents').update({ group_id: nextGroupId }).eq('id', docId);
    if (!error) {
      if (fetchDocuments) fetchDocuments();
      toast.success(nextGroupId ? "Groupe principal mis à jour." : "Document détaché du groupe.");
    } else {
      toast.error("Erreur d'association : " + error.message);
    }
  };

  const handleToggleDocumentGroups = async (docOrId, selectedGroupIds, docMeta) => {
    // docOrId peut être : un bigint ID (table documents) OU un objet doc (cas _fromMsrOnly)
    const isDocObj = typeof docOrId === 'object' && docOrId !== null;
    const doc = isDocObj ? docOrId : null;
    let docId = isDocObj ? null : docOrId;

    const primary = selectedGroupIds[0] || null;

    // Cas MSR-only : pas d'entrée dans documents → la créer d'abord
    if (isDocObj && doc._fromMsrOnly) {
      const { data: created, error: createErr } = await supabase.from('documents').insert([{
        nom: doc.nom,
        type_action: 'Modèle Référence',
        url: doc.url,
        user_id: null,
        group_id: primary,
        group_ids: selectedGroupIds,
        ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
      }]).select('id').single();
      if (createErr) {
        toast.error("Erreur d'association : " + createErr.message);
        return;
      }
      if (fetchDocuments) fetchDocuments();
      toast.success(selectedGroupIds.length > 0 ? `Document associé à ${selectedGroupIds.length} groupe(s).` : "Document détaché des groupes.");
      return;
    }

    if (!docId) return;
    const { error } = await supabase.from('documents').update({
      group_id: primary,
      group_ids: selectedGroupIds
    }).eq('id', docId);
    if (!error) {
      if (fetchDocuments) fetchDocuments();
      toast.success(selectedGroupIds.length > 0 ? `Document associé à ${selectedGroupIds.length} groupe(s).` : "Document détaché des groupes.");
    } else {
      toast.error("Erreur d'association : " + error.message);
    }
  };

  // ── Questionnaires (lecture seule ici) ──────────────────────────────────
  // DÉPLACÉ (2026-10-01) : la création / modification / suppression des questionnaires et quiz et leur
  // rattachement aux groupes se font désormais dans l'onglet "Questionnaires & Quiz"
  // (QuestionnairesView). On garde ici uniquement la liste, utilisée pour afficher les questionnaires
  // contenus dans chaque groupe de documents.
  const [questionnaireTemplates, setQuestionnaireTemplates] = React.useState([]);
  const fetchQTemplates = React.useCallback(async () => {
    if (!currentOrgId) return;
    const { data, error } = await supabase
      .from('module_step_resources')
      .select('id, titre, metadata, document_group_id, module_id')
      .eq('type', 'questionnaire')
      .eq('organisation_id', currentOrgId)
      .order('titre', { ascending: true });
    if (error) { console.error('[fetchQTemplates] error:', error); return; }
    setQuestionnaireTemplates((data || []).filter(r => r.module_id === null || r.module_id === undefined));
  }, [currentOrgId]);
  React.useEffect(() => {
    if (isAdmin) fetchQTemplates();
  }, [isAdmin, fetchQTemplates]);

  // Group clients by their documents
  const clientsWithDocs = React.useMemo(() => {
    let targetClients = clients;
    if (isFormateur) {
      targetClients = clients.filter(c => c.formateur_id === currentUserId);
    }

    return targetClients
      .map(client => ({
        ...client,
        docs: documents.filter(d => d.user_id === client.id)
      }))
      .sort((a, b) => a.nom.localeCompare(b.nom));
  }, [clients, documents, isFormateur, currentUserId]);

  const displayedDocs = isAdmin ? documents :
    isClient ? documents.filter(d => d.user_id === currentUserId && d.visible_client) :
      isFormateur ? documents.filter(d => d.visible_formateur) : [];

  // Listes pour les onglets admin
  const issuedClientDocs = displayedDocs.filter(d => !!d.user_id && !d.assigned_formateur_id);
  const issuedFormateurDocs = displayedDocs.filter(d => !!d.assigned_formateur_id);
  const sharedTemplateDocs = displayedDocs.filter(d => !d.user_id && !d.assigned_formateur_id);
  // FIX (2026-09-07) : "Historique" unifié — union dédupliquée de issuedClientDocs et
  // issuedFormateurDocs (un même document ne peut être compté qu'une fois même s'il porte à la
  // fois user_id et assigned_formateur_id), triée du plus récent au plus ancien (id décroissant —
  // la table `documents` n'a pas de colonne created_at, l'id auto-incrémenté sert de proxy fiable).
  const issuedAllDocs = React.useMemo(() => {
    const byId = new Map();
    [...issuedClientDocs, ...issuedFormateurDocs].forEach(d => byId.set(d.id, d));
    return Array.from(byId.values()).sort((a, b) => {
      const an = Number(a.id), bn = Number(b.id);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return bn - an;
      return String(b.id).localeCompare(String(a.id));
    });
  }, [issuedClientDocs, issuedFormateurDocs]);
  // NOUVEAU (2026-09-07) : documents en attente de la signature de l'ADMINISTRATEUR (rôle "organisme")
  // — l'action de signer existait déjà (bouton "Signer pour l'organisme" sur chaque carte) mais n'était
  // visible qu'en parcourant les 3 onglets un par un. On l'isole ici dans son propre onglet.
  const organismeToSignDocs = isAdmin
    ? documents.filter(d => (parseDocMetadata(d).destination_roles || []).includes('organisme') && !d.signe_par_organisme && !isBlockedBySigningOrder(d, 'organisme'))
    : [];
  const currentAudienceDocs =
    docAudienceTab === 'historique' ? issuedAllDocs :
    docAudienceTab === 'a_signer' ? organismeToSignDocs :
    sharedTemplateDocs;
  // FIX (2026-09-07) : pagination — 15 documents par page, quel que soit l'onglet actif.
  const totalDocsPages = Math.max(1, Math.ceil(currentAudienceDocs.length / DOCS_PER_PAGE));
  const safeDocsPage = Math.min(docsPage, totalDocsPages);
  const pagedAudienceDocs = currentAudienceDocs.slice((safeDocsPage - 1) * DOCS_PER_PAGE, safeDocsPage * DOCS_PER_PAGE);


  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">

      {/* ── En-tête ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            {isClient ? "Mes Documents" : "Gestion des documents"}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {isClient
              ? "Consultez et signez vos documents de formation."
              : "Bibliothèque de modèles · Documents émis · Suivi des signatures."}
          </p>
        </div>
        {isAdmin && (
          <div className="shrink-0 hidden md:flex items-center gap-1.5 text-xs text-gray-400 bg-gray-50 border border-gray-100 px-3 py-2 rounded-xl">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            {issuedClientDocs.filter(d => d.signe_par_client && d.signe_par_formateur).length} / {issuedClientDocs.length + issuedFormateurDocs.length} docs complets
          </div>
        )}
      </div>


      {/* NOUVEAU (2026-09-07) : bannière "Mes documents à signer" déplacée tout en haut de la
          page (au-dessus de "Gestion des documents"), à la demande explicite de l'utilisateur —
          l'ancien bouton "À signer par moi", noyé dans la barre d'onglets de "Documents émis" plus
          bas sur la page, passait inaperçu. C'est désormais le seul point d'entrée vers le mode
          dédié docAudienceTab === 'a_signer' (la barre d'onglets normale, plus bas, devient dans ce
          mode un en-tête dédié avec bouton "Retour" — voir plus bas dans ce fichier). */}
      {isAdmin && (
        <button
          onClick={() => { setDocAudienceTab('a_signer'); setDocsPage(1); }}
          className={`w-full flex items-center justify-between gap-4 px-6 py-4 rounded-3xl border-2 transition-all text-left ${
            organismeToSignDocs.length > 0
              ? 'bg-red-50 border-red-200 hover:border-red-400 shadow-sm'
              : 'bg-gray-50 border-gray-100 hover:border-gray-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${organismeToSignDocs.length > 0 ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              <PenTool size={20} />
            </span>
            <div>
              <p className="font-black text-gray-900 text-sm">Mes documents à signer</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {organismeToSignDocs.length > 0
                  ? `${organismeToSignDocs.length} document${organismeToSignDocs.length > 1 ? 's' : ''} en attente de votre signature (administrateur)`
                  : 'Vous êtes à jour — aucun document en attente de votre signature'}
              </p>
            </div>
          </div>
          {organismeToSignDocs.length > 0 && (
            <span className="shrink-0 min-w-[28px] h-7 px-2 bg-red-600 text-white text-xs font-black rounded-full flex items-center justify-center shadow-md">
              {organismeToSignDocs.length}
            </span>
          )}
        </button>
      )}

      {isAdmin && docAudienceTab !== 'a_signer' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-2 h-6 bg-amber-500 rounded-full mr-3"></span> Modèles de documents
          </h2>

          <div className="mb-8 p-5 bg-indigo-50 rounded-2xl border border-indigo-100">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Layout size={18}/> Groupes de documents</h3>
            <div className="flex items-center gap-3 mb-4">
              <input
                type="text"
                placeholder="Nouveau groupe (ex: Pack de début)"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="text-sm p-3 bg-white border border-indigo-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 w-full max-w-xs"
              />
              <button
                onClick={handleCreateGroup}
                disabled={isCreatingGroup || !newGroupName.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-all shadow-md disabled:opacity-50"
              >
                Créer
              </button>
            </div>
            {documentGroups.length > 0 && (
              <div className="flex flex-col gap-3">
                {documentGroups.map(g => {
                  const groupDocs = documents.filter(d => d.group_id === g.id || (d.group_ids && d.group_ids.includes(g.id)));
                  const groupQuests = questionnaireTemplates.filter(q => {
                    const qm = (() => { try { return typeof q.metadata === 'string' ? JSON.parse(q.metadata) : (q.metadata || {}); } catch { return {}; } })();
                    const gids = qm.group_ids && qm.group_ids.length > 0 ? qm.group_ids : (q.document_group_id ? [q.document_group_id] : []);
                    return gids.includes(g.id);
                  });
                  const totalItems = groupDocs.length + groupQuests.length;
                  return (
                    <div key={g.id} className="bg-white rounded-xl border border-indigo-100 shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-indigo-50 transition-colors" onClick={() => setExpandedGroupId(expandedGroupId === g.id ? null : g.id)}>
                        <div className="font-bold text-indigo-800 flex items-center gap-2">
                          {expandedGroupId === g.id ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                          {g.nom}
                          <span className="text-xs font-normal text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded-full">{groupDocs.length} doc(s)</span>
                          {groupQuests.length > 0 && <span className="text-xs font-normal text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{groupQuests.length} questionnaire(s)</span>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.id); }} className="text-gray-400 hover:text-red-500 transition-colors p-1">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {expandedGroupId === g.id && (
                        <div className="p-3 bg-indigo-50/50 border-t border-indigo-100 space-y-2">
                          {totalItems === 0 && <p className="text-xs text-gray-500 italic">Aucun élément dans ce groupe.</p>}
                          {groupDocs.length > 0 && (
                            <ul className="space-y-1.5">
                              {groupDocs.map(d => (
                                <li key={d.id} className="text-sm text-gray-700 flex items-center gap-2">
                                  <FileText size={14} className="text-indigo-400" />
                                  {d.nom}
                                  <span className="text-xs text-gray-400 font-bold uppercase">({d.extension || 'docx'})</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {groupQuests.length > 0 && (
                            <ul className="space-y-1.5">
                              {groupQuests.map(q => (
                                <li key={q.id} className="text-sm text-gray-700 flex items-center gap-2">
                                  <span className="text-amber-500">📝</span>
                                  {q.titre}
                                  <span className="text-xs text-amber-600 font-bold uppercase bg-amber-50 px-1.5 py-0.5 rounded">Questionnaire</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Boutons d'ajout de modèles ── */}
          <div className="mb-8 flex flex-col gap-3">
            {/* Avec balises → VisualTemplateEditor */}
            <button
              onClick={() => setIsTemplateEditorOpen(true)}
              className="w-full flex items-center justify-center gap-3 p-5 border-2 border-dashed border-violet-200 rounded-2xl text-violet-600 hover:border-violet-400 hover:bg-violet-50 transition-all group"
            >
              <div className="w-9 h-9 bg-violet-100 group-hover:bg-violet-200 rounded-xl flex items-center justify-center transition-all">
                <Plus size={18} className="text-violet-600" />
              </div>
              <div className="text-left">
                <p className="font-bold text-sm">Ajouter un modèle avec balises</p>
                <p className="text-xs text-violet-400 mt-0.5">Importez un .docx ou .pdf · positionnez vos balises visuellement</p>
              </div>
            </button>
            {/* Sans balises → upload direct */}
            <button
              onClick={() => simpleUploadRef.current?.click()}
              disabled={isUploadingSimple}
              className="w-full flex items-center justify-center gap-3 p-5 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:border-gray-400 hover:bg-gray-50 transition-all group disabled:opacity-50"
            >
              <div className="w-9 h-9 bg-gray-100 group-hover:bg-gray-200 rounded-xl flex items-center justify-center transition-all">
                {isUploadingSimple
                  ? <div className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-500 rounded-full animate-spin" />
                  : <Upload size={18} className="text-gray-500" />
                }
              </div>
              <div className="text-left">
                <p className="font-bold text-sm">{isUploadingSimple ? 'Envoi en cours…' : 'Ajouter un document sans balises'}</p>
                <p className="text-xs text-gray-400 mt-0.5">PDF ou .docx envoyé tel quel pour signature — sans remplissage automatique</p>
              </div>
            </button>
            <input
              ref={simpleUploadRef}
              type="file"
              accept=".pdf,.docx,.doc"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadSimpleTemplate(f); }}
            />
          </div>

          <VisualTemplateEditor
            isOpen={isTemplateEditorOpen}
            onClose={() => { setIsTemplateEditorOpen(false); setEditingTemplate(null); }}
            onSave={handleUploadVisualTemplate}
            initialData={editingTemplate}
          />

          {/* DÉPLACÉ (2026-10-01) : les questionnaires & quiz ont désormais leur propre onglet. */}
          {onOpenQuestionnaires && (
            <button type="button" onClick={onOpenQuestionnaires}
              className="w-full mb-8 p-4 bg-violet-50 rounded-2xl border border-violet-100 flex items-center justify-between text-left hover:bg-violet-100 transition-all">
              <span>
                <span className="block font-bold text-gray-900 text-sm">📝 Questionnaires & Quiz</span>
                <span className="block text-xs text-gray-500 mt-0.5">Ils se créent et se gèrent désormais dans leur propre onglet, avec leurs statistiques.</span>
              </span>
              <span className="text-xs font-black text-violet-700">Ouvrir →</span>
            </button>
          )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayTemplates.map((doc) => {
              const classif = typeof doc.metadata === 'object' && doc.metadata ? doc.metadata.classification : 'telechargeable';
              // Lire la destination réelle depuis documentTemplates (MSR) en priorité
              const tplInfo = (documentTemplates || {})[doc.nom];
              const dest = tplInfo?.destination || (doc.visible_formateur ? 'formateur' : 'client');
              const destRolesForBadge = parseDestinationRoles(dest);
              const destBadgeLabel = destRolesForBadge.map(r => r === 'client' ? '📁 Client' : r === 'formateur' ? '📋 Formateur' : '🏢 Administrateur').join(' + ');
              return (
                <div key={doc.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-amber-500 transition-all group relative flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-xs font-bold uppercase tracking-tighter px-2 py-0.5 rounded-full ${
                      destRolesForBadge.length > 1 ? 'bg-teal-100 text-teal-700' : destRolesForBadge[0] === 'formateur' ? 'bg-violet-100 text-violet-700' : destRolesForBadge[0] === 'organisme' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-600'
                    }`}>
                      {destBadgeLabel}
                    </span>
                    <span className={`text-xs font-bold uppercase tracking-tighter px-2 py-0.5 rounded-full ${
                      classif === 'a_generer' ? 'bg-purple-100 text-purple-600' :
                      classif === 'a_signer' ? 'bg-orange-100 text-orange-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {classif === 'a_generer' ? '⚙️ À générer' : classif === 'a_signer' ? '✍️ À signer' : '📥 Téléchargeable'}
                    </span>
                    <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full uppercase">
                      .{doc.extension || 'docx'}
                    </span>
                    <div className="flex items-center gap-2 ml-auto">
                      <label className="text-xs text-gray-400 hover:text-amber-600 cursor-pointer font-bold transition-colors">
                        Mettre à jour
                        <input type="file" className="hidden" accept=".docx, .pdf" onChange={(e) => handleUploadDocxTemplate(e.target.files[0], doc.nom, dest)} />
                      </label>
                      {/* AJOUT (2026-10-02) : "Voir" sur TOUS les modèles (avec ou sans balises), toujours visible —
                          demandé pour vérifier qu'on a bien déposé le bon fichier. Réutilise l'aperçu existant
                          (PDF affiché tel quel, Word converti en PDF), sans la couche "balises" si le modèle n'en a pas. */}
                      {(() => {
                        const tplForView = (documentTemplates || {})[doc.nom];
                        const viewUrl = resolveFileUrl(tplForView?.url || doc.url);
                        if (!viewUrl) return null;
                        const withFields = !!tplForView?.metadata?.has_visual_fields;
                        return (
                          <button
                            type="button"
                            onClick={() => openTemplatePreview(doc.nom, { ...(tplForView || {}), id: withFields ? tplForView?.id : null, url: viewUrl, plainView: !withFields })}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold text-violet-600 bg-violet-50 hover:bg-violet-100 transition-colors"
                            title="Voir le document"
                          >
                            <Eye size={12} /> Voir
                          </button>
                        );
                      })()}
                      {(documentTemplates || {})[doc.nom]?.metadata?.has_visual_fields && (
                        <>
                          <button
                            onClick={() => openTemplatePreview(doc.nom, (documentTemplates || {})[doc.nom])}
                            className="p-1.5 text-gray-300 hover:text-violet-600 transition-colors opacity-0 group-hover:opacity-100"
                            title="Prévisualiser les balises"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={async () => {
                              const tpl = (documentTemplates || {})[doc.nom];
                              if (!tpl?.id && !tpl?.url) return;
                              // Charger les champs existants depuis la DB
                              const { data: existingFields } = await supabase
                                .from('template_fields').select('*').eq('template_id', tpl.id).order('page');
                              // FIX (2026-09-04) : width_percent/height_percent (taille des cases "texte libre"
                              // redimensionnées) ne sont PAS stockées dans la table SQL template_fields — seulement
                              // dans la copie embarquée tpl.metadata.template_fields. On les retrouve ici par
                              // correspondance balise + page + position (x/y), les id étant régénérés à chaque
                              // ouverture de l'éditeur et donc non réutilisables comme clé de correspondance.
                              const embeddedFieldsMeta = tpl?.metadata?.template_fields || [];
                              const mappedFields = (existingFields || []).map(f => {
                                const xP = parseFloat(f.x_percent), yP = parseFloat(f.y_percent);
                                const sizeMatch = embeddedFieldsMeta.find(e =>
                                  e.tag === f.tag && (e.page || 1) === (f.page || 1) &&
                                  Math.abs(parseFloat(e.x_percent) - xP) < 0.05 && Math.abs(parseFloat(e.y_percent) - yP) < 0.05
                                );
                                return {
                                  id: `f_${f.id || Date.now()}_${Math.random().toString(36).slice(2)}`,
                                  tag: f.tag, page: f.page || 1,
                                  xPct: xP, yPct: yP,
                                  ...(sizeMatch && typeof sizeMatch.width_percent === 'number' ? { width_percent: sizeMatch.width_percent } : {}),
                                  ...(sizeMatch && typeof sizeMatch.height_percent === 'number' ? { height_percent: sizeMatch.height_percent } : {}),
                                };
                              });
                              setEditingTemplate({
                                url: tpl.url, fields: mappedFields, name: doc.nom, destination: tpl.destination || 'client', templateId: tpl.id,
                                signingMode: tpl?.metadata?.signing_mode || 'simultane',
                                signingOrder: tpl?.metadata?.signing_order || 'client_first',
                              });
                              setIsTemplateEditorOpen(true);
                            }}
                            className="p-1.5 text-gray-300 hover:text-amber-500 transition-colors opacity-0 group-hover:opacity-100"
                            title="Modifier les balises"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => {
                          setTargetToDelete({ id: doc.nom, type: 'template' });
                          setIsDeleteModalOpen(true);
                        }}
                        className="p-1.5 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  {editingName === doc.nom ? (
                    <input
                      autoFocus
                      className="font-bold text-gray-900 text-sm mb-1 border-b border-violet-400 outline-none w-full bg-transparent"
                      value={editingNameValue}
                      onChange={e => setEditingNameValue(e.target.value)}
                      onBlur={() => handleRenameSubmit(doc.nom)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRenameSubmit(doc.nom); if (e.key === 'Escape') setEditingName(null); }}
                    />
                  ) : (
                    <h3
                      className="font-bold text-gray-900 text-sm mb-1 cursor-pointer hover:text-violet-600 transition-colors"
                      title="Double-cliquer pour renommer"
                      onDoubleClick={() => { setEditingName(doc.nom); setEditingNameValue(doc.nom); }}
                    >
                      {doc.nom}
                    </h3>
                  )}
                  <div className="mt-auto pt-4 space-y-2">
                    {/* Modifier la destination inline */}
                    <select
                      className="w-full text-xs p-1.5 rounded-lg border border-gray-100 bg-gray-50 text-gray-500 font-medium cursor-pointer hover:border-amber-300 transition-colors"
                      value={dest}
                      onChange={(e) => onUpdateTemplateDestination && onUpdateTemplateDestination(doc.nom, e.target.value)}
                    >
                      <option value="client">📁 Client</option>
                      <option value="formateur">📋 Formateur</option>
                      <option value="both">👥 Les deux</option>
                    </select>
                    {/* Associer à un ou plusieurs groupes */}
                    {(() => {
                      const docGroupIds = (doc.group_ids && doc.group_ids.length > 0) ? doc.group_ids : (doc.group_id ? [doc.group_id] : []);
                      return (
                        <div className="border border-indigo-100 rounded-lg bg-indigo-50 p-2">
                          <p className="text-[11px] font-black uppercase text-indigo-600 mb-1.5 tracking-wider">Groupes</p>
                          <div className="space-y-1 max-h-24 overflow-y-auto">
                            {documentGroups.map(g => {
                              const isChecked = docGroupIds.includes(g.id);
                              return (
                                <label key={g.id} className="flex items-center gap-1.5 cursor-pointer group">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      // Filtrer les IDs de groupes périmés (groupe supprimé mais encore dans doc.group_ids)
                                      const validGroupIds = documentGroups.map(g => String(g.id));
                                      const cleanDocGroupIds = docGroupIds.filter(id => validGroupIds.includes(String(id)));
                                      const next = isChecked
                                        ? cleanDocGroupIds.filter(id => id !== g.id)
                                        : [...cleanDocGroupIds, g.id];
                                      // Passer l'objet doc si MSR-only (pas d'id bigint documents)
                                      handleToggleDocumentGroups(doc._fromMsrOnly ? doc : doc.id, next);
                                    }}
                                    className="w-3 h-3 accent-indigo-600 rounded"
                                  />
                                  <span className="text-xs text-indigo-800 font-medium group-hover:text-indigo-600 truncate">{g.nom}</span>
                                </label>
                              );
                            })}
                            {documentGroups.length === 0 && <p className="text-xs text-gray-400 italic">Aucun groupe créé.</p>}
                          </div>
                          {docGroupIds.length > 0 && (
                            <button
                              onClick={() => handleToggleDocumentGroups(doc._fromMsrOnly ? doc : doc.id, [])}
                              className="mt-1.5 text-[11px] text-violet-600 hover:text-violet-700 font-bold"
                            >
                              ✕ Détacher tous
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
            {displayTemplates.length === 0 && (
              <div className="col-span-full py-4 text-center text-gray-400 italic text-sm">Aucun modèle enregistré.</div>
            )}
          </div>
        </div>
      )}

      {/* Formulaire Administrateur d'ajout de document — conservé pour la logique handleAddDocument */}
      {false && (
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100 relative overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -z-10"></div>
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-2 h-6 bg-blue-500 rounded-full mr-3"></span> Mettre en ligne un Document
          </h2>
          <form onSubmit={handleAddDocument} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du Document</label>
                <input required type="text" value={newDocName} onChange={(e) => setNewDocName(e.target.value)} placeholder="Ex: Contrat de prestation" className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-blue-500 w-full p-3 outline-none transition-all" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type Document</label>
                <select required value={newDocType} onChange={(e) => setNewDocType(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-blue-500 w-full p-3 outline-none transition-all">
                  <option value="Autre">Autre</option>
                  <option value="Contrat">Contrat</option>
                  <option value="Émargement">Émargement</option>
                  <option value="Évaluation">Évaluation</option>
                  <option value="Présence">Présence (Séance)</option>
                </select>
              </div>
              <div className="flex-1 col-span-1 md:col-span-3 lg:col-span-1 flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">PDF / Image</label>
                  <input type="file" onChange={(e) => setNewDocFile(e.target.files[0] || null)} className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-blue-500 w-full p-2 outline-none transition-all" accept=".pdf,image/*" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ou URL</label>
                  <input type="url" value={newDocUrl} onChange={(e) => setNewDocUrl(e.target.value)} placeholder="https://..." className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-blue-500 w-full p-3 outline-none transition-all" />
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 items-end">
              <div className="w-full lg:flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Rattaché</label>
                <select required value={newDocClientId} onChange={(e) => setNewDocClientId(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-blue-500 w-full p-3 outline-none">
                  <option value="">Sélectionner le bénéficiaire</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.nom} ({c.email})</option>)}
                </select>
              </div>

              <div className="flex gap-4 items-center bg-gray-50 p-3 rounded-xl border border-gray-200">
                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={newDocVisClient} onChange={(e) => setNewDocVisClient(e.target.checked)} className="w-4 h-4 rounded text-blue-600" />
                  <span className="font-medium whitespace-nowrap">Visible Client</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={newDocVisFormateur} onChange={(e) => setNewDocVisFormateur(e.target.checked)} className="w-4 h-4 rounded text-blue-600" />
                  <span className="font-medium whitespace-nowrap">Visible Formateur</span>
                </label>
              </div>

              <button type="submit" disabled={isAddingDoc} className="w-full lg:w-max bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl px-6 py-3 shrink-0 disabled:opacity-50 transition-colors shadow-sm">
                {isAddingDoc ? "Ajout..." : "Créer le document"}
              </button>
            </div>
          </form>
        </div>
      )}


      {/* ── Documents émis ─────────────────────────────────────────────── */}
      {isAdmin && (
        <div className="mt-2 mb-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1 h-5 bg-gray-900 rounded-full"></span>
            <h2 className="text-base font-black text-gray-800 uppercase tracking-widest">Documents émis</h2>
          </div>
          <p className="text-xs text-gray-400 ml-3">Documents générés et envoyés aux bénéficiaires (clients &amp; formateurs).</p>
        </div>
      )}

      <div className="mt-4 space-y-6">
        {isFormateur ? (
          clientsWithDocs.map(client => {
            const isExpanded = expandedId === client.id;
            const clientDocs = client.docs;

            return (
              <div key={client.id} className={`bg-white rounded-3xl p-6 shadow-sm border ${isExpanded ? 'border-indigo-200' : 'border-gray-100'} transition-all`}>
                <div
                  className="flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : client.id)}
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center font-bold text-xl mr-4">{client.nom ? client.nom.charAt(0) : '?'}</div>
                    <div className="flex flex-col">
                      <h3 className="font-bold text-gray-900 text-lg">{client.nom}</h3>
                      <p className="text-xs text-gray-500 font-medium">{clientDocs.length} document(s)</p>
                    </div>
                  </div>
                  <button className={`px-5 py-2 rounded-xl text-xs font-bold transition-all ${isExpanded ? 'bg-gray-100 text-gray-600' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white'}`}>
                    {isExpanded ? "Fermer le dossier" : "Ouvrir le dossier"}
                  </button>
                </div>

                {isExpanded && (
                  <div className="mt-6 pt-6 border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-100 text-xs text-gray-400 uppercase tracking-widest font-bold">
                          <th className="pb-3 px-2">Document</th>
                          <th className="pb-3 px-2">Statut Signature</th>
                          <th className="pb-3 px-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientDocs.map(doc => (
                          <tr key={doc.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors group">
                            <td className="py-4 px-2">
                              <div className="flex items-center">
                                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mr-3 text-gray-500 transition-colors group-hover:bg-indigo-100 group-hover:text-indigo-600"><FileText /></div>
                                <div className="flex flex-col">
                                  <span className="font-bold text-gray-900 text-sm">{doc.nom}</span>
                                  {doc.type_document === 'Présence' && doc.date_seance && (
                                    <span className="text-xs text-gray-400 font-medium">Séance du : {new Date(doc.date_seance).toLocaleDateString('fr-FR')}</span>
                                  )}
                                  {doc.url && <button onClick={() => setViewingDocId(doc.id)} className="text-xs text-blue-600 font-bold hover:underline mt-0.5 text-left">Voir PDF ↗</button>}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-bold border ${doc.signe_par_client ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>Bénéficiaire</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-bold border ${doc.signe_par_formateur ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>Formateur</span>
                              </div>
                            </td>
                            <td className="py-4 px-2 text-right">
                              <div className="flex justify-end gap-2">
                                {!doc.signe_par_formateur && (
                                  <button onClick={() => setSigningDocId(doc.id)} className="px-3 py-1.5 bg-indigo-600 text-white font-bold rounded-lg text-xs hover:bg-indigo-700 shadow-sm shadow-indigo-100">Signer</button>
                                )}
                                <button onClick={() => handleDownloadPDF(doc)} className="p-1.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors" title="Télécharger">
                                  <DownloadIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {clientDocs.length === 0 && (
                          <tr><td colSpan="3" className="py-8 text-center text-gray-400 italic text-sm">Aucun document pour ce bénéficiaire.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        ) : isClient ? (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100 mb-8 max-w-5xl mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="w-2 h-6 bg-violet-600 rounded-full mr-3"></span> Mes Documents et Supports
            </h2>
            <div className="flex flex-wrap gap-4 border-b border-gray-200 mb-6">
              <button onClick={() => setClientDocTab('avant')} className={`px-4 py-3 font-bold text-sm transition-colors ${clientDocTab === 'avant' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-gray-500 hover:text-gray-800 border-b-2 border-transparent'}`}>À signer avant début</button>
              <button onClick={() => setClientDocTab('supports')} className={`px-4 py-3 font-bold text-sm transition-colors ${clientDocTab === 'supports' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-gray-500 hover:text-gray-800 border-b-2 border-transparent'}`}>Supports de formation</button>
              <button onClick={() => setClientDocTab('fin')} className={`px-4 py-3 font-bold text-sm transition-colors ${clientDocTab === 'fin' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-gray-500 hover:text-gray-800 border-b-2 border-transparent'}`}>Documents de fin</button>
            </div>

            <div className="space-y-4">
              {(() => {
                const clientDocs = displayedDocs;
                const clientSessions = (sessions || []).filter(s => s.client_id === currentUserId).sort((a, b) => new Date(a.date) - new Date(b.date));
                const currentDate = new Date();
                currentDate.setHours(0, 0, 0, 0);

                const renderDocRow = (doc) => (
                  <div key={doc.id} className="p-4 border border-gray-100 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 group hover:border-violet-200 bg-gray-50/30">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-50 text-violet-700 flex items-center justify-center rounded-xl shrink-0"><FileText size={20} /></div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{doc.nom}</p>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">{doc.type_document || doc.type || 'Document'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {(!doc.signe_par_client && (doc.type_document === 'À signer' || (!doc.metadata && (doc.type_document === 'Contrat' || String(doc.nom).toLowerCase().includes('contrat') || String(doc.nom).toLowerCase().includes('convention'))))) && (
                        <button onClick={() => setSigningDocId(doc.id)} className="px-4 py-2 bg-violet-600 text-white font-bold rounded-lg text-xs shadow-sm hover:bg-violet-700 transition-colors">Signer Document</button>
                      )}
                      {doc.signe_par_client && <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">✓ Votré signature Validée</span>}
                      <button onClick={() => handleDownloadPDF(doc)} className="p-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" title="Télécharger">
                        <DownloadIcon />
                      </button>
                    </div>
                  </div>
                );

                if (clientDocTab === 'avant') {
                  const items = clientDocs.filter(d => {
                    const moment = typeof d.metadata === 'object' && d.metadata !== null ? d.metadata.moment : null;
                    if (moment) return moment === 'debut';
                    return d.type_document === 'Contrat' || String(d.nom).toLowerCase().includes('contrat') || String(d.nom).toLowerCase().includes('convention') || String(d.nom).toLowerCase().includes('devis');
                  });
                  return items.length > 0 ? items.map(renderDocRow) : <p className="text-sm text-gray-500 italic py-4">Aucun document administratif en attente de validation.</p>;
                } else if (clientDocTab === 'fin') {
                  const items = clientDocs.filter(d => {
                    const moment = typeof d.metadata === 'object' && d.metadata !== null ? d.metadata.moment : null;
                    if (moment) return moment === 'fin';
                    return d.type_document === 'Évaluation' || String(d.nom).toLowerCase().includes('attestation') || String(d.nom).toLowerCase().includes('bilan');
                  });
                  return items.length > 0 ? items.map(renderDocRow) : <p className="text-sm text-gray-500 italic py-4">Les documents de fin de parcours apparaîtront ici.</p>;
                } else if (clientDocTab === 'supports') {
                  const supportDocs = clientDocs.filter(d => {
                    const moment = typeof d.metadata === 'object' && d.metadata !== null ? d.metadata.moment : null;
                    if (moment) return false;
                    return !['Contrat', 'Évaluation'].includes(d.type_document) && !String(d.nom).toLowerCase().includes('contrat') && !String(d.nom).toLowerCase().includes('attestation') && !String(d.nom).toLowerCase().includes('convention');
                  });
                  const unlockedSessions = clientSessions.filter(s => s.ressource_url && new Date(s.date) <= currentDate);
                  const lockedSessions = clientSessions.filter(s => s.ressource_url && new Date(s.date) > currentDate);

                  return (
                    <div className="space-y-6">
                      {unlockedSessions.length > 0 && (
                        <div>
                          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Supports Débloqués (Séances Passées)</h3>
                          <div className="space-y-3">
                            {unlockedSessions.map(s => (
                              <div key={s.id} className="p-4 border border-violet-100 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 bg-violet-50/50">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-violet-100 text-violet-700 flex items-center justify-center rounded-xl shrink-0">📚</div>
                                  <div>
                                    <p className="font-bold text-gray-900 text-sm">{s.ressource_titre || `Support de la session ${s.titre}`}</p>
                                    <p className="text-xs text-violet-700 font-bold uppercase tracking-wider">{s.titre} du {new Date(s.date).toLocaleDateString()}</p>
                                  </div>
                                </div>
                                <button onClick={() => openSecureStorageFile(s.ressource_url)} className="px-4 py-2 bg-violet-700 text-white font-bold rounded-lg text-xs shadow-sm hover:bg-violet-700 transition-colors flex items-center gap-2">
                                  Accéder au contenu ↗
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {lockedSessions.length > 0 && (
                        <div>
                          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Accès Verrouillés (Sessions Futures)</h3>
                          <div className="space-y-3">
                            {lockedSessions.map(s => (
                              <div key={s.id} className="p-4 border border-gray-100 rounded-2xl flex items-center gap-4 bg-gray-50 opacity-60">
                                <div className="w-10 h-10 bg-gray-200 text-gray-500 flex items-center justify-center rounded-xl shrink-0">🔒</div>
                                <div>
                                  <p className="font-bold text-gray-700 text-sm">{s.ressource_titre || `Support de la session ${s.titre}`}</p>
                                  <p className="text-xs text-gray-500">Sera débloqué le {new Date(s.date).toLocaleDateString()}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {supportDocs.length > 0 && (
                        <div>
                          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 mt-6">Autres Documents Communs</h3>
                          <div className="space-y-3">
                            {supportDocs.map(renderDocRow)}
                          </div>
                        </div>
                      )}

                      {unlockedSessions.length === 0 && lockedSessions.length === 0 && supportDocs.length === 0 && (
                        <p className="text-sm text-gray-500 italic py-4">Aucun support pédagogique disponible pour le moment.</p>
                      )}
                    </div>
                  );
                }
              })()}
            </div>
          </div>

        ) : (
          <div className="space-y-5">
            {/* ── Sélecteur d'onglets (ou en-tête dédiée quand "Mes documents à signer" est actif) ── */}
            {docAudienceTab === 'a_signer' ? (
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => { setDocAudienceTab('historique'); setDocsPage(1); }}
                  className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-xl transition-all shrink-0"
                >
                  ← Retour
                </button>
                <div className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0"><PenTool size={16} /></span>
                  <div>
                    <h2 className="text-base font-black text-gray-900">Mes documents à signer</h2>
                    <p className="text-xs text-gray-400">{organismeToSignDocs.length} document{organismeToSignDocs.length > 1 ? 's' : ''} en attente de votre signature (administrateur)</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1 bg-gray-100 p-1.5 rounded-2xl w-fit">
                {[
                  { key: 'historique', label: 'Historique', count: issuedAllDocs.length, activeColor: 'text-indigo-600 bg-indigo-100' },
                  { key: 'commun', label: 'Modèles partagés', count: sharedTemplateDocs.length, activeColor: 'text-gray-600 bg-gray-200' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => { setDocAudienceTab(tab.key); setDocsPage(1); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                      docAudienceTab === tab.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-black ${
                      docAudienceTab === tab.key ? tab.activeColor : 'bg-gray-200 text-gray-500'
                    }`}>{tab.count}</span>
                  </button>
                ))}
              </div>
            )}

            {docAudienceTab === 'a_signer' && isAdmin && (
              <PanneauSignatureEnLot docs={organismeToSignDocs} role="organisme" tousDocuments={documents} nomsGroupes={nomsGroupesDocs}
                libelleClient={(id) => ((clients || []).find(c => String(c.id) === String(id)) || {}).nom || ''}
                handleSignDocument={handleSignDocument} fetchDocuments={fetchDocuments} />
            )}

            {/* ── Grille de cartes ───────────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pagedAudienceDocs.map(doc => {
                const clientAssocie = clients.find(c => c.id === doc.user_id);
                const formateurAssocie = (formateurs || []).find(f => f.id === doc.assigned_formateur_id);
                const beneficiaire = clientAssocie?.nom || formateurAssocie?.nom || null;
                const classif = typeof doc.metadata === 'object' && doc.metadata !== null ? doc.metadata.classification : null;
                const requiresOrganisme = (parseDocMetadata(doc).destination_roles || []).includes('organisme');
                const allSigned = doc.signe_par_client && doc.signe_par_formateur && (!requiresOrganisme || doc.signe_par_organisme);
                const needsClientSig = docAwaitsClientSignature(doc);
                const needsFormateurSig = docAwaitsFormateurSignature(doc);
                const needsOrganismeSig = requiresOrganisme && !doc.signe_par_organisme && docRequiresSignature(doc);
                const pendingCount = (needsClientSig ? 1 : 0) + (needsFormateurSig ? 1 : 0) + (needsOrganismeSig ? 1 : 0);

                return (
                  <div
                    key={doc.id}
                    className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col overflow-hidden group ${
                      allSigned ? 'border-emerald-100 hover:border-emerald-200' :
                      pendingCount > 0 ? 'border-amber-100 hover:border-amber-200' :
                      'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    {/* Bande couleur statut */}
                    <div className={`h-1 w-full ${allSigned ? 'bg-emerald-400' : pendingCount > 0 ? 'bg-amber-300' : 'bg-gray-200'}`}></div>

                    <div className="p-5 flex flex-col gap-3 flex-1">
                      {/* En-tête */}
                      <div className="flex items-start gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                          allSigned ? 'bg-emerald-50 text-emerald-500' : 'bg-gray-50 text-gray-400'
                        }`}>
                          <FileText size={19} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">{doc.nom}</p>
                          {beneficiaire ? (
                            <p className="text-xs text-gray-500 mt-0.5 font-medium truncate">{beneficiaire}</p>
                          ) : (
                            <p className="text-xs text-gray-300 mt-0.5 italic text-[11px]">Pas de bénéficiaire</p>
                          )}
                        </div>
                        {allSigned && (
                          <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                        )}
                      </div>

                      {/* Badges signature */}
                      <div className="flex flex-wrap gap-1.5">
                        {doc.visible_client === true && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${
                            doc.signe_par_client
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              : 'bg-amber-50 text-amber-700 border-amber-100'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${doc.signe_par_client ? 'bg-emerald-500' : 'bg-amber-400'}`}></span>
                            Client {doc.signe_par_client ? '✓' : '–'}
                          </span>
                        )}
                        {doc.visible_formateur === true && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${
                            doc.signe_par_formateur
                              ? 'bg-blue-50 text-blue-700 border-blue-100'
                              : 'bg-amber-50 text-amber-700 border-amber-100'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${doc.signe_par_formateur ? 'bg-blue-500' : 'bg-amber-400'}`}></span>
                            Formateur {doc.signe_par_formateur ? '✓' : '–'}
                          </span>
                        )}
                        {requiresOrganisme && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${
                            doc.signe_par_organisme
                              ? 'bg-teal-50 text-teal-700 border-teal-100'
                              : 'bg-amber-50 text-amber-700 border-amber-100'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${doc.signe_par_organisme ? 'bg-teal-500' : 'bg-amber-400'}`}></span>
                            Administrateur {doc.signe_par_organisme ? '✓' : '–'}
                          </span>
                        )}
                        {classif === 'a_signer' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-black uppercase tracking-wide bg-purple-50 text-purple-700 border border-purple-100">
                            ✍️ À signer
                          </span>
                        )}
                        {classif === 'a_generer' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-black uppercase tracking-wide bg-sky-50 text-sky-700 border border-sky-100">
                            ⚙️ Généré
                          </span>
                        )}
                      </div>

                      {/* Date séance */}
                      {doc.type_document === 'Présence' && (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                          <span>📅</span>
                          <span className="font-medium">
                            {doc.date_seance
                              ? new Date(doc.date_seance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                              : 'Date non définie'}
                          </span>
                        </div>
                      )}

                      {/* Extension */}
                      {doc.extension && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-black uppercase text-gray-300 tracking-widest bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">.{doc.extension}</span>
                        </div>
                      )}

                      {/* Actions */}
                      {needsOrganismeSig && !isBlockedBySigningOrder(doc, 'organisme') && (
                        <button
                          onClick={() => setSigningDocId(doc.id)}
                          className="w-full py-2 rounded-xl text-xs font-bold bg-teal-600 hover:bg-teal-700 text-white transition-colors text-center"
                        >
                          Signer pour l'organisme
                        </button>
                      )}
                      <div className="flex gap-2 mt-auto pt-3 border-t border-gray-50">
                        {doc.url && (
                          <button
                            onClick={() => setViewingDocId(doc.id)}
                            className="flex-1 py-2 rounded-xl text-xs font-bold bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-100 transition-colors text-center"
                          >
                            Voir ↗
                          </button>
                        )}
                        <button
                          onClick={() => handleDownloadPDF(doc)}
                          className="flex-1 py-2 rounded-xl text-xs font-bold bg-gray-900 hover:bg-gray-800 text-white transition-colors text-center"
                        >
                          Télécharger
                        </button>
                        <button
                          onClick={() => setIssuedDocToDelete(doc)}
                          className="p-2 rounded-xl text-gray-300 hover:text-red-500 hover:bg-red-50 border border-gray-100 hover:border-red-200 transition-all"
                          title="Supprimer ce document"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {currentAudienceDocs.length === 0 && (
                <div className="col-span-full py-20 flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 border border-dashed border-gray-200">
                    <FileText size={26} className="text-gray-200" />
                  </div>
                  <p className="text-gray-400 text-sm font-semibold">
                    {docAudienceTab === 'historique' ? 'Aucun document envoyé pour le moment.' :
                     docAudienceTab === 'a_signer' ? 'Aucun document en attente de votre signature.' :
                     'Aucun modèle partagé enregistré.'}
                  </p>
                  <p className="text-gray-300 text-xs mt-1">
                    {docAudienceTab === 'historique' ? 'Générez des documents depuis les fiches clients ou formateurs.' :
                     docAudienceTab === 'a_signer' ? 'Vous êtes à jour 🎉' :
                     'Uploadez des modèles depuis la bibliothèque.'}
                  </p>
                </div>
              )}
            </div>

            {/* FIX (2026-09-07) : pagination — 15 documents par page, sur l'onglet actif. */}
            {currentAudienceDocs.length > DOCS_PER_PAGE && (
              <div className="flex items-center justify-center gap-3 pt-1">
                <button
                  onClick={() => setDocsPage(p => Math.max(1, p - 1))}
                  disabled={safeDocsPage <= 1}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Précédent
                </button>
                <span className="text-xs font-bold text-gray-400">Page {safeDocsPage} / {totalDocsPages}</span>
                <button
                  onClick={() => setDocsPage(p => Math.min(totalDocsPages, p + 1))}
                  disabled={safeDocsPage >= totalDocsPages}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Suivant →
                </button>
              </div>
            )}

            <DeleteConfirmationModal
              isOpen={!!issuedDocToDelete}
              onClose={() => setIssuedDocToDelete(null)}
              onConfirm={handleDeleteIssuedDoc}
              itemName={issuedDocToDelete?.nom || 'ce document'}
              title="Supprimer ce document ?"
            />
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={hideDeleteConfirm}
      />

      {/* ── Modal prévisualisation balises template ── */}
      {previewTemplate && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-3xl shadow-2xl flex flex-col w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* En-tête */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <div>
                <h3 className="font-black text-gray-900 text-base">{previewTemplate.key}</h3>
                {previewTemplate.tpl?.plainView ? (
                  <p className="text-xs text-gray-400 mt-0.5">Aperçu du document</p>
                ) : (
                  <p className="text-xs text-gray-400 mt-0.5">{previewFields.length} balise{previewFields.length !== 1 ? 's' : ''} positionnée{previewFields.length !== 1 ? 's' : ''}</p>
                )}
                {previewTemplate.tpl?.url && (
                  <a href={previewTemplate.tpl.url} target="_blank" rel="noopener noreferrer" className="text-[11px] font-bold text-violet-600 hover:underline">
                    Ouvrir le fichier d'origine ↗
                  </a>
                )}
              </div>
              <button onClick={() => { setPreviewTemplate(null); setPreviewPages([]); setPreviewFields([]); }} className="p-2 rounded-xl hover:bg-gray-100 transition-all text-gray-400 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            {/* Contenu */}
            <div className="overflow-y-auto p-6 flex-1">
              {previewLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-400">Chargement du document…</p>
                </div>
              ) : previewPages.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-gray-400 text-sm">Aucune page à afficher.</p>
                  {previewTemplate.tpl?.url && <p className="text-gray-400 text-xs mt-1">Utilisez « Ouvrir le fichier d'origine » ci-dessus pour le consulter.</p>}
                </div>
              ) : (
                <div className="space-y-6">
                  {previewPages.map((pg, pi) => {
                    const pageFields = previewFields.filter(f => (f.page || 1) === pi + 1);
                    return (
                      <div key={pi}>
                        <p className="text-xs font-black uppercase text-gray-400 mb-2 tracking-widest">Page {pi + 1}</p>
                        <div className="relative inline-block w-full border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          <img src={pg.dataUrl} alt={`Page ${pi + 1}`} className="w-full block" />
                          {pageFields.map((f, fi) => {
                            const isSignature = f.field_type === 'signature' || (f.tag || '').startsWith('signature_');
                            const isCheckbox = f.field_type === 'checkbox' || (f.tag || '').startsWith('checkbox_');
                            // Signature/case = centrées sur le point ; balise texte = coin bas-gauche
                            // de la ligne de base (comportement réel de drawText/pdf-lib).
                            const transform = (isSignature || isCheckbox) ? 'translate(-50%, -50%)' : 'translate(0%, -100%)';
                            if (isCheckbox) {
                              return (
                                <div
                                  key={fi}
                                  style={{ position: 'absolute', left: `${f.x_percent}%`, top: `${f.y_percent}%`, transform, width: 16, height: 16 }}
                                  className={`rounded-sm shadow-lg border-2 pointer-events-none ${f.tag === 'checkbox_client' ? 'border-blue-500 bg-blue-500/20' : 'border-orange-500 bg-orange-500/20'}`}
                                />
                              );
                            }
                            return (
                              <div
                                key={fi}
                                style={{ position: 'absolute', left: `${f.x_percent}%`, top: `${f.y_percent}%`, transform }}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-black shadow-lg border pointer-events-none ${isSignature ? 'bg-blue-500 text-white border-blue-400' : 'bg-violet-600 text-white border-violet-500'}`}
                              >
                                {isSignature ? `✍️ ${f.tag}` : `{${f.tag}}`}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!previewLoading && !previewTemplate.tpl?.plainView && previewFields.length === 0 && previewPages.length > 0 && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-700">
                  <strong>Aucune balise enregistrée</strong> pour ce modèle. Ouvrez le modèle dans l'éditeur visuel pour en ajouter.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Cloche de notifications ──────────────────────────────────────────────────
const NotificationBell = ({ sessions, documents, clients, userRole, currentUserId, setActiveTab, depotsNonLus = [], piecesAFournir = 0, onOpenClient = null }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const today = jourLocalISO(new Date());
  const in7Days = jourLocalISO(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));

  const notifications = React.useMemo(() => {
    const notifs = [];
    // AJOUT (2026-10-13) : documents envoyés par les clients, pas encore ouverts.
    if ((userRole === 'admin' || userRole === 'formateur') && depotsNonLus.length > 0) {
      const parClient = new Map();
      depotsNonLus.forEach(d => parClient.set(String(d.client_id), [...(parClient.get(String(d.client_id)) || []), d]));
      parClient.forEach((liste, cid) => {
        const c = clients.find(x => String(x.id) === cid);
        notifs.push({ type: 'warning', message: liste.length > 1 ? `${liste.length} nouveaux documents de ${c?.nom || 'un client'}` : `Nouveau document de ${c?.nom || 'un client'} : ${liste[0].titre}`, action: 'clients', clientId: cid });
      });
    }
    if (userRole === 'client' && piecesAFournir > 0) {
      notifs.push({ type: 'warning', message: `${piecesAFournir} pièce${piecesAFournir > 1 ? 's' : ''} à fournir`, action: 'mes_documents' });
    }
    if (userRole === 'admin') {
      // FIX (2026-10-05) : ne compte plus les documents simplement transmis (sans signature) ni les signatures
      // non demandées (ex. document destiné au client seul : pas de signature formateur attendue).
      const pendingDocs = documents.filter(d => (d.user_id || d.assigned_formateur_id) && (docAwaitsClientSignature(d) || docAwaitsFormateurSignature(d)));
      if (pendingDocs.length > 0) notifs.push({ type: 'warning', message: `${pendingDocs.length} document${pendingDocs.length > 1 ? 's' : ''} en attente de signature`, action: 'gestion_documents' });
      // NOUVEAU (2026-09-07) : documents en attente de la signature de L'ADMINISTRATEUR lui-même
      // (rôle "organisme") — jusqu'ici absents de la cloche, alors que l'action existe bel et bien
      // (voir le nouvel onglet "À signer par moi" dans Gestion des documents).
      const pendingOrganismeDocs = documents.filter(d => (parseDocMetadata(d).destination_roles || []).includes('organisme') && !d.signe_par_organisme && !isBlockedBySigningOrder(d, 'organisme'));
      if (pendingOrganismeDocs.length > 0) notifs.push({ type: 'warning', message: `${pendingOrganismeDocs.length} document${pendingOrganismeDocs.length > 1 ? 's' : ''} à signer par vous`, action: 'gestion_documents' });
      const weekSessions = sessions.filter(s => s.date >= today && s.date <= in7Days);
      if (weekSessions.length > 0) notifs.push({ type: 'info', message: `${weekSessions.length} séance${weekSessions.length > 1 ? 's' : ''} cette semaine`, action: 'calendrier' });
    } else if (userRole === 'formateur') {
      const myClientIds = clients.filter(c => c.formateur_id === currentUserId).map(c => c.id);
      const pendingDocs = documents.filter(d => d.assigned_formateur_id === currentUserId && d.visible_formateur !== false && !d.signe_par_formateur && !isBlockedBySigningOrder(d, 'formateur') && !isDossierDoc(d));
      if (pendingDocs.length > 0) notifs.push({ type: 'warning', message: `${pendingDocs.length} document${pendingDocs.length > 1 ? 's' : ''} à signer`, action: 'clients' });
      // Exercices rendus par un client, pas encore corrigés — visibilité auparavant nulle en dehors
      // d'aller ouvrir chaque client une par une pour vérifier.
      const pendingCorrections = sessions.filter(s =>
        myClientIds.includes(s.client_id) &&
        (s.type_activite === 'exercice' || s.type_activite === 'Exercice') &&
        s.reponse_url && s.correction_statut !== 'Validé' && s.correction_statut !== 'À corriger'
      );
      if (pendingCorrections.length > 0) notifs.push({ type: 'warning', message: `${pendingCorrections.length} exercice${pendingCorrections.length > 1 ? 's' : ''} rendu${pendingCorrections.length > 1 ? 's' : ''} à corriger`, action: 'clients' });
      const todaySessions = sessions.filter(s => myClientIds.includes(s.client_id) && s.date === today);
      if (todaySessions.length > 0) notifs.push({ type: 'info', message: `${todaySessions.length} séance${todaySessions.length > 1 ? 's' : ''} aujourd'hui`, action: 'calendrier' });
    } else if (userRole === 'client') {
      // Même logique que isSignedByClient dans ClientDocumentsView :
      // un document est considéré signé si UN enregistrement avec le même nom a signe_par_client = true
      const _parcoursFini = isModuleCompletedForClient(currentUserId, sessions, moduleDuClient(clients, currentUserId)); // lot 3
      const myDocs = documents.filter(d => String(d.user_id) === String(currentUserId) && docAwaitsClientSignature(d) && !docDeFinVerrouille(d, _parcoursFini));
      const trulyPending = myDocs.filter(d => {
        const effectivelySigned = documents.some(other =>
          String(other.user_id) === String(currentUserId) && other.nom === d.nom && other.signe_par_client
        );
        return !effectivelySigned && !isBlockedBySigningOrder(d, 'client');
      });
      if (trulyPending.length > 0) notifs.push({ type: 'warning', message: `${trulyPending.length} document${trulyPending.length > 1 ? 's' : ''} à signer`, action: 'mes_documents' });
      const nextSess = sessions.filter(s => String(s.client_id) === String(currentUserId) && s.date >= today).sort((a, b) => (a.date || '').localeCompare(b.date || ''))[0];
      if (nextSess) notifs.push({ type: 'info', message: `Prochaine séance : ${new Date(nextSess.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}`, action: 'calendrier' });
    }
    return notifs;
  }, [sessions, documents, clients, userRole, currentUserId, today, in7Days, depotsNonLus, piecesAFournir]);

  const count = notifications.length;
  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors">
        <Bell size={20} className="text-gray-500" />
        {count > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-violet-600 text-white text-[11px] font-black rounded-full flex items-center justify-center leading-none">{count}</span>}
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-[min(20rem,calc(100vw-2rem))] bg-white rounded-2xl shadow-xl border border-gray-100 z-20 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100"><p className="font-bold text-gray-900 text-sm">Notifications</p></div>
            <div className="max-h-64 overflow-y-auto">
              {count === 0
                ? <div className="py-8 text-center"><p className="text-gray-400 text-sm">Aucune notification</p></div>
                : notifications.map((n, i) => (
                    <button key={i} onClick={() => { if (n.clientId && onOpenClient) onOpenClient(n.clientId); else setActiveTab(n.action); setIsOpen(false); }}
                      className="w-full px-4 py-3.5 text-left hover:bg-gray-50 border-b border-gray-50 transition-colors last:border-0 flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.type === 'warning' ? 'bg-amber-400' : 'bg-blue-400'}`}></div>
                      <p className="text-sm text-gray-700">{n.message}</p>
                    </button>
                  ))
              }
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Tableau de bord Admin ─────────────────────────────────────────────────────
const DashboardAdminView = ({ clients, sessions, documents, formateurs, setActiveTab, modules, orgSettings, currentOrgId }) => {
  const today = new Date();
  const todayStr = jourLocalISO(today);
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const weekStartStr = jourLocalISO(weekStart);
  const weekEndStr = jourLocalISO(weekEnd);

  const issuedDocs = documents.filter(d => d.user_id || d.assigned_formateur_id);
  const pendingDocs = issuedDocs.filter(d => docAwaitsClientSignature(d) || docAwaitsFormateurSignature(d)); // FIX (2026-10-05), voir docRequiresSignature
  // LOT B (2026-10-21) : un document signé par le client seul (sans autre signature attendue) compte aussi.
  const signedDocs = issuedDocs.filter(d => (d.signe_par_client || d.signe_par_formateur || d.signe_par_organisme)
    && !docAwaitsClientSignature(d) && !docAwaitsFormateurSignature(d));
  const weekSessions = sessions.filter(s => s.date >= weekStartStr && s.date <= weekEndStr);
  const todaySessions = sessions.filter(s => s.date === todayStr);
  // AFFICHAGE (2026-10-20) : on ne compte que les éléments qui attendent une signature du client.
  const sessionsASigner = sessions.filter(s => signaturesAttendues(s).client);
  const completedSessions = sessionsASigner.filter(s => s.statut === 'Signé' || s.statut_client === 'Signé').length;
  const completionRate = sessionsASigner.length > 0 ? Math.round((completedSessions / sessionsASigner.length) * 100) : 0;
  const upcomingGroups = regrouperParSeance(sessions.filter(s => s.date >= todayStr)).slice(0, 5);
  const weekGroups = regrouperParSeance(weekSessions);
  const todayGroups = regrouperParSeance(todaySessions);

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-black text-gray-900">Tableau de bord</h1>
        <p className="text-gray-400 text-sm mt-1">Vue d'ensemble de votre activité.</p>
      </div>

      <GuideDemarrage orgSettings={orgSettings} modules={modules} documents={documents} formateurs={formateurs} clients={clients} setActiveTab={setActiveTab} currentOrgId={currentOrgId} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Clients actifs</p>
          <p className="text-3xl font-black text-gray-900 mt-2">{clients.length}</p>
          <p className="text-xs text-gray-400 mt-1">{formateurs.length} formateur{formateurs.length > 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-amber-100 shadow-sm">
          <p className="text-xs font-black text-amber-500 uppercase tracking-widest">En attente</p>
          <p className="text-3xl font-black text-amber-500 mt-2">{pendingDocs.length}</p>
          <p className="text-xs text-gray-400 mt-1">{signedDocs.length} signés</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-blue-100 shadow-sm">
          <p className="text-xs font-black text-blue-500 uppercase tracking-widest">Séances cette semaine</p>
          <p className="text-3xl font-black text-blue-500 mt-2">{weekGroups.length}</p>
          <p className="text-xs text-gray-400 mt-1">{todayGroups.length} aujourd'hui</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-emerald-100 shadow-sm">
          <p className="text-xs font-black text-emerald-500 uppercase tracking-widest">Complétion</p>
          <p className="text-3xl font-black text-emerald-500 mt-2">{completionRate}%</p>
          <p className="text-xs text-gray-400 mt-1">{completedSessions}/{sessionsASigner.length} signatures</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 text-sm">Prochaines séances</h2>
            <button onClick={() => setActiveTab('calendrier')} className="text-xs text-violet-600 font-bold hover:underline">Voir calendrier →</button>
          </div>
          <div className="divide-y divide-gray-50">
            {upcomingGroups.length === 0
              ? <div className="py-8 text-center text-gray-400 text-sm">Aucune séance à venir.</div>
              : upcomingGroups.map(g => {
                  const client = clients.find(c => c.id === g.client_id);
                  return (
                    <div key={g.cle} className="px-5 py-3.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{client?.nom || 'Client'} — {titreSeance(g)}</p>
                        <p className="text-xs text-gray-500">
                          {g.date ? new Date(g.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '—'}
                          {horaireSeance(g) ? ` · ${horaireSeance(g)}` : ''}
                          {g.items.length > 1 ? ` · ${g.items.length} éléments` : ''}
                        </p>
                      </div>
                      <span className={`text-xs font-black px-2 py-1 rounded-lg shrink-0 ${g.date === todayStr ? 'bg-violet-50 text-violet-700' : 'bg-gray-50 text-gray-500'}`}>
                        {g.date === todayStr ? "Aujourd'hui" : g.date ? new Date(g.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—'}
                      </span>
                    </div>
                  );
                })
            }
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 text-sm">Documents en attente</h2>
            <button onClick={() => setActiveTab('gestion_documents')} className="text-xs text-violet-600 font-bold hover:underline">Voir tout →</button>
          </div>
          <div className="divide-y divide-gray-50">
            {pendingDocs.length === 0
              ? <div className="py-8 text-center text-gray-400 text-sm">Tous les documents sont signés ✓</div>
              : pendingDocs.slice(0, 5).map(doc => {
                  const beneficiaire = clients.find(c => c.id === doc.user_id)?.nom || formateurs.find(f => f.id === doc.assigned_formateur_id)?.nom || '—';
                  return (
                    <div key={doc.id} className="px-5 py-3.5 flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{doc.nom}</p>
                        <p className="text-xs text-gray-400">{beneficiaire}</p>
                      </div>
                      <div className="flex gap-1 shrink-0 ml-2">
                        {docAwaitsClientSignature(doc) && <span className="text-xs bg-amber-50 text-amber-600 border border-amber-100 px-1.5 py-0.5 rounded font-bold">Client</span>}
                        {docAwaitsFormateurSignature(doc) && <span className="text-xs bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded font-bold">Formateur</span>}
                      </div>
                    </div>
                  );
                })
            }
            {pendingDocs.length > 5 && <div className="px-5 py-3 text-center text-xs text-gray-400">+ {pendingDocs.length - 5} autres documents</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Calendrier des séances ────────────────────────────────────────────────────
const CalendrierView = ({ sessions, clients, formateurs, userRole, currentUserId, fetchSessions, groupes = [], groupeSeances = [], onOpenGroupe = null }) => {
  const ctxConv = React.useContext(ConventionsContext) || {};
  const [papierCal, setPapierCal] = React.useState(null);
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [selectedDay, setSelectedDay] = React.useState(null);
  const [editingSeanceSession, setEditingSeanceSession] = React.useState(null);

  const isSafeVisioUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim());

  // Enregistre le lien visio / la note sur TOUTES les lignes de la séance (même client + même numero_seance),
  // pour que l'info reste cohérente quelle que soit la vue (Calendrier, Mes Séances...).
  const handleSaveSeanceInfo = async (session, info) => {
    const { data: rows, error } = await supabase
      .from('sessions')
      .select('id, metadata')
      .eq('client_id', session.client_id)
      .eq('numero_seance', session.numero_seance);
    if (error) { toast.error("Erreur lors de l'enregistrement"); return; }
    const targets = (rows && rows.length > 0) ? rows : [{ id: session.id, metadata: session.metadata }];
    try {
      await Promise.all(targets.map(r => {
        const existingMeta = (typeof r.metadata === 'string')
          ? (() => { try { return JSON.parse(r.metadata); } catch { return {}; } })()
          : (r.metadata || {});
        return supabase.from('sessions').update({
          metadata: { ...existingMeta, lien_visio: info.lien_visio || null, note_seance: info.note_seance || null }
        }).eq('id', r.id);
      }));
      toast.success('Informations de séance enregistrées');
      fetchSessions && fetchSessions();
      syncSeanceToCalendar(session.client_id, session.numero_seance);
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;

  // GROUPES (2026-10-13) : pour une séance de groupe, le formateur concerné est celui de la séance
  // (s'il a été changé), sinon celui du groupe, sinon le formateur référent du client.
  const groupeDe = (gid) => (groupes || []).find(g => String(g.id) === String(gid));
  const seanceDeGroupe = (gid, numero) => (groupeSeances || []).find(x => String(x.groupe_id) === String(gid) && Number(x.numero) === Number(numero));
  const formateurResponsable = (s) => {
    const client = clients.find(c => c.id === s.client_id);
    if (client?.groupe_id) {
      const gs = seanceDeGroupe(client.groupe_id, s.numero_seance);
      const g = groupeDe(client.groupe_id);
      return gs?.formateur_id || g?.formateur_id || client?.formateur_id;
    }
    return client?.formateur_id;
  };
  const filteredSessions = userRole === 'formateur'
    ? sessions.filter(s => String(formateurResponsable(s) || '') === String(currentUserId))
    : userRole === 'client'
    ? sessions.filter(s => String(s.client_id) === String(currentUserId))
    : sessions;

  const sessionsByDate = React.useMemo(() => {
    const map = {};
    filteredSessions.forEach(s => { if (s.date) { if (!map[s.date]) map[s.date] = []; map[s.date].push(s); } });
    return map;
  }, [filteredSessions]);

  const fusionnerGroupes = (liste) => {
    if (userRole === 'client') return liste;
    const out = [];
    const parCle = new Map();
    liste.forEach(g => {
      const client = clients.find(c => c.id === g.client_id);
      const gid = client?.groupe_id;
      if (!gid || g.numero === null || g.numero === undefined || Number(g.numero) === 0) { out.push(g); return; }
      const cle = `grp|${gid}|${g.numero}|${g.date || ''}`;
      if (!parCle.has(cle)) {
        const e = { cle, estGroupe: true, groupe_id: gid, client_id: g.client_id, numero: g.numero, date: g.date, heure_debut: g.heure_debut, heure_fin: g.heure_fin, membres: [], items: [] };
        parCle.set(cle, e);
        out.push(e);
      }
      const e = parCle.get(cle);
      if (!e.heure_debut && g.heure_debut) e.heure_debut = g.heure_debut;
      if (!e.heure_fin && g.heure_fin) e.heure_fin = g.heure_fin;
      e.membres.push(g);
      e.items.push(...g.items);
    });
    return out;
  };

  const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const todayStr = jourLocalISO(new Date());

  const cells = [];
  for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-black text-gray-900">Calendrier</h1>
        <p className="text-gray-400 text-sm mt-1">Planning des séances par jour.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button onClick={() => { setCurrentMonth(new Date(year, month - 1, 1)); setSelectedDay(null); }} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <ChevronLeft size={18} className="text-gray-600" />
          </button>
          <h2 className="text-lg font-black text-gray-900">{MONTHS[month]} {year}</h2>
          <button onClick={() => { setCurrentMonth(new Date(year, month + 1, 1)); setSelectedDay(null); }} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <ChevronRight size={18} className="text-gray-600" />
          </button>
        </div>
        <div className="grid grid-cols-7 border-b border-gray-100">
          {DAYS.map(d => <div key={d} className="py-3 text-center text-xs font-black text-gray-400 uppercase tracking-widest">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (!day) return <div key={idx} className="h-20 border-b border-r border-gray-50 bg-gray-50/50" />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayGroups = fusionnerGroupes(regrouperParSeance(sessionsByDate[dateStr] || []));
            const isToday = dateStr === todayStr;
            const isSelected = selectedDay === dateStr;
            return (
              <div key={idx} onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                className={`h-20 border-b border-r border-gray-50 p-1.5 cursor-pointer transition-colors ${isSelected ? 'bg-violet-50' : isToday ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mb-1 ${isToday ? 'bg-violet-600 text-white' : 'text-gray-700'}`}>{day}</div>
                <div className="space-y-0.5">
                  {dayGroups.slice(0, 2).map((g, i) => {
                    const client = clients.find(c => c.id === g.client_id);
                    if (g.estGroupe) return <div key={i} title={`${groupeDe(g.groupe_id)?.nom || 'Groupe'} · ${g.membres.length} participants`} className="flex items-center gap-1 text-[11px] bg-indigo-100 text-indigo-700 px-1 py-0.5 rounded font-bold"><span className="truncate">{g.heure_debut ? `${heureCourte(g.heure_debut)} ` : ''}{groupeDe(g.groupe_id)?.nom || 'Groupe'}</span><span className="shrink-0 ml-auto bg-white/70 rounded px-1">{g.membres.length}</span></div>;
                    return <div key={i} className="text-[11px] bg-violet-100 text-violet-700 px-1 py-0.5 rounded truncate font-medium">{g.heure_debut ? `${g.heure_debut} ` : ''}{userRole === 'client' ? titreSeance(g) : (client?.nom?.split(' ')[0] || 'Séance')}</div>;
                  })}
                  {dayGroups.length > 2 && <div className="text-[11px] text-gray-400 font-medium">+{dayGroups.length - 2}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDay && sessionsByDate[selectedDay] && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h3 className="font-bold text-gray-900 text-sm">{new Date(selectedDay + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {fusionnerGroupes(regrouperParSeance(sessionsByDate[selectedDay])).map(g => {
              const s = g.items[0];
              if (g.estGroupe) {
                const grp = groupeDe(g.groupe_id);
                const gs = seanceDeGroupe(g.groupe_id, g.numero);
                const fId = gs?.formateur_id || grp?.formateur_id;
                const fNom = (formateurs || []).find(f => String(f.id) === String(fId))?.nom;
                const nbSignes = g.membres.filter(seanceSignee).length;
                const aSigner = g.membres.some(seanceAttendSignature); // AFFICHAGE (2026-10-20)
                const lien = g.items.map(i => lireMeta(i.metadata).lien_visio).find(isSafeVisioUrl);
                return (
                  <div key={g.cle} className="px-5 py-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">
                          {grp?.nom || 'Groupe'} — {gs?.titre && !/^Séance \d+$/.test(gs.titre) ? `${titreSeance(g)} · ${gs.titre}` : titreSeance(g)}
                          <span className="ml-2 text-[11px] font-black px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 whitespace-nowrap">Groupe · {g.membres.length} participant{g.membres.length > 1 ? 's' : ''}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {horaireSeance({ heure_debut: heureCourte(g.heure_debut), heure_fin: heureCourte(g.heure_fin) }) || 'Horaire à définir'}
                          {fNom && userRole !== 'formateur' ? ` · avec ${fNom}` : ''}
                          {gs?.lieu || grp?.lieu ? ` · ${gs?.lieu || grp?.lieu}` : ''}
                        </p>
                        <details className="mt-1">
                          <summary className="text-xs text-violet-700 font-bold cursor-pointer select-none">Voir les participants</summary>
                          <ul className="mt-1 text-xs text-gray-600 space-y-0.5">
                            {g.membres.map(m => {
                              const c = clients.find(x => x.id === m.client_id);
                              return <li key={m.cle}>{aSigner ? (seanceSignee(m) ? '✓ ' : '○ ') : '• '}{c?.nom || 'Participant'}</li>;
                            })}
                          </ul>
                        </details>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {aSigner && <span className={`text-xs font-black px-2 py-1 rounded-lg ${nbSignes === g.membres.length ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{nbSignes}/{g.membres.length} signés</span>}
                        {gs && userRole !== 'client' && (
                          <button type="button" onClick={() => setPapierCal(g.cle)} className={`px-3 py-1.5 rounded-lg border text-xs font-bold ${gs.feuille_papier_url ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-gray-200 text-gray-700 hover:border-violet-300'}`}>{gs.feuille_papier_url ? '✓ ' : ''}Feuille papier</button>
                        )}
                        {onOpenGroupe && <button onClick={() => onOpenGroupe(g.groupe_id)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:border-violet-300">Ouvrir le groupe</button>}
                      </div>
                    </div>
                    {papierCal === g.cle && gs && (
                      <EmargementPapierModal groupe={grp} seance={gs} sessions={sessions}
                        participants={(clients || []).filter(c => String(c.groupe_id) === String(g.groupe_id)).sort((a, b) => (a.nom_complet || a.nom || '').localeCompare(b.nom_complet || b.nom || '', 'fr'))}
                        formateurNom={fNom || ''} orgNom={ctxConv.orgSettings?.nom || ''} nomUtilisateur={ctxConv.nomUtilisateur}
                        onClose={() => setPapierCal(null)}
                        onEnregistre={() => { fetchSessions && fetchSessions(); ctxConv.rafraichirGroupes && ctxConv.rafraichirGroupes(); }} />
                    )}
                    {lien && (
                      <a href={lien} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors">🎥 Rejoindre la visio</a>
                    )}
                  </div>
                );
              }
              const client = clients.find(c => c.id === g.client_id);
              const formateur = formateurs.find(f => f.id === client?.formateur_id);
              const metas = g.items.map(i => (typeof i.metadata === 'string')
                ? (() => { try { return JSON.parse(i.metadata); } catch { return {}; } })()
                : (i.metadata || {}));
              const lienVisio = metas.map(m => m.lien_visio).find(isSafeVisioUrl);
              const noteSeance = metas.map(m => m.note_seance).find(Boolean);
              const canEdit = userRole === 'formateur' || userRole === 'admin';
              const signee = seanceSignee(g);
              const aSignerSeance = seanceAttendSignature(g); // AFFICHAGE (2026-10-20)
              return (
                <div key={g.cle} className="px-5 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{userRole === 'client' ? titreSeance(g) : `${client?.nom || 'Client'} — ${titreSeance(g)}`}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {horaireSeance(g) || 'Horaire à définir'}
                        {formateur && userRole !== 'formateur' ? ` · avec ${formateur.nom}` : ''}
                      </p>
                      {g.items.length > 1 && (
                        <p className="text-xs text-gray-400 mt-1 truncate">{g.items.map(i => i.ressource_titre || i.nom).join(' · ')}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {aSignerSeance && (
                      <span className={`text-xs font-black px-2 py-1 rounded-lg ${signee ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {signee ? '✓ Signé' : 'En attente'}
                      </span>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => setEditingSeanceSession(s)}
                          title="Ajouter un lien / une note"
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-violet-600 hover:border-violet-200 transition-colors shrink-0"
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                  {(isSafeVisioUrl(lienVisio) || noteSeance) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {isSafeVisioUrl(lienVisio) && (
                        <a
                          href={lienVisio}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                        >
                          🎥 Rejoindre la visio
                        </a>
                      )}
                      {noteSeance && <p className="text-xs text-gray-500 italic">{noteSeance}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <SeanceInfoModal
        isOpen={!!editingSeanceSession}
        onClose={() => setEditingSeanceSession(null)}
        session={editingSeanceSession}
        onSave={handleSaveSeanceInfo}
      />
    </div>
  );
};

// ─── Modale d'informations de séance (lien visio / note) ───────────────────────
const SeanceInfoModal = ({ isOpen, onClose, session, onSave }) => {
  const [lienVisio, setLienVisio] = React.useState('');
  const [note, setNote] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (isOpen && session) {
      const meta = (typeof session.metadata === 'string')
        ? (() => { try { return JSON.parse(session.metadata); } catch { return {}; } })()
        : (session.metadata || {});
      setLienVisio(meta.lien_visio || '');
      setNote(meta.note_seance || '');
      setIsSaving(false);
    }
  }, [isOpen, session]);

  if (!isOpen || !session) return null;

  const handleSubmit = async () => {
    setIsSaving(true);
    await onSave(session, { lien_visio: lienVisio.trim(), note_seance: note.trim() });
    setIsSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fade-in">
      <div className="bg-white rounded-[32px] w-full max-w-md shadow-2xl border border-gray-100 overflow-hidden animate-slide-up">
        <div className="bg-violet-600 p-6 text-white relative">
          <p className="text-violet-200 text-xs font-black uppercase tracking-widest mb-1">📅 Informations de séance</p>
          <h2 className="text-lg font-black leading-tight">{session.ressource_titre || session.nom || 'Séance'}</h2>
          <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-all">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Lien de visioconférence (optionnel)</label>
            <input
              type="url"
              value={lienVisio}
              onChange={(e) => setLienVisio(e.target.value)}
              placeholder="https://meet.google.com/..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Note / information pour le client (optionnel)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Ex : Merci de vous connecter 5 min avant..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50">Annuler</button>
            <button onClick={handleSubmit} disabled={isSaving} className="flex-1 py-3 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 disabled:opacity-50">
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Accueil Formateur ─────────────────────────────────────────────────────────
const FormateurAccueilView = ({ formateurs, clients, sessions, documents, currentUserId, setActiveTab, setSigningDocId, setViewingSession, handleSaveCorrection }) => {
  const formateur = formateurs.find(f => f.id === currentUserId);
  const myClients = clients.filter(c => c.formateur_id === currentUserId);
  const myClientIds = myClients.map(c => c.id);
  const today = jourLocalISO(new Date());
  const in7Days = jourLocalISO(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
  const todaySessions = sessions.filter(s => myClientIds.includes(s.client_id) && s.date === today);
  const weekSessions = sessions.filter(s => myClientIds.includes(s.client_id) && s.date > today && s.date <= in7Days);
  const todayGroups = regrouperParSeance(todaySessions);
  const weekGroups = regrouperParSeance(weekSessions);
  const pendingDocs = documents.filter(d => d.assigned_formateur_id === currentUserId && d.visible_formateur !== false && !d.signe_par_formateur && !isBlockedBySigningOrder(d, 'formateur') && !isDossierDoc(d));
  // Exercices rendus par un client mais pas encore corrigés — auparavant on ne le voyait qu'en
  // ouvrant chaque client une par une dans "Mes Clients". On le remonte ici, bien visible.
  const pendingCorrections = sessions.filter(s =>
    myClientIds.includes(s.client_id) &&
    (s.type_activite === 'exercice' || s.type_activite === 'Exercice') &&
    s.reponse_url && s.correction_statut !== 'Validé' && s.correction_statut !== 'À corriger'
  );
  const [correctionModalSession, setCorrectionModalSession] = React.useState(null);

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-black text-gray-900">Bonjour, {formateur?.nom?.split(' ')[0] || 'Formateur'} 👋</h1>
        <p className="text-gray-400 text-sm mt-1">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center">
          <p className="text-3xl font-black text-gray-900">{myClients.length}</p>
          <p className="text-xs text-gray-400 mt-1 font-black uppercase tracking-widest">Mes clients</p>
        </div>
        <div className={`bg-white rounded-2xl p-5 border shadow-sm text-center ${todaySessions.length > 0 ? 'border-violet-100' : 'border-gray-100'}`}>
          <p className={`text-3xl font-black ${todayGroups.length > 0 ? 'text-violet-600' : 'text-gray-900'}`}>{todayGroups.length}</p>
          <p className="text-xs text-gray-400 mt-1 font-black uppercase tracking-widest">Séances aujourd'hui</p>
        </div>
        <div className={`bg-white rounded-2xl p-5 border shadow-sm text-center ${pendingDocs.length > 0 ? 'border-amber-100' : 'border-gray-100'}`}>
          <p className={`text-3xl font-black ${pendingDocs.length > 0 ? 'text-amber-500' : 'text-gray-900'}`}>{pendingDocs.length}</p>
          <p className="text-xs text-gray-400 mt-1 font-black uppercase tracking-widest">À signer</p>
        </div>
        <div className={`bg-white rounded-2xl p-5 border shadow-sm text-center ${pendingCorrections.length > 0 ? 'border-emerald-100' : 'border-gray-100'}`}>
          <p className={`text-3xl font-black ${pendingCorrections.length > 0 ? 'text-emerald-600' : 'text-gray-900'}`}>{pendingCorrections.length}</p>
          <p className="text-xs text-gray-400 mt-1 font-black uppercase tracking-widest">Exercices à corriger</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 text-sm">Planning de la semaine</h2>
            <button onClick={() => setActiveTab('calendrier')} className="text-xs text-violet-600 font-bold hover:underline">Calendrier →</button>
          </div>
          <div className="divide-y divide-gray-50">
            {todayGroups.length === 0 && weekGroups.length === 0
              ? <div className="py-8 text-center text-gray-400 text-sm">Aucune séance planifiée cette semaine.</div>
              : <>
                  {todayGroups.length > 0 && <>
                    <div className="px-5 py-2 bg-violet-50"><p className="text-xs font-black text-violet-700 uppercase tracking-widest">Aujourd'hui</p></div>
                    {todayGroups.map(g => {
                      const client = myClients.find(c => c.id === g.client_id);
                      return (
                        <div key={g.cle} className="px-5 py-3 flex items-center justify-between border-b border-gray-50">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{client?.nom} — {titreSeance(g)}</p>
                            <p className="text-xs text-gray-500">{g.items.length > 1 ? `${g.items.length} éléments` : (g.items[0]?.ressource_titre || g.items[0]?.nom)}</p>
                          </div>
                          {horaireSeance(g) && <span className="text-xs font-bold text-violet-700 shrink-0 ml-2">{horaireSeance(g)}</span>}
                        </div>
                      );
                    })}
                  </>}
                  {weekGroups.slice(0, 4).map(g => {
                    const client = myClients.find(c => c.id === g.client_id);
                    return (
                      <div key={g.cle} className="px-5 py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{client?.nom} — {titreSeance(g)}</p>
                          <p className="text-xs text-gray-500">{g.date ? new Date(g.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '—'}</p>
                        </div>
                        {horaireSeance(g) && <span className="text-xs font-medium text-gray-500 shrink-0">{horaireSeance(g)}</span>}
                      </div>
                    );
                  })}
                </>
            }
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="font-bold text-gray-900 text-sm">Documents à signer</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {pendingDocs.length === 0
              ? <div className="py-8 text-center text-gray-400 text-sm">Aucun document en attente ✓</div>
              : pendingDocs.slice(0, 5).map(doc => {
                  const client = clients.find(c => c.id === doc.user_id);
                  return (
                    <button
                      key={doc.id}
                      type="button"
                      // Ouvre la même visionneuse riche (avec aperçu du document et balises) que
                      // l'onglet "Documents administratifs", au lieu de l'ancienne fenêtre nue
                      // "Émargement Électronique" (setSigningDocId) qui n'affichait aucun aperçu.
                      onClick={() => setViewingSession && setViewingSession({ session: { ...doc, file_url: doc.url }, mode: 'sign' })}
                      className="w-full px-5 py-3.5 flex items-center justify-between text-left hover:bg-amber-50/60 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{doc.nom}</p>
                        {client && <p className="text-xs text-gray-400">{client.nom}</p>}
                      </div>
                      <span className="text-xs bg-amber-50 text-amber-600 border border-amber-100 px-2 py-1 rounded-lg font-bold shrink-0 ml-2">À signer</span>
                    </button>
                  );
                })
            }
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden lg:col-span-2">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="font-bold text-gray-900 text-sm">Exercices à corriger</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {pendingCorrections.length === 0
              ? <div className="py-8 text-center text-gray-400 text-sm">Aucun exercice en attente de correction ✓</div>
              : pendingCorrections.slice(0, 5).map(session => {
                  const client = myClients.find(c => c.id === session.client_id);
                  return (
                    <button
                      key={session.id}
                      type="button"
                      onClick={() => setCorrectionModalSession(session)}
                      className="w-full px-5 py-3.5 flex items-center justify-between text-left hover:bg-emerald-50/60 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{session.ressource_titre || session.nom}</p>
                        {client && <p className="text-xs text-gray-400">{client.nom}</p>}
                      </div>
                      <span className="text-xs bg-emerald-50 text-emerald-600 border border-emerald-100 px-2 py-1 rounded-lg font-bold shrink-0 ml-2">À corriger</span>
                    </button>
                  );
                })
            }
          </div>
        </div>
      </div>

      <CorrectionModal
        isOpen={!!correctionModalSession}
        onClose={() => setCorrectionModalSession(null)}
        session={correctionModalSession}
        onSave={handleSaveCorrection}
      />
    </div>
  );
};

// ─── Accueil Client ────────────────────────────────────────────────────────────
// Prénom à partir du nom complet : « Jennifer SOLLIEZ » → Jennifer ; « SOLLIEZ Jennifer » → Jennifer.
const prenomDe = (nomComplet) => {
  const mots = String(nomComplet || '').trim().split(/\s+/).filter(Boolean);
  if (mots.length === 0) return '';
  const estMajuscules = (m) => m.length > 1 && m === m.toUpperCase() && m !== m.toLowerCase();
  if (mots.length > 1 && estMajuscules(mots[0]) && !estMajuscules(mots[1])) return mots[1];
  return mots[0];
};

const AccueilView = ({ setActiveTab, clientProgress, moduleName, totalSessions, signedSessions, coachName, brandSettings, clientName, nextGroup, todos = [] }) => (
  <div className="flex flex-col items-center justify-center pt-8 md:pt-16 animate-fade-in">
    {brandSettings?.logo_url ? (
      <img src={brandSettings.logo_url} alt="Logo" className="w-20 h-20 rounded-full object-contain bg-white shadow-lg border border-gray-100 p-2 mb-5" />
    ) : (
      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-5 border border-gray-100"
        style={{color: brandSettings?.primary_color || '#7C3AED'}}>
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
      </div>
    )}
    <h1 className="text-4xl font-extrabold text-gray-900 mb-1 tracking-tight">{prenomDe(clientName) ? `Bonjour, ${prenomDe(clientName)}` : 'Bonjour'} 👋</h1>
    {(brandSettings?.welcome_message || brandSettings?.org_name) && (
      <p className="text-sm mb-2" style={{color: brandSettings?.primary_color || '#7C3AED'}}>
        {brandSettings?.welcome_message || `Bienvenue sur l'espace ${brandSettings?.org_name}`}
      </p>
    )}
    {coachName && <p className="text-sm text-gray-400 mb-4">Votre formateur : <span className="font-bold text-gray-600">{coachName}</span></p>}

    {/* Prochaine séance (UX 2026-10-12) */}
    {nextGroup && (
      <div className="w-full max-w-3xl mb-4">
        <div onClick={() => setActiveTab('mes_seances')}
          className={`cursor-pointer rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4 border transition-shadow hover:shadow-md ${nextGroup.estAujourdhui ? 'bg-violet-50 border-violet-200' : 'bg-blue-50 border-blue-100'}`}>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0 ${nextGroup.estAujourdhui ? 'bg-violet-600' : 'bg-blue-500'}`}>
            <CalendarDays size={22} />
          </div>
          <div className="flex-1 min-w-[160px]">
            <p className={`text-xs font-black uppercase tracking-widest ${nextGroup.estAujourdhui ? 'text-violet-500' : 'text-blue-400'}`}>
              {nextGroup.estAujourdhui ? 'Votre séance aujourd’hui' : 'Prochaine séance'}
            </p>
            <p className="text-base font-bold text-gray-900">
              {nextGroup.titre} · {nextGroup.estAujourdhui ? "Aujourd'hui" : new Date(nextGroup.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              {nextGroup.horaire ? ` · ${nextGroup.horaire}` : ''}
            </p>
          </div>
          {nextGroup.lienVisio && (
            <a href={nextGroup.lienVisio} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl bg-violet-600 text-white hover:bg-violet-700">
              🎥 Rejoindre la visio
            </a>
          )}
        </div>
      </div>
    )}

    {/* À faire maintenant (UX 2026-10-12) */}
    <div className="w-full max-w-3xl bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-50">
        <h2 className="font-bold text-gray-900">À faire maintenant</h2>
      </div>
      {todos.length === 0 ? (
        <p className="px-6 py-5 text-sm text-emerald-600 font-semibold">✓ Tout est à jour, rien à faire pour le moment.</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {todos.map(t => (
            <button key={t.cle} onClick={() => setActiveTab(t.onglet)}
              className="w-full px-6 py-4 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors">
              <span className="flex items-center gap-3 min-w-0">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${t.urgent ? 'bg-amber-400' : 'bg-violet-400'}`}></span>
                <span className="text-sm font-semibold text-gray-800">{t.label}</span>
              </span>
              <span className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg bg-violet-600 text-white">{t.bouton}</span>
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Barre de Progression */}
    <div className="w-full max-w-3xl bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-bold text-gray-700 uppercase tracking-widest">
          {moduleName ? `Progression du ${moduleName}` : 'Progression du parcours'}
        </span>
        <span className="text-sm font-black text-violet-600">{clientProgress}%</span>
      </div>
      <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-violet-600 transition-all duration-1000 ease-out" style={{ width: `${clientProgress}%` }}></div>
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-gray-400 italic">Suivi de votre assiduité en temps réel.</p>
        {totalSessions > 0 && (
          <p className="text-xs text-gray-400 font-semibold">{signedSessions} / {totalSessions} étapes validées</p>
        )}
      </div>
    </div>

    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl">
      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('mes_seances')}>
        <h3 className="font-bold text-gray-800 text-lg mb-2">Planning des Séances</h3>
        <p className="text-sm text-gray-500 mb-4">Consultez vos dates et émargez vos rapports de présence.</p>
        <div className="bg-violet-50 text-violet-700 px-4 py-2 rounded-xl text-sm font-bold text-center">Voir mes séances</div>
      </div>
      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('mes_documents')}>
        <h3 className="font-bold text-gray-800 text-lg mb-2">Mes documents</h3>
        <p className="text-sm text-gray-500 mb-4">Retrouvez et signez les documents de votre formation.</p>
        <div className="bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-bold text-center">Voir mes documents</div>
      </div>
    </div>
  </div>
);

const SessionsView = ({
  sessions, signSession, currentUserId, handleDownloadAttendanceCertificate, userRole,
  pedagogicalResources, handleDownloadResource, handleUploadExerciseResponse, setViewingSession, moduleActuelId = null
}) => {
  // AJOUT (2026-09-19) : exclut les exercices synthétiques de début/fin (numero_seance: null) de "Mes
  // Séances" — ils vivent désormais uniquement dans l'onglet "Exercices", jamais ici comme séance à venir.
  const mySessions = sessions.filter(s => s.client_id === currentUserId && s.numero_seance !== null && s.numero_seance !== undefined && estDuModuleActuel(s, moduleActuelId)).sort((a, b) => a.numero_seance - b.numero_seance);
  // UX (2026-10-12) : les séances passées et entièrement faites sont repliées (bouton pour les revoir).
  const [afficherTerminees, setAfficherTerminees] = React.useState(false);
  const libelleType = (t) => ({ signature: 'Émargement', document: 'Document', exercice: 'Exercice', Exercice: 'Exercice' }[t] || t);
  const estSeanceTerminee = (g) => !!g.date && g.date < localTodayISO() && g.items.every(s => {
    if (s.type_activite === 'exercice' || s.type_activite === 'Exercice') return !!s.reponse_url;
    if (s.type_activite === 'signature') return s.metadata?.requiresClientSignature === false || s.statut_client === 'Signé';
    const m = s.metadata || {};
    if (m.isToSign || m.requiresSignature || m.documentType === 'signature') return s.statut_client === 'Signé';
    return true;
  });
  const [exerciceModalSession, setExerciceModalSession] = React.useState(null);

  const parseSessionMeta = (m) => (typeof m === 'string') ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
  const isSafeVisioUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim());
  const getGroupVisioInfo = (group) => ({
    lienVisio: group.items.map(i => parseSessionMeta(i.metadata).lien_visio).find(isSafeVisioUrl),
    note: group.items.map(i => parseSessionMeta(i.metadata).note_seance).find(Boolean),
  });

  const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    const [h1, m1] = start.split(':').map(Number);
    const [h2, m2] = end.split(':').map(Number);
    let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff <= 0) return null;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}h${m > 0 ? ` ${m}min` : ''}`;
  };

  // Boutons d'action (Consulter / Signer / Exercice) — partagés entre la vue tableau (desktop) et la vue cartes (mobile).
  const renderActions = (session, group) => {
    const sessionDate = session.date || group.date;
    const signLock = signatureLockFor(sessionDate, session.numero_seance ?? group.numero);
    const isDateLocked = signLock.locked;
    const metadata = session.metadata || {};

    const isSignatureCondition = session.type_activite === 'signature' || (session.type_activite === 'document' && (metadata.isToSign || metadata.requiresSignature || metadata.documentType === 'signature'));

    if (isSignatureCondition) {
      const signedUrl = session.file_url_signed || session.signed_pdf_url || metadata.file_url_signed;
      const docUrl = session.file_url || session.ressource_url;
      return (
        <div className="flex flex-wrap gap-2 items-center justify-end w-full">
          {docUrl && (
            <button
              onClick={() => {
                const fileUrl = signedUrl || docUrl;
                setViewingSession && setViewingSession({ session: { ...session, file_url: fileUrl }, mode: 'view' });
              }}
              className="text-xs font-bold px-3 py-1.5 rounded-lg border transition-all bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
            >
              Consulter
            </button>
          )}
          <button
            disabled={session.statut_client === 'Signé' || isDateLocked}
            onClick={() => {
              if (session.statut_client === 'Signé') return;
              if (session.type_activite === 'signature') {
                signSession && signSession(session);
              } else {
                const fileUrl = docUrl || null;
                // SIGNATURES (2026-10-20) : source 'session' → enregistré sur la séance (voir signerDocumentDeSeance).
                setViewingSession && setViewingSession({ session: { ...session, file_url: fileUrl }, mode: 'sign', source: 'session' });
              }
            }}
            className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all ${
              session.statut_client === 'Signé'
                ? 'bg-green-50 text-green-600 border-green-200'
                : isDateLocked
                ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                : 'bg-violet-600 text-white border-violet-700 hover:bg-violet-700'
            }`}
          >
            {session.statut_client === 'Signé' ? 'Signé ✓' : isDateLocked ? signLock.label : (session.type_activite === 'signature' ? 'Signer ma présence' : 'Signer le document')}
          </button>
        </div>
      );
    }

    if (session.type_activite === 'document') {
      const signedUrl = session.file_url_signed || session.signed_pdf_url || metadata.file_url_signed;
      const docUrl = signedUrl || session.file_url || session.ressource_url;
      return (
        <div className="flex flex-wrap gap-2 items-center justify-end w-full">
          <button
            onClick={() => {
              setViewingSession && setViewingSession({ session: { ...session, file_url: docUrl }, mode: 'view' });
            }}
            className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all ${
              signedUrl ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
            }`}
          >
            {signedUrl ? 'Voir Signé ↗' : 'Consulter'}
          </button>
        </div>
      );
    }

    if (session.type_activite === 'exercice' || session.type_activite === 'Exercice') {
      return (
        <div className="flex flex-wrap gap-2 items-center">
          {session.reponse_url && (
            <span className={`text-[11px] font-black uppercase px-2 py-1 rounded-full ${session.correction_statut === 'Validé' ? 'bg-green-100 text-green-700' : session.correction_statut === 'À corriger' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
              {session.correction_statut === 'Validé' ? '✅ Validé' : session.correction_statut === 'À corriger' ? '📝 À corriger' : '📬 En attente'}
            </span>
          )}
          <button
            onClick={() => setExerciceModalSession(session)}
            className="text-xs font-bold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            {session.reponse_url ? "Modifier le rendu" : "Accéder à l'exercice"}
          </button>
        </div>
      );
    }

    return null;
  };

  // Badges de statut (Moi / Formateur) — partagés entre les deux vues.
  const renderStatus = (session) => (
    <div className="flex flex-col gap-1 items-start md:items-center">
      {/* AFFICHAGE (2026-10-20) : seulement les signatures réellement attendues. */}
      {signaturesAttendues(session).client && (
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${session.statut_client === 'Signé' ? 'bg-green-500' : 'bg-orange-400'}`}></span>
        <span className="text-[11px] font-black uppercase text-gray-500">Vous : {session.statut_client || (session.statut === 'Signé' ? 'Signé' : 'À venir')}</span>
      </div>
      )}
      {signaturesAttendues(session).formateur && (
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${session.statut_formateur === 'Signé' ? 'bg-green-500' : 'bg-orange-400'}`}></span>
        <span className="text-[11px] font-black uppercase text-gray-500">Formateur : {session.statut_formateur || (session.type_activite === 'signature' && session.statut === 'Signé' ? 'Signé' : 'À venir')}</span>
      </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Mes Séances d'Accompagnement</h1>
      {(() => {
        const nbSeances = new Set(mySessions.filter(s => Number(s.numero_seance) >= 1).map(s => s.numero_seance)).size;
        return (
          <p className="text-gray-500 text-lg">
            {nbSeances > 0
              ? `Retrouvez le calendrier de vos ${nbSeances} séance${nbSeances > 1 ? 's' : ''} et signez vos émargements.`
              : 'Retrouvez ici le calendrier de vos séances et signez vos émargements.'}
          </p>
        );
      })()}

      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <span className="w-2 h-6 bg-gray-900 rounded-full mr-3"></span> Liste des Séances
          </h2>
        </div>

        {(() => {
          const grouped = mySessions.reduce((acc, s) => {
            const key = s.numero_seance;
            if (!acc[key]) acc[key] = { numero: s.numero_seance, nom: (s.nom || '').split(' - ')[0], date: s.date, debut: s.heure_debut, fin: s.heure_fin, items: [] };
            acc[key].items.push(s);
            fillGroupSchedule(acc[key], s);
            return acc;
          }, {});

          const sortedGroups = Object.values(grouped).sort((a, b) => a.numero - b.numero);
          const nbTerminees = sortedGroups.filter(estSeanceTerminee).length;
          const groupesAffiches = afficherTerminees ? sortedGroups : sortedGroups.filter(g => !estSeanceTerminee(g));

          if (sortedGroups.length === 0) {
            return <p className="py-12 text-center text-gray-400 italic">Aucune séance n'est encore programmée. Votre formateur les générera prochainement.</p>;
          }

          return (
            <>
              {nbTerminees > 0 && (
                <button onClick={() => setAfficherTerminees(v => !v)} className="mb-4 text-sm font-bold text-violet-700 hover:text-violet-900">
                  {afficherTerminees ? 'Masquer les séances terminées' : `Afficher ${nbTerminees > 1 ? `les ${nbTerminees} séances terminées` : 'la séance terminée'} ✓`}
                </button>
              )}
              {/* --- Vue tableau : écrans md et plus --- */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-sm text-gray-500 uppercase tracking-widest font-bold">
                      <th className="pb-4">Séance</th>
                      <th className="pb-4">Planification (Date & Heures)</th>
                      <th className="pb-4 text-center">Statut</th>
                      <th className="pb-4 text-right">Émargement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupesAffiches.map((group, gIdx) => {
                      const today = localTodayISO();
                      const isFuture = group.date && group.date > today;
                      // Seules les séances avec une date future sont verrouillées.
                      // Les séances passées restent toujours accessibles pour que le client puisse signer.
                      const isLocked = isFuture;
                      const { lienVisio, note } = getGroupVisioInfo(group);

                      return (
                        <React.Fragment key={gIdx}>
                          <tr className={`bg-gray-50/50 ${isLocked ? 'opacity-50' : ''}`}>
                            <td colSpan="4" className={`py-3 px-4 border-l-4 ${isLocked ? 'border-gray-300' : 'border-gray-900'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className={`w-8 h-8 rounded-lg ${isLocked ? 'bg-gray-300' : 'bg-gray-900'} text-white flex items-center justify-center mr-3 text-xs font-black`}>#{group.numero}</div>
                                  <span className={`font-black ${isLocked ? 'text-gray-400' : 'text-gray-900'} text-sm uppercase tracking-tighter`}>{group.nom}</span>
                                  {group.date === localTodayISO() && <span className="ml-3 text-[11px] font-black bg-violet-600 text-white px-2 py-0.5 rounded-full">AUJOURD'HUI</span>}
                                </div>
                                <div className="text-xs font-bold text-gray-500">
                                  {isLocked && <span className="bg-gray-200 text-gray-500 px-2 py-0.5 rounded mr-2">🔒 À venir</span>}
                                  {group.date ? new Date(group.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : 'Date à définir'} • {group.debut || '--:--'} - {group.fin || '--:--'}
                                </div>
                              </div>
                              {!isLocked && (lienVisio || note) && (
                                <div className="mt-2 flex flex-wrap items-center gap-2 pl-11">
                                  {lienVisio && (
                                    <a href={lienVisio} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors">
                                      🎥 Rejoindre la visio
                                    </a>
                                  )}
                                  {note && <p className="text-xs text-gray-500 italic">{note}</p>}
                                </div>
                              )}
                            </td>
                          </tr>
                          {group.items.map(session => (
                            <tr key={session.id} className={`transition-all ${isLocked ? 'opacity-40 grayscale pointer-events-none bg-gray-50/10' : 'hover:bg-gray-50/30'}`}>
                            <td className="py-4 pl-12">
                              <div className="flex items-center gap-3">
                                <span className="text-lg">{session.type_activite === 'signature' ? '✍️' : session.type_activite === 'document' ? '📄' : '⚙️'}</span>
                                <div className="flex flex-col">
                                  <span className="font-bold text-gray-700 text-xs">{session.ressource_titre || session.nom}</span>
                                  <span className="text-[11px] text-gray-400 uppercase font-black">{libelleType(session.type_activite)}</span>
                                  {session.instructions && (session.type_activite === 'exercice' || session.type_activite === 'Exercice') && (
                                    <p className="text-xs text-gray-500 mt-1 max-w-xs leading-relaxed">{session.instructions}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4"></td>
                            <td className="py-4 text-center">{renderStatus(session)}</td>
                            <td className="py-4 text-right pr-4">
                              <div className="flex justify-end gap-2">{renderActions(session, group)}</div>
                            </td>
                          </tr>
                        ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* --- Vue cartes : mobile (< md) --- */}
              <div className="md:hidden space-y-4">
                {groupesAffiches.map((group, gIdx) => {
                  const today = localTodayISO();
                  const isFuture = group.date && group.date > today;
                  const isLocked = isFuture;
                  const { lienVisio, note } = getGroupVisioInfo(group);

                  return (
                    <div key={gIdx} className={`rounded-2xl border overflow-hidden ${isLocked ? 'border-gray-100 opacity-50' : 'border-gray-200'}`}>
                      <div className={`px-4 py-3 ${isLocked ? 'bg-gray-100' : 'bg-gray-900'}`}>
                        <div className="flex items-center min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 text-xs font-black shrink-0 ${isLocked ? 'bg-gray-300 text-gray-500' : 'bg-white/10 text-white'}`}>#{group.numero}</div>
                          <span className={`font-black text-xs uppercase tracking-tight truncate ${isLocked ? 'text-gray-400' : 'text-white'}`}>{group.nom}</span>
                          {group.date === localTodayISO() && <span className="ml-auto shrink-0 text-[11px] font-black bg-violet-500 text-white px-2 py-0.5 rounded-full">AUJOURD'HUI</span>}
                        </div>
                      </div>
                      <div className="px-4 py-2 bg-gray-50 text-xs font-bold text-gray-500 flex items-center justify-between flex-wrap gap-1">
                        {isLocked && <span className="bg-gray-200 text-gray-500 px-2 py-0.5 rounded">🔒 À venir</span>}
                        <span>{group.date ? new Date(group.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : 'Date à définir'} • {group.debut || '--:--'} - {group.fin || '--:--'}</span>
                      </div>
                      {!isLocked && (lienVisio || note) && (
                        <div className="px-4 py-2 flex flex-wrap items-center gap-2 border-b border-gray-50">
                          {lienVisio && (
                            <a href={lienVisio} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors">
                              🎥 Rejoindre la visio
                            </a>
                          )}
                          {note && <p className="text-xs text-gray-500 italic">{note}</p>}
                        </div>
                      )}
                      <div className={`divide-y divide-gray-100 ${isLocked ? 'pointer-events-none' : ''}`}>
                        {group.items.map(session => (
                          <div key={session.id} className={`p-4 space-y-3 ${isLocked ? 'grayscale' : ''}`}>
                            <div className="flex items-start gap-3">
                              <span className="text-lg shrink-0">{session.type_activite === 'signature' ? '✍️' : session.type_activite === 'document' ? '📄' : '⚙️'}</span>
                              <div className="flex flex-col min-w-0">
                                <span className="font-bold text-gray-700 text-xs">{session.ressource_titre || session.nom}</span>
                                <span className="text-[11px] text-gray-400 uppercase font-black">{libelleType(session.type_activite)}</span>
                                {session.instructions && (session.type_activite === 'exercice' || session.type_activite === 'Exercice') && (
                                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{session.instructions}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              {renderStatus(session)}
                            </div>
                            <div className="flex justify-start">
                              {renderActions(session, group)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>

      <ExerciceModal
        isOpen={!!exerciceModalSession}
        onClose={() => setExerciceModalSession(null)}
        session={exerciceModalSession}
        onSubmit={handleUploadExerciseResponse}
      />
    </div>
  );
};

// ─── Modal de signature spécifique : Autorisation de conservation ──────────────
const ConservationModal = ({ isOpen, onClose, onSave, clientPrenom, clientNom, formateurNom, dateDebut, orgNom = '' }) => {
  const organisme = orgNom || "l'organisme de formation";
  const [choice, setChoice] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const canvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);

  const clientFullName = [clientPrenom, clientNom].filter(Boolean).join(' ') || clientNom || '—';

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    if (e.touches) return { x: (e.touches[0].clientX - rect.left) * sx, y: (e.touches[0].clientY - rect.top) * sy };
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };
  const startDraw = (e) => {
    e.preventDefault();
    const c = canvasRef.current; if (!c) return;
    isDrawingRef.current = true;
    const ctx = c.getContext('2d');
    const p = getPos(e, c);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const draw = (e) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#111827';
    const p = getPos(e, c);
    ctx.lineTo(p.x, p.y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const stopDraw = () => { isDrawingRef.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); };
  const isCanvasEmpty = () => {
    const c = canvasRef.current; if (!c) return true;
    return !c.getContext('2d').getImageData(0, 0, c.width, c.height).data.some(v => v !== 0);
  };

  const handleSubmit = async () => {
    if (!choice) { toast.error('Veuillez choisir une option.'); return; }
    if (isCanvasEmpty()) { toast.error('Veuillez apposer votre signature.'); return; }
    const signatureDataUrl = canvasRef.current.toDataURL('image/png');
    setSaving(true);
    try {
      await onSave({ choice, signatureDataUrl, clientFullName, formateurNom, dateDebut });
    } catch (err) {
      console.error('[ConservationModal] Erreur:', err);
      toast.error('Erreur lors de la génération du document.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-900/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-8 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-black text-gray-900">Autorisation de conservation des documents</h2>
            <p className="text-xs text-gray-400 mt-0.5">Lisez, choisissez une option puis signez</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="px-8 py-6 space-y-5">
          {/* Texte légal */}
          <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4 text-xs text-gray-700 leading-relaxed">
            Les documents élaborés dans le cadre de votre accompagnement sont conservés ou détruits selon votre choix ci-dessous. Pour un bilan de compétences, l'article R6313-7 du Code du travail prévoit leur destruction à l'issue de la prestation, <strong>sauf accord écrit du bénéficiaire</strong>.
          </div>

          {/* Tableau d'informations */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-3 text-sm">
            {[
              { label: 'Bénéficiaire', value: `Mme / M. ${clientFullName}` },
              { label: 'Organisme', value: orgNom || '—' },
              { label: 'Formateur / consultant', value: formateurNom || '—' },
              { label: 'Date de début', value: dateDebut || '—' },
            ].map(({ label, value }, i, arr) => (
              <React.Fragment key={label}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-black text-gray-400 uppercase tracking-widest">{label}</span>
                  <span className="font-semibold text-gray-900">{value}</span>
                </div>
                {i < arr.length - 1 && <div className="h-px bg-gray-200" />}
              </React.Fragment>
            ))}
          </div>

          {/* Choix */}
          <div className="space-y-3">
            <h3 className="font-bold text-gray-900 text-sm">Choix du bénéficiaire</h3>
            <label className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${choice === 'conserve' ? 'border-emerald-400 bg-emerald-50' : 'border-gray-100 hover:border-gray-200 bg-white'}`}>
              <input type="radio" name="conservation" value="conserve" checked={choice === 'conserve'} onChange={() => setChoice('conserve')} className="mt-0.5 shrink-0 accent-emerald-500" />
              <span className="text-sm text-gray-700 leading-relaxed">
                J'autorise {organisme} à conserver mes documents pendant une durée maximale de <strong>3 ans</strong> à compter de la fin de la prestation, dans le but d'assurer le suivi de ma situation.
              </span>
            </label>
            <label className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${choice === 'detruire' ? 'border-violet-400 bg-violet-50' : 'border-gray-100 hover:border-gray-200 bg-white'}`}>
              <input type="radio" name="conservation" value="detruire" checked={choice === 'detruire'} onChange={() => setChoice('detruire')} className="mt-0.5 shrink-0 accent-violet-600" />
              <span className="text-sm text-gray-700 leading-relaxed">
                Je <strong>ne souhaite pas</strong> que mes documents soient conservés. {organisme} procédera à leur destruction à la clôture de la prestation.
              </span>
            </label>
          </div>

          {/* Signature */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-gray-900 text-sm">Signature du bénéficiaire</h3>
              <button onClick={clearCanvas} className="text-xs text-gray-400 hover:text-gray-600 underline">Effacer</button>
            </div>
            <div className="border-2 border-dashed border-gray-200 rounded-2xl overflow-hidden bg-gray-50">
              <canvas
                ref={canvasRef}
                width={600} height={140}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                className="w-full cursor-crosshair touch-none"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Dessinez votre signature dans le cadre ci-dessus</p>
          </div>
        </div>

        <div className="px-8 py-5 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors">Annuler</button>
          <button
            onClick={handleSubmit}
            disabled={saving || !choice}
            className="px-6 py-2.5 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {saving ? (
              <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Génération…</>
            ) : (
              'Signer et valider'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Vue "Mes Documents" client ────────────────────────────────────────────────
// Lit directement module_step_resources (lecture seule) filtré par le module_id
// du client connecté. Aucune écriture sur les templates. Les signatures sont
// stockées dans la table `documents` avec user_id = currentUserId uniquement.

// ─── Composant Modal : Remplir un questionnaire (vue client) ─────────────────
const QuestionnaireFillerModal = ({ questionnaire, onClose, onSubmit }) => {
  const meta = React.useMemo(() => {
    try { return typeof questionnaire.metadata === 'string' ? JSON.parse(questionnaire.metadata) : (questionnaire.metadata || {}); }
    catch { return {}; }
  }, [questionnaire]);
  const questions = meta.questions || [];
  const isQuiz = !!meta.isQuiz;
  const [answers, setAnswers] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  // AJOUT (2026-09-30) : résultat affiché après soumission — note/statut pour un quiz noté (calculée
  // ici même, tout ou rien par question, avant l'enregistrement), ou simple écran de remerciement si
  // un message de fin a été configuré (questionnaire classique ou quiz).
  const [result, setResult] = React.useState(null); // null | 'done' | { scorePercent, passed, correctCount, total }

  const setAnswer = (qId, value) => setAnswers(prev => ({ ...prev, [qId]: value }));
  const toggleMultiple = (qId, option) => {
    setAnswers(prev => {
      const current = prev[qId] || [];
      return { ...prev, [qId]: current.includes(option) ? current.filter(o => o !== option) : [...current, option] };
    });
  };

  // FIX (2026-10-02) : option "Obligatoire" par question (comme les questionnaires prospect). Une question
  // sans ce réglage (questionnaires créés avant) reste obligatoire, comme elle l'a toujours été.
  const isComplete = questions.every(q => {
    if (q.obligatoire === false) return true;
    if (q.type === 'text') return (answers[q.id] || '').trim().length > 0;
    if (q.type === 'single') return !!answers[q.id];
    if (q.type === 'multiple') return (answers[q.id] || []).length > 0;
    return true;
  });

  const computeQuizScore = () => {
    const notees = questions.filter(q => qEstNotee(q, isQuiz));
    if (!isQuiz || notees.length === 0) return null;
    let correctCount = 0;
    notees.forEach(q => {
      const answer = answers[q.id];
      if (q.type === 'single') {
        if (answer !== undefined && answer === q.correctAnswer) correctCount++;
      } else if (q.type === 'multiple') {
        const correct = Array.isArray(q.correctAnswer) ? q.correctAnswer : [];
        const given = Array.isArray(answer) ? answer : [];
        if (correct.length > 0 && correct.length === given.length && correct.every(o => given.includes(o))) correctCount++;
      }
    });
    const scorePercent = Math.round((correctCount / notees.length) * 100);
    const passed = scorePercent >= (meta.seuilReussite ?? 50);
    return { scorePercent, passed, correctCount, total: notees.length };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const scoreInfo = computeQuizScore();
    try {
      await onSubmit(answers, scoreInfo);
      if (isQuiz && scoreInfo) setResult(scoreInfo);
      else if (meta.closingMessage) setResult('done');
      else onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const isQuizResult = isQuiz && result !== 'done';
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-[32px] w-full max-w-md shadow-2xl overflow-hidden">
          <div className={`p-8 text-white text-center ${isQuizResult ? (result.passed ? 'bg-emerald-600' : 'bg-red-500') : 'bg-violet-600'}`}>
            {isQuizResult ? (
              <>
                <p className="text-5xl mb-2">{result.passed ? '✅' : '❌'}</p>
                <h2 className="text-2xl font-black">{result.passed ? 'Acquis' : 'Non acquis'}</h2>
                <p className="text-white/80 text-sm mt-1">{result.correctCount}/{result.total} bonnes réponses · {result.scorePercent}%</p>
              </>
            ) : (
              <>
                <p className="text-5xl mb-2">🎉</p>
                <h2 className="text-2xl font-black">Merci !</h2>
              </>
            )}
          </div>
          {meta.closingMessage && (
            <div className="p-6 text-center text-gray-700 text-sm whitespace-pre-wrap">{meta.closingMessage}</div>
          )}
          <div className="p-6 pt-0">
            <button onClick={onClose} className="w-full bg-gray-900 hover:bg-black text-white font-black py-3.5 rounded-2xl transition-all">Fermer</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-violet-600 p-6 text-white relative shrink-0">
          <h2 className="text-xl font-black flex items-center gap-2">📝 {questionnaire.titre}</h2>
          <p className="text-violet-200 text-sm mt-1">{questions.length} question{questions.length > 1 ? 's' : ''} à compléter</p>
          <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-all">✕</button>
          {meta.description && (
            <p className="text-violet-100 text-xs mt-3 bg-white/10 rounded-xl p-3 whitespace-pre-wrap">{meta.description}</p>
          )}
        </div>
        {/* Questions */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {questions.map((q, qi) => (
            <div key={q.id} className="space-y-3">
              <p className="font-bold text-gray-900 text-sm leading-relaxed">
                <span className="text-violet-500 font-black mr-1">{qi + 1}.</span>
                {q.text || <span className="text-gray-400 italic">Question</span>}
                {q.obligatoire === false
                  ? <span className="text-gray-400 font-normal text-xs ml-1.5">(facultatif)</span>
                  : <span className="text-red-500 ml-1">*</span>}
              </p>
              {q.type === 'text' && (
                <textarea
                  placeholder="Votre réponse..."
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none outline-none focus:ring-2 focus:ring-violet-400 transition-all"
                  rows={3}
                />
              )}
              {q.type === 'single' && (
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => (
                    <label key={oi} onClick={() => setAnswer(q.id, opt)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${answers[q.id] === opt ? 'border-violet-500 bg-violet-50' : 'border-gray-100 hover:border-violet-200 bg-gray-50'}`}>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${answers[q.id] === opt ? 'border-violet-600' : 'border-gray-300'}`}>
                        {answers[q.id] === opt && <div className="w-2 h-2 bg-violet-600 rounded-full"></div>}
                      </div>
                      <span className="text-sm text-gray-700 select-none">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.type === 'multiple' && (
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => {
                    const checked = (answers[q.id] || []).includes(opt);
                    return (
                      <label key={oi} onClick={() => toggleMultiple(q.id, opt)}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${checked ? 'border-violet-500 bg-violet-50' : 'border-gray-100 hover:border-violet-200 bg-gray-50'}`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${checked ? 'border-violet-600 bg-violet-600' : 'border-gray-300'}`}>
                          {checked && <span className="text-white text-[11px] font-black leading-none">✓</span>}
                        </div>
                        <span className="text-sm text-gray-700 select-none">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Footer */}
        <div className="p-6 border-t border-gray-100 shrink-0">
          <button onClick={handleSubmit} disabled={!isComplete || submitting}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white font-black py-4 rounded-2xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? '⏳ Envoi en cours…' : '✓ Soumettre mes réponses'}
          </button>
          {!isComplete && <p className="text-xs text-gray-400 text-center mt-2">Répondez à toutes les questions obligatoires (*) pour soumettre</p>}
        </div>
      </div>
    </div>
  );
};

// SIGNATURE DE GROUPE (2026-10-23) : les documents d'un groupe à la suite (comme un seul document de
// plusieurs pages), puis une seule signature posée sur chacun.
const chargerPdfJs = () => new Promise((resolve, reject) => {
  if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
  const s = window.document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; resolve(window.pdfjsLib); };
  s.onerror = () => reject(new Error('PDF.js indisponible'));
  window.document.head.appendChild(s);
});
const SignatureGroupeModal = ({ titre, lots = [], preparation = null, onClose, onSigner }) => {
  // lots[i] = { doc, resource, blob, champs: { cases: [], textes: [] }, conservation: bool }
  const [pages, setPages] = React.useState({}); // index du lot → { images: [], erreur }
  const [saisies, setSaisies] = React.useState({}); // index du lot → { coches: [], textes: {}, choix }
  const [auBout, setAuBout] = React.useState(false);
  const [coche, setCoche] = React.useState(false);
  const [aSigne, setASigne] = React.useState(false);
  const [envoi, setEnvoi] = React.useState(null); // index en cours
  const zoneRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const dessin = React.useRef(false);
  const n = lots.length;

  React.useEffect(() => {
    if (!n) return undefined;
    let annule = false;
    (async () => {
      let pdfjs = null;
      try { pdfjs = await chargerPdfJs(); } catch (_) { pdfjs = null; }
      for (let i = 0; i < lots.length; i++) {
        const lot = lots[i];
        try {
          if (!pdfjs) throw new Error('lecteur indisponible');
          let octets;
          if (lot.blob) octets = await lot.blob.arrayBuffer();
          else {
            const url = lot.resource && (lot.resource.file_url || lot.resource.url);
            if (!url || /\.(docx|doc)$/i.test(String(url).split('?')[0])) throw new Error('aperçu indisponible');
            const r = await fetch(url); if (!r.ok) throw new Error('HTTP ' + r.status);
            octets = await r.arrayBuffer();
          }
          const pdf = await pdfjs.getDocument({ data: new Uint8Array(octets) }).promise;
          const images = [];
          for (let p = 1; p <= pdf.numPages; p++) {
            const page = await pdf.getPage(p);
            const vp = page.getViewport({ scale: 1.4 });
            const c = window.document.createElement('canvas');
            c.width = vp.width; c.height = vp.height;
            await page.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
            images.push(c.toDataURL('image/jpeg', 0.85));
          }
          if (!annule) setPages(prev => ({ ...prev, [i]: { images } }));
        } catch (e) {
          if (!annule) setPages(prev => ({ ...prev, [i]: { images: [], erreur: e.message } }));
        }
      }
    })();
    return () => { annule = true; };
  }, [lots, n]);

  const saisie = (i) => saisies[i] || { coches: [], textes: {}, choix: null };
  const majSaisie = (i, change) => setSaisies(prev => ({ ...prev, [i]: { ...(prev[i] || { coches: [], textes: {}, choix: null }), ...change(prev[i] || { coches: [], textes: {}, choix: null }) } }));
  const basculerCase = (i, k) => majSaisie(i, s => ({ coches: s.coches.includes(k) ? s.coches.filter(x => x !== k) : [...s.coches, k] }));
  // Ce qui manque avant de pouvoir signer : une case cochée par document qui en a, le choix de conservation.
  const manquants = lots.map((lot, i) => {
    const s = saisie(i);
    if (lot.conservation && !s.choix) return `${lot.doc.nom} : choisissez si vos documents peuvent être conservés`;
    const cases = (lot.champs && lot.champs.cases) || [];
    if (cases.length && !s.coches.length) return `${lot.doc.nom} : cochez au moins une case`;
    return null;
  }).filter(Boolean);

  const toutCharge = n > 0 && Object.keys(pages).length === n;
  const verifierBout = () => {
    const el = zoneRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) setAuBout(true);
  };
  React.useEffect(() => { if (toutCharge) setTimeout(verifierBout, 50); }, [toutCharge]);

  const position = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const debut = (e) => { if (envoi !== null) return; dessin.current = true; const ctx = canvasRef.current.getContext('2d'); const p = position(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const trace = (e) => { if (!dessin.current) return; const ctx = canvasRef.current.getContext('2d'); ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.strokeStyle = '#111827'; const p = position(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setASigne(true); e.preventDefault(); };
  const fin = () => { dessin.current = false; };
  const effacer = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setASigne(false); };
  const signer = async () => {
    if (!auBout || !coche || !aSigne || manquants.length || envoi !== null) return;
    setEnvoi(0);
    const valeurs = lots.map((_, i) => {
      const s = saisie(i);
      return { coches: new Set(s.coches), textes: { ...s.textes }, choix: s.choix };
    });
    try { await onSigner(canvasRef.current.toDataURL('image/png'), (i) => setEnvoi(i), valeurs); }
    finally { setEnvoi(null); }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[150] flex items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label={`Signer ${titre}`}>
      <div className="bg-white w-full sm:max-w-3xl h-full sm:h-[94vh] sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h3 className="font-black text-gray-900 break-words">📁 {titre}</h3>
            <p className="text-xs text-gray-500">
              {preparation ? `Préparation des documents… ${preparation.fait}/${preparation.total}` : `${n} document${n > 1 ? 's' : ''} à lire puis à signer une seule fois`}
            </p>
          </div>
          <button onClick={onClose} disabled={envoi !== null} className="shrink-0 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 disabled:opacity-40" aria-label="Fermer">✕</button>
        </div>
        <div ref={zoneRef} onScroll={verifierBout} className="flex-1 overflow-y-auto bg-gray-100 px-3 sm:px-6 py-4 space-y-4">
          {preparation && <p className="text-center text-sm text-gray-500 py-10">Préparation de vos documents…</p>}
          {lots.map((lot, i) => {
            const s = saisie(i);
            const cases = (lot.champs && lot.champs.cases) || [];
            const textes = (lot.champs && lot.champs.textes) || [];
            return (
            <div key={lot.doc.id} className="space-y-3">
              <div className="sticky top-0 z-10 bg-violet-600 text-white text-xs font-bold px-3 py-2 rounded-xl shadow">
                Document {i + 1}/{n} — {lot.doc.nom}
                {(cases.length > 0 || textes.length > 0) && <span className="font-medium opacity-90"> · à compléter sur le document</span>}
              </div>
              {!pages[i] && <p className="text-center text-sm text-gray-500 py-8">Chargement…</p>}
              {pages[i] && pages[i].images.map((srcImg, p) => (
                <div key={p} className="relative bg-white shadow-sm rounded overflow-hidden">
                  <img src={srcImg} alt={`${lot.doc.nom} — page ${p + 1}`} className="w-full block" />
                  {cases.filter(f => (f.page || 1) === p + 1).map((f, fi) => {
                    const k = fieldKey(f); const ok = s.coches.includes(k);
                    return (
                      <button key={k || fi} type="button" disabled={envoi !== null} onClick={() => basculerCase(i, k)} aria-label="Case à cocher" aria-pressed={ok}
                        style={{ position: 'absolute', left: `${f.x_percent}%`, top: `${f.y_percent}%`, transform: 'translate(-50%, -50%)', width: 22, height: 22 }}
                        className={`rounded-sm border-2 shadow-lg flex items-center justify-center ${ok ? 'bg-violet-600 border-violet-700' : 'bg-white/80 border-violet-500'}`}>
                        {ok && <span className="text-white text-[11px] font-black leading-none">✓</span>}
                      </button>
                    );
                  })}
                  {textes.filter(f => (f.page || 1) === p + 1).map((f, fi) => {
                    const k = fieldKey(f);
                    return (
                      <textarea key={k || `t${fi}`} rows={1} disabled={envoi !== null} placeholder="Écrire ici…" aria-label="Champ à remplir"
                        value={s.textes[k] || ''} onChange={e => { const v = e.target.value; majSaisie(i, prev => ({ textes: { ...prev.textes, [k]: v } })); }}
                        style={{ position: 'absolute', left: `${f.x_percent}%`, top: `${f.y_percent}%`, transform: 'translate(0%, -50%)', width: `${f.width_percent || 30}%`, height: `${Math.max(2.2, f.height_percent || 3)}%`, resize: 'none', overflow: 'hidden', fontSize: 12, lineHeight: 1.2 }}
                        className="bg-yellow-50/90 border border-gray-400 rounded px-1 text-gray-900 outline-none focus:border-violet-600" />
                    );
                  })}
                </div>
              ))}
              {pages[i] && pages[i].erreur && (
                <div className="bg-white rounded-xl p-4 text-sm text-gray-600">
                  Aperçu indisponible pour ce document.{' '}
                  {(lot.resource && (lot.resource.file_url || lot.resource.url)) && (
                    <a href={lot.resource.file_url || lot.resource.url} target="_blank" rel="noopener noreferrer" className="font-bold text-violet-700 underline">Ouvrir le document</a>
                  )}
                  {(cases.length > 0 || textes.length > 0) && <p className="text-xs text-amber-700 mt-2">Ce document contient des champs à remplir : signez-le plutôt seul, avec le bouton « Signer » de la liste.</p>}
                </div>
              )}
              {lot.conservation && (
                <div className="bg-white rounded-xl border-2 border-violet-200 p-4 space-y-2">
                  <p className="text-sm font-bold text-gray-900">Votre choix pour la conservation de vos documents</p>
                  {[['conserve', "J'autorise la conservation de mes documents pendant une durée maximale de 3 ans à compter de la fin de la prestation."],
                    ['detruire', 'Je ne souhaite pas que mes documents soient conservés : ils seront détruits à la clôture de la prestation.']].map(([v, texte]) => (
                    <label key={v} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${s.choix === v ? 'border-violet-400 bg-violet-50' : 'border-gray-100'}`}>
                      <input type="radio" name={`conservation-${i}`} checked={s.choix === v} disabled={envoi !== null} onChange={() => majSaisie(i, () => ({ choix: v }))} className="mt-0.5 accent-violet-600 shrink-0" />
                      <span className="text-sm text-gray-700">{texte}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            );
          })}
          {toutCharge && <p className="text-center text-xs text-gray-400 pb-2">— Fin des documents —</p>}
        </div>
        {!preparation && n > 0 && (
          <div className="border-t border-gray-100 p-4 space-y-3 shrink-0 bg-white">
            {!auBout && <p className="text-xs font-bold text-amber-700 bg-amber-50 rounded-lg px-3 py-2">Faites défiler jusqu'à la fin des documents pour pouvoir signer.</p>}
            {auBout && manquants.length > 0 && (
              <div className="text-xs font-bold text-amber-800 bg-amber-50 rounded-lg px-3 py-2 space-y-0.5">
                <p>Avant de signer :</p>
                {manquants.map(m => <p key={m} className="font-medium">• {m}</p>)}
              </div>
            )}
            {auBout && (<>
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input type="checkbox" checked={coche} disabled={envoi !== null} onChange={e => setCoche(e.target.checked)} className="mt-0.5 w-4 h-4 accent-violet-600 shrink-0" />
              <span>J'ai lu les {n} documents ci-dessus et j'accepte de les signer électroniquement.</span>
            </label>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-gray-500">Votre signature (une seule fois)</span>
                <button onClick={effacer} disabled={envoi !== null} className="text-xs font-bold text-gray-400 hover:text-gray-700">Effacer</button>
              </div>
              <canvas ref={canvasRef} width={600} height={160}
                onPointerDown={debut} onPointerMove={trace} onPointerUp={fin} onPointerLeave={fin}
                className="w-full h-28 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 touch-none" />
            </div>
            </>)}
            <button onClick={signer} disabled={!auBout || !coche || !aSigne || manquants.length > 0 || envoi !== null}
              className="w-full py-3 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {envoi !== null ? `Signature ${Math.min(envoi + 1, n)}/${n}…` : `Signer les ${n} documents`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// SIGNATURE EN LOT — ÉQUIPE (2026-10-23) : formateur et organisme signent un groupe de documents d'un
// même client en une seule fois (groupes retrouvés grâce au modèle de la bibliothèque ; un document
// rangé dans plusieurs groupes apparaît dans chacun).
const groupesDuDocumentEnvoye = (doc, tousDocuments) => {
  const nom = String((doc && doc.nom) || '');
  let meilleur = null;
  (tousDocuments || []).forEach(l => {
    if (l.user_id || l.assigned_formateur_id || !l.nom) return;
    if (!(nom === l.nom || nom.startsWith(l.nom + ' - '))) return;
    const ids = [l.group_id, ...(Array.isArray(l.group_ids) ? l.group_ids : [])].filter(v => v != null).map(String);
    if (!ids.length) return;
    if (!meilleur || l.nom.length > meilleur.long) meilleur = { ids: Array.from(new Set(ids)), long: l.nom.length };
  });
  return meilleur ? meilleur.ids : [];
};
const champsDuRole = (doc, role) => {
  const meta = parseDocMetadata(doc);
  const champs = (Array.isArray(meta.signature_fields) && meta.signature_fields.length) ? meta.signature_fields
    : (Array.isArray(meta.template_fields) && meta.template_fields.length) ? meta.template_fields : (Array.isArray(meta.fields) ? meta.fields : []);
  const tag = (c) => String((c && c.tag) || '');
  return {
    cases: champs.filter(c => tag(c) === `checkbox_${role}` || (c && c.field_type === 'checkbox' && tag(c).includes(role))),
    textes: champs.filter(c => tag(c) === `texte_${role}` || (c && c.field_type === 'text_input' && tag(c).includes(role))),
  };
};
const docEnAttenteDe = (d, role) => {
  if (role === 'formateur') return docAwaitsFormateurSignature(d);
  if (role === 'organisme') return (parseDocMetadata(d).destination_roles || []).includes('organisme') && !d.signe_par_organisme && docRequiresSignature(d);
  return false;
};
const useNomsGroupes = (orgId) => {
  const [noms, setNoms] = React.useState({});
  React.useEffect(() => {
    if (!orgId) return undefined;
    let annule = false;
    supabase.from('document_groups').select('id, nom').eq('organisation_id', orgId)
      .then(({ data }) => { if (!annule && Array.isArray(data)) setNoms(Object.fromEntries(data.map(g => [String(g.id), g.nom]))); })
      .catch(() => {});
    return () => { annule = true; };
  }, [orgId]);
  return noms;
};
const PanneauSignatureEnLot = ({ docs = [], role, tousDocuments = [], nomsGroupes = {}, libelleClient = null, handleSignDocument, fetchDocuments }) => {
  const [actif, setActif] = React.useState(null);
  const groupes = React.useMemo(() => {
    const m = new Map();
    (docs || []).forEach(d => {
      if (!d.user_id || !docEnAttenteDe(d, role) || isBlockedBySigningOrder(d, role)) return;
      groupesDuDocumentEnvoye(d, tousDocuments).forEach(gid => {
        const cle = `${d.user_id}|${gid}`;
        if (!m.has(cle)) m.set(cle, { cle, gid, clientId: d.user_id, docs: [] });
        m.get(cle).docs.push(d);
      });
    });
    return Array.from(m.values()).filter(g => g.docs.length >= 2);
  }, [docs, role, tousDocuments]);
  if (!groupes.length && !actif) return null;
  const titreDe = (g) => {
    const nom = nomsGroupes[g.gid] || 'Groupe de documents';
    const c = libelleClient ? libelleClient(g.clientId) : '';
    return c ? `${nom} — ${c}` : nom;
  };
  const ouvrir = (g) => setActif({ titre: titreDe(g), docs: g.docs, lots: g.docs.map(d => ({ doc: d, resource: { file_url: d.signed_pdf_url || d.url }, blob: null, champs: champsDuRole(d, role) })) });
  const signer = async (signature, progression, valeurs = []) => {
    const lot = actif;
    if (!lot) return;
    const echecs = [];
    for (let i = 0; i < lot.docs.length; i++) {
      if (progression) progression(i);
      const d = lot.docs[i];
      const v = valeurs[i] || { coches: new Set(), textes: {} };
      let ok = false;
      try { ok = await handleSignDocument(d.id, role, signature, v.coches, v.textes); } catch (e) { console.error('[signature en lot]', d.nom, e); }
      if (!ok) echecs.push(d.nom);
    }
    if (fetchDocuments) await fetchDocuments();
    const n = lot.docs.length - echecs.length;
    if (n) toast.success(`✅ ${n} document${n > 1 ? 's' : ''} signé${n > 1 ? 's' : ''} en une fois.`, { duration: 6000 });
    if (echecs.length) toast.error(`Non signé${echecs.length > 1 ? 's' : ''} : ${echecs.join(', ')}. Réessayez avec le bouton « Signer » de ${echecs.length > 1 ? 'ces documents' : 'ce document'}.`, { duration: 10000 });
    setActif(null);
  };
  return (
    <>
      {groupes.length > 0 && (
        <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4 space-y-2">
          <p className="text-sm font-black text-violet-900">Signer en une seule fois</p>
          {groupes.map(g => (
            <div key={g.cle} className="bg-white rounded-xl border border-violet-100 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="text-sm font-bold text-gray-800 min-w-0 break-words">📁 {titreDe(g)} <span className="text-xs font-medium text-gray-400">· {g.docs.length} documents</span></span>
              <button onClick={() => ouvrir(g)} disabled={!!actif}
                className="shrink-0 px-3 py-2 bg-violet-700 hover:bg-violet-800 text-white text-xs font-black rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50">
                <PenTool size={13} /> Lire et signer les {g.docs.length} documents
              </button>
            </div>
          ))}
        </div>
      )}
      {actif && <SignatureGroupeModal titre={actif.titre} lots={actif.lots} onClose={() => setActif(null)} onSigner={signer} />}
    </>
  );
};

const ClientDocumentsView = ({ supabase, currentUserId, clients, documents, fetchDocuments, formateurs, orgSettings, sessions, fetchSessions }) => {
  const [moduleResources, setModuleResources] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [signingResource, setSigningResource] = React.useState(null);
  const [prefilledSignUrl, setPrefilledSignUrl] = React.useState(null);
  const prefilledSignBlob = React.useRef(null);
  // URL blob: (et non plus data:) utilisée pour l'aperçu — les data: URLs base64 volumineuses
  // peuvent ne PAS s'afficher du tout dans le lecteur PDF intégré de Chrome (page blanche silencieuse,
  // sans la moindre erreur console) ; blob: est déjà la méthode utilisée ailleurs dans l'app (ex :
  // handlePreview de l'éditeur de balises) et fonctionne de façon fiable. On garde une réf pour
  // pouvoir révoquer l'URL précédente et éviter une fuite mémoire.
  const prefilledSignBlobUrlRef = React.useRef(null);
  // Cases à cocher requises avant signature côté client (extraites du template en cours de signature)
  const [signingCheckboxFields, setSigningCheckboxFields] = React.useState([]);
  const isClientCheckboxField = (f) => (f.tag || '') === 'checkbox_client' || (f.field_type === 'checkbox' && (f.tag || '').includes('client'));
  // Champs "texte libre" à remplir par le client avant signature (extraits du template en cours de signature)
  const [signingTextFields, setSigningTextFields] = React.useState([]);
  const isClientTextField = (f) => (f.tag || '') === 'texte_client' || (f.field_type === 'text_input' && (f.tag || '').includes('client'));
  // Le document exige-t-il une signature manuscrite du client ? Par défaut oui (comportement
  // historique, pour les documents sans balises visuelles) — passe à false uniquement quand on a pu
  // résoudre les champs du template ET qu'aucun d'eux n'est une balise "signature_client" (sinon on
  // forçait une signature qui n'a ensuite nulle part où s'accrocher dans le PDF final — bug constaté
  // 2026-07-24 : "il n'y a pas de balise signature... alors le document a demandé une signature").
  const [signingRequiresSignature, setSigningRequiresSignature] = React.useState(true);
  const isClientSignatureField = (f) => (f.tag || '') === 'signature_client' || (f.field_type === 'signature' && (f.tag || '').includes('client'));
  const [viewingResource, setViewingResource] = React.useState(null);
  const [, setDebugInfo] = React.useState(null);
  const [expandedGroupId, setExpandedGroupId] = React.useState(null);
  const [groupNames, setGroupNames] = React.useState({}); // { groupId: nom }
  const [isConservationModalOpen, setIsConservationModalOpen] = React.useState(false);
  const [conservationTargetDoc, setConservationTargetDoc] = React.useState(null);
  const [questionnaireResponses, setQuestionnaireResponses] = React.useState([]);
  const [activeQuestionnaire, setActiveQuestionnaire] = React.useState(null);
  const [groupQuestionnaires, setGroupQuestionnaires] = React.useState([]);
  // AJOUT (2026-10-02) : questionnaires / quiz envoyés directement à ce client par son organisme (hors module)
  const [assignedQuestionnaires, setAssignedQuestionnaires] = React.useState([]);
  React.useEffect(() => {
    if (!currentUserId || !supabase) return;
    (async () => {
      const { data: assigns, error } = await supabase.from('questionnaire_assignments').select('questionnaire_id').eq('client_id', currentUserId);
      if (error || !assigns || assigns.length === 0) { setAssignedQuestionnaires([]); return; }
      const { data: qs } = await supabase.from('module_step_resources').select('id, titre, metadata, type')
        .in('id', assigns.map(x => x.questionnaire_id));
      setAssignedQuestionnaires((qs || []).map(q => ({ ...q, type: 'questionnaire' })));
    })();
  }, [currentUserId, supabase]);
  // AJOUT (2026-09-18) : ressources de type "exercice" posées en Documents de début/fin — sorties de
  // moduleResources (qui n'affiche plus que document/questionnaire/document_group, voir plus bas) et
  // traitées à part pour être basculées vers l'onglet "Exercices" du client (voir l'effet plus bas).
  const [moduleExerciceResources, setModuleExerciceResources] = React.useState([]);

  // Sécurité : on cherche UNIQUEMENT le client dont l'id correspond à l'utilisateur connecté
  const currentClient = React.useMemo(() => (clients || []).find(c => String(c.id) === String(currentUserId)), [clients, currentUserId]);

  // Charger les réponses questionnaire du client
  React.useEffect(() => {
    if (!currentUserId) return;
    supabase.from('questionnaire_responses').select('*').eq('client_id', currentUserId)
      .then(({ data }) => { if (data) setQuestionnaireResponses(data); });
  }, [currentUserId, supabase]);
  const moduleId = currentClient?.module_id;

  // AJOUT (2026-09-18) : le parcours est-il terminé pour CE client ? Détermine si les documents,
  // questionnaires et exercices de FIN de parcours doivent être accessibles (voir isModuleCompletedForClient
  // plus haut). Les ressources de DÉBUT restent, elles, toujours accessibles dès l'assignation du module.
  // FIX (2026-09-30) : on recharge aussi DIRECTEMENT les séances de ce client (filtre client_id) —
  // la liste globale `sessions` est filtrée sur organisation_id, si bien que des séances anciennes sans
  // organisation_id n'y figuraient pas et que la fin de parcours ne pouvait jamais être détectée.
  const [ownSessions, setOwnSessions] = React.useState([]);
  React.useEffect(() => {
    if (!currentUserId || !supabase) return;
    supabase.from('sessions').select('id, client_id, numero_seance, date, module_id').eq('client_id', currentUserId)
      .then(({ data, error }) => { if (!error && data) setOwnSessions(data); });
  }, [currentUserId, supabase, sessions]);
  const moduleCompleted = React.useMemo(() => {
    const byId = new Map();
    [...(sessions || []), ...ownSessions].forEach(s => { if (s && s.id != null) byId.set(s.id, { ...(byId.get(s.id) || {}), ...s }); });
    return isModuleCompletedForClient(currentUserId, Array.from(byId.values()), moduleDuClient(clients, currentUserId));
  }, [currentUserId, sessions, ownSessions, clients]);
  // LOT 3 (2026-10-20) : nom de la formation et séances du client, pour remplir les modèles
  // (formation, date de début, date de fin) — avant : toujours vides.
  const [moduleNomClient, setModuleNomClient] = React.useState('');
  React.useEffect(() => {
    if (!moduleId || !supabase) return;
    supabase.from('modules').select('nom').eq('id', moduleId).maybeSingle()
      .then(({ data }) => setModuleNomClient(data?.nom || ''), () => {});
  }, [moduleId, supabase]);
  const seancesDuClient = React.useMemo(
    () => [...(sessions || []), ...ownSessions].filter(x => String(x.client_id) === String(currentUserId)),
    [sessions, ownSessions, currentUserId]);

  React.useEffect(() => {
    if (!moduleId) { setLoading(false); return; }
    (async () => {
      try {
        // Pas de filtre type dans la requête → filtre JS (évite problèmes NULL + PostgREST OR syntax)
        const { data, error } = await supabase
          .from('module_step_resources')
          .select('id, titre, file_url, moment, metadata, type, ordre, document_group_id')
          .eq('module_id', moduleId)
          .in('moment', ['debut', 'fin'])
          .order('ordre', { ascending: true });

        console.log('[ClientDocumentsView] module_step_resources résultat:', { count: data?.length, error, moduleId, data });

        if (error) {
          console.error('[ClientDocumentsView] Erreur requête:', error);
          setDebugInfo({ error: error.message, moduleId });
        } else {
          // Filtre JS : exclure les signatures (inclut les NULL et document_group) ET les exercices —
          // ces derniers sont désormais traités séparément (voir moduleExerciceResources plus bas) pour
          // n'apparaître QUE dans l'onglet "Exercices" du client, plus dans "Mes Documents" (décision
          // 2026-09-18, pour éviter le doublon signalé par l'utilisateur).
          const filtered = (data || []).filter(r => r.type !== 'signature' && r.type !== 'exercice');
          console.log('[ClientDocumentsView] après filtre signature:', filtered.length, 'ressources');
          setModuleResources(filtered);
          setModuleExerciceResources((data || []).filter(r => r.type === 'exercice'));
          setDebugInfo({ count: filtered.length, moduleId, rawCount: data?.length });

          // ─── Résolution des noms de groupes via document_groups ───
          const groupIds = filtered
            .filter(r => r.type === 'document_group' && r.document_group_id)
            .map(r => r.document_group_id);
          if (groupIds.length > 0) {
            const { data: grpData } = await supabase
              .from('document_groups')
              .select('id, nom')
              .in('id', groupIds);
            if (grpData) {
              const nameMap = {};
              grpData.forEach(g => { nameMap[g.id] = g.nom; });
              setGroupNames(nameMap);
              console.log('[ClientDocumentsView] noms groupes résolus:', nameMap);
            }
            // Charger les questionnaires liés à ces groupes (templates module_id IS NULL)
            const { data: questData } = await supabase
              .from('module_step_resources')
              .select('id, titre, metadata, document_group_id')
              .eq('type', 'questionnaire')
              .is('module_id', null)
              .in('document_group_id', groupIds);
            if (questData) setGroupQuestionnaires(questData);
          }
        }
      } catch (e) {
        console.error('[ClientDocumentsView] Exception:', e);
        setDebugInfo({ error: e.message, moduleId });
      }
      setLoading(false);
    })();
  }, [moduleId, supabase, currentUserId]);

  // AJOUT (2026-09-18) : bascule les exercices posés en "Documents de début/fin" (module_step_resources)
  // vers l'onglet "Exercices" du client (ExercicesView, qui ne lit que la table `sessions`) — jusqu'ici
  // ils n'y apparaissaient jamais, quel que soit le module assigné. On crée donc, pour chaque exercice
  // éligible, une séance "synthétique" (numero_seance: null — pour la distinguer des vraies séances du
  // calendrier et ne pas fausser isModuleCompletedForClient) : les exercices de DÉBUT sont créés dès
  // que le module est chargé, ceux de FIN uniquement une fois le parcours détecté "terminé". Protégé
  // contre les doublons par le titre (comme generateSessions le fait déjà pour les vraies séances).
  React.useEffect(() => {
    if (!currentUserId || !moduleId || !supabase || moduleExerciceResources.length === 0) return;
    const eligible = moduleExerciceResources.filter(res => res.moment === 'debut' || moduleCompleted);
    if (eligible.length === 0) return;
    (async () => {
      try {
        const { data: existing, error: existErr } = await supabase
          .from('sessions').select('nom').eq('client_id', currentUserId);
        if (existErr) { console.error('[ClientDocumentsView] Erreur vérification exercices existants:', existErr); return; }
        const existingNames = new Set((existing || []).map(s => s.nom));
        const toCreate = eligible.filter(res => !existingNames.has(res.titre));
        if (toCreate.length === 0) return;
        const rows = toCreate.map(res => {
          const meta = typeof res.metadata === 'string' && res.metadata.startsWith('{')
            ? (() => { try { return JSON.parse(res.metadata); } catch { return {}; } })()
            : (res.metadata || {});
          return {
            client_id: currentUserId,
            module_id: moduleId,
            numero_seance: null,
            nom: res.titre,
            type_activite: 'exercice',
            ressource_titre: res.titre,
            file_url: res.file_url || null,
            metadata: { ...meta, moment: res.moment },
            statut: 'À venir',
            statut_client: 'À venir',
            statut_formateur: 'À venir',
            organisation_id: currentClient?.organisation_id || null,
          };
        });
        const { error: insertErr } = await supabase.from('sessions').insert(rows);
        if (insertErr) { console.error('[ClientDocumentsView] Erreur création exercices début/fin:', insertErr); return; }
        if (typeof fetchSessions === 'function') await fetchSessions();
      } catch (e) {
        console.error('[ClientDocumentsView] Exception création exercices début/fin:', e);
      }
    })();
  }, [moduleExerciceResources, moduleCompleted, currentUserId, moduleId, supabase, currentClient, fetchSessions]);

  // ─── Source 2 : documents per-client déjà dans la table documents ───
  // Capture les docs assignés manuellement ou via l'ancienne méthode de synchronisation
  const clientVisibleDocs = React.useMemo(() => {
    const myDocs = (documents || []).filter(d =>
      String(d.user_id) === String(currentUserId) && d.visible_client
    );
    return myDocs;
  }, [documents, currentUserId]);

  // Évite les doublons : titres des ressources du module + titres des docs à l'intérieur des groupes
  const allKnownTitles = React.useMemo(() => {
    const titles = new Set(moduleResources.map(r => r.titre));
    // Ajouter les titres des documents dans chaque groupe
    moduleResources
      .filter(r => r.type === 'document_group' && r.document_group_id)
      .forEach(r => {
        (documents || [])
          .filter(d => d.group_id === r.document_group_id)
          .forEach(d => { if (d.nom) titles.add(d.nom); });
      });
    return titles;
  }, [moduleResources, documents]);

  // LOT 3 (2026-10-20) : un document est « de fin » s'il est marqué comme tel, ou (anciens documents Word
  // « Titre - Client ») si son nom correspond à une ressource de fin de parcours du module.
  const titresDeFin = React.useMemo(() => moduleResources.filter(r => r.moment === 'fin').map(r => r.titre).filter(Boolean), [moduleResources]);
  const estDocDeFin = React.useCallback((d) => {
    if (docDeFinVerrouille(d, false)) return true;
    return titresDeFin.some(t => d.nom === t || String(d.nom || '').startsWith(`${t} - `));
  }, [titresDeFin]);
  const extraDebutDocs = React.useMemo(() => clientVisibleDocs.filter(d => {
    if (allKnownTitles.has(d.nom)) return false;
    if (estDocDeFin(d)) return false; // lot 3 : jamais dans « début »
    if (isBlockedBySigningOrder(d, 'client')) return false; // pas encore son tour (mode séquentiel) → masqué
    const meta = typeof d.metadata === 'object' && d.metadata !== null ? d.metadata :
      (typeof d.metadata === 'string' && d.metadata?.startsWith('{') ? (() => { try { return JSON.parse(d.metadata); } catch { return {}; } })() : {});
    // Considère "debut" si moment=debut OU si c'est un type administratif sans moment précisé
    return meta.moment === 'debut' || (!meta.moment && (d.type_document === 'À signer' || d.type_document === 'Contrat' || d.type_document === 'Convention'));
  }), [clientVisibleDocs, allKnownTitles, estDocDeFin]);

  const extraFinDocs = React.useMemo(() => clientVisibleDocs.filter(d => {
    if (allKnownTitles.has(d.nom)) return false;
    if (isBlockedBySigningOrder(d, 'client')) return false; // pas encore son tour (mode séquentiel) → masqué
    const meta = typeof d.metadata === 'object' && d.metadata !== null ? d.metadata :
      (typeof d.metadata === 'string' && d.metadata?.startsWith('{') ? (() => { try { return JSON.parse(d.metadata); } catch { return {}; } })() : {});
    return meta.moment === 'fin' || estDocDeFin(d); // lot 3
  }), [clientVisibleDocs, allKnownTitles, estDocDeFin]);

  // Filet de sécurité (ajouté le 2026-09-02) : tout document visible du client qui n'est ni dans
  // extraDebutDocs ni dans extraFinDocs (ex: envoyé via "Ajouts personnalisés" → "Envoyer", qui ne
  // renseigne ni metadata.moment ni un type_document reconnu) — sans ce filet, ces documents
  // comptaient dans le badge "X documents à signer" de la page d'accueil (qui ne filtre que sur
  // visible_client/signe_par_client) mais n'apparaissaient nulle part sur "Mes Documents".
  const extraOtherDocs = React.useMemo(() => {
    const shownIds = new Set([...extraDebutDocs, ...extraFinDocs].map(d => d.id));
    return clientVisibleDocs.filter(d => {
      if (shownIds.has(d.id)) return false;
      if (allKnownTitles.has(d.nom)) return false;
      if (isBlockedBySigningOrder(d, 'client')) return false;
      return true;
    });
  }, [clientVisibleDocs, extraDebutDocs, extraFinDocs, allKnownTitles]);

  // ─── SIGNATURE DE GROUPE (2026-10-23) ───────────────────────────────────────
  // Groupe(s) de chaque document du client (fonction SQL skorup_groupes_mes_documents ; absente → rien).
  const [groupesMesDocs, setGroupesMesDocs] = React.useState({}); // id document → [{ id, nom }]
  const nbDocsVisibles = clientVisibleDocs.length;
  React.useEffect(() => {
    let annule = false;
    supabase.rpc('skorup_groupes_mes_documents').then(({ data, error }) => {
      if (annule || error || !Array.isArray(data)) return;
      const m = {};
      data.forEach(r => {
        if (!r || r.document_id == null) return;
        const k = String(r.document_id);
        if (!m[k]) m[k] = [];
        if (!m[k].some(g => g.id === String(r.groupe_id))) m[k].push({ id: String(r.groupe_id), nom: r.groupe_nom || 'Documents' });
      });
      setGroupesMesDocs(m);
    }).catch(() => {});
    return () => { annule = true; };
  }, [supabase, currentUserId, nbDocsVisibles]);
  const estConservation = (d) => String((d && d.nom) || '').toLowerCase().includes('conservation');
  const groupesASigner = React.useMemo(() => {
    const parGroupe = new Map();
    clientVisibleDocs.forEach(d => {
      const gs = groupesMesDocs[String(d.id)];
      if (!gs || !gs.length || d.signe_par_client) return;
      if (isBlockedBySigningOrder(d, 'client')) return;
      if (estDocDeFin(d) && !moduleCompleted) return;
      if (!estConservation(d) && !docAwaitsClientSignature(d)) return;
      gs.forEach(g => {
        if (!parGroupe.has(g.id)) parGroupe.set(g.id, { id: g.id, nom: g.nom, docs: [] });
        parGroupe.get(g.id).docs.push(d);
      });
    });
    return Array.from(parGroupe.values()).filter(g => g.docs.length >= 2);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientVisibleDocs, groupesMesDocs, moduleCompleted, estDocDeFin]);
  const [signatureGroupe, setSignatureGroupe] = React.useState(null); // { groupe, lots, preparation }
  const ouvrirSignatureGroupe = async (groupe) => {
    if (signatureGroupe) return;
    setSignatureGroupe({ groupe, lots: [], preparation: { fait: 0, total: groupe.docs.length } });
    const lots = [];
    for (let i = 0; i < groupe.docs.length; i++) {
      const d = groupe.docs[i];
      if (estConservation(d)) {
        lots.push({ doc: d, resource: { file_url: d.signed_pdf_url || d.url }, blob: null, champs: { cases: [], textes: [] }, conservation: true });
      } else {
        const docMeta = (() => { try { return typeof d.metadata === 'string' ? JSON.parse(d.metadata) : (d.metadata || {}); } catch { return {}; } })();
        const resource = { id: d.id, titre: d.nom, file_url: d.url || d.file_url, metadata: { ...docMeta, classification: 'a_signer' } };
        const lot = { doc: d, resource: null, blob: null, cases: [], textes: [], signatureRequise: true };
        try { await handleOpenSigning(resource, lot); } catch (e) { console.warn('[signature groupe] préparation', d.nom, e); }
        if (!lot.resource) lot.resource = resource;
        lot.champs = { cases: lot.cases || [], textes: lot.textes || [] };
        lots.push(lot);
      }
      setSignatureGroupe(s => s && ({ ...s, preparation: { fait: i + 1, total: groupe.docs.length } }));
    }
    toast.dismiss('prefill-sign');
    setSignatureGroupe(s => s && ({ ...s, lots, preparation: null }));
  };
  const signerGroupe = async (signatureDataUrl, onProgression, valeurs = []) => {
    const lots = (signatureGroupe && signatureGroupe.lots) || [];
    const echecs = [];
    const formateurNomClient = (formateurs || []).find(f => String(f.id) === String(currentClient?.formateur_id))?.nom || '';
    for (let i = 0; i < lots.length; i++) {
      if (onProgression) onProgression(i);
      const lot = lots[i];
      const v = valeurs[i] || { coches: new Set(), textes: {}, choix: null };
      let ok = false;
      try {
        if (lot.conservation) {
          await handleConservationSign({
            choice: v.choix, signatureDataUrl,
            clientFullName: currentClient?.nom_complet || [currentClient?.prenom, currentClient?.nom].filter(Boolean).join(' ') || '—',
            formateurNom: formateurNomClient,
            dateDebut: currentClient?.date_debut ? new Date(currentClient.date_debut).toLocaleDateString('fr-FR') : '',
          }, { id: lot.doc.id, titre: lot.doc.nom });
          ok = true;
        } else {
          ok = await handleSignSave(lot.signatureRequise === false ? null : signatureDataUrl, null, v.coches, v.textes, lot);
        }
      } catch (e) { console.error('[signature groupe]', lot.doc.nom, e); }
      if (!ok) echecs.push(lot.doc.nom);
    }
    if (fetchDocuments) await fetchDocuments();
    const n = lots.length - echecs.length;
    if (n) toast.success(`✅ ${n} document${n > 1 ? 's' : ''} signé${n > 1 ? 's' : ''} en une fois.`, { duration: 6000 });
    if (echecs.length) toast.error(`Non signé${echecs.length > 1 ? 's' : ''} : ${echecs.join(', ')}. Réessayez avec le bouton « Signer » de ${echecs.length > 1 ? 'ces documents' : 'ce document'}.`, { duration: 10000 });
    setSignatureGroupe(null);
  };

  // Vérifie si CE client a déjà signé cette ressource (filtre strict sur currentUserId)
  const isSignedByClient = (resource) =>
    (documents || []).some(d => String(d.user_id) === String(currentUserId) && d.nom === resource.titre && d.signe_par_client);

  // ─── Résoudre le vrai template (module_step_resources) depuis le titre ───────
  // Nécessaire car signingResource.id peut être l'id de la table `documents` (per-client)
  // alors que template_fields.template_id est l'id de module_step_resources
  const resolveVisualTemplate = React.useCallback(async (titre) => {
    // Scopé à l'organisme du client courant (+ lignes historiques sans organisation_id) : avant ce
    // correctif, la recherche par titre seul pouvait remonter le modèle visuel d'un AUTRE organisme
    // partageant le même nom de document, exposant potentiellement ses champs/positions de signature.
    const myOrgId = (clients || []).find(c => String(c.id) === String(currentUserId))?.organisation_id || null;
    // Use .limit() instead of .maybeSingle() to handle cases where
    // multiple MSR rows share the same title (standalone template + module-linked copy)
    let rowsQuery = supabase
      .from('module_step_resources')
      .select('id, metadata')
      .eq('titre', titre)
      .eq('type', 'document')
      .limit(10);
    rowsQuery = myOrgId ? rowsQuery.eq('organisation_id', myOrgId) : rowsQuery.limit(0);
    const { data: rows } = await rowsQuery;
    if (!rows || rows.length === 0) return { templateId: null, hasVisualFields: false };
    // Prefer the row that has has_visual_fields: true
    const parseMeta = (r) => { try { return typeof r.metadata === 'string' ? JSON.parse(r.metadata) : (r.metadata || {}); } catch { return {}; } };
    const visualRow = rows.find(r => parseMeta(r).has_visual_fields === true);
    if (!visualRow) return { templateId: null, hasVisualFields: false };
    // Utiliser visual_template_id du metadata si présent (pointe vers le MSR standalone où template_fields sont stockés)
    // sinon fallback sur l'id de la ligne trouvée
    const vizMeta = parseMeta(visualRow);
    const realTemplateId = vizMeta.visual_template_id || visualRow.id;
    return { templateId: realTemplateId, hasVisualFields: true, embeddedFields: vizMeta.template_fields || null };
  }, [supabase, clients, currentUserId]);

  // ─── Nettoyage de la modal de signature ─────────────────────────────────────
  const handleCloseSigningModal = React.useCallback(() => {
    if (prefilledSignBlobUrlRef.current) {
      try { URL.revokeObjectURL(prefilledSignBlobUrlRef.current); } catch (_) {}
      prefilledSignBlobUrlRef.current = null;
    }
    setPrefilledSignUrl(null);
    prefilledSignBlob.current = null;
    setSigningResource(null);
    setSigningCheckboxFields([]);
    setSigningTextFields([]);
    setSigningRequiresSignature(true);
  }, []);

  // ─── Ouverture de la modal de signature : pré-remplit le PDF avec les données client ──
  // → le client voit ses informations dans le document AVANT de signer
  // La modal ne s'ouvre qu'APRÈS le pré-remplissage (évite la race condition)
  // SIGNATURE DE GROUPE (2026-10-23) : avec « lot », le document est préparé exactement de la même façon
  // mais rien ne s'ouvre : le résultat (PDF pré-rempli, champs à compléter) est rangé dans « lot ».
  const handleOpenSigning = React.useCallback(async (resource, lot = null) => {
    const _poserUrl = (u) => { if (!lot) { setPrefilledSignUrl(u); return; } if (u) { try { URL.revokeObjectURL(u); } catch (_) {} } };
    const _poserRefUrl = (u) => { if (!lot) prefilledSignBlobUrlRef.current = u; };
    const _poserBlob = (b) => { if (lot) lot.blob = b; else prefilledSignBlob.current = b; };
    const _poserCases = (v) => { if (lot) lot.cases = v; else setSigningCheckboxFields(v); };
    const _poserTextes = (v) => { if (lot) lot.textes = v; else setSigningTextFields(v); };
    const _poserSigReq = (v) => { if (lot) lot.signatureRequise = v; else setSigningRequiresSignature(v); };
    const _ouvrir = (r) => { if (lot) lot.resource = r; else setSigningResource(r); };
    // Nettoyer l'éventuel précédent pré-remplissage
    if (!lot && prefilledSignBlobUrlRef.current) {
      try { URL.revokeObjectURL(prefilledSignBlobUrlRef.current); } catch (_) {}
      prefilledSignBlobUrlRef.current = null;
    }
    _poserUrl(null);
    _poserBlob(null);
    _poserCases([]);
    _poserTextes([]);
    _poserSigReq(true);

    const toastId = 'prefill-sign';

    // ── Voie rapide : document déjà pré-généré par l'admin pour ce client ──
    // S'il existe un PDF non signé avec le même nom pour ce client, l'utiliser
    // directement comme aperçu (il contient déjà les données personnalisées)
    const existingPregenDoc = (documents || []).find(d =>
      String(d.user_id) === String(currentUserId) &&
      !isInternalDossierDoc(d) &&
      d.nom === resource.titre &&
      d.url &&
      !d.signe_par_client &&
      !/\.(docx|doc)$/i.test((d.url || '').split('?')[0])
    );

    if (existingPregenDoc) {
      const pregenMeta = (() => { try { return typeof existingPregenDoc.metadata === 'string' ? JSON.parse(existingPregenDoc.metadata) : (existingPregenDoc.metadata || {}); } catch { return {}; } })();

      if (pregenMeta.prefilled === true) {
        // ✅ Voie directe : PDF déjà pré-généré côté admin → charger sans aucun overlay
        toast.loading('Chargement du document…', { id: toastId });
        try {
          const resp = await fetch(existingPregenDoc.url);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const pdfBlob = new Blob([await resp.arrayBuffer()], { type: 'application/pdf' });
          _poserBlob(pdfBlob);
          // URL blob: (pas data:) — un data: URL base64 volumineux peut s'afficher en page blanche
          // silencieuse dans le lecteur PDF intégré de Chrome ; blob: est fiable et déjà utilisé
          // ailleurs dans l'app pour prévisualiser des PDF générés côté client.
          const blobUrl = URL.createObjectURL(pdfBlob);
          _poserRefUrl(blobUrl);
          _poserUrl(blobUrl);
          toast.dismiss(toastId);
          _poserCases((pregenMeta.signature_fields || []).filter(isClientCheckboxField));
          _poserTextes((pregenMeta.signature_fields || []).filter(isClientTextField));
          _poserSigReq((pregenMeta.signature_fields || []).some(isClientSignatureField));
          _ouvrir(resource);
          return;
        } catch(e) {
          console.warn('[handleOpenSigning] PDF pré-généré inaccessible, chemin fallback :', e.message);
          toast.dismiss(toastId);
          // Pas de return → continue vers le chemin overlay standard
        }
      }

      if (pregenMeta.fields?.length > 0 && pregenMeta.resolved_values) {
        // ✅ Voie rapide (fallback format précédent) : overlay avec valeurs pré-calculées
        toast.loading('Préparation du document…', { id: toastId });
        try {
          const tplUrl = pregenMeta.template_url || existingPregenDoc.url;
          const resp = await fetch(tplUrl);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          let pdfBlob = new Blob([await resp.arrayBuffer()], { type: 'application/pdf' });
          const dataFields = pregenMeta.fields.filter(f => f.field_type !== 'signature' && f.field_type !== 'checkbox' && f.field_type !== 'text_input' && !(f.tag || '').startsWith('signature_') && !(f.tag || '').startsWith('checkbox_') && !(f.tag || '').startsWith('texte_') && !estDateSignature(f));
          if (dataFields.length > 0) {
            pdfBlob = await overlayFieldsOnPdf(pdfBlob, dataFields, pregenMeta.resolved_values, {});
          }
          _poserBlob(pdfBlob);
          const blobUrl = URL.createObjectURL(pdfBlob);
          _poserRefUrl(blobUrl);
          _poserUrl(blobUrl);
          toast.dismiss(toastId);
          _poserCases((pregenMeta.fields || []).filter(isClientCheckboxField));
          _poserTextes((pregenMeta.fields || []).filter(isClientTextField));
          _poserSigReq((pregenMeta.fields || []).some(isClientSignatureField));
          _ouvrir(resource);
          return;
        } catch(e) {
          console.warn('[handleOpenSigning] Overlay métadonnées échoué, chemin overlay standard :', e.message);
          toast.dismiss(toastId);
        }
      }
      // Pas de données pré-calculées → continue vers le chemin overlay standard
    }

    // ── Voie overlay : tenter le pré-remplissage visuel via pdf-lib ──
    toast.loading('Préparation du document…', { id: toastId });
    try {
      // Priorité 0 : champs embarqués dans resource.metadata (zéro RLS, zéro requête DB)
      // Injectés par handleUploadVisualTemplate via fullMetadataObj.template_fields
      const parseMeta = (m) => { try { return typeof m === 'string' ? JSON.parse(m) : (m || {}); } catch { return {}; } };
      const rMeta = parseMeta(resource.metadata);
      let templateId = rMeta.visual_template_id || null;
      let hasVisualFields = rMeta.has_visual_fields === true;
      let embeddedTplFields = (rMeta.template_fields && rMeta.template_fields.length > 0) ? rMeta.template_fields : null;

      // Priorité 2 : chercher dans la liste documents (template reference) — bypass RLS
      if (!hasVisualFields || !templateId) {
        const refDoc = (documents || []).find(d =>
          (d.nom === resource.titre || d.nom === resource.nom) &&
          d.type_action === 'Modèle Référence'
        );
        const refMeta = parseMeta(refDoc?.metadata);
        if (refMeta.has_visual_fields === true && refMeta.visual_template_id) {
          templateId = refMeta.visual_template_id;
          hasVisualFields = true;
          if (!embeddedTplFields && refMeta.template_fields && refMeta.template_fields.length > 0) {
            embeddedTplFields = refMeta.template_fields;
          }
        }
      }

      // Priorité 3 : résolution via module_step_resources
      if (!hasVisualFields || !templateId) {
        const resolved = await resolveVisualTemplate(resource.titre || resource.nom);
        templateId = resolved.templateId;
        hasVisualFields = resolved.hasVisualFields;
        // Récupérer les champs embarqués depuis le MSR trouvé si pas encore disponibles
        if (!embeddedTplFields && resolved.embeddedFields && resolved.embeddedFields.length > 0) {
          embeddedTplFields = resolved.embeddedFields;
        }
      }

      if ((!hasVisualFields || !templateId) && !embeddedTplFields) {
        // Pas de champs visuels ni de champs embarqués → ouvrir avec le template brut
        toast.dismiss(toastId);
        _ouvrir(resource);
        return;
      }

      const templateUrl = resource.file_url || resource.url;
      const templateIsPdf = /\.pdf$/i.test((templateUrl || '').split('?')[0]);
      const resp = await fetch(templateUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const bytes = await resp.arrayBuffer();

      let pdfBlob;
      if (templateIsPdf) {
        pdfBlob = new Blob([bytes], { type: 'application/pdf' });
      } else {
        const docxBlob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        try { pdfBlob = await convertDocxBlobToPdf(docxBlob); }
        catch(_) { pdfBlob = await convertDocxBlobToPdfLocal(docxBlob); }
      }

      // Priorité 0 : champs embarqués dans resource.metadata (zéro RLS) → sinon requête DB par templateId
      const tplFieldsRaw = embeddedTplFields || (templateId
        ? (await supabase.from('template_fields').select('*').eq('template_id', templateId).order('page', { ascending: true })).data
        : null);
      const tplFields = tplFieldsRaw || [];

      if (tplFields && tplFields.length > 0) {
        const today = new Date().toLocaleDateString('fr-FR');
        // Récupérer les données du formateur/consultant assigné au client
        const formateur = (formateurs || []).find(f => String(f.id) === String(currentClient?.formateur_id));
        const dataValues = {
          nomcomplet_client:  (currentClient?.nom_complet || currentClient?.nom || '').trim(),
          numero_dossier_client: currentClient?.numero_dossier || '',
          client_email:       currentClient?.email_contact || currentClient?.email || '',
          client_phone:       currentClient?.telephone || '',
          rue_client:         currentClient?.adresse || currentClient?.rue || '',
          code_postal_client: currentClient?.code_postal || '',
          ville_client:       currentClient?.ville || '',
          region_client:      currentClient?.region || '',
          adresse_session:    currentClient?.adresse || currentClient?.rue || '',
          prix_prestation:    currentClient?.prix_prestation || currentClient?.montant_prestation || '',
          ...(await valeursFinancement(currentClient?.id, currentClient?.prix_prestation || currentClient?.montant_prestation)),
          formation_nom:      '',
          modalite_formation: currentClient?.modalite_formation || '',
          date_debut:         '',
          date_fin:           '',
          date_signature:     today,
          date_du_jour:       today,
          // Compatibilité anciens noms
          nom_client:         currentClient?.nom || '',
          prenom_client:      currentClient?.prenom || '',
          email_client:       currentClient?.email_contact || currentClient?.email || '',
          telephone_client:   currentClient?.telephone || '',
          ville:              currentClient?.ville || '',
          // Données formateur / consultant
          nom_formateur:         formateur?.nom || '',
          prenom_formateur:      formateur?.prenom || '',
          email_formateur:       formateur?.email || '',
          tel_formateur:         formateur?.telephone || '',
          telephone_formateur:   formateur?.telephone || '',
          adresse_formateur:     formateur?.adresse_formateur || formateur?.adresse_pro || formateur?.adresse_client || formateur?.adresse || '',
          region_formateur:      formateur?.region || '',
          formateur_siret:       formateur?.formateur_siret || formateur?.siret || '',
          formateur_nda:         formateur?.formateur_nda || formateur?.nda || '',
          compagnie_assurance:   formateur?.compagnie_assurance || '',
          numero_assurance_rcp:  formateur?.numero_assurance_rcp || '',
          // AJOUT (2026-09-15) : équivalent de date_signature (client) pour le formateur/organisme —
          // même valeur (date du jour de génération), demandé par l'utilisateur.
          date_signature_formateur: today,
          date_signature_organisme: today,
          // AJOUT (2026-09-16) : balises "initiales" — voir computeInitials() en haut du fichier.
          initiales_client:      computeInitials(currentClient?.nom_complet || currentClient?.nom || ''),
          initiales_formateur:   computeInitials(formateur?.nom || ''),
          initiales_organisme:   computeInitials(orgSettings?.nom || ''),
          // Alias consultant (même données)
          nom_consultant:        formateur?.nom || '',
          email_consultant:      formateur?.email || '',
          tel_consultant:        formateur?.telephone || '',
        };
        // Balises d'adresse dérivées : adresse_client combinée, rue/CP/ville_formateur éclatées
        Object.assign(dataValues, valeursFormationOrganisme({ moduleNom: moduleNomClient, seances: seancesDuClient, orgSettings })); // lot 3
        dataValues.adresse_client = [dataValues.rue_client, dataValues.code_postal_client, dataValues.ville_client].filter(Boolean).join(', ');
        { const _fAddr = parseAddressString(dataValues.adresse_formateur);
          dataValues.rue_formateur = _fAddr.rue;
          dataValues.code_postal_formateur = _fAddr.codePostal;
          dataValues.ville_formateur = _fAddr.ville; }
        // Overlay données (balises de fusion) UNIQUEMENT — les champs interactifs (signature, case,
        // texte libre) ne doivent JAMAIS recevoir de rendu ici, pas même un placeholder vide : ce
        // pré-remplissage a lieu dès l'OUVERTURE du document (avant toute signature), donc graver un
        // placeholder à ce stade le fige définitivement dans le PDF de base réutilisé pour la
        // signature — y compris pour les champs qui appartiennent à l'AUTRE partie (bug constaté
        // 2026-07-24 : champ "texte libre formateur" resté visible/figé sous la saisie du formateur).
        const dataOnlyFields = tplFields.filter(f => {
          const isInteractive = f.field_type === 'signature' || f.field_type === 'checkbox' || f.field_type === 'text_input' || (f.tag || '').startsWith('signature_') || (f.tag || '').startsWith('checkbox_') || (f.tag || '').startsWith('texte_');
          return !isInteractive && !estDateSignature(f); // date de signature : posée au moment de signer
        });
        if (dataOnlyFields.length > 0) {
          pdfBlob = await overlayFieldsOnPdf(pdfBlob, dataOnlyFields, dataValues, {});
        }
      }

      _poserBlob(pdfBlob);
      const blobUrl = URL.createObjectURL(pdfBlob);
      _poserRefUrl(blobUrl);
      _poserUrl(blobUrl);
      toast.dismiss(toastId);
      _poserCases((tplFields || []).filter(isClientCheckboxField));
      _poserTextes((tplFields || []).filter(isClientTextField));
      _poserSigReq((tplFields || []).some(isClientSignatureField));
      // Ouvrir la modal — le blob est déjà prêt, le client verra ses données immédiatement
      _ouvrir(resource);
    } catch(e) {
      console.warn('[handleOpenSigning] Pré-remplissage échoué (fallback template brut) :', e.message);
      toast.dismiss(toastId);
      // Fallback : ouvrir avec template brut
      _ouvrir(resource);
    }
  }, [currentClient, supabase, resolveVisualTemplate, documents, currentUserId, formateurs, orgSettings, moduleNomClient, seancesDuClient]);

  const handleSignSave = async (signatureDataUrl, _documentChoice = null, checkedIds = null, textValues = null, lot = null) => { /* 2026-10-21 : chargé à la demande */ const { PDFDocument } = await import('pdf-lib');
    // SIGNATURE DE GROUPE (2026-10-23) : « lot » = document préparé par handleOpenSigning(resource, lot).
    const _ressource = lot ? lot.resource : signingResource;
    if (!_ressource || !currentClient) return false;
    const _checkedIdSet = checkedIds instanceof Set ? checkedIds : new Set(checkedIds || []);
    // Valeurs tapées dans les balises "texte libre" (clé = fieldKey(field) → texte saisi)
    const _textValueMap = {};
    if (textValues) {
      (textValues instanceof Map ? Array.from(textValues.entries()) : Object.entries(textValues)).forEach(([k, v]) => { if (v) _textValueMap[k] = v; });
    }

    const toastId = 'pdf-sign';
    let signedPdfUrl = _ressource.file_url; // fallback ultime

    // ── Chercher le document déjà généré pour ce client (non signé) ─────────
    const existingGeneratedDoc = (documents || []).find(d =>
      String(d.user_id) === String(currentUserId) &&
      !isInternalDossierDoc(d) &&
      d.nom === _ressource.titre &&
      d.url &&
      !d.signe_par_client
    );

    try {
      toast.loading('Préparation du document…', { id: toastId });

      // ── Étape 1 : Obtenir un PDF de base (pré-rempli avec données client) ──
      let pdfBlob;
      let signatureEmbeddedByOverlay = false; // true quand overlayFieldsOnPdf gère la signature

      // Déterminer la source : doc généré existant (PDF) > template (DOCX)
      const templateUrl = _ressource.file_url;
      const isTemplateDocx = /\.(docx|doc)$/i.test((templateUrl || '').split('?')[0]);

      const templateIsPdf = /\.pdf$/i.test((templateUrl || '').split('?')[0]);

      // ── Résoudre le vrai template_id (priorité : resource.metadata → documents → MSR) ──
      const _parseMeta = (m) => { try { return typeof m === 'string' ? JSON.parse(m) : (m || {}); } catch { return {}; } };
      const _rMeta = _parseMeta(_ressource.metadata);
      let visualTemplateId = _rMeta.visual_template_id || null;
      let hasVisualFields = _rMeta.has_visual_fields === true;
      if (!hasVisualFields || !visualTemplateId) {
        const _refDoc = (documents || []).find(d =>
          (d.nom === _ressource.titre || d.nom === _ressource.nom) &&
          d.type_action === 'Modèle Référence'
        );
        const _refMeta = _parseMeta(_refDoc?.metadata);
        if (_refMeta.has_visual_fields === true && _refMeta.visual_template_id) {
          visualTemplateId = _refMeta.visual_template_id;
          hasVisualFields = true;
        }
      }
      if (!hasVisualFields || !visualTemplateId) {
        const _resolved = await resolveVisualTemplate(_ressource.titre || _ressource.nom);
        visualTemplateId = _resolved.templateId;
        hasVisualFields = _resolved.hasVisualFields;
      }

      if (hasVisualFields && visualTemplateId) {
        // ── Chemin visuel : données déjà overlayées dans handleOpenSigning → ajouter la signature ──
        toast.loading('Génération du document signé…', { id: toastId });

        // Revérifier auprès de Supabase la toute dernière version du PDF avant de construire par-dessus :
        // l'AUTRE partie (le formateur, pour un document destination=both) a pu signer PENDANT que ce
        // document était ouvert dans cette fenêtre — le blob mis en cache dans prefilledSignBlob.current
        // (capturé à l'OUVERTURE du document) serait alors périmé, et l'utiliser tel quel écraserait
        // silencieusement sa contribution déjà enregistrée (bug constaté 2026-07-24 : le texte du
        // formateur disparaissait du document final après que le client signe à son tour).
        let baseBlob = lot ? lot.blob : prefilledSignBlob.current;
        try {
          if (existingGeneratedDoc?.id) {
            const { data: freshDoc } = await supabase.from('documents').select('url').eq('id', existingGeneratedDoc.id).maybeSingle();
            if (freshDoc?.url) {
              const freshResp = await fetch(freshDoc.url);
              if (freshResp.ok) baseBlob = new Blob([await freshResp.arrayBuffer()], { type: 'application/pdf' });
            }
          }
        } catch (e) {
          console.warn('[handleSignSave] Impossible de récupérer la dernière version du PDF, utilisation du cache local :', e.message);
        }

        if (baseBlob) {
          // ✅ Blob pré-rempli disponible (données déjà dans le PDF) → ajouter la signature + les cases à cocher
          pdfBlob = baseBlob;
          {
            // Uniquement les champs APPARTENANT AU CLIENT (suffixe _client) : un champ "texte_formateur"/
            // "checkbox_formateur" pas encore rempli par le formateur ne doit RECEVOIR AUCUN overlay ici
            // (ni valeur, ni case, ni placeholder vide) — sinon sa case vide se retrouve gravée en dur
            // dans le PDF à l'instant où le CLIENT signe, et reste visible/figée sous le champ de saisie
            // du formateur quand il complète son propre champ plus tard (bug constaté en test 2026-07-24).
            const _isSigOrChk = f => (f.field_type === 'signature' || f.field_type === 'checkbox' || f.field_type === 'text_input' || (f.tag || '').startsWith('signature_') || (f.tag || '').startsWith('checkbox_') || (f.tag || '').startsWith('texte_')) && (f.tag || '').endsWith('_client') || f.tag === 'date_signature';
            // Chercher les champs signature/case à cocher : P1 = signature_fields (nouveau format pré-généré)
            //   P2 = fields[] (ancien format fallback) → P3 = embarqués resource.metadata → P4 = DB
            const _docMeta = _parseMeta(existingGeneratedDoc?.metadata);
            // P1 : signature_fields du format pré-généré
            let sigFields = (_docMeta.signature_fields || []).filter(_isSigOrChk);
            // P2 : champs dans fields[] (ancien format)
            if (sigFields.length === 0) {
              sigFields = (_docMeta.fields || []).filter(_isSigOrChk);
            }
            // P3 : champs embarqués dans resource.metadata (zéro RLS)
            if (sigFields.length === 0) {
              const _embedded = _rMeta.template_fields || [];
              sigFields = _embedded.filter(_isSigOrChk);
            }
            // P4 : requête DB
            if (sigFields.length === 0 && visualTemplateId) {
              const { data: sigFieldsData } = await supabase
                .from('template_fields').select('*').eq('template_id', visualTemplateId).order('page', { ascending: true });
              sigFields = (sigFieldsData || []).filter(_isSigOrChk);
            }
            const checkedMap = {};
            sigFields.forEach(f => { const k = fieldKey(f); if (_checkedIdSet.has(k)) checkedMap[k] = true; });
            const textInputMap = {};
            sigFields.forEach(f => { const k = fieldKey(f); if (_textValueMap[k]) textInputMap[k] = _textValueMap[k]; });
            if (sigFields.length > 0 && (signatureDataUrl || Object.keys(checkedMap).length > 0 || Object.keys(textInputMap).length > 0)) {
              const sigMap = signatureDataUrl ? { signature_client: signatureDataUrl } : {};
              pdfBlob = await overlayFieldsOnPdf(pdfBlob, sigFields, { date_signature: dateDuJourFr() }, sigMap, checkedMap, textInputMap);
              signatureEmbeddedByOverlay = true;
            }
          }
        } else {
          // Fallback : télécharger template + appliquer données + signature en une passe
          const templateResp = await fetch(templateUrl);
          if (!templateResp.ok) throw new Error(`HTTP ${templateResp.status} lors du téléchargement du template`);
          const templateBytes = await templateResp.arrayBuffer();

          if (templateIsPdf) {
            pdfBlob = new Blob([templateBytes], { type: 'application/pdf' });
          } else {
            const docxBlob = new Blob([templateBytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
            toast.loading('Conversion DOCX → PDF…', { id: toastId });
            try { pdfBlob = await convertDocxBlobToPdf(docxBlob); }
            catch(_) { pdfBlob = await convertDocxBlobToPdfLocal(docxBlob); }
          }

          // ← utiliser visualTemplateId (module_step_resources), pas _ressource.id (documents table)
          const { data: templateFields } = await supabase
            .from('template_fields').select('*').eq('template_id', visualTemplateId).order('page', { ascending: true });

          if (templateFields && templateFields.length > 0) {
            const today = new Date().toLocaleDateString('fr-FR');
            const formateur = (formateurs || []).find(f => String(f.id) === String(currentClient?.formateur_id));
            const dataValues = {
              nomcomplet_client:  currentClient.nom_complet || currentClient.nom || '',
              numero_dossier_client: currentClient.numero_dossier || '',
              client_email:       currentClient.email_contact || currentClient.email || '',
              client_phone:       currentClient.telephone || '',
              rue_client:         currentClient.adresse || currentClient.rue || '',
              code_postal_client: currentClient.code_postal || '',
              ville_client:       currentClient.ville || '',
              region_client:      currentClient.region || '',
              adresse_session:    currentClient.adresse || currentClient.rue || '',
              prix_prestation:    currentClient.prix_prestation || currentClient.montant_prestation || '',
              ...(await valeursFinancement(currentClient.id, currentClient.prix_prestation || currentClient.montant_prestation)),
              formation_nom:      '',
              modalite_formation: currentClient.modalite_formation || '',
              date_debut:         '',
              date_fin:           '',
              date_signature:     today,
              date_du_jour:       today,
              nom_client:         currentClient.nom || '',
              prenom_client:      currentClient.prenom || '',
              email_client:       currentClient.email_contact || currentClient.email || '',
              telephone_client:   currentClient.telephone || '',
              ville:              currentClient.ville || '',
              // Données formateur / consultant
              nom_formateur:         formateur?.nom || '',
              prenom_formateur:      formateur?.prenom || '',
              email_formateur:       formateur?.email || '',
              tel_formateur:         formateur?.telephone || '',
              telephone_formateur:   formateur?.telephone || '',
              adresse_formateur:     formateur?.adresse_formateur || formateur?.adresse_pro || formateur?.adresse_client || formateur?.adresse || '',
              region_formateur:      formateur?.region || '',
              formateur_siret:       formateur?.formateur_siret || formateur?.siret || '',
              formateur_nda:         formateur?.formateur_nda || formateur?.nda || '',
              compagnie_assurance:   formateur?.compagnie_assurance || '',
              numero_assurance_rcp:  formateur?.numero_assurance_rcp || '',
              // AJOUT (2026-09-15) : équivalent de date_signature (client) pour le formateur/organisme.
              date_signature_formateur: today,
              date_signature_organisme: today,
              // AJOUT (2026-09-16) : balises "initiales" — voir computeInitials() en haut du fichier.
              initiales_client:      computeInitials(currentClient.nom_complet || currentClient.nom || ''),
              initiales_formateur:   computeInitials(formateur?.nom || ''),
              initiales_organisme:   computeInitials(orgSettings?.nom || ''),
              nom_consultant:        formateur?.nom || '',
              email_consultant:      formateur?.email || '',
              tel_consultant:        formateur?.telephone || '',
            };
            // Balises d'adresse dérivées : adresse_client combinée, rue/CP/ville_formateur éclatées
            Object.assign(dataValues, valeursFormationOrganisme({ moduleNom: moduleNomClient, seances: seancesDuClient, orgSettings })); // lot 3
            dataValues.adresse_client = [dataValues.rue_client, dataValues.code_postal_client, dataValues.ville_client].filter(Boolean).join(', ');
            { const _fAddr = parseAddressString(dataValues.adresse_formateur);
              dataValues.rue_formateur = _fAddr.rue;
              dataValues.code_postal_formateur = _fAddr.codePostal;
              dataValues.ville_formateur = _fAddr.ville; }
            toast.loading('Personnalisation du document…', { id: toastId });
            const sigMap = signatureDataUrl ? { signature_client: signatureDataUrl } : {};
            const checkedMap = {};
            (templateFields || []).forEach(f => { const k = fieldKey(f); if (_checkedIdSet.has(k)) checkedMap[k] = true; });
            const textInputMap = {};
            (templateFields || []).forEach(f => { const k = fieldKey(f); if (_textValueMap[k]) textInputMap[k] = _textValueMap[k]; });
            // Ne jamais transmettre à overlayFieldsOnPdf les champs interactifs (signature/case/texte
            // libre) qui appartiennent au FORMATEUR : sinon son champ pas encore rempli reçoit quand
            // même un rendu "case vide/placeholder", gravé en dur dans le PDF au moment où le CLIENT
            // signe — visible et figé plus tard sous la saisie du formateur (bug constaté 2026-07-24).
            const fieldsForOverlay = (templateFields || []).filter(f => {
              const isInteractive = f.field_type === 'signature' || f.field_type === 'checkbox' || f.field_type === 'text_input' || (f.tag || '').startsWith('signature_') || (f.tag || '').startsWith('checkbox_') || (f.tag || '').startsWith('texte_');
              // Les dates de signature du formateur / de l'organisme seront posées quand EUX signeront.
              return (!isInteractive || (f.tag || '').endsWith('_client')) && f.tag !== 'date_signature_formateur' && f.tag !== 'date_signature_organisme';
            });
            pdfBlob = await overlayFieldsOnPdf(pdfBlob, fieldsForOverlay, dataValues, sigMap, checkedMap, textInputMap);
            signatureEmbeddedByOverlay = true;
          }
        }
      } else if (existingGeneratedDoc?.url && !/\.(docx|doc)$/i.test(existingGeneratedDoc.url.split('?')[0])) {
        // Doc pré-rempli existant en PDF → utiliser directement
        const resp = await fetch(existingGeneratedDoc.url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        pdfBlob = new Blob([await resp.arrayBuffer()], { type: 'application/pdf' });
      } else {
        // Fallback : source DOCX ou PDF brut
        const sourceUrl = existingGeneratedDoc?.url || templateUrl;
        const resp = await fetch(sourceUrl);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const bytes = await resp.arrayBuffer();
        if (isTemplateDocx) {
          const docxBlob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          try { pdfBlob = await convertDocxBlobToPdf(docxBlob); }
          catch(_) { pdfBlob = await convertDocxBlobToPdfLocal(docxBlob); }
        } else {
          pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        }
      }

      // ── Étape 2 : Intégrer la signature dans le PDF ───────────────────────
      toast.loading('Intégration de la signature…', { id: toastId });
      const pdfBytes = await pdfBlob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];

      if (signatureDataUrl && !signatureEmbeddedByOverlay) {
        // Fallback position fixe — uniquement si la signature n'est PAS gérée par overlayFieldsOnPdf
        // (cas : document sans balise signature_client positionnée)
        const base64Data = signatureDataUrl.split(',')[1];
        const sigBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
        const sigImage = await pdfDoc.embedPng(sigBytes);

        const maxW = 160, maxH = 60;
        const scale = Math.min(maxW / sigImage.width, maxH / sigImage.height, 1);
        const sigW = sigImage.width * scale;
        const sigH = sigImage.height * scale;
        const sigX = 60;
        const sigY = 110;

        lastPage.drawImage(sigImage, { x: sigX, y: sigY, width: sigW, height: sigH, opacity: 1 });

        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const signedDate = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const clientName = currentClient.nom_complet || currentClient.nom || '';
        if (clientName) {
          lastPage.drawText(clientName, { x: sigX, y: sigY - 14, size: 8, font, color: rgb(0.2, 0.2, 0.2) });
        }
        lastPage.drawText(`Signé le ${signedDate}`, { x: sigX, y: sigY - 26, size: 7, font, color: rgb(0.4, 0.4, 0.4) });
      }

      const signedPdfBytes = await pdfDoc.save();
      const signedBlob = new Blob([signedPdfBytes], { type: 'application/pdf' });

      // ── Étape 3 : Uploader le PDF signé ───────────────────────────────────
      toast.loading('Enregistrement du document signé…', { id: toastId });
      const safeDocName = (_ressource.titre || 'document').replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 40);
      const storagePath = `signed-documents/${currentUserId}/${Date.now()}_${safeDocName}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(storagePath, signedBlob, { contentType: 'application/pdf', upsert: true });

      if (uploadError) throw new Error('Upload échoué : ' + uploadError.message);

      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(storagePath);
      signedPdfUrl = publicUrl;
      toast.dismiss(toastId);

    } catch (err) {
      console.error('[handleSignSave]', err);
      toast.error('Erreur lors de la préparation du document : ' + err.message, { id: toastId });
      // Avant ce correctif, l'exécution continuait quand même jusqu'à l'étape 4 et enregistrait
      // signe_par_client:true avec l'URL du template NON signé (signedPdfUrl retombait sur son
      // fallback initial) : le document apparaissait "Signé ✓" côté client/formateur alors qu'aucun
      // PDF signé n'avait réellement été généré/enregistré. On arrête ici pour ne PAS enregistrer une
      // fausse signature — le client peut réessayer.
      return false;
    }

    // ── Étape 4 : Enregistrer en base ─────────────────────────────────────
    let dbError;
    // FIX (2026-09-04) : le statut ne passe "Signé" que si TOUS les rôles requis (pas seulement le
    // client) ont signé — voir getRequiredSignerRoles/isDocFullySigned. existingGeneratedDoc suffit :
    // getRequiredSignerRoles lit d'abord ses balises embarquées en metadata, puis se rabat sur la
    // table technique template_fields via son propre template_id si besoin.
    const _requiredRoles = await getRequiredSignerRoles(existingGeneratedDoc || {}, supabase);
    const _statutAfterClientSign = isDocFullySigned({ ...(existingGeneratedDoc || {}), signe_par_client: true }, _requiredRoles)
      ? 'Signé' : (existingGeneratedDoc?.statut || 'En attente de signature');
    const signatureData = {
      url: signedPdfUrl,
      // Toujours refléter aussi 'signed_pdf_url' (bug racine identifié le 2026-07-24) : cette colonne sert
      // de source pour certains écrans d'affichage (onglet "Documents Signés") ET est relue en priorité par
      // handleSignDocument côté formateur. Si on ne la met pas à jour ici, elle reste figée sur un ancien
      // snapshot pendant que 'url' avance — le prochain signataire (formateur) risque alors de construire
      // par-dessus un PDF périmé et d'effacer silencieusement la contribution du client.
      signed_pdf_url: signedPdfUrl,
      signe_par_client: true,
      date_signature_client: new Date().toISOString(),
      signature_client: signatureDataUrl,
      statut: _statutAfterClientSign,
      visible_formateur: true,
      visible_admin: true,
    };

    if (existingGeneratedDoc) {
      const { error } = await supabase.from('documents').update(signatureData).eq('id', existingGeneratedDoc.id);
      dbError = error;
    } else {
      const { error } = await supabase.from('documents').insert([{
        user_id: currentUserId,
        organisation_id: currentClient.organisation_id,
        nom: _ressource.titre,
        type_document: 'À signer',
        visible_client: true,
        ...signatureData,
      }]);
      dbError = error;
    }

    if (dbError) { toast.error('Erreur lors de la signature : ' + dbError.message); return false; }
    if (lot) { toast.dismiss(toastId); return true; }
    handleCloseSigningModal();
    if (fetchDocuments) await fetchDocuments();
    toast.success('✅ Document signé avec succès !');
    return true;
  };

  // ─── Signature spécifique : Autorisation de conservation ────────────────────
  const handleConservationSign = async ({ choice, signatureDataUrl, clientFullName, formateurNom, dateDebut }, cible = null) => { /* 2026-10-21 : chargé à la demande */ const html2canvas = (await import('html2canvas')).default; const { jsPDF } = await import('jspdf');
    const toastId = 'conservation-sign';
    toast.loading('Génération du document signé…', { id: toastId });
    try {
      const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      const conserveSel = choice === 'conserve';
      const checkConserve = conserveSel ? '&#9745;' : '&#9744;';
      const checkDetruire = !conserveSel ? '&#9745;' : '&#9744;';
      const checkColorConserve = conserveSel ? '#16a34a' : '#9ca3af';
      const checkColorDetruire = !conserveSel ? '#dc2626' : '#9ca3af';
      // 2026-10-21 : document au nom (et avec le logo) de l'organisme, texte valable pour toute prestation.
      const orgNom = escHtml(orgSettings?.nom || "l'organisme de formation");
      const orgLogo = orgSettings?.logo_url ? safeImgSrc(orgSettings.logo_url) : '';
      const orgAdresse = escHtml([orgSettings?.adresse, orgSettings?.ville].filter(Boolean).join(' — '));

      // ── Générer le HTML du document ────────────────────────────────────────
      const el = document.createElement('div');
      el.style.cssText = 'width:794px;min-height:1100px;padding:56px 60px;font-family:Arial,Helvetica,sans-serif;background:#ffffff;position:fixed;left:-9999px;top:-9999px;color:#111827;box-sizing:border-box;';
      el.innerHTML = `
        <div style="text-align:center;margin-bottom:28px;">
          ${orgLogo ? `<img src="${orgLogo}" crossorigin="anonymous" style="max-height:64px;max-width:220px;object-fit:contain;margin:0 auto 8px;display:block;" />` : ''}
          <div style="font-size:20px;font-weight:900;color:#111827;">${orgNom}</div>
          ${orgAdresse ? `<div style="font-size:8.5px;color:#6b7280;margin-top:3px;">${orgAdresse}</div>` : ''}
          <div style="margin-top:14px;height:1px;background:linear-gradient(to right,transparent,#7C3AED,transparent);"></div>
        </div>
        <h1 style="text-align:center;font-size:15px;font-weight:900;color:#7C3AED;text-transform:uppercase;letter-spacing:1px;margin:0 0 22px;">Autorisation de conservation des documents</h1>
        <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:8px;padding:12px 16px;margin-bottom:22px;font-size:9.5px;color:#374151;line-height:1.6;">
          Les documents élaborés dans le cadre de l'accompagnement sont conservés ou détruits selon le choix du bénéficiaire ci-dessous. Pour un bilan de compétences, l'article R6313-7 du Code du travail prévoit leur destruction à l'issue de la prestation, sauf accord écrit du bénéficiaire.
        </div>
        <table style="width:100%;border-collapse:collapse;margin-bottom:24px;font-size:10px;">
          <tr><td style="background:#7C3AED;color:white;font-weight:700;padding:8px 12px;border:1px solid #7C3AED;width:38%;" >Bénéficiaire</td><td style="border:1px solid #e5e7eb;padding:8px 12px;">Mme / M. ${escHtml(clientFullName)}</td></tr>
          <tr><td style="background:#7C3AED;color:white;font-weight:700;padding:8px 12px;border:1px solid #7C3AED;">Organisme</td><td style="border:1px solid #e5e7eb;padding:8px 12px;">${orgNom}</td></tr>
          <tr><td style="background:#7C3AED;color:white;font-weight:700;padding:8px 12px;border:1px solid #7C3AED;">Formateur / consultant</td><td style="border:1px solid #e5e7eb;padding:8px 12px;">${escHtml(formateurNom || '—')}</td></tr>
          <tr><td style="background:#7C3AED;color:white;font-weight:700;padding:8px 12px;border:1px solid #7C3AED;">Date de début</td><td style="border:1px solid #e5e7eb;padding:8px 12px;">${escHtml(dateDebut || '—')}</td></tr>
        </table>
        <h2 style="font-size:12px;font-weight:800;color:#7C3AED;margin:0 0 12px;">Choix du bénéficiaire</h2>
        <p style="font-size:9.5px;color:#374151;margin:0 0 16px;line-height:1.6;">
          Je soussigné(e) ${escHtml(clientFullName)}, bénéficiaire d'une prestation réalisée par ${orgNom}, indique mon choix concernant la conservation des documents élaborés dans le cadre de cette prestation :
        </p>
        <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:12px;">
          <span style="font-size:18px;line-height:1;color:${checkColorConserve};flex-shrink:0;">${checkConserve}</span>
          <span style="font-size:9.5px;color:#374151;line-height:1.6;">J'autorise ${orgNom} à conserver mes documents pendant une durée maximale de <strong>3 ans</strong> à compter de la fin de la prestation, dans le but d'assurer le suivi de ma situation.</span>
        </div>
        <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:30px;">
          <span style="font-size:18px;line-height:1;color:${checkColorDetruire};flex-shrink:0;">${checkDetruire}</span>
          <span style="font-size:9.5px;color:#374151;line-height:1.6;">Je ne souhaite pas que mes documents soient conservés. ${orgNom} procédera à leur destruction à la clôture de la prestation.</span>
        </div>
        <div style="border-top:1px solid #e5e7eb;padding-top:20px;display:flex;justify-content:space-between;align-items:flex-end;">
          <div>
            <p style="font-size:9px;color:#6b7280;margin:0 0 4px;">Fait le ${today}</p>
            <p style="font-size:9px;color:#6b7280;margin:0;">Signature du bénéficiaire :</p>
          </div>
          <img src="${safeImgSrc(signatureDataUrl)}" style="max-width:210px;max-height:80px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;padding:4px;" />
        </div>
        <p style="font-size:7.5px;color:#9ca3af;text-align:center;margin-top:28px;">Document signé électroniquement via SkorUp pour le compte de ${orgNom}.</p>
      `;
      document.body.appendChild(el);
      await new Promise(r => setTimeout(r, 350));

      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
      document.body.removeChild(el);

      const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', 0, 0, 595.28, 841.89);
      const pdfBlob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });

      // ── Upload dans Supabase Storage ───────────────────────────────────────
      const storagePath = `signed-documents/${currentUserId}/conservation_${Date.now()}.pdf`;
      const { error: upErr } = await supabase.storage.from('documents').upload(storagePath, pdfBlob, { contentType: 'application/pdf', upsert: true });
      if (upErr) throw new Error('Upload échoué : ' + upErr.message);
      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(storagePath);

      // ── Mise à jour ou insertion en base ──────────────────────────────────
      if ((cible || conservationTargetDoc)?.id) {
        await supabase.from('documents').update({ signe_par_client: true, url: publicUrl, signature_client: signatureDataUrl }).eq('id', (cible || conservationTargetDoc).id);
      } else {
        await supabase.from('documents').insert([{
          user_id: currentUserId,
          organisation_id: currentClient?.organisation_id,
          nom: (cible || conservationTargetDoc)?.titre || 'Autorisation de conservation des documents',
          url: publicUrl,
          type_document: 'À signer',
          visible_client: true,
          visible_formateur: true,
          signe_par_client: true,
          signature_client: signatureDataUrl,
        }]);
      }

      toast.success('✅ Autorisation signée et enregistrée !', { id: toastId });
      setIsConservationModalOpen(false);
      setConservationTargetDoc(null);
      if (fetchDocuments) await fetchDocuments();
    } catch (err) {
      console.error('[handleConservationSign]', err);
      toast.error('Erreur : ' + err.message, { id: toastId });
      throw err;
    }
  };

  // ─── Rendu d'un groupe de documents (type === 'document_group') ───
  const renderGroupCard = (resource) => {
    // Les docs du groupe sont dans la table documents avec group_id = document_group_id
    // Ou avec group_ids[] contenant document_group_id (multi-groupe)
    const groupDocs = resource.document_group_id
      ? (documents || []).filter(d =>
          d.group_id === resource.document_group_id ||
          (d.group_ids && d.group_ids.includes(resource.document_group_id))
        )
      : [];
    // Questionnaires liés à ce groupe (templates) — supporte multi-groupes via metadata.group_ids
    const groupQuestItems = resource.document_group_id
      ? groupQuestionnaires.filter(q => {
          const qm = (() => { try { return typeof q.metadata === 'string' ? JSON.parse(q.metadata) : (q.metadata || {}); } catch { return {}; } })();
          const gids = qm.group_ids && qm.group_ids.length > 0 ? qm.group_ids : (q.document_group_id ? [q.document_group_id] : []);
          return gids.includes(resource.document_group_id);
        })
      : [];
    const isExpanded = expandedGroupId === resource.id;
    // Priorité : nom depuis document_groups > titre si non générique > fallback
    const groupName = groupNames[resource.document_group_id]
      || (resource.titre && resource.titre !== 'Document' && resource.titre !== 'Groupe' ? resource.titre : null)
      || 'Groupe de documents';

    return (
      <div key={resource.id} className="border border-indigo-100 rounded-2xl overflow-hidden bg-white hover:border-indigo-200 transition-colors">
        {/* En-tête cliquable */}
        <button
          onClick={() => setExpandedGroupId(isExpanded ? null : resource.id)}
          className="w-full p-4 flex items-center justify-between hover:bg-indigo-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 text-indigo-600 flex items-center justify-center rounded-xl shrink-0"><Layout size={20} /></div>
            <div className="text-left">
              <p className="font-bold text-gray-900 text-sm">{groupName}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider">
                {groupDocs.length} document{groupDocs.length !== 1 ? 's' : ''}{groupQuestItems.length > 0 ? ` · ${groupQuestItems.length} questionnaire${groupQuestItems.length > 1 ? 's' : ''}` : ''} · Groupe
              </p>
            </div>
          </div>
          {isExpanded
            ? <ChevronUp size={16} className="text-indigo-400 shrink-0" />
            : <ChevronDown size={16} className="text-gray-300 shrink-0" />}
        </button>

        {/* Contenu dépliable */}
        {isExpanded && (
          <div className="border-t border-indigo-50">
            {groupDocs.length === 0 && groupQuestItems.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-5 italic">Aucun élément dans ce groupe.</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {groupDocs.map(doc => {
                  const fileUrl = doc.url || doc.file_url;
                  const isDocSigned = (documents || []).some(
                    d => String(d.user_id) === String(currentUserId) && d.nom === doc.nom && d.signe_par_client
                  );
                  const docMeta = typeof doc.metadata === 'string' && doc.metadata.startsWith('{')
                    ? (() => { try { return JSON.parse(doc.metadata); } catch { return {}; } })()
                    : (doc.metadata || {});
                  // Dans un groupe, tous les docs avec un fichier sont signables par défaut
                  // sauf si explicitement marqué "Téléchargeable uniquement"
                  const explicitlyNotToSign = doc.type_document === 'Téléchargeable' && docMeta.requiresClientSignature === false;
                  const canSign = fileUrl && !explicitlyNotToSign;

                  return (
                    <div key={doc.id} className="px-4 py-3 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText size={14} className={`shrink-0 ${canSign ? 'text-violet-400' : 'text-gray-400'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.nom}</p>
                          <p className="text-xs uppercase tracking-wider" style={{ color: isDocSigned ? '#16a34a' : (canSign ? '#f87171' : '#9ca3af') }}>
                            {isDocSigned ? 'Signé' : (canSign ? 'À signer' : (doc.type_document || 'Document'))}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isDocSigned && (
                          <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">✓ Signé</span>
                        )}
                        {!isDocSigned && canSign && (
                          <button
                            onClick={() => handleOpenSigning({ id: doc.id, titre: doc.nom, file_url: fileUrl, moment: resource.moment, metadata: { ...docMeta, classification: 'a_signer' } })}
                            className="px-3 py-1.5 bg-violet-600 text-white font-bold rounded-lg text-xs hover:bg-violet-700 transition-colors shadow-sm"
                          >Signer</button>
                        )}
                        {fileUrl && (
                          <button
                            onClick={() => setViewingResource({ id: doc.id, titre: doc.nom, file_url: fileUrl })}
                            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs hover:bg-gray-50 transition-colors"
                          >Consulter</button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Questionnaires du groupe */}
                {groupQuestItems.map(q => {
                  const qMeta = (() => { try { return typeof q.metadata === 'string' ? JSON.parse(q.metadata) : (q.metadata || {}); } catch { return {}; } })();
                  const nbQ = (qMeta.questions || []).length;
                  const isQuizItem = !!qMeta.isQuiz;
                  const qResponse = questionnaireResponses.find(r => r.questionnaire_id === q.id);
                  const isQCompleted = !!qResponse;
                  return (
                    <div key={q.id} className="px-4 py-3 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-base shrink-0">{isQuizItem ? '🎯' : '📝'}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{q.titre}</p>
                          <p className="text-xs uppercase tracking-wider text-amber-500">{nbQ} question{nbQ > 1 ? 's' : ''} · {isQuizItem ? 'Quiz noté' : 'Questionnaire'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isQCompleted ? (
                          isQuizItem && qResponse.score_percent != null ? (
                            <span className={`text-xs font-bold px-2 py-1 rounded border ${qResponse.passed ? 'text-green-600 bg-green-50 border-green-100' : 'text-red-600 bg-red-50 border-red-100'}`}>
                              {qResponse.passed ? '✅' : '❌'} {Math.round(qResponse.score_percent)}%
                            </span>
                          ) : (
                            <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">✓ Complété</span>
                          )
                        ) : (
                          <button onClick={() => setActiveQuestionnaire(q)} className="px-3 py-1.5 bg-amber-500 text-white font-bold rounded-lg text-xs hover:bg-amber-600 transition-colors shadow-sm">{isQuizItem ? 'Passer le quiz' : 'Remplir'}</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Rendu d'une carte questionnaire (client) ───
  const renderQuestionnaireCard = (resource) => {
    const meta = (() => { try { return typeof resource.metadata === 'string' ? JSON.parse(resource.metadata) : (resource.metadata || {}); } catch { return {}; } })();
    const nbQuestions = (meta.questions || []).length;
    const isQuizResource = !!meta.isQuiz;
    const response = questionnaireResponses.find(r => r.questionnaire_id === resource.id);
    const isCompleted = !!response;
    return (
      <div key={resource.id} className="p-4 border border-gray-100 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white hover:border-amber-200 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 text-xl ${isQuizResource ? 'bg-violet-50 text-violet-600' : 'bg-amber-50 text-amber-600'}`}>{isQuizResource ? '🎯' : '📝'}</div>
          <div>
            <p className="font-bold text-gray-900 text-sm">{resource.titre}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">{nbQuestions} question{nbQuestions > 1 ? 's' : ''} · {isQuizResource ? 'Quiz noté' : 'Questionnaire'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isCompleted ? (
            isQuizResource && response.score_percent != null ? (
              <span className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${response.passed ? 'text-green-600 bg-green-50 border-green-100' : 'text-red-600 bg-red-50 border-red-100'}`}>
                {response.passed ? '✅ Acquis' : '❌ Non acquis'} · {Math.round(response.score_percent)}%
              </span>
            ) : (
              <span className="text-xs font-bold text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-100">✓ Complété</span>
            )
          ) : (
            <button onClick={() => setActiveQuestionnaire(resource)} className="px-4 py-2 bg-violet-600 text-white font-bold rounded-lg text-xs shadow-sm hover:bg-violet-700 transition-colors">{isQuizResource ? 'Passer le quiz' : 'Remplir'}</button>
          )}
        </div>
      </div>
    );
  };

  // ─── Rendu d'une carte de ressource (source module_step_resources) ───
  const renderResourceCard = (resource) => {
    // Déléguer les groupes et questionnaires
    if (resource.type === 'document_group') return renderGroupCard(resource);
    if (resource.type === 'questionnaire') return renderQuestionnaireCard(resource);

    const meta = typeof resource.metadata === 'string' && resource.metadata.startsWith('{')
      ? (() => { try { return JSON.parse(resource.metadata); } catch { return {}; } })()
      : (resource.metadata || {});

    // Compatibilité double format :
    //  - ancien format (instantiateDocument) : meta.classification === 'a_signer'
    //  - nouveau format (StepResourceModal)  : meta.requiresClientSignature === true
    const needsSignature =
      meta.classification === 'a_signer' ||
      meta.requiresClientSignature === true ||
      meta.documentType === 'signature';

    const label = needsSignature ? 'À signer' : 'Téléchargeable';
    const signed = isSignedByClient(resource);

    return (
      <div key={resource.id} className="p-4 border border-gray-100 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white hover:border-violet-200 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${needsSignature ? 'bg-violet-50 text-violet-700' : 'bg-gray-50 text-gray-500'}`}><FileText size={20} /></div>
          <div>
            <p className="font-bold text-gray-900 text-sm">{resource.titre}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {signed && <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">✓ Signé</span>}
          {!signed && needsSignature && resource.file_url && (
            <button onClick={() => handleOpenSigning(resource)} className="px-4 py-2 bg-violet-600 text-white font-bold rounded-lg text-xs shadow-sm hover:bg-violet-700 transition-colors">Signer le document</button>
          )}
          {resource.file_url && (
            <button onClick={() => setViewingResource(resource)} className="px-3 py-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm text-xs">Consulter</button>
          )}
        </div>
      </div>
    );
  };

  // ─── Rendu d'une carte de document per-client (source documents table) ───
  const renderDocumentCard = (doc) => {
    const isConsDoc = doc.nom?.toLowerCase().includes('conservation');
    const needsSign = isConsDoc && !doc.signe_par_client;
    const openConservation = () => {
      setConservationTargetDoc({ id: doc.id, titre: doc.nom });
      setIsConservationModalOpen(true);
    };
    // Docs envoyés pour signature (via "Envoyer pour signature") : bouton Signer générique
    const isToSign = !isConsDoc && doc.type_document === 'À signer' && !doc.signe_par_client && docRequiresSignature(doc); // FIX (2026-10-05)
    const docMeta = (() => { try { return typeof doc.metadata === 'string' ? JSON.parse(doc.metadata) : (doc.metadata || {}); } catch { return {}; } })();
    const fileUrl = doc.url || doc.file_url;
    return (
      <div key={doc.id} className="p-4 border border-gray-100 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white hover:border-violet-200 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${isToSign || isConsDoc ? 'bg-violet-50 text-violet-700' : 'bg-gray-50 text-gray-500'}`}><FileText size={20} /></div>
          <div>
            <p className="font-bold text-gray-900 text-sm">{doc.nom}</p>
            <p className={`text-xs uppercase tracking-wider ${doc.signe_par_client ? 'text-green-600' : (isToSign ? 'text-red-400' : 'text-gray-500')}`}>
              {doc.signe_par_client ? 'Signé' : (doc.type_document || 'Document')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {doc.signe_par_client && <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">✓ Signé</span>}
          {needsSign && (
            <button onClick={openConservation} className="px-4 py-2 bg-violet-600 text-white font-bold rounded-lg text-xs shadow-sm hover:bg-violet-700 transition-colors">
              Signer le document
            </button>
          )}
          {isToSign && handleOpenSigning && (
            <button
              onClick={() => handleOpenSigning({ id: doc.id, titre: doc.nom, file_url: fileUrl, metadata: { ...docMeta, classification: 'a_signer' } })}
              className="px-4 py-2 bg-violet-600 text-white font-bold rounded-lg text-xs shadow-sm hover:bg-violet-700 transition-colors"
            >
              Signer ✍️
            </button>
          )}
          {fileUrl && (
            <button onClick={() => setViewingResource({ ...doc, titre: doc.nom, file_url: fileUrl })} className="px-3 py-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm text-xs">Consulter</button>
          )}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin"></div>
    </div>
  );

  // Sans module, on affiche quand même la page si des questionnaires ont été envoyés à la main (2026-10-02)
  if (!moduleId && assignedQuestionnaires.length === 0) return (
    <div className="max-w-3xl mx-auto py-20 text-center">
      <FileText size={48} className="mx-auto mb-4 text-gray-200" />
      <p className="font-bold text-lg text-gray-700">Aucun parcours assigné</p>
      <p className="text-sm text-gray-400 mt-1">Votre accompagnateur vous assignera bientôt un module de formation.</p>
    </div>
  );

  const debutResources = moduleResources.filter(r => r.moment === 'debut');
  // FIX (2026-09-18) : les ressources de FIN de parcours (documents, questionnaires, groupes) ne
  // s'affichent désormais QUE si le parcours du client est détecté "terminé" (voir moduleCompleted /
  // isModuleCompletedForClient plus haut) — auparavant elles étaient visibles immédiatement, comme
  // celles de début, ce qui n'était pas voulu par l'utilisateur.
  const allFinResources = moduleResources.filter(r => r.moment === 'fin');
  const finResources = moduleCompleted ? allFinResources : [];
  const finExercicesPending = !moduleCompleted && moduleExerciceResources.some(r => r.moment === 'fin');
  const finLocked = !moduleCompleted && (allFinResources.length > 0 || finExercicesPending);
  const visibleExtraFinDocs = moduleCompleted ? extraFinDocs : [];
  // Questionnaires envoyés à la main, sauf s'ils sont déjà affichés ailleurs (module ou groupe de documents)
  const shownQIds = new Set([...moduleResources.map(r => String(r.id)), ...groupQuestionnaires.map(q => String(q.id))]);
  const assignedToShow = assignedQuestionnaires.filter(q => !shownQIds.has(String(q.id)));
  const hasAnything = debutResources.length > 0 || finResources.length > 0 || extraDebutDocs.length > 0 || visibleExtraFinDocs.length > 0 || extraOtherDocs.length > 0 || assignedToShow.length > 0;

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl mx-auto">
      {activeQuestionnaire && (
        <QuestionnaireFillerModal
          questionnaire={activeQuestionnaire}
          onClose={() => setActiveQuestionnaire(null)}
          onSubmit={async (answers, scoreInfo) => {
            const { error } = await supabase.from('questionnaire_responses').insert([{
              questionnaire_id: activeQuestionnaire.id,
              client_id: currentUserId,
              responses: answers,
              completed_at: new Date().toISOString(),
              // AJOUT (2026-09-30) : note du quiz (absent pour un questionnaire classique) — voir
              // QuestionnaireFillerModal.computeQuizScore.
              ...(scoreInfo ? { score_percent: scoreInfo.scorePercent, passed: scoreInfo.passed } : {})
            }]);
            if (error) { toast.error('Erreur lors de l\'envoi : ' + error.message); throw error; }
            const { data } = await supabase.from('questionnaire_responses').select('*').eq('client_id', currentUserId);
            if (data) setQuestionnaireResponses(data);
            toast.success(scoreInfo ? '✅ Quiz envoyé !' : '✅ Questionnaire envoyé !');
            // NB : la fermeture de la modale est gérée par QuestionnaireFillerModal lui-même (affiche
            // d'abord un écran de résultat/remerciement si besoin, avant d'appeler onClose()).
          }}
        />
      )}
      <div>
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Mes Documents</h1>
        <p className="text-gray-500 text-lg mt-1">Documents administratifs de votre parcours d'accompagnement.</p>
      </div>

      {groupesASigner.length > 0 && (
        <div className="bg-violet-50 rounded-3xl border border-violet-100 p-5 space-y-3">
          <div>
            <h2 className="font-bold text-violet-900 text-lg">À signer en une seule fois</h2>
            <p className="text-sm text-violet-700">Lisez les documents à la suite, comme un seul document, puis signez une seule fois.</p>
          </div>
          {groupesASigner.map(g => (
            <div key={g.id} className="bg-white rounded-2xl border border-violet-100 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-gray-900 break-words">📁 {g.nom}</p>
                <p className="text-xs text-gray-500">{g.docs.length} documents à signer</p>
              </div>
              <button onClick={() => ouvrirSignatureGroupe(g)} disabled={!!signatureGroupe}
                className="shrink-0 px-4 py-2.5 bg-violet-600 text-white font-bold rounded-xl text-sm hover:bg-violet-700 disabled:opacity-50">
                Lire et signer les {g.docs.length} documents
              </button>
            </div>
          ))}
        </div>
      )}

      <MesEnvoisClient currentUserId={currentUserId} client={currentClient} />

      {(debutResources.length > 0 || extraDebutDocs.length > 0) && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-2 h-6 bg-emerald-500 rounded-full"></div>
            <h2 className="font-bold text-gray-900 text-lg">Documents de début de parcours</h2>
          </div>
          <div className="space-y-3">
            {debutResources.map(renderResourceCard)}
            {extraDebutDocs.map(renderDocumentCard)}
          </div>
        </div>
      )}

      {(finResources.length > 0 || visibleExtraFinDocs.length > 0) && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-2 h-6 bg-violet-600 rounded-full"></div>
            <h2 className="font-bold text-gray-900 text-lg">Documents de fin de parcours</h2>
            <span className="bg-violet-50 text-violet-600 text-xs font-bold px-2 py-1 rounded-full">Débloqués — parcours terminé</span>
          </div>
          <div className="space-y-3">
            {finResources.map(renderResourceCard)}
            {visibleExtraFinDocs.map(renderDocumentCard)}
          </div>
        </div>
      )}

      {assignedToShow.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-2 h-6 bg-amber-500 rounded-full"></div>
            <h2 className="font-bold text-gray-900 text-lg">Questionnaires à remplir</h2>
          </div>
          <div className="space-y-3">
            {assignedToShow.map(renderQuestionnaireCard)}
          </div>
        </div>
      )}

      {/* AJOUT (2026-09-18) : indique au client que des documents/exercices de fin de parcours
          existent mais ne sont pas encore débloqués, plutôt que de les faire disparaître silencieusement. */}
      {finLocked && (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-3xl p-6 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0 text-gray-400">🔒</div>
          <div>
            <p className="font-bold text-gray-700 text-sm">Documents de fin de parcours</p>
            <p className="text-xs text-gray-400 mt-0.5">Ils seront débloqués automatiquement à la date de votre dernière séance.</p>
          </div>
        </div>
      )}

      {extraOtherDocs.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-2 h-6 bg-gray-400 rounded-full"></div>
            <h2 className="font-bold text-gray-900 text-lg">Documents supplémentaires</h2>
          </div>
          <div className="space-y-3">
            {extraOtherDocs.map(renderDocumentCard)}
          </div>
        </div>
      )}

      {!hasAnything && (
        <div className="text-center py-16 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-200" />
          <p className="font-bold text-gray-600">Aucun document pour le moment</p>
          <p className="text-sm mt-1">Votre accompagnateur n'a pas encore ajouté de documents à ce module.</p>
        </div>
      )}

      {signatureGroupe && (
        <SignatureGroupeModal
          titre={signatureGroupe.groupe.nom}
          lots={signatureGroupe.lots}
          preparation={signatureGroupe.preparation}
          onClose={() => setSignatureGroupe(null)}
          onSigner={signerGroupe}
        />
      )}
      {signingResource && (
        <DocumentViewerModal
          isOpen={true}
          url={prefilledSignUrl || signingResource.file_url}
          title={signingResource.titre}
          onClose={handleCloseSigningModal}
          supabase={supabase}
          mode="sign"
          isInteractiveConsent={false}
          requiredCheckboxes={signingCheckboxFields}
          requiredTextFields={signingTextFields}
          requiresSignature={signingRequiresSignature}
          onSave={handleSignSave}
        />
      )}
      {viewingResource && (
        <DocumentViewerModal
          isOpen={true}
          url={viewingResource.file_url}
          title={viewingResource.titre}
          onClose={() => setViewingResource(null)}
          supabase={supabase}
          mode="view"
          isInteractiveConsent={false}
          onSave={() => {}}
        />
      )}
      {isConservationModalOpen && (
        <ConservationModal
          isOpen={true}
          onClose={() => { setIsConservationModalOpen(false); setConservationTargetDoc(null); }}
          onSave={handleConservationSign}
          clientPrenom={currentClient?.prenom || ''}
          clientNom={currentClient?.nom || ''}
          formateurNom={(formateurs || []).find(f => String(f.id) === String(currentClient?.formateur_id))?.nom || ''}
          dateDebut={currentClient?.date_debut ? new Date(currentClient.date_debut).toLocaleDateString('fr-FR') : ''}
          orgNom={orgSettings?.nom || ''}
        />
      )}
    </div>
  );
};

const ExercicesView = ({ setActiveTab, sessions, currentUserId, handleUploadExerciseResponse }) => {
  const exerciseSessions = (sessions || [])
    .filter(s => String(s.client_id) === String(currentUserId) && s.type_activite === 'exercice')
    .sort((a, b) => (a.numero_seance || 0) - (b.numero_seance || 0));
  const [exerciceModalSession, setExerciceModalSession] = React.useState(null);

  return (
    <div className="space-y-6 animate-fade-in relative flex flex-col max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Exercices</h1>
          <p className="text-gray-500 text-lg">Exercices assignés par votre accompagnateur.</p>
        </div>
      </div>

      {exerciseSessions.length === 0 ? (
        <div className="bg-white py-20 text-center rounded-3xl border border-dashed border-gray-200">
          <FileText className="mx-auto text-gray-200 mb-4" size={40} />
          <p className="text-gray-400 italic">Aucun exercice prévu pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
          {exerciseSessions.map(s => {
            const isDone = s.statut_client === 'Signé' || s.statut === 'Signé';
            const isUpcoming = s.date && new Date(s.date) > new Date();
            return (
              <div key={s.id} className="bg-white border-2 border-gray-100 rounded-3xl p-6 relative overflow-hidden group hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 w-24 h-24 bg-violet-50 rounded-bl-full -z-10 transition-transform group-hover:scale-110"></div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{s.ressource_titre || s.nom || `Exercice séance ${s.numero_seance}`}</h3>
                {s.date && (
                  <p className="text-gray-400 text-sm mb-4">
                    {isUpcoming ? `Prévu le ${new Date(s.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}` : `Du ${new Date(s.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide ${s.correction_statut === 'Validé' ? 'bg-green-100 text-green-700' : s.correction_statut === 'À corriger' ? 'bg-amber-100 text-amber-700' : s.reponse_url ? 'bg-indigo-100 text-indigo-700' : isDone ? 'bg-green-100 text-green-800' : isUpcoming ? 'bg-gray-100 text-gray-500' : 'bg-orange-100 text-orange-700'}`}>
                    {s.correction_statut === 'Validé' ? '✅ Validé' : s.correction_statut === 'À corriger' ? '📝 À corriger' : s.reponse_url ? '📬 En attente de correction' : isDone ? '✔ Terminé' : isUpcoming ? '🔒 À venir' : '⏳ En cours'}
                  </span>
                </div>
                {s.correction_commentaire && (
                  <div className="mt-3 px-3 py-2 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">Retour du formateur</p>
                    <p className="text-xs text-gray-600 leading-relaxed">{s.correction_commentaire}</p>
                  </div>
                )}
                {!isUpcoming && (
                  <button
                    onClick={() => setExerciceModalSession(s)}
                    className="mt-4 block w-full text-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors"
                  >
                    {s.reponse_url ? "Modifier le rendu" : "Accéder à l'exercice"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ExerciceModal
        isOpen={!!exerciceModalSession}
        onClose={() => setExerciceModalSession(null)}
        session={exerciceModalSession}
        onSubmit={handleUploadExerciseResponse}
      />
    </div>
  );
};

// ─── Connexion Google Agenda (2026-07-27) ────────────────────────────────────
// Carte affichée dans "Mon Profil" (client et formateur) permettant de connecter/déconnecter
// son Google Agenda. La connexion (tokens) est gérée côté serveur (api/calendar/google/*) ;
// ici on ne fait que lire l'état (via RLS : chacun ne voit que sa propre ligne) et déclencher
// la redirection OAuth ou la déconnexion.
const GoogleCalendarCard = ({ supabase, currentUserId }) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'connected' | 'disconnected'
  const [connectedEmail, setConnectedEmail] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const loadStatus = React.useCallback(async () => {
    if (!currentUserId) return;
    const { data } = await supabase
      .from('calendar_connections')
      .select('connected_email')
      .eq('owner_id', String(currentUserId))
      .eq('provider', 'google')
      .maybeSingle();
    if (data) { setStatus('connected'); setConnectedEmail(data.connected_email || ''); }
    else { setStatus('disconnected'); setConnectedEmail(''); }
  }, [supabase, currentUserId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSyncAll = React.useCallback(async (silent = false) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const toastId = silent ? undefined : toast.loading('Synchronisation de vos séances...');
    try {
      const resp = await fetch('/api/calendar/sync-all', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (toastId) toast.error(data.error || 'Échec de la synchronisation.', { id: toastId });
        return;
      }
      if (toastId) {
        if (data.total > 0 && data.synced < data.total) {
          const detail = (data.errors && data.errors[0]) ? ` — ${data.errors[0]}` : '';
          toast.error(`${data.synced}/${data.total} séance(s) synchronisée(s)${detail}`, { id: toastId, duration: 8000 });
          if (data.errors?.length) console.error('[sync-all] erreurs :', data.errors);
        } else {
          toast.success(
            data.total > 0 ? `${data.synced}/${data.total} séance(s) synchronisée(s) avec Google Agenda.` : 'Aucune séance à synchroniser pour le moment.',
            { id: toastId }
          );
        }
      }
    } catch (e) {
      if (toastId) toast.error('Erreur lors de la synchronisation.', { id: toastId });
    }
  }, [supabase]);

  // Affiche un message une seule fois au retour du flux OAuth (?calendar=connected|error dans l'URL),
  // et rattrape automatiquement les séances déjà créées AVANT la connexion (sync-seance.js ne pousse
  // que ce qui est créé/modifié APRÈS coup — sans ce rattrapage, l'agenda semblerait vide).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('calendar');
    if (!result) return;
    if (result === 'connected') { toast.success('Google Agenda connecté !'); loadStatus(); handleSyncAll(); }
    else if (result === 'error') { toast.error('La connexion à Google Agenda a échoué. Réessayez.'); }
    params.delete('calendar');
    const newSearch = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setIsBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Session expirée, reconnectez-vous.'); setIsBusy(false); return; }
      const resp = await fetch('/api/calendar/google/start', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await resp.json();
      if (!resp.ok || !data.url) {
        toast.error(data.error || "Impossible de démarrer la connexion Google Agenda.");
        setIsBusy(false);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error("Erreur lors de la connexion à Google Agenda.");
      setIsBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setIsBusy(true);
    const { error } = await supabase.from('calendar_connections').delete().eq('owner_id', String(currentUserId)).eq('provider', 'google');
    if (error) toast.error('Erreur lors de la déconnexion.');
    else { toast.success('Google Agenda déconnecté.'); setStatus('disconnected'); setConnectedEmail(''); }
    setIsBusy(false);
  };

  return (
    <div>
      <h3 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
        <ExternalLink size={16} className="text-indigo-500" /> Google Agenda
      </h3>
      <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          {status === 'connected' ? (
            <>
              <p className="text-sm font-bold text-gray-800">✅ Connecté{connectedEmail ? ` — ${connectedEmail}` : ''}</p>
              <p className="text-xs text-gray-400 mt-1">Vos séances sont ajoutées automatiquement à votre Google Agenda.</p>
            </>
          ) : status === 'disconnected' ? (
            <>
              <p className="text-sm font-bold text-gray-800">Non connecté</p>
              <p className="text-xs text-gray-400 mt-1">Connectez votre Google Agenda pour y retrouver automatiquement vos séances.</p>
            </>
          ) : (
            <p className="text-sm text-gray-400">Vérification...</p>
          )}
        </div>
        {status === 'connected' ? (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => handleSyncAll(false)} disabled={isBusy} className="text-xs font-bold px-4 py-2.5 rounded-xl border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50">
              Resynchroniser
            </button>
            <button onClick={handleDisconnect} disabled={isBusy} className="text-xs font-bold px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50">
              {isBusy ? '...' : 'Déconnecter'}
            </button>
          </div>
        ) : status === 'disconnected' ? (
          <button onClick={handleConnect} disabled={isBusy} className="text-xs font-bold px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 shrink-0">
            {isBusy ? 'Redirection...' : 'Connecter mon Google Agenda'}
          </button>
        ) : null}
      </div>
    </div>
  );
};

const ProfileView = ({ currentUserId, supabase, fetchUtilisateurs, formateurs, clients, userRole, orgSettings, onOrgSaved, currentOrgId }) => {
  const user = userRole === 'formateur' ? formateurs.find(f => f.id === currentUserId) : (userRole === 'client' ? clients.find(c => c.id === currentUserId) : null);

  const [logoUrl, setLogoUrl] = useState(orgSettings?.logo_url || '');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const [profileData, setProfileData] = useState({
    nom: user?.nom || '',
    nom_organisme: orgSettings?.nom || '',
    adresse_formateur: user?.adresse_formateur || user?.adresse_pro || '',
    adresse_session: user?.adresse_session || '',
    region: user?.region || '',
    adresse_org: orgSettings?.adresse || '',
    code_postal_org: orgSettings?.code_postal || '',
    ville_org: orgSettings?.ville || '',
    region_org: orgSettings?.region || '',
    siret: user?.formateur_siret || orgSettings?.siret || '',
    nda: user?.formateur_nda || orgSettings?.nda || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    compagnie_assurance: user?.compagnie_assurance || '',
    numero_assurance_rcp: user?.numero_assurance_rcp || '',
    nomcomplet: user?.nomcomplet_client || user?.nom || '',
    adresse_client: user?.adresse_postale || '',
    site_web: orgSettings?.site_web || ''
  });
  const [sameAddress, setSameAddress] = useState(
    !user?.adresse_session || user?.adresse_session === (user?.adresse_formateur || user?.adresse_pro || '')
  );

  // CORRECTIF (2026-10-12) : pour un client, on charge sa fiche depuis la base. Avant, si la page
  // « Mon profil » était ouverte directement (rechargement, lien), les champs restaient vides et un
  // clic sur « Enregistrer » effaçait son nom et son adresse.
  const [profilClientCharge, setProfilClientCharge] = useState(false);
  useEffect(() => {
    if (userRole !== 'client' || !currentUserId) return;
    supabase.from('clients').select('nom_complet, adresse_postale, telephone, email_contact').eq('id', currentUserId).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setProfileData(prev => ({
            ...prev,
            nomcomplet: data.nom_complet || '',
            adresse_client: data.adresse_postale || '',
            telephone: data.telephone || '',
            email: data.email_contact || '',
          }));
          setProfilClientCharge(true);
        }
      });
  }, [userRole, currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Changement de mot de passe (tous les rôles)
  const [nouveauMdp, setNouveauMdp] = useState('');
  const [confirmationMdp, setConfirmationMdp] = useState('');
  const [changementMdpEnCours, setChangementMdpEnCours] = useState(false);
  const handleChangerMotDePasse = async () => {
    if (nouveauMdp.length < 8) { toast.error('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (nouveauMdp !== confirmationMdp) { toast.error('Les deux mots de passe ne sont pas identiques.'); return; }
    setChangementMdpEnCours(true);
    const { error } = await supabase.auth.updateUser({ password: nouveauMdp });
    setChangementMdpEnCours(false);
    if (error) { toast.error('Impossible de changer le mot de passe : ' + error.message); return; }
    setNouveauMdp(''); setConfirmationMdp('');
    toast.success('Votre mot de passe a bien été modifié.');
  };

  // Pour les admins et formateurs : charger les vraies données depuis la BDD
  useEffect(() => {
    if ((userRole === 'admin' || userRole === 'formateur') && currentUserId) {
      supabase.from('utilisateurs').select('*').eq('id', currentUserId).single()
        .then(({ data }) => {
          if (data) {
            setProfileData(prev => ({
              ...prev,
              nom: data.nom || '',
              email: data.email || '',
              telephone: data.telephone || '',
              adresse_formateur: data.adresse_formateur || '',
              adresse_session: data.adresse_session || '',
              region: data.region || '',
              siret: data.formateur_siret || '',
              nda: data.formateur_nda || '',
              compagnie_assurance: data.compagnie_assurance || '',
              numero_assurance_rcp: data.numero_assurance_rcp || '',
            }));
          }
        });
    }
  }, [userRole, currentUserId]);

  // Synchro nom organisme, siret, nda, site_web, adresse et logo depuis orgSettings (admin)
  useEffect(() => {
    if (userRole === 'admin' && orgSettings) {
      setProfileData(prev => ({
        ...prev,
        nom_organisme: orgSettings.nom || '',
        siret: orgSettings.siret || '',
        nda: orgSettings.nda || '',
        site_web: orgSettings.site_web || '',
        adresse_org: orgSettings.adresse || '',
        code_postal_org: orgSettings.code_postal || '',
        ville_org: orgSettings.ville || '',
        region_org: orgSettings.region || ''
      }));
      setLogoUrl(orgSettings.logo_url || '');
    }
  }, [userRole, orgSettings]);

  const handleLogoUpload = async (file) => {
    const orgId = orgSettings?.id || currentOrgId;
    if (!file || !orgId) { toast.error("Impossible d'identifier votre organisation. Réessayez."); return; }
    setIsUploadingLogo(true);
    const ext = file.name.split('.').pop();
    const path = `${orgId}/logo.${ext}`;
    const { error } = await supabase.storage.from('logos').upload(path, file, { upsert: true });
    if (error) { toast.error('Erreur upload logo : ' + error.message); setIsUploadingLogo(false); return; }
    const { data } = supabase.storage.from('logos').getPublicUrl(path);
    const newLogoUrl = data.publicUrl;
    const { error: errLogo } = await supabase.from('organisations').update({ logo_url: newLogoUrl }).eq('id', orgId);
    if (errLogo) { toast.error("Le logo a été envoyé mais n'a pas pu être enregistré : " + errLogo.message); setIsUploadingLogo(false); return; }
    setLogoUrl(newLogoUrl);
    if (onOrgSaved) onOrgSaved({ logo_url: newLogoUrl });
    setIsUploadingLogo(false);
    toast.success('Logo mis à jour !');
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleUpdate = async () => {
    setIsSaving(true);
    let error;
    if (userRole === 'client') {
      if (!profilClientCharge || !String(profileData.nomcomplet || '').trim()) {
        toast.error(!profilClientCharge ? 'Vos informations sont en cours de chargement, réessayez dans un instant.' : 'Indiquez votre nom.');
        setIsSaving(false);
        return;
      }
      const result = await supabase.from('clients').update({
        nom_complet: profileData.nomcomplet.trim(),
        adresse_postale: profileData.adresse_client,
        telephone: profileData.telephone || null,
      }).eq('id', currentUserId);
      error = result.error;
    } else {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const result = await updateOwnStaffProfile(authUser, {
        nom: profileData.nom,
        email: profileData.email,
        telephone: profileData.telephone,
        adresse_formateur: profileData.adresse_formateur,
        adresse_session: sameAddress ? profileData.adresse_formateur : profileData.adresse_session,
        region: profileData.region,
        formateur_siret: profileData.siret,
        formateur_nda: profileData.nda,
        compagnie_assurance: profileData.compagnie_assurance,
        numero_assurance_rcp: profileData.numero_assurance_rcp
      });
      error = result.error;
      if (!error && userRole === 'admin' && orgSettings?.id) {
        const orgResult = await supabase.from('organisations').update({
          nom: profileData.nom_organisme,
          siret: profileData.siret,
          nda: profileData.nda,
          site_web: profileData.site_web,
          adresse: profileData.adresse_org,
          code_postal: profileData.code_postal_org,
          ville: profileData.ville_org,
          region: profileData.region_org
        }).eq('id', orgSettings.id);
        if (orgResult.error) error = orgResult.error;
        else if (onOrgSaved) onOrgSaved({
          nom: profileData.nom_organisme,
          siret: profileData.siret,
          nda: profileData.nda,
          site_web: profileData.site_web,
          adresse: profileData.adresse_org,
          code_postal: profileData.code_postal_org,
          ville: profileData.ville_org,
          region: profileData.region_org
        });
      }
    }
    if (!error) {
      toast.success('Profil mis à jour avec succès !');
      fetchUtilisateurs();
    } else {
      toast.error('Erreur lors de la mise à jour : ' + error.message);
    }
    setIsSaving(false);
  };

  const inputCls = "w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm";
  const labelCls = "block text-xs font-bold text-gray-400 uppercase mb-2";

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl mx-auto">
      <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
          <span className="w-2 h-8 bg-indigo-600 rounded-full mr-4"></span> Mon Profil
        </h2>

        {userRole === 'client' ? (
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Nom Complet</label>
              <input className={inputCls} value={profileData.nomcomplet} onChange={e => setProfileData({ ...profileData, nomcomplet: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Adresse</label>
              <input className={inputCls} value={profileData.adresse_client} onChange={e => setProfileData({ ...profileData, adresse_client: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Téléphone</label>
                <input type="tel" className={inputCls} value={profileData.telephone || ''} onChange={e => setProfileData({ ...profileData, telephone: e.target.value })} placeholder="06 12 34 56 78" />
              </div>
              <div>
                <label className={labelCls}>Email de connexion</label>
                <input className={`${inputCls} text-gray-500`} value={profileData.email || ''} readOnly />
                <p className="text-xs text-gray-400 mt-1">Pour le changer, contactez votre organisme de formation.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Logo organisme (admin uniquement) */}
            {userRole === 'admin' && (
              <div>
                <h3 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                  <Upload size={16} className="text-indigo-500" /> Logo de votre espace
                </h3>
                <div className="flex items-center gap-6">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="w-24 h-24 object-contain rounded-xl border border-gray-200 bg-gray-50 p-2" />
                  ) : (
                    <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 bg-gray-50 gap-1">
                      <Upload size={24} />
                      <span className="text-xs text-center leading-tight">Votre logo</span>
                    </div>
                  )}
                  <div>
                    <input type="file" accept="image/*" id="profile-logo-upload" className="hidden"
                      onChange={(e) => { if (e.target.files?.[0]) handleLogoUpload(e.target.files[0]); }} />
                    <label htmlFor="profile-logo-upload" className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm px-4 py-2.5 rounded-xl transition-colors">
                      {isUploadingLogo ? 'Chargement...' : 'Télécharger votre logo'}
                    </label>
                    <p className="text-xs text-gray-400 mt-2">PNG, JPG, SVG — 2 Mo max</p>
                  </div>
                </div>
              </div>
            )}
            {/* Identité */}
            <div>
              <h3 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                <FileText size={16} className="text-indigo-500" /> Identité Professionnelle
              </h3>
              <div className="space-y-4">
                {userRole === 'admin' ? (
                  <div>
                    <label className={labelCls}>Raison Sociale</label>
                    <input className={inputCls} value={profileData.nom_organisme} onChange={e => setProfileData({ ...profileData, nom_organisme: e.target.value })} placeholder="Nom de votre entreprise" />
                  </div>
                ) : (
                  <div>
                    <label className={labelCls}>Raison Sociale / Nom complet</label>
                    <input className={inputCls} value={profileData.nom} onChange={e => setProfileData({ ...profileData, nom: e.target.value })} placeholder="Nom de votre entreprise" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>SIRET</label>
                    <input className={inputCls} value={profileData.siret} onChange={e => setProfileData({ ...profileData, siret: e.target.value })} placeholder="14 chiffres" />
                  </div>
                  <div>
                    <label className={labelCls}>NDA (Qualiopi)</label>
                    <input className={inputCls} value={profileData.nda} onChange={e => setProfileData({ ...profileData, nda: e.target.value })} placeholder="N° Déclaration" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Assurance RCP — Compagnie</label>
                  <input className={inputCls} value={profileData.compagnie_assurance} onChange={e => setProfileData({ ...profileData, compagnie_assurance: e.target.value })} placeholder="Ex: AXA, MAIF, Hiscox..." />
                </div>
                <div>
                  <label className={labelCls}>Assurance RCP — N° de contrat</label>
                  <input className={inputCls} value={profileData.numero_assurance_rcp} onChange={e => setProfileData({ ...profileData, numero_assurance_rcp: e.target.value })} placeholder="N° de contrat RCP" />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                <Plus size={16} className="text-violet-600" /> Coordonnées & Contact
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Email professionnel</label>
                  <input className={inputCls} type="email" value={profileData.email} onChange={e => setProfileData({ ...profileData, email: e.target.value })} placeholder="email@pro.fr" />
                </div>
                <div>
                  <label className={labelCls}>Téléphone</label>
                  <input className={inputCls} type="tel" value={profileData.telephone} onChange={e => setProfileData({ ...profileData, telephone: e.target.value })} placeholder="06..." />
                </div>
              </div>
              {userRole === 'admin' && (
                <div className="mb-4">
                  <label className={labelCls}>Site internet</label>
                  <input className={inputCls} type="url" value={profileData.site_web} onChange={e => setProfileData({ ...profileData, site_web: e.target.value })} placeholder="https://www.votresite.fr" />
                </div>
              )}
              <div className="space-y-4">
                {userRole === 'admin' ? (
                  <>
                    <div>
                      <label className={labelCls}>Adresse Siège Social</label>
                      <AddressAutocomplete
                        value={profileData.adresse_org}
                        onChange={val => setProfileData({ ...profileData, adresse_org: val })}
                        onSelect={({ rue, codePostal, ville, region }) => setProfileData(prev => ({ ...prev, adresse_org: rue, code_postal_org: codePostal || prev.code_postal_org, ville_org: ville || prev.ville_org, region_org: region || prev.region_org }))}
                        placeholder="N° et nom de rue"
                        className={inputCls}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Code Postal</label>
                        <input className={inputCls} value={profileData.code_postal_org} onChange={e => setProfileData({ ...profileData, code_postal_org: e.target.value })} placeholder="75000" />
                      </div>
                      <div>
                        <label className={labelCls}>Ville</label>
                        <input className={inputCls} value={profileData.ville_org} onChange={e => setProfileData({ ...profileData, ville_org: e.target.value })} placeholder="Paris" />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Région</label>
                      <input list="region-list-profile-org" className={inputCls} value={profileData.region_org} onChange={e => setProfileData({ ...profileData, region_org: e.target.value })} placeholder="Ex : Bretagne" autoComplete="off" />
                      <datalist id="region-list-profile-org">{FRENCH_REGIONS.map(r => <option key={r} value={r} />)}</datalist>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className={labelCls}>Adresse Siège Social</label>
                      <AddressAutocomplete
                        value={profileData.adresse_formateur}
                        onChange={val => setProfileData({ ...profileData, adresse_formateur: val })}
                        onSelect={({ label, region }) => setProfileData(prev => ({ ...prev, adresse_formateur: label, region: region || prev.region }))}
                        placeholder="Ex : 12 Rue de la Paix, 75001 Paris"
                        className={inputCls}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="profSameAddress" checked={sameAddress} onChange={e => setSameAddress(e.target.checked)} className="w-4 h-4 accent-indigo-500 cursor-pointer rounded" />
                      <label htmlFor="profSameAddress" className="text-sm text-gray-600 cursor-pointer select-none">Même adresse pour les sessions de formation</label>
                    </div>
                    {!sameAddress && (
                      <div>
                        <label className={labelCls}>Adresse de Pratique</label>
                        <AddressAutocomplete
                          value={profileData.adresse_session}
                          onChange={val => setProfileData({ ...profileData, adresse_session: val })}
                          placeholder="Ex : 12 Rue de la Paix, 75001 Paris"
                          className={inputCls}
                        />
                      </div>
                    )}
                    <div>
                      <label className={labelCls}>Région</label>
                      <input list="region-list-profile-formateur" className={inputCls} value={profileData.region} onChange={e => setProfileData({ ...profileData, region: e.target.value })} placeholder="Ex : Bretagne" autoComplete="off" />
                      <datalist id="region-list-profile-formateur">{FRENCH_REGIONS.map(r => <option key={r} value={r} />)}</datalist>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="pt-8 mt-6 border-t border-gray-100">
          <button
            onClick={handleUpdate}
            disabled={isSaving}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save size={20} /> {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </div>
      </div>

      {/* Mot de passe (tous les rôles) */}
      <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
        <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2"><KeyRound size={18} className="text-indigo-500" /> Mot de passe</h3>
        <p className="text-sm text-gray-500 mb-5">Choisissez un nouveau mot de passe d'au moins 8 caractères.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input type="password" autoComplete="new-password" className={inputCls} placeholder="Nouveau mot de passe" value={nouveauMdp} onChange={e => setNouveauMdp(e.target.value)} />
          <input type="password" autoComplete="new-password" className={inputCls} placeholder="Confirmez le mot de passe" value={confirmationMdp} onChange={e => setConfirmationMdp(e.target.value)} />
        </div>
        <button onClick={handleChangerMotDePasse} disabled={changementMdpEnCours || !nouveauMdp}
          className="mt-4 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-40">
          {changementMdpEnCours ? 'Modification…' : 'Changer mon mot de passe'}
        </button>
      </div>

      {(userRole === 'client' || userRole === 'formateur') && (
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <GoogleCalendarCard supabase={supabase} currentUserId={currentUserId} />
        </div>
      )}
    </div>
  );
};

const RessourcesView = ({ pedagogicalResources, supabase, currentUserId }) => {
  const [persoResources, setPersoResources] = React.useState([]);
  const [uploadFile, setUploadFile] = React.useState(null);
  const [uploadName, setUploadName] = React.useState('');
  const [isUploading, setIsUploading] = React.useState(false);
  const [showUploadForm, setShowUploadForm] = React.useState(false);

  const fetchPersoResources = React.useCallback(async () => {
    if (!currentUserId) return;
    const { data } = await supabase.storage
      .from('ressources-pedagogiques')
      .list(`formateur-perso/${currentUserId}`);
    if (data) setPersoResources(data.filter(f => f.name !== '.emptyFolderPlaceholder'));
  }, [supabase, currentUserId]);

  React.useEffect(() => { fetchPersoResources(); }, [fetchPersoResources]);

  const handleDownloadShared = async (fileName) => {
    const { data, error } = await supabase.storage.from('ressources-pedagogiques').createSignedUrl(fileName, 60);
    if (!error && data) window.open(data.signedUrl, '_blank');
    else toast.error('Erreur téléchargement : ' + error?.message);
  };

  const handleDownloadPerso = async (fileName) => {
    const path = `formateur-perso/${currentUserId}/${fileName}`;
    const { data, error } = await supabase.storage.from('ressources-pedagogiques').createSignedUrl(path, 60);
    if (!error && data) window.open(data.signedUrl, '_blank');
    else toast.error('Erreur téléchargement : ' + error?.message);
  };

  const handlePersonalUpload = async () => {
    if (!uploadFile || !uploadName.trim()) { toast.error('Renseignez un nom et sélectionnez un fichier.'); return; }
    setIsUploading(true);
    const ext = uploadFile.name.split('.').pop();
    const safeN = uploadName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const path = `formateur-perso/${currentUserId}/${Date.now()}_${safeN}.${ext}`;
    const { error } = await supabase.storage.from('ressources-pedagogiques').upload(path, uploadFile);
    if (error) { toast.error('Erreur upload : ' + error.message); }
    else {
      toast.success('Document enregistré dans votre espace !');
      setUploadFile(null); setUploadName(''); setShowUploadForm(false);
      fetchPersoResources();
    }
    setIsUploading(false);
  };

  const ACCENTS_RESSOURCE = {
    emerald: { icone: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600', lien: 'hover:text-emerald-600', bouton: 'hover:bg-emerald-600 hover:border-emerald-600' },
    indigo: { icone: 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600', lien: 'hover:text-indigo-600', bouton: 'hover:bg-indigo-600 hover:border-indigo-600' },
  };
  const ResourceCard = ({ name, displayName, onDownload, accent = 'emerald' }) => { const ac = ACCENTS_RESSOURCE[accent] || ACCENTS_RESSOURCE.emerald; return (
    <div className={`bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all group`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 ${ac.icone} rounded-2xl flex items-center justify-center group-hover:text-white transition-colors`}>
          <FileText size={24} />
        </div>
        <button onClick={onDownload} className={`text-gray-400 ${ac.lien} transition-colors`}>
          <Download size={20} />
        </button>
      </div>
      <h3 className="font-bold text-gray-900 text-sm mb-1 truncate" title={displayName}>{displayName}</h3>
      <button
        onClick={onDownload}
        className={`mt-4 w-full py-2 bg-gray-50 text-gray-700 text-xs font-bold rounded-xl border border-gray-100 ${ac.bouton} hover:text-white transition-all uppercase tracking-wider`}
      >
        Télécharger
      </button>
    </div>
  ); };

  return (
    <div className="space-y-10 animate-fade-in max-w-7xl mx-auto">

      {/* Section 1 : Mes documents personnels */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Mon Espace Documents</h1>
            <p className="text-gray-500 text-lg">Stockez vos supports de cours, assurances, et documents personnels.</p>
          </div>
          <button
            onClick={() => setShowUploadForm(v => !v)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl shadow-lg shadow-indigo-100 transition-all"
          >
            <Upload size={16} /> Ajouter un document
          </button>
        </div>

        {showUploadForm && (
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 mb-6 space-y-3">
            <input
              type="text"
              value={uploadName}
              onChange={e => setUploadName(e.target.value)}
              placeholder="Nom du document (ex: Attestation Assurance 2025)"
              className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl p-3 outline-none focus:ring-2 focus:ring-indigo-300"
            />
            <input
              type="file"
              onChange={e => setUploadFile(e.target.files[0] || null)}
              className="w-full bg-white border border-gray-200 text-sm rounded-xl p-2 outline-none"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            <div className="flex gap-2">
              <button
                onClick={handlePersonalUpload}
                disabled={isUploading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-black py-2.5 rounded-xl transition-all disabled:opacity-50"
              >
                {isUploading ? 'Envoi...' : 'Enregistrer'}
              </button>
              <button
                onClick={() => { setShowUploadForm(false); setUploadFile(null); setUploadName(''); }}
                className="px-5 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {persoResources.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {persoResources.map(res => {
              const displayName = res.name.replace(/^\d+_/, '').replace(/_/g, ' ');
              return <ResourceCard key={res.name} name={res.name} displayName={displayName} onDownload={() => handleDownloadPerso(res.name)} accent="indigo" />;
            })}
          </div>
        ) : (
          <div className="bg-white py-14 text-center rounded-3xl border border-dashed border-gray-200">
            <FileText className="mx-auto text-gray-200 mb-4" size={40} />
            <p className="text-gray-400 italic text-sm">Aucun document personnel. Cliquez sur "Ajouter un document" pour commencer.</p>
          </div>
        )}
      </div>

      {/* Section 2 : Ressources partagées par l'administration */}
      {pedagogicalResources.length > 0 && (
        <div>
          <div className="mb-5">
            <h2 className="text-xl font-extrabold text-gray-700 tracking-tight">Bibliothèque de l'organisme</h2>
            <p className="text-gray-400 text-sm">Documents mis à disposition par votre organisme.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pedagogicalResources.map(res => {
              const displayName = res.name.split('_').slice(1).join('_') || res.name;
              return <ResourceCard key={res.path || res.name} name={res.name} displayName={displayName} onDownload={() => handleDownloadShared(res.path || res.name)} accent="emerald" />;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SetPasswordView = ({ supabase, onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');

  // Au montage : vérifier le token dans l'URL via verifyOtp
  useEffect(() => {
    const verifyToken = async () => {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const tokenHash = params.get('token_hash');
      const type = params.get('type');

      if (tokenHash && type) {
        // Échanger le token_hash contre une session Supabase
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type
        });
        if (error) {
          console.error('Erreur verifyOtp:', error);
          setVerifyError('Le lien est invalide ou a expiré. Demandez un nouveau lien à votre administrateur.');
        }
      }
      // Si pas de token_hash, on cherche access_token (ancien flux)
      // Supabase le gère automatiquement via onAuthStateChange
      setIsVerifying(false);
    };
    verifyToken();
  }, [supabase]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (password.length < 8) {
      setErrorMsg('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Les mots de passe ne correspondent pas.');
      return;
    }

    setIsUpdating(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) {
      // Nettoyer les tokens sensibles mais garder l'email pour le LoginView si besoin
      const params = new URLSearchParams(window.location.hash.substring(1));
      const email = params.get('email');
      const newHash = email ? `#email=${encodeURIComponent(email)}` : '';
      window.history.replaceState(null, '', window.location.pathname + newHash);
      onComplete();
    } else {
      setErrorMsg('Erreur : ' + error.message);
    }
    setIsUpdating(false);
  };

  if (isVerifying) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-md animate-fade-in text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg animate-pulse" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-9 h-9 object-contain" /></div>
          <p className="text-gray-500 font-medium">Vérification de votre lien en cours...</p>
        </div>
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-md animate-fade-in text-center">
          <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center text-white text-2xl font-black mx-auto mb-6 shadow-lg">!</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Lien expiré</h2>
          <p className="text-gray-500 text-sm mb-6">{verifyError}</p>
          <button onClick={() => { window.history.replaceState(null, '', '/'); window.location.reload(); }} className="bg-gray-900 text-white font-bold py-3 px-6 rounded-xl">Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-6">
      <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-md animate-fade-in">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-9 h-9 object-contain" /></div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Finalisez votre accès</h2>
        <p className="text-gray-500 text-sm mb-6 text-center">Créez votre mot de passe pour accéder à SkorUp.</p>
        <form onSubmit={handleSetPassword} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Nouveau mot de passe</label>
            <input
              type="password"
              required
              minLength={8}
              placeholder="Minimum 8 caractères"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Confirmer le mot de passe</label>
            <input
              type="password"
              required
              placeholder="Saisissez à nouveau votre mot de passe"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            />
          </div>
          {errorMsg && (
            <p className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl">{errorMsg}</p>
          )}
          <button
            type="submit"
            disabled={isUpdating}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isUpdating ? 'Configuration en cours...' : 'Valider et accéder à mon espace'}
          </button>
        </form>
      </div>
    </div>
  );
};

const ResetPasswordPage = ({ supabase, onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const tokenHash = params.get('token_hash');
      const type = params.get('type');

      if (tokenHash && type === 'recovery') {
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
        if (error) {
          setVerifyError('Lien expiré ou invalide. Veuillez refaire une demande de réinitialisation.');
        }
      }
      setIsVerifying(false);
    };
    verifyToken();
  }, [supabase]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (password.length < 8) {
      setErrorMsg('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Les mots de passe ne correspondent pas.');
      return;
    }

    setIsUpdating(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) {
      onComplete();
    } else {
      setErrorMsg('Erreur lors de la mise à jour : ' + error.message);
    }
    setIsUpdating(false);
  };

  if (isVerifying) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white p-8 rounded-3xl shadow-xl w-full max-w-md animate-fade-in text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg animate-pulse" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-9 h-9 object-contain" /></div>
          <p className="text-gray-500 font-medium">Vérification de votre lien en cours...</p>
        </div>
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-md animate-fade-in text-center">
          <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center text-white text-2xl font-black mx-auto mb-6">!</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Lien expiré</h2>
          <p className="text-gray-500 text-sm mb-6">{verifyError}</p>
          <button onClick={() => { window.history.replaceState(null, '', '/'); window.location.reload(); }} className="bg-gray-900 text-white font-bold py-3 px-6 rounded-xl">Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-6">
      <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-md animate-fade-in">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{background:'#100524'}}><img src="/logo-mark.png" alt="SkorUp" className="w-9 h-9 object-contain" /></div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Nouveau mot de passe</h2>
        <p className="text-gray-500 text-sm mb-6 text-center">Créez votre nouveau mot de passe sécurisé.</p>
        <form onSubmit={handleUpdatePassword} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Nouveau mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Confirmer le mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {errorMsg && (
            <p className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl">{errorMsg}</p>
          )}
          <button
            type="submit"
            disabled={isUpdating}
            className="w-full bg-violet-700 hover:bg-violet-700 text-white font-bold py-3 rounded-xl shadow-lg transition-all disabled:opacity-50"
          >
            {isUpdating ? 'Enregistrement...' : 'Enregistrer le nouveau mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );
};

const InviteModal = ({ isOpen, onClose, onInvite, isAddingUser, formateurs, defaultRole }) => {
  const [formData, setFormData] = useState({ nom: '', email: '', role: 'client', formateur_id: '' });
  // 2026-10-21 : décocher = créer le dossier sans prévenir le client (prospect en étude, financement en attente…).
  const [envoyerInvitation, setEnvoyerInvitation] = useState(true);

  // FIX (2026-09-15) : le formulaire gardait le nom/email/formateur du DERNIER utilisateur créé
  // quand on rouvrait la modale pour en créer un autre — car ce composant reste monté en permanence
  // (il retourne juste `null` quand isOpen est faux, voir plus bas) donc son state React n'était
  // jamais réinitialisé entre deux ouvertures. Signalé par l'utilisateur le 15/09/2026. On réinitialise
  // maintenant TOUT le formulaire (pas seulement le rôle) à chaque ouverture — tout en gardant la
  // pré-sélection du rôle depuis l'onglet d'origine (Clients → client, Formateurs → formateur, ajouté
  // le 2026-08-05). En cas d'erreur d'envoi, la modale reste ouverte SANS se refermer (voir handleInvite
  // plus bas) donc les données saisies ne sont pas perdues — seule une réouverture après un succès
  // (fermeture automatique) repart bien de zéro.
  React.useEffect(() => {
    if (isOpen) {
      setFormData({ nom: '', email: '', role: defaultRole || 'client', formateur_id: '' });
      setEnvoyerInvitation(true);
    }
  }, [isOpen, defaultRole]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100 w-full max-w-md animate-scale-up relative">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600">✕</button>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Inviter un Utilisateur</h2>
        <p className="text-gray-500 text-sm mb-8">{formData.role === 'client' && !envoyerInvitation
          ? "Le dossier sera créé sans envoyer d'email au client."
          : 'Un email sera envoyé pour définir le mot de passe.'}</p>

        <form onSubmit={(e) => { e.preventDefault(); onInvite({ ...formData, envoyerInvitation: formData.role === 'client' ? envoyerInvitation : true }); }} className="space-y-5">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Nom Complet</label>
            <input
              required
              className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.nom}
              onChange={e => setFormData({ ...formData, nom: e.target.value })}
              placeholder="Jean Dupont"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Email</label>
            <input
              required
              type="email"
              className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              placeholder="jean.dupont@email.com"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Rôle</label>
            <select
              className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.role}
              onChange={e => setFormData({ ...formData, role: e.target.value })}
            >
              <option value="client">Client (Bénéficiaire)</option>
              <option value="formateur">Formateur</option>
            </select>
          </div>

          {formData.role === 'client' && (
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Formateur Référent</label>
              <select
                required
                className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.formateur_id}
                onChange={e => setFormData({ ...formData, formateur_id: e.target.value })}
              >
                <option value="">Sélectionner un formateur</option>
                {formateurs.map(f => (
                  <option key={f.id} value={f.id}>{f.nom}</option>
                ))}
              </select>
            </div>
          )}

          {formData.role === 'client' && (
            <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-violet-600"
                checked={envoyerInvitation}
                onChange={e => setEnvoyerInvitation(e.target.checked)}
              />
              <span className="text-sm">
                <span className="font-bold text-gray-800">Envoyer l'invitation par email maintenant</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {envoyerInvitation
                    ? 'Le client reçoit tout de suite un email pour créer son mot de passe.'
                    : "Aucun email n'est envoyé (ni invitation, ni rappel). Vous pourrez suivre le dossier normalement, puis envoyer l'accès depuis sa fiche quand il sera validé."}
                </span>
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={isAddingUser}
            className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2"
          >
            {isAddingUser
              ? (formData.role === 'client' && !envoyerInvitation ? 'Création...' : 'Envoi...')
              : (formData.role === 'client' && !envoyerInvitation ? 'Créer le dossier' : 'Envoyer l\'invitation')}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==========================================
// ESPACE PROCESSUS PARTAGÉS
// ==========================================

const SharedProcessesView = ({ supabase, userRole, currentUserId, currentOrgId }) => {
  const isAdmin = userRole === 'admin';
  const isFormateur = userRole === 'formateur';
  const isClient = userRole === 'client';

  const [processes, setProcesses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [isAdding, setIsAdding] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [form, setForm] = React.useState({ titre: '', description: '', type: 'link', url: '', visible_client: true, visible_formateur: true });
  const [uploadFile, setUploadFile] = React.useState(null);
  const [filterType, setFilterType] = React.useState('all');
  const [processToDelete, setProcessToDelete] = React.useState(null);

  const fetchProcesses = React.useCallback(async () => {
    setLoading(true);
    const db = supabase;
    // Le filtre est désormais TOUJOURS appliqué (avant : seulement si currentOrgId était renseigné,
    // sinon la requête remontait silencieusement les shared_processes de TOUS les organismes).
    let query = db.from('shared_processes').select('*').order('created_at', { ascending: false });
    query = currentOrgId ? query.eq('organisation_id', currentOrgId) : query.limit(0);
    const { data, error } = await query;
    if (!error && data) setProcesses(data);
    setLoading(false);
  }, [supabase, currentOrgId]);

  React.useEffect(() => { fetchProcesses(); }, [fetchProcesses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) { toast.error('Titre requis.'); return; }
    setIsSaving(true);
    const db = supabase;

    let fileUrl = form.url || null;

    if (uploadFile && (form.type === 'pdf' || form.type === 'document')) {
      const ext = uploadFile.name.split('.').pop();
      const path = `processes/${currentOrgId || 'global'}/${Date.now()}.${ext}`;
      const { error: upErr } = await db.storage.from('documents').upload(path, uploadFile, { upsert: true });
      if (upErr) { toast.error('Erreur upload : ' + upErr.message); setIsSaving(false); return; }
      const { data: pub } = db.storage.from('documents').getPublicUrl(path);
      fileUrl = pub?.publicUrl || null;
    }

    const { error } = await db.from('shared_processes').insert([{
      organisation_id: currentOrgId || null,
      titre: form.titre.trim(),
      description: form.description.trim() || null,
      type: form.type,
      url: fileUrl,
      visible_client: form.visible_client,
      visible_formateur: form.visible_formateur,
    }]);

    setIsSaving(false);
    if (error) { toast.error('Erreur : ' + error.message); return; }
    toast.success('Processus ajouté !');
    setForm({ titre: '', description: '', type: 'link', url: '', visible_client: true, visible_formateur: true });
    setUploadFile(null);
    setIsAdding(false);
    fetchProcesses();
  };

  const handleDelete = async () => {
    if (!processToDelete) return;
    const db = supabase;
    // Scopé à l'organisme courant (+ lignes historiques sans organisation_id) : avant ce correctif,
    // la suppression ne filtrait que par id, sans aucune vérification d'appartenance à l'organisme.
    if (!currentOrgId) { toast.error('Organisme introuvable — suppression annulée par sécurité.'); return; }
    const delQuery = db.from('shared_processes').delete().eq('id', processToDelete.id).eq('organisation_id', currentOrgId);
    await delQuery;
    toast.success('Ressource supprimée.');
    setProcessToDelete(null);
    fetchProcesses();
  };

  const visibleProcesses = processes.filter(p => {
    if (isAdmin) return true;
    if (isFormateur) return p.visible_formateur;
    if (isClient) return p.visible_client;
    return false;
  }).filter(p => filterType === 'all' ? true : p.type === filterType);

  const typeIcon = (type) => ({ pdf: '📄', video: '🎬', link: '🔗', document: '📎' }[type] || '📎');
  const typeLabel = (type) => ({ pdf: 'PDF', video: 'Vidéo', link: 'Lien', document: 'Fichier' }[type] || type);

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
            <span className="w-2 h-6 bg-purple-500 rounded-full"></span> {isClient ? 'Ressources' : 'Espace Processus & Ressources'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{isClient ? 'Guides et ressources partagés par votre organisme de formation.' : 'Partagez des guides, tutoriels et ressources avec vos équipes et clients.'}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setIsAdding(!isAdding)} className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-sm transition-all shadow-md flex items-center gap-2">
            <Plus size={16} /> {isAdding ? 'Annuler' : 'Ajouter une ressource'}
          </button>
        )}
      </div>

      {/* Formulaire ajout (admin) */}
      {isAdmin && isAdding && (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-purple-100 animate-fade-in">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Plus size={16} className="text-purple-600" /> Nouvelle ressource</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">Titre *</label>
                <input type="text" required value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} placeholder="Ex: Guide d'utilisation de la plateforme" className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="link">🔗 Lien web</option>
                  <option value="video">🎬 Vidéo (YouTube, Vimeo...)</option>
                  <option value="pdf">📄 PDF (upload)</option>
                  <option value="document">📎 Fichier (upload)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Décrivez brièvement le contenu..." rows={2} className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-purple-500 resize-none" />
            </div>
            {(form.type === 'link' || form.type === 'video') && (
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">URL *</label>
                <input type="url" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
            )}
            {(form.type === 'pdf' || form.type === 'document') && (
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">Fichier *</label>
                <input type="file" accept={form.type === 'pdf' ? '.pdf' : '*'} onChange={e => {
                  const f = e.target.files[0];
                  if (f) {
                    setUploadFile(f);
                    if (!form.titre.trim()) {
                      const nameWithoutExt = f.name.replace(/\.[^/.]+$/, '');
                      setForm(prev => ({ ...prev, titre: nameWithoutExt }));
                    }
                  }
                }} className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-bold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer" />
              </div>
            )}
            <div className="flex items-center gap-6">
              <span className="text-xs font-bold text-gray-600 uppercase">Visible par :</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.visible_client} onChange={e => setForm(f => ({ ...f, visible_client: e.target.checked }))} className="w-4 h-4 accent-purple-600 rounded" />
                <span className="text-sm font-medium text-gray-700">Clients</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.visible_formateur} onChange={e => setForm(f => ({ ...f, visible_formateur: e.target.checked }))} className="w-4 h-4 accent-purple-600 rounded" />
                <span className="text-sm font-medium text-gray-700">Formateurs</span>
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setIsAdding(false)} className="px-5 py-2.5 text-gray-700 font-bold hover:bg-gray-100 rounded-xl transition-colors text-sm">Annuler</button>
              <button type="submit" disabled={isSaving} className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors shadow-md text-sm disabled:opacity-50">
                {isSaving ? '...' : '✓ Publier'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        {['all', 'link', 'video', 'pdf', 'document'].map(t => (
          <button key={t} onClick={() => setFilterType(t)} className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${filterType === t ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:border-purple-300'}`}>
            {t === 'all' ? 'Tous' : typeLabel(t)} {t === 'all' ? `(${visibleProcesses.length})` : ''}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      ) : visibleProcesses.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 text-center">
          <div className="text-5xl mb-4">📚</div>
          <p className="text-gray-500 font-medium">Aucune ressource partagée pour le moment.</p>
          {isAdmin && <p className="text-sm text-gray-400 mt-1">Cliquez sur "Ajouter une ressource" pour commencer.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleProcesses.map(p => (
            <div key={p.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-purple-200 transition-all group">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className="text-2xl shrink-0">{typeIcon(p.type)}</span>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-sm leading-tight truncate">{p.titre}</p>
                    {p.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.description}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{typeLabel(p.type)}</span>
                      {isAdmin && (
                        <>
                          {p.visible_client && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Clients</span>}
                          {p.visible_formateur && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Formateurs</span>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.url && (
                    <button onClick={() => openSecureStorageFile(p.url)} className="p-2 bg-purple-50 text-purple-700 hover:bg-purple-600 hover:text-white rounded-xl transition-all" title="Ouvrir">
                      <Eye size={16} />
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => setProcessToDelete(p)} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all" title="Supprimer">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <DeleteConfirmationModal
        isOpen={!!processToDelete}
        onClose={() => setProcessToDelete(null)}
        onConfirm={handleDelete}
        itemName={processToDelete?.titre || 'cette ressource'}
        title="Supprimer cette ressource ?"
      />
    </div>
  );
};

// ==========================================
// MESSAGERIE INTERNE
// ==========================================

const MessagesView = ({ supabase, userRole, currentUserId, clients, formateurs, currentOrgId, orgName = '' }) => {
  const isAdmin = userRole === 'admin';
  const isFormateur = userRole === 'formateur';
  const isClient = userRole === 'client';

  const [conversations, setConversations] = React.useState([]);
  const [activeConvId, setActiveConvId] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [newContent, setNewContent] = React.useState('');
  const [attachFile, setAttachFile] = React.useState(null);
  const [isSending, setIsSending] = React.useState(false);
  const [newConvReceiver, setNewConvReceiver] = React.useState('');
  const messagesEndRef = React.useRef(null);
  const [confirmDeleteConvId, setConfirmDeleteConvId] = React.useState(null);

  const db = supabase;

  // DÉCISION (20/10/2026) : « Admin SkorUp » n'arrivait chez personne. Le formateur écrit maintenant à
  // l'administration de SON organisme (le premier compte administrateur de l'organisme).
  const [adminOrganisme, setAdminOrganisme] = React.useState(null);
  React.useEffect(() => {
    if (!db || !currentOrgId || isAdmin) return;
    db.from('utilisateurs').select('id').eq('organisation_id', currentOrgId).eq('role', 'admin')
      .order('id', { ascending: true }).limit(1)
      .then(({ data }) => setAdminOrganisme((data || [])[0] || null), () => {});
  }, [db, currentOrgId, isAdmin]);

  // Déterminer les interlocuteurs possibles selon le rôle
  const possibleReceivers = React.useMemo(() => {
    if (isAdmin) {
      const f = (formateurs || []).map(u => ({ id: String(u.id), label: `${u.nom || ''} ${u.prenom || ''}`.trim(), role: 'formateur' }));
      const c = (clients || []).map(u => ({ id: String(u.id), label: `${u.nom || ''} ${u.prenom || ''}`.trim(), role: 'client' }));
      return [...f, ...c];
    }
    if (isFormateur) {
      const myClients = (clients || []).filter(c => String(c.formateur_id) === String(currentUserId));
      const c = myClients.map(u => ({ id: String(u.id), label: `${u.nom || ''} ${u.prenom || ''}`.trim(), role: 'client' }));
      // Administration de l'organisme (sauf si c'est moi-même, en « espace formateur »)
      const adminEntry = adminOrganisme && String(adminOrganisme.id) !== String(currentUserId)
        ? [{ id: String(adminOrganisme.id), label: `Administration${orgName ? ` (${orgName})` : ''}`, role: 'admin' }] : [];
      return [...adminEntry, ...c];
    }
    if (isClient) {
      // Find own formateur
      const me = (clients || []).find(c => String(c.id) === String(currentUserId));
      if (me?.formateur_id) {
        const f = (formateurs || []).find(f => String(f.id) === String(me.formateur_id));
        if (f) return [{ id: String(f.id), label: `${f.nom || ''} ${f.prenom || ''}`.trim(), role: 'formateur' }];
      }
      return [];
    }
    return [];
  }, [isAdmin, isFormateur, isClient, clients, formateurs, currentUserId, adminOrganisme, orgName]);

  // Charger les conversations (dernier message par paire)
  const fetchConversations = React.useCallback(async () => {
    const { data, error } = await db.from('messages')
      .select('*')
      .or(`sender_id.eq.${currentUserId},receiver_id.eq.${currentUserId}`)
      .order('created_at', { ascending: false });

    if (error || !data) return;

    // Regrouper par paire sender/receiver
    const convMap = {};
    data.forEach(m => {
      const otherId = m.sender_id === String(currentUserId) ? m.receiver_id : m.sender_id;
      const key = [String(currentUserId), String(otherId)].sort().join('_');
      if (!convMap[key]) {
        const other = possibleReceivers.find(r => r.id === otherId);
        convMap[key] = { key, otherId, otherLabel: other?.label || otherId, otherRole: other?.role || (m.sender_id === String(currentUserId) ? m.receiver_role : m.sender_role), lastMessage: m, unread: 0 };
      }
      if (m.receiver_id === String(currentUserId) && !m.read_at) convMap[key].unread++;
    });

    setConversations(Object.values(convMap).sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)));
  }, [db, currentUserId, possibleReceivers]);

  // Charger messages d'une conversation
  const fetchMessages = React.useCallback(async (otherId) => {
    setLoadingMessages(true);
    const { data, error } = await db.from('messages')
      .select('*')
      .or(`and(sender_id.eq.${currentUserId},receiver_id.eq.${otherId}),and(sender_id.eq.${otherId},receiver_id.eq.${currentUserId})`)
      .order('created_at', { ascending: true });
    if (!error && data) {
      setMessages(data);
      // Marquer comme lus
      await db.from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('receiver_id', String(currentUserId))
        .eq('sender_id', String(otherId))
        .is('read_at', null);
    }
    setLoadingMessages(false);
  }, [db, currentUserId]);

  React.useEffect(() => { fetchConversations(); }, [fetchConversations]);

  React.useEffect(() => {
    if (activeConvId) {
      fetchMessages(activeConvId);
    }
  }, [activeConvId, fetchMessages]);

  React.useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newContent.trim() && !attachFile) return;
    if (!activeConvId) return;
    setIsSending(true);

    let attachUrl = null;
    let attachName = null;
    if (attachFile) {
      const path = `messages/${currentUserId}/${Date.now()}_${attachFile.name}`;
      const { error: upErr } = await db.storage.from('documents').upload(path, attachFile, { upsert: true });
      if (upErr) { toast.error('Erreur upload : ' + upErr.message); setIsSending(false); return; }
      const { data: pub } = db.storage.from('documents').getPublicUrl(path);
      attachUrl = pub?.publicUrl || null;
      attachName = attachFile.name;
    }

    const receiver = possibleReceivers.find(r => r.id === activeConvId);
    const senderRole = userRole;
    const receiverRole = receiver?.role || 'client';

    const { error } = await db.from('messages').insert([{
      organisation_id: currentOrgId || null,
      sender_id: String(currentUserId),
      sender_role: senderRole,
      receiver_id: String(activeConvId),
      receiver_role: receiverRole,
      content: newContent.trim() || null,
      attachment_url: attachUrl,
      attachment_name: attachName,
    }]);

    setIsSending(false);
    if (error) { toast.error('Erreur envoi : ' + error.message); return; }
    setNewContent('');
    setAttachFile(null);
    await fetchMessages(activeConvId);
    await fetchConversations();
  };

  const handleDeleteConversation = async (otherId) => {
    const { error } = await db.from('messages').delete()
      .or(`and(sender_id.eq.${currentUserId},receiver_id.eq.${otherId}),and(sender_id.eq.${otherId},receiver_id.eq.${currentUserId})`);
    if (error) { toast.error('Erreur suppression : ' + error.message); return; }
    if (activeConvId === otherId) { setActiveConvId(null); setMessages([]); }
    setConfirmDeleteConvId(null);
    await fetchConversations();
    toast.success('Conversation supprimée.');
  };

  const startConversation = (receiverId) => {
    setActiveConvId(receiverId);
    setNewConvReceiver('');
  };

  const roleColor = (role) => ({ admin: 'bg-violet-600', formateur: 'bg-indigo-500', client: 'bg-emerald-500' }[role] || 'bg-gray-500');
  const roleLabel = (role) => ({ admin: 'Admin', formateur: 'Formateur', client: 'Client' }[role] || role);
  const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
  const formatDay = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';

  // UX (2026-10-12) : un client n'a qu'un interlocuteur (son formateur) — la conversation s'ouvre
  // directement, sans passer par la liste « Nouvelle conversation ».
  const conversationOuverteAuto = React.useRef(false);
  React.useEffect(() => {
    if (isClient && !conversationOuverteAuto.current && !activeConvId && possibleReceivers.length === 1) {
      conversationOuverteAuto.current = true;
      setActiveConvId(possibleReceivers[0].id);
    }
  }, [isClient, activeConvId, possibleReceivers]);

  const activeOther = possibleReceivers.find(r => r.id === activeConvId);

  return (
    <div className="animate-fade-in max-w-7xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
          <span className="w-2 h-6 bg-teal-500 rounded-full"></span> Messagerie
        </h2>
        <p className="text-sm text-gray-500 mt-1">Échangez avec vos interlocuteurs en toute simplicité.</p>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden" style={{ minHeight: '520px' }}>
        <div className="flex h-[75vh] md:h-[600px]">
          {/* Colonne gauche : conversations — masquée sur mobile dès qu'une conversation est ouverte */}
          <div className={`w-full md:w-72 shrink-0 border-r border-gray-100 flex-col ${activeConvId ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Conversations</p>
              {/* Démarrer nouvelle conversation */}
              {possibleReceivers.length > 0 && !(isClient && possibleReceivers.length === 1) && (
                <select
                  value={newConvReceiver}
                  onChange={e => { if (e.target.value) startConversation(e.target.value); }}
                  className="w-full text-xs border border-gray-200 rounded-xl p-2 outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50"
                >
                  <option value="">+ Nouvelle conversation</option>
                  {possibleReceivers.map(r => (
                    <option key={r.id} value={r.id}>{r.label} ({roleLabel(r.role)})</option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <p className="text-xs text-gray-400 italic p-4 text-center">Aucune conversation.</p>
              ) : (
                conversations.map(conv => (
                  <div
                    key={conv.key}
                    className={`relative group border-b border-gray-50 transition-colors ${activeConvId === conv.otherId ? 'bg-teal-50 border-l-4 border-l-teal-500' : 'hover:bg-gray-50'}`}
                  >
                    <button
                      onClick={() => setActiveConvId(conv.otherId)}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full ${roleColor(conv.otherRole)} text-white flex items-center justify-center font-bold text-xs shrink-0`}>
                          {conv.otherLabel.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0 pr-6">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-gray-900 truncate">{conv.otherLabel}</span>
                            {conv.unread > 0 && <span className="bg-teal-500 text-white text-[11px] font-bold rounded-full px-1.5 py-0.5 shrink-0">{conv.unread}</span>}
                          </div>
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            {conv.lastMessage.content || (conv.lastMessage.attachment_name ? `📎 ${conv.lastMessage.attachment_name}` : '')}
                          </p>
                        </div>
                      </div>
                    </button>
                    {/* Bouton suppression */}
                    {confirmDeleteConvId === conv.otherId ? (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white border border-red-200 rounded-xl px-2 py-1 shadow-sm z-10">
                        <span className="text-xs text-red-500 font-bold whitespace-nowrap">Supprimer ?</span>
                        <button onClick={() => handleDeleteConversation(conv.otherId)}
                          className="text-xs font-black text-white bg-red-500 hover:bg-red-600 rounded-lg px-1.5 py-0.5 transition-colors">Oui</button>
                        <button onClick={() => setConfirmDeleteConvId(null)}
                          className="text-xs font-black text-gray-500 hover:text-gray-800 rounded-lg px-1 py-0.5">Non</button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteConvId(conv.otherId); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50"
                        title="Supprimer la conversation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Colonne droite : messages — masquée sur mobile tant qu'aucune conversation n'est ouverte */}
          <div className={`flex-1 flex-col min-w-0 ${activeConvId ? 'flex' : 'hidden md:flex'}`}>
            {!activeConvId ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="text-5xl mb-4">💬</div>
                <p className="text-gray-500 font-medium">Sélectionnez une conversation</p>
                <p className="text-sm text-gray-400 mt-1">ou démarrez-en une nouvelle depuis la liste.</p>
              </div>
            ) : (
              <>
                {/* En-tête conversation */}
                <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                  <button
                    onClick={() => setActiveConvId(null)}
                    className="md:hidden p-1.5 -ml-1.5 text-gray-400 hover:text-gray-700 shrink-0"
                    title="Retour aux conversations"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <div className={`w-9 h-9 rounded-full ${roleColor(activeOther?.role || 'client')} text-white flex items-center justify-center font-bold text-sm shrink-0`}>
                    {(activeOther?.label || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{activeOther?.label || activeConvId}</p>
                    <p className="text-xs text-gray-400 uppercase font-bold">{roleLabel(activeOther?.role || '')}</p>
                  </div>
                </div>

                {/* Liste messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loadingMessages && <p className="text-center text-gray-400 text-sm">Chargement...</p>}
                  {!loadingMessages && messages.length === 0 && (
                    <p className="text-center text-gray-400 text-sm italic py-8">Aucun message. Démarrez la conversation !</p>
                  )}
                  {messages.map((m, idx) => {
                    const isMine = m.sender_id === String(currentUserId);
                    const showDate = idx === 0 || formatDay(m.created_at) !== formatDay(messages[idx-1]?.created_at);
                    return (
                      <React.Fragment key={m.id}>
                        {showDate && (
                          <div className="text-center"><span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full font-bold">{formatDay(m.created_at)}</span></div>
                        )}
                        <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs rounded-2xl px-4 py-2.5 shadow-sm ${isMine ? 'bg-teal-500 text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'}`}>
                            {m.content && <p className="text-sm leading-relaxed">{m.content}</p>}
                            {m.attachment_url && (
                              <button onClick={() => openSecureStorageFile(m.attachment_url)} className={`flex items-center gap-1.5 text-xs font-bold mt-1 underline ${isMine ? 'text-teal-100' : 'text-indigo-600'}`}>
                                📎 {m.attachment_name || 'Pièce jointe'}
                              </button>
                            )}
                            <p className={`text-[11px] mt-1 ${isMine ? 'text-teal-100' : 'text-gray-400'}`}>{formatTime(m.created_at)}</p>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Zone de saisie */}
                <div className="p-4 border-t border-gray-100">
                  {attachFile && (
                    <div className="flex items-center gap-2 mb-2 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600">
                      📎 <span className="font-medium truncate">{attachFile.name}</span>
                      <button onClick={() => setAttachFile(null)} className="ml-auto text-gray-400 hover:text-red-500">✕</button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <label className="p-2 text-gray-400 hover:text-teal-600 cursor-pointer transition-colors shrink-0" title="Joindre un fichier">
                      📎
                      <input type="file" className="hidden" onChange={e => setAttachFile(e.target.files[0])} />
                    </label>
                    <textarea
                      value={newContent}
                      onChange={e => setNewContent(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                      placeholder="Votre message... (Entrée pour envoyer)"
                      rows={1}
                      className="flex-1 border border-gray-200 rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-gray-50"
                    />
                    <button onClick={handleSend} disabled={isSending || (!newContent.trim() && !attachFile)} className="p-3 bg-teal-500 hover:bg-teal-600 text-white rounded-2xl transition-all shadow-md disabled:opacity-40 shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// PARAMÈTRES ORGANISME (ADMIN)
// ==========================================

const OrganisationSettingsView = ({ supabase, currentOrgId, orgSettings, onSaved, brandSettings, onBrandSaved, orgId, onRefresh }) => {
  const [nom, setNom] = useState(orgSettings?.nom || '');
  const [siret, setSiret] = useState(orgSettings?.siret || '');
  const [adresse, setAdresse] = useState(orgSettings?.adresse || '');
  const [logoUrl, setLogoUrl] = useState(orgSettings?.logo_url || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // ── Champs organisme étendus ──
  const [nda, setNda] = useState(orgSettings?.nda || '');
  const [siteWeb, setSiteWeb] = useState(orgSettings?.site_web || '');
  const [codePostal, setCodePostal] = useState(orgSettings?.code_postal || '');
  const [ville, setVille] = useState(orgSettings?.ville || '');
  const [region, setRegion] = useState(orgSettings?.region || '');
  const [emailContact, setEmailContact] = useState('');
  const [telephoneContact, setTelephoneContact] = useState('');
  const [compagnieAssurance, setCompagnieAssurance] = useState('');
  const [numeroAssuranceRcp, setNumeroAssuranceRcp] = useState('');
  // ── Branding ──
  const [brandColor, setBrandColor] = useState(brandSettings?.primary_color || '#7C3AED');
  const [brandName, setBrandName] = useState(brandSettings?.org_name || '');
  const [brandWelcome, setBrandWelcome] = useState(brandSettings?.welcome_message || '');
  const [brandLogoUrl, setBrandLogoUrl] = useState(brandSettings?.logo_url || '');
  const [isBrandUploading, setIsBrandUploading] = useState(false);
  const [isBrandSaving, setIsBrandSaving] = useState(false);
  const [settingsTab, setSettingsTab] = useState(() => {
    const st = orgSettings?.subscription_status;
    const bloque = ['canceled', 'past_due', 'unpaid', 'paused'].includes(st)
      || (st === 'trialing' && orgSettings?.trial_ends_at && new Date(orgSettings.trial_ends_at) < new Date());
    return bloque ? 'abonnement' : 'organisme';
  });

  useEffect(() => {
    if (brandSettings) {
      setBrandColor(brandSettings.primary_color || '#7C3AED');
      setBrandName(brandSettings.org_name || '');
      setBrandWelcome(brandSettings.welcome_message || '');
      setBrandLogoUrl(brandSettings.logo_url || '');
    }
  }, [brandSettings]);

  useEffect(() => {
    if (orgSettings) {
      setNom(orgSettings.nom || '');
      setSiret(orgSettings.siret || '');
      setAdresse(orgSettings.adresse || '');
      setLogoUrl(orgSettings.logo_url || '');
      setNda(orgSettings.nda || '');
      setSiteWeb(orgSettings.site_web || '');
      setCodePostal(orgSettings.code_postal || '');
      setVille(orgSettings.ville || '');
      setRegion(orgSettings.region || '');
    }
  }, [orgSettings]);

  // Charge email / téléphone / assurance depuis la table utilisateurs (admin connecté)
  useEffect(() => {
    const fetchAdminContact = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser?.email) return;
      let { data } = await supabase.from('utilisateurs')
        .select('email, telephone, compagnie_assurance, numero_assurance_rcp')
        .eq('auth_uid', authUser.id)
        .limit(1)
        .maybeSingle();
      if (!data) {
        ({ data } = await supabase.from('utilisateurs')
          .select('email, telephone, compagnie_assurance, numero_assurance_rcp')
          .eq('email', authUser.email)
          .limit(1)
          .maybeSingle());
      }
      if (data) {
        setEmailContact(data.email || '');
        setTelephoneContact(data.telephone || '');
        setCompagnieAssurance(data.compagnie_assurance || '');
        setNumeroAssuranceRcp(data.numero_assurance_rcp || '');
      }
    };
    fetchAdminContact();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBrandLogoUpload = async (file) => {
    if (!file) return;
    setIsBrandUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('organisation_id', currentOrgId);
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
        body: formData
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Erreur upload');
      setBrandLogoUrl(result.publicUrl);
      toast.success("Logo uploadé !");
    } catch (err) {
      toast.error("Erreur upload logo : " + err.message);
    }
    setIsBrandUploading(false);
  };

  const handleBrandSave = async () => {
    setIsBrandSaving(true);
    const orgId = currentOrgId;
    if (!orgId) { toast.error("Organisation introuvable."); setIsBrandSaving(false); return; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/save-brand-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          organisation_id: orgId,
          org_name: brandName,
          primary_color: brandColor,
          welcome_message: brandWelcome,
          logo_url: brandLogoUrl
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Erreur sauvegarde');
      document.documentElement.style.setProperty('--brand-primary', brandColor);
      document.documentElement.style.setProperty('--brand-primary-dark', brandColor + 'CC');
      toast.success("Personnalisation sauvegardée !");
      if (onBrandSaved) onBrandSaved({ org_name: brandName, primary_color: brandColor, welcome_message: brandWelcome, logo_url: brandLogoUrl });
    } catch (err) {
      toast.error("Erreur : " + err.message);
    }
    setIsBrandSaving(false);
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setIsUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${currentOrgId}/logo.${ext}`;
    const { error } = await supabase.storage.from('logos').upload(path, file, { upsert: true });
    if (error) { toast.error("Erreur upload logo : " + error.message); setIsUploading(false); return; }
    const { data } = supabase.storage.from('logos').getPublicUrl(path);
    setLogoUrl(data.publicUrl);
    setIsUploading(false);
    toast.success("Logo uploadé !");
  };

  const handleSave = async () => {
    setIsSaving(true);
    // 1. Mise à jour de la table organisations
    const { error } = await supabase.from('organisations').update({
      nom, siret, adresse, logo_url: logoUrl,
      nda, site_web: siteWeb, code_postal: codePostal, ville, region
    }).eq('id', currentOrgId);
    if (error) {
      toast.error("Erreur : " + error.message);
      setIsSaving(false);
      return;
    }
    // 2. Mise à jour de la table utilisateurs (email, téléphone, assurance)
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (authUser?.email) {
      await updateOwnStaffProfile(authUser, {
        email: emailContact,
        telephone: telephoneContact,
        compagnie_assurance: compagnieAssurance,
        numero_assurance_rcp: numeroAssuranceRcp,
      });
    }
    toast.success("Paramètres sauvegardés !");
    onSaved({ nom, siret, adresse, logo_url: logoUrl, nda, site_web: siteWeb, code_postal: codePostal, ville, region });
    setIsSaving(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header + sous-navigation ── */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h2 className="text-2xl font-black text-gray-900 tracking-tight">Paramètres</h2>
        <p className="text-gray-500 text-sm mt-1">Gérez votre organisme, votre portail et votre abonnement.</p>
        <div className="tabs-scroll flex gap-1 mt-5 bg-gray-100 p-1 rounded-xl w-fit max-w-full">
          {[
            { key: 'organisme', label: 'Organisme' },
            { key: 'personnalisation', label: 'Personnalisation', proOnly: true },
            { key: 'abonnement', label: 'Abonnement' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setSettingsTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all ${settingsTab === tab.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {tab.label}
              {tab.proOnly && !hasFeatureAccess(orgSettings, 'pro') && <span className="text-[11px] font-black bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-md">PRO</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Onglet Organisme ── */}
      {settingsTab === 'organisme' && (
      <div className="space-y-6">
        {/* ── Identité Professionnelle ── */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-1">Identité Professionnelle</h3>
            <p className="text-xs text-gray-400">Ces informations apparaîtront sur vos documents officiels.</p>
          </div>

          {/* Logo */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-3">Logo de l'organisme</label>
            <div className="flex items-center gap-6">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo organisme" className="w-24 h-24 object-contain rounded-xl border border-gray-200 bg-gray-50 p-2" />
              ) : (
                <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 bg-gray-50">
                  <Upload className="w-8 h-8" />
                </div>
              )}
              <div>
                <input type="file" accept="image/*" id="logo-upload" className="hidden"
                  onChange={(e) => { if (e.target.files?.[0]) handleLogoUpload(e.target.files[0]); }} />
                <label htmlFor="logo-upload" className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm px-4 py-2.5 rounded-xl transition-colors">
                  {isUploading ? 'Chargement...' : 'Changer le logo'}
                </label>
                <p className="text-xs text-gray-400 mt-2">PNG, JPG, SVG — 2 Mo max</p>
              </div>
            </div>
          </div>

          {/* Raison sociale */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Raison Sociale</label>
            <input type="text" value={nom} onChange={e => setNom(e.target.value)}
              placeholder="Nom de votre entreprise"
              className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>

          {/* SIRET + NDA */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Numéro SIRET</label>
              <input type="text" value={siret} onChange={e => setSiret(e.target.value)} placeholder="14 chiffres"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">NDA (Qualiopi)</label>
              <input type="text" value={nda} onChange={e => setNda(e.target.value)} placeholder="N° Déclaration d'Activité"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
          </div>

          {/* Assurance RCP */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Assurance RCP — Compagnie</label>
              <input type="text" value={compagnieAssurance} onChange={e => setCompagnieAssurance(e.target.value)} placeholder="Ex: AXA, MAIF, Hiscox..."
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Assurance RCP — N° de contrat</label>
              <input type="text" value={numeroAssuranceRcp} onChange={e => setNumeroAssuranceRcp(e.target.value)} placeholder="N° de contrat RCP"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
          </div>
        </div>

        {/* ── Coordonnées & Contact ── */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-1">Coordonnées & Contact</h3>
            <p className="text-xs text-gray-400">Informations de contact de l'organisme.</p>
          </div>

          {/* Email + Téléphone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Email professionnel</label>
              <input type="email" value={emailContact} onChange={e => setEmailContact(e.target.value)} placeholder="contact@monorganisme.fr"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Téléphone</label>
              <input type="tel" value={telephoneContact} onChange={e => setTelephoneContact(e.target.value)} placeholder="06 00 00 00 00"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
          </div>

          {/* Site web */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Site Internet</label>
            <input type="url" value={siteWeb} onChange={e => setSiteWeb(e.target.value)} placeholder="https://www.votresite.fr"
              className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
          </div>

          {/* Adresse siège */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Adresse Siège Social</label>
            <AddressAutocomplete
              value={adresse}
              onChange={setAdresse}
              onSelect={({ rue, codePostal: cp, ville: v, region: r }) => { setAdresse(rue); if (cp) setCodePostal(cp); if (v) setVille(v); if (r) setRegion(r); }}
              placeholder="N° et nom de rue"
              className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all"
            />
          </div>

          {/* Code postal + Ville */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Code Postal</label>
              <input type="text" value={codePostal} onChange={e => setCodePostal(e.target.value)} placeholder="75000"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Ville</label>
              <input type="text" value={ville} onChange={e => setVille(e.target.value)} placeholder="Paris"
                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            </div>
          </div>

          {/* Région */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Région</label>
            <input list="region-list-org" type="text" value={region} onChange={e => setRegion(e.target.value)} placeholder="Ex : Bretagne" autoComplete="off"
              className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-600 transition-all" />
            <datalist id="region-list-org">{FRENCH_REGIONS.map(r => <option key={r} value={r} />)}</datalist>
          </div>
        </div>

        {/* ── Bouton de sauvegarde ── */}
        <button onClick={handleSave} disabled={isSaving}
          className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" />
          {isSaving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
        </button>
      </div>
      )}

      {/* ── Onglet Personnalisation (réservé aux plans Pro et Illimité) ── */}
      {settingsTab === 'personnalisation' && !hasFeatureAccess(orgSettings, 'pro') && (
        <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100 text-center">
          <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-violet-600" />
          </div>
          <h3 className="text-lg font-black text-gray-900 mb-2">Fonctionnalité réservée aux plans Pro et Illimité</h3>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
            La personnalisation de votre portail client (logo, couleurs, message d'accueil) est disponible à partir du plan Pro.
          </p>
          <button onClick={() => setSettingsTab('abonnement')} className="bg-violet-600 hover:bg-violet-700 text-white font-bold px-6 py-3 rounded-xl transition-all">
            Voir les plans →
          </button>
        </div>
      )}
      {settingsTab === 'personnalisation' && hasFeatureAccess(orgSettings, 'pro') && (
      <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
        <div>
          <h3 className="text-lg font-black text-gray-900">🎨 Personnalisation du portail</h3>
          <p className="text-gray-400 text-sm mt-1">Logo, couleurs et message affiché à vos clients et formateurs.</p>
        </div>

        {/* Aperçu live */}
        <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 px-5 py-3.5" style={{background: brandColor}}>
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt="Logo" className="w-8 h-8 rounded-lg object-contain bg-white/20 p-1" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background:'#100524'}}>
                <img src="/logo-mark.png" alt="SkorUp" className="h-4 w-auto object-contain" />
              </div>
            )}
            <span className="text-white font-bold text-sm">{brandName || 'Nom de votre organisme'}</span>
          </div>
          <div className="bg-gray-50 px-5 py-3 border-t border-gray-100">
            <p className="text-gray-500 text-xs italic">{brandWelcome || "Message d’accueil de votre portail client"}</p>
          </div>
        </div>

        {/* Logo de marque */}
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase mb-3">Logo du portail</label>
          <div className="flex items-center gap-5">
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt="Logo marque" className="w-20 h-20 object-contain rounded-xl border border-gray-200 bg-gray-50 p-2" />
            ) : (
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 bg-gray-50">
                <Upload className="w-7 h-7" />
              </div>
            )}
            <div>
              <input type="file" accept="image/*" id="brand-logo-upload" className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) handleBrandLogoUpload(e.target.files[0]); }} />
              <label htmlFor="brand-logo-upload" className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm px-4 py-2.5 rounded-xl transition-colors block text-center">
                {isBrandUploading ? 'Chargement...' : 'Choisir un logo'}
              </label>
              <p className="text-xs text-gray-400 mt-2">PNG, JPG, SVG — 2 Mo max</p>
              {brandLogoUrl && (
                <button onClick={() => setBrandLogoUrl('')} className="text-xs text-red-400 hover:text-red-600 mt-1">Supprimer</button>
              )}
            </div>
          </div>
        </div>

        {/* Nom de marque */}
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Nom affiché dans le portail</label>
          <input type="text" value={brandName} onChange={e => setBrandName(e.target.value)}
            placeholder="Ex : VB Coaching"
            className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 transition-all"
            style={{'--tw-ring-color': brandColor}} />
        </div>

        {/* Couleur principale */}
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase mb-3">Couleur principale</label>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {['#7C3AED','#2563EB','#059669','#DC2626','#D97706','#0F172A','#EC4899','#0EA5E9'].map(c => (
                <button key={c} onClick={() => setBrandColor(c)} title={c}
                  className={`w-9 h-9 rounded-xl border-2 transition-all ${brandColor === c ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'}`}
                  style={{background: c}} />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input type="color" value={/^#[0-9A-Fa-f]{6}$/.test(brandColor) ? brandColor : '#7C3AED'}
                  onChange={e => setBrandColor(e.target.value)}
                  className="w-12 h-12 rounded-xl border-2 border-gray-200 cursor-pointer p-1 bg-white" title="Ouvrir le sélecteur de couleur" />
              </div>
              <div className="flex items-center gap-2 flex-1 max-w-xs bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <span className="w-5 h-5 rounded-md flex-shrink-0 border border-gray-200" style={{background: /^#[0-9A-Fa-f]{6}$/.test(brandColor) ? brandColor : '#7C3AED'}} />
                <input
                  type="text"
                  value={brandColor}
                  onChange={e => {
                    const val = e.target.value.startsWith('#') ? e.target.value : '#' + e.target.value;
                    setBrandColor(val);
                  }}
                  onBlur={e => {
                    if (!/^#[0-9A-Fa-f]{6}$/.test(brandColor)) setBrandColor('#7C3AED');
                  }}
                  maxLength={7}
                  placeholder="#000000"
                  className="flex-1 bg-transparent text-sm font-mono text-gray-700 outline-none"
                />
              </div>
              <span className="text-xs text-gray-400">ou saisissez votre code couleur</span>
            </div>
          </div>
        </div>

        {/* Message d'accueil */}
        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">Message d'accueil (portail client)</label>
          <textarea value={brandWelcome} onChange={e => setBrandWelcome(e.target.value)} rows={2}
            placeholder="Ex : Bienvenue sur votre espace de suivi de formation."
            className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 transition-all resize-none" />
        </div>

        <button onClick={handleBrandSave} disabled={isBrandSaving}
          className="w-full text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          style={{background: brandColor}}>
          <Save className="w-4 h-4" />
          {isBrandSaving ? 'Sauvegarde...' : 'Sauvegarder la personnalisation'}
        </button>
      </div>
      )}

      {/* ── Onglet Abonnement ── */}
      {settingsTab === 'abonnement' && (
        <AbonnementView orgId={orgId || currentOrgId} orgSettings={orgSettings} onRefresh={onRefresh} />
      )}
    </div>
  );
};

// ==========================================
// COMPOSANT PRINCIPAL
// ==========================================

const FichesMetiersView = ({ userRole, currentUserId, currentOrgId, supabase, clients, formateurs }) => {
  // 2026-10-21 : référentiel ROME chargé à l'ouverture de l'écran (vide pendant une fraction de seconde).
  const [romeData, setRomeData] = React.useState(ROME_VIDE);
  React.useEffect(() => {
    let actif = true;
    chargerRomeData().then(d => { if (actif) setRomeData(d); }).catch(e => console.error('[FichesMetiers] référentiel ROME :', e));
    return () => { actif = false; };
  }, []);
  // ── États navigation statique ROME 4.0 ────────────────────────────
  const [activeMainTab, setActiveMainTab] = React.useState('explorer');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [selectedGrandDomaine, setSelectedGrandDomaine] = React.useState(null);
  const [selectedDomainePro, setSelectedDomainePro] = React.useState(null);
  const [selectedMetier, setSelectedMetier] = React.useState(null);

  // ── États fiches assignées (snapshot DB locale) ────────────────────
  const [assignedJobSheets, setAssignedJobSheets] = React.useState([]);
  const [loadingAssigned, setLoadingAssigned] = React.useState(false);
  const [filterSector, setFilterSector] = React.useState('');
  const [assignedSearch, setAssignedSearch] = React.useState('');
  const [selectedLocalFiche, setSelectedLocalFiche] = React.useState(null);

  // ── États modal assignation ────────────────────────────────────────
  const [isAssignModalOpen, setIsAssignModalOpen] = React.useState(false);
  const [ficheToAssign, setFicheToAssign] = React.useState(null);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [isAssigning, setIsAssigning] = React.useState(false);

  // ── États vue client ───────────────────────────────────────────────
  const [clientFiches, setClientFiches] = React.useState([]);
  const [loadingClient, setLoadingClient] = React.useState(false);

  const searchTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    if (userRole === 'admin' || userRole === 'formateur') {
      fetchAssignedJobSheets();
    } else if (userRole === 'client') {
      fetchClientFiches();
    }
  }, [userRole, currentUserId]);

  const fetchAssignedJobSheets = async () => {
    setLoadingAssigned(true);
    let q = supabase.from('job_sheets').select('*').order('created_at', { ascending: false });
    q = currentOrgId ? q.eq('organisation_id', currentOrgId) : q.limit(0);
    const { data } = await q;
    if (data) setAssignedJobSheets(data);
    setLoadingAssigned(false);
  };

  const fetchClientFiches = async () => {
    setLoadingClient(true);
    const { data } = await supabase
      .from('client_job_sheets')
      .select('*, job_sheets(*)')
      .eq('client_id', currentUserId);
    if (data) setClientFiches(data.map(d => d.job_sheets).filter(Boolean));
    setLoadingClient(false);
  };

  // ── Recherche locale instantanée dans les données ROME 4.0 ────────
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setSelectedGrandDomaine(null);
    setSelectedDomainePro(null);
    clearTimeout(searchTimeoutRef.current);
    if (!val.trim() || val.trim().length < 2) { setSearchResults([]); return; }
    searchTimeoutRef.current = setTimeout(() => {
      const q = val.toLowerCase().trim();
      const results = [];
      for (const gd of romeData.grandsDomaines) {
        for (const dp of gd.domainesProfessionnels) {
          for (const m of dp.metiers) {
            if (m.libelle.toLowerCase().includes(q) || m.code.toLowerCase().includes(q) || dp.libelle.toLowerCase().includes(q)) {
              results.push({ ...m, grandDomaine: gd, domainePro: dp });
            }
          }
        }
      }
      setSearchResults(results.slice(0, 40));
    }, 200);
  };

  // ── Assigner une fiche métier à un bénéficiaire ───────────────────
  const handleAssignRomeFiche = async () => {
    if (!selectedClientId || !ficheToAssign) return toast.error('Sélectionnez un bénéficiaire');
    setIsAssigning(true);
    try {
      const romeCode = ficheToAssign.code || '';
      const romeTitle = ficheToAssign.libelle || '';

      // Retrouver les métadonnées dans les données statiques
      let secteur = '';
      let domainePro = '';
      for (const gd of romeData.grandsDomaines) {
        for (const dp of gd.domainesProfessionnels) {
          if (dp.metiers.find(m => m.code === romeCode)) {
            secteur = gd.libelle;
            domainePro = dp.libelle;
            break;
          }
        }
      }

      // Vérifier si la fiche existe déjà (même code ROME + org)
      let jobSheetId;
      if (romeCode) {
        const { data: existing } = await supabase
          .from('job_sheets')
          .select('id')
          .eq('extra_info', `rome:${romeCode}`)
          .eq('organisation_id', currentOrgId)
          .maybeSingle();
        if (existing) jobSheetId = existing.id;
      }

      // Créer le snapshot local si pas encore en DB
      if (!jobSheetId) {
        const { data: newSheet, error: insertErr } = await supabase
          .from('job_sheets')
          .insert([{
            title: romeTitle,
            sector: domainePro || secteur,
            description: `Fiche ROME 4.0 — ${romeTitle}. Domaine : ${domainePro}.`,
            extra_info: `rome:${romeCode}`,
            organisation_id: currentOrgId,
          }])
          .select('id')
          .single();
        if (insertErr) throw insertErr;
        jobSheetId = newSheet.id;
      }

      // Associer au client
      // CORRECTIF (2026-07-29) : client_job_sheets.assigned_by_formateur_id est une colonne de
      // type uuid (l'auth_uid Supabase Auth du formateur), alors que currentUserId est l'id interne
      // ENTIER de la table utilisateurs (ex: 22, visible dans l'URL/le state formateur) — les confondre
      // provoquait "invalid input syntax for type uuid: '22'" et empêchait toute assignation de fiche
      // métier depuis l'espace formateur. On récupère donc le véritable auth_uid du formateur avant
      // l'insertion, comme pour clients.formateur_auth_uid ailleurs dans l'app.
      let assignedByAuthUid = null;
      if (userRole === 'formateur' && currentUserId) {
        const { data: formateurRow } = await supabase
          .from('utilisateurs')
          .select('auth_uid')
          .eq('id', currentUserId)
          .maybeSingle();
        assignedByAuthUid = formateurRow?.auth_uid || null;
      }

      const { error: assignErr } = await supabase
        .from('client_job_sheets')
        .insert([{
          job_sheet_id: jobSheetId,
          client_id: selectedClientId,
          assigned_by_formateur_id: assignedByAuthUid,
        }]);

      if (assignErr) {
        if (assignErr.code === '23505') return toast.error('Ce bénéficiaire a déjà cette fiche !');
        throw assignErr;
      }

      toast.success('Fiche métier assignée avec succès !');
      setIsAssignModalOpen(false);
      setFicheToAssign(null);
      setSelectedClientId('');
      fetchAssignedJobSheets();
    } catch (e) {
      toast.error('Erreur : ' + e.message);
    }
    setIsAssigning(false);
  };

  const availableClients = userRole === 'formateur'
    ? clients.filter(c => c.formateur_id === currentUserId)
    : clients;

  // Palette de couleurs par lettre de grand domaine
  const domaineGradients = {
    A: 'from-green-500 to-emerald-600',
    B: 'from-purple-500 to-fuchsia-600',
    C: 'from-blue-500 to-cyan-600',
    D: 'from-amber-500 to-orange-500',
    E: 'from-red-500 to-rose-600',
    F: 'from-orange-400 to-yellow-500',
    G: 'from-teal-500 to-cyan-600',
    H: 'from-slate-500 to-gray-600',
    I: 'from-violet-500 to-purple-600',
    J: 'from-blue-600 to-indigo-700',
    K: 'from-emerald-500 to-teal-600',
    L: 'from-pink-500 to-rose-500',
    M: 'from-indigo-500 to-blue-600',
    N: 'from-amber-600 to-orange-600',
  };

  // Retrouver le grand domaine d'une lettre
  const getGrandDomaineByCode = (code) => romeData.grandsDomaines.find(gd => gd.code === code);

  // ════════════════════════════════════════════════════════════════════
  // VUE CLIENT — fiches assignées uniquement
  // ════════════════════════════════════════════════════════════════════
  if (userRole === 'client') {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Mes Fiches Métiers</h2>
          <p className="text-gray-500 text-sm mt-1">Découvrez les métiers sélectionnés pour vous par votre conseiller.</p>
        </div>

        {loadingClient ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-10 h-10 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin" />
          </div>
        ) : clientFiches.length === 0 ? (
          <div className="bg-white p-10 rounded-2xl text-center border border-gray-100 shadow-sm">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
              <Briefcase size={32} />
            </div>
            <h3 className="text-xl font-black text-gray-800 mb-2">Aucune fiche assignée</h3>
            <p className="text-gray-500">Votre conseiller ne vous a pas encore assigné de fiches métiers.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientFiches.map(fiche => (
              <div
                key={fiche.id}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => setSelectedLocalFiche(fiche)}
              >
                <span className="bg-indigo-50 text-indigo-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">{fiche.sector || 'Métier'}</span>
                <h3 className="text-lg font-extrabold text-gray-900 mt-3 mb-2 group-hover:text-violet-700 transition-colors">{fiche.title}</h3>
                <p className="text-sm text-gray-600 line-clamp-3">{fiche.description}</p>
              </div>
            ))}
          </div>
        )}

        {selectedLocalFiche && (
          <LocalFicheDetailModal fiche={selectedLocalFiche} onClose={() => setSelectedLocalFiche(null)} />
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // VUE ADMIN / FORMATEUR
  // ════════════════════════════════════════════════════════════════════
  const totalMetiers = romeData.grandsDomaines.reduce((acc, gd) =>
    acc + gd.domainesProfessionnels.reduce((a, dp) => a + dp.metiers.length, 0), 0);

  const displayAssigned = assignedJobSheets
    .filter(f => !filterSector || f.sector === filterSector)
    .filter(f => !assignedSearch || f.title?.toLowerCase().includes(assignedSearch.toLowerCase()) || f.sector?.toLowerCase().includes(assignedSearch.toLowerCase()));
  const assignedSectors = [...new Set(assignedJobSheets.map(f => f.sector))].filter(Boolean);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Fiches Métiers</h2>
            <p className="text-gray-500 text-sm mt-1">
              Référentiel ROME 4.0 — {romeData.grandsDomaines.length} grands domaines · {totalMetiers} métiers
            </p>
          </div>
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
            <button
              onClick={() => setActiveMainTab('explorer')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeMainTab === 'explorer' ? 'bg-white shadow text-violet-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Explorateur ROME
            </button>
            <button
              onClick={() => setActiveMainTab('assigned')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeMainTab === 'assigned' ? 'bg-white shadow text-violet-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Fiches Assignées
              {assignedJobSheets.length > 0 && (
                <span className="bg-violet-100 text-violet-700 text-xs font-black px-2 py-0.5 rounded-full">{assignedJobSheets.length}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          TAB 1 — EXPLORATEUR ROME (données statiques ROME 4.0)
      ══════════════════════════════════════════════════════════════ */}
      {activeMainTab === 'explorer' && (
        <div className="space-y-4">
          {/* Barre de recherche instantanée */}
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un métier, un domaine… (ex : développeur, infirmier, cuisine)"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 outline-none focus:ring-2 focus:ring-violet-600 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Résultats de recherche */}
          {searchQuery.trim().length >= 2 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {searchResults.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-600 font-bold">Aucun résultat pour « {searchQuery} »</p>
                  <p className="text-gray-400 text-sm mt-1">Essayez d'autres mots-clés ou explorez par domaine</p>
                </div>
              ) : (
                <>
                  <div className="px-6 py-3 border-b border-gray-50 bg-gray-50/50 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''}
                    </p>
                    <span className="text-xs text-gray-300 font-medium">ROME 4.0</span>
                  </div>
                  <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
                    {searchResults.map(metier => (
                      <button
                        key={metier.code}
                        onClick={() => { setSelectedMetier({ ...metier }); setSearchQuery(''); setSearchResults([]); }}
                        className="w-full flex items-center justify-between px-6 py-3.5 hover:bg-violet-50 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-black text-gray-300 font-mono bg-gray-100 px-2 py-1 rounded-lg flex-shrink-0">{metier.code}</span>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 group-hover:text-violet-700 transition-colors truncate">{metier.libelle}</p>
                            <p className="text-xs text-gray-400 truncate">{metier.domainePro?.libelle}</p>
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-300 group-hover:text-violet-500 flex-shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Navigation hiérarchique */}
          {!searchQuery.trim() && (
            <>
              {/* Fil d'Ariane */}
              {(selectedGrandDomaine || selectedDomainePro) && (
                <div className="flex items-center gap-2 text-sm font-bold flex-wrap">
                  <button
                    onClick={() => { setSelectedGrandDomaine(null); setSelectedDomainePro(null); }}
                    className="text-violet-600 hover:text-violet-800 transition-colors"
                  >
                    Domaines
                  </button>
                  {selectedGrandDomaine && (
                    <>
                      <ChevronRight size={14} className="text-gray-300" />
                      <button
                        onClick={() => setSelectedDomainePro(null)}
                        className={`transition-colors ${selectedDomainePro ? 'text-violet-600 hover:text-violet-800' : 'text-gray-600'}`}
                      >
                        {selectedGrandDomaine.code} — {selectedGrandDomaine.libelle.substring(0, 35)}{selectedGrandDomaine.libelle.length > 35 ? '…' : ''}
                      </button>
                    </>
                  )}
                  {selectedDomainePro && (
                    <>
                      <ChevronRight size={14} className="text-gray-300" />
                      <span className="text-gray-600">{selectedDomainePro.libelle}</span>
                    </>
                  )}
                </div>
              )}

              {/* Niveau 0 : Grille des 14 grands domaines */}
              {!selectedGrandDomaine && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">14 Grands domaines professionnels</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {romeData.grandsDomaines.map(gd => {
                      const nbMetiers = gd.domainesProfessionnels.reduce((a, dp) => a + dp.metiers.length, 0);
                      return (
                        <button
                          key={gd.code}
                          onClick={() => setSelectedGrandDomaine(gd)}
                          className={`bg-gradient-to-br ${domaineGradients[gd.code] || 'from-violet-500 to-purple-600'} p-5 rounded-2xl text-left text-white hover:scale-[1.02] active:scale-[0.99] transition-all shadow-sm hover:shadow-lg group`}
                        >
                          <div className="text-3xl font-black opacity-20 mb-1 leading-none">{gd.code}</div>
                          <h3 className="font-bold text-sm leading-snug line-clamp-2 mb-3">{gd.libelle}</h3>
                          <div className="flex items-center justify-between">
                            <span className="text-white/60 text-xs font-bold">{gd.domainesProfessionnels.length} domaines · {nbMetiers} métiers</span>
                            <ChevronRight size={14} className="text-white/50 group-hover:text-white/90 transition-colors" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Niveau 1 : Domaines professionnels du grand domaine */}
              {selectedGrandDomaine && !selectedDomainePro && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">
                    {selectedGrandDomaine.domainesProfessionnels.length} domaines professionnels
                  </p>
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className={`bg-gradient-to-r ${domaineGradients[selectedGrandDomaine.code] || 'from-violet-500 to-purple-600'} px-6 py-4`}>
                      <p className="text-white/70 text-xs font-bold uppercase tracking-wider mb-1">Grand domaine {selectedGrandDomaine.code}</p>
                      <h3 className="text-white font-black text-lg leading-tight">{selectedGrandDomaine.libelle}</h3>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {selectedGrandDomaine.domainesProfessionnels.map(dp => (
                        <button
                          key={dp.code}
                          onClick={() => setSelectedDomainePro(dp)}
                          className="w-full flex items-center justify-between px-6 py-4 hover:bg-violet-50 transition-colors text-left group"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-black text-gray-300 font-mono bg-gray-100 px-2 py-1 rounded-lg">{dp.code}</span>
                            <div>
                              <p className="font-bold text-gray-900 group-hover:text-violet-700 transition-colors">{dp.libelle}</p>
                              <p className="text-xs text-gray-400">{dp.metiers.length} métier{dp.metiers.length > 1 ? 's' : ''}</p>
                            </div>
                          </div>
                          <ChevronRight size={18} className="text-gray-300 group-hover:text-violet-500 transition-colors flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Niveau 2 : Métiers du domaine professionnel */}
              {selectedGrandDomaine && selectedDomainePro && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">
                    {selectedDomainePro.metiers.length} métier{selectedDomainePro.metiers.length > 1 ? 's' : ''}
                  </p>
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{selectedDomainePro.libelle}</span>
                      <span className="text-xs text-gray-300 font-mono">{selectedDomainePro.code}</span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {selectedDomainePro.metiers.map(metier => (
                        <button
                          key={metier.code}
                          onClick={() => setSelectedMetier({ ...metier, grandDomaine: selectedGrandDomaine, domainePro: selectedDomainePro })}
                          className="w-full flex items-center justify-between px-6 py-4 hover:bg-violet-50 transition-colors text-left group"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-black text-gray-300 font-mono bg-gray-100 px-2 py-1 rounded-lg">{metier.code}</span>
                            <span className="font-bold text-gray-900 group-hover:text-violet-700 transition-colors">{metier.libelle}</span>
                          </div>
                          <ChevronRight size={18} className="text-gray-300 group-hover:text-violet-500 transition-colors flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 2 — FICHES ASSIGNÉES (snapshots DB locale)
      ══════════════════════════════════════════════════════════════ */}
      {activeMainTab === 'assigned' && (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher dans les fiches assignées…"
                value={assignedSearch}
                onChange={e => setAssignedSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 focus:ring-violet-600 transition-all"
              />
            </div>
            {assignedSectors.length > 0 && (
              <select
                value={filterSector}
                onChange={e => setFilterSector(e.target.value)}
                className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 focus:ring-violet-600"
              >
                <option value="">Tous les secteurs</option>
                {assignedSectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>

          {loadingAssigned ? (
            <div className="flex justify-center items-center h-32">
              <div className="w-8 h-8 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : displayAssigned.length === 0 ? (
            <div className="bg-white p-10 rounded-2xl text-center border border-gray-100 shadow-sm">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                <Briefcase size={28} />
              </div>
              <h3 className="text-xl font-black text-gray-800 mb-2">Aucune fiche assignée</h3>
              <p className="text-gray-500 text-sm">Explorez l'onglet ROME et cliquez sur « Assigner » pour envoyer une fiche à un bénéficiaire.</p>
            </div>
          ) : (
            <>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                {displayAssigned.length} fiche{displayAssigned.length > 1 ? 's' : ''} assignée{displayAssigned.length > 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayAssigned.map(fiche => {
                  const romeCode = fiche.extra_info?.startsWith('rome:') ? fiche.extra_info.replace('rome:', '') : null;
                  return (
                    <div
                      key={fiche.id}
                      className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer group flex flex-col"
                      onClick={() => setSelectedLocalFiche(fiche)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="bg-indigo-50 text-indigo-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">{fiche.sector || 'Métier'}</span>
                        {romeCode && <span className="text-xs font-mono text-gray-300 bg-gray-50 px-2 py-0.5 rounded-lg">{romeCode}</span>}
                      </div>
                      <h3 className="text-base font-extrabold text-gray-900 mb-2 group-hover:text-violet-700 transition-colors leading-snug">{fiche.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2 flex-1">{fiche.description}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Modal fiche métier ─────────────────────────────────────────── */}
      {selectedMetier && (
        <div
          className="fixed inset-0 bg-gray-900/70 z-[200] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in"
          onClick={() => setSelectedMetier(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* En-tête avec couleur du grand domaine */}
            <div className={`bg-gradient-to-r ${domaineGradients[selectedMetier.grandDomaine?.code] || 'from-violet-500 to-purple-600'} p-8 rounded-t-3xl relative`}>
              <button
                onClick={() => setSelectedMetier(null)}
                className="absolute top-5 right-5 w-9 h-9 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="bg-white/20 text-white text-xs font-black px-3 py-1.5 rounded-xl font-mono">{selectedMetier.code}</span>
                {selectedMetier.grandDomaine && (
                  <span className="bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-xl">{selectedMetier.grandDomaine.libelle}</span>
                )}
              </div>
              <h2 className="text-2xl font-black text-white leading-tight">{selectedMetier.libelle}</h2>
              {selectedMetier.domainePro && (
                <p className="text-white/70 text-sm font-semibold mt-1">{selectedMetier.domainePro.libelle}</p>
              )}
            </div>

            {/* Corps */}
            <div className="p-8 space-y-6 flex-1">
              {/* Source et lien MétierScope */}
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FileCheck size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-blue-900 text-sm">Consulter la fiche complète sur MétierScope</p>
                    <p className="text-blue-700 text-xs mt-1 leading-relaxed">
                      Copiez le nom du métier ci-dessous, puis collez-le dans la recherche MétierScope pour accéder à la fiche complète (compétences, formations, conditions d'accès).
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <button
                        onClick={() => { navigator.clipboard.writeText(selectedMetier.libelle); toast.success('Nom du métier copié !'); }}
                        className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-black hover:bg-blue-800 transition-colors"
                      >
                        <span>📋 Copier « {selectedMetier.libelle} »</span>
                      </button>
                      <a
                        href="https://candidat.francetravail.fr/metierscope/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-blue-600 font-bold text-xs hover:text-blue-900 transition-colors underline underline-offset-2"
                      >
                        Ouvrir MétierScope ↗
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Informations hiérarchiques */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Code ROME</p>
                  <p className="font-black text-gray-900 font-mono">{selectedMetier.code}</p>
                </div>
                {selectedMetier.grandDomaine && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-gray-400 uppercase mb-1">Grand domaine</p>
                    <p className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">{selectedMetier.grandDomaine.libelle}</p>
                  </div>
                )}
                {selectedMetier.domainePro && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-gray-400 uppercase mb-1">Domaine professionnel</p>
                    <p className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">{selectedMetier.domainePro.libelle}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer — assigner */}
            {(userRole === 'admin' || userRole === 'formateur') && (
              <div className="p-6 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3 rounded-b-3xl">
                <p className="text-sm text-gray-500">Ce métier correspond à l'un de vos bénéficiaires ?</p>
                <button
                  onClick={() => { setFicheToAssign(selectedMetier); setIsAssignModalOpen(true); setSelectedMetier(null); }}
                  className="flex items-center gap-2 bg-violet-700 text-white px-6 py-3 rounded-xl font-bold hover:bg-violet-800 transition-colors shadow-lg"
                >
                  <Users size={18} /> Assigner à un bénéficiaire
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal fiche locale (onglet Fiches Assignées) ─────────────── */}
      {selectedLocalFiche && (
        <LocalFicheDetailModal
          fiche={selectedLocalFiche}
          onClose={() => setSelectedLocalFiche(null)}
          onAssign={userRole !== 'client' ? (f) => {
            const romeCode = f.extra_info?.startsWith('rome:') ? f.extra_info.replace('rome:', '') : null;
            setFicheToAssign({ code: romeCode, libelle: f.title });
            setSelectedLocalFiche(null);
            setIsAssignModalOpen(true);
          } : null}
        />
      )}

      {/* ── Modal assignation ─────────────────────────────────────────── */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 bg-gray-900/70 z-[250] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-md border border-gray-100">
            <h3 className="text-xl font-black text-gray-900 mb-2">Assigner la fiche métier</h3>
            <p className="text-sm text-gray-500 mb-6">
              Assigner <strong className="text-gray-900">« {ficheToAssign?.libelle} »</strong> à un bénéficiaire.
            </p>
            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Bénéficiaire</label>
              <select
                className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-violet-400 text-sm font-semibold text-gray-700"
                value={selectedClientId}
                onChange={e => setSelectedClientId(e.target.value)}
              >
                <option value="">— Choisir un bénéficiaire —</option>
                {availableClients.map(c => (
                  <option key={c.id} value={c.id}>{c.nom} ({c.email_contact})</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setIsAssignModalOpen(false); setFicheToAssign(null); setSelectedClientId(''); }}
                className="px-5 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAssignRomeFiche}
                disabled={!selectedClientId || isAssigning}
                className="px-6 py-3 bg-violet-700 text-white font-bold rounded-xl hover:bg-violet-800 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAssigning
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Envoi…</>
                  : <><Users size={16} /> Assigner</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sous-composant : modal de détail pour une fiche locale (snapshot DB)
const LocalFicheDetailModal = ({ fiche, onClose, onAssign }) => {
  const romeCode = fiche.extra_info?.startsWith('rome:') ? fiche.extra_info.replace('rome:', '') : null;
  return (
    <div className="fixed inset-0 bg-gray-900/70 z-[200] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-8 border-b border-gray-100 relative">
          <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full flex items-center justify-center transition-colors">
            <X size={20} />
          </button>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="bg-indigo-50 text-indigo-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">{fiche.sector || 'Métier'}</span>
            {romeCode && <span className="bg-violet-50 text-violet-600 text-xs font-black px-3 py-1 rounded-xl font-mono">{romeCode}</span>}
          </div>
          <h2 className="text-3xl font-black text-gray-900">{fiche.title}</h2>
        </div>

        <div className="p-8 space-y-6 flex-1">
          {fiche.description && (
            <section>
              <h3 className="text-base font-black text-gray-700 mb-2 flex items-center gap-2"><FileCheck size={18} className="text-violet-500" /> Missions principales</h3>
              <p className="text-gray-700 leading-relaxed">{fiche.description}</p>
            </section>
          )}
          {fiche.skills && (
            <section>
              <h3 className="text-base font-black text-gray-700 mb-2">Compétences</h3>
              <div className="flex flex-wrap gap-2">
                {fiche.skills.split(',').map((s, i) => (
                  <span key={i} className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-3 py-1 rounded-lg text-sm font-bold">{s.trim()}</span>
                ))}
              </div>
            </section>
          )}
          {fiche.qualities && (
            <section>
              <h3 className="text-base font-black text-gray-700 mb-2">Qualités</h3>
              <div className="flex flex-wrap gap-2">
                {fiche.qualities.split(',').map((q, i) => (
                  <span key={i} className="bg-amber-50 text-amber-700 border border-amber-100 px-3 py-1 rounded-lg text-sm font-bold">{q.trim()}</span>
                ))}
              </div>
            </section>
          )}
          {fiche.career_evolution && (
            <section>
              <h3 className="text-base font-black text-gray-700 mb-2">Accès à l'emploi / Évolution</h3>
              <p className="text-gray-700 text-sm bg-blue-50 p-4 rounded-xl">{fiche.career_evolution}</p>
            </section>
          )}
          {romeCode && (
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
              <span>Source : France Travail — ROME 4.0</span>
              <a href={`https://candidat.francetravail.fr/metierscope/fiche-metier?codeRome=${romeCode}&intitule=${encodeURIComponent(fiche.title || '')}`} target="_blank" rel="noopener noreferrer" className="text-violet-500 hover:text-violet-700 font-bold">
                Voir sur MétierScope ↗
              </a>
            </div>
          )}
        </div>

        {onAssign && (
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-between items-center rounded-b-3xl">
            <p className="text-sm text-gray-500">Assigner cette fiche à un autre bénéficiaire ?</p>
            <button onClick={() => onAssign(fiche)} className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-gray-800 transition-colors shadow">
              <Users size={16} /> Assigner
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// ==========================================
// AUTOMATION SETTINGS VIEW
// ==========================================
const KNOWN_TRIGGER_TYPES = ['no_signature', 'reminder_before_session', 'welcome'];

const TRIGGER_LABELS = {
  no_signature: 'Relance émargement non signé',
  reminder_before_session: 'Rappel avant séance',
  welcome: 'Email de bienvenue',
};

const getTriggerLabel = (type) => TRIGGER_LABELS[type] || type;

// Traduction + style des statuts d'envoi (automation_logs.status), pour le "Journal des envois" —
// avant ce correctif la valeur brute anglaise ("sent") s'affichait telle quelle.
const LOG_STATUS_LABELS = {
  sent: { label: 'Envoyé', className: 'bg-green-50 text-green-700 border-green-200' },
  simulated: { label: 'Simulé (test)', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  error: { label: 'Échec', className: 'bg-red-50 text-red-700 border-red-200' },
};
const getLogStatusInfo = (status) =>
  LOG_STATUS_LABELS[status] || { label: status || '—', className: 'bg-gray-50 text-gray-600 border-gray-200' };

// Signification de chaque balise disponible dans les emails de relance — affichée dans le
// panneau d'aide à côté de l'éditeur, pour que l'admin sache exactement ce que chaque balise
// va afficher dans l'email réellement envoyé (voir api/automation/trigger-manual.js et
// api/automation/process.js pour la logique de remplissage correspondante).
const TAG_DEFINITIONS = {
  '{{client_name}}': {
    label: 'Nom du client',
    example: 'Jean Dupont',
    description: "Le nom complet du client, tel qu'enregistré dans sa fiche.",
  },
  '{{session_title}}': {
    label: 'Titre de la séance',
    example: 'Séance 2 - Émargement de présence',
    description: "L'intitulé de la séance concernée.",
  },
  '{{session_date}}': {
    label: 'Date de la séance',
    example: 'jeudi 30 juillet 2026',
    description: 'La date de la séance, écrite en toutes lettres.',
  },
  '{{session_time}}': {
    label: 'Heure de la séance',
    example: '11:00',
    description: "L'heure de début de la séance.",
  },
  '{{session_number}}': {
    label: 'Numéro de la séance',
    example: '2',
    description: "Le numéro d'ordre de la séance (ex : la 2ème séance du parcours).",
  },
  '{{numero_dossier}}': {
    label: 'Numéro de dossier',
    example: 'D-2026-014',
    description: "Le numéro de dossier du client, si renseigné dans sa fiche.",
  },
  '{{module_name}}': {
    label: 'Nom du module / de la formation',
    example: 'Bilan de compétences',
    description: "Le nom du module ou de la formation suivie par le client, si renseigné dans sa fiche.",
  },
};

const TRIGGER_VARS = {
  no_signature: ['{{client_name}}', '{{session_title}}', '{{session_date}}', '{{session_time}}', '{{session_number}}', '{{numero_dossier}}', '{{module_name}}'],
  reminder_before_session: ['{{client_name}}', '{{session_title}}', '{{session_date}}', '{{session_time}}', '{{session_number}}', '{{numero_dossier}}', '{{module_name}}'],
  welcome: ['{{client_name}}', '{{numero_dossier}}', '{{module_name}}'],
};

// Balises pour les types personnalisés (ajouté 2026-07-29) : depuis que ces relances sont
// rattachées à une vraie séance (module + numéro de séance + avant/après), les mêmes balises
// de séance que les relances prédéfinies sont désormais disponibles, plutôt que juste {{client_name}}.
const CUSTOM_TAGS = ['{{client_name}}', '{{session_title}}', '{{session_date}}', '{{session_time}}', '{{session_number}}', '{{numero_dossier}}', '{{module_name}}'];

const EMPTY_FORM = {
  trigger_type: 'no_signature',
  delay_days: 3,
  email_subject: '',
  email_body: '',
  is_active: true,
  // Champs utilisés uniquement par les types personnalisés (__custom__) — ajoutés 2026-07-29
  // pour permettre de cibler un module/une séance précise et un moment avant/après.
  target_module_id: '',
  target_numero_seance: '',
  timing_direction: 'before',
  require_unsigned: false,
};

// ─── Vue Admin/Formateur : Questionnaires Prospects ────────────────────────────────────────────
// Demandé par l'utilisateur le 22/09/2026 : envoyer un questionnaire d'entretien préalable à une
// personne qui n'a pas (encore) de compte SkorUp, par email, sans qu'elle ait à se connecter.
// Les MODÈLES (questions) sont gérés par l'admin uniquement ; l'admin ET le formateur peuvent
// ENVOYER un questionnaire à un prospect en choisissant un modèle actif. L'envoi passe par
// api/prospects.js (jamais un insert Supabase direct) car c'est cette fonction qui génère le
// token aléatoire du lien public et envoie l'email — voir ce fichier pour le détail de sécurité.
// --- Finances (2026-09-26) : vue d'ensemble admin des encaissements clients ---------------------
function FinancesView({ supabase, currentOrgId, clients }) {
  const [paiements, setPaiements] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchPaiements = async () => {
      setLoading(true);
      const { data, error } = await supabase.from('client_paiements').select('*').eq('organisation_id', currentOrgId);
      if (!error) setPaiements(data || []);
      setLoading(false);
    };
    if (currentOrgId) fetchPaiements();
  }, [currentOrgId, supabase]);

  const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  const paiementsByClient = paiements.reduce((acc, p) => {
    acc[p.client_id] = (acc[p.client_id] || 0) + (parseFloat(p.montant) || 0);
    return acc;
  }, {});

  const rows = (clients || [])
    .filter(c => c.montant_prestation && parseFloat(c.montant_prestation) > 0)
    .map(c => {
      const montantTotal = parseFloat(c.montant_prestation) || 0;
      const montantPaye = paiementsByClient[c.id] || 0;
      const resteDu = montantTotal - montantPaye;
      const pourcentageFormateur = parseFloat(c.pourcentage_formateur) || 0;
      const partFormateur = montantTotal * pourcentageFormateur / 100;
      const partOrganisme = montantTotal - partFormateur;
      return { client: c, montantTotal, montantPaye, resteDu, pourcentageFormateur, partFormateur, partOrganisme };
    })
    .sort((a, b) => b.resteDu - a.resteDu);

  const totals = rows.reduce((acc, r) => ({
    montantTotal: acc.montantTotal + r.montantTotal,
    montantPaye: acc.montantPaye + r.montantPaye,
    resteDu: acc.resteDu + r.resteDu,
    partFormateur: acc.partFormateur + r.partFormateur,
    partOrganisme: acc.partOrganisme + r.partOrganisme,
  }), { montantTotal: 0, montantPaye: 0, resteDu: 0, partFormateur: 0, partOrganisme: 0 });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black text-gray-900">Finances</h2>
        <p className="text-sm text-gray-500 mt-1">Vue d'ensemble des encaissements clients. Les paiements se saisissent dans l'onglet "Finances" de chaque fiche client.</p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 italic">Chargement...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <p className="text-xs font-black uppercase text-gray-400 tracking-widest mb-1">CA prévisionnel</p>
              <p className="text-lg font-black text-gray-800">{fmt(totals.montantTotal)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
              <p className="text-xs font-black uppercase text-emerald-700 tracking-widest mb-1">Encaissé</p>
              <p className="text-lg font-black text-emerald-800">{fmt(totals.montantPaye)}</p>
            </div>
            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4">
              <p className="text-xs font-black uppercase text-orange-700 tracking-widest mb-1">Reste dû</p>
              <p className="text-lg font-black text-orange-800">{fmt(totals.resteDu)}</p>
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
              <p className="text-xs font-black uppercase text-indigo-700 tracking-widest mb-1">Part formateurs</p>
              <p className="text-lg font-black text-indigo-800">{fmt(totals.partFormateur)}</p>
            </div>
            <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4">
              <p className="text-xs font-black uppercase text-violet-700 tracking-widest mb-1">Part organisme</p>
              <p className="text-lg font-black text-violet-800">{fmt(totals.partOrganisme)}</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left">
                  <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-widest">Client</th>
                  <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-widest text-right">Montant total</th>
                  <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-widest text-right">Payé</th>
                  <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-widest text-right">Reste dû</th>
                  <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-widest text-right">% Formateur</th>
                  <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-widest text-right">Part formateur</th>
                  <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-widest text-right">Part organisme</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="7" className="p-8 text-center text-gray-400 italic">Aucun client avec un montant de prestation renseigné.</td></tr>
                ) : rows.map(r => (
                  <tr key={r.client.id} className="border-b border-gray-50 last:border-0">
                    <td className="p-4 font-bold text-gray-800">{r.client.nomcomplet_client || r.client.nom_complet || 'Client'}</td>
                    <td className="p-4 text-right">{fmt(r.montantTotal)}</td>
                    <td className="p-4 text-right text-emerald-700 font-bold">{fmt(r.montantPaye)}</td>
                    <td className={`p-4 text-right font-bold ${r.resteDu > 0 ? 'text-orange-700' : 'text-gray-400'}`}>{fmt(r.resteDu)}</td>
                    <td className="p-4 text-right text-gray-500">{r.pourcentageFormateur ? `${r.pourcentageFormateur}%` : '—'}</td>
                    <td className="p-4 text-right text-indigo-700">{r.pourcentageFormateur ? fmt(r.partFormateur) : '—'}</td>
                    <td className="p-4 text-right text-violet-700">{r.pourcentageFormateur ? fmt(r.partOrganisme) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ProspectsView({ supabase, currentOrgId, userRole, currentUserId = null }) {
  const isAdmin = userRole === 'admin';
  const [templates, setTemplates] = useState([]);
  const [envois, setEnvois] = useState([]);
  const [loading, setLoading] = useState(true);
  // Annuaire id -> nom (admins + formateurs de l'organisme), pour afficher qui a envoyé chaque
  // questionnaire (2026-09-29). Fetché ici plutôt que de dépendre d'une liste "formateurs" passée
  // en prop, car celle du composant parent exclut les admins alors qu'un admin peut aussi envoyer.
  const [staffById, setStaffById] = useState({});

  // --- Modale d'envoi ---
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ nom: '', prenom: '', email: '', templateId: '' });
  const [sending, setSending] = useState(false);

  // --- Gestion des modèles (admin uniquement) ---
  const [showTemplates, setShowTemplates] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [tTitre, setTTitre] = useState('');
  const [tQuestions, setTQuestions] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // --- Consultation des réponses d'un envoi rempli ---
  const [viewingEnvoi, setViewingEnvoi] = useState(null);

  // --- Statistiques par modèle (demande utilisateur, 2026-09-22, usage Qualiopi) ---
  const [showStats, setShowStats] = useState(false);
  const [statsTemplateId, setStatsTemplateId] = useState('');

  const fetchTemplates = async () => {
    if (!currentOrgId) return;
    const { data, error } = await supabase
      .from('prospect_questionnaire_templates')
      .select('*')
      .eq('organisation_id', currentOrgId)
      .order('created_at', { ascending: false });
    if (!error) setTemplates(data || []);
  };

  const fetchEnvois = async () => {
    if (!currentOrgId) return;
    const { data, error } = await supabase
      .from('prospect_questionnaire_envois')
      .select('*')
      .eq('organisation_id', currentOrgId)
      .order('envoye_at', { ascending: false });
    // FIX (2026-10-20) : en espace formateur, uniquement SES envois (liste et statistiques). La base
    // le fait déjà pour un vrai formateur ; ce filtre couvre aussi l'admin en « Mon espace Formateur ».
    if (!error) setEnvois(isAdmin ? (data || []) : (data || []).filter(e => currentUserId != null && String(e.envoye_par) === String(currentUserId)));
  };

  const fetchStaff = async () => {
    if (!currentOrgId) return;
    const { data, error } = await supabase
      .from('utilisateurs')
      .select('id, nom')
      .eq('organisation_id', currentOrgId);
    if (!error && data) {
      const map = {};
      data.forEach(u => { map[u.id] = u.nom; });
      setStaffById(map);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchTemplates(), fetchEnvois(), fetchStaff()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, currentUserId, userRole]);

  const activeTemplates = templates.filter(t => t.actif);
  const templateTitre = (id) => templates.find(t => t.id === id)?.titre || 'Modèle supprimé';

  // ── Statistiques par modèle — calculées ici, côté navigateur, à partir des envois déjà en
  //     base (aucune fonction serveur supplémentaire nécessaire) ──
  React.useEffect(() => {
    if (!statsTemplateId && templates.length > 0) setStatsTemplateId(templates[0].id);
  }, [templates]); // eslint-disable-line react-hooks/exhaustive-deps

  const statsTemplate = templates.find(t => t.id === statsTemplateId);
  const statsEnvois = React.useMemo(
    () => statsTemplate ? envois.filter(e => e.template_id === statsTemplate.id && e.statut === 'rempli') : [],
    [statsTemplate, envois]
  );
  const questionStats = React.useMemo(() => {
    if (!statsTemplate) return [];
    const total = statsEnvois.length;
    return (statsTemplate.questions || []).map(q => {
      if (q.type === 'text') {
        const answered = statsEnvois.filter(e => (e.reponses?.[q.id] || '').toString().trim().length > 0).length;
        return { question: q, type: 'text', answered, total };
      }
      const counts = {};
      (q.options || []).forEach(opt => { counts[opt] = 0; });
      statsEnvois.forEach(e => {
        const rep = e.reponses?.[q.id];
        if (q.type === 'single') {
          if (rep && Object.prototype.hasOwnProperty.call(counts, rep)) counts[rep]++;
        } else if (q.type === 'multiple') {
          (Array.isArray(rep) ? rep : []).forEach(opt => { if (Object.prototype.hasOwnProperty.call(counts, opt)) counts[opt]++; });
        }
      });
      return { question: q, type: q.type, counts, total };
    });
  }, [statsTemplate, statsEnvois]);

  // ── PDF des réponses d'un prospect (usage Qualiopi : trace du positionnement préalable) ──
  // Génération 100% côté navigateur avec jsPDF (déjà utilisé ailleurs dans SkorUp pour d'autres
  // PDF) — aucune fonction serveur supplémentaire nécessaire.
  const handleDownloadResponsesPdf = async (envoi) => { /* 2026-10-21 : chargé à la demande */ const { jsPDF } = await import('jspdf');
    const template = templates.find(t => t.id === envoi.template_id);
    const questions = template?.questions || [];
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const marginX = 50;
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const maxWidth = pageWidth - marginX * 2;
    const lineHeight = 15;
    let y = 60;

    const ensureSpace = (needed) => {
      if (y + needed > pageHeight - 50) { pdf.addPage(); y = 60; }
    };

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(20);
    const titleLines = pdf.splitTextToSize(template?.titre || 'Questionnaire prospect', maxWidth);
    pdf.text(titleLines, marginX, y);
    y += titleLines.length * 20 + 10;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text(`${envoi.prenom || ''} ${envoi.nom || ''} — ${envoi.email || ''}`, marginX, y);
    y += 14;
    pdf.text(`Répondu le ${envoi.rempli_at ? new Date(envoi.rempli_at).toLocaleDateString('fr-FR') : '—'}`, marginX, y);
    y += 20;
    pdf.setDrawColor(220);
    pdf.line(marginX, y, pageWidth - marginX, y);
    y += 24;

    questions.forEach((q, qi) => {
      const rep = envoi.reponses?.[q.id];
      const repText = Array.isArray(rep) ? (rep.length > 0 ? rep.join(', ') : 'Sans réponse') : ((rep && String(rep).trim()) || 'Sans réponse');

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(20);
      const qLines = pdf.splitTextToSize(`${qi + 1}. ${q.text || ''}`, maxWidth);
      ensureSpace(qLines.length * lineHeight + 20);
      pdf.text(qLines, marginX, y);
      y += qLines.length * lineHeight + 4;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(80);
      const aLines = pdf.splitTextToSize(repText, maxWidth);
      ensureSpace(aLines.length * lineHeight + 20);
      pdf.text(aLines, marginX, y);
      y += aLines.length * lineHeight + 18;
    });

    const safeName = `${envoi.prenom || ''}_${envoi.nom || ''}`.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') || 'prospect';
    pdf.save(`questionnaire_${safeName}.pdf`);
  };

  // ── Constructeur de modèle (admin) — reprend le même schéma de questions que les
  //     questionnaires de module existants (text / single / multiple) ──
  // FIX (demande utilisateur, 2026-09-22) : "obligatoire" par défaut à false — une question de
  // questionnaire prospect n'empêche donc plus la soumission tant qu'elle n'est pas explicitement
  // cochée comme obligatoire. S'applique aussi aux questions de modèles déjà créés (voir isComplete
  // dans ProspectQuestionnaireView : un champ "obligatoire" absent est traité comme false).
  const tAddQuestion = () => setTQuestions(prev => [...prev, { id: Date.now(), text: '', type: 'single', options: ['', ''], obligatoire: false }]);
  const tRemoveQuestion = (id) => setTQuestions(prev => prev.filter(q => q.id !== id));
  const tUpdateQuestion = (id, field, val) => setTQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: val } : q));
  const tAddOption = (qId) => setTQuestions(prev => prev.map(q => q.id === qId ? { ...q, options: [...q.options, ''] } : q));
  const tUpdateOption = (qId, oi, val) => setTQuestions(prev => prev.map(q => q.id === qId ? { ...q, options: q.options.map((o, i) => i === oi ? val : o) } : q));
  const tRemoveOption = (qId, oi) => setTQuestions(prev => prev.map(q => q.id === qId ? { ...q, options: q.options.filter((_, i) => i !== oi) } : q));

  const resetBuilder = () => { setTTitre(''); setTQuestions([]); setEditingTemplateId(null); setShowBuilder(false); };

  const handleSaveTemplate = async () => {
    if (!tTitre.trim() || tQuestions.length === 0) return;
    setSavingTemplate(true);
    const questionsPayload = tQuestions.map(q => ({ id: String(q.id), text: q.text, type: q.type, options: q.options, obligatoire: !!q.obligatoire }));
    if (editingTemplateId) {
      const { error } = await supabase
        .from('prospect_questionnaire_templates')
        .update({ titre: tTitre.trim(), questions: questionsPayload, updated_at: new Date().toISOString() })
        .eq('id', editingTemplateId);
      if (error) { toast.error('Erreur mise à jour : ' + error.message); setSavingTemplate(false); return; }
      toast.success('Modèle mis à jour.');
    } else {
      const { error } = await supabase
        .from('prospect_questionnaire_templates')
        .insert([{ titre: tTitre.trim(), questions: questionsPayload, organisation_id: currentOrgId, actif: true }]);
      if (error) { toast.error('Erreur création : ' + error.message); setSavingTemplate(false); return; }
      toast.success('Modèle créé.');
    }
    setSavingTemplate(false);
    resetBuilder();
    fetchTemplates();
  };

  const handleEditTemplate = (t) => {
    setTTitre(t.titre || '');
    setTQuestions((t.questions || []).map(q => ({ ...q, id: q.id || Date.now() + Math.random() })));
    setEditingTemplateId(t.id);
    setShowBuilder(true);
    setShowTemplates(true);
  };

  const handleToggleActif = async (t) => {
    const { error } = await supabase.from('prospect_questionnaire_templates').update({ actif: !t.actif }).eq('id', t.id);
    if (error) { toast.error('Erreur : ' + error.message); return; }
    fetchTemplates();
  };

  // ── Envoi d'un questionnaire à un prospect ──
  const handleSend = async () => {
    if (!sendForm.templateId) { toast.error('Choisissez un modèle de questionnaire.'); return; }
    if (!sendForm.nom.trim() || !sendForm.prenom.trim()) { toast.error('Le nom et le prénom sont requis.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sendForm.email.trim())) { toast.error('Adresse email invalide.'); return; }
    setSending(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) { toast.error('Session expirée, reconnectez-vous.'); setSending(false); return; }
      const resp = await fetch('/api/prospects?action=envoyer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          templateId: sendForm.templateId,
          nom: sendForm.nom.trim(),
          prenom: sendForm.prenom.trim(),
          email: sendForm.email.trim(),
          origin: window.location.origin,
        }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok) { toast.error(result.error || 'Erreur lors de l\'envoi.'); setSending(false); return; }
      if (result.simulated) toast('Questionnaire créé, mais RESEND_API_KEY n\'est pas configurée côté serveur : aucun email envoyé.', { icon: '⚠️' });
      else if (result.sent === false) toast.error('Le questionnaire est enregistré mais l\'email n\'a pas pu partir : ' + (result.error || ''));
      else toast.success('Questionnaire envoyé à ' + sendForm.email.trim());
      setShowSendModal(false);
      setSendForm({ nom: '', prenom: '', email: '', templateId: '' });
      fetchEnvois();
    } catch (e) {
      toast.error('Erreur réseau : ' + e.message);
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
            <Send className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Questionnaires Prospects</h1>
            <p className="text-sm text-gray-500">Envoyez un questionnaire d'entretien préalable, sans que le prospect ait besoin d'un compte SkorUp</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {isAdmin && (
            <button
              onClick={() => setShowTemplates(v => !v)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
            >
              <Settings className="w-4 h-4" /> {showTemplates ? 'Masquer les modèles' : 'Gérer les modèles'}
            </button>
          )}
          <button
            onClick={() => setShowStats(v => !v)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
          >
            <TrendingUp className="w-4 h-4" /> {showStats ? 'Masquer les statistiques' : 'Statistiques'}
          </button>
          <button
            onClick={() => setShowSendModal(true)}
            disabled={activeTemplates.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            title={activeTemplates.length === 0 ? 'Aucun modèle actif disponible' : ''}
          >
            <Plus className="w-4 h-4" /> Envoyer un questionnaire
          </button>
        </div>
      </div>

      {activeTemplates.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          {isAdmin
            ? 'Aucun modèle de questionnaire actif pour le moment. Cliquez sur "Gérer les modèles" pour en créer un.'
            : 'Aucun modèle de questionnaire n\'est disponible pour le moment — demandez à votre administrateur d\'en créer un.'}
        </div>
      )}

      {/* ── Statistiques par modèle (usage Qualiopi) ── */}
      {showStats && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-base font-bold text-gray-800">Statistiques</h2>
            {templates.length > 0 && (
              <select value={statsTemplateId} onChange={e => setStatsTemplateId(e.target.value)}
                className="p-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400">
                {templates.map(t => <option key={t.id} value={t.id}>{t.titre}</option>)}
              </select>
            )}
          </div>
          {templates.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Aucun modèle pour le moment.</p>
          ) : statsEnvois.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Aucune réponse reçue pour ce modèle pour le moment.</p>
          ) : (
            <div className="space-y-6">
              <p className="text-sm text-gray-500">{statsEnvois.length} réponse{statsEnvois.length > 1 ? 's' : ''} reçue{statsEnvois.length > 1 ? 's' : ''}</p>
              {questionStats.map(({ question, type, counts, answered, total }, qi) => (
                <div key={question.id}>
                  <p className="font-bold text-gray-800 text-sm mb-2">{qi + 1}. {question.text}</p>
                  {type === 'text' ? (
                    <p className="text-xs text-gray-500">{answered} / {total} ont répondu à cette question</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(counts).map(([opt, count]) => {
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={opt}>
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                              <span>{opt || <span className="italic text-gray-400">Option vide</span>}</span>
                              <span className="font-bold">{count} ({pct}%)</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-violet-600 transition-all duration-500" style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Gestion des modèles (admin) ── */}
      {isAdmin && showTemplates && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-gray-800">Modèles de questionnaires</h2>
            <button
              onClick={() => { if (showBuilder) resetBuilder(); else setShowBuilder(true); }}
              className="flex items-center gap-2 bg-violet-700 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm hover:bg-violet-800 transition-all"
            >
              <Plus size={13} /> {showBuilder ? 'Annuler' : 'Nouveau modèle'}
            </button>
          </div>

          {showBuilder && (
            <div className="bg-violet-50 rounded-2xl p-5 border border-violet-200 space-y-4 mb-5">
              <div>
                <label className="block text-xs font-black text-violet-700 uppercase tracking-widest mb-1.5">Titre du modèle</label>
                <input type="text" placeholder="Ex : Questionnaire d'entretien préalable"
                  value={tTitre} onChange={e => setTTitre(e.target.value)}
                  className="w-full p-3 bg-white border border-violet-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-black text-violet-700 uppercase tracking-widest">Questions ({tQuestions.length})</label>
                  <button type="button" onClick={tAddQuestion} className="text-xs font-black text-violet-600 hover:text-violet-800 uppercase">+ Question</button>
                </div>
                {tQuestions.length === 0 && (
                  <div className="py-5 text-center bg-white rounded-xl border border-dashed border-gray-200">
                    <p className="text-gray-400 text-xs">Cliquez sur "+ Question" pour commencer</p>
                  </div>
                )}
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {tQuestions.map((q, qi) => (
                    <div key={q.id} className="bg-white rounded-xl p-3 border border-violet-100 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-black text-violet-600 uppercase">Q{qi + 1}</span>
                        <button type="button" onClick={() => tRemoveQuestion(q.id)} className="w-5 h-5 rounded bg-red-50 text-red-400 text-xs flex items-center justify-center hover:bg-red-100">✕</button>
                      </div>
                      <input type="text" placeholder="Texte de la question..." value={q.text} onChange={e => tUpdateQuestion(q.id, 'text', e.target.value)}
                        className="w-full p-2.5 bg-gray-50 border border-violet-200 rounded-lg text-sm outline-none focus:ring-1 focus:ring-violet-400" />
                      <div className="flex gap-1.5">
                        {[['single', '◉ Unique'], ['multiple', '☑ Multiple'], ['text', '✏️ Libre']].map(([val, lbl]) => (
                          <button key={val} type="button" onClick={() => tUpdateQuestion(q.id, 'type', val)}
                            className={`flex-1 py-1 rounded text-xs font-black transition-all ${q.type === val ? 'bg-violet-600 text-white' : 'bg-white border border-violet-200 text-gray-500 hover:border-violet-400'}`}>{lbl}</button>
                        ))}
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none pt-0.5">
                        <input type="checkbox" checked={!!q.obligatoire} onChange={e => tUpdateQuestion(q.id, 'obligatoire', e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-violet-300 text-violet-600 focus:ring-violet-400" />
                        <span className="text-xs font-bold text-gray-500">Obligatoire — le prospect devra y répondre pour valider</span>
                      </label>
                      {(q.type === 'single' || q.type === 'multiple') && (
                        <div className="space-y-1">
                          {(q.options || []).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <span className="text-gray-400 text-xs">{q.type === 'single' ? '○' : '□'}</span>
                              <input type="text" placeholder={`Option ${oi + 1}`} value={opt} onChange={e => tUpdateOption(q.id, oi, e.target.value)}
                                className="flex-1 p-1.5 bg-gray-50 border border-violet-100 rounded-lg text-xs outline-none focus:ring-1 focus:ring-violet-400" />
                              {q.options.length > 1 && <button type="button" onClick={() => tRemoveOption(q.id, oi)} className="text-gray-300 hover:text-red-400 text-xs">✕</button>}
                            </div>
                          ))}
                          <button type="button" onClick={() => tAddOption(q.id)} className="text-xs text-violet-500 hover:text-violet-700 font-bold">+ Option</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={handleSaveTemplate} disabled={!tTitre.trim() || tQuestions.length === 0 || savingTemplate}
                className="w-full bg-violet-700 hover:bg-violet-800 text-white font-black py-3 rounded-xl transition-all disabled:opacity-50">
                {savingTemplate ? 'Enregistrement…' : editingTemplateId ? '✓ Mettre à jour le modèle' : '✓ Enregistrer le modèle'}
              </button>
            </div>
          )}

          {templates.length > 0 ? (
            <div className="space-y-2">
              {templates.map(t => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:border-violet-200 transition-all">
                  <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => handleToggleActif(t)} className="focus:outline-none shrink-0" title={t.actif ? 'Désactiver' : 'Activer'}>
                      {t.actif ? <ToggleRight className="w-6 h-6 text-violet-600" /> : <ToggleLeft className="w-6 h-6 text-gray-300" />}
                    </button>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 text-sm truncate">{t.titre}</p>
                      <p className="text-xs text-gray-400">{(t.questions || []).length} question{(t.questions || []).length > 1 ? 's' : ''} {!t.actif && '· Désactivé'}</p>
                    </div>
                  </div>
                  <button onClick={() => handleEditTemplate(t)} className="text-gray-400 hover:text-violet-600 shrink-0 p-1.5">
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-4">Aucun modèle pour le moment.</p>
          )}
        </div>
      )}

      {/* ── Liste des envois ── */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-base font-bold text-gray-800 mb-4">Questionnaires envoyés ({envois.length})</h2>
        {envois.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">Aucun questionnaire envoyé pour le moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                  <th className="pb-2 pr-3">Prospect</th>
                  <th className="pb-2 pr-3">Modèle</th>
                  <th className="pb-2 pr-3">Envoyé par</th>
                  <th className="pb-2 pr-3">Envoyé le</th>
                  <th className="pb-2 pr-3">Statut</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {envois.map(e => (
                  <tr key={e.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3">
                      <p className="font-bold text-gray-800">{e.prenom} {e.nom}</p>
                      <p className="text-xs text-gray-400">{e.email}</p>
                    </td>
                    <td className="py-3 pr-3 text-gray-600">{templateTitre(e.template_id)}</td>
                    <td className="py-3 pr-3 text-gray-600 text-xs">{staffById[e.envoye_par] || '—'}</td>
                    <td className="py-3 pr-3 text-gray-500 text-xs">{e.envoye_at ? new Date(e.envoye_at).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="py-3 pr-3">
                      {e.statut === 'rempli'
                        ? <span className="inline-flex items-center gap-1 text-xs font-black text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> Rempli</span>
                        : <span className="inline-flex items-center gap-1 text-xs font-black text-amber-700 bg-amber-50 px-2 py-1 rounded-full"><Clock className="w-3 h-3" /> En attente</span>}
                    </td>
                    <td className="py-3 text-right">
                      {e.statut === 'rempli' && (
                        <button onClick={() => setViewingEnvoi(e)} className="text-violet-600 hover:text-violet-800 text-xs font-bold flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" /> Voir les réponses
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modale d'envoi ── */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-violet-600 p-6 text-white relative">
              <h2 className="text-lg font-black">Envoyer un questionnaire</h2>
              <p className="text-violet-200 text-sm mt-1">Le prospect recevra un lien par email, sans avoir besoin de compte.</p>
              <button onClick={() => setShowSendModal(false)} className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20">✕</button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1">Prénom</label>
                  <input type="text" value={sendForm.prenom} onChange={e => setSendForm(f => ({ ...f, prenom: e.target.value }))}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400" />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1">Nom</label>
                  <input type="text" value={sendForm.nom} onChange={e => setSendForm(f => ({ ...f, nom: e.target.value }))}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1">Email</label>
                <input type="email" value={sendForm.email} onChange={e => setSendForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1">Modèle de questionnaire</label>
                <select value={sendForm.templateId} onChange={e => setSendForm(f => ({ ...f, templateId: e.target.value }))}
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-400">
                  <option value="">Choisir un modèle…</option>
                  {activeTemplates.map(t => <option key={t.id} value={t.id}>{t.titre}</option>)}
                </select>
              </div>
              <button onClick={handleSend} disabled={sending}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white font-black py-3 rounded-xl transition-all disabled:opacity-50 mt-2">
                {sending ? 'Envoi en cours…' : 'Envoyer le questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale de consultation des réponses ── */}
      {viewingEnvoi && (() => {
        const template = templates.find(t => t.id === viewingEnvoi.template_id);
        const questions = template?.questions || [];
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
              <div className="bg-violet-600 p-6 text-white relative shrink-0">
                <h2 className="text-lg font-black">{viewingEnvoi.prenom} {viewingEnvoi.nom}</h2>
                <p className="text-violet-200 text-sm mt-1">{template?.titre || 'Modèle supprimé'} · Répondu le {viewingEnvoi.rempli_at ? new Date(viewingEnvoi.rempli_at).toLocaleDateString('fr-FR') : '—'}</p>
                <button onClick={() => setViewingEnvoi(null)} className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20">✕</button>
              </div>
              <div className="p-6 space-y-5 overflow-y-auto flex-1">
                {questions.map((q, qi) => {
                  const rep = viewingEnvoi.reponses?.[q.id];
                  return (
                    <div key={q.id}>
                      <p className="font-bold text-gray-900 text-sm mb-1.5"><span className="text-violet-500 font-black mr-1">{qi + 1}.</span>{q.text}</p>
                      <p className="text-gray-600 text-sm bg-gray-50 rounded-xl p-3">
                        {Array.isArray(rep) ? (rep.length > 0 ? rep.join(', ') : <span className="text-gray-300 italic">Sans réponse</span>) : (rep || <span className="text-gray-300 italic">Sans réponse</span>)}
                      </p>
                    </div>
                  );
                })}
                {questions.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Le modèle utilisé pour cet envoi a été supprimé — impossible d'afficher les questions.</p>}
              </div>
              {questions.length > 0 && (
                <div className="p-4 border-t border-gray-100 shrink-0">
                  <button onClick={() => handleDownloadResponsesPdf(viewingEnvoi)}
                    className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-bold py-2.5 rounded-xl transition-all text-sm">
                    <Download className="w-4 h-4" /> Télécharger en PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function AutomationSettingsView({ supabase, currentOrgId }) {
  const [settings, setSettings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [customTriggerName, setCustomTriggerName] = useState('');
  const [showTagHelp, setShowTagHelp] = useState(true);
  // Modules et "dossiers de séance" (module_session_templates) de l'organisme — utilisés pour
  // construire les sélecteurs "Module ciblé" / "Séance ciblée" des relances personnalisées
  // (ajouté 2026-07-29). Chaque template a un "ordre" qui correspond exactement au numero_seance
  // des vraies séances générées pour ce module (voir generateSessions).
  const [modules, setModules] = useState([]);
  const [moduleTemplates, setModuleTemplates] = useState([]);
  const emailBodyRef = useRef(null);
  const [confirmState, setConfirmState] = React.useState({ open: false, title: '', message: '', onConfirm: null });
  const showDeleteConfirm = (title, message, onConfirmFn) => setConfirmState({ open: true, title, message, onConfirm: onConfirmFn });
  const hideDeleteConfirm = () => setConfirmState(prev => ({ ...prev, open: false, onConfirm: null }));

  const fetchSettings = async () => {
    if (!currentOrgId) return;
    const { data } = await supabase
      .from('automation_settings')
      .select('*')
      .eq('organisation_id', currentOrgId)
      .order('created_at', { ascending: true });
    if (data) setSettings(data);
  };

  const fetchLogs = async () => {
    // automation_logs n'a pas sa propre colonne organisation_id — avant ce correctif, cette requête
    // n'appliquait AUCUN filtre et le "Journal des envois" affichait les emails de TOUS les organismes.
    // On passe par la relation automation_setting_id → automation_settings.organisation_id (jointure
    // PostgREST via !inner) pour ne garder que les envois de l'organisme courant.
    if (!currentOrgId) { setLogs([]); return; }
    // On récupère aussi trigger_type et email_subject de la relance d'origine (jointure), pour
    // afficher dans le journal un intitulé clair (ex: "Rappel avant séance") plutôt que le champ
    // technique reference_type (toujours "session").
    const { data, error } = await supabase
      .from('automation_logs')
      .select('*, automation_settings!inner(organisation_id, trigger_type, email_subject)')
      .eq('automation_settings.organisation_id', currentOrgId)
      .order('sent_at', { ascending: false })
      .limit(50);
    if (!error && data) setLogs(data);
  };

  const fetchModulesAndTemplates = async () => {
    if (!currentOrgId) return;
    const [{ data: mData }, { data: tData }] = await Promise.all([
      supabase.from('modules').select('id, nom').eq('organisation_id', currentOrgId).order('nom', { ascending: true }),
      supabase.from('module_session_templates').select('id, module_id, titre, ordre').eq('organisation_id', currentOrgId).order('ordre', { ascending: true }),
    ]);
    if (mData) setModules(mData);
    if (tData) setModuleTemplates(tData);
  };

  useEffect(() => {
    fetchSettings();
    fetchModulesAndTemplates();
  }, []);

  useEffect(() => {
    if (showLogs) fetchLogs();
  }, [showLogs]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setCustomTriggerName('');
    setIsAdding(true);
  };

  const openEdit = (s) => {
    const isCustom = !KNOWN_TRIGGER_TYPES.includes(s.trigger_type);
    setForm({
      trigger_type: isCustom ? '__custom__' : s.trigger_type,
      delay_days: s.delay_days,
      email_subject: s.email_subject,
      email_body: s.email_body,
      is_active: s.is_active,
      target_module_id: s.target_module_id != null ? String(s.target_module_id) : '',
      target_numero_seance: s.target_numero_seance != null ? String(s.target_numero_seance) : '',
      timing_direction: s.timing_direction || 'before',
      require_unsigned: !!s.require_unsigned,
    });
    setCustomTriggerName(isCustom ? s.trigger_type : '');
    setEditingId(s.id);
    setIsAdding(true);
  };

  const cancelForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCustomTriggerName('');
  };

  const saveForm = async () => {
    if (!form.email_subject.trim() || !form.email_body.trim()) {
      toast.error('Sujet et corps du mail sont requis.');
      return;
    }
    if (form.trigger_type === '__custom__' && !customTriggerName.trim()) {
      toast.error('Veuillez donner un nom au type personnalisé.');
      return;
    }
    setIsSaving(true);
    const isCustom = form.trigger_type === '__custom__';
    const resolvedTriggerType = isCustom
      ? customTriggerName.trim().toLowerCase().replace(/\s+/g, '_')
      : form.trigger_type;
    // Les champs target_module_id/target_numero_seance/timing_direction/require_unsigned ne
    // s'appliquent qu'aux types personnalisés — on les vide (null) pour les types prédéfinis, et on
    // convertit les chaînes vides du formulaire en null pour les colonnes numériques (ajouté 2026-07-29).
    const payload = {
      ...form,
      trigger_type: resolvedTriggerType,
      target_module_id: isCustom && form.target_module_id !== '' ? Number(form.target_module_id) : null,
      target_numero_seance: isCustom && form.target_numero_seance !== '' ? Number(form.target_numero_seance) : null,
      timing_direction: isCustom ? form.timing_direction : null,
      require_unsigned: isCustom ? !!form.require_unsigned : false,
      updated_at: new Date().toISOString(),
      organisation_id: currentOrgId,
    };
    if (editingId) {
      const { error } = await supabase
        .from('automation_settings')
        .update(payload)
        .eq('id', editingId);
      if (error) { toast.error('Erreur lors de la mise à jour.'); setIsSaving(false); return; }
      toast.success('Relance mise à jour.');
    } else {
      const { error } = await supabase.from('automation_settings').insert(payload);
      if (error) { toast.error('Erreur lors de la création.'); setIsSaving(false); return; }
      toast.success('Relance créée.');
    }
    setIsSaving(false);
    cancelForm();
    fetchSettings();
  };

  const toggleActive = async (s) => {
    const { error } = await supabase
      .from('automation_settings')
      .update({ is_active: !s.is_active, updated_at: new Date().toISOString() })
      .eq('id', s.id);
    if (error) { toast.error('Erreur de mise à jour.'); return; }
    fetchSettings();
  };

  const deleteSetting = (id) => {
    showDeleteConfirm(
      'Supprimer cette relance ?',
      'Cette relance automatique sera définitivement supprimée.',
      async () => {
        hideDeleteConfirm();
        const { error } = await supabase.from('automation_settings').delete().eq('id', id);
        if (error) { toast.error('Erreur lors de la suppression.'); return; }
        toast.success('Relance supprimée.');
        fetchSettings();
      }
    );
  };

  // Depuis ce correctif, "Tester maintenant" appelle une fonction serverless (api/automation/trigger-manual.js)
  // au lieu de tout faire dans le navigateur : avant, cette fonction lisait automation_settings/clients/sessions
  // SANS AUCUN filtre organisation_id (un admin pouvait déclencher l'envoi d'emails à des clients d'un AUTRE
  // organisme) et envoyait les emails via Resend avec une clé API exposée en clair dans le bundle JS
  // (REACT_APP_RESEND_API_KEY). La fonction serverless vérifie l'admin appelant via son token Supabase, ne
  // traite que SON organisme, et garde la clé Resend côté serveur uniquement.
  const triggerManual = async () => {
    setIsTesting(true);
    const toastId = 'automation-trigger';
    toast.loading('Analyse des relances en cours…', { id: toastId });
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error('Session expirée — reconnectez-vous.');

      const resp = await fetch('/api/automation/trigger-manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(result.error || `Erreur HTTP ${resp.status}`);

      if (result.simulated > 0) {
        toast('⚠️ Simulation : ' + result.simulated + ' email(s) prêt(s) — la clé Resend n\'est pas configurée côté serveur (variable RESEND_API_KEY sur Vercel).', { id: toastId, icon: '⚠️', duration: 8000 });
      } else if (result.sent > 0) {
        toast.success(`✅ ${result.sent} email(s) envoyé(s) avec succès !`, { id: toastId });
      } else {
        toast.success(result.message || 'Aucun email à envoyer.', { id: toastId });
      }
    } catch (e) {
      console.error('[triggerManual]', e);
      toast.error('Erreur : ' + e.message, { id: toastId });
    }
    setIsTesting(false);
    if (showLogs) fetchLogs();
  };

  const insertVar = (varName) => {
    const el = emailBodyRef.current;
    // Si le curseur est positionné dans le champ, on insère la balise à cet endroit précis
    // (plutôt que toujours à la fin) — plus pratique pour rédiger un email déjà en cours.
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      setForm(f => ({ ...f, email_body: f.email_body.slice(0, start) + varName + f.email_body.slice(end) }));
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        const pos = start + varName.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setForm(f => ({ ...f, email_body: f.email_body + varName }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Paramètres de Relance</h1>
            <p className="text-sm text-gray-500">Automatisations email pilotées par Vercel Cron (chaque jour à 8h)</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={triggerManual}
            disabled={isTesting}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            <Clock className="w-4 h-4" />
            {isTesting ? 'En cours…' : 'Tester maintenant'}
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-all shadow-sm"
          >
            <Plus className="w-4 h-4" /> Nouvelle relance
          </button>
        </div>
      </div>

      {/* Formulaire Ajout / Édition */}
      {isAdding && (
        <div className="flex flex-col lg:flex-row gap-4 items-start">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-violet-100 flex-1 min-w-0 w-full">
          <h2 className="text-base font-bold text-gray-800 mb-4">
            {editingId ? 'Modifier la relance' : 'Créer une relance'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Type de déclencheur</label>
              <select
                value={form.trigger_type}
                onChange={e => setForm(f => ({ ...f, trigger_type: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
              >
                <option value="no_signature">Relance émargement non signé</option>
                <option value="reminder_before_session">Rappel avant séance</option>
                <option value="welcome">Email de bienvenue (nouveau client)</option>
                <option value="__custom__">+ Type personnalisé…</option>
              </select>
              {form.trigger_type === '__custom__' && (
                <input
                  type="text"
                  placeholder="Nom du type (ex : newsletter_mensuelle)"
                  value={customTriggerName}
                  onChange={e => setCustomTriggerName(e.target.value)}
                  className="mt-2 w-full border border-violet-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                />
              )}
            </div>
            {form.trigger_type !== '__custom__' && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  {form.trigger_type === 'no_signature'
                    ? 'Délai après la séance (jours)'
                    : form.trigger_type === 'reminder_before_session'
                    ? 'Jours avant la séance'
                    : 'Jours après la création du compte'}
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.delay_days}
                  onChange={e => setForm(f => ({ ...f, delay_days: parseInt(e.target.value) || 1 }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
            )}
          </div>

          {form.trigger_type === '__custom__' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 rounded-xl bg-violet-50/50 border border-violet-100">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Module ciblé</label>
                <select
                  value={form.target_module_id}
                  onChange={e => setForm(f => ({ ...f, target_module_id: e.target.value, target_numero_seance: '' }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                >
                  <option value="">Tous les modules</option>
                  {modules.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Séance ciblée</label>
                {form.target_module_id ? (
                  <select
                    value={form.target_numero_seance}
                    onChange={e => setForm(f => ({ ...f, target_numero_seance: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                  >
                    <option value="">Toutes les séances du module</option>
                    {moduleTemplates
                      .filter(t => String(t.module_id) === String(form.target_module_id))
                      .map(t => <option key={t.id} value={t.ordre}>{`Séance ${t.ordre} - ${t.titre}`}</option>)}
                  </select>
                ) : (
                  <input
                    type="number"
                    min={1}
                    placeholder="Toutes (laisser vide) ou un numéro précis"
                    value={form.target_numero_seance}
                    onChange={e => setForm(f => ({ ...f, target_numero_seance: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Moment de l'envoi</label>
                <select
                  value={form.timing_direction}
                  onChange={e => setForm(f => ({ ...f, timing_direction: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                >
                  <option value="before">Avant la séance</option>
                  <option value="after">Après la séance</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Nombre de jours {form.timing_direction === 'after' ? 'après' : 'avant'}
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.delay_days}
                  onChange={e => setForm(f => ({ ...f, delay_days: parseInt(e.target.value) || 1 }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.require_unsigned}
                    onChange={e => setForm(f => ({ ...f, require_unsigned: e.target.checked }))}
                    className="rounded border-gray-300 text-violet-600 focus:ring-violet-400"
                  />
                  <span className="text-xs font-medium text-gray-700">Envoyer seulement si la séance n'est pas encore émargée/signée</span>
                </label>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Objet du mail</label>
            <input
              type="text"
              placeholder="Ex : Rappel — votre émargement est en attente"
              value={form.email_subject}
              onChange={e => setForm(f => ({ ...f, email_subject: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-500">Corps du mail</label>
              <button
                type="button"
                onClick={() => setShowTagHelp(s => !s)}
                className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                {showTagHelp ? 'Masquer les balises' : 'Voir les balises disponibles'}
              </button>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(TRIGGER_VARS[form.trigger_type] || CUSTOM_TAGS).map(v => (
                <button
                  key={v}
                  onClick={() => insertVar(v)}
                  title={TAG_DEFINITIONS[v]?.description || ''}
                  className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 font-mono"
                >
                  {v}
                </button>
              ))}
            </div>
            <textarea
              ref={emailBodyRef}
              rows={6}
              placeholder="Bonjour {{client_name}},&#10;&#10;Nous n'avons pas encore reçu votre émargement pour {{session_title}} du {{session_date}}."
              value={form.email_body}
              onChange={e => setForm(f => ({ ...f, email_body: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-300 font-mono resize-y"
            />
          </div>
          <div className="flex items-center justify-between mt-4">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <span className="text-sm font-medium text-gray-700">Activer immédiatement</span>
              <button onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))} className="focus:outline-none">
                {form.is_active
                  ? <ToggleRight className="w-6 h-6 text-violet-600" />
                  : <ToggleLeft className="w-6 h-6 text-gray-400" />}
              </button>
            </label>
            <div className="flex gap-2">
              <button onClick={cancelForm} className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Annuler
              </button>
              <button
                onClick={saveForm}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition-all"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>

        {showTagHelp && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 w-full lg:w-80 shrink-0 lg:sticky lg:top-4 overflow-hidden">
            <div className="flex items-center gap-2 mb-1">
              <HelpCircle className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-bold text-gray-800">Balises disponibles</h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Cliquez sur une balise pour l'insérer dans le corps du mail à l'endroit du curseur.
            </p>
            <div className="space-y-2">
              {(TRIGGER_VARS[form.trigger_type] || CUSTOM_TAGS).map(v => {
                const def = TAG_DEFINITIONS[v] || {};
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => insertVar(v)}
                    className="w-full text-left px-3 py-2 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50 transition-colors overflow-hidden"
                  >
                    <span className="text-xs font-mono font-semibold text-indigo-600 break-all">{v}</span>
                    {def.example && (
                      <div className="text-xs text-gray-400 italic mt-0.5 break-words">ex : {def.example}</div>
                    )}
                    {def.label && <div className="text-xs font-medium text-gray-700 mt-1 break-words">{def.label}</div>}
                    {def.description && <div className="text-[11px] text-gray-500 mt-0.5 break-words">{def.description}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </div>
      )}

      {/* Liste des relances */}
      <div className="space-y-3">
        {settings.length === 0 && !isAdding && (
          <div className="bg-white rounded-2xl p-10 text-center border border-dashed border-gray-200">
            <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Aucune relance configurée. Cliquez sur "Nouvelle relance" pour commencer.</p>
          </div>
        )}
        {settings.map(s => (
          <div key={s.id} className={`bg-white rounded-2xl p-5 shadow-sm border transition-all ${s.is_active ? 'border-gray-100' : 'border-dashed border-gray-200 opacity-60'}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.is_active ? 'bg-violet-50' : 'bg-gray-100'}`}>
                  <Mail className={`w-4 h-4 ${s.is_active ? 'text-violet-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{getTriggerLabel(s.trigger_type)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {s.trigger_type === 'no_signature'
                      ? `Si non signé après ${s.delay_days} jour${s.delay_days > 1 ? 's' : ''}`
                      : s.trigger_type === 'reminder_before_session'
                      ? `${s.delay_days} jour${s.delay_days > 1 ? 's' : ''} avant la séance`
                      : s.trigger_type === 'welcome'
                      ? `Envoyé ${s.delay_days} jour${s.delay_days > 1 ? 's' : ''} après la création du compte`
                      : (() => {
                          // Relance personnalisée : décrire précisément la règle configurée
                          // (module / séance / avant-après / jours / condition signature) — ajouté 2026-07-29.
                          const moduleLabel = s.target_module_id
                            ? (modules.find(m => String(m.id) === String(s.target_module_id))?.nom || 'module inconnu')
                            : 'tous modules';
                          const seanceLabel = (s.target_numero_seance !== null && s.target_numero_seance !== undefined)
                            ? `séance ${s.target_numero_seance}`
                            : 'toutes séances';
                          const dirLabel = s.timing_direction === 'after' ? 'après' : 'avant';
                          const unsignedLabel = s.require_unsigned ? ' · seulement si non signé' : '';
                          return `${moduleLabel} · ${seanceLabel} · ${s.delay_days} jour${s.delay_days > 1 ? 's' : ''} ${dirLabel}${unsignedLabel}`;
                        })()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleActive(s)}
                  title={s.is_active ? 'Désactiver' : 'Activer'}
                  className="focus:outline-none"
                >
                  {s.is_active
                    ? <ToggleRight className="w-6 h-6 text-violet-600 hover:text-violet-700" />
                    : <ToggleLeft className="w-6 h-6 text-gray-300 hover:text-gray-500" />}
                </button>
                <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-all">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => deleteSetting(s.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 pl-12">
              <p className="text-xs text-gray-500 font-medium">Objet : <span className="font-normal text-gray-700">{s.email_subject || <em>—</em>}</span></p>
              <p className="text-xs text-gray-400 mt-1 line-clamp-2 font-mono whitespace-pre-wrap">{s.email_body}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Journal des envois */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <button
          onClick={() => setShowLogs(v => !v)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-all"
        >
          <div className="flex items-center gap-2 font-semibold text-gray-700 text-sm">
            <History className="w-4 h-4 text-gray-400" />
            Journal des envois (50 derniers)
          </div>
          {showLogs ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {showLogs && (
          <div className="border-t border-gray-100 overflow-x-auto">
            {logs.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Aucun envoi enregistré.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Date d'envoi</th>
                    <th className="text-left px-4 py-2 font-semibold">Email destinataire</th>
                    <th className="text-left px-4 py-2 font-semibold">Relance</th>
                    <th className="text-left px-4 py-2 font-semibold">Objet du mail</th>
                    <th className="text-left px-4 py-2 font-semibold">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => {
                    const statusInfo = getLogStatusInfo(log.status);
                    return (
                      <tr key={log.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                        <td className="px-4 py-2 text-gray-600">
                          {new Date(log.sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className="px-4 py-2 text-gray-800 font-medium">{log.client_email || '—'}</td>
                        <td className="px-4 py-2 text-gray-700">
                          {getTriggerLabel(log.automation_settings?.trigger_type) || '—'}
                        </td>
                        <td className="px-4 py-2 text-gray-500">{log.automation_settings?.email_subject || '—'}</td>
                        <td className="px-4 py-2">
                          <span className={`border px-2 py-0.5 rounded-md font-medium ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={hideDeleteConfirm}
      />
    </div>
  );
}

// ─── TrialBanner (sidebar) ──────────────────────────────────────────────────
function TrialBanner({ orgSettings, onUpgrade }) {
  const trialEndsAt = orgSettings?.trial_ends_at ? new Date(orgSettings.trial_ends_at) : null;
  const daysLeft = trialEndsAt ? Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const expired = daysLeft !== null && daysLeft <= 0;
  return (
    <div
      onClick={onUpgrade}
      className="cursor-pointer mx-1 mb-3 rounded-xl p-3 transition-all hover:opacity-80"
      style={{
        background: expired ? 'rgba(220,38,38,0.15)' : 'rgba(124,58,237,0.15)',
        border: `1px solid ${expired ? 'rgba(220,38,38,0.3)' : 'rgba(124,58,237,0.3)'}`,
      }}
    >
      <p className="text-xs font-bold" style={{ color: expired ? '#F87171' : '#A78BFA' }}>
        {expired ? '⚠️ Essai expiré' : `🎁 ${daysLeft}j d'essai restant${daysLeft !== 1 ? 's' : ''}`}
      </p>
      <p className="text-xs text-slate-400 mt-0.5">
        {expired ? 'Cliquez pour souscrire' : 'Cliquez pour choisir un plan'}
      </p>
    </div>
  );
}

// ─── PaywallScreen ───────────────────────────────────────────────────────────
function PaywallScreen({ onSubscribe, isAdmin = true, status = null }) {
  const paiementEnAttente = ['past_due', 'unpaid', 'paused'].includes(status);
  // Seul l'admin de l'organisme peut gérer l'abonnement : un formateur ou un client dont
  // l'organisme a un abonnement expiré/impayé voit un message d'attente, pas le bouton de paiement
  // (parametres_org, où mène onSubscribe, ne lui est de toute façon pas accessible).
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center px-6">
        <div className="w-20 h-20 rounded-2xl bg-violet-100 flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Accès temporairement suspendu</h2>
        <p className="text-gray-500 mb-8 max-w-md">
          L'abonnement de votre organisme n'est plus actif. Merci de contacter votre administrateur
          pour qu'il renouvelle l'abonnement — l'accès sera rétabli automatiquement dès que ce sera fait.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center px-6">
      <div className="w-20 h-20 rounded-2xl bg-violet-100 flex items-center justify-center mb-6">
        <svg className="w-10 h-10 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-3">
        {paiementEnAttente ? 'Un paiement est en attente' : status === 'canceled' ? 'Votre abonnement est arrêté' : "Votre période d'essai est terminée"}
      </h2>
      <p className="text-gray-500 mb-8 max-w-md">
        {paiementEnAttente
          ? "Le dernier paiement de votre abonnement n'a pas abouti. Mettez à jour votre moyen de paiement depuis « Mon abonnement » : l'accès sera rétabli automatiquement."
          : "Merci d'avoir essayé SkorUp ! Pour continuer à utiliser la plateforme, veuillez souscrire à l'un de nos plans."}
      </p>
      <button
        onClick={onSubscribe}
        className="bg-violet-600 hover:bg-violet-700 text-white font-bold py-4 px-10 rounded-2xl shadow-lg transition-all text-lg"
      >
        {paiementEnAttente ? 'Régulariser mon abonnement →' : 'Choisir mon plan →'}
      </button>
      <p className="text-xs text-gray-400 mt-4">
        Accès immédiat après paiement — Sans engagement pour le plan mensuel
      </p>
    </div>
  );
}

// ─── AbonnementView ──────────────────────────────────────────────────────────
function AbonnementView({ orgId, orgSettings, onRefresh }) {
  const [isLoading, setIsLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billing, setBilling] = useState('monthly');

  const status        = orgSettings?.subscription_status || 'trialing';
  const trialEndsAt   = orgSettings?.trial_ends_at ? new Date(orgSettings.trial_ends_at) : null;
  const daysLeft      = trialEndsAt ? Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const subscribedPlan = orgSettings?.subscribed_plan;
  const isActive      = status === 'active';

  const PLAN_LABELS = {
    essentiel_monthly: 'Essentiel mensuel',
    essentiel_annual:  'Essentiel annuel',
    pro_monthly:       'Pro mensuel',
    pro_annual:        'Pro annuel',
    illimite_monthly:  'Illimité mensuel',
    illimite_annual:   'Illimité annuel',
  };
  const planLabel = PLAN_LABELS[subscribedPlan] || subscribedPlan || '';

  const PLANS = [
    {
      key: 'essentiel', name: 'Essentiel', monthlyPrice: 29, annualPrice: 26,
      limit: '50 dossiers clients',
      features: ['Gestion clients & séances', 'Documents & signatures', 'Messagerie interne', 'Support email'],
    },
    {
      key: 'pro', name: 'Pro', monthlyPrice: 49, annualPrice: 44,
      limit: '200 dossiers clients', popular: true,
      features: ['Tout Essentiel +', 'Relances automatiques', 'Personnalisation marque', 'Support prioritaire'],
    },
    {
      key: 'illimite', name: 'Illimité', monthlyPrice: 89, annualPrice: 80,
      limit: 'Dossiers illimités',
      features: ['Tout Pro +', 'Clients illimités', 'Onboarding dédié', 'Support téléphonique'],
    },
  ];

  const handleSubscribe = async (planKey) => {
    setIsLoading(planKey);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ orgId, plan: planKey, billing }),
      });
      const { url, error } = await res.json();
      if (error) throw new Error(error);
      if (url) window.open(url, '_blank');
    } catch (err) {
      toast.error('Erreur : ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/customer-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ orgId }),
      });
      const { url, error } = await res.json();
      if (error) throw new Error(error);
      if (url) window.open(url, '_blank');
    } catch (err) {
      toast.error('Erreur : ' + err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const statusInfo = {
    trialing: { label: `Essai gratuit${daysLeft !== null ? ` — ${Math.max(0, daysLeft)}j restant${daysLeft !== 1 ? 's' : ''}` : ''}`, color: 'bg-violet-50 text-violet-700 border-violet-200' },
    active:   { label: `Abonné — ${planLabel}`, color: 'bg-green-50 text-green-700 border-green-200' },
    canceled: { label: 'Abonnement annulé', color: 'bg-red-50 text-red-700 border-red-200' },
    past_due: { label: 'Paiement en retard', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    unpaid:   { label: 'Paiement en retard', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    paused:   { label: 'Abonnement suspendu', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    incomplete: { label: 'Paiement en cours de validation', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  }[status] || { label: status, color: 'bg-gray-50 text-gray-700 border-gray-200' };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Mon Abonnement</h1>
        <p className="text-gray-500">Gérez votre formule SkorUp et vos informations de paiement.</p>
      </div>

      {/* Statut actuel */}
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold mb-8 ${statusInfo.color}`}>
        <span className="w-2 h-2 rounded-full bg-current opacity-70 inline-block"></span>
        {statusInfo.label}
      </div>

      {/* Si abonnement actif → portail Stripe. 2026-10-19 : aussi en cas de retard de paiement, pour
          mettre à jour la carte au lieu de souscrire un second abonnement. */}
      {(isActive || (orgSettings?.stripe_customer_id && ['past_due', 'unpaid', 'paused', 'incomplete'].includes(status))) && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Gérer votre abonnement</h2>
          <p className="text-sm text-gray-500 mb-4">
            Modifiez votre plan, téléchargez vos factures ou annulez votre abonnement
            depuis le portail de paiement sécurisé Stripe.
          </p>
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            className="bg-gray-900 hover:bg-black text-white font-bold py-3 px-6 rounded-xl transition-all flex items-center gap-2 disabled:opacity-50"
          >
            {portalLoading ? 'Redirection...' : '→ Accéder au portail de paiement'}
          </button>
        </div>
      )}

      {/* Plans (si pas actif) */}
      {!isActive && (
        <>
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-xl p-1 flex gap-1">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${billing === 'monthly' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >Mensuel</button>
              <button
                onClick={() => setBilling('annual')}
                className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${billing === 'annual' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >Annuel <span className="text-green-600 font-bold text-xs">-10%</span></button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {PLANS.map(plan => (
              <div key={plan.key} className={`bg-white rounded-2xl border-2 shadow-sm p-6 flex flex-col relative ${plan.popular ? 'border-violet-500' : 'border-gray-100'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full">Populaire</div>
                )}
                <h3 className="font-extrabold text-gray-900 text-lg mb-1">{plan.name}</h3>
                <p className="text-xs text-gray-400 mb-4">{plan.limit}</p>
                <div className="mb-4">
                  <span className="text-4xl font-black text-gray-900">
                    {billing === 'monthly' ? plan.monthlyPrice : plan.annualPrice}€
                  </span>
                  <span className="text-gray-400 text-sm">/mois</span>
                  {billing === 'annual' && (
                    <p className="text-xs text-green-600 font-semibold mt-1">
                      Facturé {plan.annualPrice * 12}€/an
                    </p>
                  )}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSubscribe(plan.key)}
                  disabled={isLoading === plan.key}
                  className={`w-full py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-50 ${plan.popular ? 'bg-violet-600 hover:bg-violet-700 text-white' : 'bg-gray-900 hover:bg-black text-white'}`}
                >
                  {isLoading === plan.key ? 'Redirection...' : 'Choisir ce plan →'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="text-xs text-gray-400 text-center">
        Paiement sécurisé par Stripe — Annulation possible à tout moment
      </p>
    </div>
  );
}

// ─── Navigation par URL (2026-07-27) ──────────────────────────────────────────
// Fait correspondre chaque valeur du `activeTab` DE App() (état interne existant, complètement
// inchangé — aucun des ~50 appels setActiveTab() n'est modifié) à une URL propre et unique. Objectif :
// bouton précédent/suivant du navigateur qui fonctionne réellement, liens partageables/à rafraîchir
// sans perdre sa place, un vrai historique. Implémenté avec l'API History native (pushState/popstate),
// sans nouvelle dépendance (pas de react-router) : le rewrite catch-all déjà présent dans vercel.json
// ("/(.*)" → "/index.html") garantit qu'une actualisation ou un lien direct sur n'importe laquelle de
// ces URLs sert bien l'app React (pas de 404), donc aucun changement de config serveur nécessaire.
// N'INCLUT PAS les tabs internes à ClientDetailView (infos/seances/docs_signes/exercices/docs/
// questionnaires) : ce sont des sous-onglets d'une fiche client, avec leur PROPRE état local
// `activeTab` (composant différent) — hors-scope ici, uniquement la navigation principale de l'app.
const TAB_TO_PATH = {
  dashboard: '/tableau-de-bord',
  accueil: '/accueil',
  accueil_formateur: '/espace-formateur',
  clients: '/clients',
  groupes: '/groupes',
  formateurs: '/formateurs',
  modules: '/modules',
  processus: '/processus',
  fiches_metiers: '/fiches-metiers',
  calendrier: '/calendrier',
  messagerie: '/messagerie',
  gestion_documents: '/documents',
  questionnaires: '/questionnaires',
  mes_documents: '/mes-documents',
  mes_seances: '/mes-seances',
  parametres_org: '/parametres',
  relances: '/relances',
  profil: '/profil',
  exercices: '/exercices',
  prospects: '/prospects',
  bpf: '/bpf',
  veille: '/veille',
};
// Réciproque URL → onglet — 1:1, sans collision (chaque chemin ci-dessus n'a qu'un seul propriétaire).
const PATH_TO_TAB = {};
Object.entries(TAB_TO_PATH).forEach(([tab, path]) => { PATH_TO_TAB[path] = tab; });

// Plafonds de dossiers clients par plan (décision produit du 2026-07-28 — auparavant
// affiché sur la page tarifs mais jamais réellement appliqué nulle part dans le code).
const CLIENT_LIMIT_BY_PLAN = { essentiel: 50, pro: 200, illimite: Infinity };

// Donne le plafond de dossiers clients applicable à l'organisme, selon son abonnement.
// Pendant la période d'essai gratuit, on applique par défaut le plafond du plan le moins
// cher (Essentiel) plutôt que de laisser un essai illimité, pour éviter les abus.
const getClientLimitForOrg = (orgSettings) => {
  if (!orgSettings) return CLIENT_LIMIT_BY_PLAN.essentiel;
  if (orgSettings.subscription_status === 'trialing') return CLIENT_LIMIT_BY_PLAN.essentiel;
  const planKey = (orgSettings.subscribed_plan || '').split('_')[0]; // 'pro_annual' → 'pro'
  return CLIENT_LIMIT_BY_PLAN[planKey] ?? CLIENT_LIMIT_BY_PLAN.essentiel;
};

// Verrouillage des fonctionnalités "Pro" (décision produit du 2026-07-28) : Relances Auto et
// Personnalisation de marque sont désormais réservées aux plans Pro et Illimité, pour que la
// page tarifs corresponde enfin à la réalité du logiciel. Pendant l'essai gratuit, on applique
// les restrictions du plan Essentiel (cohérent avec le plafond de dossiers clients déjà appliqué
// pendant l'essai) plutôt que de donner accès à tout.
const PLAN_TIER_RANK = { essentiel: 0, pro: 1, illimite: 2 };
const getPlanTier = (orgSettings) => {
  if (!orgSettings) return 'essentiel';
  if (orgSettings.subscription_status === 'trialing') return 'essentiel';
  // SÉCURITÉ (2026-10-19) : un abonnement impayé / suspendu (past_due, unpaid, paused,
  // incomplete…) ne donne plus accès aux fonctionnalités de l'offre souscrite.
  if (orgSettings.subscription_status && orgSettings.subscription_status !== 'active') return 'essentiel';
  const planKey = (orgSettings.subscribed_plan || '').split('_')[0];
  return PLAN_TIER_RANK[planKey] !== undefined ? planKey : 'essentiel';
};
const hasFeatureAccess = (orgSettings, minTier) => PLAN_TIER_RANK[getPlanTier(orgSettings)] >= PLAN_TIER_RANK[minTier];

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// GROUPES (2026-10-13) — formations collectives.
// Un groupe réunit plusieurs clients autour d'un même module et d'un planning commun. Chaque
// participant garde son propre dossier (séances, documents, signatures, n° de dossier) : le groupe
// sert à tout faire EN UNE FOIS (création des dossiers, dates, horaires, lien visio, formateur).
// Tables : groupes, groupe_seances, clients.groupe_id (voir groupes_etape1.sql).
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const RE_EMAIL_GROUPE = /[A-Z0-9._%+'-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)*\.[A-Z]{2,}/i;
const nomDepuisEmail = (email) => String(email || '').split('@')[0].split(/[._-]+/).filter(Boolean)
  .map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
// Lit une liste collée (Excel, e-mail, une personne par ligne) : nom, e-mail, et un éventuel
// n° de dossier (le premier morceau qui contient un chiffre).
const analyserListeParticipants = (texte, { emailFacultatif = false } = {}) => {
  const vus = new Set();
  return String(texte || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean).map((ligne) => {
    const m = ligne.match(RE_EMAIL_GROUPE);
    if (!m) {
      // 30/10/2026 : groupe sans compte → une ligne peut ne contenir que le nom (et un n° de dossier).
      if (!emailFacultatif) return { ligne, erreur: "Pas d'adresse e-mail sur cette ligne" };
      const parts = ligne.split(/[;\t|,]+/).map(x => x.trim().replace(/^["'<(]+|[">)']+$/g, '').trim()).filter(Boolean);
      const noms = [];
      let dos = '';
      parts.forEach(p => { if (/\d/.test(p)) { if (!dos) dos = p; } else noms.push(p); });
      const nomSeul = noms.join(' ').replace(/\s+/g, ' ').trim();
      if (!nomSeul) return { ligne, erreur: 'Nom manquant sur cette ligne' };
      const cle = `nom:${nomSeul.toLowerCase()}`;
      if (vus.has(cle)) return { ligne, email: '', nom: nomSeul, erreur: 'En double dans la liste' };
      vus.add(cle);
      return { ligne, email: '', nom: nomSeul, numero_dossier: dos };
    }
    const email = m[0].toLowerCase();
    const reste = `${ligne.slice(0, m.index)}\t${ligne.slice(m.index + m[0].length)}`;
    const morceaux = reste.split(/[;\t|,]+/).map(x => x.trim().replace(/^["'<(]+|[">)']+$/g, '').trim()).filter(Boolean);
    const nomParts = [];
    let dossier = '';
    morceaux.forEach(p => { if (/\d/.test(p)) { if (!dossier) dossier = p; } else nomParts.push(p); });
    const nom = nomParts.join(' ').replace(/\s+/g, ' ').trim() || nomDepuisEmail(email);
    if (vus.has(email)) return { ligne, email, nom, erreur: 'En double dans la liste' };
    vus.add(email);
    return { ligne, email, nom, numero_dossier: dossier };
  });
};
const aujourdhuiISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const heureCourte = (h) => (h ? String(h).slice(0, 5) : '');
const dateCourte = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const lireMeta = (m) => (typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {}));
const participantsDuGroupe = (clients, groupeId) => (clients || []).filter(c => c.groupe_id && String(c.groupe_id) === String(groupeId));
const seancesDuGroupe = (groupeSeances, groupeId) => (groupeSeances || [])
  .filter(s => String(s.groupe_id) === String(groupeId))
  .sort((a, b) => Number(a.numero) - Number(b.numero));
// Émargements d'une séance du groupe : combien de participants ont signé leur présence.
const statsEmargementSeance = (seance, participants, sessions) => {
  let signes = 0, attendus = 0;
  (participants || []).forEach(p => {
    const lignes = (sessions || []).filter(s => String(s.client_id) === String(p.id)
      && Number(s.numero_seance) === Number(seance.numero) && s.type_activite === 'signature'
      && lireMeta(s.metadata).requiresClientSignature !== false);
    if (lignes.length === 0) return;
    attendus++;
    if (lignes.every(s => s.statut_client === 'Signé')) signes++;
  });
  return { signes, attendus };
};
// Analyse les participants saisis (liste collée + clients existants choisis) avant création.
const analyserParticipantsGroupe = ({ texte, choisis, clients, groupes, moduleId, groupeId, sansCompte = false }) => {
  const res = [];
  const emailsVus = new Set();
  const verifierExistant = (c) => {
    if (c.groupe_id && String(c.groupe_id) !== String(groupeId || '')) {
      const g = (groupes || []).find(x => String(x.id) === String(c.groupe_id));
      return `Fait déjà partie du groupe « ${g?.nom || 'autre groupe'} »`;
    }
    if (groupeId && c.groupe_id && String(c.groupe_id) === String(groupeId)) return 'Fait déjà partie de ce groupe';
    if (moduleId && c.module_id && String(c.module_id) !== String(moduleId)) return 'Suit déjà un autre module : changez-le dans sa fiche avant de l\'ajouter';
    return null;
  };
  analyserListeParticipants(texte, { emailFacultatif: sansCompte }).forEach(l => {
    if (l.erreur) { res.push({ type: 'erreur', nom: l.nom || l.ligne, email: l.email || '', erreur: l.erreur }); return; }
    if (l.email) emailsVus.add(l.email);
    const existant = l.email ? (clients || []).find(c => (c.email_contact || c.email || '').toLowerCase() === l.email) : null;
    if (existant) {
      const err = verifierExistant(existant);
      res.push({ type: err ? 'erreur' : 'existant', clientId: existant.id, nom: existant.nom_complet || existant.nom, email: l.email, numero_dossier: l.numero_dossier, erreur: err });
    } else {
      res.push({ type: 'nouveau', nom: l.nom, email: l.email, numero_dossier: l.numero_dossier });
    }
  });
  (choisis || []).forEach(id => {
    const c = (clients || []).find(x => String(x.id) === String(id));
    if (!c) return;
    const email = (c.email_contact || c.email || '').toLowerCase();
    if (email && emailsVus.has(email)) return;
    emailsVus.add(email);
    const err = verifierExistant(c);
    res.push({ type: err ? 'erreur' : 'existant', clientId: c.id, nom: c.nom_complet || c.nom, email, erreur: err });
  });
  return res;
};

const champGroupe = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-500';
const libelleGroupe = 'block text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5';

// Saisie des participants : liste collée + choix parmi les clients existants + aperçu.
const SaisieParticipantsGroupe = ({ texte, setTexte, choisis, setChoisis, clients, analyse, groupeId, sansCompte = false }) => {
  const [aAjouter, setAAjouter] = React.useState('');
  const disponibles = (clients || [])
    .filter(c => !c.groupe_id && !(choisis || []).some(id => String(id) === String(c.id)))
    .filter(c => !groupeId || String(c.groupe_id || '') !== String(groupeId));
  const nb = (t) => analyse.filter(a => a.type === t).length;
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-3 min-w-0">
        <label htmlFor="grp-liste" className={libelleGroupe}>Collez votre liste (une personne par ligne)</label>
        <textarea id="grp-liste" value={texte} onChange={e => setTexte(e.target.value)} rows={9}
          placeholder={sansCompte ? 'Sophie Laurent\nKarim Benali ; 061/2026/1183\nÉlodie Caradec ; e.caradec@mail.fr\n…' : 'Sophie Laurent ; sophie.laurent@mail.fr\nKarim Benali ; k.benali@mail.fr ; 061/2026/1183\n…'}
          className={`${champGroupe} font-mono text-[13px] leading-6 resize-y`} />
        <p className="text-xs text-gray-500">{sansCompte ? 'Un nom par ligne ; l\'e-mail et le n° de dossier sont facultatifs (séparés par un point-virgule, une virgule ou une tabulation).' : 'Nom et e-mail, séparés par un point-virgule, une virgule ou une tabulation (copie depuis Excel). Vous pouvez ajouter le n° de dossier à la suite.'}</p>
        <div>
          <label htmlFor="grp-existant" className={libelleGroupe}>Ou ajoutez un client qui a déjà un dossier</label>
          <div className="flex gap-2">
            <select id="grp-existant" value={aAjouter} onChange={e => setAAjouter(e.target.value)} className={champGroupe}>
              <option value="">Choisir un client…</option>
              {disponibles.map(c => <option key={c.id} value={c.id}>{c.nom_complet || c.nom}{c.email_contact ? ` (${c.email_contact})` : ''}</option>)}
            </select>
            <button type="button" disabled={!aAjouter} onClick={() => { setChoisis([...(choisis || []), aAjouter]); setAAjouter(''); }}
              className="px-4 rounded-xl bg-violet-50 text-violet-700 font-bold text-sm disabled:opacity-40 hover:bg-violet-100 shrink-0">Ajouter</button>
          </div>
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className={libelleGroupe + ' mb-0'}>Aperçu</p>
          <p className="text-xs text-gray-500">{nb('nouveau')} nouveau(x) · {nb('existant')} déjà client(s){nb('erreur') ? ` · ${nb('erreur')} à corriger` : ''}</p>
        </div>
        {analyse.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">Les participants reconnus apparaîtront ici.</div>
        ) : (
          <ul className="rounded-2xl border border-gray-100 divide-y divide-gray-50 max-h-80 overflow-y-auto bg-white">
            {analyse.map((a, i) => (
              <li key={i} className="px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-sm text-gray-900 truncate">{a.nom}</p>
                  <p className="text-xs text-gray-500 truncate">{a.email || (a.type === 'nouveau' ? 'Sans e-mail' : '')}{a.numero_dossier ? ` · Dossier ${a.numero_dossier}` : ''}</p>
                  {a.erreur && <p className="text-xs text-red-600 mt-0.5">{a.erreur}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.type === 'nouveau' && <span className="text-[11px] font-black px-2 py-1 rounded-lg bg-violet-50 text-violet-700">Nouveau dossier</span>}
                  {a.type === 'existant' && <span className="text-[11px] font-black px-2 py-1 rounded-lg bg-gray-100 text-gray-600">Déjà client</span>}
                  {a.type === 'erreur' && <span className="text-[11px] font-black px-2 py-1 rounded-lg bg-red-50 text-red-600">Non ajouté</span>}
                  {a.clientId && (choisis || []).some(id => String(id) === String(a.clientId)) && (
                    <button type="button" title="Retirer de la liste" onClick={() => setChoisis(choisis.filter(id => String(id) !== String(a.clientId)))}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"><X size={14} /></button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Écran d'attente / rapport pendant la création ou l'ajout de participants.
const RapportGroupe = ({ progression, rapport, onFermer, libelleFermer = 'Continuer' }) => (
  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 max-w-2xl">
    {!rapport ? (
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 rounded-full border-2 border-violet-200 border-t-violet-600 animate-spin" />
        <div>
          <p className="font-bold text-gray-900">Création en cours, ne fermez pas la page…</p>
          <p className="text-sm text-gray-500 mt-0.5">{progression}</p>
        </div>
      </div>
    ) : (
      <div className="space-y-4">
        <p className="text-lg font-black text-gray-900">{rapport.titre}</p>
        <ul className="text-sm text-gray-700 space-y-1">
          {rapport.invites > 0 && <li>✓ {rapport.invites} invitation{rapport.invites > 1 ? 's' : ''} envoyée{rapport.invites > 1 ? 's' : ''} par e-mail</li>}
          {rapport.sansCompte > 0 && <li>✓ {rapport.sansCompte} dossier{rapport.sansCompte > 1 ? 's' : ''} créé{rapport.sansCompte > 1 ? 's' : ''} sans compte (aucun e-mail envoyé)</li>}
          {rapport.ajoutes > 0 && <li>✓ {rapport.ajoutes} client{rapport.ajoutes > 1 ? 's' : ''} existant{rapport.ajoutes > 1 ? 's' : ''} ajouté{rapport.ajoutes > 1 ? 's' : ''} au groupe</li>}
          {rapport.seances > 0 && <li>✓ Dates et horaires reportés sur {rapport.seances} séance{rapport.seances > 1 ? 's' : ''} de chaque participant</li>}
        </ul>
        {rapport.erreurs.length > 0 && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4">
            <p className="font-bold text-red-700 text-sm mb-2">{rapport.erreurs.length} participant{rapport.erreurs.length > 1 ? 's n\'ont' : ' n\'a'} pas pu être ajouté{rapport.erreurs.length > 1 ? 's' : ''} :</p>
            <ul className="text-sm text-red-700 space-y-1">
              {rapport.erreurs.map((e, i) => <li key={i}><b>{e.nom}</b>{e.email ? ` (${e.email})` : ''} : {humanizeErrorMessage(e.message)}</li>)}
            </ul>
            <p className="text-xs text-red-600 mt-2">Vous pourrez les ajouter plus tard depuis l'onglet « Participants » du groupe.</p>
          </div>
        )}
        <button onClick={onFermer} className="px-5 py-2.5 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700">{libelleFermer}</button>
      </div>
    )}
  </div>
);

// ─── Création d'un groupe en 3 étapes ─────────────────────────────────────────────────────────────
const GroupeCreation = ({ clients, groupes, formateurs, modules, moduleSessionTemplates, orgSettings, actions, onAnnuler, onCree }) => {
  const [etape, setEtape] = React.useState(1);
  const [infos, setInfos] = React.useState({ nom: '', module_id: '', formateur_id: '', lieu: '', lien_visio: '' });
  const [seances, setSeances] = React.useState([]);
  const [seancesPour, setSeancesPour] = React.useState(null);
  const [heuresTous, setHeuresTous] = React.useState({ debut: '', fin: '' });
  const [texte, setTexte] = React.useState('');
  const [choisis, setChoisis] = React.useState([]);
  const [sansCompte, setSansCompte] = React.useState(false);
  const [progression, setProgression] = React.useState('');
  const [rapport, setRapport] = React.useState(null);
  const [enCours, setEnCours] = React.useState(false);
  const [groupeCreeId, setGroupeCreeId] = React.useState(null);

  const majInfo = (k, v) => setInfos(prev => ({ ...prev, [k]: v }));
  const preparerSeances = () => {
    const cle = String(infos.module_id || '');
    if (seancesPour === cle && seances.length > 0) return;
    const mod = (modules || []).find(m => String(m.id) === cle);
    const tpl = (moduleSessionTemplates || []).filter(t => String(t.module_id) === cle && Number(t.ordre) >= 1)
      .sort((a, b) => Number(a.ordre) - Number(b.ordre));
    let lignes = tpl.map(t => ({ numero: Number(t.ordre), titre: t.titre || `Séance ${t.ordre}`, date: '', heure_debut: '', heure_fin: '', formateur_id: '', ajoutee: false }));
    if (lignes.length === 0 && mod?.seances_prevues) {
      lignes = Array.from({ length: Number(mod.seances_prevues) || 0 }, (_, i) => ({ numero: i + 1, titre: `Séance ${i + 1}`, date: '', heure_debut: '', heure_fin: '', formateur_id: '', ajoutee: false }));
    }
    if (lignes.length === 0) lignes = [{ numero: 1, titre: 'Séance 1', date: '', heure_debut: '', heure_fin: '', formateur_id: '', ajoutee: true, emargement: true }];
    setSeances(lignes);
    setSeancesPour(cle);
  };
  const majSeance = (i, k, v) => setSeances(prev => prev.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const ajouterSeance = () => setSeances(prev => {
    const n = prev.reduce((m, s) => Math.max(m, Number(s.numero) || 0), 0) + 1;
    return [...prev, { numero: n, titre: `Séance ${n}`, date: '', heure_debut: '', heure_fin: '', formateur_id: '', ajoutee: true, emargement: true }];
  });

  const analyse = React.useMemo(() => analyserParticipantsGroupe({ texte, choisis, clients, groupes, moduleId: infos.module_id, sansCompte }), [texte, choisis, clients, groupes, infos.module_id, sansCompte]);
  const valides = analyse.filter(a => a.type === 'nouveau' || a.type === 'existant');
  const nbNouveaux = analyse.filter(a => a.type === 'nouveau').length;
  const limite = getClientLimitForOrg(orgSettings);
  const restant = Number.isFinite(limite) ? limite - (clients || []).length : Infinity;
  const depasse = Number.isFinite(restant) && nbNouveaux > restant;

  const peutContinuer1 = infos.nom.trim().length > 0;
  const creer = async () => {
    setEnCours(true);
    setRapport(null);
    try {
      const res = await actions.creerGroupe({ infos, seances, participants: valides, sansCompte }, setProgression);
      setGroupeCreeId(res.groupeId);
      setRapport({ ...res.rapport, titre: `Le groupe « ${infos.nom.trim()} » est créé.` });
    } catch (e) {
      toast.error(`Le groupe n'a pas pu être créé : ${e.message}`);
      setEnCours(false);
    }
  };

  if (enCours) {
    return <RapportGroupe progression={progression} rapport={rapport} onFermer={() => onCree(groupeCreeId)} libelleFermer="Ouvrir le groupe" />;
  }

  const Etape = ({ n, label }) => (
    <div className={`flex items-center gap-2 text-sm font-bold ${etape === n ? 'text-violet-700' : etape > n ? 'text-emerald-600' : 'text-gray-400'}`}>
      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${etape === n ? 'bg-violet-600 text-white' : etape > n ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{etape > n ? <Check size={14} /> : n}</span>
      {label}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <button onClick={onAnnuler} className="text-gray-500 hover:text-gray-900 font-bold flex items-center transition-colors"><ChevronLeft className="w-5 h-5 mr-1" /> Retour aux groupes</button>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Nouveau groupe</h1>
          <p className="text-gray-500 text-sm mt-1">Les dates, horaires et participants sont saisis une seule fois pour tout le groupe.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Etape n={1} label="Infos" /><span className="w-6 h-0.5 bg-gray-200 hidden sm:block" />
          <Etape n={2} label="Séances" /><span className="w-6 h-0.5 bg-gray-200 hidden sm:block" />
          <Etape n={3} label="Participants" />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 md:p-8">
        {etape === 1 && (
          <div className="grid gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <label htmlFor="grp-nom" className={libelleGroupe}>Nom du groupe *</label>
              <input id="grp-nom" value={infos.nom} onChange={e => majInfo('nom', e.target.value)} placeholder="Ex : Management d'équipe · Promo novembre" className={champGroupe} />
            </div>
            <div>
              <label htmlFor="grp-module" className={libelleGroupe}>Module de formation</label>
              <select id="grp-module" value={infos.module_id} onChange={e => majInfo('module_id', e.target.value)} className={champGroupe}>
                <option value="">Aucun module (séances libres)</option>
                {(modules || []).map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
              </select>
              <p className="text-xs text-gray-500 mt-1.5">Chaque participant recevra les séances et documents de ce module, comme un client individuel.</p>
            </div>
            <div>
              <label htmlFor="grp-formateur" className={libelleGroupe}>Formateur du groupe</label>
              <select id="grp-formateur" value={infos.formateur_id} onChange={e => majInfo('formateur_id', e.target.value)} className={champGroupe}>
                <option value="">À choisir plus tard</option>
                {(formateurs || []).map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
              </select>
              <p className="text-xs text-gray-500 mt-1.5">Vous pourrez mettre un autre formateur sur certaines séances à l'étape suivante.</p>
            </div>
            <div>
              <label htmlFor="grp-lieu" className={libelleGroupe}>Lieu (si en salle)</label>
              <input id="grp-lieu" value={infos.lieu} onChange={e => majInfo('lieu', e.target.value)} placeholder="Ex : Salle de formation, Lille" className={champGroupe} />
            </div>
            <div>
              <label htmlFor="grp-visio" className={libelleGroupe}>Lien visio (si à distance)</label>
              <input id="grp-visio" type="url" value={infos.lien_visio} onChange={e => majInfo('lien_visio', e.target.value)} placeholder="https://meet.google.com/…" className={champGroupe} />
            </div>
          </div>
        )}

        {etape === 2 && (
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
              <p className="text-sm text-gray-600 max-w-2xl">Indiquez la date et les horaires de chaque séance. La durée est libre : une séance peut être une demi-journée, une journée ou une heure. Vous pourrez tout modifier ensuite.</p>
              <div className="flex flex-wrap items-end gap-2 bg-gray-50 rounded-2xl p-3">
                <div><label htmlFor="grp-h1" className="block text-[11px] font-bold text-gray-500 mb-1">Début</label><input id="grp-h1" type="time" value={heuresTous.debut} onChange={e => setHeuresTous(p => ({ ...p, debut: e.target.value }))} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                <div><label htmlFor="grp-h2" className="block text-[11px] font-bold text-gray-500 mb-1">Fin</label><input id="grp-h2" type="time" value={heuresTous.fin} onChange={e => setHeuresTous(p => ({ ...p, fin: e.target.value }))} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                <button type="button" onClick={() => setSeances(prev => prev.map(s => ({ ...s, heure_debut: heuresTous.debut || s.heure_debut, heure_fin: heuresTous.fin || s.heure_fin })))}
                  className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm font-bold text-gray-700 hover:border-violet-300">Mettre ces horaires partout</button>
              </div>
            </div>
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-2xl">
              {seances.map((s, i) => (
                <div key={s.numero} className="p-3 md:p-4 flex flex-wrap items-end gap-3">
                  <div className="w-full sm:w-44 min-w-0">
                    <p className="text-sm font-black text-gray-900">Séance {s.numero}</p>
                    {s.ajoutee
                      ? <input aria-label={`Titre de la séance ${s.numero}`} value={s.titre} onChange={e => majSeance(i, 'titre', e.target.value)} className="mt-1 w-full px-2 py-1.5 rounded-lg border border-gray-200 text-xs" />
                      : (!/^Séance \d+$/.test(s.titre || '') && <p className="text-xs text-gray-500 truncate">{s.titre}</p>)}
                    {s.ajoutee && (
                      <label className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold text-gray-600 cursor-pointer select-none" title="Séance hors module : une feuille d'émargement est ajoutée au dossier de chaque participant si la case est cochée">
                        <input type="checkbox" checked={s.emargement !== false} onChange={e => majSeance(i, 'emargement', e.target.checked)} className="rounded text-violet-600" />
                        Feuille d'émargement
                      </label>
                    )}
                  </div>
                  <div><label htmlFor={`grp-d-${i}`} className="block text-[11px] font-bold text-gray-500 mb-1">Date</label><input id={`grp-d-${i}`} type="date" value={s.date} onChange={e => majSeance(i, 'date', e.target.value)} className="px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label htmlFor={`grp-hd-${i}`} className="block text-[11px] font-bold text-gray-500 mb-1">Début</label><input id={`grp-hd-${i}`} type="time" value={s.heure_debut} onChange={e => majSeance(i, 'heure_debut', e.target.value)} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label htmlFor={`grp-hf-${i}`} className="block text-[11px] font-bold text-gray-500 mb-1">Fin</label><input id={`grp-hf-${i}`} type="time" value={s.heure_fin} onChange={e => majSeance(i, 'heure_fin', e.target.value)} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div className="min-w-0 flex-1 sm:flex-none sm:w-56">
                    <label htmlFor={`grp-f-${i}`} className="block text-[11px] font-bold text-gray-500 mb-1">Formateur</label>
                    <select id={`grp-f-${i}`} value={s.formateur_id} onChange={e => majSeance(i, 'formateur_id', e.target.value)} className="w-full px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white">
                      <option value="">Formateur du groupe</option>
                      {(formateurs || []).map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                    </select>
                  </div>
                  {s.ajoutee && seances.length > 1 && (
                    <button type="button" onClick={() => setSeances(prev => prev.filter((_, j) => j !== i))} title="Retirer cette séance"
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200"><Trash2 size={15} /></button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={ajouterSeance} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-50 text-violet-700 font-bold text-sm hover:bg-violet-100"><Plus size={16} /> Ajouter une séance</button>
          </div>
        )}

        {etape === 3 && (
          <div className="space-y-4">
            <ChoixAccesParticipants sansCompte={sansCompte} setSansCompte={setSansCompte} nom="acces-creation" />
            <SaisieParticipantsGroupe texte={texte} setTexte={setTexte} choisis={choisis} setChoisis={setChoisis} clients={clients} analyse={analyse} sansCompte={sansCompte} />
            <div className={`rounded-2xl p-4 text-sm ${depasse ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'}`}>
              {nbNouveaux} nouveau{nbNouveaux > 1 ? 'x' : ''} dossier{nbNouveaux > 1 ? 's' : ''} à créer
              {Number.isFinite(restant) ? ` · il vous en reste ${Math.max(0, restant)} sur ${limite} dans votre offre.` : '.'}
              {depasse && ' Retirez des participants ou passez à une offre supérieure.'}
              {nbNouveaux > 0 && !depasse && (sansCompte ? ' Aucun e-mail ne sera envoyé : vous pourrez envoyer l\'accès plus tard depuis la fiche d\'un participant.' : ' Chaque nouveau participant reçoit un e-mail d\'invitation pour créer son mot de passe.')}
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-between gap-3 mt-8 pt-5 border-t border-gray-100">
          <button onClick={() => (etape === 1 ? onAnnuler() : setEtape(etape - 1))} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-bold text-sm hover:bg-gray-50">{etape === 1 ? 'Annuler' : 'Retour'}</button>
          {etape < 3 ? (
            <button disabled={etape === 1 && !peutContinuer1}
              onClick={() => { if (etape === 1) preparerSeances(); setEtape(etape + 1); }}
              className="px-5 py-2.5 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 disabled:opacity-40">Continuer</button>
          ) : (
            <button disabled={depasse} onClick={creer}
              className="px-5 py-2.5 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 disabled:opacity-40">
              {valides.length > 0 ? `Créer le groupe avec ${valides.length} participant${valides.length > 1 ? 's' : ''}` : 'Créer le groupe (participants plus tard)'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Ligne de séance modifiable dans la fiche du groupe ───────────────────────────────────────────
const LigneSeanceGroupe = ({ seance, groupe, formateurs, stats, actions, estAujourdhui, estPassee, onPapier = null }) => {
  const initial = React.useMemo(() => ({
    date: seance.date || '', heure_debut: heureCourte(seance.heure_debut), heure_fin: heureCourte(seance.heure_fin),
    formateur_id: seance.formateur_id ? String(seance.formateur_id) : '', lien_visio: seance.lien_visio || '', lieu: seance.lieu || '',
  }), [seance]);
  const [brouillon, setBrouillon] = React.useState(initial);
  const [plus, setPlus] = React.useState(false);
  const [enreg, setEnreg] = React.useState(false);
  React.useEffect(() => { setBrouillon(initial); }, [initial]);
  const modifie = Object.keys(initial).some(k => String(initial[k] || '') !== String(brouillon[k] || ''));
  const maj = (k, v) => setBrouillon(p => ({ ...p, [k]: v }));
  const enregistrer = async () => {
    setEnreg(true);
    try {
      await actions.majSeanceGroupe(seance, brouillon);
      toast.success(`Séance ${seance.numero} mise à jour pour tout le groupe`);
    } catch (e) {
      toast.error(`La séance n'a pas pu être enregistrée : ${e.message}`);
    }
    setEnreg(false);
  };
  const fId = brouillon.formateur_id || groupe.formateur_id;
  const fNom = (formateurs || []).find(f => String(f.id) === String(fId))?.nom;
  const pid = `gs-${seance.id}`;
  return (
    <div className={`p-4 ${estAujourdhui ? 'bg-violet-50/60' : ''}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-44 min-w-0">
          <p className="text-sm font-black text-gray-900 flex items-center gap-2">Séance {seance.numero}{estAujourdhui && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-violet-600 text-white">AUJOURD'HUI</span>}</p>
          <p className="text-xs text-gray-500 truncate">{seance.titre && !/^Séance \d+$/.test(seance.titre) ? seance.titre : (fNom ? `avec ${fNom}` : '')}</p>
        </div>
        <div><label htmlFor={`${pid}-d`} className="block text-[11px] font-bold text-gray-500 mb-1">Date</label><input id={`${pid}-d`} type="date" value={brouillon.date} onChange={e => maj('date', e.target.value)} className="px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
        <div><label htmlFor={`${pid}-hd`} className="block text-[11px] font-bold text-gray-500 mb-1">Début</label><input id={`${pid}-hd`} type="time" value={brouillon.heure_debut} onChange={e => maj('heure_debut', e.target.value)} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
        <div><label htmlFor={`${pid}-hf`} className="block text-[11px] font-bold text-gray-500 mb-1">Fin</label><input id={`${pid}-hf`} type="time" value={brouillon.heure_fin} onChange={e => maj('heure_fin', e.target.value)} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
        <div className="min-w-0 flex-1 sm:flex-none sm:w-52">
          <label htmlFor={`${pid}-f`} className="block text-[11px] font-bold text-gray-500 mb-1">Formateur</label>
          <select id={`${pid}-f`} value={brouillon.formateur_id} onChange={e => maj('formateur_id', e.target.value)} className="w-full px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white">
            <option value="">Formateur du groupe</option>
            {(formateurs || []).map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {stats.attendus > 0 && (
            <span className={`text-xs font-black px-2 py-1 rounded-lg ${stats.signes === stats.attendus ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{stats.signes}/{stats.attendus} signés</span>
          )}
          {stats.attendus === 0 && seance.ajoutee && seance.emargement === false && (
            <span className="text-xs font-bold px-2 py-1 rounded-lg bg-gray-50 text-gray-400">Sans émargement</span>
          )}
          {estPassee && stats.attendus > stats.signes && (
            <span className="text-xs font-black px-2 py-1 rounded-lg bg-amber-50 text-amber-700">{stats.attendus - stats.signes} en attente</span>
          )}
          {onPapier && (
            <button type="button" onClick={onPapier} className={`px-3 py-2 rounded-lg border text-xs font-bold inline-flex items-center gap-1.5 ${seance.feuille_papier_url ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-gray-200 text-gray-600 hover:border-violet-300'}`}>
              {seance.feuille_papier_url ? <Check size={14} /> : <FileText size={14} />} Feuille papier
            </button>
          )}
          <button type="button" onClick={() => setPlus(!plus)} className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:border-violet-300 inline-flex items-center gap-1.5"><Video size={14} /> Lieu / visio</button>
          {seance.ajoutee && (
            <button type="button" title="Supprimer cette séance" onClick={() => actions.demanderSuppressionSeance(seance)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200"><Trash2 size={15} /></button>
          )}
          {modifie && (
            <button type="button" disabled={enreg} onClick={enregistrer} className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-50">{enreg ? 'Enregistrement…' : 'Enregistrer'}</button>
          )}
        </div>
      </div>
      {plus && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2 bg-gray-50 rounded-xl p-3">
          <div><label htmlFor={`${pid}-l`} className="block text-[11px] font-bold text-gray-500 mb-1">Lieu de cette séance</label><input id={`${pid}-l`} value={brouillon.lieu} onChange={e => maj('lieu', e.target.value)} placeholder={groupe.lieu || 'Lieu du groupe'} className="w-full px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
          <div><label htmlFor={`${pid}-v`} className="block text-[11px] font-bold text-gray-500 mb-1">Lien visio de cette séance</label><input id={`${pid}-v`} type="url" value={brouillon.lien_visio} onChange={e => maj('lien_visio', e.target.value)} placeholder={groupe.lien_visio || 'https://…'} className="w-full px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
          <p className="sm:col-span-2 text-xs text-gray-500">Laissez vide pour utiliser le lieu et le lien du groupe.</p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// SIGNATURE PAR L'ENTREPRISE, GROUPES SANS COMPTE, ÉMARGEMENT PAPIER (30/10/2026)
// Formations courtes / en présentiel : les participants peuvent être des dossiers sans compte, la
// convention est signée par l'entreprise via un lien (serveur : api/prospects.js, table
// conventions_entreprise) et la présence se prend sur une feuille papier déposée ensuite.
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const ConventionsContext = React.createContext(null);
const STATUTS_CONVENTION = {
  envoye: ['En attente de l\'entreprise', 'bg-amber-50 text-amber-700'],
  signe_entreprise: ['À signer par l\'organisme', 'bg-violet-50 text-violet-700'],
  termine: ['Signée', 'bg-emerald-50 text-emerald-700'],
  annule: ['Annulée', 'bg-gray-100 text-gray-500'],
};
const LIMITE_ESSENTIEL_CONVENTIONS = 3;
const erreurTableAbsente = (err) => !!err && (err.code === '42P01' || err.code === 'PGRST205' || /(does not exist|schema cache|could not find)/i.test(err.message || ''));
const nouvelUuid = () => {
  const c = window.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const o = new Uint8Array(16); c.getRandomValues(o);
  o[6] = (o[6] & 0x0f) | 0x40; o[8] = (o[8] & 0x3f) | 0x80;
  const h = Array.from(o, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};
const COLONNES_CONVENTIONS = 'id, organisation_id, groupe_id, client_id, titre, entreprise_nom, entreprise_adresse, signataire_nom, signataire_fonction, signataire_email, ordre, code_requis, message, pdf_path, pdf_sha256, pdf_signe_path, statut, code_expire_at, code_essais, code_envoye_at, nb_codes, champs_entreprise, signature_entreprise, signature_organisme, organisme_signataire, signe_entreprise_at, signe_organisme_at, historique, envoye_par, created_at, ouvert_at, relance_at, nb_relances, expire_at';
const urlDocumentStockage = (chemin) => (chemin ? supabase.storage.from('documents').getPublicUrl(chemin).data.publicUrl : null);
const appelSignatureApi = async (action, body) => {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(`/api/prospects?action=${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify({ ...body, origin: window.location.origin }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(d.error || `Erreur ${r.status}`); e.limite = !!d.limite; throw e; }
  return d;
};
// dateHeureFr : déjà définie plus bas dans le fichier (même format).

// ─── Zone de signature (doigt ou souris) ─────────────────────────────────────────────────────────
const PadSignature = ({ onChange, label = 'Signature', hauteur = 160, id = 'pad-signature' }) => {
  const ref = React.useRef(null);
  const dessin = React.useRef(false);
  const [vide, setVide] = React.useState(true);
  React.useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    c.width = Math.max(1, c.offsetWidth) * ratio;
    c.height = hauteur * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0f172a';
  }, [hauteur]);
  const point = (e) => { const r = ref.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  // Image envoyée : 600 px de large au plus (fond transparent).
  const exporter = () => {
    const c = ref.current;
    const k = Math.min(1, 600 / c.width);
    const o = document.createElement('canvas');
    o.width = Math.round(c.width * k); o.height = Math.round(c.height * k);
    o.getContext('2d').drawImage(c, 0, 0, o.width, o.height);
    onChange(o.toDataURL('image/png'));
  };
  const debut = (e) => { e.preventDefault(); try { ref.current.setPointerCapture(e.pointerId); } catch (_) { /* rien */ } dessin.current = true; const p = point(e); const ctx = ref.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + 0.1, p.y + 0.1); ctx.stroke(); };
  const bouge = (e) => { if (!dessin.current) return; e.preventDefault(); const p = point(e); const ctx = ref.current.getContext('2d'); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const fin = () => { if (!dessin.current) return; dessin.current = false; setVide(false); exporter(); };
  const effacer = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setVide(true); onChange(null); };
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label htmlFor={id} className="text-sm font-bold text-gray-800">{label}</label>
        <button type="button" onClick={effacer} disabled={vide} className="text-xs font-bold text-violet-700 underline disabled:opacity-40 disabled:no-underline">Effacer</button>
      </div>
      <canvas id={id} ref={ref} role="img" aria-label="Zone de signature : signez avec le doigt ou la souris"
        onPointerDown={debut} onPointerMove={bouge} onPointerUp={fin} onPointerLeave={fin} onPointerCancel={fin}
        className="w-full rounded-xl border-2 border-dashed border-gray-300 bg-white touch-none cursor-crosshair" style={{ height: hauteur }} />
      {vide && <p className="text-xs text-gray-500 mt-1">Signez dans le cadre avec le doigt ou la souris.</p>}
    </div>
  );
};

// ─── Valeurs des balises d'une convention (groupe ou dossier seul) ───────────────────────────────
// Les balises « client » du modèle reçoivent les informations de l'ENTREPRISE (c'est elle qui signe).
const valeursConventionEntreprise = ({ participants = [], seances = [], groupe = null, module = null, formateur = null, orgSettings = null, entreprise = {}, financements = [], pourPdfVisuel = false }) => {
  const noms = participants.map(p => `${p.nom_complet || p.nom || 'Participant'}${p.numero_dossier ? ` (dossier ${p.numero_dossier})` : ''}`);
  const heures = seances.reduce((s, x) => s + heuresEntre(heureCourte(x.heure_debut), heureCourte(x.heure_fin)), 0);
  const prixParticipants = participants.reduce((s, p) => s + (Number(p.montant_prestation) || 0), 0);
  const prix = prixParticipants || ((Number(module?.prix_prestation) || 0) * participants.length);
  const base = valeursFormationOrganisme({ moduleNom: module?.nom || groupe?.nom || 'Formation', seances: seances.map(x => ({ numero_seance: x.numero || x.numero_seance || 1, date: x.date })), orgSettings });
  const adrF = parseAddressString(formateur?.adresse_formateur || formateur?.adresse_pro || formateur?.adresse || '');
  return {
    ...base,
    // Entreprise (balises dédiées + balises « client » des modèles existants)
    entreprise_nom: entreprise.nom || '', entreprise_adresse: entreprise.adresse || '',
    entreprise_signataire: entreprise.signataireNom || '', entreprise_signataire_fonction: entreprise.signataireFonction || '',
    nomcomplet_client: entreprise.nom || '', adresse_client: entreprise.adresse || '', client_email: entreprise.signataireEmail || '',
    numero_dossier_client: '', client_phone: '', rue_client: '', code_postal_client: '', ville_client: '', region_client: '',
    initiales_client: computeInitials(entreprise.signataireNom || entreprise.nom || ''),
    // Groupe / stagiaires
    groupe_nom: groupe?.nom || '',
    nombre_stagiaires: String(participants.length),
    liste_stagiaires: pourPdfVisuel ? noms.join(', ') : noms.join('\n'),
    duree_heures: heures ? nombreFr(Math.round(heures * 100) / 100) : '',
    lieu_formation: groupe?.lieu || '', adresse_session: groupe?.lieu || '',
    modalite_formation: groupe?.lien_visio && !groupe?.lieu ? 'À distance' : 'Présentiel',
    prix_prestation: prix ? nombreFr(prix) : '',
    ...valeursBalisesFinancement(financements, prix),
    // Formateur
    nom: formateur?.nom || '', nom_formateur: formateur?.nom || '', formateur_nom_complet: formateur?.nom || '', raison_sociale: formateur?.nom || '',
    email_formateur: formateur?.email || '', tel_formateur: formateur?.telephone || '',
    formateur_nda: formateur?.formateur_nda || formateur?.nda || '', formateur_siret: formateur?.formateur_siret || formateur?.siret || '',
    adresse_formateur: formateur?.adresse_formateur || formateur?.adresse_pro || formateur?.adresse || '',
    rue_formateur: adrF.rue, code_postal_formateur: adrF.codePostal, ville_formateur: adrF.ville, region_formateur: formateur?.region || '',
    initiales_formateur: computeInitials(formateur?.nom || ''), initiales_organisme: computeInitials(orgSettings?.nom || ''),
    date_du_jour: new Date().toLocaleDateString('fr-FR'),
    date_signature: '', date_signature_formateur: '', date_signature_organisme: '',
  };
};

// PDF de la convention à partir d'un modèle de la bibliothèque (Word, PDF ou modèle à balises posées).
const genererPdfConventionDepuisModele = async (templateInfo, valeursPour) => {
  if (!templateInfo?.url) throw new Error('Ce modèle n\'a pas de fichier.');
  const resp = await fetch(templateInfo.url);
  if (!resp.ok) throw new Error('Impossible de récupérer le modèle.');
  const ab = await resp.arrayBuffer();
  const tete = new Uint8Array(ab.slice(0, 4));
  const estPdf = tete[0] === 0x25 && tete[1] === 0x50 && tete[2] === 0x44 && tete[3] === 0x46;
  const estZip = tete[0] === 0x50 && tete[1] === 0x4B;
  const versPdf = async (docxBlob) => { try { return await convertDocxBlobToPdf(docxBlob); } catch (_) { return convertDocxBlobToPdfLocal(docxBlob); } };
  if (templateInfo.metadata?.has_visual_fields === true && templateInfo.id) {
    let champs = [];
    const { data, error } = await supabase.from('template_fields').select('*').eq('template_id', templateInfo.id).order('page', { ascending: true });
    if (!error && Array.isArray(data) && data.length) champs = data;
    else if (Array.isArray(templateInfo.metadata?.template_fields)) champs = templateInfo.metadata.template_fields;
    const donnees = champs.filter(f => {
      const t = f.tag || '';
      const interactif = f.field_type === 'signature' || f.field_type === 'checkbox' || f.field_type === 'text_input' || t.startsWith('signature_') || t.startsWith('checkbox_') || t.startsWith('texte_');
      return !interactif && !estDateSignature(f);
    });
    const basePdf = estPdf ? new Blob([ab], { type: 'application/pdf' }) : await versPdf(new Blob([ab]));
    return overlayFieldsOnPdf(basePdf, donnees, valeursPour(true));
  }
  if (estPdf) return new Blob([ab], { type: 'application/pdf' });
  if (estZip) {
    const [{ default: PizZip }, { default: Docxtemplater }] = await Promise.all([import('pizzip'), import('docxtemplater')]);
    const zip = new PizZip(ab);
    if (!zip.file('word/document.xml')) throw new Error('Ce modèle n\'est ni un document Word ni un PDF.');
    const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true, nullGetter: () => '' });
    doc.render(valeursPour(false));
    return versPdf(doc.getZip().generate({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
  }
  throw new Error('Ce modèle n\'est ni un document Word ni un PDF.');
};

// ─── Fenêtre « Faire signer par l'entreprise » ─────────────────────────────────────────────────
const ConventionEntrepriseModal = ({ cible, onClose, onEnvoye }) => {
  const ctx = React.useContext(ConventionsContext) || {};
  const { orgSettings, documentTemplates = {}, estPro, nomUtilisateur, modules = [], formateurs = [] } = ctx;
  const { groupe, client, participants = [], seances = [] } = cible;
  const module = (modules || []).find(m => String(m.id) === String(groupe?.module_id || client?.module_id));
  const fId = groupe?.formateur_id || client?.formateur_id;
  const formateur = (formateurs || []).find(f => String(f.id) === String(fId)) || null;
  const [f, setF] = React.useState({
    titre: `Convention de formation — ${groupe?.nom || client?.nom_complet || client?.nom || ''}`.trim(),
    source: 'modele', modele: '', fichier: null,
    entrepriseNom: '', entrepriseAdresse: '', signataireNom: '', signataireFonction: '', signataireEmail: '',
    ordre: 'entreprise_dabord', organismeSignataire: nomUtilisateur || '', signatureOrganisme: null,
    codeRequis: !!estPro, message: '',
  });
  const [financements, setFinancements] = React.useState([]);
  const [enCours, setEnCours] = React.useState('');
  const maj = (k, v) => setF(p => ({ ...p, [k]: v }));
  const modeles = Object.keys(documentTemplates || {}).filter(t => documentTemplates[t]?.url);

  // Pré-remplissage depuis les financements des participants (entreprise ou OPCO avec un contact).
  React.useEffect(() => {
    let annule = false;
    (async () => {
      const lignes = [];
      for (const p of participants.slice(0, 40)) lignes.push(...(await chargerFinancementsDossier(p.id)));
      if (annule) return;
      setFinancements(lignes);
      const l = lignes.find(x => x.type === 'entreprise' && (x.contact_email || x.financeur_nom)) || lignes.find(x => x.type === 'opco' && x.contact_email);
      if (l) setF(p => ({
        ...p,
        entrepriseNom: p.entrepriseNom || l.financeur_nom || '',
        signataireNom: p.signataireNom || l.contact_nom || '',
        signataireEmail: p.signataireEmail || l.contact_email || '',
      }));
    })();
    return () => { annule = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valeursPour = (pourPdfVisuel) => valeursConventionEntreprise({
    participants, seances, groupe, module, formateur, orgSettings, financements, pourPdfVisuel,
    entreprise: { nom: f.entrepriseNom.trim(), adresse: f.entrepriseAdresse.trim(), signataireNom: f.signataireNom.trim(), signataireFonction: f.signataireFonction.trim(), signataireEmail: f.signataireEmail.trim() },
  });
  const fabriquerPdf = async () => {
    if (f.source === 'fichier') {
      if (!f.fichier) throw new Error('Choisissez le PDF de la convention.');
      const tete = new Uint8Array(await f.fichier.slice(0, 4).arrayBuffer());
      if (!(tete[0] === 0x25 && tete[1] === 0x50 && tete[2] === 0x44 && tete[3] === 0x46)) throw new Error('Ce fichier n\'est pas un PDF.');
      return f.fichier;
    }
    if (!f.modele) throw new Error('Choisissez un modèle.');
    return genererPdfConventionDepuisModele(documentTemplates[f.modele], valeursPour);
  };
  const apercu = async () => {
    const fenetre = window.open('', '_blank');
    setEnCours('Préparation de l\'aperçu…');
    try {
      const pdf = await fabriquerPdf();
      const url = URL.createObjectURL(pdf);
      if (fenetre && !fenetre.closed) fenetre.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    } catch (e) {
      if (fenetre && !fenetre.closed) fenetre.close();
      toast.error(e.message || String(e));
    }
    setEnCours('');
  };
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.signataireEmail.trim());
  const pret = f.titre.trim() && f.signataireNom.trim() && emailOk && (f.source === 'fichier' ? !!f.fichier : !!f.modele)
    && (f.ordre === 'entreprise_dabord' || (f.signatureOrganisme && f.organismeSignataire.trim()));
  const envoyer = async () => {
    setEnCours('Préparation du PDF…');
    let chemin = null;
    try {
      const pdf = await fabriquerPdf();
      if (pdf.size > 15 * 1024 * 1024) throw new Error('PDF trop lourd (15 Mo maximum).');
      chemin = `convention_entreprise_${nouvelUuid()}.pdf`;
      setEnCours('Enregistrement du document…');
      const { error: up } = await supabase.storage.from('documents').upload(chemin, pdf, { contentType: 'application/pdf' });
      if (up) throw new Error('Le PDF n\'a pas pu être enregistré : ' + up.message);
      setEnCours('Envoi du lien à l\'entreprise…');
      const r = await appelSignatureApi('envoyer_signature', {
        groupeId: groupe?.id || null, clientId: groupe ? null : client?.id,
        titre: f.titre.trim(), pdfPath: chemin,
        entrepriseNom: f.entrepriseNom.trim(), entrepriseAdresse: f.entrepriseAdresse.trim(),
        signataireNom: f.signataireNom.trim(), signataireFonction: f.signataireFonction.trim(), signataireEmail: f.signataireEmail.trim(),
        ordre: f.ordre, codeRequis: f.codeRequis, message: f.message.trim(),
        ...(f.ordre === 'organisme_dabord' ? { signatureOrganisme: f.signatureOrganisme, organismeSignataire: f.organismeSignataire.trim() } : {}),
      });
      toast.success(r.simulated ? 'Demande enregistrée (envoi d\'email non configuré sur ce serveur).' : `Lien de signature envoyé à ${f.signataireEmail.trim()}`, { duration: 6000 });
      onEnvoye && onEnvoye();
      onClose();
    } catch (e) {
      if (chemin) { try { await supabase.storage.from('documents').remove([chemin]); } catch (_) { /* rien */ } }
      toast.error(e.message || String(e), { duration: e.limite ? 10000 : 6000 });
      setEnCours('');
    }
  };
  const champ = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-500';
  const lib = 'block text-xs font-bold text-gray-600 mb-1';
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-3 sm:p-4" onClick={() => !enCours && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby="conv-titre-fenetre" className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 sm:p-6 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="conv-titre-fenetre" className="text-lg font-black text-gray-900">Faire signer par l'entreprise</h2>
            <p className="text-sm text-gray-500 mt-0.5">L'entreprise reçoit un lien, lit le document et le signe en ligne, sans créer de compte.</p>
          </div>
          <button type="button" aria-label="Fermer" onClick={onClose} disabled={!!enCours} className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 shrink-0"><X size={18} /></button>
        </div>
        <div className="p-5 sm:p-6 space-y-5">
          <div><label htmlFor="conv-titre" className={lib}>Titre du document</label><input id="conv-titre" value={f.titre} onChange={e => maj('titre', e.target.value)} className={champ} /></div>

          <fieldset className="space-y-2">
            <legend className={lib}>Document à signer</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer ${f.source === 'modele' ? 'border-violet-500 bg-violet-50/60' : 'border-gray-200'}`}>
                <input type="radio" name="conv-source" checked={f.source === 'modele'} onChange={() => maj('source', 'modele')} className="mt-1" />
                <span className="text-sm"><b>Créer depuis un modèle</b><span className="block text-xs text-gray-500">Rempli avec l'entreprise, les stagiaires, les dates et les heures.</span></span>
              </label>
              <label className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer ${f.source === 'fichier' ? 'border-violet-500 bg-violet-50/60' : 'border-gray-200'}`}>
                <input type="radio" name="conv-source" checked={f.source === 'fichier'} onChange={() => maj('source', 'fichier')} className="mt-1" />
                <span className="text-sm"><b>Déposer un PDF</b><span className="block text-xs text-gray-500">Une convention déjà prête.</span></span>
              </label>
            </div>
            {f.source === 'modele' ? (
              <div>
                <label htmlFor="conv-modele" className="sr-only">Modèle</label>
                <select id="conv-modele" value={f.modele} onChange={e => maj('modele', e.target.value)} className={champ}>
                  <option value="">Choisir un modèle de la bibliothèque…</option>
                  {modeles.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Balises utiles : {'{entreprise_nom}'}, {'{liste_stagiaires}'}, {'{nombre_stagiaires}'}, {'{duree_heures}'}, {'{date_debut}'}, {'{prix_prestation}'}. Les balises du client reçoivent les informations de l'entreprise.</p>
              </div>
            ) : (
              <div>
                <label htmlFor="conv-fichier" className="sr-only">Fichier PDF</label>
                <input id="conv-fichier" type="file" accept="application/pdf,.pdf" onChange={e => maj('fichier', e.target.files?.[0] || null)} className="block w-full text-sm text-gray-700 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-violet-50 file:text-violet-700 file:font-bold" />
              </div>
            )}
          </fieldset>

          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className={`${lib} sm:col-span-2`}>Entreprise et signataire</legend>
            <div><label htmlFor="conv-ent" className={lib}>Entreprise</label><input id="conv-ent" value={f.entrepriseNom} onChange={e => maj('entrepriseNom', e.target.value)} className={champ} /></div>
            <div><label htmlFor="conv-adr" className={lib}>Adresse de l'entreprise</label><input id="conv-adr" value={f.entrepriseAdresse} onChange={e => maj('entrepriseAdresse', e.target.value)} className={champ} /></div>
            <div><label htmlFor="conv-sn" className={lib}>Nom du signataire *</label><input id="conv-sn" value={f.signataireNom} onChange={e => maj('signataireNom', e.target.value)} className={champ} /></div>
            <div><label htmlFor="conv-sf" className={lib}>Fonction</label><input id="conv-sf" value={f.signataireFonction} onChange={e => maj('signataireFonction', e.target.value)} placeholder="Ex : Responsable RH" className={champ} /></div>
            <div className="sm:col-span-2"><label htmlFor="conv-se" className={lib}>Email du signataire *</label><input id="conv-se" type="email" value={f.signataireEmail} onChange={e => maj('signataireEmail', e.target.value)} className={champ} />
              {f.signataireEmail && !emailOk && <p className="text-xs text-red-600 mt-1">Adresse email incomplète.</p>}</div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className={lib}>Ordre des signatures</legend>
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="conv-ordre" checked={f.ordre === 'entreprise_dabord'} onChange={() => maj('ordre', 'entreprise_dabord')} /> L'entreprise d'abord, puis vous</label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="conv-ordre" checked={f.ordre === 'organisme_dabord'} onChange={() => maj('ordre', 'organisme_dabord')} /> Vous d'abord (vous signez maintenant pour l'organisme)</label>
            {f.ordre === 'organisme_dabord' && (
              <div className="rounded-2xl bg-gray-50 p-3 space-y-3">
                <div><label htmlFor="conv-os" className={lib}>Nom de la personne qui signe pour l'organisme</label><input id="conv-os" value={f.organismeSignataire} onChange={e => maj('organismeSignataire', e.target.value)} className={champ} /></div>
                <PadSignature id="conv-pad-org" label="Signature de l'organisme" onChange={v => maj('signatureOrganisme', v)} hauteur={140} />
              </div>
            )}
          </fieldset>

          <label className={`flex items-start gap-2 text-sm ${estPro ? '' : 'opacity-70'}`}>
            <input type="checkbox" disabled={!estPro} checked={!!estPro && f.codeRequis} onChange={e => maj('codeRequis', e.target.checked)} className="mt-0.5" />
            <span>Demander un code reçu par email avant de signer <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-violet-600 text-white align-middle">PRO</span>
              {!estPro && <span className="block text-xs text-gray-500">Disponible avec l'offre Pro. L'offre Essentiel permet {LIMITE_ESSENTIEL_CONVENTIONS} signatures par lien par mois.</span>}</span>
          </label>

          <div><label htmlFor="conv-msg" className={lib}>Message (facultatif)</label><textarea id="conv-msg" rows={2} value={f.message} onChange={e => maj('message', e.target.value)} className={`${champ} resize-y`} /></div>
        </div>
        <div className="p-5 sm:p-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500 min-h-[1rem]" aria-live="polite">{enCours}</p>
          <div className="flex flex-wrap gap-2 justify-end">
            <button type="button" onClick={apercu} disabled={!!enCours || (f.source === 'fichier' ? !f.fichier : !f.modele)} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 disabled:opacity-40 inline-flex items-center gap-2"><Eye size={15} /> Aperçu</button>
            <button type="button" onClick={envoyer} disabled={!pret || !!enCours} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40 inline-flex items-center gap-2"><Send size={15} /> Envoyer le lien de signature</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Contresignature par l'organisme ─────────────────────────────────────────────────────────────
const ContresignatureModal = ({ convention, onClose, onSigne }) => {
  const ctx = React.useContext(ConventionsContext) || {};
  const [nom, setNom] = React.useState(ctx.nomUtilisateur || '');
  const [sig, setSig] = React.useState(null);
  const [enCours, setEnCours] = React.useState(false);
  const signer = async () => {
    setEnCours(true);
    try {
      await appelSignatureApi('signer_organisme', { conventionId: convention.id, signature: sig, nom: nom.trim() });
      toast.success('Document signé : le PDF final a été envoyé à l\'entreprise.', { duration: 6000 });
      onSigne && onSigne();
      onClose();
    } catch (e) { toast.error(e.message || String(e)); setEnCours(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[210] p-3 sm:p-4" onClick={() => !enCours && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby="contresig-titre" className="bg-white rounded-3xl w-full max-w-lg shadow-2xl p-5 sm:p-6 space-y-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 id="contresig-titre" className="text-lg font-black text-gray-900">Signer pour l'organisme</h2>
        <p className="text-sm text-gray-600">{convention.titre} — signé par {convention.signataire_nom}{convention.entreprise_nom ? ` (${convention.entreprise_nom})` : ''} le {dateHeureFr(convention.signe_entreprise_at)}.</p>
        <a href={urlDocumentStockage(convention.pdf_signe_path || convention.pdf_path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-violet-700 underline"><ExternalLink size={14} /> Relire le document</a>
        <div><label htmlFor="contresig-nom" className="block text-xs font-bold text-gray-600 mb-1">Votre nom</label><input id="contresig-nom" value={nom} onChange={e => setNom(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm" /></div>
        <PadSignature id="contresig-pad" label="Votre signature" onChange={setSig} />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={enCours} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Annuler</button>
          <button type="button" onClick={signer} disabled={!sig || !nom.trim() || enCours} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40">{enCours ? 'Signature…' : 'Signer et envoyer le PDF final'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Liste des conventions (onglet du groupe, ou fiche client › Finances) ─────────────────────────
const BlocConventionsEntreprise = ({ groupe = null, client = null, participants = [], seances = [] }) => {
  const ctx = React.useContext(ConventionsContext) || {};
  const { currentOrgId, estPro, groupes = [], clients = [], sessions = [] } = ctx;
  const groupeDuClient = !groupe && client?.groupe_id ? (groupes || []).find(g => String(g.id) === String(client.groupe_id)) : null;
  const [etat, setEtat] = React.useState('chargement'); // chargement | ok | absent | erreur
  const [liste, setListe] = React.useState([]);
  const [duMois, setDuMois] = React.useState(0);
  const [fenetre, setFenetre] = React.useState(false);
  const [contresig, setContresig] = React.useState(null);
  const [confirm, setConfirm] = React.useState(null);
  const charger = React.useCallback(async () => {
    if (!currentOrgId || (!groupe && !client)) return;
    // Audit 04/11/2026 : colonnes listées (le jeton du lien et l'empreinte du code ne sont plus lisibles).
    let req = supabase.from('conventions_entreprise').select(COLONNES_CONVENTIONS).eq('organisation_id', currentOrgId);
    if (groupe) req = req.eq('groupe_id', groupe.id);
    else if (groupeDuClient) req = req.or(`client_id.eq.${client.id},groupe_id.eq.${groupeDuClient.id}`);
    else req = req.eq('client_id', client.id);
    const { data, error } = await req.order('created_at', { ascending: false });
    if (error) { setEtat(erreurTableAbsente(error) ? 'absent' : 'erreur'); return; }
    setListe(data || []);
    setEtat('ok');
    if (!estPro) {
      const d = new Date();
      const debut = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
      const { count } = await supabase.from('conventions_entreprise').select('id', { count: 'exact', head: true }).eq('organisation_id', currentOrgId).gte('created_at', debut);
      setDuMois(count || 0);
    }
  }, [currentOrgId, groupe, client, groupeDuClient, estPro]);
  React.useEffect(() => { charger(); }, [charger]);

  const action = async (nom, conv, succes) => {
    try { await appelSignatureApi(nom, { conventionId: conv.id }); toast.success(succes); charger(); }
    catch (e) { toast.error(e.message || String(e)); }
  };
  if (etat === 'absent') {
    return <p className="text-xs text-gray-500 bg-gray-50 rounded-xl p-3">Signature par l'entreprise : lancez le SQL « signature_entreprise » dans Supabase pour l'activer.</p>;
  }
  // Données pour remplir la convention : un groupe (tous ses participants) ou un dossier seul.
  const cible = groupe
    ? { groupe, participants, seances }
    : (() => {
      const lignes = (sessions || []).filter(s => String(s.client_id) === String(client?.id) && Number(s.numero_seance) >= 1 && s.date);
      const parNumero = new Map();
      lignes.forEach(s => { const n = Number(s.numero_seance); const e = parNumero.get(n); if (!e || heuresEntre(heureCourte(s.heure_debut), heureCourte(s.heure_fin)) > heuresEntre(heureCourte(e.heure_debut), heureCourte(e.heure_fin))) parNumero.set(n, { numero: n, date: s.date, heure_debut: s.heure_debut, heure_fin: s.heure_fin }); });
      const c = (clients || []).find(x => String(x.id) === String(client?.id)) || client;
      return { client: c, participants: c ? [c] : [], seances: Array.from(parNumero.values()) };
    })();
  const limiteAtteinte = !estPro && duMois >= LIMITE_ESSENTIEL_CONVENTIONS;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-gray-900 flex items-center gap-2"><Briefcase size={17} className="text-violet-600" /> Convention signée par l'entreprise</p>
          <p className="text-xs text-gray-500 mt-0.5">L'entreprise signe en ligne par un lien, sans compte.{!estPro ? ` Offre Essentiel : ${Math.min(duMois, LIMITE_ESSENTIEL_CONVENTIONS)}/${LIMITE_ESSENTIEL_CONVENTIONS} ce mois-ci.` : ''}</p>
        </div>
        <button type="button" onClick={() => setFenetre(true)} disabled={etat !== 'ok' || limiteAtteinte}
          title={limiteAtteinte ? 'Limite de l\'offre Essentiel atteinte ce mois-ci : passez à l\'offre Pro' : undefined}
          className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-40 inline-flex items-center gap-2"><Send size={15} /> Faire signer par l'entreprise</button>
      </div>
      {limiteAtteinte && <p className="text-xs text-amber-700 bg-amber-50 rounded-xl p-3">Vous avez utilisé les {LIMITE_ESSENTIEL_CONVENTIONS} signatures par lien de l'offre Essentiel ce mois-ci. L'offre Pro les rend illimitées.</p>}
      {etat === 'erreur' && <p className="text-sm text-red-600">Les conventions n'ont pas pu être chargées.</p>}
      {etat === 'ok' && liste.length === 0 && <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4">Aucune convention envoyée pour l'instant.</p>}
      {liste.length > 0 && (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-2xl bg-white">
          {liste.map(c => {
            const [libelle, ton] = STATUTS_CONVENTION[c.statut] || ['—', 'bg-gray-100 text-gray-500'];
            const duGroupe = !groupe && c.groupe_id;
            return (
              <li key={c.id} className="p-4 space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-gray-900 break-words">{c.titre}</p>
                    <p className="text-xs text-gray-500 break-words">{c.signataire_nom}{c.signataire_fonction ? `, ${c.signataire_fonction}` : ''}{c.entreprise_nom ? ` · ${c.entreprise_nom}` : ''} · {c.signataire_email}{duGroupe && groupeDuClient ? ` · groupe « ${groupeDuClient.nom} »` : ''}</p>
                  </div>
                  <span className={`text-xs font-black px-2 py-1 rounded-lg shrink-0 ${ton}`}>{libelle}</span>
                </div>
                <p className="text-xs text-gray-500">
                  Envoyée le {dateHeureFr(c.created_at)}
                  {c.ouvert_at ? ` · ouverte le ${dateHeureFr(c.ouvert_at)}` : c.statut === 'envoye' ? ' · pas encore ouverte' : ''}
                  {c.signe_entreprise_at ? ` · signée par l'entreprise le ${dateHeureFr(c.signe_entreprise_at)}` : ''}
                  {c.signe_organisme_at ? ` · signée par l'organisme le ${dateHeureFr(c.signe_organisme_at)}` : ''}
                  {c.nb_relances ? ` · ${c.nb_relances} rappel${c.nb_relances > 1 ? 's' : ''}` : ''}
                </p>
                <div className="flex flex-wrap gap-2">
                  {c.pdf_signe_path
                    ? <a href={urlDocumentStockage(c.pdf_signe_path)} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:border-violet-300 inline-flex items-center gap-1.5"><FileCheck size={14} /> PDF signé</a>
                    : <a href={urlDocumentStockage(c.pdf_path)} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:border-violet-300 inline-flex items-center gap-1.5"><FileText size={14} /> Document</a>}
                  {c.statut === 'signe_entreprise' && <button type="button" onClick={() => setContresig(c)} className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-bold inline-flex items-center gap-1.5"><PenTool size={14} /> Signer pour l'organisme</button>}
                  {c.statut === 'envoye' && <button type="button" onClick={() => action('relancer_signature', c, 'Rappel envoyé à l\'entreprise')} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:border-violet-300 inline-flex items-center gap-1.5"><Mail size={14} /> Relancer</button>}
                  {(c.statut === 'envoye' || c.statut === 'signe_entreprise') && (
                    <button type="button" onClick={() => setConfirm(c)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-500 hover:text-red-600 hover:border-red-200">Annuler l'envoi</button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {fenetre && <ConventionEntrepriseModal cible={cible} onClose={() => setFenetre(false)} onEnvoye={charger} />}
      {contresig && <ContresignatureModal convention={contresig} onClose={() => setContresig(null)} onSigne={charger} />}
      <ConfirmModal isOpen={!!confirm} title="Annuler l'envoi ?" message="Le lien envoyé à l'entreprise ne fonctionnera plus. Le document et son historique restent dans la liste." confirmLabel="Annuler l'envoi"
        onCancel={() => setConfirm(null)} onConfirm={async () => { const c = confirm; setConfirm(null); await action('annuler_signature', c, 'Envoi annulé'); }} />
    </div>
  );
};

// ─── Page publique /signer?t=… : l'entreprise lit et signe, sans compte ─────────────────────────
const SignatureEntreprisePage = () => {
  const token = React.useMemo(() => new URLSearchParams(window.location.search).get('t') || '', []);
  const [etat, setEtat] = React.useState('chargement'); // chargement | a_signer | signe | termine | annule | expire | erreur | fini
  const [infos, setInfos] = React.useState({});
  const [erreur, setErreur] = React.useState('');
  const [lu, setLu] = React.useState(false);
  const [accepte, setAccepte] = React.useState(false);
  const [siret, setSiret] = React.useState('');
  const [adresse, setAdresse] = React.useState('');
  const [signature, setSignature] = React.useState(null);
  const [code, setCode] = React.useState('');
  const [codeEnvoye, setCodeEnvoye] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [resultat, setResultat] = React.useState(null);
  React.useEffect(() => {
    if (!token) { setEtat('erreur'); setErreur('Ce lien est incomplet.'); return; }
    fetch(`/api/prospects?action=lire_signature&t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setEtat('erreur'); setErreur(d.error || 'Ce lien est invalide.'); return; }
        setInfos(d); setAdresse(d.entrepriseAdresse || ''); setEtat(d.etat);
      })
      .catch(() => { setEtat('erreur'); setErreur('Impossible de charger le document pour le moment.'); });
  }, [token]);
  const post = async (action, body) => {
    const r = await fetch(`/api/prospects?action=${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token, ...body }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'Erreur, réessayez dans un instant.');
    return d;
  };
  const demanderCode = async () => {
    setEnCours(true); setErreur('');
    try { const d = await post('code_signature', {}); setCodeEnvoye(d.email || 'votre adresse'); }
    catch (e) { setErreur(e.message); }
    setEnCours(false);
  };
  const signer = async () => {
    setEnCours(true); setErreur('');
    try {
      const d = await post('signer_entreprise', { signature, accepte, code, champs: { siret, adresse } });
      setResultat(d); setEtat('fini');
    } catch (e) { setErreur(e.message); }
    setEnCours(false);
  };
  const entete = (
    <header className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
      <span aria-hidden="true" className="w-9 h-9 rounded-xl bg-violet-600 text-white font-black text-sm flex items-center justify-center shrink-0">{computeInitials(infos.organisme || 'S') || 'S'}</span>
      <span className="min-w-0"><b className="block text-sm text-gray-900 truncate">{infos.organisme || 'SkorUp'}</b><span className="block text-xs text-gray-500">Signature en ligne</span></span>
    </header>
  );
  const cadre = (contenu) => (
    <div className="min-h-dvh bg-gray-50">{entete}<main className="max-w-xl mx-auto p-4 space-y-4">{contenu}</main></div>
  );
  if (etat === 'chargement') return cadre(<p className="text-center text-sm text-gray-500 py-16">Chargement du document…</p>);
  const message = (titre, texte, lien) => cadre(
    <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center space-y-3 mt-6">
      <h1 className="text-xl font-black text-gray-900">{titre}</h1>
      <p className="text-sm text-gray-600 leading-relaxed">{texte}</p>
      {lien && <a href={lien} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-violet-600 text-white text-sm font-bold"><Download size={16} /> Télécharger le document signé</a>}
    </div>
  );
  if (etat === 'erreur') return message('Lien invalide', erreur || 'Ce lien ne fonctionne pas.');
  if (etat === 'annule') return message('Demande annulée', `${infos.organisme || 'L\'organisme'} a annulé cette demande de signature. Contactez-le si besoin.`);
  if (etat === 'expire') return message('Lien expiré', `Ce lien n'est plus valable. Demandez à ${infos.organisme || 'l\'organisme de formation'} de vous le renvoyer.`);
  if (etat === 'signe') return message('Document déjà signé', `Merci, votre signature est enregistrée. ${infos.organisme || 'L\'organisme'} va signer à son tour ; vous recevrez le PDF final par email.`);
  if (etat === 'termine') return message('Document signé', 'Ce document est signé par tout le monde.', infos.pdfUrl);
  if (etat === 'fini') return resultat?.etat === 'termine'
    ? message('Merci, c\'est signé', 'Le document est maintenant signé par tout le monde. Le PDF final vous a été envoyé par email.', resultat.pdfUrl)
    : message('Merci, c\'est signé', `Votre signature est enregistrée. ${infos.organisme || 'L\'organisme'} va signer à son tour ; vous recevrez le PDF final par email.`);
  const codeOk = !infos.codeRequis || /^\d{6}$/.test(code.trim());
  const pret = lu && accepte && signature && codeOk && !enCours;
  return cadre(
    <>
      <div className="space-y-1.5 pt-2">
        <h1 className="text-2xl font-black text-gray-900 leading-tight break-words">{infos.titre}</h1>
        <p className="text-sm text-gray-600">Signataire : <b>{infos.signataireNom}</b>{infos.signataireFonction ? `, ${infos.signataireFonction}` : ''}{infos.entrepriseNom ? `, ${infos.entrepriseNom}` : ''}</p>
        {infos.organismeASigne && <p className="text-xs text-emerald-700">Déjà signé par {infos.organisme}.</p>}
      </div>
      {infos.message && <p className="text-sm text-gray-700 bg-white border border-gray-100 rounded-2xl p-4 whitespace-pre-line break-words">{infos.message}</p>}
      <section className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        <p className="font-bold text-gray-900">1. Lire le document</p>
        <a href={infos.pdfUrl} target="_blank" rel="noopener noreferrer" onClick={() => setLu(true)} className="flex items-center justify-center gap-2 min-h-[48px] rounded-xl border border-violet-200 bg-violet-50 text-violet-800 text-sm font-bold"><FileText size={17} /> Ouvrir le document (PDF)</a>
        {!lu && <p className="text-xs text-gray-500">Ouvrez le document avant de signer.</p>}
      </section>
      <section className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        <p className="font-bold text-gray-900">2. Compléter (facultatif)</p>
        <div><label htmlFor="sig-siret" className="block text-xs font-bold text-gray-600 mb-1">N° SIRET de l'entreprise</label><input id="sig-siret" inputMode="numeric" value={siret} onChange={e => setSiret(e.target.value)} className="w-full min-h-[44px] px-3 rounded-xl border border-gray-200 text-base" /></div>
        <div><label htmlFor="sig-adr" className="block text-xs font-bold text-gray-600 mb-1">Adresse de l'entreprise</label><input id="sig-adr" value={adresse} onChange={e => setAdresse(e.target.value)} className="w-full min-h-[44px] px-3 rounded-xl border border-gray-200 text-base" /></div>
      </section>
      <section className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        <p className="font-bold text-gray-900">3. Signer</p>
        <label className="flex items-start gap-3 text-sm text-gray-800"><input type="checkbox" checked={accepte} onChange={e => setAccepte(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0" /> J'ai lu le document et je l'accepte (« lu et approuvé »).</label>
        <PadSignature id="sig-pad" label="Votre signature" onChange={setSignature} hauteur={170} />
        {infos.codeRequis && (
          <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-3 space-y-2">
            <p className="text-sm text-gray-800">Pour confirmer votre identité, un code à 6 chiffres est envoyé à {infos.email}.</p>
            <button type="button" onClick={demanderCode} disabled={enCours} className="min-h-[44px] px-4 rounded-xl border border-violet-200 bg-white text-sm font-bold text-violet-800 disabled:opacity-50">{codeEnvoye ? 'Renvoyer le code' : 'Recevoir le code par email'}</button>
            {codeEnvoye && <p className="text-xs text-emerald-700">Code envoyé à {codeEnvoye} (valable 15 minutes).</p>}
            <div><label htmlFor="sig-code" className="block text-xs font-bold text-gray-600 mb-1">Code reçu</label><input id="sig-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full min-h-[44px] px-3 rounded-xl border border-gray-200 text-base tracking-[0.3em]" /></div>
          </div>
        )}
        {erreur && <p role="alert" className="text-sm text-red-600">{erreur}</p>}
        <button type="button" onClick={signer} disabled={!pret} className="w-full min-h-[52px] rounded-xl bg-violet-600 text-white text-base font-black disabled:opacity-40">{enCours ? 'Signature en cours…' : 'Signer le document'}</button>
        <p className="text-xs text-gray-500 text-center">Aucun compte n'est créé. La date, l'heure et l'adresse IP de la signature sont conservées avec le document.</p>
      </section>
    </>
  );
};

// ─── Feuille d'émargement papier ─────────────────────────────────────────────────────────────────
const coupeDejeuner = (debut, fin) => { const a = minutesHeure(heureCourte(debut)), b = minutesHeure(heureCourte(fin)); return a !== null && b !== null && a < 12 * 60 + 30 && b > 13 * 60 + 30; };
const genererFeuillePapierPdf = async ({ groupe, seance, participants, orgNom, formateurNom }) => {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const gras = await pdf.embedFont(StandardFonts.HelveticaBold);
  const sur = (t, f = font) => Array.from(String(t ?? '').replace(/[  \t\r\n]/g, ' ')).map(ch => { try { f.encodeText(ch); return ch; } catch (_) { return '?'; } }).join('');
  const coupe = (t, f, taille, max) => { let s = sur(t, f); while (s && f.widthOfTextAtSize(s, taille) > max) s = s.slice(0, -1); return s; };
  const W = 595.28, H = 841.89, M = 40;
  const deux = coupeDejeuner(seance.heure_debut, seance.heure_fin);
  const colonnes = deux ? [['N°', 28], ['Nom du stagiaire', 211], ['Signature matin', 118], ['Signature après-midi', 118]] : [['N°', 28], ['Nom du stagiaire', 251], ['Signature', 236]];
  const dateLongue = seance.date ? new Date(`${seance.date}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '____ / ____ / ________';
  let page, y;
  const nouvellePage = (suite) => {
    page = pdf.addPage([W, H]); y = H - M;
    page.drawText(coupe(orgNom || '', gras, 10, W - 2 * M), { x: M, y, size: 10, font: gras, color: rgb(0.43, 0.16, 0.85) }); y -= 22;
    page.drawText(sur(`Feuille d'émargement${suite ? ' (suite)' : ''}`, gras), { x: M, y, size: 18, font: gras }); y -= 20;
    [`Formation : ${groupe?.nom || ''}${seance.titre && !/^Séance \d+$/.test(seance.titre) ? ` · ${seance.titre}` : ''} (séance ${seance.numero})`,
      `Date : ${dateLongue} · Horaires : ${heureCourte(seance.heure_debut) || '__:__'} – ${heureCourte(seance.heure_fin) || '__:__'}`,
      `Lieu : ${seance.lieu || groupe?.lieu || '________________'} · Formateur : ${formateurNom || '________________'}`]
      .forEach(l => { page.drawText(coupe(l, font, 10, W - 2 * M), { x: M, y, size: 10, font }); y -= 14; });
    y -= 8;
    let x = M;
    colonnes.forEach(([t, w]) => { page.drawRectangle({ x, y: y - 20, width: w, height: 20, color: rgb(0.95, 0.93, 0.99), borderColor: rgb(0.6, 0.6, 0.65), borderWidth: 0.7 }); page.drawText(sur(t, gras), { x: x + 5, y: y - 14, size: 9, font: gras }); x += w; });
    y -= 20;
  };
  nouvellePage(false);
  const lignes = participants.length ? participants : [];
  const total = Math.max(lignes.length, 1) + 2; // deux lignes vides pour un participant de dernière minute
  for (let i = 0; i < total; i++) {
    if (y - 34 < M + 120) nouvellePage(true);
    let x = M;
    const p = lignes[i];
    colonnes.forEach(([, w], j) => {
      page.drawRectangle({ x, y: y - 34, width: w, height: 34, borderColor: rgb(0.6, 0.6, 0.65), borderWidth: 0.7 });
      if (j === 0) page.drawText(String(i + 1), { x: x + 8, y: y - 21, size: 10, font });
      if (j === 1 && p) page.drawText(coupe(p.nom_complet || p.nom || '', font, 10, w - 10), { x: x + 5, y: y - 21, size: 10, font });
      x += w;
    });
    y -= 34;
  }
  y -= 24;
  page.drawText(sur('Signature du formateur', gras), { x: M, y, size: 10, font: gras });
  page.drawText(sur('Nombre de présents : ______', font), { x: W / 2 + 20, y, size: 10, font });
  page.drawRectangle({ x: M, y: y - 70, width: 230, height: 60, borderColor: rgb(0.6, 0.6, 0.65), borderWidth: 0.7 });
  page.drawText(sur('Après la séance : prenez la feuille en photo et déposez-la dans SkorUp (Feuille papier).', font), { x: M, y: M - 10, size: 8, font, color: rgb(0.4, 0.4, 0.45) });
  return new Blob([await pdf.save()], { type: 'application/pdf' });
};

const EmargementPapierModal = ({ groupe, seance, participants, sessions, formateurNom, orgNom, nomUtilisateur, onClose, onEnregistre }) => {
  const lignesDe = (p) => (sessions || []).filter(s => String(s.client_id) === String(p.id) && Number(s.numero_seance) === Number(seance.numero)
    && s.type_activite === 'signature' && lireMeta(s.metadata).requiresClientSignature !== false);
  const signeEnLigne = (p) => { const l = lignesDe(p); return l.length > 0 && l.every(s => s.statut_client === 'Signé' && !lireMeta(s.metadata).emargement_papier); };
  const initiales = React.useMemo(() => {
    const pr = (seance.presences_papier && typeof seance.presences_papier === 'object') ? seance.presences_papier : {};
    return Object.fromEntries(participants.map(p => [p.id, pr[p.id] ? { presence: pr[p.id].presence || 'present', heures: pr[p.id].heures ?? '' } : { presence: 'present', heures: '' }]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seance.id]);
  const [presences, setPresences] = React.useState(initiales);
  const [feuilleUrl, setFeuilleUrl] = React.useState(seance.feuille_papier_url || '');
  const [enCours, setEnCours] = React.useState('');
  const dureeSeance = heuresEntre(heureCourte(seance.heure_debut), heureCourte(seance.heure_fin));
  const majP = (id, k, v) => setPresences(p => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const telecharger = async () => {
    setEnCours('Préparation de la feuille…');
    try {
      const blob = await genererFeuillePapierPdf({ groupe, seance, participants, orgNom, formateurNom });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Feuille_emargement_${(groupe?.nom || 'groupe').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]+/g, '_')}_seance_${seance.numero}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('La feuille n\'a pas pu être préparée : ' + (e.message || e)); }
    setEnCours('');
  };
  const deposer = async (fichier) => {
    if (!fichier) return;
    if (fichier.size > 15 * 1024 * 1024) { toast.error('Fichier trop lourd (15 Mo maximum).'); return; }
    const ext = (/\.(pdf|jpe?g|png|heic|webp)$/i.exec(fichier.name || '') || [null, fichier.type === 'application/pdf' ? 'pdf' : 'jpg'])[1].toLowerCase();
    setEnCours('Envoi de la feuille…');
    const chemin = `feuille_emargement_${nouvelUuid()}.${ext}`;
    const { error } = await supabase.storage.from('documents').upload(chemin, fichier, { contentType: fichier.type || undefined });
    if (error) toast.error('La feuille n\'a pas pu être envoyée : ' + error.message);
    else setFeuilleUrl(urlDocumentStockage(chemin));
    setEnCours('');
  };
  const valider = async () => {
    setEnCours('Enregistrement…');
    const maintenant = new Date().toISOString();
    const pr = {};
    participants.forEach(p => {
      const v = presences[p.id] || { presence: 'present' };
      pr[p.id] = v.presence === 'partiel' ? { presence: 'partiel', heures: Math.max(0, Number(String(v.heures).replace(',', '.')) || 0) } : { presence: v.presence };
    });
    const { error } = await supabase.from('groupe_seances').update({ feuille_papier_url: feuilleUrl, feuille_papier_le: maintenant, feuille_papier_par: nomUtilisateur || null, presences_papier: pr }).eq('id', seance.id);
    if (error) {
      toast.error(erreurTableAbsente(error) || /feuille_papier|presences_papier/.test(error.message || '') ? 'Lancez d\'abord le SQL « signature_entreprise » dans Supabase.' : 'Enregistrement impossible : ' + error.message, { duration: 8000 });
      setEnCours(''); return;
    }
    // Report dans le dossier de chaque participant (les émargements déjà signés en ligne ne bougent pas).
    let erreurs = 0;
    for (const p of participants) {
      if (signeEnLigne(p)) continue;
      const v = pr[p.id];
      for (const s of lignesDe(p)) {
        const meta = lireMeta(s.metadata);
        const papier = { feuille_url: feuilleUrl, presence: v.presence, ...(v.presence === 'partiel' ? { heures: v.heures } : {}), valide_le: maintenant, valide_par: nomUtilisateur || null };
        const majS = { metadata: { ...meta, emargement_papier: papier } };
        if (v.presence !== 'absent') {
          majS.statut_client = 'Signé'; majS.date_signature_client = s.date_signature_client || maintenant;
          if (meta.requiresTrainerSignature === true) { majS.statut_formateur = 'Signé'; majS.date_signature_formateur = s.date_signature_formateur || maintenant; }
          majS.statut = 'Signé';
        } else if (meta.emargement_papier && !s.signature_client && s.statut_client === 'Signé') {
          // Présent par erreur sur une feuille précédente : redevient « à venir ».
          majS.statut_client = 'À venir'; majS.date_signature_client = null; majS.statut = 'À venir';
          if (!s.signature_formateur) { majS.statut_formateur = 'À venir'; majS.date_signature_formateur = null; }
        }
        const { error: e2 } = await supabase.from('sessions').update(majS).eq('id', s.id);
        if (e2) erreurs++;
      }
    }
    setEnCours('');
    if (erreurs) toast.error(`${erreurs} dossier${erreurs > 1 ? 's n\'ont' : ' n\'a'} pas pu être mis à jour : réessayez.`, { duration: 8000 });
    else toast.success('Feuille d\'émargement enregistrée');
    onEnregistre && onEnregistre();
    if (!erreurs) onClose();
  };
  const nbPresents = participants.filter(p => signeEnLigne(p) || (presences[p.id]?.presence || 'present') !== 'absent').length;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-3 sm:p-4" onClick={() => !enCours && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby="papier-titre" className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 sm:p-6 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="papier-titre" className="text-lg font-black text-gray-900">Feuille d'émargement papier</h2>
            <p className="text-sm text-gray-500 mt-0.5 break-words">{groupe?.nom} · séance {seance.numero}{seance.date ? ` · ${dateCourte(seance.date)}` : ''}{seance.heure_debut ? ` · ${heureCourte(seance.heure_debut)}–${heureCourte(seance.heure_fin)}` : ''}</p>
          </div>
          <button type="button" aria-label="Fermer" onClick={onClose} disabled={!!enCours} className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 shrink-0"><X size={18} /></button>
        </div>
        <div className="p-5 sm:p-6 space-y-5">
          <section className="space-y-2">
            <p className="text-sm font-black text-gray-900">1. Avant la séance</p>
            <button type="button" onClick={telecharger} disabled={!!enCours} className="px-4 py-2.5 rounded-xl bg-violet-50 text-violet-800 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50"><Download size={16} /> Télécharger la feuille à imprimer</button>
            <p className="text-xs text-gray-500">Noms des participants, date, horaires et formateur déjà remplis.</p>
          </section>
          <section className="space-y-2">
            <p className="text-sm font-black text-gray-900">2. Après la séance : déposer la feuille signée</p>
            <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-gray-200 cursor-pointer hover:border-violet-300">
              <Upload size={18} className="text-violet-600 shrink-0" />
              <span className="text-sm text-gray-700">{feuilleUrl ? 'Remplacer la photo ou le scan' : 'Photo (téléphone) ou scan PDF de la feuille signée'}</span>
              <input type="file" accept="image/*,application/pdf" className="sr-only" onChange={e => deposer(e.target.files?.[0])} />
            </label>
            {feuilleUrl && <a href={feuilleUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700"><Check size={15} /> Feuille déposée : la voir</a>}
          </section>
          <section className="space-y-2">
            <p className="text-sm font-black text-gray-900">3. Qui était présent ?</p>
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-2xl">
              {participants.map(p => {
                const v = presences[p.id] || { presence: 'present' };
                const enLigne = signeEnLigne(p);
                return (
                  <li key={p.id} className="p-3 flex flex-wrap items-center gap-2 justify-between">
                    <span className="text-sm font-bold text-gray-900 min-w-0 break-words">{p.nom_complet || p.nom}</span>
                    {enLigne ? <span className="text-xs font-black px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700">Signé en ligne</span> : (
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="sr-only" htmlFor={`pres-${p.id}`}>Présence de {p.nom_complet || p.nom}</label>
                        <select id={`pres-${p.id}`} value={v.presence} onChange={e => majP(p.id, 'presence', e.target.value)} className="px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white">
                          <option value="present">Présent</option>
                          <option value="absent">Absent</option>
                          <option value="partiel">Présent en partie</option>
                        </select>
                        {v.presence === 'partiel' && (
                          <span className="inline-flex items-center gap-1 text-sm"><label htmlFor={`h-${p.id}`} className="sr-only">Heures de présence</label>
                            <input id={`h-${p.id}`} inputMode="decimal" value={v.heures} onChange={e => majP(p.id, 'heures', e.target.value)} placeholder={dureeSeance ? String(dureeSeance / 2).replace('.', ',') : '2'} className="w-16 px-2 py-2 rounded-lg border border-gray-200 text-sm" /> h</span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-gray-500">{nbPresents} présent{nbPresents > 1 ? 's' : ''} sur {participants.length}. Les absences et les heures partielles sont reprises dans le BPF.</p>
          </section>
        </div>
        <div className="p-5 sm:p-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500" aria-live="polite">{enCours || (!feuilleUrl ? 'Déposez la feuille signée pour valider.' : '')}</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={!!enCours} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Fermer</button>
            <button type="button" onClick={valider} disabled={!feuilleUrl || !!enCours || participants.length === 0} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40">Valider la séance</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Choix à l'ajout de participants : avec un espace SkorUp, ou sans compte.
const ChoixAccesParticipants = ({ sansCompte, setSansCompte, nom = 'acces-participants' }) => (
  <fieldset>
    <legend className={libelleGroupe}>Comment les participants suivent-ils la formation ?</legend>
    <div className="grid gap-2 sm:grid-cols-2">
      <label className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer ${!sansCompte ? 'border-violet-500 bg-violet-50/60' : 'border-gray-200'}`}>
        <input type="radio" name={nom} checked={!sansCompte} onChange={() => setSansCompte(false)} className="mt-1" />
        <span className="text-sm"><b className="text-gray-900">Avec un espace SkorUp</b><span className="block text-xs text-gray-500 mt-0.5">Chacun reçoit un e-mail, signe ses documents et ses émargements en ligne.</span></span>
      </label>
      <label className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer ${sansCompte ? 'border-violet-500 bg-violet-50/60' : 'border-gray-200'}`}>
        <input type="radio" name={nom} checked={sansCompte} onChange={() => setSansCompte(true)} className="mt-1" />
        <span className="text-sm"><b className="text-gray-900">Sans compte (présentiel, formation courte)</b><span className="block text-xs text-gray-500 mt-0.5">Aucun e-mail aux participants, émargement sur papier. L'e-mail devient facultatif.</span></span>
      </label>
    </div>
  </fieldset>
);

// ─── Fiche d'un groupe ────────────────────────────────────────────────────────────────────────────
const GroupeFiche = ({ groupe, groupes, groupeSeances, clients, sessions, formateurs, modules, orgSettings, actions, onRetour, ouvrirFicheClient }) => {
  const [onglet, setOnglet] = React.useState('planning');
  const [edition, setEdition] = React.useState(null);
  const [ajoutSeance, setAjoutSeance] = React.useState(null);
  const [ajoutParticipants, setAjoutParticipants] = React.useState(false);
  const [texte, setTexte] = React.useState('');
  const [choisis, setChoisis] = React.useState([]);
  const [progression, setProgression] = React.useState('');
  const [rapport, setRapport] = React.useState(null);
  const [enCours, setEnCours] = React.useState(false);
  const [confirm, setConfirm] = React.useState(null);
  const [papier, setPapier] = React.useState(null);
  const ctxConv = React.useContext(ConventionsContext) || {};

  const participants = participantsDuGroupe(clients, groupe.id).sort((a, b) => (a.nom_complet || a.nom || '').localeCompare(b.nom_complet || b.nom || '', 'fr'));
  const seances = seancesDuGroupe(groupeSeances, groupe.id);
  const today = aujourdhuiISO();
  const mod = (modules || []).find(m => String(m.id) === String(groupe.module_id));
  const formateur = (formateurs || []).find(f => String(f.id) === String(groupe.formateur_id));
  const passees = seances.filter(s => s.date && s.date < today);
  const statsParSeance = Object.fromEntries(seances.map(s => [s.id, statsEmargementSeance(s, participants, sessions)]));
  const totalAttendus = passees.reduce((n, s) => n + statsParSeance[s.id].attendus, 0);
  const totalSignes = passees.reduce((n, s) => n + statsParSeance[s.id].signes, 0);
  const enAttente = totalAttendus - totalSignes;

  // 30/10/2026 : un groupe dont tous les participants sont sans compte propose « sans compte » par défaut.
  const [sansCompteAjout, setSansCompteAjout] = React.useState(() => participantsDuGroupe(clients, groupe.id).length > 0 && participantsDuGroupe(clients, groupe.id).every(c => c.acces_differe));
  const analyse = React.useMemo(() => analyserParticipantsGroupe({ texte, choisis, clients, groupes, moduleId: groupe.module_id, groupeId: groupe.id, sansCompte: sansCompteAjout }), [texte, choisis, clients, groupes, groupe.module_id, groupe.id, sansCompteAjout]);
  const valides = analyse.filter(a => a.type === 'nouveau' || a.type === 'existant');
  const nbNouveaux = analyse.filter(a => a.type === 'nouveau').length;
  const limite = getClientLimitForOrg(orgSettings);
  const restant = Number.isFinite(limite) ? limite - (clients || []).length : Infinity;
  const depasse = Number.isFinite(restant) && nbNouveaux > restant;

  const lancerAjout = async () => {
    setEnCours(true); setRapport(null);
    try {
      const r = await actions.ajouterParticipants(groupe, valides, setProgression, sansCompteAjout);
      setRapport({ ...r, titre: 'Participants ajoutés au groupe.' });
      setTexte(''); setChoisis([]);
    } catch (e) {
      toast.error(`Les participants n'ont pas pu être ajoutés : ${e.message}`);
      setEnCours(false);
    }
  };

  const actionsFiche = { ...actions, demanderSuppressionSeance: (s) => setConfirm({
    titre: `Supprimer la séance ${s.numero} ?`,
    message: 'Elle sera retirée du planning du groupe et du dossier de chaque participant. Impossible si quelqu\'un l\'a déjà signée.',
    action: async () => { try { await actions.supprimerSeanceGroupe(s); toast.success('Séance supprimée'); } catch (e) { toast.error(e.message); } },
  }) };

  if (enCours) {
    return (
      <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
        <RapportGroupe progression={progression} rapport={rapport} onFermer={() => { setEnCours(false); setAjoutParticipants(false); setOnglet('participants'); }} />
      </div>
    );
  }

  const Kpi = ({ valeur, libelle, ton = 'gray' }) => (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
      <p className={`text-2xl font-black tabular-nums ${ton === 'amber' ? 'text-amber-600' : 'text-gray-900'}`}>{valeur}</p>
      <p className="text-xs text-gray-500 font-medium mt-0.5">{libelle}</p>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <button onClick={onRetour} className="text-gray-500 hover:text-gray-900 font-bold flex items-center transition-colors"><ChevronLeft className="w-5 h-5 mr-1" /> Retour aux groupes</button>

      <div className="bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between md:items-start gap-4">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-widest text-violet-600 mb-1">Groupe</p>
          <h2 className="text-2xl font-bold text-gray-900 break-words">{groupe.nom}</h2>
          <p className="text-gray-500 mt-1 text-sm">
            {mod ? mod.nom : 'Sans module'} · {formateur ? `Formateur : ${formateur.nom}` : 'Formateur à choisir'}
            {groupe.lieu ? ` · ${groupe.lieu}` : ''}
          </p>
          {groupe.lien_visio && /^https?:\/\//i.test(groupe.lien_visio) && (
            <a href={groupe.lien_visio} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold text-violet-700 hover:underline"><Video size={14} /> Lien visio du groupe</a>
          )}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button onClick={() => setEdition({ nom: groupe.nom || '', formateur_id: groupe.formateur_id ? String(groupe.formateur_id) : '', lieu: groupe.lieu || '', lien_visio: groupe.lien_visio || '', appliquerFormateur: true })}
            className="bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-bold py-2 px-4 rounded-xl shadow-sm text-sm flex items-center gap-2"><Pencil size={15} /> Modifier</button>
          <button onClick={() => { setOnglet('participants'); setAjoutParticipants(true); }}
            className="bg-violet-600 hover:bg-violet-700 text-white font-bold py-2 px-4 rounded-xl shadow-sm text-sm flex items-center gap-2"><UserPlus size={15} /> Ajouter des participants</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi valeur={participants.length} libelle={participants.length > 1 ? 'participants' : 'participant'} />
        <Kpi valeur={`${passees.length} / ${seances.length}`} libelle="séances réalisées" />
        <Kpi valeur={totalAttendus ? `${Math.round((totalSignes / totalAttendus) * 100)} %` : '—'} libelle="présence signée" />
        <Kpi valeur={enAttente} libelle={enAttente > 1 ? 'émargements en attente' : 'émargement en attente'} ton={enAttente ? 'amber' : 'gray'} />
      </div>

      <div className="flex gap-6 border-b border-gray-200 overflow-x-auto">
        {[['planning', 'Planning'], ['participants', `Participants (${participants.length})`], ['convention', 'Convention entreprise']].map(([k, l]) => (
          <button key={k} onClick={() => setOnglet(k)} className={`pb-3 px-1 font-bold text-sm whitespace-nowrap border-b-2 transition-colors ${onglet === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{l}</button>
        ))}
      </div>

      {onglet === 'planning' && (
        <div className="space-y-4">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm divide-y divide-gray-100 overflow-hidden">
            {seances.length === 0 && <p className="p-6 text-sm text-gray-500">Aucune séance pour l'instant.</p>}
            {seances.map(s => (
              <LigneSeanceGroupe key={s.id} seance={s} groupe={groupe} formateurs={formateurs} stats={statsParSeance[s.id]} actions={actionsFiche} onPapier={participants.length ? () => setPapier(s) : null}
                estAujourdhui={s.date === today} estPassee={!!s.date && s.date < today} />
            ))}
          </div>
          {ajoutSeance ? (
            <div className="bg-white rounded-3xl border border-violet-100 shadow-sm p-4 flex flex-wrap items-end gap-3">
              <div className="min-w-0 flex-1 sm:flex-none sm:w-56"><label htmlFor="ns-t" className="block text-[11px] font-bold text-gray-500 mb-1">Titre</label><input id="ns-t" value={ajoutSeance.titre} onChange={e => setAjoutSeance(p => ({ ...p, titre: e.target.value }))} placeholder="Ex : Séance de rattrapage" className="w-full px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
              <div><label htmlFor="ns-d" className="block text-[11px] font-bold text-gray-500 mb-1">Date</label><input id="ns-d" type="date" value={ajoutSeance.date} onChange={e => setAjoutSeance(p => ({ ...p, date: e.target.value }))} className="px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
              <div><label htmlFor="ns-hd" className="block text-[11px] font-bold text-gray-500 mb-1">Début</label><input id="ns-hd" type="time" value={ajoutSeance.heure_debut} onChange={e => setAjoutSeance(p => ({ ...p, heure_debut: e.target.value }))} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
              <div><label htmlFor="ns-hf" className="block text-[11px] font-bold text-gray-500 mb-1">Fin</label><input id="ns-hf" type="time" value={ajoutSeance.heure_fin} onChange={e => setAjoutSeance(p => ({ ...p, heure_fin: e.target.value }))} className="w-28 px-2 py-2 rounded-lg border border-gray-200 text-sm" /></div>
              <label className="flex items-center gap-1.5 py-2 text-xs font-bold text-gray-600 cursor-pointer select-none" title="Une feuille d'émargement est ajoutée au dossier de chaque participant si la case est cochée">
                <input type="checkbox" checked={ajoutSeance.emargement !== false} onChange={e => setAjoutSeance(p => ({ ...p, emargement: e.target.checked }))} className="rounded text-violet-600" />
                Feuille d'émargement
              </label>
              <div className="flex gap-2">
                <button onClick={() => setAjoutSeance(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-600">Annuler</button>
                <button disabled={ajoutSeance.enCours} onClick={async () => {
                  setAjoutSeance(p => ({ ...p, enCours: true }));
                  try { await actions.ajouterSeanceGroupe(groupe, ajoutSeance); toast.success('Séance ajoutée pour tout le groupe'); setAjoutSeance(null); }
                  catch (e) { toast.error(`La séance n'a pas pu être ajoutée : ${e.message}`); setAjoutSeance(p => ({ ...p, enCours: false })); }
                }} className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold disabled:opacity-50">{ajoutSeance.enCours ? 'Ajout…' : 'Ajouter la séance'}</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAjoutSeance({ titre: '', date: '', heure_debut: '', heure_fin: '', emargement: true })} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-50 text-violet-700 font-bold text-sm hover:bg-violet-100"><CalendarPlus size={16} /> Ajouter une séance</button>
          )}
          <p className="text-xs text-gray-500">Une modification ici s'applique au dossier de chaque participant (son espace, ses rappels, le calendrier).</p>
        </div>
      )}

      {onglet === 'participants' && (
        <div className="space-y-4">
          {ajoutParticipants && (
            <div className="bg-white rounded-3xl border border-violet-100 shadow-sm p-5 md:p-6 space-y-4">
              <p className="font-black text-gray-900">Ajouter des participants</p>
              <ChoixAccesParticipants sansCompte={sansCompteAjout} setSansCompte={setSansCompteAjout} nom="acces-ajout" />
              <SaisieParticipantsGroupe texte={texte} setTexte={setTexte} choisis={choisis} setChoisis={setChoisis} clients={clients} analyse={analyse} groupeId={groupe.id} sansCompte={sansCompteAjout} />
              {depasse && <p className="text-sm text-red-600">Votre offre ne permet pas d'ajouter {nbNouveaux} nouveaux dossiers (il en reste {Math.max(0, restant)}).</p>}
              <div className="flex flex-wrap gap-2 justify-end">
                <button onClick={() => { setAjoutParticipants(false); setTexte(''); setChoisis([]); }} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Annuler</button>
                <button disabled={valides.length === 0 || depasse} onClick={lancerAjout} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40">Ajouter {valides.length || ''} participant{valides.length > 1 ? 's' : ''}</button>
              </div>
            </div>
          )}
          {participants.length === 0 ? (
            <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-8 text-center">
              <p className="font-bold text-gray-800">Aucun participant pour l'instant</p>
              <p className="text-sm text-gray-500 mt-1">Ajoutez-les en collant une liste de noms et d'e-mails.</p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <ul className="divide-y divide-gray-100">
                {participants.map(p => {
                  const signes = seances.filter(s => {
                    const st = statsEmargementSeance(s, [p], sessions);
                    return st.attendus > 0 && st.signes === st.attendus;
                  }).length;
                  const pct = seances.length ? Math.round((signes / seances.length) * 100) : 0;
                  return (
                    <li key={p.id} className="px-4 md:px-6 py-3.5 flex flex-wrap items-center gap-3 justify-between">
                      <button onClick={() => ouvrirFicheClient(p.id)} className="flex items-center gap-3 min-w-0 text-left group">
                        <span className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-black shrink-0">{(p.nom_complet || p.nom || '?').split(/\s+/).map(x => x[0]).slice(0, 2).join('').toUpperCase()}</span>
                        <span className="min-w-0">
                          <span className="block font-bold text-sm text-gray-900 group-hover:text-violet-700 truncate">{p.nom_complet || p.nom}</span>
                          <span className="block text-xs text-gray-500 truncate">{p.email_contact || p.email || 'Sans e-mail'}{p.numero_dossier ? ` · Dossier ${p.numero_dossier}` : ''}{p.acces_differe ? <span className="ml-1.5 text-[10px] font-black px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">Sans compte</span> : null}</span>
                        </span>
                      </button>
                      <div className="flex items-center gap-3">
                        <div className="w-28 hidden sm:block">
                          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                          <p className="text-[11px] text-gray-500 mt-1">{signes}/{seances.length} séances signées</p>
                        </div>
                        <button onClick={() => setConfirm({
                          titre: `Retirer ${p.nom_complet || p.nom} du groupe ?`,
                          message: 'Son dossier, ses séances, ses dates et ses signatures sont conservés : il redevient un client suivi individuellement.',
                          libelle: 'Retirer',
                          action: async () => { try { await actions.retirerDuGroupe(p.id); toast.success('Participant retiré du groupe'); } catch (e) { toast.error(e.message); } },
                        })} className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:text-red-600 hover:border-red-200 inline-flex items-center gap-1.5"><UserMinus size={14} /> Retirer</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {onglet === 'convention' && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 md:p-6">
          <BlocConventionsEntreprise groupe={groupe} participants={participants} seances={seances} />
        </div>
      )}

      {papier && (
        <EmargementPapierModal groupe={groupe} seance={seancesDuGroupe(groupeSeances, groupe.id).find(x => x.id === papier.id) || papier} participants={participants} sessions={sessions}
          formateurNom={(formateurs || []).find(f => String(f.id) === String(papier.formateur_id || groupe.formateur_id))?.nom || ''}
          orgNom={orgSettings?.nom || ''} nomUtilisateur={ctxConv.nomUtilisateur} onClose={() => setPapier(null)}
          onEnregistre={() => actions.rafraichir && actions.rafraichir()} />
      )}

      {edition && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setEdition(null)}>
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <p className="text-lg font-black text-gray-900">Modifier le groupe</p>
            <div><label htmlFor="eg-nom" className={libelleGroupe}>Nom du groupe</label><input id="eg-nom" value={edition.nom} onChange={e => setEdition(p => ({ ...p, nom: e.target.value }))} className={champGroupe} /></div>
            <div>
              <label htmlFor="eg-f" className={libelleGroupe}>Formateur du groupe</label>
              <select id="eg-f" value={edition.formateur_id} onChange={e => setEdition(p => ({ ...p, formateur_id: e.target.value }))} className={champGroupe}>
                <option value="">À choisir plus tard</option>
                {(formateurs || []).map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
              </select>
              {String(edition.formateur_id || '') !== String(groupe.formateur_id || '') && edition.formateur_id && participants.length > 0 && (
                <label className="flex items-start gap-2 mt-2 text-sm text-gray-700"><input type="checkbox" className="mt-1" checked={edition.appliquerFormateur} onChange={e => setEdition(p => ({ ...p, appliquerFormateur: e.target.checked }))} /> En faire aussi le formateur référent des {participants.length} participants</label>
              )}
            </div>
            <div><label htmlFor="eg-l" className={libelleGroupe}>Lieu</label><input id="eg-l" value={edition.lieu} onChange={e => setEdition(p => ({ ...p, lieu: e.target.value }))} className={champGroupe} /></div>
            <div><label htmlFor="eg-v" className={libelleGroupe}>Lien visio</label><input id="eg-v" type="url" value={edition.lien_visio} onChange={e => setEdition(p => ({ ...p, lien_visio: e.target.value }))} className={champGroupe} /></div>
            <p className="text-xs text-gray-500">Module : {mod ? mod.nom : 'aucun'} (il ne peut pas être changé une fois le groupe créé).</p>
            <div className="flex flex-wrap justify-between gap-2 pt-2">
              <button onClick={() => { setEdition(null); setConfirm({
                titre: `Supprimer le groupe « ${groupe.nom} » ?`,
                message: 'Aucun dossier n\'est supprimé : les participants redeviennent des clients individuels, avec leurs séances, dates et signatures.',
                action: async () => { try { await actions.supprimerGroupe(groupe); toast.success('Groupe supprimé'); onRetour(); } catch (e) { toast.error(e.message); } },
              }); }} className="px-4 py-2.5 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50">Supprimer le groupe</button>
              <div className="flex gap-2">
                <button onClick={() => setEdition(null)} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Annuler</button>
                <button disabled={!edition.nom.trim() || edition.enCours} onClick={async () => {
                  setEdition(p => ({ ...p, enCours: true }));
                  try { await actions.majGroupe(groupe, edition); toast.success('Groupe mis à jour'); setEdition(null); }
                  catch (e) { toast.error(`Le groupe n'a pas pu être modifié : ${e.message}`); setEdition(p => ({ ...p, enCours: false })); }
                }} className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-40">{edition.enCours ? 'Enregistrement…' : 'Enregistrer'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={!!confirm} title={confirm?.titre} message={confirm?.message} confirmLabel={confirm?.libelle || 'Supprimer'}
        onCancel={() => setConfirm(null)} onConfirm={async () => { const c = confirm; setConfirm(null); await c.action(); }} />
    </div>
  );
};

// ─── Page « Groupes » (liste, création, fiche) ───────────────────────────────────────────────────
const GroupesView = ({ groupes, groupeSeances, groupesDispo, clients, sessions, formateurs, modules, moduleSessionTemplates, orgSettings, selectedGroupeId, setSelectedGroupeId, actions, ouvrirFicheClient }) => {
  const [creation, setCreation] = React.useState(false);
  const groupe = (groupes || []).find(g => String(g.id) === String(selectedGroupeId));

  if (!groupesDispo) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-gray-100 shadow-sm p-8 space-y-2">
        <h1 className="text-2xl font-black text-gray-900">Groupes</h1>
        <p className="text-gray-600">Les formations de groupe ne sont pas encore activées sur votre espace. Elles le seront après la prochaine mise à jour de la base de données.</p>
      </div>
    );
  }
  if (creation) {
    return <GroupeCreation clients={clients} groupes={groupes} formateurs={formateurs} modules={modules} moduleSessionTemplates={moduleSessionTemplates}
      orgSettings={orgSettings} actions={actions} onAnnuler={() => setCreation(false)}
      onCree={(id) => { setCreation(false); setSelectedGroupeId(id || null); }} />;
  }
  if (groupe) {
    return <GroupeFiche groupe={groupe} groupes={groupes} groupeSeances={groupeSeances} clients={clients} sessions={sessions} formateurs={formateurs}
      modules={modules} orgSettings={orgSettings} actions={actions} onRetour={() => setSelectedGroupeId(null)} ouvrirFicheClient={ouvrirFicheClient} />;
  }

  const today = aujourdhuiISO();
  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Groupes</h1>
          <p className="text-gray-500 text-sm mt-1">Formations collectives : un planning commun, et chaque participant garde son propre dossier.</p>
        </div>
        <button onClick={() => setCreation(true)} className="inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-bold py-2.5 px-5 rounded-xl shadow-sm text-sm"><Plus size={16} /> Nouveau groupe</button>
      </div>

      {(groupes || []).length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-10 text-center max-w-2xl mx-auto">
          <div className="w-12 h-12 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center mx-auto mb-3"><UsersRound size={24} /></div>
          <p className="font-black text-gray-900 text-lg">Aucun groupe pour l'instant</p>
          <p className="text-sm text-gray-500 mt-1 mb-5">Créez un groupe pour inscrire plusieurs participants d'un coup et saisir les dates une seule fois.</p>
          <button onClick={() => setCreation(true)} className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-bold py-2.5 px-5 rounded-xl text-sm"><Plus size={16} /> Créer mon premier groupe</button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groupes.map(g => {
            const parts = participantsDuGroupe(clients, g.id);
            const ss = seancesDuGroupe(groupeSeances, g.id);
            const prochaine = ss.filter(s => s.date && s.date >= today).sort((a, b) => a.date.localeCompare(b.date))[0];
            const faites = ss.filter(s => s.date && s.date < today).length;
            const mod = (modules || []).find(m => String(m.id) === String(g.module_id));
            const f = (formateurs || []).find(x => String(x.id) === String(g.formateur_id));
            return (
              <button key={g.id} onClick={() => setSelectedGroupeId(g.id)} className="text-left bg-white rounded-3xl border border-gray-100 shadow-sm p-5 hover:border-violet-200 hover:shadow-md transition-all">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-black text-gray-900 text-lg leading-snug break-words">{g.nom}</p>
                  <span className="text-[11px] font-black px-2 py-1 rounded-lg bg-violet-50 text-violet-700 shrink-0">{parts.length} participant{parts.length > 1 ? 's' : ''}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{mod ? mod.nom : 'Sans module'}{f ? ` · ${f.nom}` : ''}</p>
                <div className="mt-4 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${ss.length ? Math.round((faites / ss.length) * 100) : 0}%` }} /></div>
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-500">
                  <span>{faites}/{ss.length} séances réalisées</span>
                  <span className="font-bold text-gray-700">{prochaine ? `Prochaine : ${dateCourte(prochaine.date)}${prochaine.heure_debut ? ` · ${heureCourte(prochaine.heure_debut)}` : ''}` : (ss.length ? 'Terminé ou sans date' : '')}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// DOCUMENTS ENVOYÉS PAR LES CLIENTS + PIÈCES À FOURNIR (2026-10-13)
// Le client envoie un document (justificatif, attestation, travail…) à son formateur et/ou à
// l'organisme depuis « Mes documents ». Admin et formateur le retrouvent dans l'onglet « Reçus du
// client » de la fiche. Offre Pro : chaque organisme crée SA liste de pièces (aucune par défaut),
// les demande à un client et suit leur réception (à fournir → reçue → validée / à refaire).
// Tables : depots_clients, pieces_catalogue, pieces_demandees (voir depots_etape1.sql).
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const DEPOT_TAILLE_MAX = 10 * 1024 * 1024;
const DEPOT_CATEGORIES = ["Pièce d'identité", 'Justificatif', 'Attestation', 'CV', 'Travail demandé', 'Autre'];
const DEPOT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx,.odt,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.txt,.rtf';
const DEPOT_DESTINATAIRES = { formateur: 'Mon formateur', organisme: "L'organisme", les_deux: 'Formateur et organisme' };
const DEPOT_DESTINATAIRES_STAFF = { formateur: 'Pour le formateur', organisme: "Pour l'organisme", les_deux: 'Pour le formateur et l\'organisme' };
const PIECE_STATUTS = {
  a_fournir: { label: 'À fournir', cls: 'bg-amber-50 text-amber-700' },
  recue: { label: 'Reçue', cls: 'bg-blue-50 text-blue-700' },
  validee: { label: 'Validée', cls: 'bg-emerald-50 text-emerald-700' },
  a_refaire: { label: 'À refaire', cls: 'bg-red-50 text-red-600' },
};
const tailleLisible = (o) => (!o ? '' : o < 1024 * 1024 ? `${Math.max(1, Math.round(o / 1024))} Ko` : `${(o / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`);
const dateHeureFr = (d) => (d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const nomFichierSur = (nom) => String(nom || 'document').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(-80);
const cheminStockage = (url) => {
  const m = String(url || '').match(/\/storage\/v1\/object\/(?:public|sign)\/([^/?]+)\/(.+?)(?:\?|$)/);
  return m ? { bucket: m[1], path: decodeURIComponent(m[2]) } : null;
};

// Réduit une photo prise au téléphone avant l'envoi (2000 px maximum, JPEG qualité 0,8).
const compresserImageDepot = async (file) => {
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type) || file.size < 1.5 * 1024 * 1024) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((ok, ko) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ko; i.src = url; });
    const ratio = Math.min(1, 2000 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * ratio);
    canvas.height = Math.round(img.height * ratio);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise(ok => canvas.toBlob(ok, 'image/jpeg', 0.8));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
};

// E-mail au formateur et/ou à l'admin (voir api/formateur/notify.js, type « depot_client »).
const notifierDepotClient = (depotId) => {
  supabase.auth.getSession().then(({ data }) => {
    const token = data?.session?.access_token;
    if (!token) return;
    fetch('/api/formateur/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ type: 'depot_client', depotId, origin: window.location.origin }),
    }).catch(() => {});
  }).catch(() => {});
};

// ─── Fenêtre d'envoi d'un document (côté client) ─────────────────────────────────────────────────
const EnvoiDocumentModal = ({ isOpen, onClose, piece, aUnFormateur, onEnvoyer }) => {
  const [fichier, setFichier] = React.useState(null);
  const [titre, setTitre] = React.useState('');
  const [categorie, setCategorie] = React.useState('');
  const [destinataire, setDestinataire] = React.useState('les_deux');
  const [message, setMessage] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState('');
  const [survol, setSurvol] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setFichier(null); setMessage(''); setErreur(''); setEnCours(false);
    setTitre(piece ? piece.nom : '');
    setCategorie(piece ? 'Justificatif' : '');
    setDestinataire(aUnFormateur ? 'les_deux' : 'organisme');
  }, [isOpen, piece, aUnFormateur]);

  if (!isOpen) return null;

  const choisir = (f) => {
    if (!f) return;
    setErreur('');
    setFichier(f);
    if (!piece && !titre.trim()) setTitre(f.name.replace(/\.[^.]+$/, ''));
  };
  const envoyer = async () => {
    if (!fichier) { setErreur('Choisissez un fichier.'); return; }
    if (!titre.trim()) { setErreur('Donnez un titre à votre document.'); return; }
    setEnCours(true); setErreur('');
    try {
      await onEnvoyer({ fichier, titre: titre.trim(), categorie: categorie || null, destinataire, message: message.trim() || null, piece_id: piece?.id || null });
      onClose();
    } catch (e) {
      setErreur(e.message || 'Le document n\'a pas pu être envoyé.');
      setEnCours(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => !enCours && onClose()}>
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="bg-violet-600 p-6 text-white relative">
          <p className="text-violet-200 text-xs font-black uppercase tracking-widest mb-1">{piece ? 'Pièce demandée' : 'Nouveau document'}</p>
          <h2 className="text-lg font-black leading-tight">{piece ? piece.nom : 'Envoyer un document'}</h2>
          {piece?.description && <p className="text-sm text-violet-100 mt-1">{piece.description}</p>}
          <button onClick={onClose} disabled={enCours} aria-label="Fermer" className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20">✕</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div
            onDragOver={e => { e.preventDefault(); setSurvol(true); }}
            onDragLeave={() => setSurvol(false)}
            onDrop={e => { e.preventDefault(); setSurvol(false); choisir(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-5 text-center transition-colors ${survol ? 'border-violet-500 bg-violet-50' : 'border-gray-200 bg-gray-50 hover:border-violet-300'}`}>
            <input ref={inputRef} type="file" accept={DEPOT_ACCEPT} className="hidden" onChange={e => choisir(e.target.files?.[0])} />
            {fichier ? (
              <div>
                <p className="font-bold text-gray-900 break-all">📎 {fichier.name}</p>
                <p className="text-xs text-gray-500 mt-1">{tailleLisible(fichier.size)} · cliquez pour changer</p>
              </div>
            ) : (
              <div>
                <p className="font-bold text-gray-800"><Upload size={18} className="inline mr-1.5 -mt-0.5" />Choisir un fichier</p>
                <p className="text-xs text-gray-500 mt-1">PDF, photo, Word… 10 Mo maximum. Sur téléphone, vous pouvez prendre une photo.</p>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="depot-titre" className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5">Titre</label>
            <input id="depot-titre" value={titre} onChange={e => setTitre(e.target.value)} placeholder="Ex : Attestation employeur" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          {!piece && (
            <div>
              <label htmlFor="depot-cat" className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5">Type de document</label>
              <select id="depot-cat" value={categorie} onChange={e => setCategorie(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500">
                <option value="">Choisir…</option>
                {DEPOT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div>
            <p className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5">Envoyer à</p>
            <div className="grid gap-2 sm:grid-cols-3">
              {(aUnFormateur ? ['formateur', 'organisme', 'les_deux'] : ['organisme']).map(d => (
                <button key={d} type="button" onClick={() => setDestinataire(d)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${destinataire === d ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300'}`}>{DEPOT_DESTINATAIRES[d]}</button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="depot-msg" className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5">Message (facultatif)</label>
            <textarea id="depot-msg" value={message} onChange={e => setMessage(e.target.value)} rows={2} placeholder="Ex : voici mon attestation signée" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          {erreur && <p className="text-sm font-bold text-red-600 bg-red-50 rounded-xl px-3 py-2">{erreur}</p>}
        </div>
        <div className="p-6 pt-0 flex gap-3">
          <button onClick={onClose} disabled={enCours} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50">Annuler</button>
          <button onClick={envoyer} disabled={enCours} className="flex-1 py-3 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 disabled:opacity-50">{enCours ? 'Envoi…' : 'Envoyer'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── « Mes documents » côté client : envoyer, pièces demandées, historique ──────────────────────
const MesEnvoisClient = ({ currentUserId, client }) => {
  const [dispo, setDispo] = React.useState(true);
  const [depots, setDepots] = React.useState([]);
  const [pieces, setPieces] = React.useState([]);
  const [modal, setModal] = React.useState(null); // null | { piece }
  const [aRetirer, setARetirer] = React.useState(null);

  const charger = React.useCallback(async () => {
    if (!currentUserId) return;
    const [d, p] = await Promise.all([
      supabase.from('depots_clients').select('*').eq('client_id', currentUserId).order('created_at', { ascending: false }),
      supabase.from('pieces_demandees').select('*').eq('client_id', currentUserId).order('demande_le', { ascending: true }),
    ]);
    if (d.error) { console.warn('[depots] non disponibles :', d.error.message); setDispo(false); return; }
    setDispo(true);
    setDepots(d.data || []);
    setPieces(p.error ? [] : (p.data || []));
  }, [currentUserId]);
  React.useEffect(() => { charger(); }, [charger]);

  if (!dispo || !client) return null;

  const envoyer = async ({ fichier, titre, categorie, destinataire, message, piece_id }) => {
    const f = await compresserImageDepot(fichier);
    if (f.size > DEPOT_TAILLE_MAX) throw new Error(`Ce fichier fait ${tailleLisible(f.size)} : la limite est de 10 Mo. Réduisez-le ou envoyez une photo.`);
    const chemin = `depots/${currentUserId}/${Date.now()}_${nomFichierSur(f.name)}`;
    const { error: upErr } = await supabase.storage.from('documents').upload(chemin, f, { contentType: f.type || undefined, upsert: false });
    if (upErr) throw new Error(`Le fichier n'a pas pu être envoyé : ${upErr.message}`);
    const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(chemin);
    const { data: ligne, error } = await supabase.from('depots_clients').insert([{
      organisation_id: client.organisation_id, client_id: currentUserId, titre, categorie, message, destinataire,
      fichier_url: publicUrl, fichier_nom: f.name, taille: f.size, piece_id,
    }]).select().single();
    if (error) {
      await supabase.storage.from('documents').remove([chemin]).catch(() => {});
      throw new Error(`Le document n'a pas pu être enregistré : ${error.message}`);
    }
    notifierDepotClient(ligne.id);
    toast.success('Document envoyé. Votre interlocuteur est prévenu par e-mail.');
    await charger();
  };

  const retirer = async (depot) => {
    const { data, error } = await supabase.from('depots_clients').delete().eq('id', depot.id).select('id');
    if (error || !data || data.length === 0) {
      toast.error('Ce document a déjà été consulté : il ne peut plus être retiré.');
      await charger();
      return;
    }
    const ch = cheminStockage(depot.fichier_url);
    if (ch) await supabase.storage.from(ch.bucket).remove([ch.path]).catch(() => {});
    toast.success('Document retiré.');
    await charger();
  };

  const aFournir = pieces.filter(p => p.statut === 'a_fournir' || p.statut === 'a_refaire');

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-2 h-6 bg-amber-500 rounded-full mt-0.5 shrink-0"></div>
          <div>
            <h2 className="font-bold text-gray-900 text-lg">Envoyer un document</h2>
            <p className="text-sm text-gray-500">Transmettez un justificatif, une attestation ou un travail à votre formateur ou à votre organisme.</p>
          </div>
        </div>
        <button onClick={() => setModal({ piece: null })} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 shrink-0"><Upload size={16} /> Envoyer un document</button>
      </div>

      {pieces.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="font-bold text-gray-900">Pièces demandées</h3>
            {aFournir.length > 0 && <span className="text-xs font-black px-2 py-1 rounded-lg bg-amber-50 text-amber-700">{aFournir.length} à fournir</span>}
          </div>
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-2xl">
            {pieces.map(p => (
              <li key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-sm text-gray-900">{p.nom}</p>
                  {p.description && <p className="text-xs text-gray-500">{p.description}</p>}
                  {p.statut === 'a_refaire' && p.motif && <p className="text-xs text-red-600 mt-0.5">À refaire : {p.motif}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-black px-2 py-1 rounded-lg ${PIECE_STATUTS[p.statut || 'a_fournir']?.cls || ''}`}>{PIECE_STATUTS[p.statut || 'a_fournir']?.label || p.statut}</span>
                  {(p.statut === 'a_fournir' || p.statut === 'a_refaire') && (
                    <button onClick={() => setModal({ piece: p })} className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-bold hover:bg-violet-700">Envoyer</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {depots.length > 0 && (
        <div>
          <h3 className="font-bold text-gray-900 mb-2">Mes envois</h3>
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-2xl">
            {depots.map(d => (
              <li key={d.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-sm text-gray-900 break-words">{d.titre}</p>
                  <p className="text-xs text-gray-500">{[d.categorie, dateHeureFr(d.created_at), DEPOT_DESTINATAIRES[d.destinataire], tailleLisible(d.taille)].filter(Boolean).join(' · ')}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {d.consulte_le
                    ? <span className="text-xs font-black px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700">Consulté</span>
                    : <span className="text-xs font-black px-2 py-1 rounded-lg bg-gray-100 text-gray-600">Envoyé</span>}
                  <button onClick={() => openSecureStorageFile(d.fichier_url)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:border-violet-300">Voir</button>
                  {!d.consulte_le && <button onClick={() => setARetirer(d)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-500 hover:text-red-600 hover:border-red-200">Retirer</button>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <EnvoiDocumentModal isOpen={!!modal} piece={modal?.piece || null} aUnFormateur={!!client.formateur_id}
        onClose={() => setModal(null)} onEnvoyer={envoyer} />
      <ConfirmModal isOpen={!!aRetirer} title="Retirer ce document ?" message="Il sera supprimé. Vous pourrez en envoyer un autre."
        confirmLabel="Retirer" onCancel={() => setARetirer(null)} onConfirm={async () => { const d = aRetirer; setARetirer(null); await retirer(d); }} />
    </div>
  );
};

// ─── Liste des pièces de l'organisme (admin) ─────────────────────────────────────────────────────
const CataloguePiecesModal = ({ isOpen, onClose, currentOrgId, catalogue, onChange }) => {
  const [nom, setNom] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [edition, setEdition] = React.useState(null);
  if (!isOpen) return null;
  const ajouter = async () => {
    if (!nom.trim()) return;
    const { error } = await supabase.from('pieces_catalogue').insert([{ organisation_id: currentOrgId, nom: nom.trim(), description: description.trim() || null, ordre: (catalogue || []).length + 1 }]);
    if (error) { toast.error(`La pièce n'a pas pu être ajoutée : ${error.message}`); return; }
    setNom(''); setDescription('');
    await onChange();
  };
  const enregistrer = async () => {
    const { error } = await supabase.from('pieces_catalogue').update({ nom: edition.nom.trim(), description: (edition.description || '').trim() || null }).eq('id', edition.id);
    if (error) { toast.error(`Modification impossible : ${error.message}`); return; }
    setEdition(null);
    await onChange();
  };
  const supprimer = async (p) => {
    const { error } = await supabase.from('pieces_catalogue').delete().eq('id', p.id);
    if (error) { toast.error(`Suppression impossible : ${error.message}`); return; }
    await onChange();
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-black text-gray-900">Ma liste de pièces</p>
            <p className="text-sm text-gray-500">Les pièces que votre organisme demande habituellement. Vous les cochez ensuite dans la fiche de chaque client.</p>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200">✕</button>
        </div>
        {(catalogue || []).length === 0 ? (
          <p className="text-sm text-gray-500 bg-gray-50 rounded-2xl p-4">Votre liste est vide. Ajoutez ci-dessous les pièces dont vous avez besoin.</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-2xl">
            {catalogue.map(p => (
              <li key={p.id} className="p-3">
                {edition?.id === p.id ? (
                  <div className="space-y-2">
                    <input aria-label="Nom de la pièce" value={edition.nom} onChange={e => setEdition(x => ({ ...x, nom: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                    <input aria-label="Explication pour le client" value={edition.description || ''} onChange={e => setEdition(x => ({ ...x, description: e.target.value }))} placeholder="Explication pour le client (facultatif)" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEdition(null)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold">Annuler</button>
                      <button onClick={enregistrer} disabled={!edition.nom.trim()} className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-bold disabled:opacity-40">Enregistrer</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-gray-900">{p.nom}</p>
                      {p.description && <p className="text-xs text-gray-500">{p.description}</p>}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => setEdition({ ...p })} title="Modifier" className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:text-violet-600"><Pencil size={14} /></button>
                      <button onClick={() => supprimer(p)} title="Supprimer" className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="bg-violet-50 rounded-2xl p-4 space-y-2">
          <p className="text-xs font-black text-violet-800 uppercase tracking-wider">Ajouter une pièce</p>
          <input aria-label="Nom de la nouvelle pièce" value={nom} onChange={e => setNom(e.target.value)} placeholder="Nom de la pièce" className="w-full px-3 py-2 rounded-lg border border-violet-200 text-sm bg-white" />
          <input aria-label="Explication de la nouvelle pièce" value={description} onChange={e => setDescription(e.target.value)} placeholder="Explication pour le client (facultatif)" className="w-full px-3 py-2 rounded-lg border border-violet-200 text-sm bg-white" />
          <div className="flex justify-end"><button onClick={ajouter} disabled={!nom.trim()} className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold disabled:opacity-40">Ajouter à ma liste</button></div>
        </div>
      </div>
    </div>
  );
};

// ─── Onglet « Reçus du client » (admin et formateur) ─────────────────────────────────────────────
const DepotsClientPanel = ({ client, currentOrgId, userRole, estPro, nomUtilisateur, onVu }) => {
  const [dispo, setDispo] = React.useState(true);
  const [depots, setDepots] = React.useState([]);
  const [pieces, setPieces] = React.useState([]);
  const [catalogue, setCatalogue] = React.useState([]);
  const [catalogueOuvert, setCatalogueOuvert] = React.useState(false);
  const [choix, setChoix] = React.useState([]);
  const [ponctuelle, setPonctuelle] = React.useState('');
  const [refaire, setRefaire] = React.useState(null); // { piece, motif }
  const [confirm, setConfirm] = React.useState(null);

  const charger = React.useCallback(async () => {
    if (!client?.id) return;
    const [d, p, c] = await Promise.all([
      supabase.from('depots_clients').select('*').eq('client_id', client.id).order('created_at', { ascending: false }),
      supabase.from('pieces_demandees').select('*').eq('client_id', client.id).order('demande_le', { ascending: true }),
      currentOrgId ? supabase.from('pieces_catalogue').select('*').eq('organisation_id', currentOrgId).order('ordre', { ascending: true }) : Promise.resolve({ data: [] }),
    ]);
    if (d.error) { console.warn('[depots] non disponibles :', d.error.message); setDispo(false); return; }
    setDispo(true);
    setDepots(d.data || []);
    setPieces(p.error ? [] : (p.data || []));
    setCatalogue(c.error ? [] : (c.data || []));
  }, [client?.id, currentOrgId]);
  React.useEffect(() => { charger(); }, [charger]);

  if (!dispo) {
    return <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm text-gray-500 text-sm">L'envoi de documents par les clients n'est pas encore activé sur votre espace.</div>;
  }

  const marquerConsulte = async (d) => {
    if (d.consulte_le) return;
    await supabase.from('depots_clients').update({ consulte_le: new Date().toISOString(), consulte_par: nomUtilisateur || null }).eq('id', d.id);
    await charger();
    if (onVu) onVu();
  };
  const ouvrir = async (d) => { await openSecureStorageFile(d.fichier_url); marquerConsulte(d); };
  const telecharger = async (d) => { await handleDownloadNamedFile({ url: d.fichier_url, nom: d.titre }, client?.nom_complet || client?.nom); marquerConsulte(d); };
  const supprimerDepot = async (d) => {
    const { error } = await supabase.from('depots_clients').delete().eq('id', d.id);
    if (error) { toast.error(`Suppression impossible : ${error.message}`); return; }
    const ch = cheminStockage(d.fichier_url);
    if (ch) await supabase.storage.from(ch.bucket).remove([ch.path]).catch(() => {});
    toast.success('Document supprimé.');
    await charger();
    if (onVu) onVu();
  };

  const demander = async (lignes) => {
    if (lignes.length === 0) return;
    const { error } = await supabase.from('pieces_demandees').insert(lignes.map(l => ({ organisation_id: currentOrgId, client_id: client.id, statut: 'a_fournir', demande_le: new Date().toISOString(), ...l })));
    if (error) { toast.error(`La demande n'a pas pu être enregistrée : ${error.message}`); return; }
    notifyClientByEmail(client.id, lignes.length > 1 ? `${lignes.length} pièces à fournir` : `Pièce à fournir : ${lignes[0].nom}`);
    toast.success(lignes.length > 1 ? `${lignes.length} pièces demandées au client` : 'Pièce demandée au client');
    setChoix([]); setPonctuelle('');
    await charger();
  };
  const changerStatut = async (p, statut, motif = null) => {
    const { error } = await supabase.from('pieces_demandees').update({ statut, motif, traite_le: new Date().toISOString() }).eq('id', p.id);
    if (error) { toast.error(`Modification impossible : ${error.message}`); return; }
    await charger();
  };
  const supprimerPiece = async (p) => {
    const { error } = await supabase.from('pieces_demandees').delete().eq('id', p.id);
    if (error) { toast.error(`Suppression impossible : ${error.message}`); return; }
    await charger();
  };

  const dejaDemandees = new Set(pieces.map(p => String(p.catalogue_id || '')));
  const proposables = catalogue.filter(c => !dejaDemandees.has(String(c.id)));
  const nonLus = depots.filter(d => !d.consulte_le).length;

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Documents envoyés par le client</h3>
            <p className="text-sm text-gray-500">Ouvrir un document le marque comme consulté pour le client.</p>
          </div>
          {nonLus > 0 && <span className="text-xs font-black px-2 py-1 rounded-lg bg-violet-600 text-white shrink-0">{nonLus} nouveau{nonLus > 1 ? 'x' : ''}</span>}
        </div>
        {depots.length === 0 ? (
          <p className="text-sm text-gray-400 bg-gray-50 rounded-2xl p-5 text-center">Aucun document reçu pour l'instant.</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-2xl">
            {depots.map(d => (
              <li key={d.id} className={`p-4 flex flex-wrap items-start justify-between gap-3 ${d.consulte_le ? '' : 'bg-violet-50/50'}`}>
                <div className="min-w-0">
                  <p className="font-bold text-sm text-gray-900 break-words flex items-center gap-2 flex-wrap">
                    {d.titre}
                    {!d.consulte_le && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-violet-600 text-white">NOUVEAU</span>}
                    {d.piece_id && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">PIÈCE DEMANDÉE</span>}
                  </p>
                  <p className="text-xs text-gray-500">{[d.categorie, `reçu le ${dateHeureFr(d.created_at)}`, DEPOT_DESTINATAIRES_STAFF[d.destinataire], tailleLisible(d.taille)].filter(Boolean).join(' · ')}</p>
                  {d.message && <p className="text-sm text-gray-700 mt-1.5 bg-gray-50 rounded-lg px-3 py-2">« {d.message} »</p>}
                  {d.consulte_le && <p className="text-[11px] text-gray-400 mt-1">Consulté le {dateHeureFr(d.consulte_le)}{d.consulte_par ? ` par ${d.consulte_par}` : ''}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => ouvrir(d)} className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-bold hover:bg-violet-700">Ouvrir</button>
                  <button onClick={() => telecharger(d)} title="Télécharger" className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:text-violet-600"><Download size={14} /></button>
                  <button onClick={() => setConfirm({ titre: 'Supprimer ce document ?', message: 'Le fichier sera définitivement supprimé.', action: () => supprimerDepot(d) })} title="Supprimer" className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">Pièces à fournir {!estPro && <span className="text-[11px] font-black bg-violet-500 text-white px-1.5 py-0.5 rounded-md">PRO</span>}</h3>
            <p className="text-sm text-gray-500">Demandez au client les pièces dont vous avez besoin et suivez leur réception.</p>
          </div>
          {estPro && userRole === 'admin' && (
            <button onClick={() => setCatalogueOuvert(true)} className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 hover:border-violet-300">Gérer ma liste de pièces</button>
          )}
        </div>
        {!estPro ? (
          <p className="text-sm text-gray-600 bg-violet-50 rounded-2xl p-4">Cette fonction fait partie de l'offre Pro. Elle permet de créer votre propre liste de pièces, de la demander à chaque client et de suivre ce qui manque.</p>
        ) : (
          <div className="space-y-4">
            {pieces.length > 0 && (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-2xl">
                {pieces.map(p => {
                  const depot = depots.find(d => String(d.id) === String(p.depot_id));
                  return (
                    <li key={p.id} className="p-4 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-gray-900">{p.nom}</p>
                          <p className="text-xs text-gray-500">Demandée le {dateHeureFr(p.demande_le).slice(0, 10)}{p.statut === 'a_refaire' && p.motif ? ` · À refaire : ${p.motif}` : ''}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-xs font-black px-2 py-1 rounded-lg ${PIECE_STATUTS[p.statut || 'a_fournir']?.cls || ''}`}>{PIECE_STATUTS[p.statut || 'a_fournir']?.label || p.statut}</span>
                          {depot && <button onClick={() => ouvrir(depot)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:border-violet-300">Voir</button>}
                          {p.statut === 'recue' && <button onClick={() => changerStatut(p, 'validee')} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700">Valider</button>}
                          {(p.statut === 'recue' || p.statut === 'validee') && <button onClick={() => setRefaire({ piece: p, motif: '' })} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:text-red-600 hover:border-red-200">À refaire</button>}
                          <button onClick={() => setConfirm({ titre: `Ne plus demander « ${p.nom} » ?`, message: 'La demande sera retirée de la liste du client. Un document déjà envoyé reste dans la liste des documents reçus.', libelle: 'Retirer', action: () => supprimerPiece(p) })} title="Retirer la demande" className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500"><X size={14} /></button>
                        </div>
                      </div>
                      {refaire?.piece.id === p.id && (
                        <div className="flex flex-wrap gap-2 items-center bg-red-50 rounded-xl p-3">
                          <input aria-label="Motif" value={refaire.motif} onChange={e => setRefaire(r => ({ ...r, motif: e.target.value }))} placeholder="Motif pour le client (ex : document illisible)" className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-red-100 text-sm bg-white" />
                          <button onClick={() => setRefaire(null)} className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-bold bg-white">Annuler</button>
                          <button onClick={async () => { const r = refaire; setRefaire(null); await changerStatut(r.piece, 'a_refaire', r.motif.trim() || null); }} className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-bold">Demander à refaire</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-black text-gray-500 uppercase tracking-wider">Demander des pièces</p>
              {catalogue.length === 0 ? (
                <p className="text-sm text-gray-500">{userRole === 'admin' ? 'Votre liste de pièces est vide : cliquez sur « Gérer ma liste de pièces » pour la créer.' : 'La liste de pièces de votre organisme est vide. Vous pouvez demander une pièce ponctuelle ci-dessous.'}</p>
              ) : proposables.length === 0 ? (
                <p className="text-sm text-gray-500">Toutes les pièces de votre liste ont déjà été demandées à ce client.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {proposables.map(c => (
                    <label key={c.id} className="flex items-start gap-2 bg-white rounded-xl border border-gray-200 px-3 py-2 cursor-pointer">
                      <input type="checkbox" className="mt-1 accent-violet-600" checked={choix.includes(c.id)} onChange={e => setChoix(prev => e.target.checked ? [...prev, c.id] : prev.filter(x => x !== c.id))} />
                      <span className="min-w-0"><span className="block text-sm font-bold text-gray-800">{c.nom}</span>{c.description && <span className="block text-xs text-gray-500">{c.description}</span>}</span>
                    </label>
                  ))}
                </div>
              )}
              {choix.length > 0 && (
                <div className="flex justify-end">
                  <button onClick={() => demander(catalogue.filter(c => choix.includes(c.id)).map(c => ({ catalogue_id: c.id, nom: c.nom, description: c.description || null })))}
                    className="px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-bold">Demander {choix.length} pièce{choix.length > 1 ? 's' : ''}</button>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <input aria-label="Pièce ponctuelle" value={ponctuelle} onChange={e => setPonctuelle(e.target.value)} placeholder="Ou une pièce juste pour ce client…" className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" />
                <button onClick={() => demander([{ nom: ponctuelle.trim() }])} disabled={!ponctuelle.trim()} className="px-4 py-2 rounded-lg border border-violet-200 bg-white text-violet-700 text-sm font-bold disabled:opacity-40">Demander</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <CataloguePiecesModal isOpen={catalogueOuvert} onClose={() => setCatalogueOuvert(false)} currentOrgId={currentOrgId} catalogue={catalogue} onChange={charger} />
      <ConfirmModal isOpen={!!confirm} title={confirm?.titre} message={confirm?.message} confirmLabel={confirm?.libelle || 'Supprimer'}
        onCancel={() => setConfirm(null)} onConfirm={async () => { const c = confirm; setConfirm(null); await c.action(); }} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// STATUT DES COMPTES CLIENTS (2026-10-19)
// Admin et formateur voient si chaque client a bien activé son compte SkorUp :
//   • « Compte activé »      : le client s'est déjà connecté (date de dernière connexion) ;
//   • « Invitation envoyée » : compte créé, mais jamais de connexion → bouton « Renvoyer le lien » ;
//   • « Aucun compte »       : la fiche existe sans compte de connexion → « Créer le compte ».
// Les dates viennent de la fonction SQL statut_comptes_clients() (comptes_clients.sql) : l'admin
// voit son organisme, un formateur seulement ses clients. Tant que le SQL n'est pas lancé, rien ne
// s'affiche (l'application reste identique). Relance automatique à 3 et 7 jours : offre Pro
// (api/automation/process.js).
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const ComptesClientsContext = React.createContext(null);

// 2026-10-21 : 'differe' = dossier créé sans envoyer d'email (clients.acces_differe), pas encore de compte.
const statutCompteDe = (info, differe = false) => (!info ? null : (!info.compte_existe ? ((differe && !info.email_deja_utilise) ? 'differe' : 'aucun') : (info.derniere_connexion ? 'active' : 'attente')));

const STATUT_COMPTE_UI = {
  active: { label: 'Compte activé', icone: '✓', cls: 'bg-green-100 text-green-700' },
  attente: { label: 'Invitation envoyée', icone: '⏳', cls: 'bg-amber-100 text-amber-700' },
  aucun: { label: 'Aucun compte', icone: '⛔', cls: 'bg-red-100 text-red-700' },
  differe: { label: 'Accès non envoyé', icone: '✉️', cls: 'bg-slate-100 text-slate-700' },
};

const depuisQuandCompte = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const jours = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return 'hier';
  if (jours < 45) return `il y a ${jours} jours`;
  return `le ${d.toLocaleDateString('fr-FR')}`;
};

const dateHeureCompte = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

const sousTexteCompte = (info, st) => {
  if (st === 'active') return `Dernière connexion : ${depuisQuandCompte(info.derniere_connexion)}`;
  if (st === 'attente') return `Invitation ${depuisQuandCompte(info.invite_le)} · jamais connecté`;
  if (st === 'differe') return "aucun email envoyé au client pour l'instant";
  if (st === 'aucun') return info.email_deja_utilise ? 'email déjà utilisé par un autre compte de votre organisme' : 'la fiche existe, pas le compte';
  return '';
};

const useCompteClient = (clientId) => {
  const ctx = React.useContext(ComptesClientsContext);
  const info = ctx?.dispo ? ctx.statuts[String(clientId)] : null;
  return { ctx, info, st: statutCompteDe(info, !!ctx?.differes?.has(String(clientId))) };
};

// 2026-10-21 : « Renvoyer le lien » envoyé directement par SkorUp (Resend) au lieu de l'email Supabase :
// plus rapide, au nom de l'organisme, avec le guide d'utilisation. Le serveur vérifie l'organisme et le
// formateur, et limite les envois (2 minutes entre deux liens, 5 par 24 h et par client).
const _liensEnCours = new Set();
const renvoyerLienAcces = async (client) => {
  const email = client.email || client.email_contact || client.client_email || '';
  // 2026-10-21 : retour immédiat (« Envoi en cours… ») et pas de second envoi pendant le premier.
  const cle = String(client.id);
  if (_liensEnCours.has(cle)) return false;
  _liensEnCours.add(cle);
  const idToast = `lien-${cle}`;
  toast.loading(`Envoi du lien à ${email || 'ce client'}…`, { id: idToast });
  try {
    const { data: { session: s } } = await supabase.auth.getSession();
    const resp = await fetch('/api/formateur/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.access_token}` },
      body: JSON.stringify({ type: 'renvoi_lien', clientId: client.id, origin: window.location.origin }),
    });
    const r = await resp.json().catch(() => ({}));
    if (!resp.ok) { toast.error(r.error || "Le lien n'a pas pu être envoyé.", { id: idToast, duration: 8000 }); return false; }
    if (r.sent === false) { toast.error(r.error || r.message || "L'email n'a pas pu partir. Réessayez dans quelques minutes.", { id: idToast, duration: 8000 }); return false; }
    const dest = r.email || email || 'le client';
    toast.success(r.method === 'invite'
      ? `Email d'invitation envoyé à ${dest}.`
      : `Email de connexion envoyé à ${dest}. Le client peut cliquer sur le lien pour choisir son mot de passe.`, { id: idToast, duration: 6000 });
    return true;
  } catch (err) {
    toast.error(`Erreur : ${err.message}`, { id: idToast });
    return false;
  } finally {
    _liensEnCours.delete(cle);
  }
};

// Ancien nom, gardé pour les boutons des listes.
const renvoyerLienClient = async (client) => renvoyerLienAcces(client);


const BadgeCompteClient = ({ clientId, detail = true, align = 'right', fallback = null }) => {
  const { info, st } = useCompteClient(clientId);
  if (!st) return fallback;
  const ui = STATUT_COMPTE_UI[st];
  return (
    <div className={align === 'right' ? 'text-right' : 'text-left'}>
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black whitespace-nowrap ${ui.cls}`}>
        {ui.icone} {ui.label}
      </span>
      {detail && <div className="text-[11px] text-gray-500 mt-0.5">{sousTexteCompte(info, st)}</div>}
    </div>
  );
};

const BoutonCreerCompte = ({ client, petit = false }) => {
  const { ctx, info, st } = useCompteClient(client.id);
  const [enCours, setEnCours] = React.useState(false);
  if ((st !== 'aucun' && st !== 'differe') || !ctx?.creerCompte) return null;
  const differe = st === 'differe';
  return (
    <button
      type="button"
      disabled={enCours}
      onClick={async (e) => { e.stopPropagation(); setEnCours(true); try { await ctx.creerCompte(client); } finally { setEnCours(false); } }}
      title={info?.email_deja_utilise ? "Cette adresse email est déjà utilisée par un autre compte : changez l'email du client avant." : (differe ? "Crée l'accès du client et lui envoie un email pour choisir son mot de passe" : 'Crée le compte de connexion du client et lui envoie un lien pour choisir son mot de passe')}
      className={`inline-flex items-center gap-1.5 font-bold rounded-xl border ${differe ? 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100' : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'} transition-all disabled:opacity-60 whitespace-nowrap ${petit ? 'px-3 py-1.5 text-xs' : 'px-4 py-2.5 text-sm'}`}
    >
      {differe ? <Mail size={petit ? 13 : 15} /> : <UserPlus size={petit ? 13 : 15} />} {enCours ? (differe ? 'Envoi…' : 'Création…') : (differe ? "Envoyer l'accès au client" : 'Créer le compte et envoyer le lien')}
    </button>
  );
};

// Bouton d'action rapide dans les listes : « Renvoyer le lien » (en attente) ou « Créer le compte ».
const ActionCompteClient = ({ client }) => {
  const { st } = useCompteClient(client.id);
  if (st === 'aucun' || st === 'differe') return <span className="hidden sm:inline-flex"><BoutonCreerCompte client={client} petit /></span>;
  if (st !== 'attente') return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); renvoyerLienClient(client); }}
      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 whitespace-nowrap"
    >
      <Mail size={13} /> Renvoyer le lien
    </button>
  );
};

// Masque son contenu pour un client sans compte (ex. « Renvoyer le lien » ne peut pas marcher).
const MasquerSiSansCompte = ({ clientId, children }) => {
  const { st } = useCompteClient(clientId);
  return (st === 'aucun' || st === 'differe') ? null : children;
};

const BandeauComptesAActiver = ({ clientIds = [], onVoir = null }) => {
  const ctx = React.useContext(ComptesClientsContext);
  if (!ctx?.dispo) return null;
  const sts = clientIds.map(id => statutCompteDe(ctx.statuts[String(id)], !!ctx.differes?.has(String(id))));
  const attente = sts.filter(s => s === 'attente').length;
  const aucun = sts.filter(s => s === 'aucun').length;
  const total = attente + aucun;
  if (total === 0) return null;
  const details = [attente ? `${attente} invitation${attente > 1 ? 's' : ''} en attente` : '', aucun ? `${aucun} sans compte` : ''].filter(Boolean).join(' · ');
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p>
        ⚠️ <b>{total} client{total > 1 ? 's' : ''}</b> {total > 1 ? "n'ont" : "n'a"} pas encore activé {total > 1 ? 'leur' : 'son'} compte SkorUp
        <span className="text-amber-700"> ({details})</span>.
      </p>
      {onVoir && (
        <button type="button" onClick={onVoir} className="text-xs font-black px-3 py-1.5 rounded-lg bg-white border border-amber-200 text-amber-800 hover:bg-amber-100">
          Voir {total > 1 ? 'ces clients' : 'ce client'}
        </button>
      )}
    </div>
  );
};

const FiltresComptes = ({ clients = [], valeur, onChange }) => {
  const ctx = React.useContext(ComptesClientsContext);
  if (!ctx?.dispo || clients.length === 0) return null;
  const compte = (st) => clients.filter(c => statutCompteDe(ctx.statuts[String(c.id)], !!ctx.differes?.has(String(c.id))) === st).length;
  const options = [
    ['tous', `Tous (${clients.length})`],
    ['active', `✅ Activés (${compte('active')})`],
    ['attente', `⏳ En attente (${compte('attente')})`],
    ['aucun', `⛔ Sans compte (${compte('aucun')})`],
  ];
  if (compte('differe') > 0 || valeur === 'differe') options.push(['differe', `✉️ Accès non envoyé (${compte('differe')})`]);
  if (valeur === 'a_activer') options.push(['a_activer', `À activer (${compte('attente') + compte('aucun')}) ✕`]);
  return (
    <div className="mb-5 flex flex-wrap gap-2" role="group" aria-label="Filtrer par statut du compte">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          aria-pressed={valeur === v}
          onClick={() => onChange(valeur === v && v === 'a_activer' ? 'tous' : v)}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${valeur === v ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const BlocAccesCompte = ({ client, onRenvoyer }) => {
  const { ctx, info, st } = useCompteClient(client.id);
  if (!st) return null;
  const ligne = (label, valeur) => (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-bold text-gray-900">{valeur}</dd>
    </>
  );
  let relance = null;
  if (st === 'attente') {
    if (!ctx.estPro) {
      relance = <>Avec l'offre <span className="text-[10px] font-black bg-violet-600 text-white rounded px-1.5 py-0.5">PRO</span>, SkorUp renvoie automatiquement le lien au client au bout de 3 jours, puis de 7 jours, et vous prévient.</>;
    } else if (info.relance_2_le) {
      relance = <>2 relances envoyées (le {new Date(info.relance_1_le || info.relance_2_le).toLocaleDateString('fr-FR')} et le {new Date(info.relance_2_le).toLocaleDateString('fr-FR')}). Plus de rappel automatique : contactez le client directement.</>;
    } else if (info.relance_1_le) {
      relance = <>1<sup>re</sup> relance envoyée le {new Date(info.relance_1_le).toLocaleDateString('fr-FR')}. Dernière relance automatique 7 jours après l'invitation.</>;
    } else {
      relance = <>S'il ne se connecte pas, SkorUp lui renverra le lien au bout de 3 jours, puis de 7 jours, et vous préviendra.</>;
    }
  }
  return (
    <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-black text-sm text-gray-800">Accès à SkorUp</h4>
        <BadgeCompteClient clientId={client.id} detail={false} />
      </div>
      {st !== 'aucun' && st !== 'differe' ? (
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
          {ligne('Invitation', dateHeureCompte(info.invite_le))}
          {ligne('Compte activé le', st === 'active' ? dateHeureCompte(info.active_le) : '—')}
          {ligne('Dernière connexion', st === 'active' ? `${dateHeureCompte(info.derniere_connexion)} (${depuisQuandCompte(info.derniere_connexion)})` : '—')}
        </dl>
      ) : (
        <p className="mt-3 text-sm text-gray-600">
          {st === 'differe'
            ? "Ce dossier a été créé sans prévenir le client : aucun email ne lui a été envoyé (ni invitation, ni rappel). Quand vous êtes prêt, cliquez sur « Envoyer l'accès au client » : il recevra un email pour choisir son mot de passe."
            : info.email_deja_utilise
            ? "L'adresse email de ce client est déjà utilisée par un autre compte SkorUp. Corrigez son email ci-dessous, puis créez son compte."
            : "La fiche de ce client existe, mais son compte de connexion n'a jamais été créé (ou a été supprimé) : il ne peut pas se connecter."}
        </p>
      )}
      {relance && <p className="mt-3 text-xs text-gray-500 leading-relaxed">{relance}</p>}
      {st === 'attente' && onRenvoyer && (
        <button type="button" onClick={onRenvoyer} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200">
          <Mail size={15} /> Renvoyer le lien
        </button>
      )}
      {(st === 'aucun' || st === 'differe') && <div className="mt-3"><BoutonCreerCompte client={client} /></div>}
    </div>
  );
};

// Apparence par défaut (aucun organisme) — 2026-10-19.
const MARQUE_PAR_DEFAUT = { org_name: 'SkorUp', primary_color: '#7C3AED', logo_url: null, welcome_message: 'Bienvenue sur votre espace de formation.' };

export default function App() {
  // --- États Session et Navigation ---
  const [userRole, setUserRole] = useState(null); // 'admin' | 'formateur' | 'client' | null
  const [currentUserId, setCurrentUserId] = useState(null);
  // true quand un compte admin visualise son espace formateur (via le bouton "Mon espace Formateur",
  // ouvert dans un nouvel onglet avec ?view=formateur dans l'URL) — voir handleLogin plus bas.
  const [isAdminActingAsFormateur, setIsAdminActingAsFormateur] = useState(false);
  // Restaure l'onglet depuis l'URL au chargement (F5, lien direct, favori) — voir TAB_TO_PATH/
  // PATH_TO_TAB ci-dessus. handleLogin() ci-dessous ne réécrase PAS cette valeur lors d'une simple
  // restauration de session silencieuse (preserveTab=true), seulement lors d'une connexion fraîche.
  const [activeTab, setActiveTab] = useState(() => PATH_TO_TAB[window.location.pathname] || 'accueil');

  // ── Synchronisation URL ↔ onglet actif (2026-07-27) ──────────────────────────
  // 1) Chaque changement d'activeTab pousse une VRAIE entrée d'historique navigateur (pushState),
  //    avec l'URL propre correspondante (TAB_TO_PATH). Ignore le tout premier rendu (remplace au lieu
  //    de pousser, pour ne pas créer une entrée en double avec celle déjà présente au chargement) et
  //    ignore tant que userRole est vide (écran de connexion / reset / invitation : ces écrans gèrent
  //    déjà leur propre état d'URL séparément, ne pas interférer).
  const isFirstTabSync = React.useRef(true);
  React.useEffect(() => {
    if (!userRole) return;
    if (isSignatureEntreprise || isProspectQuestionnaire) return; // 30/10/2026 : page publique ouverte par une personne connectée
    const path = TAB_TO_PATH[activeTab];
    if (!path) return; // onglet inconnu de la table (ex: valeur interne à un sous-composant) → ignoré
    if (isFirstTabSync.current) {
      isFirstTabSync.current = false;
      if (window.location.pathname !== path) window.history.replaceState({ tab: activeTab }, '', path + window.location.search);
      return;
    }
    if (window.location.pathname !== path) {
      window.history.pushState({ tab: activeTab }, '', path + window.location.search);
    }
  }, [activeTab, userRole]);

  // 2) Bouton précédent/suivant du navigateur : restaure l'onglet correspondant à la nouvelle URL au
  //    lieu de ne rien faire (comportement d'avant ce correctif, source du "ça me déconnecte" —
  //    en réalité rien n'était déconnecté, mais sans historique le bouton retour du navigateur n'avait
  //    littéralement aucune page interne vers laquelle revenir).
  React.useEffect(() => {
    const onPopState = () => {
      const tab = PATH_TO_TAB[window.location.pathname];
      if (tab) setActiveTab(tab);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // 3) Filet de sécurité "page blanche" : un onglet restauré depuis l'URL (F5, ancien onglet de
  //    navigateur resté ouvert, lien direct) peut ne correspondre à aucun rendu pour le rôle
  //    réellement connecté (ex: /tableau-de-bord → 'dashboard', réservé aux admins). Dans ce cas,
  //    aucune des conditions activeTab === 'xxx' && userRole === 'yyy' du rendu principal ne
  //    matche : rien ne s'affiche. On revient alors sur l'onglet d'accueil du rôle concerné.
  React.useEffect(() => {
    if (!userRole) return;
    const ROLE_TABS = {
      admin: ['dashboard', 'clients', 'groupes', 'formateurs', 'calendrier', 'gestion_documents', 'questionnaires', 'modules', 'fiches_metiers', 'relances', 'prospects', 'finances', 'bpf', 'veille', 'processus', 'messagerie', 'parametres_org', 'profil', 'set-password'],
      formateur: ['accueil_formateur', 'clients', 'calendrier', 'fiches_metiers', 'prospects', 'processus', 'messagerie', 'ressources', 'profil', 'set-password'],
      client: ['accueil', 'mes_seances', 'calendrier', 'mes_documents', 'exercices', 'fiches_metiers', 'processus', 'messagerie', 'profil', 'set-password'],
    };
    const ROLE_DEFAULT_TAB = { admin: 'dashboard', formateur: 'accueil_formateur', client: 'accueil' };
    const allowed = ROLE_TABS[userRole];
    if (allowed && !allowed.includes(activeTab)) {
      setActiveTab(ROLE_DEFAULT_TAB[userRole] || 'accueil');
    }
  }, [userRole, activeTab]);

  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Détection synchrone au démarrage : lien invitation (token_hash ou access_token dans le hash)
  const [isSettingPassword, setIsSettingPassword] = useState(() => {
    const hash = window.location.hash;
    const path = window.location.pathname;
    return (
      path === '/set-password' ||
      hash.includes('type=invite') ||
      (hash.includes('access_token') && !hash.includes('type=recovery')) ||
      (hash.includes('token_hash') && !hash.includes('type=recovery'))
    );
  });

  const [isResetPassword, setIsResetPassword] = useState(() => {
    const hash = window.location.hash;
    const path = window.location.pathname;
    return (
      path === '/reset-password' ||
      hash.includes('type=recovery')
    );
  });

  const [resetSuccessMsg, setResetSuccessMsg] = useState('');
  const [isSignup, setIsSignup] = useState(() => window.location.pathname === '/signup');
  // Questionnaire prospect (sans compte SkorUp) : /questionnaire?t=<token> — voir ProspectQuestionnaireView.
  const [isProspectQuestionnaire] = useState(() => window.location.pathname === '/questionnaire');
  // 30/10/2026 : signature d'une convention par une entreprise (sans compte) : /signer?t=<token>.
  const [isSignatureEntreprise] = useState(() => window.location.pathname === '/signer');
  const [needsSetup, setNeedsSetup] = useState(false);
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [orgSettings, setOrgSettings] = useState(null);
  const [brandSettings, setBrandSettings] = useState(MARQUE_PAR_DEFAUT);
  // Contexte joint aux erreurs enregistrées (voir erreurs.js) : rôle, organisation, onglet.
  useEffect(() => {
    window.__skorupContexte = { role: userRole, organisation: currentOrgId, onglet: activeTab };
  }, [userRole, currentOrgId, activeTab]);

  // --- Modale de confirmation suppression globale ---
  const [appConfirmState, setAppConfirmState] = useState({ open: false, title: '', message: '', onConfirm: null });
  const showAppConfirm = (title, message, onConfirmFn) => setAppConfirmState({ open: true, title, message, onConfirm: onConfirmFn });
  const hideAppConfirm = () => setAppConfirmState(prev => ({ ...prev, open: false, onConfirm: null }));

  // --- Vérification initiale de la session ---
  useEffect(() => {
    const initSession = async () => {
      setIsLoadingSession(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user?.email) {
        // 2026-10-21 : ouverture instantanée — profil (rôle, organisme) mémorisé lors de la dernière
        // ouverture : on affiche l'application tout de suite, puis on vérifie auprès de la base.
        let profilMemo = null;
        try { profilMemo = JSON.parse(localStorage.getItem(`skorup_profil_v1_${session.user.id}`) || 'null'); } catch (_) { profilMemo = null; }
        const memoValide = !!(profilMemo && (profilMemo.role === 'admin' || profilMemo.role === 'formateur') && profilMemo.id && profilMemo.org
          && profilMemo.le && Date.now() - profilMemo.le < 7 * 86400000);
        if (memoValide) {
          handleLogin(profilMemo.role, profilMemo.id, profilMemo.org, true);
          setIsLoadingSession(false);
        }
    
        // RLS couvre correctement le profil via auth_org_id() SECURITY DEFINER
        const userData = await findAccountProfile(session.user);
        if (memoValide) {
          const identique = userData && userData.role === profilMemo.role && String(userData.id) === String(profilMemo.id)
            && String(userData.organisation_id) === String(profilMemo.org);
          if (identique) return;
          // Le profil a changé (rôle, organisme…) : on oublie la mémoire et on repart des vraies valeurs.
          try { localStorage.removeItem(`skorup_profil_v1_${session.user.id}`); } catch (_) { /* rien */ }
          if (!userData || !userData.role) { setUserRole(null); setCurrentUserId(null); setCurrentOrgId(null); }
        }
        const userDataErr = null;
        if (userDataErr) console.error('[initSession] Erreur lecture profil utilisateur (rôle) :', userDataErr);
        if (userData && userData.role) {
          // preserveTab=true : restauration SILENCIEUSE d'une session déjà ouverte (F5, réouverture
          // d'onglet) — ne pas écraser l'onglet déjà restauré depuis l'URL (voir useState(activeTab)
          // plus haut), sinon un lien profond ou un simple F5 ramènerait toujours à la page d'accueil.
          handleLogin(userData.role, userData.id, userData.organisation_id, true);
        } else {
          const { data: clientData, error: clientDataErr } = await supabase.from('clients').select('id, organisation_id').ilike('email_contact', escapeLike(session.user.email)).maybeSingle();
          if (clientDataErr) console.error('[initSession] Erreur lecture profil client :', clientDataErr);
          if (clientData) {
            handleLogin('client', clientData.id, clientData.organisation_id, true);
          } else {
            // Session active mais aucun profil DB trouvé
            const metaRole = session.user?.user_metadata?.role;
            if (!metaRole || metaRole === 'admin') {
              // Admin sans organisation → setup
              setNeedsSetup(true);
            }
            // Client/formateur sans enregistrement DB → ne jamais rediriger vers setup
          }
        }
      }
      setIsLoadingSession(false);
    };
    initSession();
  }, []);

  // États de sélection et d'affichage centralisés
  const [signingSessionId, setSigningSessionId] = useState(null);
  const [signingDocId, setSigningDocId] = useState(null);
  const [viewingDocId, setViewingDocId] = useState(null);
  const [viewingSession, setViewingSession] = useState(null); // { session, mode: 'view'|'sign' }
  const [lastModifiedSessionId, setLastModifiedSessionId] = useState(null);
  const [editedTimes, setEditedTimes] = useState({});
  const [isSessionItemModalOpen, setIsSessionItemModalOpen] = useState(false);
  const [changementModule, setChangementModule] = useState(null); // 04/11/2026 : fenêtre « Changer le module »
  const [isDocSettingsOpen, setIsDocSettingsOpen] = useState(false);
  const [docSettingsTarget, setDocSettingsTarget] = useState(null); // session row
  const [targetSessionForAddition, setTargetSessionForAddition] = useState(null);

  // Modèles de Documents par défaut
  const [documentTemplates, setDocumentTemplates] = useState({});
  const [selectedClientForDocs, setSelectedClientForDocs] = useState('');
  const [expandedClientId, setExpandedClientId] = useState(null);

  // --- Supabase Database (États locaux mis à jour via DB) ---
  const [formateurs, setFormateurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsArchives, setClientsArchives] = useState([]); // LOT C
  const [documents, setDocuments] = useState([]);
  // Nombre de messages non lus adressés au compte connecté (badge menu "Messagerie") — voir les
  // effets de chargement/rafraîchissement plus bas (montage, retour sur l'onglet, sondage 60s).
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // --- Admin agissant aussi comme formateur (bouton "Mon espace Formateur") ---
  // adminSelfProfile est chargé dès que le compte connecté est admin (pour le bouton lui-même)
  // ou déjà en train de visualiser l'espace formateur via ce même compte (pour le "Bonjour, {nom}").
  const [adminSelfProfile, setAdminSelfProfile] = useState(null); // { id, nom }
  useEffect(() => {
    if ((userRole === 'admin' || isAdminActingAsFormateur) && currentUserId) {
      supabase.from('utilisateurs').select('id, nom').eq('id', currentUserId).single()
        .then(({ data }) => { if (data) setAdminSelfProfile(data); });
    } else if (!isAdminActingAsFormateur && userRole !== 'admin') {
      setAdminSelfProfile(null);
    }
  }, [userRole, isAdminActingAsFormateur, currentUserId]);
  // Cas symétrique côté CLIENT : si le formateur_id du client connecté correspond en
  // réalité à un compte admin (coach = l'admin elle-même), il est absent du tableau
  // `formateurs` (qui ne contient que role='formateur'). On va chercher ce profil pour
  // que le client voie bien son coach (messagerie + "Votre coach" sur l'Accueil).
  const [clientCoachFallback, setClientCoachFallback] = useState(null); // { id, nom }
  useEffect(() => {
    if (userRole !== 'client' || !currentUserId) { setClientCoachFallback(null); return; }
    const me = (clients || []).find(c => String(c.id) === String(currentUserId));
    const already = me?.formateur_id && (formateurs || []).find(f => String(f.id) === String(me.formateur_id));
    if (!me?.formateur_id || already) { setClientCoachFallback(null); return; }
    supabase.from('utilisateurs').select('id, nom').eq('id', me.formateur_id).maybeSingle()
      .then(({ data }) => setClientCoachFallback(data || null));
  }, [userRole, currentUserId, clients, formateurs]);
  // Liste des formateurs "assignables" : les vrais formateurs + l'admin lui-même (pour qu'un client
  // puisse lui être assigné comme coach, et pour que son propre espace formateur affiche son nom).
  // Volontairement PAS utilisée pour AdminFormateursView (page de gestion avec suppression) afin
  // d'éviter tout risque de suppression accidentelle du compte admin lui-même.
  const assignableFormateurs = React.useMemo(() => {
    if (adminSelfProfile) {
      return [{ ...adminSelfProfile, role: 'formateur', estAdmin: true }, ...formateurs.filter(f => f.id !== adminSelfProfile.id)];
    }
    if (clientCoachFallback) {
      return [{ ...clientCoachFallback, role: 'formateur' }, ...formateurs.filter(f => f.id !== clientCoachFallback.id)];
    }
    return formateurs;
  }, [formateurs, adminSelfProfile, clientCoachFallback]);

  // États Modules Supabase
  const [modules, setModules] = useState([]);
  const [moduleSessionTemplates, setModuleSessionTemplates] = useState([]);
  const [moduleStepResources, setModuleStepResources] = useState([]);

  // États formulaire "Ajouter un compte"
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('client');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [isAddingUser, setIsAddingUser] = useState(false);

  // États formulaire "Ingénierie Modules" (Admin)
  const [newModuleName, setNewModuleName] = useState('');
  const [newModuleSeances, setNewModuleSeances] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [modelingModuleId, setModelingModuleId] = useState(null);

  // États formulaire "Étape de Parcours"
  const [newStepTitle, setNewStepTitle] = useState('');
  const [newStepActivity, setNewStepActivity] = useState('Signature');
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [isAddingStepResource, setIsAddingStepResource] = useState(false);
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState(null);

  // États formulaire "Ajouter un document"
  const [newDocName, setNewDocName] = useState('');
  const [newDocType, setNewDocType] = useState('Autre');
  const [newDocUrl, setNewDocUrl] = useState('');
  const [newDocFile, setNewDocFile] = useState(null);
  const [newDocClientId, setNewDocClientId] = useState('');
  const [newDocVisClient, setNewDocVisClient] = useState(true);
  const [newDocVisFormateur, setNewDocVisFormateur] = useState(true);
  const [isAddingDoc, setIsAddingDoc] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDestination, setNewTemplateDestination] = useState('client'); // 'client' | 'formateur'
  const [newTemplateClassification, setNewTemplateClassification] = useState('telechargeable'); // 'a_generer' | 'a_signer' | 'telechargeable'
  const [pedagogicalResources, setPedagogicalResources] = useState([]);
  const [newResourceName, setNewResourceName] = useState('');
  const [isUploadingResource, setIsUploadingResource] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  // Rôle pré-sélectionné à l'ouverture du modal d'invitation (ajouté 2026-08-05) — permet aux
  // boutons "Nouveau Client" (onglet Clients) et "Nouveau Formateur" (onglet Formateurs) de
  // pré-remplir le bon rôle, sans que l'admin ait à le changer manuellement à chaque fois.
  const [inviteDefaultRole, setInviteDefaultRole] = useState('client');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState(null); // { type: 'template'|'client'|'formateur', id: ... }



  // ── FIX (2026-10-02) : fiabilisation des chargements (modules, séances, clients, documents) ──────
  // beginLoad(cle) renvoie isStale() : vrai si, pendant l'attente de la réponse, un chargement plus récent
  // du même type a été lancé ou si l'organisme a changé (déconnexion, autre compte). Une réponse périmée
  // (ex. une requête lancée avant que l'organisme soit connu, qui arrive APRÈS la bonne) ne peut donc plus
  // écraser les bonnes données — l'une des causes des modules / séances qui "disparaissaient".
  const loadSeqRef = useRef({});
  const orgIdRef = useRef(null);
  orgIdRef.current = currentOrgId;
  const beginLoad = (key) => {
    const seq = (loadSeqRef.current[key] || 0) + 1;
    loadSeqRef.current[key] = seq;
    const org = currentOrgId;
    return () => loadSeqRef.current[key] !== seq || orgIdRef.current !== org;
  };
  // 2026-10-21 : suivi du chargement (indicateur « Mise à jour… », pas de liste vide trompeuse).
  const [chargements, setChargements] = useState({});
  const marquerCharge = (k) => setChargements(p => (p[k] ? p : { ...p, [k]: true }));
  const notifyLoadError = (what) => toast.error(`Impossible de charger ${what} pour le moment. Vérifiez votre connexion puis rechargez la page.`, { id: `load-error-${what}` });

  const fetchModules = async () => {
    if (!currentOrgId) { setModules([]); setModuleSessionTemplates([]); setModuleStepResources([]); return; }
    const isStale = beginLoad('modules');
    // Les 3 listes sont chargées en parallèle puis appliquées ENSEMBLE : plus d'état intermédiaire où les
    // modules s'affichent sans leurs séances.
    const [mRes, mstRes, msrRes] = await Promise.all([
      fetchAllRowsWithRetry(() => supabase.from('modules').select('id, nom, seances_prevues, prix_prestation')
        .eq('organisation_id', currentOrgId).order('id', { ascending: true }), { label: 'modules' }),
      fetchAllRowsWithRetry(() => supabase.from('module_session_templates').select('*')
        .eq('organisation_id', currentOrgId).order('ordre', { ascending: true }).order('id', { ascending: true }), { label: 'séances des modules' }),
      fetchAllRowsWithRetry(() => supabase.from('module_step_resources').select('*')
        .eq('organisation_id', currentOrgId).order('ordre', { ascending: true }).order('id', { ascending: true }), { label: 'contenus des modules' }),
    ]);
    if (isStale()) return;
    marquerCharge('modules');
    // AJOUT (2026-09-17) : tri alphabétique à la source, même logique que formateurs/clients ci-dessus.
    if (mRes.data) setModules([...mRes.data].sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' })));
    if (mstRes.data) setModuleSessionTemplates(mstRes.data);
    if (msrRes.data) setModuleStepResources(msrRes.data);
    if (mRes.error || mstRes.error || msrRes.error) notifyLoadError('les modules');
  };

  // ─── GROUPES (2026-10-13) : données et actions ─────────────────────────────────────────────────
  // Voir GroupesView. Si les tables n'existent pas encore (SQL groupes_etape1.sql pas encore lancé),
  // la page Groupes affiche simplement qu'ils ne sont pas activés : le reste de l'appli n'est pas touché.
  const [groupes, setGroupes] = useState([]);
  const [groupeSeances, setGroupeSeances] = useState([]);
  const [groupesDispo, setGroupesDispo] = useState(true);
  const [selectedGroupeId, setSelectedGroupeId] = useState(null);

  const fetchGroupes = async () => {
    if (!currentOrgId) { setGroupes([]); setGroupeSeances([]); return; }
    const isStale = beginLoad('groupes');
    const [gRes, sRes] = await Promise.all([
      supabase.from('groupes').select('*').eq('organisation_id', currentOrgId).order('created_at', { ascending: false }),
      supabase.from('groupe_seances').select('*').eq('organisation_id', currentOrgId).order('numero', { ascending: true }),
    ]);
    if (isStale()) return;
    if (gRes.error || sRes.error) {
      console.warn('[groupes] non disponibles :', (gRes.error || sRes.error)?.message);
      setGroupesDispo(false); setGroupes([]); setGroupeSeances([]);
      return;
    }
    setGroupesDispo(true);
    setGroupes(gRes.data || []);
    setGroupeSeances(sRes.data || []);
  };

  const idsParticipants = (groupeId) => participantsDuGroupe(clients, groupeId).map(c => c.id);

  // Crée le dossier d'un nouveau participant (même chemin que « Nouveau client » : invitation par
  // e-mail, fiche client, documents par défaut), directement rattaché au groupe.
  const inviterParticipant = async ({ nom, email, formateurId, groupeId, numeroDossier }) => {
    const { data: { session: authSession } } = await supabase.auth.getSession();
    const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/invite-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSession?.access_token}` },
      body: JSON.stringify({ email, nom, role: 'client', action: 'invite', redirectTo: window.location.origin }),
    });
    const result = await resp.json().catch(() => ({}));
    if (!resp.ok || !result.userId) throw new Error(result.error || "L'invitation n'a pas pu être envoyée");
    const formateurIntId = formateurId ? Number(formateurId) : null;
    const formateurRecord = formateurIntId ? formateurs.find(f => Number(f.id) === formateurIntId) : null;
    const { error: dbError } = await supabase.from('clients').insert([{
      id: result.userId,
      nom_complet: nom,
      email_contact: email,
      formateur_id: formateurIntId,
      formateur_auth_uid: formateurRecord?.auth_uid || null,
      organisation_id: currentOrgId,
      groupe_id: groupeId,
      ...(numeroDossier ? { numero_dossier: numeroDossier } : {}),
    }]);
    if (dbError) throw new Error(dbError.message);
    const defaultClientDocs = Object.entries(documentTemplates || {})
      .filter(([, tpl]) => (tpl.destination || 'client') === 'client')
      .map(([titre, tpl], idx) => ({
        client_id: result.userId, template_titre: titre, template_url: tpl.url || null,
        destination: 'client', ordre: idx, organisation_id: currentOrgId,
      }));
    if (defaultClientDocs.length > 0) {
      const { error: docsError } = await supabase.from('client_documents').insert(defaultClientDocs);
      if (docsError) console.error('[groupes] init client_documents :', docsError);
    }
    return result.userId;
  };

  // Même chose que choisir un module dans la fiche client, sans recharger l'écran à chaque personne.
  const affecterModuleParticipant = async (clientId, moduleId) => {
    const { error } = await supabase.from('clients').update({ module_id: moduleId }).eq('id', clientId);
    if (error) throw new Error(error.message);
    const { data: c, error: e2 } = await supabase.from('clients').select('*').eq('id', clientId).single();
    if (e2 || !c) throw new Error(e2?.message || 'Dossier introuvable');
    const compatible = { ...c, nom: c.nom_complet || c.nom || c.email_contact || 'Bénéficiaire', module_id: moduleId };
    await generateSessions(compatible);
    await distributeDocumentsForModule(compatible, moduleId);
  };

  const lienVisioSeance = (seance, groupe) => String(seance?.lien_visio || groupe?.lien_visio || '').trim();

  // Reporte la date, les horaires et le lien visio d'une séance du groupe sur les séances des participants.
  // LOT A (2026-10-21) :
  //  - une séance déjà signée (émargement ou document, par qui que ce soit) chez un participant ne change
  //    JAMAIS de date ni d'horaires : c'est la preuve de sa présence (renvoie le nombre de participants concernés) ;
  //  - options.dates : 'tout' (modification volontaire de la séance du groupe), 'renseignees' (intégration
  //    d'un participant : une séance du groupe sans date n'efface pas la date déjà prévue chez lui),
  //    'aucune' (changement du lien visio : les dates ne sont pas touchées).
  const appliquerSeanceAuxParticipants = async (seance, groupe, clientIds, options = {}) => {
    if (!clientIds || clientIds.length === 0) return 0;
    const modeDates = options.dates || 'tout';
    const { data: lignes, error: errLignes } = await supabase.from('sessions').select('*')
      .in('client_id', clientIds).eq('numero_seance', seance.numero);
    if (errLignes) throw new Error(errLignes.message);
    const clientsSignes = new Set((lignes || []).filter(elementSeanceEstSigne).map(r => String(r.client_id)));
    const modifiables = (lignes || []).filter(r => !clientsSignes.has(String(r.client_id)));
    const maj = {};
    if (modeDates !== 'aucune') {
      ['date', 'heure_debut', 'heure_fin'].forEach(k => {
        if (modeDates === 'tout' || seance[k]) maj[k] = seance[k] || null;
      });
    }
    if (Object.keys(maj).length && modifiables.length) {
      const { error } = await supabase.from('sessions').update(maj).in('id', modifiables.map(r => r.id));
      if (error) throw new Error(error.message);
    }
    const lien = lienVisioSeance(seance, groupe);
    if (lien && /^https?:\/\//i.test(lien)) {
      await Promise.all(modifiables.map(r => {
        const meta = lireMeta(r.metadata);
        if (meta.lien_visio === lien) return null;
        return supabase.from('sessions').update({ metadata: { ...meta, lien_visio: lien } }).eq('id', r.id);
      }));
    }
    return clientsSignes.size;
  };
  const MESSAGE_SEANCE_SIGNEE_GROUPE = (n) => `${n} participant${n > 1 ? 's ont' : ' a'} déjà signé cette séance : sa date est conservée (preuve de présence).`;

  // Agenda Google des participants (et de leur formateur), comme pour une séance individuelle.
  const synchroniserAgendasGroupe = (clientIds, numeros) => {
    (async () => {
      for (const id of clientIds) {
        for (const n of numeros) {
          syncSeanceToCalendar(id, n);
          await new Promise(r => setTimeout(r, 150));
        }
      }
    })();
  };

  // Séances ajoutées au groupe en plus de celles du module : une feuille d'émargement par participant.
  const creerSeancesSupplementaires = async (clientIds, seancesAjoutees, groupe) => {
    if (!clientIds.length || !seancesAjoutees.length) return;
    const numeros = seancesAjoutees.map(s => s.numero);
    const { data: deja } = await supabase.from('sessions').select('client_id, numero_seance')
      .in('client_id', clientIds).in('numero_seance', numeros);
    const lignes = [];
    const ajoutsParClient = {};
    clientIds.forEach(cid => {
      const client = clients.find(c => String(c.id) === String(cid));
      seancesAjoutees.forEach(s => {
        if ((deja || []).some(d => String(d.client_id) === String(cid) && Number(d.numero_seance) === Number(s.numero))) return;
        ajoutsParClient[cid] = (ajoutsParClient[cid] || 0) + 1;
        // 2026-10-20 : émargement décoché → simple dossier de séance (comme une séance vide du module),
        // pour que la séance, sa date et ses horaires apparaissent quand même dans le dossier.
        if (s.emargement === false) {
          lignes.push({
            client_id: cid,
            module_id: client?.module_id || groupe?.module_id || null,
            organisation_id: currentOrgId,
            numero_seance: Number(s.numero),
            nom: s.titre || `Séance ${s.numero}`,
            type_activite: 'Dossier (Vide)',
            is_administrative: false,
            position: 0,
            metadata: { isCustom: true, documentType: 'info', groupe_id: groupe?.id || null },
            statut: 'À venir', statut_client: 'À venir', statut_formateur: 'À venir',
            date: s.date || null, heure_debut: s.heure_debut || null, heure_fin: s.heure_fin || null,
          });
          return;
        }
        lignes.push({
          client_id: cid,
          module_id: client?.module_id || groupe?.module_id || null,
          organisation_id: currentOrgId,
          numero_seance: Number(s.numero),
          nom: `${s.titre || `Séance ${s.numero}`} - Émargement de présence`,
          ressource_titre: 'Émargement de présence',
          type_activite: 'signature',
          is_administrative: false,
          position: 0,
          metadata: { isCustom: true, documentType: 'signature', requiresClientSignature: true, requiresTrainerSignature: true, groupe_id: groupe?.id || null },
          statut: 'À venir', statut_client: 'À venir', statut_formateur: 'À venir',
          date: s.date || null, heure_debut: s.heure_debut || null, heure_fin: s.heure_fin || null,
        });
      });
    });
    if (lignes.length === 0) return;
    const { error } = await supabase.from('sessions').insert(lignes);
    if (error) throw new Error(error.message);
    const { data: totaux } = await supabase.from('clients').select('id, seances_totales').in('id', Object.keys(ajoutsParClient));
    await Promise.all((totaux || []).map(c => supabase.from('clients')
      .update({ seances_totales: (c.seances_totales || 0) + ajoutsParClient[c.id] }).eq('id', c.id)));
  };

  // Rattache une liste de participants (nouveaux ou existants) à un groupe, puis reporte le planning.
  // 30/10/2026 : participant sans compte → dossier seul (acces_differe), aucun e-mail, e-mail facultatif.
  const creerParticipantSansCompte = async ({ nom, email, formateurId, groupeId, numeroDossier }) => {
    const id = nouvelUuid();
    const formateurIntId = formateurId ? Number(formateurId) : null;
    const formateurRecord = formateurIntId ? formateurs.find(f => Number(f.id) === formateurIntId) : null;
    const { error: dbError } = await supabase.from('clients').insert([{
      id, nom_complet: nom, email_contact: email || null,
      formateur_id: formateurIntId, formateur_auth_uid: formateurRecord?.auth_uid || null,
      organisation_id: currentOrgId, groupe_id: groupeId, acces_differe: true,
      ...(numeroDossier ? { numero_dossier: numeroDossier } : {}),
    }]);
    if (dbError) {
      if (!email && /email_contact|not-null|null value/i.test(dbError.message || '')) throw new Error('ajoutez son e-mail, ou lancez le SQL « signature_entreprise » pour rendre l\'e-mail facultatif');
      throw new Error(dbError.message);
    }
    const defaultClientDocs = Object.entries(documentTemplates || {})
      .filter(([, tpl]) => (tpl.destination || 'client') === 'client')
      .map(([titre, tpl], idx) => ({ client_id: id, template_titre: titre, template_url: tpl.url || null, destination: 'client', ordre: idx, organisation_id: currentOrgId }));
    if (defaultClientDocs.length > 0) {
      const { error: docsError } = await supabase.from('client_documents').insert(defaultClientDocs);
      if (docsError) console.error('[groupes] init client_documents :', docsError);
    }
    return id;
  };

  const integrerParticipants = async (groupe, seancesGroupe, participants, onProgress, rapport, sansCompte = false) => {
    const ids = [];
    let i = 0;
    for (const p of participants) {
      i++;
      onProgress(`Participant ${i} sur ${participants.length} : ${p.nom}`);
      try {
        let clientId = p.clientId;
        const existant = clientId ? clients.find(c => String(c.id) === String(clientId)) : null;
        if (p.type === 'nouveau' && sansCompte) {
          clientId = await creerParticipantSansCompte({ nom: p.nom, email: p.email, formateurId: groupe.formateur_id, groupeId: groupe.id, numeroDossier: p.numero_dossier });
          rapport.sansCompte = (rapport.sansCompte || 0) + 1;
        } else if (p.type === 'nouveau') {
          clientId = await inviterParticipant({ nom: p.nom, email: p.email, formateurId: groupe.formateur_id, groupeId: groupe.id, numeroDossier: p.numero_dossier });
          rapport.invites++;
        } else {
          const maj = { groupe_id: groupe.id };
          if (!existant?.formateur_id && groupe.formateur_id) maj.formateur_id = groupe.formateur_id;
          if (p.numero_dossier && !existant?.numero_dossier) maj.numero_dossier = p.numero_dossier;
          const { error } = await supabase.from('clients').update(maj).eq('id', clientId);
          if (error) throw new Error(error.message);
          rapport.ajoutes++;
        }
        if (groupe.module_id && String(existant?.module_id || '') !== String(groupe.module_id)) {
          await affecterModuleParticipant(clientId, Number(groupe.module_id));
        }
        await creerSeancesSupplementaires([clientId], seancesGroupe.filter(s => s.ajoutee), groupe);
        ids.push(clientId);
      } catch (e) {
        rapport.erreurs.push({ nom: p.nom, email: p.email, message: e.message || String(e) });
      }
    }
    if (ids.length) {
      onProgress('Report des dates et horaires dans chaque dossier…');
      let seancesSigneesConservees = 0;
      for (const s of seancesGroupe) {
        try { seancesSigneesConservees += await appliquerSeanceAuxParticipants(s, groupe, ids, { dates: 'renseignees' }); }
        catch (e) { rapport.erreurs.push({ nom: `Séance ${s.numero}`, message: e.message }); }
      }
      if (seancesSigneesConservees > 0) toast(`Des séances déjà signées par des participants ont gardé leur date (${seancesSigneesConservees}).`, { icon: '🔒', duration: 7000 });
      rapport.seances = seancesGroupe.filter(s => s.date || s.heure_debut).length;
      synchroniserAgendasGroupe(ids, seancesGroupe.filter(s => s.date).map(s => s.numero));
    }
    return ids;
  };

  const verifierQuotaGroupe = async (nbNouveaux) => {
    if (!nbNouveaux) return;
    const limite = getClientLimitForOrg(orgSettings);
    if (!Number.isFinite(limite)) return;
    const { count, error } = await compterDossiersActifs(currentOrgId); // LOT C : archivés non comptés
    if (error) throw new Error('Impossible de vérifier votre nombre de dossiers clients pour le moment');
    if ((count || 0) + nbNouveaux > limite) {
      throw new Error(`votre offre permet ${limite} dossiers clients : il en reste ${Math.max(0, limite - (count || 0))} et ce groupe en ajoute ${nbNouveaux}`);
    }
  };

  const rafraichirApresGroupe = async () => {
    await Promise.all([fetchGroupes(), fetchUtilisateurs(), fetchSessions(), fetchDocuments()]);
  };

  const actionsGroupes = {
    rafraichir: () => rafraichirApresGroupe(),
    creerGroupe: async ({ infos, seances, participants, sansCompte = false }, onProgress = () => {}) => {
      const rapport = { invites: 0, ajoutes: 0, seances: 0, erreurs: [] };
      await verifierQuotaGroupe(participants.filter(p => p.type === 'nouveau').length);
      onProgress('Création du groupe…');
      const { data: g, error } = await supabase.from('groupes').insert([{
        organisation_id: currentOrgId,
        nom: infos.nom.trim(),
        module_id: infos.module_id ? Number(infos.module_id) : null,
        formateur_id: infos.formateur_id ? Number(infos.formateur_id) : null,
        lieu: infos.lieu.trim() || null,
        lien_visio: infos.lien_visio.trim() || null,
      }]).select().single();
      if (error || !g) throw new Error(error?.message || 'Groupe non créé');
      const lignes = (seances || []).map(s => ({
        groupe_id: g.id, organisation_id: currentOrgId, numero: Number(s.numero), titre: s.titre || null,
        date: s.date || null, heure_debut: s.heure_debut || null, heure_fin: s.heure_fin || null,
        formateur_id: s.formateur_id ? Number(s.formateur_id) : null, ajoutee: !!s.ajoutee,
        // 2026-10-20 : envoyé seulement quand on DÉcoche l'émargement (sinon comportement d'avant).
        ...(s.ajoutee && s.emargement === false ? { emargement: false } : {}),
      }));
      let seancesGroupe = [];
      if (lignes.length) {
        const { data: sg, error: e2 } = await supabase.from('groupe_seances').insert(lignes).select();
        if (e2) { await supabase.from('groupes').delete().eq('id', g.id); throw new Error(e2.message); }
        seancesGroupe = sg || lignes;
      }
      await integrerParticipants(g, seancesGroupe, participants, onProgress, rapport, sansCompte);
      onProgress('Mise à jour de l\'affichage…');
      await rafraichirApresGroupe();
      return { groupeId: g.id, rapport };
    },

    ajouterParticipants: async (groupe, participants, onProgress = () => {}, sansCompte = false) => {
      const rapport = { invites: 0, ajoutes: 0, seances: 0, erreurs: [] };
      await verifierQuotaGroupe(participants.filter(p => p.type === 'nouveau').length);
      await integrerParticipants(groupe, seancesDuGroupe(groupeSeances, groupe.id), participants, onProgress, rapport, sansCompte);
      onProgress('Mise à jour de l\'affichage…');
      await rafraichirApresGroupe();
      return rapport;
    },

    majSeanceGroupe: async (seance, brouillon) => {
      const groupe = groupes.find(g => String(g.id) === String(seance.groupe_id));
      const payload = {
        date: brouillon.date || null,
        heure_debut: brouillon.heure_debut || null,
        heure_fin: brouillon.heure_fin || null,
        formateur_id: brouillon.formateur_id ? Number(brouillon.formateur_id) : null,
        lieu: (brouillon.lieu || '').trim() || null,
        lien_visio: (brouillon.lien_visio || '').trim() || null,
      };
      const { error } = await supabase.from('groupe_seances').update(payload).eq('id', seance.id);
      if (error) throw new Error(error.message);
      const ids = idsParticipants(seance.groupe_id);
      const changePlanning = ['date', 'heure_debut', 'heure_fin', 'lien_visio']
        .some(k => String(payload[k] || '') !== String((k.startsWith('heure') ? heureCourte(seance[k]) : seance[k]) || ''));
      if (changePlanning && ids.length) {
        const signes = await appliquerSeanceAuxParticipants({ ...seance, ...payload }, groupe, ids, { dates: 'tout' });
        if (signes > 0) toast(MESSAGE_SEANCE_SIGNEE_GROUPE(signes), { icon: '🔒', duration: 7000 });
        synchroniserAgendasGroupe(ids, [seance.numero]);
      }
      await Promise.all([fetchGroupes(), fetchSessions()]);
    },

    ajouterSeanceGroupe: async (groupe, data) => {
      const ids = idsParticipants(groupe.id);
      const maxGroupe = seancesDuGroupe(groupeSeances, groupe.id).reduce((m, s) => Math.max(m, Number(s.numero) || 0), 0);
      const maxDossiers = sessions.filter(s => ids.some(id => String(id) === String(s.client_id)))
        .reduce((m, s) => Math.max(m, Number(s.numero_seance) || 0), 0);
      const numero = Math.max(maxGroupe, maxDossiers) + 1;
      const { data: s, error } = await supabase.from('groupe_seances').insert([{
        groupe_id: groupe.id, organisation_id: currentOrgId, numero, titre: (data.titre || '').trim() || null,
        date: data.date || null, heure_debut: data.heure_debut || null, heure_fin: data.heure_fin || null, ajoutee: true,
        ...(data.emargement === false ? { emargement: false } : {}),
      }]).select().single();
      if (error) throw new Error(error.message);
      await creerSeancesSupplementaires(ids, [s], groupe);
      await appliquerSeanceAuxParticipants(s, groupe, ids, { dates: 'tout' });
      if (s.date) synchroniserAgendasGroupe(ids, [s.numero]);
      await Promise.all([fetchGroupes(), fetchSessions(), fetchUtilisateurs()]);
    },

    supprimerSeanceGroupe: async (seance) => {
      if (!seance.ajoutee) throw new Error('Les séances du module ne peuvent pas être supprimées depuis le groupe.');
      const ids = idsParticipants(seance.groupe_id);
      // LOT 2 (2026-10-20) : lignes relues en base (pas seulement l'écran), et seules ces lignes-là
      // sont supprimées — jamais si l'une d'elles est signée.
      let lignes = [];
      if (ids.length) {
        const { data: lu, error: errLu } = await supabase.from('sessions').select('*').in('client_id', ids).eq('numero_seance', seance.numero);
        if (errLu) throw new Error(errLu.message);
        lignes = lu || [];
      }
      if (lignes.some(elementSeanceEstSigne)) {
        throw new Error('Cette séance a déjà été signée par au moins une personne : elle ne peut pas être supprimée.');
      }
      if (lignes.length) {
        const { error } = await supabase.from('sessions').delete().in('id', lignes.map(l => l.id));
        if (error) throw new Error(error.message);
        const parClient = {};
        lignes.forEach(s => { parClient[s.client_id] = true; });
        const { data: totaux } = await supabase.from('clients').select('id, seances_totales').in('id', Object.keys(parClient));
        await Promise.all((totaux || []).map(c => supabase.from('clients')
          .update({ seances_totales: Math.max(0, (c.seances_totales || 0) - 1) }).eq('id', c.id)));
      }
      const { error: e2 } = await supabase.from('groupe_seances').delete().eq('id', seance.id);
      if (e2) throw new Error(e2.message);
      await Promise.all([fetchGroupes(), fetchSessions(), fetchUtilisateurs()]);
    },

    retirerDuGroupe: async (clientId) => {
      const { error } = await supabase.from('clients').update({ groupe_id: null }).eq('id', clientId);
      if (error) throw new Error(error.message);
      await fetchUtilisateurs();
    },

    majGroupe: async (groupe, edition) => {
      const payload = {
        nom: edition.nom.trim(),
        formateur_id: edition.formateur_id ? Number(edition.formateur_id) : null,
        lieu: (edition.lieu || '').trim() || null,
        lien_visio: (edition.lien_visio || '').trim() || null,
      };
      const { error } = await supabase.from('groupes').update(payload).eq('id', groupe.id);
      if (error) throw new Error(error.message);
      const ids = idsParticipants(groupe.id);
      if (edition.appliquerFormateur && payload.formateur_id && String(payload.formateur_id) !== String(groupe.formateur_id || '') && ids.length) {
        const { error: e2 } = await supabase.from('clients').update({ formateur_id: payload.formateur_id }).in('id', ids);
        if (e2) throw new Error(e2.message);
      }
      if (String(payload.lien_visio || '') !== String(groupe.lien_visio || '') && ids.length) {
        const g2 = { ...groupe, ...payload };
        for (const s of seancesDuGroupe(groupeSeances, groupe.id)) {
          if (!s.lien_visio) await appliquerSeanceAuxParticipants(s, g2, ids, { dates: 'aucune' });
        }
      }
      await Promise.all([fetchGroupes(), fetchUtilisateurs(), fetchSessions()]);
    },

    supprimerGroupe: async (groupe) => {
      const { error } = await supabase.from('groupes').delete().eq('id', groupe.id);
      if (error) throw new Error(error.message);
      setSelectedGroupeId(null);
      await Promise.all([fetchGroupes(), fetchUtilisateurs()]);
    },
  };

  const ouvrirGroupe = (groupeId) => { setSelectedGroupeId(groupeId); setActiveTab('groupes'); };
  const ouvrirFicheClientDepuisGroupe = (clientId) => { setExpandedClientId(clientId); setActiveTab('clients'); };

  // ─── DOCUMENTS ENVOYÉS PAR LES CLIENTS (2026-10-13) : notifications et navigation ──────────────
  const [depotsNonLus, setDepotsNonLus] = useState([]);
  const [piecesAFournirClient, setPiecesAFournirClient] = useState(0);
  const [ongletFicheDemande, setOngletFicheDemande] = useState(null);
  const fetchDepotsNonLus = async () => {
    if (!currentOrgId || !userRole) { setDepotsNonLus([]); setPiecesAFournirClient(0); return; }
    if (userRole === 'client') {
      const { data, error } = await supabase.from('pieces_demandees').select('id, statut').eq('client_id', currentUserId);
      setPiecesAFournirClient(error ? 0 : (data || []).filter(p => p.statut === 'a_fournir' || p.statut === 'a_refaire').length);
      return;
    }
    const { data, error } = await supabase.from('depots_clients').select('id, client_id, titre, destinataire, created_at')
      .eq('organisation_id', currentOrgId).is('consulte_le', null).order('created_at', { ascending: false });
    setDepotsNonLus(error ? [] : (data || []));
  };
  React.useEffect(() => {
    fetchDepotsNonLus();
    const t = setInterval(() => { if (!document.hidden) fetchDepotsNonLus(); }, 120000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, userRole, currentUserId]);
  // Ce que la cloche affiche : l'admin voit ce qui est adressé à l'organisme (ou à lui-même s'il est
  // le formateur du client) ; un formateur voit ce que ses clients lui ont adressé.
  const depotsPourMoi = React.useMemo(() => (depotsNonLus || []).filter(d => {
    const c = clients.find(x => String(x.id) === String(d.client_id));
    const estMonClient = c && String(c.formateur_id) === String(currentUserId);
    if (userRole === 'formateur') return estMonClient && d.destinataire !== 'organisme';
    return d.destinataire !== 'formateur' || estMonClient;
  }), [depotsNonLus, clients, currentUserId, userRole]);
  const ouvrirRecusClient = (clientId) => { setOngletFicheDemande('recus'); setExpandedClientId(clientId); setActiveTab('clients'); };

  const estPro = hasFeatureAccess(orgSettings, 'pro');
  // 03/11/2026 : veille — nombre d'informations de la dernière semaine pas encore traitées (badge du menu).
  const [veilleNonLues, setVeilleNonLues] = useState(0);
  useEffect(() => {
    if (userRole !== 'admin' || !estPro || !currentOrgId) { setVeilleNonLues(0); return; }
    let annule = false;
    compterVeilleNonTraitees(currentOrgId).then(n => { if (!annule) setVeilleNonLues(n); });
    return () => { annule = true; };
  }, [userRole, estPro, currentOrgId]);
  const nomUtilisateurConnecte = assignableFormateurs.find(f => String(f.id) === String(currentUserId))?.nom
    || (userRole === 'admin' ? (orgSettings?.nom || 'Administrateur') : 'Formateur');

  // ─── STATUT DES COMPTES CLIENTS (2026-10-19) : lu via statut_comptes_clients() ─────────────────
  const [statutsComptes, setStatutsComptes] = useState({});
  const [statutsComptesDispo, setStatutsComptesDispo] = useState(false);
  const chargerStatutsComptes = React.useCallback(async () => {
    if (!currentOrgId || (userRole !== 'admin' && userRole !== 'formateur')) { setStatutsComptesDispo(false); return; }
    const { data, error } = await supabase.rpc('statut_comptes_clients');
    if (error) { setStatutsComptesDispo(false); return; } // SQL pas encore lancé : rien ne s'affiche
    const parClient = {};
    (data || []).forEach(r => { parClient[String(r.client_id)] = r; });
    setStatutsComptes(parClient);
    setStatutsComptesDispo(true);
  }, [currentOrgId, userRole]);
  React.useEffect(() => {
    chargerStatutsComptes();
    const t = setInterval(() => { if (!document.hidden) chargerStatutsComptes(); }, 300000);
    return () => clearInterval(t);
  }, [chargerStatutsComptes, clients.length]);
  const creerCompteClient = async (client) => {
    const idToast = `acces-${client.id}`;
    toast.loading(`Envoi de l'accès à ${client.email || client.email_contact || 'ce client'}…`, { id: idToast });
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      const resp = await fetch('/api/formateur/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.access_token}` },
        body: JSON.stringify({ type: 'compte_client', clientId: client.id, origin: window.location.origin }),
      });
      const r = await resp.json().catch(() => ({}));
      if (!resp.ok) { toast.error(r.error || "Le compte n'a pas pu être créé.", { id: idToast, duration: 8000 }); return false; }
      const email = client.email || client.email_contact || 'ce client';
      toast.success(r.sent
        ? `Compte créé : un lien pour choisir son mot de passe a été envoyé à ${email}.`
        : "Compte créé, mais l'email n'a pas pu partir : utilisez « Renvoyer le lien ».", { id: idToast, duration: 7000 });
      await chargerStatutsComptes();
      fetchUtilisateurs();
      return true;
    } catch (e) {
      toast.error('Erreur : ' + e.message, { id: idToast });
      return false;
    }
  };
  const clientsAccesDifferes = React.useMemo(() => new Set((clients || []).filter(c => c.acces_differe).map(c => String(c.id))), [clients]);
  const comptesCtx = { statuts: statutsComptes, dispo: statutsComptesDispo, estPro, recharger: chargerStatutsComptes, creerCompte: creerCompteClient, differes: clientsAccesDifferes };



  const fetchSessions = async () => {
    if (!currentOrgId) { setSessions([]); return; }
    const isStale = beginLoad('sessions');
    const pArchives = idsDossiersArchives(currentOrgId); // LOT C
    const colonnes = (await colonnesLegeres()).sessions;
    const { data, error } = await fetchAllRowsWithRetry(() => supabase.from('sessions').select(colonnes)
      .eq('organisation_id', currentOrgId).order('id', { ascending: true }), { label: 'séances' });
    const archives = await pArchives;
    if (isStale()) return;
    marquerCharge('sessions');
    if (data) setSessions(sansArchives(data, archives, 'client_id'));
    if (error) notifyLoadError('les séances');
  };

  const fetchUtilisateurs = async () => {
    if (!currentOrgId) { setFormateurs([]); setClients([]); return; }
    const isStale = beginLoad('utilisateurs');
    // 1. Formateurs (table 'utilisateurs') et 2. clients (table 'clients', source unique) — en parallèle
    const [fRes, cRes] = await Promise.all([
      fetchAllRowsWithRetry(() => supabase
        .from('utilisateurs')
        .select('id, nom, email, role, formateur_siret, formateur_nda, adresse_formateur, adresse_session, telephone, compagnie_assurance, numero_assurance_rcp')
        .eq('role', 'formateur')
        .eq('organisation_id', currentOrgId)
        .order('id', { ascending: true }), { label: 'formateurs' }),
      fetchAllRowsWithRetry(() => supabase.from('clients').select('*')
        .eq('organisation_id', currentOrgId).order('id', { ascending: true }), { label: 'clients' }),
    ]);
    if (isStale()) return;
    marquerCharge('utilisateurs');
    const formateursData = fRes.data;
    // LOT C : dossiers archivés à part (ils ne sont plus dans les listes ni dans la limite de l'offre).
    const clientsData = cRes.data ? cRes.data.filter(c => !c.archive_le) : cRes.data;
    if (cRes.data) setClientsArchives(cRes.data.filter(c => c.archive_le)
      .sort((a, b) => String(b.archive_le).localeCompare(String(a.archive_le))));
    if (fRes.error || cRes.error) notifyLoadError('les clients et formateurs');

    if (formateursData) {
      // AJOUT (2026-09-17) : tri alphabétique à la source, demandé par l'utilisateur ("comme toutes
      // les listes") — jusqu'ici les formateurs remontaient dans l'ordre brut de la base (ordre de
      // création), d'où par exemple le sélecteur "Formateur Référent" (InviteModal, création client)
      // affiché dans le désordre. En triant ICI, à la source, TOUTES les listes/menus déroulants qui
      // consomment `formateurs` en héritent automatiquement, sans avoir à modifier chaque endroit un
      // par un — une vue qui a déjà son propre tri local (ex. FormateurAdminView) n'est pas affectée
      // négativement (trier un tableau déjà trié ne change rien).
      setFormateurs([...formateursData].sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' })));
    }

    if (clientsData) {
      // Mapping standard depuis la table 'clients'
      const mappedClients = clientsData.map(c => ({
        id: c.id,
        nom: c.nom_complet || "Client sans nom",
        email: c.email_contact,
        role: 'client',
        numero_dossier: c.numero_dossier,
        formateur_id: c.formateur_id,
        module_id: c.module_id,
        seances_effectuees: c.seances_effectuees || 0,
        seances_totales: c.seances_totales || 0,
        nomcomplet_client: c.nom_complet,
        nom_complet: c.nom_complet,
        nom: c.nom_complet || "Client sans nom",
        prenom: c.prenom || '',
        client_phone: c.telephone,
        telephone: c.telephone || '',
        client_email: c.email_contact,
        email_contact: c.email_contact,
        acces_differe: c.acces_differe === true,
        adresse_session: c.adresse_postale,
        adresse: c.adresse_postale || c.rue || '',
        rue: c.rue || '',
        code_postal: c.code_postal || '',
        ville: c.ville || '',
        montant_prestation: c.montant_prestation,
        prix_prestation: c.montant_prestation || '',
        pourcentage_formateur: c.pourcentage_formateur,
        modalite_formation: c.modalite_formation || 'Mixte',
        organisation_id: c.organisation_id || null,
        groupe_id: c.groupe_id || null
      }));
      // AJOUT (2026-09-17) : tri alphabétique à la source (même logique que formateurs ci-dessus).
      mappedClients.sort((a, b) => (a.nom_complet || a.nom || '').localeCompare(b.nom_complet || b.nom || '', 'fr', { sensitivity: 'base' }));
      setClients(mappedClients);
    }
  };

  const fetchDocuments = async () => {
    if (!currentOrgId) { setDocuments([]); setDocumentTemplates({}); return; }
    const isStale = beginLoad('documents');
    const pArchivesDocs = idsDossiersArchives(currentOrgId); // LOT C
    const colonnesDocs = (await colonnesLegeres()).documents;
    // 1. Documents classiques (contrats générés, preuves, etc.) et 2. modèles maîtres (table unifiée
    // module_step_resources, type='document' uniquement) — en parallèle, voir fetchAllRowsWithRetry.
    const [dRes, tRes] = await Promise.all([
      fetchAllRowsWithRetry(() => supabase.from('documents').select(colonnesDocs)
        .eq('organisation_id', currentOrgId).order('id', { ascending: true }), { label: 'documents' }),
      fetchAllRowsWithRetry(() => supabase.from('module_step_resources').select('*').eq('type', 'document')
        .eq('organisation_id', currentOrgId).order('id', { ascending: true }), { label: 'modèles de documents' }),
    ]);
    const archivesDocs = await pArchivesDocs;
    if (isStale()) return;
    marquerCharge('documents');
    const docsData = dRes.data;
    if (docsData) setDocuments(sansArchives(docsData, archivesDocs, 'user_id'));
    const modsData = tRes.data;
    const modErr = tRes.error;
    if (dRes.error || tRes.error) notifyLoadError('les documents');
    if (!modErr && modsData) {
      console.log(`[fetchDocuments] ${modsData.length} modèles documents récupérés.`);
      const templates = {};
      modsData.forEach(m => {
        // Mapping schema : titre -> nom, file_url -> url, destination -> routing
        if (m.titre && m.file_url) {
          const parsedMeta = (typeof m.metadata === 'string' && m.metadata.startsWith('{')) ? JSON.parse(m.metadata) : (m.metadata || {});
          templates[m.titre] = {
            id: m.id,
            url: m.file_url,
            name: m.titre,
            destination: m.destination || 'client',
            classification: parsedMeta.classification || 'telechargeable',
            document_group_id: m.document_group_id || null,
            metadata: parsedMeta
          };
        }
      });
      // Compléter avec les anciens "Modèle Référence" de la table documents
      // qui ne seraient pas encore dans module_step_resources
      const existingKeys = new Set(Object.keys(templates));
      const oldRefs = (docsData || []).filter(d =>
        (d.type_action === 'Modèle Référence' || d.type_document === 'Modèle Référence') &&
        d.nom && d.url && !existingKeys.has(d.nom)
      );
      oldRefs.forEach(r => {
        templates[r.nom] = { url: r.url, name: r.nom, destination: 'formateur', classification: 'a_signer' };
      });
      // AJOUT (2026-09-17) : tri alphabétique du catalogue de modèles (demandé par l'utilisateur,
      // "même les documents") — cette liste est un CATALOGUE (menu dans lequel on choisit un modèle à
      // ajouter/envoyer), sans ordre intentionnel, contrairement aux documents DÉJÀ assignés à un
      // client (module = ordre du parcours pédagogique, "Ajouts personnalisés" = ordre propre via son
      // champ `ordre`, tous deux volontairement laissés inchangés). Les objets JS conservent l'ordre
      // d'insertion des clés : reconstruire l'objet triées par titre suffit.
      setDocumentTemplates(Object.fromEntries(
        Object.entries(templates).sort((a, b) => a[0].localeCompare(b[0], 'fr', { sensitivity: 'base' }))
      ));
    } else if (modErr) {
      console.error("[fetchDocuments] Erreur lors de la récupération des ressources modèles :", modErr);
      // Fallback : utiliser les Modèles Référence de la table documents
      const refs = docsData?.filter(d => d.type_action === 'Modèle Référence' || d.type_document === 'Modèle Référence') || [];
      const templates = {};
      refs.forEach(r => {
        templates[r.nom] = { url: r.url, name: r.nom, destination: r.destination || 'formateur' };
      });
      setDocumentTemplates(Object.fromEntries(
        Object.entries(templates).sort((a, b) => a[0].localeCompare(b[0], 'fr', { sensitivity: 'base' }))
      ));
    }
  };

  const fetchUnreadMessagesCount = async () => {
    if (!currentUserId) { setUnreadMessagesCount(0); return; }
    const { count, error } = await supabase.from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', String(currentUserId))
      .is('read_at', null);
    if (!error) setUnreadMessagesCount(count || 0);
  };

  const fetchOrgSettings = async () => {
    if (!currentOrgId) return;
    const { data } = await supabase.from('organisations').select('id, nom, logo_url, siret, adresse, code_postal, ville, region, nda, site_web, subscription_status, trial_ends_at, stripe_customer_id, subscribed_plan').eq('id', currentOrgId).single();
    if (data) setOrgSettings(data);
  };

  // Recharge les infos org dès que currentOrgId est disponible (notamment au rechargement de page)
  React.useEffect(() => {
    if (currentOrgId) fetchOrgSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  const fetchBrandSettings = React.useCallback(async () => {
    if (!currentOrgId) return;
    const { data } = await supabase.from('organisation_settings').select('*').eq('organisation_id', currentOrgId).maybeSingle();
    // 2026-10-19 : on repart toujours des valeurs par défaut (avant : fusion avec l'organisme précédent).
    if (!data) {
      setBrandSettings(MARQUE_PAR_DEFAUT);
      ['--brand-primary', '--brand-primary-light', '--brand-primary-dark'].forEach(v => document.documentElement.style.removeProperty(v));
    }
    if (data) {
      setBrandSettings({ ...MARQUE_PAR_DEFAUT, ...data });
      if (data.primary_color) {
        document.documentElement.style.setProperty('--brand-primary', data.primary_color);
        document.documentElement.style.setProperty('--brand-primary-light', data.primary_color + '20');
        document.documentElement.style.setProperty('--brand-primary-dark', data.primary_color + 'CC');
      }
    }
  }, [supabase, currentOrgId]);

  React.useEffect(() => { fetchBrandSettings(); }, [fetchBrandSettings]);

  // DÉCISION (20/10/2026) : la bibliothèque pédagogique est propre à chaque organisme — la fonction
  // skorup_bibliotheque ne renvoie que ses ressources (dossier org_<organisme>/ + anciens fichiers
  // déposés par une personne de l'organisme). Repli sur l'ancienne liste tant que le SQL n'est pas lancé.
  const fetchPedagogicalResources = async () => {
    const versRessource = (chemin, extra = {}) => ({
      ...extra, id: chemin, path: chemin, name: chemin.split('/').pop(),
      url: supabase.storage.from('ressources-pedagogiques').getPublicUrl(chemin).data?.publicUrl,
    });
    if (!currentOrgId) { setPedagogicalResources([]); return; }
    const { data: lignes, error: errRpc } = await supabase.rpc('skorup_bibliotheque');
    if (!errRpc && Array.isArray(lignes)) {
      setPedagogicalResources(lignes.map(l => versRessource(l.chemin, { metadata: { size: l.taille }, created_at: l.cree_le })));
      return;
    }
    const { data, error } = await supabase.storage.from('ressources-pedagogiques').list();
    if (!error && data) {
      setPedagogicalResources(data.filter(f => f.id && f.name !== '.emptyFolderPlaceholder').map(f => versRessource(f.name, f)));
    }
  };

  const handleUploadResource = async (file) => {
    if (!file || !newResourceName.trim()) return;
    setIsUploadingResource(true);
    const ext = file.name.split('.').pop();
    const cleanName = newResourceName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const fileName = `${Date.now()}_${cleanName}.${ext}`;

    const { error } = await supabase.storage.from('ressources-pedagogiques').upload(fileName, file);

    if (!error) {
      toast.success('Ressource pédagogique ajoutée avec succès !');
      setNewResourceName('');
      fetchPedagogicalResources();
    } else {
      console.error("Erreur upload ressource:", error);
      toast.error('Erreur lors de l\'upload : ' + error.message);
    }
    setIsUploadingResource(false);
  };

  const handleInviteUser = async (formData) => {
    const { email, nom, role } = formData;
    // 2026-10-21 : « Envoyer l'invitation par email maintenant » décoché → dossier créé SANS compte de
    // connexion et SANS aucun email ; l'accès s'envoie plus tard (bouton « Envoyer l'accès au client »,
    // api/formateur/notify.js type compte_client, qui crée le compte avec le même identifiant).
    const sansEmail = role === 'client' && formData.envoyerInvitation === false;
    setIsAddingUser(true);
    try {
      // 0. Plafond de dossiers clients selon le plan (décision produit du 2026-07-28) — les dossiers
      // créés sans email comptent aussi (décision du 21/10/2026).
      if (role === 'client') {
        const clientLimit = getClientLimitForOrg(orgSettings);
        if (Number.isFinite(clientLimit)) {
          const { count: currentClientCount, error: countError } = await compterDossiersActifs(currentOrgId); // LOT C
          if (countError) {
            console.error('Erreur vérification du quota de dossiers clients:', countError);
            toast.error("Impossible de vérifier votre quota de dossiers clients pour le moment. Réessayez.");
            return;
          }
          if ((currentClientCount || 0) >= clientLimit) {
            toast.error(
              `Vous avez atteint la limite de ${clientLimit} dossiers clients de votre plan actuel. Passez à un plan supérieur pour en ajouter davantage.`,
              { duration: 8000 }
            );
            return;
          }
        }
      }

      let newUserId = null;
      if (sansEmail) {
        // Pas de compte de connexion : on évite seulement un doublon d'email dans l'organisme.
        const emailNet = String(email || '').trim();
        const { data: dejaLaTous } = await supabase.from('clients').select('*')
          .eq('organisation_id', currentOrgId)
          .ilike('email_contact', emailNet.replace(/[\\%_]/g, (m) => '\\' + m))
          .limit(20);
        const dejaLa = (dejaLaTous || []).filter(r => !r.archive_le); // LOT C : un dossier archivé ne bloque pas
        if (dejaLa.length > 0) {
          toast.error('Un client avec cette adresse email existe déjà dans votre organisme.');
          return;
        }
        // Identifiant du futur compte (crypto.randomUUID n'existe pas sur certains navigateurs anciens).
        const c = window.crypto;
        if (c && typeof c.randomUUID === 'function') newUserId = c.randomUUID();
        else {
          const o = new Uint8Array(16); c.getRandomValues(o);
          o[6] = (o[6] & 0x0f) | 0x40; o[8] = (o[8] & 0x3f) | 0x80;
          const h = Array.from(o, x => x.toString(16).padStart(2, '0')).join('');
          newUserId = `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
        }
      } else {
        // 1. Inviter via Edge Function sécurisée (service_role côté serveur)
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const inviteRes = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/invite-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSession.access_token}`
          },
          body: JSON.stringify({ email, nom, role, action: 'invite', redirectTo: window.location.origin })
        });
        const inviteResult = await inviteRes.json().catch(() => ({}));
        if (!inviteRes.ok || !inviteResult.userId) {
          console.error("Erreur invitation Supabase:", inviteResult);
          toast.error(`L'invitation a échoué : ${inviteResult.error || 'réponse inattendue du serveur'}`);
          return;
        }
        newUserId = inviteResult.userId;
      }

      // 2. Créer l'entrée dans la table correspondante
      if (role === 'client') {
        const formateurIntId = formData.formateur_id ? Number(formData.formateur_id) : null;
        const formateurRecord = formateurIntId ? formateurs.find(f => f.id === formateurIntId) : null;
        const { error: dbError } = await supabase.from('clients').insert([{
          id: newUserId,
          nom_complet: nom,
          email_contact: email,
          formateur_id: formateurIntId,
          formateur_auth_uid: formateurRecord?.auth_uid || null,
          organisation_id: currentOrgId,
          ...(sansEmail ? { acces_differe: true } : {})
        }]);
        if (dbError) {
          console.error("Erreur DB clients:", dbError);
          toast.error(sansEmail
            ? `Le dossier n'a pas pu être créé : ${dbError.message}`
            : `Le compte a été créé mais la fiche client n'a pas pu être enregistrée : ${dbError.message}. Prévenez le support avant de réessayer.`,
            { duration: 10000 });
          return;
        }
        // Initialisation automatique des documents depuis la modélothèque
        const defaultClientDocs = Object.entries(documentTemplates || {})
          .filter(([, tpl]) => (tpl.destination || 'client') === 'client')
          .map(([titre, tpl], idx) => ({
            client_id: newUserId,
            template_titre: titre,
            template_url: tpl.url || null,
            destination: 'client',
            ordre: idx,
            organisation_id: currentOrgId
          }));
        if (defaultClientDocs.length > 0) {
          const { error: docsError } = await supabase.from('client_documents').insert(defaultClientDocs);
          if (docsError) console.error('Erreur init client_documents:', docsError);
        }
      } else {
        // Pour les formateurs : on garde la table 'utilisateurs' (ID entier automatique)
        // On stocke aussi l'auth_uid (UUID Supabase Auth) pour les balises de documents
        const { error: dbError } = await supabase.from('utilisateurs').insert([{
          nom: nom,
          email: email,
          role: role,
          organisation_id: currentOrgId,
          auth_uid: newUserId || null
        }]);
        if (dbError) {
          console.error("Erreur DB utilisateurs après invitation:", dbError);
          toast.error(`Le compte a été créé mais le formateur n'a pas pu être enregistré : ${dbError.message}. Prévenez le support avant de réessayer.`, { duration: 10000 });
          return;
        }
      }

      toast.success(sansEmail
        ? `Dossier de ${nom} créé. Aucun email n'a été envoyé : utilisez « Envoyer l'accès au client » dans sa fiche quand vous serez prêt.`
        : `✅ Invitation envoyée par email à ${email} !`, { duration: sansEmail ? 8000 : 4000 });
      setIsInviteModalOpen(false);
      fetchUtilisateurs();
    } catch (err) {
      console.error('handleInviteUser:', err);
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setIsAddingUser(false);
    }
  };

  // --- Actions Navigation ---
  // preserveTab (2026-07-27) : true UNIQUEMENT lors d'une restauration SILENCIEUSE de session déjà
  // ouverte (F5, réouverture d'onglet — voir initSession ci-dessus) — dans ce cas on NE TOUCHE PAS à
  // l'onglet, qui a déjà été correctement restauré depuis l'URL par le useState(activeTab) plus haut.
  // Pour une connexion fraîche (formulaire de login, fin d'invitation/reset mot de passe), on continue
  // de rediriger vers la page d'accueil du rôle comme avant — preserveTab reste false partout ailleurs.
  const handleLogin = (role, id = null, orgId = null, preserveTab = false) => {
    // Un admin qui clique sur "Mon espace Formateur" ouvre un nouvel onglet vers la même URL
    // avec ?view=formateur — cette même session/compte se connecte alors normalement (role='admin'
    // renvoyé par la DB), mais on affiche l'espace formateur à la place, avec le même currentUserId.
    const wantsFormateurView = role === 'admin' && new URLSearchParams(window.location.search).get('view') === 'formateur';
    setUserRole(wantsFormateurView ? 'formateur' : role);
    setIsAdminActingAsFormateur(wantsFormateurView);
    setCurrentUserId(id);
    setCurrentOrgId(orgId);
    // FIX (nouvelle fonctionnalité, 2026-09-11) : lien direct "?client=<id>" envoyé par email au
    // formateur lors d'une assignation (voir assignFormateur / api/formateur/notify-assignment.js)
    // — ouvre directement la fiche du client concerné (onglet "Mes clients", client déplié) plutôt
    // que la page d'accueil habituelle. S'applique qu'il s'agisse d'un vrai compte formateur (role
    // === 'formateur') ou de l'admin agissant comme son propre coach (wantsFormateurView) — mais
    // jamais pour un client, qui n'a pas accès à cet onglet. Prioritaire même sur une restauration
    // silencieuse de session (preserveTab) : un F5 sur ce lien doit rouvrir la même fiche.
    const deepLinkClientId = new URLSearchParams(window.location.search).get('client');
    if (deepLinkClientId && (role === 'formateur' || wantsFormateurView)) {
      const parsedId = Number(deepLinkClientId);
      setExpandedClientId(Number.isNaN(parsedId) ? deepLinkClientId : parsedId);
      setActiveTab('clients');
      window.history.replaceState({}, document.title, window.location.pathname + (wantsFormateurView ? '?view=formateur' : ''));
      return;
    }
    // FIX (nouvelle fonctionnalité, 2026-09-11) : lien direct "?tab=mes_documents" envoyé par email
    // au CLIENT lors de l'envoi d'un document (voir notifyClientNewDocument /
    // api/client/notify-new-document.js) — ouvre directement son onglet "Mes Documents" plutôt que
    // sa page d'accueil habituelle. Réservé au rôle client (seul rôle où cet onglet existe — voir
    // ROLE_TABS plus haut dans le fichier).
    const deepLinkTab = new URLSearchParams(window.location.search).get('tab');
    if (deepLinkTab === 'mes_documents' && role === 'client') {
      setActiveTab('mes_documents');
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    if (preserveTab) return;
    if (wantsFormateurView) { setActiveTab('accueil_formateur'); return; }
    if (role === 'admin') setActiveTab('dashboard');
    if (role === 'formateur') setActiveTab('accueil_formateur');
    if (role === 'client') setActiveTab('accueil');
  };

  const handleLogout = async () => {
    // 2026-10-21 : la mémoire de l'ouverture instantanée est effacée à la déconnexion.
    try { Object.keys(localStorage).filter(k => k.startsWith('skorup_cache_') || k.startsWith('skorup_profil_')).forEach(k => localStorage.removeItem(k)); } catch (_) { /* rien */ }
    await supabase.auth.signOut();
    setUserRole(null);
    setCurrentUserId(null);
    setCurrentOrgId(null);
    // SÉCURITÉ (2026-10-19) : la personne suivante sur le même navigateur ne doit pas voir le logo,
    // le nom ni les couleurs de l'organisme précédent.
    setOrgSettings(null);
    setBrandSettings(MARQUE_PAR_DEFAUT);
    ['--brand-primary', '--brand-primary-light', '--brand-primary-dark'].forEach(v => document.documentElement.style.removeProperty(v));
    setIsAdminActingAsFormateur(false);
    setActiveTab('accueil');
    setMobileMenuOpen(false);
    // Revenir explicitement à la racine (page de connexion) : le useEffect de synchronisation
    // URL<->onglet ci-dessous ne s'exécute que lorsque userRole est renseigné (voir plus bas), donc il
    // ne remettrait pas seul l'URL à '/' ici puisque userRole vient d'être mis à null.
    window.history.replaceState(null, '', '/');
  };

  // --- Détection retour Stripe Checkout ---
  useEffect(() => {
    if (!currentOrgId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      toast.success('🎉 Abonnement activé ! Bienvenue sur SkorUp Pro.');
      window.history.replaceState({}, document.title, window.location.pathname);
      // Rafraîchir après 3s (laisser le temps au webhook Stripe de mettre à jour Supabase)
      setTimeout(() => fetchOrgSettings(), 3000);
    }
    if (params.get('checkout') === 'cancel') {
      toast('Paiement annulé. Vous pouvez réessayer depuis "Mon Abonnement".', { icon: 'ℹ️' });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // Retour depuis le portail client Stripe (changement/annulation de plan en self-service).
    if (params.get('billing') === 'return') {
      window.history.replaceState({}, document.title, window.location.pathname);
      // Rafraîchir après 3s puis à nouveau après 8s (laisser le temps au webhook Stripe de
      // mettre à jour Supabase — un changement de plan peut prendre un peu plus de temps
      // qu'un premier paiement à se propager).
      setTimeout(() => fetchOrgSettings(), 3000);
      setTimeout(() => fetchOrgSettings(), 8000);
    }
  }, [currentOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Rafraîchit les infos d'abonnement quand l'utilisateur revient sur l'onglet ---
  // (le portail client Stripe s'ouvre dans un nouvel onglet ; sans ceci, l'onglet d'origine
  // resterait affiché avec l'ancien plan tant que la page n'est pas rechargée manuellement,
  // même après que le webhook Stripe ait correctement mis à jour Supabase.)
  useEffect(() => {
    if (!currentOrgId) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchOrgSettings();
    };
    window.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  // --- Badge "messages non lus" (menu latéral) : retour sur l'onglet + sondage 60s ---
  // Pas d'infra realtime dans ce projet (voir MessagesView) : le sondage périodique est le moyen
  // le plus simple de signaler un nouveau message reçu pendant que l'app reste ouverte.
  useEffect(() => {
    if (!currentUserId) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnreadMessagesCount();
    };
    window.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    const interval = setInterval(() => { if (!document.hidden) fetchUnreadMessagesCount(); }, 60000);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Rafraîchit aussi dès qu'on quitte l'onglet Messagerie (les conversations ouvertes y ont été
  // marquées comme lues par MessagesView), pour que le badge redescende immédiatement.
  const prevActiveTabForUnread = React.useRef(activeTab);
  useEffect(() => {
    if (prevActiveTabForUnread.current === 'messagerie' && activeTab !== 'messagerie') {
      fetchUnreadMessagesCount();
    }
    prevActiveTabForUnread.current = activeTab;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // --- Suppression Sécurisée (Cascade & Auth) ---
  const handleDeleteClient = async (clientId) => {
    // LOT C (2026-10-22) : le serveur (delete-user v2) archive le dossier d'un client qui a déjà signé
    // (rien n'est effacé) et supprime entièrement les autres. 27/10/2026 : userType « client » (celui
    // qu'attend delete-user v2, en ligne) ; « client_v2 » partait dans la branche formateur et échouait.
    // La réponse doit porter version 2, sinon on le signale.
    const idToast = `suppr-client-${clientId}`;
    toast.loading('Suppression en cours…', { id: idToast });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ userId: clientId, userType: 'client' })
      });
      let result = {};
      try { result = await res.json(); } catch (_) { result = {}; }
      if (!res.ok || result.version !== 2) throw new Error(result.version !== 2 && res.status === 403 ? "le service de suppression n'est pas encore à jour (delete-user)" : (result.error || 'Erreur suppression'));
      oublierArchives();
      setExpandedClientId(null);
      await Promise.all([fetchUtilisateurs(), fetchSessions(), fetchDocuments()]);
      if (result.archive) {
        toast.success("Ce client avait déjà signé des documents : son dossier est archivé. Son accès à SkorUp est fermé et ses documents signés sont conservés (Clients → Dossiers archivés).", { id: idToast, duration: 10000 });
      } else {
        toast.success("Client et toutes ses données supprimés.", { id: idToast });
      }
      if (result.compteSupprime === false) toast.error("Attention : son compte de connexion n'a pas pu être supprimé. Contactez le support SkorUp.", { duration: 9000 });
      return;
    } catch (err) {
      toast.dismiss(idToast);
      console.error("Erreur suppression client:", err);
      // CORRECTIF (2026-08-05) : la fonction Edge "delete-user" supprime d'abord la fiche client
      // (table clients + données liées) PUIS tente de supprimer le compte Auth Supabase correspondant.
      // Pour certains clients (invitation jamais finalisée, compte déjà retiré côté Auth par ailleurs...),
      // cette seconde étape échoue avec "User not found" alors que la fiche, elle, a bien été supprimée —
      // l'admin voyait donc une erreur rouge malgré une suppression réellement effectuée. On vérifie ici
      // si la fiche existe encore vraiment avant d'afficher une erreur : si elle a disparu, la suppression
      // a fonctionné malgré le message technique renvoyé par la fonction.
      const { data: stillExists } = await supabase.from('clients').select('id, archive_le').eq('id', clientId).maybeSingle();
      oublierArchives();
      if (!stillExists || stillExists.archive_le) setExpandedClientId(null);
      await fetchUtilisateurs();
      await fetchSessions();
      await fetchDocuments();
      if (stillExists && !stillExists.archive_le) {
        toast.error("Le client n'a pas été supprimé : " + err.message + ". Rien n'a été effacé.", { duration: 9000 });
      } else if (stillExists) {
        toast.success("Dossier archivé : ses documents signés sont conservés (Clients → Dossiers archivés).", { duration: 8000 });
      } else if (!stillExists) {
        toast("Client supprimé — son compte de connexion avait déjà été retiré séparément (message technique sans conséquence).", { icon: '⚠️', duration: 6000 });
      } else {
        toast.error("Erreur lors de la suppression : " + err.message);
      }
    }
  };

  const handleDeleteFormateur = async (formateurId) => {
    // Garde-fou : ce compte peut apparaître dans la liste des formateurs (voir "assignableFormateurs")
    // s'il s'agit d'un admin agissant aussi comme formateur — on ne le supprime jamais depuis cet écran,
    // ce serait supprimer le compte admin lui-même (perte d'accès totale à l'organisme).
    if (String(formateurId) === String(currentUserId) && userRole === 'admin') {
      toast.error("Impossible de supprimer votre propre compte administrateur depuis cet écran.");
      return;
    }
    // Garde-fou multi-tenant : on ne supprime/désassigne que dans le périmètre de l'organisation courante
    // (cf. audit sécurité — ce endpoint acceptait n'importe quel formateurId sans vérifier son organisation_id).
    if (!currentOrgId) {
      toast.error("Organisation introuvable, suppression annulée.");
      return;
    }
    // LOT B (2026-10-21) : ordre sûr. On vérifie, on libère ses clients et groupes en notant lesquels,
    // on supprime la fiche en contrôlant qu'elle l'a bien été (sinon tout est remis comme avant), et on ne
    // supprime le compte de connexion qu'ensuite.
    const idToast = `suppr-formateur-${formateurId}`;
    toast.loading('Suppression du formateur…', { id: idToast });
    let liberes = { clients: [], groupes: [], seances: [] };
    const retablir = async () => {
      if (liberes.clients.length) await supabase.from('clients').update({ formateur_id: formateurId }).in('id', liberes.clients);
      if (liberes.groupes.length) await supabase.from('groupes').update({ formateur_id: formateurId }).in('id', liberes.groupes);
      if (liberes.seances.length) await supabase.from('groupe_seances').update({ formateur_id: formateurId }).in('id', liberes.seances);
    };
    try {
      const { data: fiche, error: errFiche } = await supabase.from('utilisateurs').select('id, nom')
        .eq('id', formateurId).eq('organisation_id', currentOrgId).maybeSingle();
      if (errFiche || !fiche) { toast.error("Formateur introuvable dans votre organisme : rien n'a été supprimé.", { id: idToast }); return; }
      const lire = async (table) => {
        const { data, error } = await supabase.from(table).select('id').eq('formateur_id', formateurId).eq('organisation_id', currentOrgId);
        return error ? [] : (data || []).map(r => r.id);
      };
      const [cl, gr, gs] = await Promise.all([lire('clients'), lire('groupes'), lire('groupe_seances')]);
      for (const [table, ids, cle] of [['clients', cl, 'clients'], ['groupes', gr, 'groupes'], ['groupe_seances', gs, 'seances']]) {
        if (!ids.length) continue;
        const { error: e } = await supabase.from(table).update({ formateur_id: null }).in('id', ids);
        if (e) { await retablir(); toast.error(`Suppression annulée (${e.message}). Rien n'a été modifié.`, { id: idToast }); return; }
        liberes[cle] = ids;
      }
      // LOT C (2026-10-22) : le serveur (delete-user v2) supprime la fiche PUIS le compte de connexion
      // (le bon identifiant). On vérifie ensuite nous-mêmes que la fiche a bien disparu.
      let reponse = {};
      let okServeur = false;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ userId: formateurId, userType: 'formateur' })
        });
        okServeur = resp.ok;
        try { reponse = await resp.json(); } catch (_) { reponse = {}; }
      } catch (e) { reponse = { error: e.message }; }
      const { data: encore, error: errRelecture } = await supabase.from('utilisateurs').select('id').eq('id', formateurId).maybeSingle();
      if (errRelecture) {
        toast.error('Vérification impossible : actualisez la page pour voir si le formateur a bien été supprimé.', { id: idToast, duration: 9000 });
        await Promise.all([fetchUtilisateurs(), fetchGroupes()]);
        return;
      }
      if (encore) {
        await retablir();
        toast.error(`Le formateur n'a pas pu être supprimé${reponse.error ? ' : ' + reponse.error : ''}. Ses clients et ses groupes ont été remis comme avant.`, { id: idToast, duration: 9000 });
        await fetchUtilisateurs();
        return;
      }
      const compteSupprime = okServeur && reponse.version === 2 && reponse.compteSupprime !== false;
      await Promise.all([fetchUtilisateurs(), fetchGroupes()]);
      const n = liberes.clients.length;
      toast.success(`${fiche.nom || 'Le formateur'} a été supprimé.${n ? ` ${n} client${n > 1 ? 's sont' : ' est'} maintenant sans formateur : pensez à leur en attribuer un.` : ''}`, { id: idToast, duration: 8000 });
      if (!compteSupprime) toast.error("Attention : son compte de connexion n'a pas pu être supprimé. Contactez le support SkorUp.", { duration: 9000 });
    } catch (err) {
      console.error("Erreur suppression formateur:", err);
      await retablir().catch(() => {});
      toast.error('Erreur : ' + err.message, { id: idToast });
      fetchUtilisateurs();
    }
  };

  // LOT C : restaurer un dossier archivé (il revient dans la liste, sans accès : « Envoyer l'accès au client »).
  const restaurerDossierClient = async (c) => {
    const limite = getClientLimitForOrg(orgSettings);
    if (Number.isFinite(limite)) {
      const { count, error } = await compterDossiersActifs(currentOrgId);
      if (error) { toast.error('Impossible de vérifier votre nombre de dossiers clients pour le moment. Réessayez.'); return; }
      if ((count || 0) >= limite) { toast.error(`Votre offre permet ${limite} dossiers clients actifs : libérez une place avant de restaurer ce dossier.`, { duration: 8000 }); return; }
    }
    const { data, error } = await supabase.from('clients').update({ archive_le: null })
      .eq('id', c.id).eq('organisation_id', currentOrgId).select('id');
    if (error || !data || data.length === 0) { toast.error('Restauration impossible' + (error ? ' : ' + error.message : '') + '.'); return; }
    oublierArchives();
    await Promise.all([fetchUtilisateurs(), fetchSessions(), fetchDocuments()]);
    toast.success(`Dossier de ${c.nom_complet || 'ce client'} restauré. Il n'a plus d'accès à SkorUp : utilisez « Envoyer l'accès au client » si besoin.`, { duration: 9000 });
  };

  // --- Suppression via modale de confirmation ---
  const handleConfirmDelete = async () => {
    if (!targetToDelete) return;
    setIsDeleteModalOpen(false);
    if (targetToDelete.type === 'template') {
      await handleDeleteDocxTemplate(targetToDelete.id);
    } else if (targetToDelete.type === 'client') {
      await handleDeleteClient(targetToDelete.id);
    } else if (targetToDelete.type === 'formateur') {
      await handleDeleteFormateur(targetToDelete.id);
    }
    setTargetToDelete(null);
  };

  // --- Actions Supabase : Utilisateurs ---
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserName.trim() || !newUserEmail.trim()) return;
    setIsAddingUser(true);

    let error;
    if (newUserRole === 'client') {
      const { error: clientError } = await supabase
        .from('clients')
        .insert([{
          nom_complet: newUserName,
          email_contact: newUserEmail,
          telephone: clientPhone,
          formateur_id: null,
          organisation_id: currentOrgId
        }]);
      error = clientError;
    } else {
      const { error: userError } = await supabase
        .from('utilisateurs')
        .insert([{
          nom: newUserName,
          email: newUserEmail,
          role: newUserRole,
          organisation_id: currentOrgId
        }])
        .select();
      error = userError;
    }

    if (error) {
      console.error("Erreur ajout user", error);
      toast.error('Erreur ajout serveur : ' + error?.message);
    } else {
      toast.success(`Utilisateur ${newUserName} ajouté avec succès !`);
      await fetchUtilisateurs();
      await fetchDocuments();
      setNewUserName('');
      setNewUserEmail('');
      setClientPhone('');
      setClientEmail('');
    }
    setIsAddingUser(false);
  };

  // AJOUT (2026-09-18) : équivalent de instantiateDocument mais pour les ressources de type "exercice"
  // posées en Documents de début/fin d'un module — crée une séance "synthétique" dans `sessions`
  // (numero_seance: null, pour ne pas fausser isModuleCompletedForClient) afin que l'exercice apparaisse
  // dans l'onglet "Exercices" du client (ExercicesView, qui ne lit QUE la table `sessions`), avec le
  // vrai workflow de rendu/correction. Appelée immédiatement côté ADMIN — à l'assignation du module
  // (distributeDocumentsForModule) et à l'ajout d'une nouvelle ressource à un module déjà assigné
  // (handleAddModuleMomentResource) — plutôt que d'attendre que le client ouvre une page précise :
  // signalé par l'utilisateur le 2026-09-18 ("il faut que les documents de début du module apparaissent
  // chez TOUS les clients qui y sont assignés", sans dépendre de l'onglet visité par le client.
  const instantiateExerciceSession = async (client, resource, moment) => {
    if (moment === 'fin' && !isModuleCompletedForClient(client.id, sessions, client.module_id)) return; // pas encore débloqué
    try {
      const { data: existing } = await supabase.from('sessions').select('id').eq('client_id', client.id).eq('nom', resource.titre).maybeSingle();
      if (existing) return; // anti-doublon (même principe que instantiateDocument)
      const meta = typeof resource.metadata === 'string' && resource.metadata.startsWith('{')
        ? (() => { try { return JSON.parse(resource.metadata); } catch { return {}; } })()
        : (resource.metadata || {});
      const { error } = await supabase.from('sessions').insert([{
        client_id: client.id,
        module_id: client.module_id,
        numero_seance: null,
        nom: resource.titre,
        type_activite: 'exercice',
        ressource_titre: resource.titre,
        file_url: resource.file_url || null,
        metadata: { ...meta, moment },
        statut: 'À venir',
        statut_client: 'À venir',
        statut_formateur: 'À venir',
        organisation_id: client.organisation_id || currentOrgId || null,
      }]);
      if (error) console.error('[instantiateExerciceSession] Erreur insertion:', error.message, { client: client.id, res: resource.titre });
    } catch (e) {
      console.error('[instantiateExerciceSession] Exception:', e.message);
    }
  };

  const instantiateDocument = async (client, templateResource, moment) => {
    const meta = typeof templateResource.metadata === 'string' && templateResource.metadata.startsWith('{') ? JSON.parse(templateResource.metadata) : (templateResource.metadata || {});
    const classification = meta.classification || 'telechargeable';
    const hasVisualFields = meta.has_visual_fields === true;
    const visualTemplateId = meta.visual_template_id || null;
    // Destinataires (2026-07-27) : un ou plusieurs parmi client/formateur/organisme (n'importe quel
    // admin de l'organisation) → détermine visible_client / visible_formateur / requiresOrganisme.
    // parseDestinationRoles comprend aussi les anciennes valeurs 'client' | 'formateur' | 'both'.
    const destRoles = parseDestinationRoles(templateResource.destination);
    const visClient      = destRoles.includes('client');
    const visFormateur   = destRoles.includes('formateur');
    const requiresOrganisme = destRoles.includes('organisme');
    // Si le formateur doit signer, on lui assigne le document explicitement via assigned_formateur_id
    const assignedFormateurId = visFormateur ? (client.formateur_id || null) : null;

    // Anti-doublon : ne pas recréer un document déjà existant pour ce client
    // LOT 3 (2026-10-20) : .limit(1) au lieu de .maybeSingle() — dès que 2 lignes avaient le même nom,
    // maybeSingle renvoyait une erreur → « aucun document » → un doublon de plus à chaque distribution.
    const { data: _existants } = await supabase.from('documents')
      .select('id, url, signe_par_client, signe_par_formateur, signe_par_organisme, statut, signed_pdf_url, metadata').eq('user_id', client.id).eq('nom', templateResource.titre)
      .order('id', { ascending: true }).limit(1);
    const existing = (_existants || [])[0] || null;
    if (existing) {
      if (!hasVisualFields) return; // doc classique : anti-doublon strict
      if (documentEstSigne(existing)) return; // SIGNÉ (par qui que ce soit, lot 2) : ne jamais écraser
      // Vérifier si le doc a déjà été préparé avec le nouveau système (metadata.fields + resolved_values)
      const existMeta = (() => { try { return typeof existing.metadata === 'string' ? JSON.parse(existing.metadata) : (existing.metadata || {}); } catch { return {}; } })();
      if (existMeta.prefilled === true) return; // PDF pré-généré → skip (format le plus récent)
      if (existMeta.fields?.length > 0 && existMeta.resolved_values) return; // fallback métadonnées → skip
      // Ancien format sans données → supprimer pour régénérer avec pré-génération PDF
      await supabase.from('documents').delete().eq('id', existing.id);
    }

    let docId = null;

    // ── Branche 1 : template visuel avec balises → générer PDF pré-rempli côté admin + upload Storage ──
    // L'admin (accès DB complet, pdf-lib disponible) génère le PDF et l'uploade une fois.
    // Le client charge simplement l'URL pré-générée → aucun overlay côté client, aucun problème RLS.
    if (hasVisualFields && visualTemplateId) {
      let tplFields = null;
      let resolvedValues = null;
      try {
        // 1. Récupérer les champs (métadonnées embarquées prioritaires → DB fallback)
        tplFields = (meta.template_fields && meta.template_fields.length > 0) ? meta.template_fields : null;
        if (!tplFields) {
          const { data: dbFields, error: fieldsErr } = await supabase
            .from('template_fields').select('*').eq('template_id', visualTemplateId).order('page', { ascending: true });
          if (fieldsErr) throw fieldsErr;
          tplFields = dbFields;
        }
        if (!tplFields || tplFields.length === 0) throw new Error('Aucun champ de positionnement trouvé');

        // 2. Construire les valeurs résolues (admin-side → accès complet données client + formateur)
        const formateur = (formateurs || []).find(f => String(f.id) === String(client.formateur_id));
        const today = new Date().toLocaleDateString('fr-FR');
        resolvedValues = {
          nomcomplet_client:   (client.nom_complet || ((client.prenom || '') + ' ' + (client.nom || '')).trim() || '').trim(),
          numero_dossier_client: client.numero_dossier || '',
          client_email:        client.email_contact || client.email || '',
          client_phone:        client.telephone || '',
          rue_client:          client.adresse || client.adresse_postale || client.rue || '',
          code_postal_client:  client.code_postal || '',
          ville_client:        client.ville || '',
          region_client:       client.region || '',
          adresse_session:     client.adresse || client.adresse_postale || client.rue || '',
          prix_prestation:     client.prix_prestation || client.montant_prestation || '',
          ...(await valeursFinancement(client.id, client.prix_prestation || client.montant_prestation)),
          modalite_formation:  client.modalite_formation || '',
          formation_nom:       '',
          date_debut:          '',
          date_fin:            '',
          date_signature:      today,
          date_du_jour:        today,
          nom_client:          client.nom || '',
          prenom_client:       client.prenom || '',
          email_client:        client.email_contact || client.email || '',
          telephone_client:    client.telephone || '',
          ville:               client.ville || '',
          nom_formateur:       formateur?.nom || '',
          prenom_formateur:    formateur?.prenom || '',
          email_formateur:     formateur?.email || '',
          tel_formateur:       formateur?.telephone || '',
          telephone_formateur: formateur?.telephone || '',
          adresse_formateur:   formateur?.adresse_formateur || formateur?.adresse_pro || formateur?.adresse || '',
          region_formateur:    formateur?.region || '',
          formateur_siret:     formateur?.formateur_siret || formateur?.siret || '',
          formateur_nda:       formateur?.formateur_nda || formateur?.nda || '',
          compagnie_assurance: formateur?.compagnie_assurance || '',
          numero_assurance_rcp: formateur?.numero_assurance_rcp || '',
          // AJOUT (2026-09-15) : équivalent de date_signature (client) pour le formateur/organisme.
          date_signature_formateur: today,
          date_signature_organisme: today,
          // AJOUT (2026-09-16) : balises "initiales" — voir computeInitials() en haut du fichier.
          initiales_client:    computeInitials(client.nom_complet || ((client.prenom || '') + ' ' + (client.nom || '')).trim() || ''),
          initiales_formateur: computeInitials(formateur?.nom || ''),
          initiales_organisme: computeInitials(orgSettings?.nom || ''),
          nom_consultant:      formateur?.nom || '',
          email_consultant:    formateur?.email || '',
          tel_consultant:      formateur?.telephone || '',
        };
        // Balises d'adresse dérivées : adresse_client combinée, rue/CP/ville_formateur éclatées
        // LOT 3 (2026-10-20) : formation, dates réelles des séances et organisme (avant : toujours vides).
        try {
          const { data: _seancesClient } = await supabase.from('sessions').select('numero_seance, date').eq('client_id', client.id);
          Object.assign(resolvedValues, valeursFormationOrganisme({
            moduleNom: (modules || []).find(m => String(m.id) === String(client.module_id))?.nom,
            seances: _seancesClient || [], orgSettings,
          }));
        } catch (_) { /* valeurs laissées vides, comme avant */ }
        resolvedValues.adresse_client = [resolvedValues.rue_client, resolvedValues.code_postal_client, resolvedValues.ville_client].filter(Boolean).join(', ');
        { const _fAddr = parseAddressString(resolvedValues.adresse_formateur);
          resolvedValues.rue_formateur = _fAddr.rue;
          resolvedValues.code_postal_formateur = _fAddr.codePostal;
          resolvedValues.ville_formateur = _fAddr.ville; }

        // 3. Générer le PDF pré-rempli (navigateur admin, avec ignoreEncryption: true)
        const templateResp = await fetch(templateResource.file_url);
        if (!templateResp.ok) throw new Error(`HTTP ${templateResp.status} pour ${templateResource.file_url}`);
        let pdfBlob = new Blob([await templateResp.arrayBuffer()], { type: 'application/pdf' });

        // Les champs texte libre (texte_client/texte_formateur) sont comme les signatures/cases :
        // remplis par le SIGNATAIRE au moment de signer, pas par l'admin à la génération — ils ne
        // doivent donc PAS recevoir l'overlay "données" ici (sinon la boîte vide se retrouve gravée
        // en dur, non modifiable, dans le PDF pré-généré).
        const dataFields = tplFields.filter(f => f.field_type !== 'signature' && f.field_type !== 'checkbox' && f.field_type !== 'text_input' && !(f.tag || '').startsWith('signature_') && !(f.tag || '').startsWith('checkbox_') && !(f.tag || '').startsWith('texte_') && !estDateSignature(f));
        if (dataFields.length > 0) {
          pdfBlob = await overlayFieldsOnPdf(pdfBlob, dataFields, resolvedValues, {});
        }

        // 4. Uploader le PDF pré-rempli dans Supabase Storage
        const safeName = (templateResource.titre || 'doc')
          .normalize('NFD').replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 40);
        const storagePath = `prefilled-documents/${client.id}/${Date.now()}_${safeName}.pdf`;
        const { error: uploadErr } = await supabase.storage
          .from('documents')
          .upload(storagePath, pdfBlob, { contentType: 'application/pdf', upsert: true });
        if (uploadErr) throw uploadErr;
        const { data: { publicUrl: prefilledUrl } } = supabase.storage.from('documents').getPublicUrl(storagePath);

        // 5. Stocker avec URL pré-remplie + positions des champs signature/case/texte libre pour l'étape de signature
        const sigFields = tplFields.filter(f => f.field_type === 'signature' || f.field_type === 'checkbox' || f.field_type === 'text_input' || (f.tag || '').startsWith('signature_') || (f.tag || '').startsWith('checkbox_') || (f.tag || '').startsWith('texte_') || estDateSignature(f));
        // Mode de signature (Stage 3, 2026-07-24) : copié depuis le modèle (meta.signing_mode/signing_order,
        // réglé une fois dans VisualTemplateEditor) sur CHAQUE document instancié — isBlockedBySigningOrder
        // lit ces champs directement sur la ligne `documents`, sans avoir à retourner interroger le modèle.
        const docMetadata = JSON.stringify({
          has_visual_fields: true,
          visual_template_id: visualTemplateId,
          template_url: templateResource.file_url,
          prefilled: true,
          moment, // lot 3 : 'debut' | 'fin'
          signature_fields: sigFields,
          destination_roles: destRoles,
          signing_mode: meta.signing_mode || 'simultane',
          ...(meta.signing_mode === 'sequentiel' ? { signing_order: normalizeSigningOrder(meta) } : {}),
        });

        const { data: newDoc, error: insertErr } = await supabase.from('documents').insert([{
          user_id: client.id,
          organisation_id: client.organisation_id || currentOrgId,
          nom: templateResource.titre,
          url: prefilledUrl,
          type_document: 'À signer',
          visible_client: visClient,
          visible_formateur: visFormateur,
          signe_par_client: false,
          ...(requiresOrganisme ? { signe_par_organisme: false } : {}),
          metadata: docMetadata,
          ...(assignedFormateurId ? { assigned_formateur_id: assignedFormateurId } : {}),
        }]).select().single();

        if (insertErr) throw insertErr;
        if (newDoc) docId = newDoc.id;
        console.log(`[instantiateDocument] PDF pré-généré pour ${client.nom_complet || client.nom} → ${prefilledUrl}`);

      } catch (e) {
        console.error('[instantiateDocument] Erreur génération PDF pré-rempli, fallback métadonnées :', e.message);
        // Fallback : stocker champs + valeurs résolues pour overlay côté client
        const fallbackMeta = JSON.stringify({
          has_visual_fields: true,
          visual_template_id: visualTemplateId,
          template_url: templateResource.file_url,
          fields: tplFields || [],
          resolved_values: resolvedValues || {},
          moment, // lot 3
          destination_roles: destRoles,
          signing_mode: meta.signing_mode || 'simultane',
          ...(meta.signing_mode === 'sequentiel' ? { signing_order: normalizeSigningOrder(meta) } : {}),
        });
        const { data: newDoc, error: fallbackErr } = await supabase.from('documents').insert([{
          user_id: client.id,
          organisation_id: client.organisation_id || currentOrgId,
          nom: templateResource.titre,
          url: templateResource.file_url,
          type_document: 'À signer',
          visible_client: visClient,
          visible_formateur: visFormateur,
          signe_par_client: false,
          ...(requiresOrganisme ? { signe_par_organisme: false } : {}),
          metadata: fallbackMeta,
          ...(assignedFormateurId ? { assigned_formateur_id: assignedFormateurId } : {}),
        }]).select().single();
        if (fallbackErr) {
          console.error('[instantiateDocument] Erreur fallback insert:', fallbackErr.message);
          toast.error(`Erreur (fallback) "${templateResource.titre}": ${fallbackErr.message || fallbackErr.code}`);
        }
        if (newDoc) docId = newDoc.id;
      }

    // ── Branche 2 : template DOCX classique à générer (remplacement de balises Word) ──
    } else if (classification === 'a_generer') {
      try {
        // LOT 3 (2026-10-20) : le document Word généré s'appelle « Titre - Client » (et non « Titre ») →
        // l'anti-doublon ci-dessus ne le trouvait jamais : un nouveau document (et un email) à chaque
        // distribution. On vérifie donc aussi ce nom-là.
        const { data: _dejaGeneres } = await supabase.from('documents').select('id')
          .eq('user_id', client.id).like('nom', `${templateResource.titre} - %`).limit(1);
        if (_dejaGeneres && _dejaGeneres.length > 0) return;
        const generatedDoc = await handleGenerateDocx(client, templateResource.titre, false, null, true, 'send', null, { moment });
        if (generatedDoc) docId = generatedDoc.id;
      } catch (e) {
        console.error("Erreur génération automatique :", e);
      }

    // ── Branche 3 : document statique (téléchargeable ou à signer sans données) ──
    } else {
      const { data: newDoc, error: insertErr } = await supabase.from('documents').insert([{
        user_id: client.id,
        organisation_id: client.organisation_id || currentOrgId,
        nom: templateResource.titre,
        url: templateResource.file_url,
        type_document: classification === 'a_signer' ? 'À signer' : 'Téléchargeable',
        visible_client: visClient && moment === 'debut',
        metadata: { moment }, // lot 3
        visible_formateur: visFormateur,
        ...(assignedFormateurId ? { assigned_formateur_id: assignedFormateurId } : {}),
      }]).select().single();

      if (insertErr) {
        console.error('[instantiateDocument] Erreur insertion document:', insertErr.message, { client: client.id, doc: templateResource.titre });
        toast.error(`Erreur création "${templateResource.titre}" : ${insertErr.message || insertErr.code || 'RLS ?'}`);
        return;
      }
      if (newDoc) docId = newDoc.id;
    }

    if (docId) {
      await supabase.from('client_documents').insert([{
        client_id: client.id,
        document_id: docId,
        module_id: client.module_id,
        status: 'pending',
        moment: moment,
        metadata: { classification }
      }]);
    }

    // ── Destination "Les deux" : créer aussi un document pour le formateur ──
    // Le formateur voit le document dans sa section "Documents administratifs" et peut le signer
    const resourceDestination = templateResource.destination || 'client';
    if (resourceDestination === 'both' && client.formateur_id) {
      try {
        // Anti-doublon formateur
        const { data: existingFDoc } = await supabase.from('documents')
          .select('id').eq('assigned_formateur_id', client.formateur_id)
          .eq('nom', templateResource.titre).maybeSingle();

        if (!existingFDoc) {
          // Reprendre les mêmes métadonnées enrichies (champs + valeurs résolues) si template visuel
          // ── IMPORTANT : ce document (copie "formateur" d'un template destination=both) doit lui
          // aussi porter signature_fields dans ses métadonnées, sinon la visionneuse de signature ne
          // sait pas quels champs signature/case/texte-libre appartiennent au formateur → aucun champ
          // interactif ne s'affiche (régression trouvée en test 2026-07-24 : "Texte libre formateur"
          // non modifiable + "Texte libre client" visible alors qu'il ne devrait pas l'être).
          let fDocSigFields = [];
          if (hasVisualFields && visualTemplateId) {
            try {
              let fTplFields = (meta.template_fields && meta.template_fields.length > 0) ? meta.template_fields : null;
              if (!fTplFields) {
                const { data: fDbFields } = await supabase
                  .from('template_fields').select('*').eq('template_id', visualTemplateId).order('page', { ascending: true });
                fTplFields = fDbFields || [];
              }
              fDocSigFields = (fTplFields || []).filter(f =>
                f.field_type === 'signature' || f.field_type === 'checkbox' || f.field_type === 'text_input' ||
                (f.tag || '').startsWith('signature_') || (f.tag || '').startsWith('checkbox_') || (f.tag || '').startsWith('texte_') || estDateSignature(f)
              );
            } catch (e) {
              console.warn('[instantiateDocument] Impossible de résoudre les champs interactifs (doc formateur, destination=both) :', e.message);
            }
          }
          const fDocMetadata = (hasVisualFields && visualTemplateId) ? JSON.stringify({
            has_visual_fields: true,
            visual_template_id: visualTemplateId,
            template_url: templateResource.file_url,
            signature_fields: fDocSigFields,
            signing_mode: meta.signing_mode || 'simultane',
            ...(meta.signing_mode === 'sequentiel' ? { signing_order: meta.signing_order || 'client_first' } : {}),
          }) : null;

          await supabase.from('documents').insert([{
            assigned_formateur_id: client.formateur_id,
            organisation_id: client.organisation_id,
            nom: templateResource.titre,
            url: templateResource.file_url,
            type_document: 'À signer',
            visible_client: false,
            visible_formateur: true,
            signe_par_formateur: false,
            ...(fDocMetadata ? { metadata: fDocMetadata } : {}),
          }]);
          console.log(`[instantiateDocument] Document formateur créé (destination=both) : ${templateResource.titre} → formateur ${client.formateur_id}`);
        }
      } catch (e) {
        console.error('[instantiateDocument] Erreur création doc formateur (destination=both) :', e.message);
      }
    }
  };

  const distributeDocumentsForModule = async (client, moduleId) => {
    try {
        const { data: resources, error: resErr } = await supabase
        .from('module_step_resources')
        .select('*')
        .eq('module_id', moduleId)
        .in('moment', ['debut', 'fin']);
      
      if (resErr || !resources || resources.length === 0) return;

      let hasExercices = false;
      for (const res of resources) {
        if (res.type === 'document_group' && res.document_group_id) {
          const { data: groupDocs, error: grpErr } = await supabase
            .from('module_step_resources')
            .select('*')
            .eq('document_group_id', res.document_group_id);

          if (!grpErr && groupDocs) {
            for (const doc of groupDocs) {
              if (doc.type === 'document') await instantiateDocument(client, doc, res.moment);
            }
          }
        } else if (res.type === 'document') {
          await instantiateDocument(client, res, res.moment);
        } else if (res.type === 'exercice') {
          // AJOUT (2026-09-18) : distribue aussi les exercices de début/fin — voir instantiateExerciceSession.
          hasExercices = true;
          await instantiateExerciceSession(client, res, res.moment);
        }
      }
      fetchDocuments();
      if (hasExercices && typeof fetchSessions === 'function') await fetchSessions();
    } catch (e) {
      console.error("[distributeDocuments] Erreur :", e);
    }
  };

  // Régénère les PDF pré-remplis pour tous les templates visuels du module d'un client
  // Utile pour les clients existants dont les documents ont été créés avant la génération automatique
  const handleRegenerateVisualDocs = async (clientId) => {
    const toastId = 'regen-docs';
    toast.loading('Régénération des documents…', { id: toastId });
    try {
      const clientObj = clients.find(c => String(c.id) === String(clientId));
      if (!clientObj || !clientObj.module_id) {
        toast.error('Client sans module assigné.', { id: toastId });
        return;
      }
      // Récupérer les données complètes du client depuis Supabase
      const { data: fullClient } = await supabase.from('clients').select('*').eq('id', clientId).single();
      if (!fullClient) { toast.error('Client introuvable.', { id: toastId }); return; }
      const compatibleClient = { ...fullClient, nom: fullClient.nom_complet || fullClient.nom || fullClient.email || 'Bénéficiaire' };

      // Supprimer les entrées documents non signées dont l'URL est l'URL brute du template
      // (l'anti-doublon dans instantiateDocument s'en charge aussi, mais on force ici)
      const { data: resources } = await supabase
        .from('module_step_resources').select('*')
        .eq('module_id', fullClient.module_id)
        .in('moment', ['debut', 'fin']);

      for (const res of (resources || [])) {
        const meta = typeof res.metadata === 'string' ? (() => { try { return JSON.parse(res.metadata); } catch { return {}; } })() : (res.metadata || {});
        if (!meta.has_visual_fields) continue; // ignorer les non-visuels

        // Supprimer les docs non signés avec l'URL brute du template (pour forcer régénération)
        // LOT 2 (2026-10-20) : seulement les documents que PERSONNE n'a signés.
        {
          const { data: cands } = await supabase.from('documents').select('*').eq('user_id', clientId).eq('nom', res.titre);
          const aSuppr = (cands || []).filter(d => !documentEstSigne(d)).map(d => d.id);
          if (aSuppr.length) await supabase.from('documents').delete().in('id', aSuppr);
        }

        // Destination "Les deux" : une COPIE SÉPARÉE existe aussi pour le formateur assigné
        // (user_id NULL, assigned_formateur_id renseigné à la place — voir instantiateDocument) —
        // sans ce nettoyage, cette copie-là restait figée avec l'ancienne métadonnée (sans
        // signature_fields) même après une "régénération" côté client.
        if ((res.destination || 'client') === 'both' && fullClient.formateur_id) {
          const { data: cands } = await supabase.from('documents').select('*')
            .eq('assigned_formateur_id', fullClient.formateur_id).eq('nom', res.titre).is('user_id', null);
          const aSuppr = (cands || []).filter(d => !documentEstSigne(d)).map(d => d.id);
          if (aSuppr.length) await supabase.from('documents').delete().in('id', aSuppr);
        }
      }

      // Relancer la distribution (instantiateDocument régénère les PDFs visuels)
      await distributeDocumentsForModule(compatibleClient, fullClient.module_id);
      await fetchDocuments();
      toast.success('Documents régénérés avec succès !', { id: toastId });
    } catch (e) {
      console.error('[handleRegenerateVisualDocs]', e);
      toast.error('Erreur : ' + e.message, { id: toastId });
    }
  };

  const appliquerChangementModule = async (clientId, moduleId) => {
    const finalModuleId = moduleId || null;

    // 1. Sauvegarde dans Supabase table 'clients'
    const { error: updateError } = await supabase
      .from('clients')
      .update({ module_id: finalModuleId })
      .eq('id', clientId);

    if (updateError) {
      console.error("[handleModuleChange] Erreur lors de l'update client:", updateError);
      toast.error("Erreur lors de l'assignation : " + updateError.message);
      return false;
    }

    const { data: updatedClient, error: fetchErr } = await supabase.from('clients').select('*').eq('id', clientId).single();
    if (fetchErr) {
      console.error("[handleModuleChange] Erreur lors de la récupération du client mis à jour:", fetchErr);
      toast.error('Le module a été enregistré, mais la fiche n\'a pas pu être relue : rechargez la page, puis utilisez « Reprendre dans le parcours actuel » si besoin.');
      return false;
    }


    if (updatedClient && finalModuleId) {
      // Mapping pour compatibilité avec generateSessions (nom_complet -> nom)
      const compatibleClient = {
        ...updatedClient,
        nom: updatedClient.nom_complet || updatedClient.nom || updatedClient.email || 'Bénéficiaire',
        id: updatedClient.id,
        module_id: finalModuleId
      };

        // 3. Déclenchement automatique des sessions (Qualiopi)
      await generateSessions(compatibleClient);
      
      // 3.5. Distribution automatique des documents
      await distributeDocumentsForModule(compatibleClient, finalModuleId);
    } else {
      console.warn('[handleModuleChange] Conditions non remplies pour generateSessions:', { hasClient: !!updatedClient, hasModule: !!finalModuleId });
    }

    // 4. Rafraîchir l'UI globale
    await fetchUtilisateurs();
    await fetchDocuments();
    return true;
  };

  // 04/11/2026 : séances que le nouveau module créera (pour l'aperçu, avant de rien écrire).
  const seancesVirtuellesModule = async (lignes, moduleId) => {
    const deja = (lignes || []).some(s => String(s.module_id) === String(moduleId) && !s.is_administrative
      && (() => { const m = lireMetaSeance(s.metadata); return !m.isCustom && !m.groupe_id; })());
    if (deja) return [];
    const { data: templates } = await supabase.from('module_session_templates').select('*').eq('module_id', moduleId).order('ordre', { ascending: true });
    const virt = [];
    if (templates && templates.length) {
      const { data: res } = await supabase.from('module_step_resources').select('*').in('template_id', templates.map(t => t.id)).order('ordre', { ascending: true });
      templates.forEach(t => {
        const rs = (res || []).filter(x => String(x.template_id) === String(t.id));
        if (!rs.length) virt.push({ id: `virtuel-${t.id}`, module_id: moduleId, numero_seance: t.ordre, nom: t.titre, ressource_titre: null, metadata: {} });
        rs.forEach(x => virt.push({ id: `virtuel-${t.id}-${x.id}`, module_id: moduleId, numero_seance: t.ordre, nom: `${t.titre} - ${x.titre}`, ressource_titre: x.titre, ressource_id: x.ressource_id || null, metadata: {} }));
      });
    } else {
      const mod = (modules || []).find(m => String(m.id) === String(moduleId));
      for (let i = 1; i <= Number(mod?.seances_prevues || 0); i++) virt.push({ id: `virtuel-s${i}`, module_id: moduleId, numero_seance: i, nom: `${mod.nom} - Séance ${i}`, ressource_titre: null, metadata: {} });
    }
    return virt;
  };

  // Écrit la fusion calculée par planFusionModule (relue juste avant, sur les vraies séances du client).
  const executerFusionModule = async (clientId, moduleId, { renumeroter = true } = {}) => {
    const { data: lignes, error } = await supabase.from('sessions').select('*').eq('client_id', clientId);
    if (error) throw error;
    const plan = planFusionModule(lignes || [], moduleId, { renumeroter });
    const le = new Date().toISOString();
    const aSynchroniser = new Set();
    let nb = 0;
    for (const c of plan.changements) {
      const o = (lignes || []).find(l => String(l.id) === String(c.id));
      if (!o) continue;
      const metadata = { ...lireMetaSeance(o.metadata), fusion_module: { le, module_avant: o.module_id ?? null, numero_avant: o.numero_seance ?? null } };
      const { error: errMaj } = await supabase.from('sessions').update({ ...c.maj, metadata }).eq('id', c.id);
      if (errMaj) throw errMaj;
      nb++;
      if (o.numero_seance != null) aSynchroniser.add(Number(o.numero_seance));
      if (c.maj.numero_seance != null) aSynchroniser.add(Number(c.maj.numero_seance));
    }
    aSynchroniser.forEach(n => planifierSyncAgenda(clientId, n));
    return { plan, nb };
  };

  // Ouvre la fenêtre de confirmation avec le récapitulatif (rien n'est écrit avant « Confirmer »).
  // options.fusionSeule : bouton « Reprendre dans le parcours actuel » (le module ne change pas).
  const handleModuleChange = async (clientId, moduleId, options = {}) => {
    const fusionSeule = !!options.fusionSeule;
    const cible = moduleId ? String(moduleId) : '';
    const client = (clients || []).find(c => String(c.id) === String(clientId));
    if (!fusionSeule && String(client?.module_id ?? '') === cible) return;
    const nomModule = (id) => (id ? ((modules || []).find(m => String(m.id) === String(id))?.nom || 'Module supprimé') : 'Aucun module');
    const base = {
      clientId, moduleId: cible, fusionSeule, renumeroter: !client?.groupe_id,
      nomClient: client?.nom_complet || client?.nom || 'ce client', ancien: nomModule(client?.module_id), nouveau: nomModule(cible),
      chargement: !!cible,
    };
    setChangementModule(base);
    if (!cible) return;
    try {
      const { data: lignes, error } = await supabase.from('sessions').select('*').eq('client_id', clientId);
      if (error) throw error;
      const toutes = fusionSeule ? (lignes || []) : (lignes || []).concat(await seancesVirtuellesModule(lignes, cible));
      const plan = planFusionModule(toutes, cible, { renumeroter: base.renumeroter });
      let automatisations = [];
      if (plan.decales.length) {
        const nums = plan.decales.map(d => d.avant);
        const { data: autos } = await supabase.from('automation_settings').select('id, email_subject, target_module_id, target_numero_seance, is_active').eq('organisation_id', currentOrgId);
        automatisations = (autos || []).filter(x => x.is_active !== false && String(x.target_module_id) === cible && nums.includes(Number(x.target_numero_seance)));
      }
      setChangementModule(prev => (prev && prev.clientId === clientId ? { ...prev, chargement: false, plan, automatisations } : prev));
    } catch (e) {
      setChangementModule(prev => (prev && prev.clientId === clientId ? { ...prev, chargement: false, erreur: 'Le récapitulatif n\'a pas pu être préparé : ' + (e.message || e) } : prev));
    }
  };

  const confirmerChangementModule = async () => {
    const etat = changementModule;
    if (!etat || etat.enCours || etat.chargement) return;
    setChangementModule({ ...etat, enCours: true });
    try {
      // Audit 04/11/2026 : si le module n'a pas été enregistré, on ne déplace aucune séance.
      if (!etat.fusionSeule && (await appliquerChangementModule(etat.clientId, etat.moduleId)) === false) { setChangementModule(null); return; }
      let nb = 0;
      if (etat.moduleId) ({ nb } = await executerFusionModule(etat.clientId, etat.moduleId, { renumeroter: etat.renumeroter }));
      await fetchSessions();
      toast.success(etat.fusionSeule ? `${nb} élément(s) repris dans le parcours actuel.` : `Module changé${nb ? ` : ${nb} élément(s) repris ou mis à jour` : ''}.`);
    } catch (e) {
      console.error('[changement de module]', e);
      toast.error('Le changement n\'a pas pu être terminé : ' + (e.message || e) + '. Rien n\'a été supprimé ; vous pouvez relancer « Reprendre dans le parcours actuel » depuis la fiche.');
      await fetchSessions();
    }
    setChangementModule(null);
  };

  // --- Actions Supabase : Configuration Modules ---
  const handleAddModule = async (e) => {
    e.preventDefault();
    if (!newModuleName.trim()) return;
    const nbSeances = parseInt(newModuleSeances) || 1;
    const { data: newModule, error } = await supabase.from('modules')
      .insert([{ nom: newModuleName, seances_prevues: nbSeances, organisation_id: currentOrgId }])
      .select().single();
    if (!error) {
      // Créer automatiquement les N dossiers de séance vides ("Séance 1", "Séance 2"...)
      // correspondant au nombre saisi, pour éviter d'avoir à les ajouter un par un via
      // "Modéliser Parcours" (décision produit du 2026-07-28, suite à une confusion
      // remontée : le champ "Séances prévues" ne créait auparavant qu'une étiquette,
      // sans générer les séances elles-mêmes).
      if (newModule) {
        const templatesToInsert = Array.from({ length: nbSeances }, (_, i) => ({
          module_id: newModule.id,
          titre: `Séance ${i + 1}`,
          ordre: i + 1,
          organisation_id: currentOrgId
        }));
        const { error: templatesError } = await supabase.from('module_session_templates').insert(templatesToInsert);
        if (templatesError) {
          console.error('Erreur création des séances par défaut:', templatesError);
          toast.error("Module créé, mais la création automatique des séances a échoué : " + templatesError.message);
        }
      }
      await fetchModules();
      setNewModuleName('');
      setNewModuleSeances(1);
      toast.success("Module créé avec succès !");
    }
    else toast.error('Erreur lors de la création du module : ' + error.message);
  };

  const handleDeleteModule = (moduleId, moduleName) => {
    // Décision produit (2026-07-23) : plutôt que de bloquer la suppression tant que des
    // clients/utilisateurs sont rattachés (comportement précédent), on les désassigne
    // automatiquement (module_id -> NULL, ils repassent "Aucun module assigné") puis on
    // supprime le module. Couvre à la fois clients.module_id et utilisateurs.module_id
    // (cette dernière colonne porte une contrainte de clé étrangère "utilisateurs_module_id_fkey"
    // découverte en production, jamais utilisée ailleurs dans l'app).
    showAppConfirm(
      `Supprimer le module "${moduleName}" ?`,
      "Les clients et utilisateurs encore rattachés à ce module basculeront automatiquement sur \"Aucun module assigné\". Ce module et tout son contenu (parcours, étapes, ressources) seront ensuite définitivement supprimés. Cette action est irréversible.",
      async () => {
        hideAppConfirm();
        if (!currentOrgId) { toast.error('Organisme introuvable — suppression annulée par sécurité.'); return; }
        try {
          // 1. Désassigner les clients rattachés (scopé strictement à l'organisme courant)
          const clientsUnassignQuery = supabase.from('clients').update({ module_id: null }).eq('module_id', moduleId).eq('organisation_id', currentOrgId);
          const { data: unassignedClients, error: clientsUnassignErr } = await clientsUnassignQuery.select('id');
          if (clientsUnassignErr) throw clientsUnassignErr;

          // 2. Désassigner les utilisateurs (formateurs/admins) rattachés
          const staffUnassignQuery = supabase.from('utilisateurs').update({ module_id: null }).eq('module_id', moduleId).eq('organisation_id', currentOrgId);
          const { data: unassignedStaff, error: staffUnassignErr } = await staffUnassignQuery.select('id');
          if (staffUnassignErr) throw staffUnassignErr;

          // 3. Supprimer le contenu du module puis le module lui-même
          const { error: msrErr } = await supabase.from('module_step_resources').delete().eq('module_id', moduleId);
          if (msrErr) throw msrErr;
          const { error: mstErr } = await supabase.from('module_session_templates').delete().eq('module_id', moduleId);
          if (mstErr) throw mstErr;
          const delQuery = supabase.from('modules').delete().eq('id', moduleId).eq('organisation_id', currentOrgId);
          const { error } = await delQuery;
          if (error) throw error;

          await fetchModules();
          await fetchUtilisateurs();
          const n1 = unassignedClients?.length || 0;
          const n2 = unassignedStaff?.length || 0;
          const detail = (n1 || n2) ? ` (${n1} client(s) et ${n2} utilisateur(s) repassés en "Aucun module assigné")` : '';
          toast.success(`Module supprimé avec succès.${detail}`);
        } catch (err) {
          console.error("Erreur suppression module:", err);
          // Filet de sécurité : la désassignation ci-dessus est elle-même soumise aux règles de
          // sécurité (RLS) — un compte historique sans organisation_id pourrait ne pas être
          // désassignable par cette voie (même limite que la vérification RLS précédente). Dans
          // ce cas la suppression du module échoue encore avec une violation de clé étrangère :
          // on l'attrape ici pour afficher un message clair plutôt que le texte brut de Postgres.
          const isForeignKeyViolation = err?.code === '23503' || /foreign key constraint/i.test(err?.message || '');
          if (isForeignKeyViolation) {
            toast.error("Impossible de supprimer ce module : un compte y est encore rattaché et n'a pas pu être désassigné automatiquement (probablement un compte historique sans organisation). Ce cas nécessite une correction manuelle en base — contactez le support technique.", { duration: 10000 });
          } else {
            toast.error("Erreur lors de la suppression : " + err.message);
          }
        }
      }
    );
  };

  const createSessionFolder = async (moduleId, title) => {
    if (!title.trim()) return;

    setIsAddingStep(true);
    const { error } = await supabase.from('module_session_templates').insert([{
      module_id: Number(moduleId),
      titre: title,
      ordre: moduleSessionTemplates.filter(t => String(t.module_id) === String(moduleId)).length + 1,
      organisation_id: currentOrgId
    }]);

    if (error) {
      console.error("Erreur création dossier Supabase:", error);
      toast.error("Erreur lors de la création du dossier : " + error.message);
    } else {
        setNewStepTitle('');
      await fetchModules();
      toast.success("Dossier de séance créé !");
    }
    setIsAddingStep(false);
  };

  const handleRenameFolder = async (folderId, newTitle) => {
    if (!newTitle.trim()) return;
    const { error } = await supabase.from('module_session_templates').update({ titre: newTitle }).eq('id', folderId);
    if (!error) {
      await fetchModules();
      toast.success("Séance renommée avec succès !");
    } else {
      toast.error("Erreur renommage : " + error.message);
    }
  };

  // AJOUT (2026-09-17) : renommer un module APRÈS sa création — jusqu'ici seul le nom des "dossiers
  // de séance" à l'intérieur d'un module était modifiable (voir handleRenameFolder ci-dessus), pas le
  // nom du module lui-même. Demandé par l'utilisateur le 17/09/2026.
  const handleRenameModule = async (moduleId, newTitle) => {
    if (!newTitle.trim()) return;
    const { error } = await supabase.from('modules').update({ nom: newTitle.trim() }).eq('id', moduleId);
    if (!error) {
      await fetchModules();
      toast.success("Module renommé avec succès !");
    } else {
      toast.error("Erreur renommage : " + error.message);
    }
  };

  const handleRenameResource = async (resourceId, newTitle) => {
    if (!newTitle.trim()) return;
    const { error } = await supabase.from('module_step_resources').update({ titre: newTitle }).eq('id', resourceId);
    if (!error) {
      await fetchModules();
      toast.success("Élément renommé !");
    } else {
      toast.error("Erreur renommage : " + error.message);
    }
  };

  // AJOUT (2026-09-25) : permet de changer QUI doit signer un émargement de module APRÈS sa
  // création, sans avoir à le supprimer/recréer (demandé par l'utilisateur — jusqu'ici
  // StepResourceModal ne proposait ce choix qu'à la création, et uniquement pour les documents,
  // jamais pour les émargements).
  //
  // Règle de propagation volontairement asymétrique (validée avec l'utilisateur le 25/09/2026) :
  //  - si on AJOUTE la signature formateur (false/absent -> true), on la propage aussi à tous les
  //    dossiers clients déjà générés à partir de cet émargement (sans risque : ça ajoute juste une
  //    case à signer en plus, aucune signature déjà obtenue n'est touchée) ;
  //  - si on RETIRE la signature formateur (true -> false), on NE touche PAS aux dossiers déjà créés
  //    (on ne veut jamais faire disparaître un suivi ou une signature déjà obtenue sur un dossier en
  //    cours) — seuls les futurs dossiers générés après ce changement n'auront plus cette case.
  //
  // Le rattachement séance générée <-> ressource de modèle se fait par titre + module_id, comme le
  // fait déjà generateSessions() pour sa dé-duplication (pas de clé étrangère directe
  // sessions -> module_step_resources dans ce schéma).
  const handleUpdateStepResourceSignatures = async (resourceId, { requiresClientSignature, requiresTrainerSignature }) => {
    const resource = moduleStepResources.find(r => r.id === resourceId);
    if (!resource) return;

    const parseMeta = (m) => (typeof m === 'string' && m.startsWith('{')) ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
    const currentMeta = parseMeta(resource.metadata);
    const wasTrainerRequired = currentMeta.requiresTrainerSignature === true;
    const newMeta = { ...currentMeta, requiresClientSignature, requiresTrainerSignature, documentType: 'signature' };

    const { error } = await supabase.from('module_step_resources').update({ metadata: newMeta }).eq('id', resourceId);
    if (error) {
      toast.error('Erreur lors de la mise à jour : ' + error.message);
      return;
    }

    // Propagation UNIQUEMENT dans le sens "ajout" — voir commentaire ci-dessus.
    let propagatedCount = 0;
    if (!wasTrainerRequired && requiresTrainerSignature === true) {
      const template = moduleSessionTemplates.find(t => t.id === resource.template_id);
      const moduleId = template?.module_id;
      if (moduleId) {
        const { data: matching, error: fetchErr } = await supabase
          .from('sessions')
          .select('id, metadata')
          .eq('module_id', moduleId)
          .eq('ressource_titre', resource.titre);
        if (!fetchErr && matching && matching.length > 0) {
          const toUpdate = matching.filter(s => parseMeta(s.metadata).requiresTrainerSignature !== true);
          let echecs = 0;
          for (const s of toUpdate) {
            const m = parseMeta(s.metadata);
            const { error: eMaj } = await supabase.from('sessions').update({ metadata: { ...m, requiresTrainerSignature: true } }).eq('id', s.id);
            if (eMaj) echecs++;
          }
          propagatedCount = toUpdate.length - echecs; // LOT B : nombre réellement mis à jour
          if (echecs) toast.error(`${echecs} dossier(s) n'ont pas pu être mis à jour.`);
          if (propagatedCount > 0 && typeof fetchSessions === 'function') await fetchSessions();
        }
      }
    }

    await fetchModules();
    if (propagatedCount > 0) {
      toast.success(`Paramètres enregistrés — signature formateur ajoutée à ${propagatedCount} dossier(s) client déjà existant(s).`);
    } else {
      toast.success('Paramètres de signature enregistrés.');
    }
  };

  const handleAddStepResource = async (templateId, stepData) => {
    if (!templateId) return;

    setIsAddingStepResource(true);
    const { error } = await supabase.from('module_step_resources').insert([{
      template_id: templateId,
      titre: stepData.title || (stepData.type === 'signature' ? 'Émargement' : 'Document'),
      type: stepData.type,
      ressource_id: (stepData.resourceId && !stepData.resourceId.includes('/')) ? stepData.resourceId : null,
      file_url: (stepData.fileUrl) ? stepData.fileUrl : ((stepData.resourceId && stepData.resourceId.includes('/')) ? stepData.resourceId : null),
      metadata: stepData.metadata,
      destination: stepData.destination || 'client',
      ordre: moduleStepResources.filter(r => r.template_id === templateId).length + 1,
      ...(stepData.instructions ? { instructions: stepData.instructions } : {}),
      ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
    }]);

    if (error) {
      console.error("Erreur ajout ressource:", error);
      toast.error("Erreur lors de l'ajout : " + error.message);
    } else {
      setIsResourceModalOpen(false);
      await fetchModules();
    }
    setIsAddingStepResource(false);
  };

  const handleAddModuleMomentResource = async (moduleId, moment, stepData) => {
    setIsAddingStepResource(true);
    const { data: newResource, error } = await supabase.from('module_step_resources').insert([{
      module_id: moduleId,
      moment: moment,
      template_id: null,
      titre: stepData.title || (stepData.type === 'signature' ? 'Émargement' : stepData.type === 'document_group' ? stepData.title || 'Groupe' : 'Document'),
      type: stepData.type,
      ressource_id: (stepData.resourceId && !stepData.resourceId.includes('/')) ? stepData.resourceId : null,
      file_url: stepData.fileUrl || ((stepData.resourceId && stepData.resourceId.includes('/')) ? stepData.resourceId : null),
      metadata: stepData.metadata,
      destination: stepData.destination || 'client',
      document_group_id: stepData.document_group_id || null,
      ordre: moduleStepResources.filter(r => r.module_id === moduleId && r.moment === moment).length + 1,
      ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
    }]).select().single();
    if (error) {
      toast.error("Erreur lors de l'ajout : " + error.message);
    } else {
      // Auto-distribuer cette ressource aux clients déjà assignés à ce module — pour que "Documents de
      // début" (et "de fin", une fois le parcours terminé) apparaissent chez TOUS les clients concernés
      // dès l'ajout, sans dépendre de la page que le client ouvre ensuite (corrigé le 2026-09-18, suite
      // à un exercice de début resté invisible côté client).
      if ((moment === 'debut' || moment === 'fin') && newResource && (stepData.type === 'document' || stepData.type === 'exercice')) {
        const assignedClients = clients.filter(c =>
          String(c.module_id) === String(moduleId) &&
          (!currentOrgId || String(c.organisation_id) === String(currentOrgId))
        );
        let distribues = 0; // LOT B : nombre réel
        for (const client of assignedClients) {
          try {
            if (stepData.type === 'document') await instantiateDocument(client, newResource, moment);
            else await instantiateExerciceSession(client, newResource, moment);
            distribues++;
          } catch (e) { console.error('[distribution]', client.id, e); }
        }
        if (stepData.type !== 'document' && assignedClients.length > 0 && typeof fetchSessions === 'function') await fetchSessions();
        if (assignedClients.length > 0) {
          if (distribues > 0) toast.success(`✅ ${stepData.type === 'exercice' ? 'Exercice' : 'Document'} distribué à ${distribues} client(s) existant(s)`);
          if (distribues < assignedClients.length) toast.error(`${assignedClients.length - distribues} client(s) n'ont pas pu le recevoir. Réessayez avec « Sync documents clients ».`);
        }
      }
      await fetchModules();
    }
    setIsAddingStepResource(false);
  };

  // Redistribue manuellement tous les documents ET exercices début/fin d'un module à ses clients (sans
  // toucher aux séances du calendrier) — utile pour rattraper des clients déjà assignés avant l'ajout
  // d'une ressource (voir distributeDocumentsForModule, qui gère désormais aussi les exercices).
  const handleRedistributeModuleDocs = async (moduleId) => {
    const assignedClients = clients.filter(c =>
      String(c.module_id) === String(moduleId) &&
      (!currentOrgId || String(c.organisation_id) === String(currentOrgId))
    );
    if (assignedClients.length === 0) {
      toast('Aucun client assigné à ce module.'); return;
    }
    toast.loading('Redistribution en cours…', { id: 'redistribution' });
    let ok = 0; // LOT B : nombre réel
    for (const client of assignedClients) {
      try { await distributeDocumentsForModule(client, moduleId); ok++; } catch (e) { console.error('[redistribution]', client.id, e); }
    }
    await fetchDocuments();
    if (typeof fetchSessions === 'function') await fetchSessions();
    if (ok === assignedClients.length) toast.success(`✅ Documents et exercices redistribués à ${ok} client(s)`, { id: 'redistribution' });
    else toast.error(`Redistribués à ${ok} client(s) sur ${assignedClients.length}. Réessayez pour les autres.`, { id: 'redistribution' });
  };

  const handleDeleteFolder = (folderId) => {
    showAppConfirm(
      'Supprimer ce dossier ?',
      'Le dossier et tout son contenu seront définitivement supprimés.',
      async () => {
        hideAppConfirm();
        const { error } = await supabase.from('module_session_templates').delete().eq('id', folderId);
        if (!error) await fetchModules();
        else toast.error("Erreur suppression : " + error.message);
      }
    );
  };

  const handleDeleteStepResource = (resourceId) => {
    showAppConfirm(
      'Supprimer cet élément ?',
      'Cet élément sera définitivement supprimé du module.',
      async () => {
        hideAppConfirm();
        const { error } = await supabase.from('module_step_resources').delete().eq('id', resourceId);
        if (!error) await fetchModules();
        else toast.error("Erreur suppression : " + error.message);
      }
    );
  };

  const generateSessions = async (client) => {
    // Client ID est systématiquement un UUID (String)
    const finalClientId = client.id;

    // Module ID est systématiquement un BigInt (Number)
    const finalModuleId = Number(client.module_id);

    console.log(`[generateSessions] Début. ClientID: ${finalClientId}, ModuleID: ${finalModuleId}`);

    const moduleId = finalModuleId; 
    if (!moduleId || isNaN(moduleId)) {
      console.warn("[generateSessions] ID de module invalide ou absent pour le client:", finalClientId);
      return;
    }

    // Récupérer l'objet module correspondant
    const module = modules.find(m => String(m.id) === String(moduleId));
    if (!module) {
      console.error("[generateSessions] Module non trouvé dans l'état local pour l'id:", moduleId, "Modules dispos:", modules.map(m => m.id));
      return;
    }

    // Verrou de concurrence local pour empêcher que l'effet ne s'exécute plusieurs fois simultanément
    window._generatingSessionsFor = window._generatingSessionsFor || new Set();
    if (window._generatingSessionsFor.has(client.id)) return;
    window._generatingSessionsFor.add(client.id);

    try {
      // 1. Charger les templates pour ce module (bigint)
      const { data: templates, error: tempError } = await supabase
        .from('module_session_templates')
        .select('*')
        .eq('module_id', moduleId)
        .order('ordre', { ascending: true });

      console.log('[generateSessions] Templates récupérés:', templates?.length || 0, 'Erreur:', tempError);

      if (tempError) {
        console.error('[generateSessions] Erreur fetch templates:', tempError);
        return;
      }

      // 2. Vérifier les séances existantes pour ce client (UUID ou BigInt)
      const { data: existingSessions, error: checkError } = await supabase
        .from('sessions')
        .select('nom')
        .eq('client_id', finalClientId);

      if (checkError) {
        console.error('Erreur vérification séances:', checkError);
        return;
      }

      const existingTitles = new Set((existingSessions || []).map(s => s.nom));
      const sessionsToInsert = [];

      // 3. Si templates existent, on les utilise. Sinon, fallback sur l'ancienne logique
      if (templates && templates.length > 0) {
        // Nouvelle logique Imbriquée (Keyro)
        for (const t of templates) {
          console.log('[generateSessions] Traitement template:', t.id, t.titre);
          const { data: resources } = await supabase
            .from('module_step_resources')
            .select('*')
            .eq('template_id', t.id)
            .order('ordre', { ascending: true });

          console.log(`[generateSessions]   -> ${resources?.length || 0} ressources trouvées pour template ${t.id}`);

          if (resources && resources.length > 0) {
            resources.forEach(res => {
              sessionsToInsert.push({
                client_id: finalClientId,
                module_id: finalModuleId,
                numero_seance: t.ordre,
                nom: `${t.titre} - ${res.titre}`, // Rend le nom unique pour la DB
                type_activite: (res.type && res.type.toLowerCase().includes('signature')) ? 'signature' :
                               (res.type && res.type.toLowerCase().includes('exercice')) ? 'exercice' : 'document',
                ressource_id: res.ressource_id || null,
                file_url: res.file_url || null,
                ressource_titre: res.titre,
                metadata: (typeof res.metadata === 'string' && res.metadata.startsWith('{')) ? JSON.parse(res.metadata) : (res.metadata || {}), // Propagate metadata
                statut: 'À venir',
                statut_client: 'À venir',
                statut_formateur: 'À venir',
                organisation_id: client.organisation_id || null
              });
            });
          } else {
            // Dossier vide
            sessionsToInsert.push({
              client_id: finalClientId,
              module_id: finalModuleId,
              numero_seance: t.ordre,
              nom: t.titre,
              type_activite: 'Dossier (Vide)',
              metadata: {},
              statut: 'À venir',
              statut_client: 'À venir',
              statut_formateur: 'À venir',
              organisation_id: client.organisation_id || null
            });
          }
        }
      } else {
        // Fallback backward compatibility : génération par nombre de séances prévues
        for (let i = 1; i <= module.seances_prevues; i++) {
          const defaultTitle = `${module.nom} - Séance ${i}`;
          if (!existingTitles.has(defaultTitle)) {
            sessionsToInsert.push({
              client_id: finalClientId,
              module_id: finalModuleId,
              file_url: null,
              numero_seance: i,
              nom: defaultTitle,
              metadata: {},
              statut: 'À venir',
              statut_client: 'À venir',
              statut_formateur: 'À venir',
              organisation_id: client.organisation_id || null
            });
          }
        }
      }

      // 2. Déduplication manuelle pour éviter l'erreur 42P10 (absence d'index unique ON CONFLICT)
      console.log('[generateSessions] Récupération des séances existantes pour déduplication...');
      const { data: alreadyInDb, error: fetchSessionsErr } = await supabase
        .from('sessions')
        .select('nom, ressource_titre, metadata, is_administrative')
        .eq('client_id', finalClientId)
        .eq('module_id', finalModuleId);

      if (fetchSessionsErr) {
        // LOT A (2026-10-21) : sans vérification possible, on ne crée rien (pas de risque de doublons).
        console.error('[generateSessions] Vérification impossible, aucune séance créée :', fetchSessionsErr);
        return;
      }
      // LOT A (2026-10-21) : les séances d'un module ne sont créées qu'UNE fois pour un client. S'il a déjà
      // des séances de ce module (hors bloc administratif et éléments ajoutés à la main ou par un groupe),
      // on ne recrée rien : ce que l'admin a supprimé ou renommé reste supprimé ou renommé.
      const moduleDejaGenere = (alreadyInDb || []).some(s => {
        const m = lireMetaSeance(s.metadata);
        return !s.is_administrative && !m.isCustom && !m.groupe_id;
      });
      if (moduleDejaGenere) {
        console.log('[generateSessions] Séances du module déjà présentes : rien à créer.');
        if (typeof fetchSessions === 'function') await fetchSessions();
        return;
      }

      const finalSessionsToInsert = sessionsToInsert.filter(newSession => {
        const isDuplicate = alreadyInDb?.some(existing => 
          existing.nom === newSession.nom && 
          (existing.ressource_titre === newSession.ressource_titre || (!existing.ressource_titre && !newSession.ressource_titre))
        );
        return !isDuplicate;
      });

      console.log(`[generateSessions] Après déduplication : ${finalSessionsToInsert.length} séances à insérer.`);

      if (finalSessionsToInsert.length === 0) {
        console.log('[generateSessions] Toutes les séances existent déjà. Pas d\'insertion nécessaire.');
        if (typeof fetchSessions === 'function') await fetchSessions();
        return;
      }

      // 3. Insertion simple (sans ON CONFLICT)
      const { error } = await supabase.from('sessions').insert(finalSessionsToInsert);

      if (!error) {
        console.log(`[generateSessions] SUCCÈS: ${finalSessionsToInsert.length} séance(s) insérée(s).`);
        if (typeof fetchSessions === 'function') await fetchSessions();
      } else {
        console.error('[generateSessions] ERREUR insertion finale :', error);
        console.error('[generateSessions] Payload tenté:', finalSessionsToInsert);
      }
    } finally {
      window._generatingSessionsFor.delete(client.id);
    }
  };

  // ── FIX (2026-10-02) : enregistrement FIABLE des dates / horaires de séance ─────────────────────────
  // Signalé : "quand elle met les horaires de ses séances, quand elle revient dessus ils ne sont pas
  // enregistrés". Causes corrigées :
  //  - l'horaire n'était enregistré qu'à la sortie du champ (onBlur) : changer d'onglet, de client ou
  //    fermer la page juste après la saisie le perdait → enregistrement AUTOMATIQUE 0,8 s après la saisie
  //    (et toujours immédiatement à la sortie du champ) ;
  //  - une erreur, ou une mise à jour refusée par la base (0 ligne modifiée, ex. droits), passait en
  //    silence et l'écran gardait l'horaire tapé comme s'il était enregistré → message d'erreur clair ;
  //  - effacer un horaire n'était jamais enregistré (seules les valeurs non vides étaient envoyées).
  const editedTimesRef = useRef({});
  const pendingTimeSavesRef = useRef({});   // sessionId → minuteur d'enregistrement automatique
  const sessionsRefreshTimerRef = useRef(null);
  const scheduleSessionsRefresh = () => {
    clearTimeout(sessionsRefreshTimerRef.current);
    sessionsRefreshTimerRef.current = setTimeout(() => { fetchSessions(); }, 1500);
  };
  // Met à jour en base, vérifie que la ligne a VRAIMENT été modifiée, et répercute aussitôt à l'écran.
  const persistSessionFields = async (sessionId, updates) => {
    const { data, error } = await supabase.from('sessions').update(updates).eq('id', sessionId).select('id');
    if (error || !data || data.length === 0) {
      console.error('[séance] enregistrement refusé', sessionId, updates, error);
      toast.error(`La modification de la séance n'a pas pu être enregistrée${error ? ' : ' + error.message : ' (droits insuffisants sur cette séance)'}. Réessayez ou contactez le support.`, { id: 'session-save-error' });
      return false;
    }
    setLastModifiedSessionId(sessionId);
    setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, ...updates } : s)));
    scheduleSessionsRefresh();
    if (['date', 'heure_debut', 'heure_fin'].some(k => k in updates)) {
      const s = sessions.find(x => String(x.id) === String(sessionId));
      if (s) planifierSyncAgenda(s.client_id, s.numero_seance);
    }
    return true;
  };

  const onSaveTimes = async (sessionId) => {
    clearTimeout(pendingTimeSavesRef.current[sessionId]);
    delete pendingTimeSavesRef.current[sessionId];
    const times = editedTimesRef.current[sessionId];
    if (!times) return;
    const updates = {};
    if ('start' in times) updates.heure_debut = times.start || null;
    if ('end' in times) updates.heure_fin = times.end || null;
    if (Object.keys(updates).length === 0) return;
    const ok = await persistSessionFields(sessionId, updates);
    // On n'efface la saisie locale que si elle a bien été enregistrée ET n'a pas changé entre-temps.
    if (ok && editedTimesRef.current[sessionId] === times) {
      const next = { ...editedTimesRef.current };
      delete next[sessionId];
      editedTimesRef.current = next;
      setEditedTimes(next);
    }
  };

  const onTimeChange = (sessionId, field, value, groupDefaults) => {
    const base = editedTimesRef.current[sessionId] || (groupDefaults ? { start: groupDefaults.start || '', end: groupDefaults.end || '' } : {});
    const next = { ...editedTimesRef.current, [sessionId]: { ...base, [field]: value } };
    editedTimesRef.current = next;
    setEditedTimes(next);
    clearTimeout(pendingTimeSavesRef.current[sessionId]);
    pendingTimeSavesRef.current[sessionId] = setTimeout(() => { onSaveTimes(sessionId); }, 800);
  };

  // Prévenir si on ferme la page alors qu'un horaire attend encore d'être enregistré (< 1 s)
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (Object.keys(pendingTimeSavesRef.current).length > 0) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const updateSessionTime = async (sessionId, field, value) => {
    return persistSessionFields(sessionId, { [field]: value || null });
  };

  const updateSessionDate = async (sessionId, newDate) => {
    return persistSessionFields(sessionId, { date: newDate || null });
  };

  const signSession = (session) => {
    setSigningSessionId(session.id);
  };

  /**
   * Superpose la signature sur le PDF original et sauvegarde le résultat
   */
   // --- Fonctions utilitaires Archivage ---
  const overlaySignatureOnPdf = async (session, client, role) => { /* 2026-10-21 : chargé à la demande */ const { PDFDocument } = await import('pdf-lib');
    try {
        
      const originalRelativePath = session.file_url || session.ressource_url || session.file_url_signed || session.metadata?.file_url_signed;
      if (!originalRelativePath) {
            return null;
      }

      // 1. Obtenir une URL de lecture valide (signée si nécessaire) pour le fetch
        
      // Extraction du bucket/path
      const extractBucketPath = (fullUrl) => {
        if (!fullUrl) return null;
        let bucket = 'documents';
        let path = fullUrl;
        
        const match = fullUrl.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/?]+)\/(.+?)(?:\?|$)/);
        if (match) {
          bucket = match[1];
          path = decodeURIComponent(match[2]);
        } else {
          const isRessource = fullUrl.startsWith('ressources') || fullUrl.startsWith('modeling-imports');
          bucket = isRessource ? 'ressources-pedagogiques' : 'documents';
        }

        // Rectification bucket
        if (path.startsWith('modeling-imports/') && bucket === 'documents') {
          bucket = 'ressources-pedagogiques';
        }
        return { bucket, path };
      };

      const extracted = extractBucketPath(originalRelativePath);
      if (!extracted) return null;

      const { data: signData, error: signErr } = await supabase.storage.from(extracted.bucket).createSignedUrl(extracted.path, 300);
      // SÉCURITÉ (2026-10-12) : repli sur le lien public si le lien temporaire est refusé
      // (ancien fichier sans auteur identifiable).
      const readUrl = (!signErr && signData?.signedUrl)
        ? signData.signedUrl
        : supabase.storage.from(extracted.bucket).getPublicUrl(extracted.path)?.data?.publicUrl;
      if (!readUrl) {
        throw new Error(`Erreur signature URL pour archivage: ${signErr?.message}`);
      }

      // 2. Récupérer le PDF original via l'URL signée
      const existingPdfBytes = await fetch(readUrl).then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status} lors de la lecture du PDF original`);
        return res.arrayBuffer();
      });
      
      const pdfDoc = await PDFDocument.load(existingPdfBytes, { ignoreEncryption: true });
      const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // 3. Ajouter le bloc de signature sur la dernière page
      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];
      const { width } = lastPage.getSize();

      // Overlay consentement interactif — config extensible par modèle de document SaaS
      // Clé : identifiant du modèle (session.consent_template). 'default' = Attestation VB.
      // Coordonnées en points pdf-lib (origine bas-gauche). À calibrer selon chaque PDF source.
      const CONSENT_OVERLAYS = {
        default: { pageIndex: 0, checkboxX: 67, autoriseY: 318, refuseY: 291 },
      };

      if (session.document_choice) {
        const overlay = CONSENT_OVERLAYS[session.consent_template] || CONSENT_OVERLAYS.default;
        const consentPage = pages[overlay.pageIndex];
        const checkY = session.document_choice === 'autorise' ? overlay.autoriseY : overlay.refuseY;
        consentPage.drawText('X', {
          x: overlay.checkboxX,
          y: checkY,
          size: 12,
          font: helveticaBold,
          color: rgb(0, 0, 0),
        });
      }

      const formateur = formateurs.find(f => f.id === session.formateur_id);
      const signerName = (role === 'formateur' || role === 'admin')
        ? (formateur?.nom || 'Le Formateur')
        : (client.nom_complet || client.nom || 'Le Client');

      const xPos = role === 'formateur' || role === 'admin' ? width / 2 + 10 : 50;
      const now = new Date();
      const nowStr = now.toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });

      lastPage.drawText(role === 'formateur' || role === 'admin' ? 'Le sous-traitant' : 'Le bénéficiaire', {
        x: xPos, y: 90, size: 10, font: helveticaBold, color: rgb(0, 0, 0),
      });
      lastPage.drawText(signerName, {
        x: xPos, y: 74, size: 10, font: helvetica, color: rgb(0, 0, 0),
      });
      lastPage.drawText(`Signé numériquement le ${nowStr}`, {
        x: xPos, y: 58, size: 8, font: helvetica, color: rgb(0.33, 0.33, 0.33),
      });

      // 6. Exporter le PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const file = new File([blob], `signed_${session.id}_${role}.pdf`, { type: 'application/pdf' });

      // 7. Upload sur Supabase Storage
      const fileName = `signed_${session.id}_${role}_${Date.now()}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('signed_documents')
        .upload(fileName, file);

      if (uploadError) {
        console.error("[SignatureProof] Erreur upload:", uploadError);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('signed_documents')
        .getPublicUrl(fileName);

        return publicUrl;
    } catch (err) {
      console.error("[SignatureProof] Erreur globale lors de la superposition:", err);
      return null;
    }
  };

  const handleSessionSignatureSave = async (sessionId, signatureDataUrl, documentChoice) => {
    const session = sessions.find(s => String(s.id) === String(sessionId));
    if (!session) {
      toast.error('Session introuvable (id: ' + sessionId + ')');
      return;
    }

    setLastModifiedSessionId(sessionId);
    const updateData = {};
    const client = clients.find(c => c.id === session.client_id);

    // FIX (2026-09-25) : même correctif que handleEmargementSave — voir son commentaire pour le
    // détail. "statut" ne doit passer à 'Signé' que lorsque toutes les signatures EXIGÉES par la
    // séance sont réunies, jamais dès qu'une seule partie a signé.
    if (userRole === 'formateur' || userRole === 'admin') {
      updateData.date_signature_formateur = new Date().toISOString();
      updateData.statut_formateur = 'Signé';
    } else {
      updateData.date_signature_client = new Date().toISOString();
      updateData.statut_client = 'Signé';
    }
    {
      const sigMeta = session.metadata || {};
      const requiresClientSig = sigMeta.requiresClientSignature !== false;
      const requiresTrainerSig = sigMeta.requiresTrainerSignature === true;
      const clientNowSigned = updateData.statut_client === 'Signé' || session.statut_client === 'Signé';
      const trainerNowSigned = updateData.statut_formateur === 'Signé' || session.statut_formateur === 'Signé';
      if ((!requiresClientSig || clientNowSigned) && (!requiresTrainerSig || trainerNowSigned)) {
        updateData.statut = 'Signé';
      }
    }

    if (documentChoice) {
      updateData.document_choice = documentChoice;
    }

    // Automatisation de l'archivage: Génère un PDF signé lors de chaque validation s'il y a un document existant
    if ((session.type_activite === 'document' || session.type_activite === 'signature') && client) {
      const signedUrl = await overlaySignatureOnPdf({ ...session, ...updateData }, client, userRole);
      if (signedUrl) {
        updateData.metadata = { ...(session.metadata || {}), file_url_signed: signedUrl };
        updateData.signed_pdf_url = signedUrl; // Colonne dédiée pour l'onglet Documents Signés
      }
    }

    const { error } = await supabase.from('sessions').update(updateData).eq('id', sessionId);

    if (!error) {
      if (updateData.statut === 'Signé') {
        const client = clients.find(c => c.id === session.client_id);
        if (client) {
          const newEffectuees = (client.seances_effectuees || 0) + 1;
          await supabase.from('clients').update({ seances_effectuees: newEffectuees }).eq('id', client.id);
          await fetchUtilisateurs();
        }
      }
      await fetchSessions();
      toast.success(`Émargement enregistré avec succès !`);
      setViewingSession(null); // Close viewer if open
    } else {
      console.error("Erreur signature session:", error);
      toast.error("Erreur lors de la signature : " + error.message);
    }
  };

  // SIGNATURES (2026-10-20) : signature d'un « document à signer » placé dans une séance. Relit la
  // séance, construit le PDF signé, l'enregistre, puis seulement ensuite marque la séance signée.
  // Si une étape échoue : message d'erreur et RIEN n'est enregistré (le signataire peut réessayer).
  const signerDocumentDeSeance = async (sessionId, role, signatureDataUrl, checkedIds = null, textValues = null) => {
    const toastId = `sign-seance-${sessionId}`;
    toast.loading('Enregistrement de votre signature…', { id: toastId });
    try {
      const { data: frais, error: errLecture } = await supabase.from('sessions').select('*').eq('id', sessionId).maybeSingle();
      if (errLecture || !frais) throw new Error(errLecture?.message || 'séance introuvable');
      const colStatut = role === 'client' ? 'statut_client' : 'statut_formateur';
      if (frais[colStatut] === 'Signé') {
        toast.success('Ce document est déjà signé.', { id: toastId });
        await fetchSessions();
        return true;
      }
      const meta = lireMeta(frais.metadata);
      const source = meta.file_url_signed || frais.signed_pdf_url || frais.file_url || frais.ressource_url;
      if (!source) throw new Error('aucun fichier n\'est rattaché à ce document');
      const client = clients.find(c => String(c.id) === String(frais.client_id));
      const pdf = await construirePdfSigneSeance({
        sourceUrl: source, meta, role, signatureDataUrl, checkedIds, textValues,
        nomSignataire: role === 'client' ? (client?.nom_complet || client?.nom || '') : (nomUtilisateurConnecte || ''),
      });
      const chemin = `signed-documents/seances/${Date.now()}_${String(sessionId).slice(0, 8)}_${role}.pdf`;
      const { error: errUpload } = await supabase.storage.from('documents').upload(chemin, pdf, { contentType: 'application/pdf' });
      if (errUpload) throw new Error('envoi du PDF signé impossible : ' + errUpload.message);
      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(chemin);
      const maintenant = new Date().toISOString();
      const maj = role === 'client'
        ? { signature_client: signatureDataUrl, statut_client: 'Signé', date_signature_client: maintenant }
        : { signature_formateur: signatureDataUrl, statut_formateur: 'Signé', date_signature_formateur: maintenant };
      maj.metadata = { ...meta, file_url_signed: publicUrl };
      maj.signed_pdf_url = publicUrl;
      const clientOk = role === 'client' || frais.statut_client === 'Signé';
      const formateurOk = role === 'formateur' || frais.statut_formateur === 'Signé';
      if ((meta.requiresClientSignature === false || clientOk) && (meta.requiresTrainerSignature !== true || formateurOk)) maj.statut = 'Signé';
      const { error: errMaj } = await supabase.from('sessions').update(maj).eq('id', sessionId);
      if (errMaj) throw new Error(errMaj.message);
      await fetchSessions();
      toast.success('Document signé avec succès !', { id: toastId });
      return true;
    } catch (e) {
      console.error('[signerDocumentDeSeance]', e);
      toast.error(`La signature n'a pas pu être enregistrée (${e.message || e}). Rien n'a été modifié : vous pouvez réessayer.`, { id: toastId, duration: 8000 });
      return false;
    }
  };

  const handleEmargementSave = async (formateurSig, clientSig) => {
    const sessionId = signingSessionId;
    if (!sessionId) return;

    const updateData = {};

    if (formateurSig) {
      updateData.signature_formateur = formateurSig;
      updateData.statut_formateur = 'Signé';
      updateData.date_signature_formateur = new Date().toISOString();
    }

    if (clientSig) {
      updateData.signature_client = clientSig;
      updateData.statut_client = 'Signé';
      updateData.date_signature_client = new Date().toISOString();
    }

    // FIX (2026-09-25) : le statut global "statut" (encore lu par les écrans de planning via
    // isSigned = statut_formateur === 'Signé' || statut === 'Signé') ne doit passer à 'Signé' que
    // lorsque TOUTES les signatures réellement exigées par la séance (metadata.requiresClientSignature
    // !== false, metadata.requiresTrainerSignature === true) sont réunies. Avant ce correctif, signer
    // d'UN SEUL côté (ex. le client) mettait déjà updateData.statut = 'Signé', ce qui faisait
    // apparaître le bouton "Signer" du formateur en "Signé ✓" (désactivé) dans
    // "Mes Clients > Planning des Séances" alors qu'il n'avait pas encore signé lui-même —
    // l'empêchant purement et simplement d'émarger.
    // SIGNATURES (2026-10-20) : on relit la séance (l'autre partie a pu signer depuis l'ouverture de la
    // page) — sinon le statut final et le compteur pouvaient être faux.
    const { data: _seanceFraiche } = await supabase.from('sessions').select('*').eq('id', sessionId).maybeSingle();
    const emargementSession = _seanceFraiche || sessions.find(s => String(s.id) === String(sessionId));
    const emargementMeta = lireMeta(emargementSession?.metadata);
    // Un « document à signer » (et non un émargement) signé par le formateur : la signature est aussi
    // posée sur le PDF du document. Si cela échoue, rien n'est enregistré.
    if (emargementSession?.type_activite === 'document' && (formateurSig || clientSig)
      && (emargementMeta.file_url_signed || emargementSession.signed_pdf_url || emargementSession.file_url || emargementSession.ressource_url)) {
      const _role = formateurSig ? 'formateur' : 'client';
      try {
        const _client = clients.find(c => String(c.id) === String(emargementSession.client_id));
        const _pdf = await construirePdfSigneSeance({
          sourceUrl: emargementMeta.file_url_signed || emargementSession.signed_pdf_url || emargementSession.file_url || emargementSession.ressource_url,
          meta: emargementMeta, role: _role, signatureDataUrl: formateurSig || clientSig,
          nomSignataire: _role === 'client' ? (_client?.nom_complet || _client?.nom || '') : (nomUtilisateurConnecte || ''),
        });
        const _chemin = `signed-documents/seances/${Date.now()}_${String(sessionId).slice(0, 8)}_${_role}.pdf`;
        const { error: _errUp } = await supabase.storage.from('documents').upload(_chemin, _pdf, { contentType: 'application/pdf' });
        if (_errUp) throw new Error('envoi du PDF signé impossible : ' + _errUp.message);
        const { data: { publicUrl: _url } } = supabase.storage.from('documents').getPublicUrl(_chemin);
        updateData.metadata = { ...emargementMeta, file_url_signed: _url };
        updateData.signed_pdf_url = _url;
      } catch (e) {
        console.error('[handleEmargementSave] PDF signé :', e);
        toast.error(`La signature n'a pas pu être enregistrée (${e.message || e}). Rien n'a été modifié : vous pouvez réessayer.`, { duration: 8000 });
        return;
      }
    }
    const requiresClientSig = emargementMeta.requiresClientSignature !== false;
    const requiresTrainerSig = emargementMeta.requiresTrainerSignature === true;
    const clientNowSigned = !!clientSig || emargementSession?.statut_client === 'Signé';
    const trainerNowSigned = !!formateurSig || emargementSession?.statut_formateur === 'Signé';
    if ((!requiresClientSig || clientNowSigned) && (!requiresTrainerSig || trainerNowSigned)) {
      updateData.statut = 'Signé';
    }

    const { error } = await supabase.from('sessions').update(updateData).eq('id', sessionId);
    if (!error) {
      // FIX (2026-09-26) : incrémente le compteur de progression du client (clients.seances_effectuees,
      // lu par la barre "PROGRESSION X%" de la vue formateur "Mes Clients") dès que cette séance passe
      // à 'Signé' — cette fonction ne le faisait jamais, contrairement à handleSessionSignatureSave qui
      // gère le même compteur pour les documents. On ne compte que la transition (pas encore signée ->
      // signée) pour éviter un double comptage si la fonction était rappelée.
      if (updateData.statut === 'Signé' && emargementSession?.statut !== 'Signé') {
        const emargementClient = clients.find(c => c.id === emargementSession?.client_id);
        if (emargementClient) {
          const newEffectuees = (emargementClient.seances_effectuees || 0) + 1;
          await supabase.from('clients').update({ seances_effectuees: newEffectuees }).eq('id', emargementClient.id);
          await fetchUtilisateurs();
        }
      }
      await fetchSessions();
      setSigningSessionId(null);
      toast.success(userRole === 'client' ? 'Merci ! Votre présence est bien enregistrée.' : 'Émargement enregistré !');
    } else {
      toast.error('Erreur : ' + error.message);
    }
  };

  const handleAddSessionItem = async (data) => {
    if (!targetSessionForAddition) return;
    
    setIsSessionItemModalOpen(false);
    
    const clientId = targetSessionForAddition.clientId;
    const { sessionChoice, sessionData } = data;

    let numero = 1;
    let date = null;
    let debut = null;
    let fin = null;

    if (sessionChoice === 'existing') {
      numero = sessionData.numero_seance;
      date = sessionData.date;
      debut = sessionData.heure_debut;
      fin = sessionData.heure_fin;
    } else {
      // Nouvelle séance
      const clientSessions = sessions.filter(s => s.client_id === clientId);
      numero = clientSessions.length > 0 ? Math.max(...clientSessions.map(s => s.numero_seance)) + 1 : 1;
      date = sessionData.date;
      debut = sessionData.heure_debut;
      fin = sessionData.heure_fin;
    }

    const targetGroupItems = sessions.filter(s =>
      s.client_id === clientId && s.numero_seance === numero
    );
    const nextPos = targetGroupItems.length > 0
      ? Math.max(...targetGroupItems.map(s => s.position || 0)) + 1
      : 0;

    // organisation_id manquant ici auparavant : les autres insertions dans `sessions` (génération
    // automatique, bloc administratif) l'incluent toujours — sans lui, la policy RLS de la table
    // (filtrée par organisation) rejette l'insertion avec un 403, d'où "Erreur lors de l'ajout."
    const targetClientForItem = clients.find(c => c.id === clientId);
    const { error } = await supabase.from('sessions').insert([{
      client_id: clientId,
      module_id: targetClientForItem?.module_id,
      organisation_id: targetClientForItem?.organisation_id || currentOrgId || null,
      numero_seance: numero,
      nom: data.title,
      ressource_titre: data.title,
      type_activite: data.type,
      file_url: data.resourceId,
      ressource_url: data.resourceId,
      is_administrative: numero === 0,
      position: nextPos,
      metadata: {
        isCustom: true,
        documentType: data.type === 'signature' ? 'signature' : (data.isToSign ? 'signature' : 'info')
      },
      statut: 'À venir',
      statut_client: 'À venir',
      statut_formateur: 'À venir',
      date: date,
      heure_debut: debut,
      heure_fin: fin,
      ...(data.instructions ? { instructions: data.instructions } : {})
    }]);



    if (!error) {
      await fetchSessions();
      toast.success("Élément ajouté avec succès !");
    } else {
      console.error("Erreur ajout item:", error);
      toast.error("Erreur lors de l'ajout.");
    }
  };

  const handleMoveSessionItem = async (sessionId, targetNumeroSeance, targetDate, targetDebut, targetFin) => {
    const movedSession = sessions.find(s => s.id === sessionId);
    // LOT B (2026-10-21) : un élément signé reste dans sa séance (la signature correspond à cette date).
    const { data: ligneActuelle, error: errLecture } = await supabase.from('sessions').select('*').eq('id', sessionId).maybeSingle();
    if (errLecture) { toast.error('Vérification impossible, déplacement annulé : ' + errLecture.message); return; }
    if (elementSeanceEstSigne(ligneActuelle)) {
      toast.error('Cet élément a déjà été signé : il reste dans sa séance (la signature correspond à cette date).', { duration: 7000 });
      return;
    }
    const ancienNumero = ligneActuelle?.numero_seance ?? movedSession?.numero_seance;
    const targetGroupItems = sessions.filter(s =>
      s.client_id === movedSession?.client_id &&
      s.numero_seance === targetNumeroSeance &&
      s.id !== sessionId
    );
    const nextPosition = targetGroupItems.length > 0
      ? Math.max(...targetGroupItems.map(s => s.position || 0)) + 1
      : 0;

    const { error } = await supabase.from('sessions').update({
      numero_seance: targetNumeroSeance,
      date: targetDate || null,
      heure_debut: targetDebut || null,
      heure_fin: targetFin || null,
      position: nextPosition,
      is_administrative: targetNumeroSeance === 0,
    }).eq('id', sessionId);

    if (!error) {
      await fetchSessions();
      toast.success("Activité déplacée avec succès !");
      const clientDuDeplacement = ligneActuelle?.client_id || movedSession?.client_id;
      planifierSyncAgenda(clientDuDeplacement, ancienNumero);
      planifierSyncAgenda(clientDuDeplacement, targetNumeroSeance);
    } else {
      toast.error("Erreur lors du déplacement : " + error.message);
    }
  };

  const handleAddAdminBloc = async (clientId) => {
    const hasAdminBloc = sessions.some(s => s.client_id === clientId && s.is_administrative);
    if (hasAdminBloc) {
      toast.error("Un bloc administratif existe déjà pour ce bénéficiaire.");
      return;
    }
    const regularSessions = sessions.filter(s => s.client_id === clientId && !s.is_administrative);
    const seance1Date = regularSessions
      .filter(s => s.numero_seance === 1)
      .find(s => s.date)?.date;
    let adminDate = null;
    if (seance1Date) {
      const d = new Date(seance1Date);
      d.setDate(d.getDate() - 7);
      adminDate = d.toISOString().split('T')[0];
    }
    const client = clients.find(c => c.id === clientId);
    const { error } = await supabase.from('sessions').insert([{
      client_id: clientId,
      module_id: client?.module_id || null,
      organisation_id: currentOrgId,
      numero_seance: 0,
      nom: 'Bloc Administratif',
      ressource_titre: 'Document administratif',
      type_activite: 'Document PDF',
      is_administrative: true,
      position: 0,
      date: adminDate,
      statut: 'À venir',
      statut_client: 'À venir',
      statut_formateur: 'À venir',
      metadata: { isCustom: true, documentType: 'info' }
    }]);
    if (!error) {
      await fetchSessions();
      toast.success("Bloc Administratif créé !");
    } else {
      toast.error("Erreur : " + error.message);
    }
  };

  const handleAddSession = async (client) => {
    const clientSessions = sessions.filter(s => s.client_id === client.id).sort((a, b) => a.numero_seance - b.numero_seance);
    const nextNum = clientSessions.length > 0 ? Math.max(...clientSessions.map(s => s.numero_seance)) + 1 : 1;
    const module = modules.find(m => m.id === client.module_id);
    const moduleName = module ? module.nom : "Séance";

    const { error: insError } = await supabase.from('sessions').insert([{
      numero_seance: nextNum,
      nom: `${moduleName} - Séance ${nextNum}`,
      client_id: client.id,
      module_id: client.module_id,
      organisation_id: client.organisation_id || currentOrgId || null,
      metadata: {},
      statut: 'À venir',
      statut_client: 'À venir',
      statut_formateur: 'À venir'
    }]);

    if (!insError) {
      const newTotal = (client.seances_totales || 0) + 1;
      await supabase.from('clients').update({ seances_totales: newTotal }).eq('id', client.id);
      await fetchUtilisateurs();
      await fetchSessions();
    } else {
      toast.error("Erreur lors de l'ajout de la séance: " + insError.message);
    }
  };

  const handleDeleteSession = (sessionOrId) => {
    const session = typeof sessionOrId === 'object' ? sessionOrId : sessions.find(s => s.id === sessionOrId);
    if (!session) return;

    const isCustom = session.metadata?.isCustom === true;
    const confirmTitle = isCustom ? "Supprimer cet élément personnalisé ?" : `Supprimer la séance N°${session.numero_seance} ?`;
    const confirmMsg = 'Cette action est irréversible.';

    showAppConfirm(confirmTitle, confirmMsg, async () => {
      hideAppConfirm();
      if (!(await peutSupprimerElementSeance(session.id))) return; // lot 2 : jamais un élément signé
      const { error: delError } = await supabase.from('sessions').delete().eq('id', session.id);
      if (!delError) {
        if (!isCustom) {
          const client = clients.find(c => c.id === session.client_id);
          if (client) {
            const newTotal = Math.max(0, (client.seances_totales || 0) - 1);
            await supabase.from('clients').update({ seances_totales: newTotal }).eq('id', client.id);
            await fetchUtilisateurs();
          }
        }
        await fetchSessions();
      } else {
        toast.error("Erreur suppression: " + delError.message);
      }
    });
  };

  const assignFormateur = async (userId, formateurId) => {
    // Capturé AVANT la mise à jour : sert à ne pas ré-envoyer un email de notification si on
    // ré-enregistre le même formateur sans changement réel (voir plus bas).
    const previousFormateurId = clients.find(c => c.id === userId)?.formateur_id || null;
    const { error } = await supabase.from('clients').update({ formateur_id: formateurId || null }).eq('id', userId);
    if (!error) {
      setClients(clients.map(c => c.id === userId ? { ...c, formateur_id: formateurId } : c));
      // FIX (nouvelle fonctionnalité, 2026-09-11) : prévient le formateur par email dès qu'un
      // client lui est assigné ou réassigné (voir api/formateur/notify-assignment.js), pour qu'il
      // ne le découvre pas par hasard en allant vérifier sa liste de clients — demande utilisateur.
      // Volontairement PAS envoyé si on retire le formateur (formateurId vide) ni si c'est le même
      // formateur qu'avant (pas de vrai changement). "Fire-and-forget" : un souci d'envoi (clé
      // Resend absente, formateur sans email...) ne doit jamais remettre en cause l'assignation
      // elle-même, déjà enregistrée en base à ce stade — juste logué en console pour diagnostic.
      if (formateurId && String(formateurId) !== String(previousFormateurId)) {
        supabase.auth.getSession().then(({ data: sessionData }) => {
          const accessToken = sessionData?.session?.access_token;
          if (!accessToken) return;
          fetch('/api/formateur/notify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            body: JSON.stringify({ type: 'assignation', clientId: userId, formateurId, origin: window.location.origin }),
          }).then(async (resp) => {
            const result = await resp.json().catch(() => ({}));
            if (!resp.ok) {
              console.warn('[assignFormateur] Notification formateur non envoyée :', result.error || resp.status);
            } else if (result.simulated) {
              console.warn('[assignFormateur] Notification formateur simulée (RESEND_API_KEY non configurée côté serveur).');
            }
          }).catch(e => console.warn('[assignFormateur] Erreur réseau notification formateur :', e.message));
        }).catch(e => console.warn('[assignFormateur] Erreur session notification formateur :', e.message));
      }
    } else {
      toast.error("Erreur assignation: " + error.message);
    }
  };

  const handleUploadDocxTemplate = async (fileArg, typeArg, destinationArg, classificationArg, formateurIdsArg = null) => {
    try {
      const file = fileArg || null;
      const type = typeArg || newTemplateName || (file ? file.name.replace(/\.[^/.]+$/, '') : null);
      const destination = destinationArg || newTemplateDestination || 'client';
      const classification = classificationArg || newTemplateClassification || 'telechargeable';

      if (!file) {
        toast.error("Veuillez sélectionner un fichier .docx ou .pdf d'abord.");
        return;
      }

      const fileExt = file.name.split('.').pop().toLowerCase();
      // Supprime les accents (é→e, à→a, etc.) puis remplace tout ce qui n'est pas alphanumérique par _
      const safeType = type.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `template_${safeType}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);
      setDocumentTemplates(prev => ({
        ...prev,
        [type]: { url: publicUrl, name: file.name, destination, classification }
      }));

      // Sauvegarder dans module_step_resources (Source unifiée)
      let msrQuery = supabase.from('module_step_resources').select('id, metadata').eq('titre', type).eq('type', 'document');
      if (currentOrgId) msrQuery = msrQuery.eq('organisation_id', currentOrgId);
      const { data: existing } = await msrQuery;

      const metadataObj = { classification };
      if (existing && existing.length > 0) {
        const oldMeta = (typeof existing[0].metadata === 'string' && existing[0].metadata.startsWith('{')) ? JSON.parse(existing[0].metadata) : (existing[0].metadata || {});
        const newMeta = JSON.stringify({ ...oldMeta, classification });
        await supabase.from('module_step_resources').update({ file_url: publicUrl, destination, metadata: newMeta, extension: fileExt }).eq('id', existing[0].id);
      } else {
        await supabase.from('module_step_resources').insert([{
          titre: type,
          file_url: publicUrl,
          type: 'document',
          destination,
          metadata: JSON.stringify(metadataObj),
          extension: fileExt,
          ...(currentOrgId ? { organisation_id: currentOrgId } : {})
        }]);
      }

      // Sync avec documents
      let docCheckQuery = supabase.from('documents').select('id').eq('nom', type).is('user_id', null).is('assigned_formateur_id', null); // lot 2
      if (currentOrgId) docCheckQuery = docCheckQuery.eq('organisation_id', currentOrgId);
      const { data: existingDoc } = await docCheckQuery;
      if (existingDoc && existingDoc.length > 0) {
        await supabase.from('documents').update({ url: publicUrl, extension: fileExt }).eq('id', existingDoc[0].id);
      } else {
        const { error: docInsertErr } = await supabase.from('documents').insert([{
          nom: type,
          type_action: 'Modèle Référence',
          url: publicUrl,
          extension: fileExt,
          user_id: null,
          ...(currentOrgId ? { organisation_id: currentOrgId } : {})
        }]);
        if (docInsertErr) console.warn('[handleUploadDocxTemplate] documents insert error (non-bloquant):', docInsertErr.message);
      }

      await fetchDocuments();

      // Le template est enregistré dans la bibliothèque — pas de distribution automatique.
      // L'admin l'envoie manuellement depuis la fiche de chaque formateur.
      toast.success(`Modèle "${type}" ajouté à la bibliothèque. Ouvrez la fiche d'un formateur pour lui envoyer.`);
    } catch (err) {
      console.error("Upload Template Error:", err);
      toast.error("Erreur lors de l'upload: " + (err.message || 'Erreur inconnue'));
    }
  };

  // ── VisualTemplateEditor : upload DOCX + sauvegarde des champs visuels ──
  const handleUploadVisualTemplate = async (fileArg, nameArg, destinationArg, fieldsArg, classificationArg, editingTemplateId = null, signingConfigArg = null) => {
    try {
      const file = fileArg;
      const name = nameArg || (file ? file.name.replace(/\.[^/.]+$/, '') : 'modele');
      const destination = destinationArg || 'client';
      const fieldsToSave = fieldsArg || [];
      const classification = classificationArg || 'a_generer';
      // Mode de signature (Stage 3, 2026-07-24 ; généralisé à 3 parties le 2026-07-27) : 'simultane'
      // (défaut, comportement historique) ou 'sequentiel' avec un ordre — réglé une fois ici sur le
      // modèle, propagé sur chaque document instancié (instantiateDocument lit meta.signing_mode/
      // signing_order) et sur les instances déjà envoyées à des clients (propagation ci-dessous).
      // signing_order est désormais un TABLEAU de rôles ('client'|'formateur'|'organisme') dans
      // l'ordre de signature — normalizeSigningOrder() (module-level) sait toujours lire l'ancien
      // format 'client_first'/'formateur_first' pour les modèles créés avant ce changement.
      const signingMode = signingConfigArg?.mode === 'sequentiel' ? 'sequentiel' : 'simultane';
      const signingOrderRoles = Array.isArray(signingConfigArg?.order) && signingConfigArg.order.length
        ? signingConfigArg.order.filter(r => SIGNER_ROLES.includes(r))
        : null;

      if (!file) throw new Error('Aucun fichier fourni.');

      // 1. Upload du fichier dans le Storage (toujours — même en mode édition pour garantir une URL valide)
      const fileExt = (file.name.split('.').pop() || 'docx').toLowerCase();
      const safeName = name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `template_visual_${safeName}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);

      // 2. Insérer / mettre à jour le MSR maître dans module_step_resources
      // Note : visual_template_id sera ajouté juste après (auto-référence, nécessite msrId)
      const metadataObj = {
        classification, has_visual_fields: true,
        signing_mode: signingMode,
        ...(signingMode === 'sequentiel' && signingOrderRoles ? { signing_order: signingOrderRoles } : {}),
      };
      let msrId = null;

      if (editingTemplateId) {
        // Mode édition : mettre à jour par ID — jamais créer de doublon
        msrId = editingTemplateId;
        await supabase.from('module_step_resources').update({
          titre: name, file_url: publicUrl, destination,
          metadata: JSON.stringify(metadataObj),
        }).eq('id', msrId);
      } else {
        // Nouveau template : chercher par nom ou créer
        let msrQuery = supabase.from('module_step_resources').select('id').eq('titre', name).eq('type', 'document');
        if (currentOrgId) msrQuery = msrQuery.eq('organisation_id', currentOrgId);
        const { data: existingMsr } = await msrQuery;

        if (existingMsr && existingMsr.length > 0) {
          msrId = existingMsr[0].id;
          await supabase.from('module_step_resources').update({
            file_url: publicUrl, destination,
            metadata: JSON.stringify(metadataObj),
          }).eq('id', msrId);
        } else {
          const { data: insertedMsr, error: msrErr } = await supabase.from('module_step_resources').insert([{
            titre: name, file_url: publicUrl, type: 'document', destination,
            metadata: JSON.stringify(metadataObj),
            ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
          }]).select('id').single();
          if (msrErr) throw msrErr;
          msrId = insertedMsr.id;
        }
      }

      // FIX 1 — Ajouter visual_template_id + champs embarqués dans le metadata MSR.
      // visual_template_id : auto-référence, nécessaire pour instantiateDocument branch 1.
      // template_fields embarqués : élimine la dépendance RLS sur la table template_fields
      //   → champs accessibles sans requête DB partout (handleOpenSigning, instantiateDocument, handleSignSave).
      const embeddedFields = fieldsToSave.map(f => ({
        template_id: msrId,
        // client_key : identifiant unique posé par l'éditeur (f.id) — conservé ici car cette copie
        // "zéro RLS" dans metadata n'a pas d'id de ligne DB. Indispensable pour distinguer 2 cases
        // à cocher entre elles (sinon elles partagent la même identité "undefined" — voir fieldKey).
        client_key: f.id,
        tag: f.tag,
        page: f.page || 1,
        x_percent: parseFloat(f.xPct.toFixed(4)),
        y_percent: parseFloat(f.yPct.toFixed(4)),
        // FIX (2026-09-04) : largeur/hauteur de la case "texte libre" (en % de la page), réglées par
        // l'admin via la poignée de redimensionnement — undefined pour les autres types de balise.
        ...(typeof f.width_percent === 'number' ? { width_percent: parseFloat(f.width_percent.toFixed(4)) } : {}),
        ...(typeof f.height_percent === 'number' ? { height_percent: parseFloat(f.height_percent.toFixed(4)) } : {}),
        field_type: (f.tag || '').startsWith('signature_') ? 'signature' : (f.tag || '').startsWith('checkbox_') ? 'checkbox' : (f.tag || '').startsWith('texte_') ? 'text_input' : 'text',
        font_size: 11,
      }));
      const fullMetadataObj = { ...metadataObj, visual_template_id: msrId, template_fields: embeddedFields };
      await supabase.from('module_step_resources').update({
        metadata: JSON.stringify(fullMetadataObj),
      }).eq('id', msrId);

      // 3. Sauvegarder les champs visuels dans template_fields
      // Non-bloquant : les champs sont déjà dupliqués en zéro-RLS dans metadata.template_fields
      // (voir FIX 1 ci-dessus) → si cette table a un souci (ex : contrainte de schéma qui ne connaît
      // pas encore 'checkbox' comme field_type), le document reste malgré tout utilisable côté client
      // via les champs embarqués, au lieu de faire échouer TOUT l'enregistrement du modèle.
      if (fieldsToSave.length > 0 && msrId) {
        try {
          await supabase.from('template_fields').delete().eq('template_id', msrId);
          const rows = fieldsToSave.map(f => ({
            template_id: msrId,
            tag: f.tag,
            page: f.page || 1,
            x_percent: parseFloat(f.xPct.toFixed(4)),
            y_percent: parseFloat(f.yPct.toFixed(4)),
            font_size: 11,
            field_type: (f.tag || '').startsWith('signature_') ? 'signature' : (f.tag || '').startsWith('checkbox_') ? 'checkbox' : (f.tag || '').startsWith('texte_') ? 'text_input' : 'text',
            ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
          }));
          const { error: fieldsErr } = await supabase.from('template_fields').insert(rows);
          if (fieldsErr) {
            console.warn('[handleUploadVisualTemplate] Échec insertion template_fields (non-bloquant, fallback metadata embarquée) :', fieldsErr.message);
            toast('⚠️ Positions enregistrées en secours (table technique indisponible) : ' + fieldsErr.message, { icon: '⚠️', duration: 6000 });
          }
        } catch (e) {
          console.warn('[handleUploadVisualTemplate] Erreur template_fields (non-bloquant) :', e.message);
        }
      }

      // FIX 2 — Propager visual_template_id aux copies MSR module-liées (même titre), à l'intérieur
      // du MÊME organisme uniquement — ou sans organisation_id renseigné (copies historiques non
      // scopées). Le filtre organisation_id avait été totalement supprimé ici, ce qui permettait à
      // l'admin d'un organisme de modifier les modèles de documents de n'importe quel AUTRE organisme
      // partageant le même titre de modèle — un trou de cloisonnement multi-tenant critique.
      if (msrId && currentOrgId) {
        const propagateQuery = supabase.from('module_step_resources')
          .update({ metadata: JSON.stringify(fullMetadataObj) })
          .eq('titre', name)
          .eq('type', 'document')
          .eq('organisation_id', currentOrgId)
          .neq('id', msrId);
        await propagateQuery;
      }

      // 4. Sync avec table documents (Modèle Référence) — stocker visual_template_id + champs pour Priority 2
      const visualDocMeta = JSON.stringify({ has_visual_fields: true, visual_template_id: msrId, template_fields: embeddedFields });
      let docCheckQuery = supabase.from('documents').select('id').eq('nom', name).is('user_id', null).is('assigned_formateur_id', null); // lot 2
      if (currentOrgId) docCheckQuery = docCheckQuery.eq('organisation_id', currentOrgId);
      const { data: existingDoc } = await docCheckQuery;
      if (existingDoc && existingDoc.length > 0) {
        await supabase.from('documents').update({ url: publicUrl, metadata: visualDocMeta }).eq('id', existingDoc[0].id);
      } else {
        const { error: docInsertErr } = await supabase.from('documents').insert([{
          nom: name, type_action: 'Modèle Référence', url: publicUrl,
          metadata: visualDocMeta,
          user_id: null,
          ...(currentOrgId ? { organisation_id: currentOrgId } : {}),
        }]);
        if (docInsertErr) console.warn('[handleUploadVisualTemplate] documents insert error (non-bloquant):', docInsertErr.message);
      }

      // FIX 3 — Auto-distribuer aux clients concernés (contexte admin, pas de RLS).
      // Couvre 3 sources : module direct, document_group→module, documents clients existants.
      if (msrId) {
        try {
          const masterResource = {
            titre: name, file_url: publicUrl, destination,
            metadata: JSON.stringify(fullMetadataObj),
          };

          // Source 1 : MSR liés directement à un module (type=document, module_id non null)
          // Scopé à l'organisation courante (+ legacy sans organisation_id) pour éviter de traiter
          // des ressources d'autres organismes portant le même titre.
          let directLinksQuery = supabase
            .from('module_step_resources').select('module_id')
            .eq('titre', name).eq('type', 'document').not('module_id', 'is', null);
          directLinksQuery = currentOrgId ? directLinksQuery.eq('organisation_id', currentOrgId) : directLinksQuery.limit(0);
          const { data: directLinks } = await directLinksQuery;
          const directModuleIds = [...new Set((directLinks || []).map(m => m.module_id).filter(Boolean))];

          // Source 2 : document dans un document_group → remonter au module via le groupe
          let groupLinksQuery = supabase
            .from('module_step_resources').select('document_group_id')
            .eq('titre', name).eq('type', 'document').not('document_group_id', 'is', null);
          groupLinksQuery = currentOrgId ? groupLinksQuery.eq('organisation_id', currentOrgId) : groupLinksQuery.limit(0);
          const { data: groupLinks } = await groupLinksQuery;
          const groupIds = [...new Set((groupLinks || []).map(d => d.document_group_id).filter(Boolean))];
          let groupModuleIds = [];
          if (groupIds.length > 0) {
            let groupParentsQuery = supabase
              .from('module_step_resources').select('module_id')
              .eq('type', 'document_group').in('document_group_id', groupIds).not('module_id', 'is', null);
            groupParentsQuery = currentOrgId ? groupParentsQuery.eq('organisation_id', currentOrgId) : groupParentsQuery.limit(0);
            const { data: groupParents } = await groupParentsQuery;
            groupModuleIds = [...new Set((groupParents || []).map(m => m.module_id).filter(Boolean))];
          }

          const allModuleIds = [...new Set([...directModuleIds, ...groupModuleIds])];

          // Source 3 : clients ayant déjà un document avec ce nom (non signé) → mettre à jour leur doc
          let existingClientDocsQuery = supabase
            .from('documents').select('user_id')
            .eq('nom', name).not('user_id', 'is', null).eq('signe_par_client', false);
          existingClientDocsQuery = currentOrgId ? existingClientDocsQuery.eq('organisation_id', currentOrgId) : existingClientDocsQuery.limit(0);
          const { data: existingClientDocs } = await existingClientDocsQuery;
          const existingClientIds = new Set((existingClientDocs || []).map(d => String(d.user_id)));

          // Union de toutes les sources
          const clientsFromModules = allModuleIds.length > 0
            ? (clients || []).filter(c => c.module_id && allModuleIds.includes(c.module_id))
            : [];
          const clientsFromExisting = (clients || []).filter(c => existingClientIds.has(String(c.id)));

          const allClientIds = new Set([
            ...clientsFromModules.map(c => String(c.id)),
            ...clientsFromExisting.map(c => String(c.id)),
          ]);
          const allClientsToUpdate = (clients || []).filter(c => allClientIds.has(String(c.id)));

          if (allClientsToUpdate.length > 0) {
            for (const client of allClientsToUpdate) {
              await instantiateDocument(client, masterResource);
            }
            console.log(`[handleUploadVisualTemplate] Auto-distribué à ${allClientsToUpdate.length} client(s).`);
          }
        } catch (distErr) {
          console.warn('[handleUploadVisualTemplate] Auto-distribution error:', distErr.message);
        }
      }

      // 5. Mettre à jour le state local
      setDocumentTemplates(prev => ({
        ...prev,
        [name]: { id: msrId, url: publicUrl, name, destination, classification, metadata: fullMetadataObj }
      }));

      await fetchDocuments();
      toast.success(`Modèle "${name}" enregistré avec ${fieldsToSave.length} balise${fieldsToSave.length !== 1 ? 's' : ''}.`);
    } catch (err) {
      console.error('[handleUploadVisualTemplate]', err);
      toast.error('Erreur : ' + (err.message || 'Erreur inconnue'));
    }
  };

  const handleDeleteDocxTemplate = async (templateKey) => {
    try {
      const template = documentTemplates[templateKey];
      if (!template) return;
      if (!currentOrgId) {
        toast.error("Organisme introuvable — suppression annulée par sécurité.");
        return;
      }

      // Extraction du nom du fichier pour le storage
      const fileName = template.url.split('/').pop().split('?')[0];

      // 1. LOT 2 (2026-10-20) : le fichier n'est PLUS supprimé du stockage — les documents déjà
      // envoyés aux clients (y compris signés) l'utilisent encore. Nettoyage des fichiers inutiles à part.
      void fileName;

      // 2. Supprimer de documents (Modèle Référence) — limité à l'organisme courant (+ lignes
      // historiques sans organisation_id). Avant ce correctif, aucun filtre d'organisme n'existait :
      // un modèle du même nom appartenant à un AUTRE organisme aurait aussi été supprimé.
      await supabase.from('documents').delete()
        .eq('nom', templateKey).eq('type_action', 'Modèle Référence')
        .eq('organisation_id', currentOrgId);

      // 3. Supprimer de module_step_resources — même précaution, + limité au type 'document' pour ne
      // pas toucher d'autres ressources de module qui partageraient le même titre par coïncidence.
      await supabase.from('module_step_resources').delete()
        .eq('titre', templateKey).eq('type', 'document')
        .eq('organisation_id', currentOrgId);

      // 4. Update local state
      const newTemplates = { ...documentTemplates };
      delete newTemplates[templateKey];
      setDocumentTemplates(newTemplates);

      // 5. Rafraîchir la liste des documents
      await fetchDocuments();

      toast.success(`Modèle "${templateKey}" supprimé définitivement.`);
    } catch (err) {
      console.error("Delete Template Error:", err);
      toast.error("Erreur lors de la suppression.");
    }
  };

  // Mise à jour de la destination d'un modèle existant
  const handleUpdateTemplateDestination = async (templateKey, newDest) => {
    // Même précaution multi-tenant que handleDeleteDocxTemplate/handleUploadVisualTemplate ci-dessus :
    // limité strictement aux lignes de l'organisme courant.
    if (!currentOrgId) { toast.error('Organisme introuvable — action annulée par sécurité.'); return; }
    const destQuery = supabase.from('module_step_resources').update({ destination: newDest }).eq('titre', templateKey).eq('organisation_id', currentOrgId);
    await destQuery;
    setDocumentTemplates(prev => ({ ...prev, [templateKey]: { ...prev[templateKey], destination: newDest } }));
    toast.success(`Destination de "${templateKey}" mise à jour.`);
  };

  // Sauvegarde des paramètres d'un document de planning (rename + qui signe)
  const handleSaveDocSettings = async ({ nom, requiresClientSignature, requiresTrainerSignature }) => {
    if (!docSettingsTarget) return;
    const id = docSettingsTarget.id;
    const currentMeta = docSettingsTarget.metadata || {};
    const newMeta = {
      ...currentMeta,
      requiresClientSignature,
      requiresTrainerSignature,
    };
    const { error } = await supabase
      .from('sessions')
      .update({ ressource_titre: nom.trim() || docSettingsTarget.ressource_titre, metadata: newMeta })
      .eq('id', id);
    if (!error) {
      await fetchSessions();
      setIsDocSettingsOpen(false);
      setDocSettingsTarget(null);
      toast.success(`Paramètres du document "${nom}" enregistrés.`);
    } else {
      toast.error('Erreur lors de la sauvegarde : ' + error.message);
    }
  };

  const handleDownloadResource = async (fileName) => {
    try {
      // Si le fileName est déjà une URL complète (modélothèque), on tente un lien signé
      // (utile si l'URL pointe vers notre storage Supabase) avant de se rabattre dessus telle quelle.
      if (fileName && (fileName.startsWith('http') || fileName.startsWith('https'))) {
        await openSecureStorageFile(fileName);
        return;
      }

      // Sinon, on va chercher le fichier dans ton storage Supabase
      // On tente d'abord 'ressources-pedagogiques' (historique) puis 'documents' (nouveauté)
      let { data, error } = await supabase.storage
        .from('ressources-pedagogiques')
        .createSignedUrl(fileName, 3600);

      if (error || !data?.signedUrl) {
        console.log('handleDownloadResource: Pas trouvé dans ressources-pedagogiques, tentative dans documents...');
        const secondTry = await supabase.storage
          .from('documents')
          .createSignedUrl(fileName, 3600);
        
        data = secondTry.data;
        error = secondTry.error;
      }

      if (error || !data?.signedUrl) throw error || new Error("Fichier introuvable dans le stockage.");
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error("Erreur téléchargement:", err);
      toast.error('Erreur lors du téléchargement : ' + (err.message || 'Fichier introuvable'));
    }
  };

  // AJOUT (2026-10-09) : le FORMATEUR peut aussi déposer le rendu d'un exercice (photo, PDF, Word...)
  // quand l'exercice a été fait pendant la séance — options.byTrainer = true. Le nom du fichier
  // reçoit un suffixe unique : deux dépôts le même jour pour le même exercice ne se bloquent plus.
  const handleUploadExerciseResponse = async (sessionId, file, options = {}) => {
    try {
      const fileExt = (file.name.split('.').pop() || 'bin').toLowerCase();
      const targetSession = sessions.find(s => s.id === sessionId);
      const client = clients.find(c => c.id === targetSession?.client_id);
      const normalize = (str) => (str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toUpperCase();
      const nomClient = normalize(client?.nom_complet || client?.nom || 'CLIENT');
      const nomExercice = normalize(targetSession?.ressource_titre || 'EXERCICE');
      const date = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
      const fileName = `${nomClient}_${nomExercice}_${date}_${Date.now().toString(36)}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('exercice-returns')
        .upload(fileName, file, { contentType: file.type || undefined });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('exercice-returns')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('sessions')
        .update({
          reponse_url: publicUrl,
          statut: 'Rendu',
          ...(options.byTrainer ? { metadata: { ...(targetSession?.metadata || {}), deposeParFormateur: true, deposeLe: new Date().toISOString() } } : {})
        })
        .eq('id', sessionId);

      if (updateError) throw updateError;

      await fetchSessions();
      toast.success(options.byTrainer ? 'Rendu déposé : il est maintenant visible par le client.' : 'Réponse envoyée avec succès !');
      return true;
    } catch (error) {
      console.error('Erreur upload exercice:', error);
      toast.error('Erreur lors de l\'envoi : ' + error.message);
      return false;
    }
  };

  // AJOUT (2026-10-09) : « Ajouter un exercice réalisé en séance » (vue formateur, onglet Exercices).
  // Crée un exercice dans la séance choisie, déjà "rendu" avec le fichier déposé par le formateur.
  // Il apparaît dans les exercices du client (visible par lui) et dans le planning de la séance.
  const handleAddCompletedExercise = async ({ client, numero, title, file }) => {
    try {
      const fileExt = (file.name.split('.').pop() || 'bin').toLowerCase();
      const normalize = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toUpperCase();
      const date = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
      const fileName = `${normalize(client?.nom_complet || client?.nom || 'CLIENT')}_${normalize(title || 'EXERCICE')}_${date}_${Date.now().toString(36)}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('exercice-returns').upload(fileName, file, { contentType: file.type || undefined });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('exercice-returns').getPublicUrl(fileName);

      const groupItems = sessions.filter(s => s.client_id === client.id && Number(s.numero_seance) === Number(numero));
      const sched = scheduleOf(groupItems);
      const nextPos = groupItems.length > 0 ? Math.max(...groupItems.map(s => s.position || 0)) + 1 : 0;
      const { error } = await supabase.from('sessions').insert([{
        client_id: client.id,
        module_id: client.module_id || null,
        organisation_id: client.organisation_id || currentOrgId || null,
        numero_seance: Number(numero),
        nom: title,
        ressource_titre: title,
        type_activite: 'exercice',
        reponse_url: publicUrl,
        position: nextPos,
        is_administrative: Number(numero) === 0,
        metadata: { isCustom: true, documentType: 'info', deposeParFormateur: true, deposeLe: new Date().toISOString() },
        statut: 'Rendu',
        statut_client: 'À venir',
        statut_formateur: 'À venir',
        date: sched.date || null,
        heure_debut: sched.debut || null,
        heure_fin: sched.fin || null,
      }]);
      if (error) throw error;
      await fetchSessions();
      toast.success(`Exercice « ${title} » ajouté à la séance ${numero} — visible par le client.`);
      return true;
    } catch (error) {
      console.error('Erreur ajout exercice réalisé:', error);
      toast.error("Erreur lors de l'ajout de l'exercice : " + error.message);
      return false;
    }
  };

  const handleSaveCorrection = async (sessionId, statut, commentaire) => {
    const { error } = await supabase.from('sessions')
      .update({ correction_statut: statut, correction_commentaire: commentaire })
      .eq('id', sessionId);
    if (error) throw error;
    await fetchSessions();
    toast.success('Correction enregistrée !');
  };

  // FIX (nouvelle fonctionnalité, 2026-09-11) : prévient le client par email dès qu'un document lui
  // est envoyé (bouton "Envoyer" sur sa fiche, ou envoi automatique) — voir
  // api/client/notify-new-document.js. Même principe "fire-and-forget" que notifyFormateurAssignment
  // (voir assignFormateur) : un souci d'envoi n'annule jamais l'envoi du document lui-même, qui a
  // déjà réussi en base à ce stade — juste logué en console pour diagnostic.
  const notifyClientNewDocument = (clientId, documentName) => {
    // 2026-10-23 : regroupé avec les autres documents envoyés au même client (un seul email).
    programmerEmailDocument('client', clientId, documentName);
  };

  // FIX (nouvelle fonctionnalité, 2026-09-11) : symétrique de notifyClientNewDocument ci-dessus,
  // pour le FORMATEUR — utilisée à la fois par handleGenerateDocx (document simultané, ou
  // séquentiel où le formateur est premier dans l'ordre) et par handleSignDocument (document
  // séquentiel où une signature précédente vient de débloquer le tour du formateur).
  const notifyFormateurNewDocument = (formateurId, documentName) => {
    // 2026-10-23 : regroupé avec les autres documents à signer par le même formateur (un seul email).
    programmerEmailDocument('formateur', formateurId, documentName);
  };

  // FIX (nouvelle fonctionnalité, 2026-09-11) : décide QUI notifier juste après qu'un document
  // devienne disponible pour signature — respecte la signature séquentielle (metadata.signing_mode/
  // signing_order, voir isBlockedBySigningOrder plus haut dans le fichier) : en mode séquentiel,
  // seul le rôle dont c'est VRAIMENT le tour (pas bloqué par un signataire précédent qui n'a pas
  // encore signé) est notifié maintenant ; l'autre le sera plus tard, quand son tour viendra (voir
  // le bloc ajouté dans handleSignDocument). En mode simultané (par défaut), tout le monde de requis
  // est notifié immédiatement, comme avant ce correctif. Appelée juste après un insert réussi dans
  // `documents`, avec l'objet tel qu'inséré (docToInsert) qui porte déjà metadata + les colonnes
  // signe_par_* à false.
  const notifyDocumentRecipients = (insertedDoc, documentName) => {
    // GROUPE (2026-10-23) : envoi groupé en cours pour ce client → on note le document (succès) et
    // un seul email récapitulatif part à la fin.
    if (_envoiGroupe.actif && insertedDoc.user_id && String(insertedDoc.user_id) === String(_envoiGroupe.clientId)) {
      _envoiGroupe.documents.push({ nom: documentName, prevenir: !!(insertedDoc.visible_client && !isBlockedBySigningOrder(insertedDoc, 'client')) });
    } else if (insertedDoc.visible_client && insertedDoc.user_id && !isBlockedBySigningOrder(insertedDoc, 'client')) {
      notifyClientNewDocument(insertedDoc.user_id, documentName);
    }
    if (insertedDoc.visible_formateur && insertedDoc.assigned_formateur_id && !isBlockedBySigningOrder(insertedDoc, 'formateur')) {
      notifyFormateurNewDocument(insertedDoc.assigned_formateur_id, documentName);
    }
  };

  // extraMeta (lot 3, 2026-10-20) : ex. { moment: 'fin' } pour un document de fin de parcours.
  const handleGenerateDocx = async (clientRow, type, isForFormateur = false, formateurId = null, isAutoGenerate = false, mode = 'send', previewWindow = null, extraMeta = null) => { /* 2026-10-21 : chargé à la demande */ const [{ default: PizZip }, { default: Docxtemplater }] = await Promise.all([import('pizzip'), import('docxtemplater')]);
    try {
      const templateInfo = documentTemplates[type];
      if (!templateInfo || !templateInfo.url) {
        toast.error(`Aucun modèle .docx trouvé pour "${type}". Veuillez l'uploader dans l'onglet Ingénierie.`);
        return;
      }

      // Destination automatique depuis la configuration du modèle
      const templateDestination = templateInfo.destination || 'client';
      const effectiveIsForFormateur = isForFormateur || templateDestination === 'formateur';

      let dataToMerge = {};
      let targetId = null;
      let targetName = "Document";
      let uploadBucket = 'documents';
      let finalClient = null;

      // FIX (2026-09-07) : cette branche ("formateur seul", sans aucune donnée client) ne doit
      // s'appliquer que lorsqu'il n'y a PAS de client en contexte (appel direct depuis la fiche
      // Formateur). Dès qu'un clientRow est fourni (appel depuis une fiche client — bouton
      // "Envoyer" sur un document rattaché à ce client), on passe par la branche "else" ci-dessous
      // qui résout correctement le formateur DE CE CLIENT (finalClient.formateur_id) et merge en
      // plus ses données à lui — avant ce correctif, `fId` retombait par erreur sur clientRow.id
      // (l'ID du CLIENT, pas de son formateur), et la recherche dans `utilisateurs` échouait
      // systématiquement ("Formateur non trouvé") pour tout document destination=formateur envoyé
      // depuis une fiche client.
      if ((effectiveIsForFormateur || formateurId) && !clientRow) {
        const fId = formateurId;
        const { data: theFormateur } = await supabase.from('utilisateurs').select('*').eq('id', fId).single();
        if (!theFormateur) throw new Error("Formateur non trouvé");

        dataToMerge = {
          // ── Formateur ──
          nom: theFormateur.nom || '',
          nom_formateur: theFormateur.nom || '',
          formateur_nom_complet: theFormateur.nom || '',
          raison_sociale: theFormateur.nom || '',
          adresse_formateur: theFormateur.adresse_formateur || theFormateur.adresse_pro || theFormateur.adresse_client || theFormateur.adresse || '',
          adresse_session: theFormateur.adresse_session || theFormateur.adresse_formateur || theFormateur.adresse_pro || theFormateur.adresse || '',
          region_formateur: theFormateur.region || '',
          formateur_nda: theFormateur.formateur_nda || theFormateur.nda || '',
          formateur_siret: theFormateur.formateur_siret || theFormateur.siret || '',
          email_formateur: theFormateur.email || '',
          tel_formateur: theFormateur.telephone || '',
          compagnie_assurance: theFormateur.compagnie_assurance || '',
          numero_assurance_rcp: theFormateur.numero_assurance_rcp || '',
          date_signature: '', // 20/10/2026 : vraie date posée au moment de signer
          date_du_jour: new Date().toLocaleDateString('fr-FR'),
          // AJOUT (2026-09-15) : équivalent de date_signature (client) pour le formateur/organisme.
          date_signature_formateur: '', // 20/10/2026 : vraie date posée au moment de signer
          date_signature_organisme: '', // 20/10/2026 : vraie date posée au moment de signer
          // AJOUT (2026-09-16) : balises "initiales" — voir computeInitials() en haut du fichier.
          // (Pas de client dans cette branche — initiales_client reste vide.)
          initiales_formateur: computeInitials(theFormateur.nom || ''),
          initiales_organisme: computeInitials(orgSettings?.nom || ''),
          // ── Organisme (variables org_*) ──
          org_nom: orgSettings?.nom || '',
          org_siret: orgSettings?.siret || '',
          org_nda: orgSettings?.nda || '',
          org_adresse: orgSettings?.adresse || '',
          org_code_postal: orgSettings?.code_postal || '',
          org_ville: orgSettings?.ville || '',
          org_region: orgSettings?.region || '',
          org_site_web: orgSettings?.site_web || '',
        };
        // FIX (2026-09-07) : {ville_formateur} / {code_postal_formateur} / {rue_formateur}
        // ressortaient toujours vides — balises listées dans ALL_TAGS mais jamais dérivées
        // ici depuis adresse_formateur (chaîne combinée), contrairement aux autres points
        // de génération de documents.
        { const _fAddr = parseAddressString(dataToMerge.adresse_formateur);
          dataToMerge.rue_formateur = _fAddr.rue;
          dataToMerge.code_postal_formateur = _fAddr.codePostal;
          dataToMerge.ville_formateur = _fAddr.ville; }
        targetId = fId;
        targetName = theFormateur.nom || "Formateur";
      } else {
        // 1. Récupération depuis la table ciblée 'clients'
        const { data: theClient } = await supabase.from('clients').select('*').eq('id', clientRow.id).single();
        finalClient = theClient || clientRow; // fallback

        // 2. Récupération formateur dans 'utilisateurs'
        let theCoach = { nom: 'Non assigné' };
        if (finalClient.formateur_id) {
          const { data: coachData } = await supabase.from('utilisateurs').select('*').eq('id', finalClient.formateur_id).single();
          if (coachData) theCoach = coachData;
        }

        const module = modules.find(m => m.id === (finalClient.module_id || clientRow.module_id));

        // Extraction impérative des dates via requête sur sessions
        const { data: sessionDates, error: dateError } = await supabase
          .from('sessions')
          .select('date, numero_seance')
          .eq('client_id', clientRow.id)
          .not('date', 'is', null)
          .order('date', { ascending: true });

        let dateDebut = '[Date non définie]';
        let dateFin = '[Date non définie]';

        // LOT 3 (2026-10-20) : seulement les vraies séances (numéro ≥ 1) — avant, le bloc administratif
        // (daté 7 jours avant la séance 1) donnait une date de début 7 jours trop tôt.
        const _vraies = valeursFormationOrganisme({ seances: dateError ? [] : (sessionDates || []) });
        if (_vraies.date_debut) {
          dateDebut = _vraies.date_debut;
          dateFin = _vraies.date_fin;
        }

        dataToMerge = {
          // ── Formateur ──
          nom: theCoach.nom || 'Formateur',
          nom_formateur: theCoach.nom || 'Formateur',
          formateur_nom_complet: theCoach.nom || '',
          raison_sociale: theCoach.nom || 'Formateur',
          adresse_formateur: theCoach.adresse_formateur || theCoach.adresse_pro || theCoach.adresse_client || theCoach.adresse || '',
          region_formateur: theCoach.region || '',
          formateur_nda: theCoach.formateur_nda || theCoach.nda || '',
          formateur_siret: theCoach.formateur_siret || theCoach.siret || '',
          email_formateur: theCoach.email || '',
          tel_formateur: theCoach.telephone || '',
          compagnie_assurance: theCoach.compagnie_assurance || '',
          numero_assurance_rcp: theCoach.numero_assurance_rcp || '',
          // ── Client / Bénéficiaire ──
          nomcomplet_client: finalClient.nom_complet || finalClient.nomcomplet_client || `${finalClient.nom || ''} ${finalClient.prenom || ''}`.trim(),
          // FIX (2026-09-07) : balise {numero_dossier_client} ("Dossier n° : ...") ressortait
          // toujours vide sur le PDF généré — clé absente de dataToMerge alors qu'elle fait
          // partie des balises officiellement disponibles pour un destinataire Client (voir
          // AVAILABLE_TAGS['Client'] plus haut) et que clients.numero_dossier est bien renseigné.
          numero_dossier_client: finalClient.numero_dossier || '',
          client_phone: finalClient.telephone || finalClient.client_phone || '',
          client_email: finalClient.email_contact || finalClient.client_email || finalClient.email || '',
          prix_prestation: finalClient.montant_prestation || module?.prix_prestation || '',
          ...(await valeursFinancement(finalClient.id, finalClient.montant_prestation || module?.prix_prestation)),
          rue_client: finalClient.rue || '',
          code_postal_client: finalClient.code_postal || '',
          ville_client: finalClient.ville || '',
          // LOT 3 (2026-10-20) : balise {adresse_client} (adresse complète) — absente du Word jusqu'ici.
          adresse_client: finalClient.rue
            ? [finalClient.rue, finalClient.code_postal, finalClient.ville].filter(Boolean).join(', ')
            : (finalClient.adresse_postale || [finalClient.code_postal, finalClient.ville].filter(Boolean).join(' ')),
          region_client: finalClient.region || '',
          adresse_session: finalClient.adresse_postale || finalClient.adresse_session || finalClient.adresse_client || '',
          modalite_formation: finalClient.modalite_formation || 'Mixte',
          date_debut: dateDebut,
          date_fin: dateFin,
          date_signature: '', // 20/10/2026 : vraie date posée au moment de signer
          date_du_jour: new Date().toLocaleDateString('fr-FR'),
          // AJOUT (2026-09-15) : équivalent de date_signature (client) pour le formateur/organisme.
          date_signature_formateur: '', // 20/10/2026 : vraie date posée au moment de signer
          date_signature_organisme: '', // 20/10/2026 : vraie date posée au moment de signer
          // AJOUT (2026-09-16) : balises "initiales" — voir computeInitials() en haut du fichier.
          initiales_client: computeInitials(finalClient.nom_complet || finalClient.nomcomplet_client || `${finalClient.nom || ''} ${finalClient.prenom || ''}`.trim()),
          initiales_formateur: computeInitials(theCoach.nom || ''),
          initiales_organisme: computeInitials(orgSettings?.nom || ''),
          formation_nom: module?.nom || 'Formation',
          // ── Organisme (variables org_*) ──
          org_nom: orgSettings?.nom || '',
          org_siret: orgSettings?.siret || '',
          org_nda: orgSettings?.nda || '',
          org_adresse: orgSettings?.adresse || '',
          org_code_postal: orgSettings?.code_postal || '',
          org_ville: orgSettings?.ville || '',
          org_region: orgSettings?.region || '',
          org_site_web: orgSettings?.site_web || '',
        };
        // FIX (2026-09-07) : {ville_formateur} / {code_postal_formateur} / {rue_formateur}
        // ressortaient toujours vides — balises listées dans ALL_TAGS mais jamais dérivées
        // ici depuis adresse_formateur (chaîne combinée), contrairement aux autres points
        // de génération de documents.
        { const _fAddr = parseAddressString(dataToMerge.adresse_formateur);
          dataToMerge.rue_formateur = _fAddr.rue;
          dataToMerge.code_postal_formateur = _fAddr.codePostal;
          dataToMerge.ville_formateur = _fAddr.ville; }
        targetId = clientRow.id;
        targetName = finalClient.nom_complet || clientRow.nom || "Client";
      }

      // ── Branche : template visuel (balises posées via VisualTemplateEditor) ──
      const isVisualTemplate = templateInfo?.metadata?.has_visual_fields === true;
      if (isVisualTemplate && templateInfo.id) {
        toast.loading('Génération du document visuel…', { id: 'gen-doc' });
        // FIX (2026-09-07) : `targetName` (nom du client/formateur) peut contenir des accents
        // (ex: "Stéphane Subra") — un simple remplacement des espaces ne suffit pas à produire
        // une clé de stockage valide pour Supabase ("Invalid key"), même correctif que safeType
        // plus bas.
        const safeName = targetName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_');

        // 1. Charger les champs visuels depuis Supabase
        const { data: templateFieldsData, error: tfErr } = await supabase
          .from('template_fields')
          .select('*')
          .eq('template_id', templateInfo.id)
          .order('page', { ascending: true });
        if (tfErr) throw new Error('Erreur chargement balises : ' + tfErr.message);

        // 2. Récupérer le fichier template (PDF ou DOCX)
        const templateResp = await fetch(templateInfo.url);
        if (!templateResp.ok) throw new Error('Impossible de récupérer le modèle.');
        const templateBlob = await templateResp.blob();
        const templateIsPdf = /\.pdf$/i.test((templateInfo.url || '').split('?')[0]);

        // 3. Si DOCX → convertir en PDF ; si déjà PDF → utiliser directement
        let basePdfBlob;
        if (templateIsPdf) {
          basePdfBlob = templateBlob;
        } else {
          try {
            toast.loading('Conversion DOCX → PDF…', { id: 'gen-doc' });
            basePdfBlob = await convertDocxBlobToPdf(templateBlob);
          } catch (_) {
            toast.loading('Conversion locale…', { id: 'gen-doc' });
            basePdfBlob = await convertDocxBlobToPdfLocal(templateBlob);
          }
        }

        // 4. Superposer les valeurs aux positions stockées
        // Champs interactifs (signature/case/texte libre) exclus : cet appel ne fournit ni sigMap ni
        // checkedMap ni textInputMap, donc les inclure graverait un placeholder vide en dur dans le
        // PDF généré — pour le client ET pour le formateur — avant même toute signature (même classe
        // de bug que celle corrigée dans handleOpenSigning/handleSignSave le 2026-07-24).
        toast.loading('Superposition des données…', { id: 'gen-doc' });
        const _dataOnlyFields = (templateFieldsData || []).filter(f => {
          const isInteractive = f.field_type === 'signature' || f.field_type === 'checkbox' || f.field_type === 'text_input' || (f.tag || '').startsWith('signature_') || (f.tag || '').startsWith('checkbox_') || (f.tag || '').startsWith('texte_');
          return !isInteractive && !estDateSignature(f); // date de signature : posée au moment de signer
        });
        const filledPdfBlob = await overlayFieldsOnPdf(basePdfBlob, _dataOnlyFields, dataToMerge);

        // Nom de fichier "clé de stockage" : le titre du modèle (`type`) peut contenir des accents,
        // espaces ou parenthèses (ex: "Pack de démarrage BC ST 14h") — utilisés tels quels, Supabase
        // Storage refuse la clé ("Invalid key"), ce qui faisait échouer l'upload/l'archivage du
        // document (le téléchargement local juste au-dessus, lui, fonctionnait quand même — d'où la
        // confusion : le fichier se téléchargeait mais un message d'erreur s'affichait). On assainit
        // uniquement la clé de stockage ; le nom affiché dans l'UI (docToInsert.nom) garde les accents.
        const safeType = type.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_');
        const finalFileName = `${safeType}_${safeName}_${Date.now()}.pdf`;

        // Mode "Consulter" : aperçu volatile pour vérification avant envoi — ouvert dans un
        // nouvel onglet du navigateur (jamais téléchargé sur le disque), jamais archivé ni visible
        // du client/formateur, aucune ligne créée dans "documents" (demande du 2026-09-02, affinée
        // le 2026-09-02 : la 1ère version téléchargeait systématiquement le fichier, ce n'est plus
        // le cas — Consulter n'est qu'une prévisualisation à l'écran).
        if (mode === 'preview') {
          const previewObjectUrl = URL.createObjectURL(filledPdfBlob);
          if (previewWindow && !previewWindow.closed) {
            previewWindow.location.href = previewObjectUrl;
          } else {
            window.open(previewObjectUrl, '_blank');
          }
          setTimeout(() => URL.revokeObjectURL(previewObjectUrl), 5 * 60 * 1000);
          toast.success("Aperçu ouvert dans un nouvel onglet — ce document n'a pas été envoyé.", { id: 'gen-doc' });
          return;
        }

        // Le document n'est plus téléchargé automatiquement sur l'ordinateur de l'Admin lors d'un
        // envoi réel, ni pour le client ni pour le formateur (demande du 2026-09-02) : "Envoyer"
        // envoie uniquement, sans effet de bord local. Pour obtenir une copie locale, utiliser le
        // bouton "Consulter" avant l'envoi.

        // 6. Upload + insert Supabase (même logique que la branche classique)
        toast.loading('Upload du document…', { id: 'gen-doc' });
        const uploadFile = new File([filledPdfBlob], finalFileName, { type: 'application/pdf' });
        const { error: upErr } = await supabase.storage.from(uploadBucket).upload(finalFileName, uploadFile);
        if (upErr) throw upErr;
        const { data: { publicUrl } } = supabase.storage.from(uploadBucket).getPublicUrl(finalFileName);

        // Le document généré hérite de la classification du modèle source (même détection
        // "à signer" que partout ailleurs dans ce fichier) — sans ça, type_document restait
        // toujours "Administratif" et le bouton "Signer" ne s'affichait jamais côté client, quel
        // que soit l'ordre de signature configuré sur le modèle (bug remonté le 2026-09-02).
        const _tplMetaForInsert = templateInfo.metadata || {};
        const _tplFieldsForInsert = Array.isArray(_tplMetaForInsert.template_fields) ? _tplMetaForInsert.template_fields : [];
        // FIX (2026-09-07) : détecte une balise de signature de N'IMPORTE QUEL rôle (client,
        // formateur OU organisme) — avant ce correctif, seules les balises CLIENT étaient
        // regardées, donc un modèle destiné uniquement au formateur et/ou à l'organisme (sans
        // client) ne recevait jamais le statut "À signer", même avec une vraie balise de
        // signature posée dessus.
        const _hasAnySignTag = _tplFieldsForInsert.some(f => [
          'signature_client', 'checkbox_client', 'texte_client',
          'signature_formateur', 'checkbox_formateur', 'texte_formateur',
          'signature_organisme', 'checkbox_organisme', 'texte_organisme',
        ].includes(f.tag));
        const needsSignatureForInsert = _hasAnySignTag || templateInfo.classification === 'a_signer' || (_tplMetaForInsert.requiresClientSignature === true && _tplMetaForInsert.documentType !== 'info') || _tplMetaForInsert.documentType === 'signature';
        // FIX (2026-09-07) : destinataires réels du document — remplace la détection binaire
        // effectiveIsForFormateur (égalité stricte à 'formateur', qui ratait toute destination
        // combinée type "formateur,organisme") par la même lecture que partout ailleurs dans le
        // fichier (parseDestinationRoles). Sans ça : (a) destination_roles n'était jamais écrit
        // dans les métadonnées du document généré → l'onglet admin "Mes documents à signer" ne
        // pouvait jamais le trouver ; (b) le document était systématiquement rattaché au CLIENT
        // (user_id + visible_client forcé) même quand celui-ci n'était pas destinataire → il
        // apparaissait à tort dans "Documents envoyés pour signature" du client, avec des badges
        // "en attente de signature client/formateur" non pertinents.
        const _destRolesForInsert = parseDestinationRoles(templateDestination);
        const _visClientForInsert = _destRolesForInsert.includes('client');
        const _visFormateurForInsert = _destRolesForInsert.includes('formateur');
        const _requiresOrganismeForInsert = _destRolesForInsert.includes('organisme');
        const docToInsert = {
          nom: `${type} - ${targetName}`,
          // FIX (2026-10-05) : un document sans signature envoyé au client devient "Téléchargeable" (et
          // plus "Administratif", type réservé au dossier interne organisme/formateur).
          type_document: needsSignatureForInsert ? 'À signer' : (_visClientForInsert ? 'Téléchargeable' : 'Administratif'),
          url: publicUrl,
          ...(_visClientForInsert ? { signe_par_client: false } : {}),
          ...(_visFormateurForInsert ? { signe_par_formateur: false } : {}),
          ...(_requiresOrganismeForInsert ? { signe_par_organisme: false } : {}),
          visible_admin: true,
          template_id: templateInfo.id || null, // ← lien vers le template pour incrustation signature
          metadata: { ..._tplMetaForInsert, ...(extraMeta || {}), destination_roles: _destRolesForInsert, signature_requise: !!needsSignatureForInsert },
          // requiresTrainerSignature retiré (2026-09-03) : ce n'est pas une colonne de la table
          // `documents`, seulement un champ du JSONB metadata des templates — l'insérer en top-level
          // faisait échouer TOUT `handleGenerateDocx` avec "Could not find the 'requiresTrainerSignature'
          // column of 'documents' in the schema cache". L'info reste disponible via `metadata` ci-dessus.
          // organisation_id manquant ici auparavant → violait la policy RLS "documents_insert_..."
          // (elle exige organisation_id::text = app_current_org_id()::text pour un staff admin/formateur).
          organisation_id: ((effectiveIsForFormateur || formateurId) && !clientRow) ? (currentOrgId || null) : (finalClient?.organisation_id || currentOrgId || null),
        };
        if (_visClientForInsert) {
          // Le client EST destinataire → document rattaché à son dossier (comportement inchangé),
          // qu'il y ait ou non aussi un formateur/organisme destinataire.
          docToInsert.user_id = targetId;
          docToInsert.visible_client = true;
          docToInsert.visible_formateur = _visFormateurForInsert;
          if (finalClient?.formateur_id) docToInsert.assigned_formateur_id = finalClient.formateur_id;
        } else if ((effectiveIsForFormateur || formateurId) && !clientRow) {
          // FIX (2026-09-07) : `targetId` ne vaut l'ID du formateur QUE dans le cas "envoi direct
          // depuis la fiche Formateur" (pas de clientRow — voir le garde-fou !clientRow ajouté plus
          // haut dans cette fonction). Dès qu'un clientRow est présent, targetId vaut l'ID DU CLIENT
          // (uuid) et ne doit jamais atterrir dans documents.assigned_formateur_id (bigint) — cela
          // provoquait "invalid input syntax for type bigint" pour tout document destination=
          // formateur envoyé depuis une fiche client (bug remonté le 2026-09-07). Ce cas retombe
          // maintenant sur la branche finale ci-dessous, qui utilise finalClient.formateur_id (bigint).
          docToInsert.assigned_formateur_id = targetId;
          docToInsert.visible_formateur = true;
          docToInsert.visible_client = false;
        } else {
          // Le client n'est PAS destinataire (ex: "formateur,organisme" uniquement) même si ce
          // document a été généré depuis SA fiche. `visible_client` reste false (le client ne le
          // voit jamais sur son propre portail — tous les points de lecture côté client filtrent
          // sur visible_client, jamais sur user_id seul), MAIS on pose quand même user_id = targetId
          // (le client, quand un clientRow existe) pour que ce document reste traçable depuis la
          // fiche de CE client (section "Documents envoyés pour signature" de ClientDetailView,
          // qui filtre justement sur user_id) — corrigé le 2026-09-07 : un précédent correctif
          // avait au contraire arrêté de poser user_id ici pour éviter un badge "en attente de
          // signature client" non pertinent, mais ce badge est déjà neutralisé par needsClientSign
          // (dérivé de destination_roles) depuis un correctif encore antérieur.
          if (clientRow) docToInsert.user_id = targetId;
          docToInsert.visible_client = false;
          docToInsert.visible_formateur = _visFormateurForInsert;
          if (finalClient?.formateur_id) docToInsert.assigned_formateur_id = finalClient.formateur_id;
        }
        const { error: insertErr } = await supabase.from('documents').insert([docToInsert]);
        if (insertErr) throw new Error('Erreur Supabase : ' + insertErr.message);
        await fetchDocuments();
        toast.success(`Document "${type}" généré.`, { id: 'gen-doc' });
        if (!(extraMeta && extraMeta.moment === 'fin')) notifyDocumentRecipients(docToInsert, type); // lot 3
        return;
      }
      // ── Fin branche visuelle ──

      const response = await fetch(templateInfo.url);
      if (!response.ok) throw new Error(`Fetch template error: ${response.statusText}`);
      const arrayBuffer = await response.arrayBuffer();
      // FIX (2026-10-02) : "Can't find end of central directory : is this a zip file ?" — un modèle PDF
      // déposé SANS balises (ex. supports stagiaires) passait ici dans le moteur Word (.docx), qui ne sait
      // lire que des fichiers Word. On reconnaît maintenant le format au contenu du fichier : un .docx
      // (archive "PK") est rempli comme avant ; tout autre fichier (PDF, image...) est envoyé TEL QUEL.
      const _head = new Uint8Array(arrayBuffer.slice(0, 4));
      const _isZip = _head[0] === 0x50 && _head[1] === 0x4B; // "PK" = archive zip (.docx, mais aussi .xlsx, .pptx...)
      // FIX (2026-10-02) : un tableur Excel (.xlsx) est AUSSI une archive "PK" — il était pris pour un Word et
      // plantait ("Filetype xlsx is supported only with the paid XlsxModule"). Un vrai .docx contient
      // obligatoirement word/document.xml : seul ce cas passe par le remplissage Word, le reste part tel quel.
      let _isDocxFile = false;
      if (_isZip) {
        try { _isDocxFile = !!new PizZip(arrayBuffer).file('word/document.xml'); } catch (_) { _isDocxFile = false; }
      }
      const _isPdfFile = _head[0] === 0x25 && _head[1] === 0x50 && _head[2] === 0x44 && _head[3] === 0x46; // "%PDF"
      const safeName = targetName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_');
      let finalBlob;
      let finalExt;
      let finalMime;

      if (!_isDocxFile) {
        const _urlExt = ((templateInfo.url || '').split('?')[0].split('.').pop() || '').toLowerCase();
        finalExt = _isPdfFile ? 'pdf' : (/^[a-z0-9]{2,5}$/.test(_urlExt) ? _urlExt : 'bin');
        const _knownMimes = {
          xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          xls: 'application/vnd.ms-excel',
          pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          csv: 'text/csv', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', zip: 'application/zip',
        };
        finalMime = _isPdfFile ? 'application/pdf' : (_knownMimes[finalExt] || response.headers.get('content-type') || 'application/octet-stream');
        finalBlob = new Blob([arrayBuffer], { type: finalMime });
      } else {
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

      doc.render(dataToMerge);

      const docxBlob = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      // Étape 1 : Conversion DOCX → PDF (via ConvertAPI si disponible, sinon DOCX direct)
      toast.loading('Génération du document…', { id: 'gen-doc' });
      // FIX (2026-09-07), même correctif que la branche visuelle ci-dessus.

      finalBlob = docxBlob;
      finalExt = 'docx';
      finalMime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      // Tentative 1 : ConvertAPI (pixel-perfect si crédits disponibles)
      try {
        toast.loading('Conversion en PDF…', { id: 'gen-doc' });
        const pdfBlob = await convertDocxBlobToPdf(docxBlob);
        finalBlob = pdfBlob;
        finalExt = 'pdf';
        finalMime = 'application/pdf';
      } catch (_apiErr) {
        // Tentative 2 : conversion locale (docx-preview + html2canvas + jsPDF)
        // → le document est stocké en PDF dès la génération, la signature sera propre
        try {
          toast.loading('Conversion locale en cours (peut prendre 10-15s)…', { id: 'gen-doc' });
          const pdfBlob = await convertDocxBlobToPdfLocal(docxBlob);
          finalBlob = pdfBlob;
          finalExt = 'pdf';
          finalMime = 'application/pdf';
        } catch (_localErr) {
          console.warn('[handleGenerateDocx] Toutes les conversions ont échoué, stockage DOCX :', _localErr.message);
          // Dernier recours : stocker en DOCX (signature moins propre mais document disponible)
        }
      }

      }

      // Même correctif que la branche visuelle ci-dessus : clé de stockage assainie (accents/espaces
      // interdits par Supabase Storage), nom affiché inchangé.
      const safeType = type.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_');
      const finalFileName = `${safeType}_${safeName}_${Date.now()}.${finalExt}`;

      // Mode "Consulter" : même court-circuit que la branche visuelle ci-dessus (aperçu ouvert
      // dans un nouvel onglet, jamais téléchargé automatiquement).
      if (mode === 'preview') {
        const previewObjectUrl = URL.createObjectURL(finalBlob);
        if (previewWindow && !previewWindow.closed) {
          previewWindow.location.href = previewObjectUrl;
        } else {
          window.open(previewObjectUrl, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(previewObjectUrl), 5 * 60 * 1000);
        toast.success("Aperçu ouvert dans un nouvel onglet — ce document n'a pas été envoyé.", { id: 'gen-doc' });
        return;
      }

      // Le document n'est plus téléchargé automatiquement sur l'ordinateur de l'Admin lors d'un
      // envoi réel, ni pour la lettre de mission ni pour les autres documents, ni pour le client ni
      // pour le formateur (demande du 2026-09-02) : "Envoyer" envoie uniquement, sans effet de bord
      // local. Pour obtenir une copie locale, utiliser le bouton "Consulter" avant l'envoi.

      // Upload du fichier généré
      toast.loading('Upload du document…', { id: 'gen-doc' });
      const uploadFile = new File([finalBlob], finalFileName, { type: finalMime });
      const { error: uploadError } = await supabase.storage.from(uploadBucket).upload(finalFileName, uploadFile);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from(uploadBucket).getPublicUrl(finalFileName);

      // Même correctif que la branche visuelle ci-dessus (voir commentaire détaillé là-bas, 2026-09-07).
      const _tplMetaForInsert = templateInfo.metadata || {};
      const _tplFieldsForInsert = Array.isArray(_tplMetaForInsert.template_fields) ? _tplMetaForInsert.template_fields : [];
      // FIX (2026-09-07), même correctif que la branche visuelle ci-dessus.
      const _hasAnySignTag = _tplFieldsForInsert.some(f => [
        'signature_client', 'checkbox_client', 'texte_client',
        'signature_formateur', 'checkbox_formateur', 'texte_formateur',
        'signature_organisme', 'checkbox_organisme', 'texte_organisme',
      ].includes(f.tag));
      const needsSignatureForInsert = _hasAnySignTag || templateInfo.classification === 'a_signer' || (_tplMetaForInsert.requiresClientSignature === true && _tplMetaForInsert.documentType !== 'info') || _tplMetaForInsert.documentType === 'signature';
      const _destRolesForInsert = parseDestinationRoles(templateDestination);
      const _visClientForInsert = _destRolesForInsert.includes('client');
      const _visFormateurForInsert = _destRolesForInsert.includes('formateur');
      const _requiresOrganismeForInsert = _destRolesForInsert.includes('organisme');
      const docToInsert = {
        nom: `${type} - ${targetName}`,
        // FIX (2026-10-05) : voir la branche visuelle ci-dessus.
        type_document: needsSignatureForInsert ? 'À signer' : (_visClientForInsert ? 'Téléchargeable' : 'Administratif'),
        url: publicUrl,
        ...(_visClientForInsert ? { signe_par_client: false } : {}),
        ...(_visFormateurForInsert ? { signe_par_formateur: false } : {}),
        ...(_requiresOrganismeForInsert ? { signe_par_organisme: false } : {}),
        visible_admin: true,
        metadata: { ..._tplMetaForInsert, ...(extraMeta || {}), destination_roles: _destRolesForInsert, signature_requise: !!needsSignatureForInsert },
        // requiresTrainerSignature retiré (2026-09-03), voir commentaire équivalent ci-dessus (branche visuelle).
        // organisation_id manquant ici auparavant → violait la policy RLS "documents_insert_..."
        // (elle exige organisation_id::text = app_current_org_id()::text pour un staff admin/formateur).
        organisation_id: ((effectiveIsForFormateur || formateurId) && !clientRow) ? (currentOrgId || null) : (finalClient?.organisation_id || currentOrgId || null),
      };

      if (_visClientForInsert) {
        docToInsert.user_id = targetId;
        docToInsert.visible_client = true;
        docToInsert.visible_formateur = _visFormateurForInsert;
        if (finalClient && finalClient.formateur_id) {
          docToInsert.assigned_formateur_id = finalClient.formateur_id;
        }
      } else if ((effectiveIsForFormateur || formateurId) && !clientRow) {
        // FIX (2026-09-07), même correctif que la branche visuelle ci-dessus.
        docToInsert.assigned_formateur_id = targetId;
        docToInsert.visible_formateur = true;
        docToInsert.visible_client = false;
      } else {
        // FIX (2026-09-07), même correctif que la branche visuelle ci-dessus : traçabilité côté
        // fiche client, sans jamais impacter la visibilité réelle côté portail client (gouvernée
        // exclusivement par visible_client, laissé à false ici).
        if (clientRow) docToInsert.user_id = targetId;
        docToInsert.visible_client = false;
        docToInsert.visible_formateur = _visFormateurForInsert;
        if (finalClient && finalClient.formateur_id) {
          docToInsert.assigned_formateur_id = finalClient.formateur_id;
        }
      }

      console.log('Données envoyées à Supabase :', JSON.stringify(docToInsert, null, 2));

      const { data: insertedDocs, error: insertError } = await supabase.from('documents').insert([docToInsert]).select();
      if (insertError) {
        console.error('Erreur insert Supabase :', insertError);
        throw new Error(`Erreur Supabase : ${insertError.message || insertError.details || JSON.stringify(insertError)}`);
      }

      await fetchDocuments();
      if (isAutoGenerate) {
        toast.dismiss('gen-doc');
      } else {
        toast.success(`Document généré et archivé.`, { id: 'gen-doc' });
      }
      if (!(extraMeta && extraMeta.moment === 'fin')) notifyDocumentRecipients(docToInsert, type); // lot 3
      return insertedDocs ? insertedDocs[0] : null;
    } catch (error) {
      console.error("Docx Error:", error);
      if (isAutoGenerate) {
        toast.dismiss('gen-doc');
      } else {
        toast.error("Erreur lors de la génération : " + error.message, { id: 'gen-doc' });
      }
      return null;
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!newDocName.trim() || !newDocClientId) return;
    setIsAddingDoc(true);

    let finalUrl = newDocUrl;
    if (newDocFile) {
      const fileExt = newDocFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, newDocFile);

      if (uploadError) {
        console.error("Erreur upload:", uploadError);
        toast.error("Erreur upload: " + uploadError.message);
        setIsAddingDoc(false);
        return;
      }
      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(fileName);
      finalUrl = publicUrl;
    }

    const { error } = await supabase
      .from('documents')
      .insert([{
        nom: newDocName,
        type_document: newDocType,
        url: finalUrl,
        user_id: newDocClientId,
        visible_client: newDocVisClient,
        visible_formateur: newDocVisFormateur,
        signe_par_client: false,
        signe_par_formateur: false
      }]);

    if (!error) {
      // Rechargement immédiat pour récupérer le document dans l'UI
      await fetchDocuments();
      setNewDocName('');
      setNewDocType('Autre');
      setNewDocUrl('');
      setNewDocFile(null);
      setNewDocClientId('');
      setNewDocVisClient(true);
      setNewDocVisFormateur(true);
    } else {
      console.error("Erreur ajout doc", error);
      toast.error('Erreur Doc : ' + error.message);
    }
    setIsAddingDoc(false);
  };

  const handleSignDocument = async (docId, signerType, signatureDataUrl = null, checkedIds = null, textValues = null) => {
    // SIGNATURES (2026-10-20) : renvoie true seulement si la signature est réellement enregistrée.
    const doc = documents.find(d => String(d.id) === String(docId));
    if (!doc) { toast.error('Document introuvable : actualisez la page puis réessayez.'); return false; }
    let _pdfAttendu = false, _pdfOk = false;
    // Valeurs tapées dans les balises "texte libre" appartenant à ce signataire (clé = fieldKey(field))
    const _textValueMap = {};
    if (textValues) {
      (textValues instanceof Map ? Array.from(textValues.entries()) : Object.entries(textValues)).forEach(([k, v]) => { if (v) _textValueMap[k] = v; });
    }

    // signerType = 'client' | 'formateur' | 'organisme' — colonnes résolues via les tables de
    // correspondance module-level (SIGNED_FLAG_COLUMN etc.) plutôt qu'un ternaire binaire, pour
    // accueillir le rôle "organisme" sans dupliquer cette logique une 3e fois.
    const updateColumn = {
      [SIGNED_FLAG_COLUMN[signerType]]: true,
      [SIGNATURE_DATE_COLUMN[signerType]]: new Date().toISOString(),
    };

    if (signatureDataUrl) {
      updateColumn[SIGNATURE_IMAGE_COLUMN[signerType]] = signatureDataUrl;
    }

    const simulatedDoc = { ...doc, ...updateColumn };

    // LOT B (2026-10-21) : plus de compteur écrit dans la table des utilisateurs (mauvaise table) ; la
    // progression est calculée à partir des séances (progressionParcours).

    let signedPdfUpdate = {};

    // ── Incrustation de la signature ET/OU des cases à cocher ET/OU du texte libre à la position
    // visuelle — NE PAS gater ce bloc sur la seule présence d'une signature : depuis l'ajout des
    // balises texte libre, un signataire peut valider un document SANS dessiner de signature (aucune
    // balise signature posée pour lui) ; si on se limitait à "if (signatureDataUrl)", son texte tapé
    // n'était alors jamais gravé du tout (bug constaté 2026-07-24 : "pas le texte du formateur").
    const _hasCheckedIds = checkedIds && (typeof checkedIds.size === 'number' ? checkedIds.size > 0 : Object.keys(checkedIds).length > 0);
    if (signatureDataUrl || Object.keys(_textValueMap).length > 0 || _hasCheckedIds) {
      try {
        const sigTag = `signature_${signerType}`;
        const checkboxTag = `checkbox_${signerType}`;
        const textTag = `texte_${signerType}`;
        const dateTag = tagDateSignature(signerType); // décision du 20/10/2026
        let sigFields = null;

        // FIX (2026-09-03, round 2) : priorité désormais metadata AVANT la table technique DB —
        // voir commentaire détaillé plus haut dans le fichier. Les 3 sources ci-dessous (signature_fields,
        // template_fields, fields) sont EXACTEMENT celles utilisées par DocumentViewerModal côté formateur
        // pour construire requiredTextFields/textValues : garder le même ordre garantit que fieldKey(f)
        // calcule les MÊMES clés ici et là-bas, condition indispensable pour que le texte tapé par le
        // formateur (indexé par ces clés) soit effectivement retrouvé et gravé dans le PDF.
        const docMeta = (() => { try { return typeof doc.metadata === 'string' ? JSON.parse(doc.metadata) : (doc.metadata || {}); } catch { return {}; } })();
        const _candidateFields = (Array.isArray(docMeta.signature_fields) && docMeta.signature_fields.length > 0) ? docMeta.signature_fields
          : (Array.isArray(docMeta.template_fields) && docMeta.template_fields.length > 0) ? docMeta.template_fields
          : (Array.isArray(docMeta.fields) ? docMeta.fields : []);
        {
          const match = _candidateFields.filter(f => f.tag === sigTag || f.tag === checkboxTag || f.tag === textTag || f.tag === dateTag);
          if (match.length > 0) sigFields = match;
        }

        // Dernier recours : ancien flow instantiateDocument, qui ne portait ses balises que dans la
        // table technique `template_fields` (pas de copie embarquée en metadata). On ne l'utilise QUE
        // si aucune des 3 sources metadata ci-dessus n'a rien donné, pour ne jamais mélanger deux jeux
        // de clés incompatibles au sein d'un même sigFields.
        if (!sigFields && doc.template_id) {
          const { data: dbFields } = await supabase
            .from('template_fields').select('*')
            .eq('template_id', doc.template_id).in('tag', [sigTag, checkboxTag, textTag, dateTag]);
          if (dbFields && dbFields.length > 0) sigFields = dbFields;
        }

        console.debug('[handleSignDocument]', signerType, 'sigFields:', sigFields ? sigFields.length : 0,
          'textValues keys:', Object.keys(_textValueMap));

        if (sigFields && sigFields.length > 0) {
          // Revérifier la dernière version du PDF avant de construire par-dessus : l'AUTRE partie a pu
          // signer entre-temps (même correctif que côté client dans handleSignSave, 2026-07-24).
          //
          // IMPORTANT (bug racine identifié le 2026-07-24, "on ne voit plus que le texte formateur") :
          // 'url' est la colonne mise à jour par TOUS les chemins d'écriture (signature client, régénération
          // admin, upload manuel...), alors que 'signed_pdf_url' n'est écrite QUE par cette fonction et par
          // l'ancien flux "sous-traitant" (handleDocumentSignatureSave). Si on préfère 'signed_pdf_url' dès
          // qu'elle existe, un ancien snapshot y reste figé indéfiniment et masque toute mise à jour plus
          // récente de 'url' (ex : signature du client faite APRÈS un premier essai formateur) → son contenu
          // disparaît silencieusement au tour de signature suivant. 'url' doit donc rester la source de
          // vérité pour construire par-dessus ; 'signed_pdf_url' n'est écrite qu'en miroir, pour l'affichage.
          let pdfUrl = doc.url || doc.signed_pdf_url;
          try {
            const { data: freshDoc } = await supabase.from('documents').select('url, signed_pdf_url').eq('id', docId).maybeSingle();
            if (freshDoc) pdfUrl = freshDoc.url || freshDoc.signed_pdf_url || pdfUrl;
          } catch (e) {
            console.warn('[handleSignDocument] Impossible de récupérer la dernière version du PDF, utilisation de la version locale :', e.message);
          }
          if (pdfUrl && /\.pdf$/i.test(pdfUrl.split('?')[0])) {
            _pdfAttendu = true;
            const pdfResp = await fetch(pdfUrl);
            if (pdfResp.ok) {
              const pdfBlob = await pdfResp.blob();
              const signaturesMap = { [sigTag]: signatureDataUrl };
              // checkedIds : Set (ou objet) des id de champs case-à-cocher cochés par ce signataire
              const checkedMap = {};
              if (checkedIds) {
                sigFields.filter(f => f.tag === checkboxTag).forEach(f => {
                  const k = fieldKey(f);
                  checkedMap[k] = typeof checkedIds.has === 'function' ? checkedIds.has(k) : !!checkedIds[k];
                });
              }
              const textInputMap = {};
              sigFields.filter(f => f.tag === textTag).forEach(f => {
                const k = fieldKey(f);
                if (_textValueMap[k]) textInputMap[k] = _textValueMap[k];
              });
              console.debug('[handleSignDocument]', signerType, 'textInputMap filled:', Object.keys(textInputMap).length, '/', sigFields.filter(f => f.tag === textTag).length);
              const updatedPdfBlob = await overlayFieldsOnPdf(pdfBlob, sigFields, { [dateTag]: dateDuJourFr() }, signaturesMap, checkedMap, textInputMap);
              const fileName = `signed_${docId}_${signerType}_${Date.now()}.pdf`;
              const { error: upErr } = await supabase.storage.from('documents')
                .upload(fileName, new File([updatedPdfBlob], fileName, { type: 'application/pdf' }));
              if (!upErr) {
                const { data: { publicUrl: newPdfUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);
                signedPdfUpdate = { signed_pdf_url: newPdfUrl, url: newPdfUrl };
                _pdfOk = true;
              }
            }
          }
        }
      } catch (sigErr) {
        console.warn('[handleSignDocument] Incrustation signature échouée :', sigErr.message);
      }
    }
    // SIGNATURES (2026-10-20) : la signature devait être posée sur le PDF et ça a échoué → on n'écrit
    // RIEN (avant : « Signé » enregistré sans signature sur le document).
    if (_pdfAttendu && !_pdfOk) {
      toast.error("La signature n'a pas pu être ajoutée au document. Rien n'a été modifié : réessayez dans un instant.", { duration: 8000 });
      return false;
    }

    // FIX (2026-09-04) : le statut ne passe "Signé" que si TOUS les rôles requis ont signé (pas
    // seulement CE signataire) — voir getRequiredSignerRoles/isDocFullySigned.
    const _requiredRolesForDoc = await getRequiredSignerRoles(doc, supabase);
    const _mergedDocState = { ...doc, ...updateColumn };
    const _finalStatut = isDocFullySigned(_mergedDocState, _requiredRolesForDoc) ? 'Signé' : doc.statut;

    const { error } = await supabase
      .from('documents')
      .update({
        ...updateColumn,
        ...signedPdfUpdate,
        statut: _finalStatut,
        visible_admin: true,
      })
      .eq('id', docId);

    if (error) {
      toast.error("Erreur signature: " + error.message);
      await fetchDocuments();
      return false;
    } else {
      setDocuments(prev => prev.map(d => String(d.id) === String(docId) ? { ...d, ...simulatedDoc, ...signedPdfUpdate, statut: _finalStatut } : d));
      // FIX (nouvelle fonctionnalité, 2026-09-11) : document séquentiel — cette signature vient
      // peut-être de débloquer le TOUR d'un autre rôle requis (ex : ordre formateur → client, le
      // formateur vient de signer → c'est maintenant au client, qui doit être notifié MAINTENANT,
      // pas à l'envoi initial du document où il était encore bloqué). En mode simultané (par
      // défaut), tout le monde de requis a déjà été notifié dès l'envoi initial (voir
      // notifyDocumentRecipients dans handleGenerateDocx) : rien à refaire ici.
      const docMetaForNotif = parseDocMetadata(doc);
      if (docMetaForNotif.signing_mode === 'sequentiel') {
        const docLabel = doc.nom || 'un document';
        for (const otherRole of _requiredRolesForDoc) {
          if (otherRole === signerType) continue; // celui qui vient de signer, pas concerné
          const flagCol = SIGNED_FLAG_COLUMN[otherRole];
          if (_mergedDocState[flagCol]) continue; // a déjà signé plus tôt, déjà notifié en son temps
          if (isBlockedBySigningOrder(_mergedDocState, otherRole)) continue; // toujours pas son tour
          // Vient de passer de "bloqué" à "débloqué" grâce à cette signature : c'est son tour.
          if (otherRole === 'client' && _mergedDocState.user_id) {
            notifyClientNewDocument(_mergedDocState.user_id, docLabel);
          } else if (otherRole === 'formateur' && _mergedDocState.assigned_formateur_id) {
            notifyFormateurNewDocument(_mergedDocState.assigned_formateur_id, docLabel);
          }
        }
      }
    }
    return true;
  };

  const handleDocumentSignatureSave = async (docId, signatureDataUrl = null, documentChoice = null) => { /* 2026-10-21 : chargé à la demande */ const { PDFDocument } = await import('pdf-lib');
    const doc = documents.find(d => String(d.id) === String(docId));
    if (!doc) {
      toast.error('Document introuvable (id: ' + docId + ')');
      return;
    }

    const TOAST_ID = 'sign-doc';
    toast.loading('Signature du document en cours…', { id: TOAST_ID });

    try {
      const formateur = formateurs.find(f => f.id === doc.assigned_formateur_id);
      const signerName = formateur?.nom || 'Le Formateur';
      const now = new Date();
      const nowStr = now.toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });

      const docUrl = doc.url || '';
      const isDocx = /\.(docx|doc)$/i.test(docUrl);

      let pdfBlob;
      let targetPdfBytes;

      // Extract bucket and path
      const urlMatch = docUrl.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+?)(?:\?|$)/);
      let bucket = 'documents';
      let path = docUrl;
      if (urlMatch) {
        bucket = urlMatch[1];
        path = decodeURIComponent(urlMatch[2]);
      }

      // Résolution de l'URL de téléchargement : URL publique directe ou signed URL
      let fetchUrl = docUrl;
      if (!docUrl.startsWith('http')) {
        // Chemin relatif → URL publique
        fetchUrl = supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
      }
      // Si l'URL publique échoue (bucket privé), on tente une signed URL
      const testResp = await fetch(fetchUrl, { method: 'HEAD' }).catch(() => null);
      if (!testResp || !testResp.ok) {
        const { data: signData, error: signErr } = await supabase.storage.from(bucket).createSignedUrl(path, 300);
        if (signErr || !signData?.signedUrl) throw new Error('Lecture document impossible : ' + (signErr?.message || 'URL inaccessible'));
        fetchUrl = signData.signedUrl;
      }

      let signatureAlreadyEmbedded = false;

      if (isDocx) {
        // Conversion DOCX → PDF : ConvertAPI en priorité, sinon conversion locale (docx-preview + html2canvas + jsPDF)
        toast.loading('Conversion du document DOCX en PDF…', { id: TOAST_ID });
        const docxBytes = await fetch(fetchUrl).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); });
        const docxBlob = new Blob([docxBytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });

        let pdfBlobFromDocx = null;

        // Tentative 1 : ConvertAPI (si crédits disponibles)
        try {
          pdfBlobFromDocx = await convertDocxBlobToPdf(docxBlob);
        } catch (_apiErr) {
          console.log('[signing] ConvertAPI indisponible, conversion locale en cours…', _apiErr.message);
        }

        // Tentative 2 : conversion locale (docx-preview + html2canvas + jsPDF)
        if (!pdfBlobFromDocx) {
          try {
            toast.loading('Rendu du document en cours (conversion locale)…', { id: TOAST_ID });
            pdfBlobFromDocx = await convertDocxBlobToPdfLocal(docxBlob);
          } catch (_localErr) {
            console.error('[signing] Conversion locale échouée:', _localErr);
          }
        }

        if (!pdfBlobFromDocx) {
          throw new Error('Impossible de convertir le document DOCX en PDF. Veuillez réessayer ou contacter l\'administrateur.');
        }

        targetPdfBytes = await pdfBlobFromDocx.arrayBuffer();
      } else {
        targetPdfBytes = await fetch(fetchUrl).then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.arrayBuffer();
        });
      }

      if (!signatureAlreadyEmbedded) {
        // Étape 2 : On superpose la signature par coordonnées (pdf-lib)
        const pdfDoc = await PDFDocument.load(targetPdfBytes, { ignoreEncryption: true });
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        const pages = pdfDoc.getPages();
        const lastPage = pages[pages.length - 1];
        const { width } = lastPage.getSize();

        // Overlay consentement interactif — même config que overlaySignatureOnPdf
        const CONSENT_OVERLAYS = {
          default: { pageIndex: 0, checkboxX: 67, autoriseY: 318, refuseY: 291 },
        };
        if (documentChoice) {
          const overlay = CONSENT_OVERLAYS[doc.consent_template] || CONSENT_OVERLAYS.default;
          const consentPage = pages[overlay.pageIndex];
          const checkY = documentChoice === 'autorise' ? overlay.autoriseY : overlay.refuseY;
          consentPage.drawText('X', { x: overlay.checkboxX, y: checkY, size: 12, font: helveticaBold, color: rgb(0, 0, 0) });
        }

        // Coordonnées pour la zone "Le sous-traitant" (droite de la page)
        const xPos = width / 2 + 10;
        let yOffset = 112;

        // Superposer l'image de signature manuscrite (PNG transparent) si fournie
        if (signatureDataUrl) {
          try {
            const sigBytes = await fetch(signatureDataUrl).then(r => r.arrayBuffer());
            const sigImg = await pdfDoc.embedPng(sigBytes);
            const aspect = sigImg.width / sigImg.height;
            const sigW = Math.min(150, sigImg.width);
            const sigH = sigW / aspect;
            lastPage.drawImage(sigImg, { x: xPos, y: yOffset, width: sigW, height: sigH });
          } catch (imgErr) {
            console.warn("Erreur lors de l'intégration de l'image de signature:", imgErr);
          }
        }

        // Texte d'horodatage en dessous de l'image
        lastPage.drawText('Le sous-traitant', {
          x: xPos, y: 100, size: 10, font: helveticaBold, color: rgb(0, 0, 0),
        });
        lastPage.drawText(signerName, {
          x: xPos, y: 84, size: 10, font: helvetica, color: rgb(0, 0, 0),
        });
        lastPage.drawText(`Signé numériquement le ${nowStr}`, {
          x: xPos, y: 68, size: 8, font: helvetica, color: rgb(0.33, 0.33, 0.33),
        });

        const pdfBytesOut = await pdfDoc.save();
        pdfBlob = new Blob([pdfBytesOut], { type: 'application/pdf' });
      }

      // Upload du PDF signé
      toast.loading('Upload du document signé…', { id: TOAST_ID });
      const fileName = `signed_${docId}_${Date.now()}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, new File([pdfBlob], fileName, { type: 'application/pdf' }));
      if (uploadError) throw new Error('Upload : ' + uploadError.message);

      const { data: { publicUrl: signedPdfUrl } } = supabase.storage.from('documents').getPublicUrl(fileName);

      // FIX (2026-09-04) : le statut ne passe "Signé" que si TOUS les rôles requis ont signé.
      const _requiredRolesLegacy = await getRequiredSignerRoles(doc, supabase);
      const _statutAfterFormateurSign = isDocFullySigned({ ...doc, signe_par_formateur: true }, _requiredRolesLegacy)
        ? 'Signé' : (doc.statut || 'En attente de signature');
      const updateData = {
        signe_par_formateur: true,
        date_signature_formateur: now.toISOString(),
        statut: _statutAfterFormateurSign,
        visible_admin: true,
        signed_pdf_url: signedPdfUrl,
        ...(documentChoice ? { document_choice: documentChoice } : {}),
      };
      const { error: dbError } = await supabase.from('documents').update(updateData).eq('id', docId);
      if (dbError) throw dbError;

      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, ...updateData } : d));
      toast.success('Document signé avec succès !', { id: TOAST_ID });

    } catch (err) {
      console.error('[handleDocumentSignatureSave]', err);
      toast.error('Erreur : ' + err.message, { id: TOAST_ID });
      await fetchDocuments();
    }
  };

  const updateDateSeance = async (docId, date) => {

    setDocuments(documents.map(d => d.id === docId ? { ...d, date_seance: date } : d));
    const { error } = await supabase.from('documents').update({ date_seance: date }).eq('id', docId);
    if (error) {
      console.error("Erreur update date seance", error);
      toast.error("Erreur lors de l'enregistrement de la date.");
      await fetchDocuments();
    }
  };

  const handleSignatureSave = async (dataUrl, checkedIds = null, textValues = null) => {
    if (!signingDocId) return;
    // 'organisme' = n'importe quel admin de l'organisation (2026-07-27) — un admin qui signe le
    // fait toujours au titre de l'organisme, jamais au titre du formateur.
    const signerType = userRole === 'client' ? 'client' : userRole === 'admin' ? 'organisme' : 'formateur';
    // FIX (2026-09-07) : textValues (balises texte_<rôle> saisies dans SignatureModal) n'était
    // jusqu'ici jamais transmis à handleSignDocument, qui pourtant sait déjà les graver dans le
    // PDF (paramètre présent depuis longtemps mais jamais alimenté par ce flux de signature).
    await handleSignDocument(signingDocId, signerType, dataUrl, checkedIds, textValues);
    setSigningDocId(null);
  };

  const handleDownloadPDF = async (doc, recapType = 'general') => { /* 2026-10-21 : chargé à la demande */ const html2canvas = (await import('html2canvas')).default; const { jsPDF } = await import('jspdf'); const { PDFDocument } = await import('pdf-lib');
    if (doc && doc.id) {
      // Détection : Si doc n'a pas d'URL mais a un rôle ou email, c'est un client -> Générer Récapitulatif
      const isClientRecap = (doc.role || doc.email) && !doc.url && !doc.url_signed_pdf;

      if (isClientRecap) {
        try {
          const isEmargementOnly = recapType === 'emargement';
          toast.loading(isEmargementOnly ? "Génération du récap émargements..." : "Génération du récapitulatif...", { id: 'recap' });
          // AJOUT (2026-09-19) : exclut les exercices synthétiques de début/fin (numero_seance: null) du
          // récapitulatif PDF — ce ne sont pas des séances planifiées.
          const clientSessions = sessions.filter(s => s.client_id === doc.id && s.numero_seance !== null && s.numero_seance !== undefined).sort((a, b) => {
            if (a.numero_seance !== b.numero_seance) return a.numero_seance - b.numero_seance;
            return new Date(a.created_at) - new Date(b.created_at);
          });

          // Group sessions by numero_seance
          const _signatures = await chargerSignaturesSeances(doc.id); // 2026-10-21 : images lues à la demande
          const grouped = clientSessions.map(s => ({ ...s, ...(_signatures.get(String(s.id)) || {}) })).reduce((acc, s) => {
            if (!acc[s.numero_seance]) acc[s.numero_seance] = { numero: s.numero_seance, nom: s.nom, date: s.date, debut: s.heure_debut, fin: s.heure_fin, items: [] };
            acc[s.numero_seance].items.push(s);
            fillGroupSchedule(acc[s.numero_seance], s);
            return acc;
          }, {});

          const recapEl = document.createElement('div');
          recapEl.style.width = '800px';
          recapEl.style.padding = '50px';
          recapEl.style.background = '#fff';
          recapEl.style.fontFamily = 'Arial, sans-serif';
          recapEl.style.position = 'fixed';
          recapEl.style.left = '-9999px';

          let sessionsContent = Object.values(grouped).sort((a, b) => a.numero - b.numero).map(g => {
            const filteredItems = isEmargementOnly ? g.items.filter(i => i.type_activite === 'signature') : g.items;
            if (filteredItems.length === 0) return '';
            const itemsHtml = filteredItems.map(item => {
              const sigClient = safeImgSrc(item.signature_image || item.signature_client) || null;
              const sigCoach = safeImgSrc(item.signature_formateur) || null;
              const dateSigClient = item.date_signature_client ? new Date(item.date_signature_client).toLocaleString('fr-FR') : (item.statut === 'Signé' ? 'Horodatage certifié' : null);
              const dateSigCoach = item.date_signature_formateur ? new Date(item.date_signature_formateur).toLocaleString('fr-FR') : (item.statut_formateur === 'Signé' ? 'Horodatage certifié' : null);

              return `
                <div style="margin-bottom: 25px; padding-left: 20px; border-left: 3px solid #f3f4f6;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                    <span style="font-weight: bold; font-size: 14px; color: #1f2937;">${item.type_activite === 'signature' ? '✍️' : '📄'} ${escHtml(item.ressource_titre || item.nom)}</span>
                    <span style="font-size: 11px; font-weight: 800; color: ${item.statut === 'Signé' ? '#059669' : '#f59e0b'}; text-transform: uppercase;">${escHtml(item.statut)}</span>
                  </div>
                  
                  <div style="display: flex; gap: 40px; margin-top: 10px;">
                    ${sigClient ? `
                      <div style="flex: 1;">
                        <p style="font-size: 9px; font-weight: bold; color: #9ca3af; margin-bottom: 5px; text-transform: uppercase;">Signature Bénéficiaire</p>
                        <img src="${sigClient}" style="max-width: 150px; height: 60px; border: 1px solid #f3f4f6; padding: 5px; border-radius: 8px;" />
                        <p style="font-size: 8px; color: #6b7280; margin-top: 3px;">Signé le ${dateSigClient}</p>
                      </div>
                    ` : ''}
                    ${sigCoach ? `
                      <div style="flex: 1;">
                        <p style="font-size: 9px; font-weight: bold; color: #9ca3af; margin-bottom: 5px; text-transform: uppercase;">Signature Formateur</p>
                        <img src="${sigCoach}" style="max-width: 150px; height: 60px; border: 1px solid #f3f4f6; padding: 5px; border-radius: 8px;" />
                        <p style="font-size: 8px; color: #6b7280; margin-top: 3px;">Signé le ${dateSigCoach}</p>
                      </div>
                    ` : ''}
                  </div>
                </div>
              `;
            }).join('');

            return `
              <div style="margin-bottom: 40px; page-break-inside: avoid;">
                <div style="background: #f8fafc; padding: 15px 20px; border-radius: 12px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center;">
                  <div>
                    <h3 style="margin: 0; color: #4f46e5; font-size: 16px;">SÉANCE ${escHtml(g.numero)} : ${escHtml(String(g.nom || '').split(' - ')[0])}</h3>
                    <p style="margin: 5px 0 0; font-size: 12px; color: #64748b; font-weight: bold;">
                      📅 ${g.date ? new Date(g.date).toLocaleDateString('fr-FR') : 'Date non définie'} 
                      &nbsp;&nbsp; ⏰ ${escHtml(g.debut || '--:--')} - ${escHtml(g.fin || '--:--')}
                    </p>
                  </div>
                </div>
                <div style="padding-left: 10px;">
                  ${itemsHtml}
                </div>
              </div>
            `;
          }).join('');

          recapEl.innerHTML = `
            <div style="border: 1px solid #e2e8f0; border-radius: 24px; padding: 40px; background: #fff;">
              <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 40px; padding-bottom: 30px; border-bottom: 2px solid #f1f5f9;">
                <div>
                  <h1 style="color: #4f46e5; font-size: 28px; font-weight: 900; margin: 0; letter-spacing: -0.025em;">${isEmargementOnly ? 'FEUILLES D\'ÉMARGEMENT' : 'RÉCAPITULATIF DE SÉANCES'}</h1>
                  <p style="color: #94a3b8; font-size: 11px; font-weight: bold; margin-top: 8px; text-transform: uppercase; letter-spacing: 0.1em;">${isEmargementOnly ? 'Émargements signés — Traçabilité Qualiopi' : 'Document de traçabilité Qualiopi'}</p>
                </div>
                <div style="text-align: right;">
                  <h2 style="font-size: 18px; font-weight: 800; color: #1e293b; margin: 0;">${escHtml(doc.nomcomplet_client || doc.nom)}</h2>
                  <p style="font-size: 12px; color: #64748b; margin-top: 4px; font-weight: 600;">Dossier : ${escHtml(doc.numero_dossier || 'N/A')}</p>
                </div>
              </div>

              <div>
                ${sessionsContent}
              </div>

              <div style="margin-top: 50px; padding: 25px; background: #fdf2f2; border-radius: 16px; border: 1px solid #fee2e2;">
                <p style="font-size: 10px; color: #b91c1c; font-weight: bold; text-align: center; margin: 0; line-height: 1.5;">
                  Ce document certifie l'ensemble des activités réalisées et signées par les deux parties.<br/>
                  Généré automatiquement par SkorUp le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}.
                </p>
              </div>
            </div>
          `;

          document.body.appendChild(recapEl);
          await new Promise(r => setTimeout(r, 500));
          const canvas = await html2canvas(recapEl, { scale: 2 });
          document.body.removeChild(recapEl);

          const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
          // FIX (2026-09-26) : pagination du récapitulatif — avant ce correctif, le canevas complet
          // (qui grandit avec le nombre de séances) était posé en une seule fois sur une unique page
          // A4 sans jamais appeler pdf.addPage(), donc tout ce qui dépassait la hauteur d'une page
          // était silencieusement coupé (le PDF semblait "s'arrêter" après quelques séances). On
          // découpe maintenant le canevas en tranches de la hauteur d'une page A4 et on ajoute une
          // page par tranche, pour que toutes les séances soient toujours présentes, quel que soit
          // leur nombre.
          const imgWidthPt = 515;
          const pageHeightPt = 842 - 80; // hauteur A4 (pt) moins marges haut/bas de 40pt
          const pxPerPt = canvas.width / imgWidthPt;
          const pageHeightPx = Math.max(1, Math.round(pageHeightPt * pxPerPt));

          let yOffset = 0;
          let isFirstPage = true;
          while (yOffset < canvas.height) {
            const sliceHeightPx = Math.min(pageHeightPx, canvas.height - yOffset);
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sliceHeightPx;
            sliceCanvas.getContext('2d').drawImage(
              canvas, 0, yOffset, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx
            );
            const sliceImgData = sliceCanvas.toDataURL('image/png');
            const sliceHeightPt = sliceHeightPx / pxPerPt;
            if (!isFirstPage) pdf.addPage();
            pdf.addImage(sliceImgData, 'PNG', 40, 40, imgWidthPt, sliceHeightPt);
            yOffset += sliceHeightPx;
            isFirstPage = false;
          }
          pdf.save(`${nomTelechargement(isEmargementOnly ? 'Émargements' : 'Récapitulatif', doc.nom || doc.nom_complet || 'Client')}.pdf`);
          
          toast.success(isEmargementOnly ? "Récap émargements généré !" : "Récapitulatif généré !", { id: 'recap' });
          return;
        } catch (err) {
          console.error("Recap Error:", err);
          toast.error("Erreur lors de la génération du récapitulatif", { id: 'recap' });
          return;
        }
      }

      const urlToDownload = doc.url_signed_pdf || doc.url;
      if (!urlToDownload) return toast.error("Aucun fichier à télécharger");

      const fetchBase64Image = async (url) => {
        if (!url) return null;
        if (url.startsWith('data:image')) return url; // Already base64
        try {
          const res = await fetch(url);
          const blobImg = await res.blob();
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blobImg);
          });
        } catch (e) {
          console.error("Erreur téléchargement image :", e);
          return null;
        }
      };

      try {
        const res = await fetch(urlToDownload);
        if (!res.ok) throw new Error(`Impossible de récupérer le document (${res.status})`);

        const blob = await res.blob();
        let pdfDoc;

        if (blob.type === 'application/pdf' || urlToDownload.toLowerCase().includes('.pdf')) {
          const pdfBytes = await blob.arrayBuffer();
          pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
        } else {
          pdfDoc = await PDFDocument.create();
          const page = pdfDoc.addPage([595.28, 841.89]);
          const imageBytes = await blob.arrayBuffer();
          const img = blob.type === 'image/png'
            ? await pdfDoc.embedPng(imageBytes)
            : await pdfDoc.embedJpg(imageBytes);
          const { width, height } = img.scaleToFit(500, 700);
          page.drawImage(img, { x: 47, y: 841.89 - height - 80, width, height });
        }

        const client = clients.find(c => c.id === doc.user_id);
        const clientName = client ? client.nom : 'Bénéficiaire';

        const formateur = formateurs.find(f => f.id === client?.formateur_id);
        const formateurName = formateur ? formateur.nom : 'Formateur';

        const now = new Date();
        const dateText = now.toLocaleDateString('fr-FR');
        const timeText = now.toLocaleTimeString('fr-FR');

        const _sigDoc = await chargerSignaturesDocument(doc.id); // 2026-10-21 : images lues à la demande
        const signatureClientUrl = _sigDoc.signature_client || doc.signature_client || doc.signature_client_url || null;
        const signatureFormateurUrl = _sigDoc.signature_formateur || doc.signature_formateur || doc.signature_formateur_url || null;

        console.log('Signature Client:', signatureClientUrl);
        console.log('Signature Formateur:', signatureFormateurUrl);

        const clientImageBase64 = await fetchBase64Image(signatureClientUrl);
        const formateurImageBase64 = await fetchBase64Image(signatureFormateurUrl);

        const certEl = document.createElement('div');
        certEl.style.width = '794px';
        certEl.style.minHeight = '1123px';
        certEl.style.padding = '40px';
        certEl.style.fontFamily = 'Arial, Helvetica, sans-serif';
        certEl.style.background = '#ffffff';
        certEl.style.color = '#1f2937';
        certEl.style.position = 'fixed';
        certEl.style.left = '-9999px';
        certEl.style.top = '-9999px';

        const renderSigBlock = (role, imageBase64, name) => {
          const safeSrc = safeImgSrc(imageBase64);
          const imageHtml = safeSrc
            ? `<img src="${safeSrc}" style="max-width: 280px; height: auto; border: 1px solid #d1d5db;" />`
            : '<div style="width:280px;height:120px;border:1px dashed #9ca3af;display:flex;align-items:center;justify-content:center;color:#6b7280;font-size:12px;">Aucune signature attachée</div>';

          return `
            <div style="flex:1; min-width:280px; padding: 14px; border: 1px solid #d1d5db; border-radius: 12px; margin: 8px;">
              <h3 style="font-size: 16px; margin-bottom: 10px;">${escHtml(role)}</h3>
              <p style="font-size: 12px; margin-bottom: 8px; font-weight: bold;">${escHtml(name)}</p>
              <p style="font-size: 12px; margin-bottom: 10px;">${dateText} à ${timeText}</p>
              ${imageHtml}
            </div>
          `;
        };

        certEl.innerHTML = `
          <div style="padding: 20px; border: 1px solid #d1d5db; border-radius: 12px;">
            <h1 style="font-size: 24px; margin-bottom: 16px;">Certificat de signature électronique</h1>
            <p style="font-size: 14px; margin-bottom: 16px;"><strong>Document :</strong> ${escHtml(doc.nom || 'Document')}</p>
            <div style="display:flex; flex-wrap:wrap; gap:16px;">
              ${renderSigBlock('Client', clientImageBase64, clientName)}
              ${renderSigBlock('Formateur', formateurImageBase64, formateurName)}
            </div>
            <p style="font-size: 11px; margin-top: 30px; color:#4b5563;">Mentions légales : Ce document fait foi de preuve d'émargement pour Qualiopi, certifié par les deux parties.</p>
          </div>
        `;

        document.body.appendChild(certEl);

        // Petite pause pour être sûr que le rendu image (Base64) est bien injecté
        await new Promise((resolve) => setTimeout(resolve, 500));

        const canvas = await html2canvas(certEl, { scale: 2, backgroundColor: '#ffffff' });
        document.body.removeChild(certEl);

        const certPdf = new jsPDF({ unit: 'pt', format: 'a4' });
        const imgData = canvas.toDataURL('image/png');
        certPdf.addImage(imgData, 'PNG', 0, 0, 595.28, 841.89);

        const certPdfBytes = certPdf.output('arraybuffer');
        const certPageDoc = await PDFDocument.load(certPdfBytes, { ignoreEncryption: true });
        const [certPage] = await pdfDoc.copyPages(certPageDoc, [0]);
        pdfDoc.addPage(certPage);

        const finalPdfBytes = await pdfDoc.save();
        const finalBlob = new Blob([finalPdfBytes], { type: 'application/pdf' });

        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(finalBlob);
        // 2026-10-16 : « Nom du document - Nom du client.pdf » au lieu de certificat_signature_<id>_<date>.
        const _clientDuDoc = (clients || []).find(c => String(c.id) === String(doc.user_id));
        const _nomDocPdf = String(doc.nom || 'Document').replace(/\.pdf$/i, '');
        downloadLink.download = `${nomTelechargement(_nomDocPdf, _clientDuDoc?.nom_complet || _clientDuDoc?.nom)}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadLink.href);

      } catch (error) {
        console.error('Erreur de génération du PDF final :', error);
        toast.error('Impossible de générer le PDF signé. Vérifiez la console.');
      }
    } else {
      toast.success('Simulation : Téléchargement du bilan...');
    }
  };



  // ── 2026-10-21 : ouverture instantanée (comme Linear, GitHub…) ─────────────────────────────────────
  // Les dernières données connues sont gardées dans le navigateur (par organisme ET par personne, sans
  // aucune image de signature) et affichées tout de suite à l'ouverture ; les vraies données arrivent
  // ensuite et les remplacent. Tout est effacé à la déconnexion. Réservé à l'équipe (admin, formateur).
  // Mémorise le profil réel (rôle en base, pas la vue « Mon espace Formateur ») pour la prochaine ouverture.
  useEffect(() => {
    if (!currentOrgId || !currentUserId || !(userRole === 'admin' || userRole === 'formateur')) return;
    const roleReel = isAdminActingAsFormateur ? 'admin' : userRole;
    supabase.auth.getSession().then(({ data }) => {
      const uid = data && data.session && data.session.user && data.session.user.id;
      if (!uid) return;
      try { localStorage.setItem(`skorup_profil_v1_${uid}`, JSON.stringify({ role: roleReel, id: currentUserId, org: currentOrgId, le: Date.now() })); } catch (_) { /* rien */ }
    }).catch(() => {});
  }, [currentOrgId, currentUserId, userRole, isAdminActingAsFormateur]);
  const cleMemoire = (currentOrgId && currentUserId && (userRole === 'admin' || userRole === 'formateur'))
    ? `skorup_cache_v1_${currentOrgId}_${userRole}_${currentUserId}` : null;
  useEffect(() => {
    setChargements({});
    if (!cleMemoire) return;
    let m = null;
    try { m = JSON.parse(localStorage.getItem(cleMemoire) || 'null'); } catch (_) { m = null; }
    if (!m || !m.le || Date.now() - m.le > 7 * 86400000) return;
    if (Array.isArray(m.clients)) setClients(m.clients);
    if (Array.isArray(m.formateurs)) setFormateurs(m.formateurs);
    if (Array.isArray(m.modules)) setModules(m.modules);
    if (Array.isArray(m.moduleSessionTemplates)) setModuleSessionTemplates(m.moduleSessionTemplates);
    if (Array.isArray(m.moduleStepResources)) setModuleStepResources(m.moduleStepResources);
    if (Array.isArray(m.sessions)) setSessions(m.sessions);
    if (Array.isArray(m.documents)) setDocuments(m.documents);
    if (m.documentTemplates && typeof m.documentTemplates === 'object') setDocumentTemplates(m.documentTemplates);
    if (Array.isArray(m.groupes)) setGroupes(m.groupes);
    if (Array.isArray(m.groupeSeances)) setGroupeSeances(m.groupeSeances);
    if (m.orgSettings && typeof m.orgSettings === 'object') setOrgSettings(prev => prev || m.orgSettings);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleMemoire]);
  const chargementEnCours = !!cleMemoire && ['utilisateurs', 'sessions', 'documents', 'modules'].some(k => !chargements[k]);
  useEffect(() => {
    if (!cleMemoire || chargementEnCours) return;
    const t = setTimeout(() => {
      const sansSignature = (r) => {
        if (!r || typeof r !== 'object') return r;
        const { signature_client, signature_formateur, signature_organisme, signature_image, ...reste } = r; // eslint-disable-line no-unused-vars
        return reste;
      };
      try {
        const texte = JSON.stringify({
          le: Date.now(), clients, formateurs, modules, moduleSessionTemplates, moduleStepResources,
          sessions: sessions.map(sansSignature), documents: documents.map(sansSignature), documentTemplates,
          groupes, groupeSeances, orgSettings,
        });
        if (texte.length < 3000000) localStorage.setItem(cleMemoire, texte);
        else localStorage.removeItem(cleMemoire);
      } catch (_) { /* navigateur plein ou privé : on s'en passe */ }
    }, 1500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleMemoire, chargementEnCours, clients, formateurs, modules, moduleSessionTemplates, moduleStepResources, sessions, documents, documentTemplates, groupes, groupeSeances, orgSettings]);

  // --- Chargement des données au lancement (Supabase) ---
  useEffect(() => {
    fetchUtilisateurs();
    fetchDocuments();
    fetchModules();
    fetchSessions();
    fetchGroupes();
    fetchPedagogicalResources();
    fetchOrgSettings();
    fetchUnreadMessagesCount();

    // Détection des liens d'invitation ou de récupération de mot de passe.
    // IMPORTANT : PASSWORD_RECOVERY doit amener sur l'écran "Nouveau mot de passe"
    // (isResetPassword), PAS sur "Finalisez votre accès" (isSettingPassword, réservé
    // aux invitations). Les confondre faisait passer les liens de réinitialisation par
    // les deux écrans à la suite (le mot de passe était déjà changé sur le premier,
    // d'où l'erreur "déjà utilisé" en le retapant sur le second).
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsResetPassword(true);
        setIsSettingPassword(false);
      } else if (
        (event === 'SIGNED_IN' && window.location.hash.includes('type=invite')) ||
        window.location.pathname === '/set-password'
      ) {
        setIsSettingPassword(true);
      }
    });
    return () => { authListener?.subscription?.unsubscribe(); };
  // FIX (2026-09-03, voir incident "clients/formateurs disparus") : currentOrgId ajouté aux
  // dépendances en filet de sécurité — si sa valeur se stabilise après userRole, les données sont
  // rechargées automatiquement au lieu de rester bloquées sur des listes vides (limit(0)).
  }, [userRole, currentOrgId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // AJOUT (2026-10-02) : quand on revient sur l'onglet après plus de 2 minutes (ordinateur en veille, autre
  // onglet...), on recharge discrètement les données principales — sinon une session expirée ou des
  // modifications faites ailleurs laissaient des listes vides ou périmées jusqu'au prochain clic.
  const lastFullLoadRef = useRef(Date.now());
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !currentOrgId) return;
      if (Date.now() - lastFullLoadRef.current < 2 * 60 * 1000) return;
      lastFullLoadRef.current = Date.now();
      fetchUtilisateurs();
      fetchDocuments();
      fetchModules();
      fetchSessions();
      fetchGroupes();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, currentOrgId]);

  // Auto-génération supprimée : elle déclenchait generateSessions 3x lors du chargement des données.
  // Les séances sont désormais générées uniquement via le bouton manuel dans la vue Formateur.

  // Affichage du simulateur de connexion ou de la page de mot de passe
  if (isSettingPassword) {
    return (
      <SetPasswordView
        supabase={supabase}
        onComplete={async () => {
          // Effacer immédiatement le hash URL pour empêcher onAuthStateChange de re-déclencher le formulaire
          window.history.replaceState(null, '', window.location.pathname);
          setIsSettingPassword(false);

          // Attendre que Supabase finalise la session
          await new Promise(r => setTimeout(r, 500));

          // Récupérer l'utilisateur authentifié
          const { data: { user } } = await supabase.auth.getUser();

          if (user && user.email) {
            // Chercher le rôle dans la base de données (admin/formateur)
            const userData = await findAccountProfile(user);

            if (userData && userData.role) {
              handleLogin(userData.role, userData.id, userData.organisation_id);
              return;
            }

            // Chercher côté clients
            const { data: clientData } = await supabase
              .from('clients')
              .select('id, organisation_id')
              .ilike('email_contact', escapeLike(user.email))
              .single();
            if (clientData?.id) {
              handleLogin('client', clientData.id, clientData.organisation_id);
              return;
            }
          }

          // Fallback: pas de rôle trouvé
          console.warn('Impossible de déterminer le rôle automatiquement.');
        }}
      />
    );
  }

  if (isResetPassword) {
    return (
      <ResetPasswordPage
        supabase={supabase}
        onComplete={async () => {
          // Le mot de passe est déjà enregistré côté Supabase Auth à ce stade (updateUser
          // a réussi). Tout ce qui suit n'est qu'une tentative de reconnexion automatique
          // pour le confort — si elle échoue pour une raison quelconque (réseau, RLS, etc.),
          // on ne doit JAMAIS laisser l'utilisateur bloqué sur cet écran en pensant que rien
          // n'a été enregistré : on retombe toujours proprement sur l'écran de connexion.
          const fallbackToLogin = async () => {
            try { await supabase.auth.signOut(); } catch (e) { console.error('Erreur déconnexion après reset:', e); }
            window.history.replaceState(null, '', '/');
            setIsResetPassword(false);
            setResetSuccessMsg("Votre mot de passe a été réinitialisé avec succès. Connectez-vous avec vos nouveaux identifiants.");
          };

          try {
            // Auto login process
            const { data: { user } } = await supabase.auth.getUser();

            if (user && user.email) {
              const userData = await findAccountProfile(user);
              if (userData && userData.role) {
                handleLogin(userData.role, userData.id, userData.organisation_id);
                setIsResetPassword(false);
                return;
              }

              const { data: clientData } = await supabase.from('clients').select('id, organisation_id').ilike('email_contact', escapeLike(user.email)).single();
              if (clientData && clientData.id) {
                 handleLogin('client', clientData.id, clientData.organisation_id);
                 setIsResetPassword(false);
                 return;
              }
            }

            // Rôle non trouvé automatiquement (compte legacy, RLS, etc.)
            await fallbackToLogin();
          } catch (err) {
            console.error('Erreur pendant la reconnexion automatique après réinitialisation du mot de passe:', err);
            await fallbackToLogin();
          }
        }}
      />
    );
  }

  if (isSignup) {
    return (
      <SignupView
        supabase={supabase}
        onComplete={() => { window.history.replaceState(null, '', '/'); setIsSignup(false); }}
      />
    );
  }

  if (isSignatureEntreprise) {
    return <SignatureEntreprisePage />;
  }

  if (isProspectQuestionnaire) {
    return <ProspectQuestionnaireView />;
  }

  if (needsSetup) {
    return (
      <SetupOrganisationPage />
    );
  }

  if (isLoadingSession) {
    return (
      <div className="h-dvh w-full flex flex-col items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-violet-600/20 border-t-violet-600 rounded-full animate-spin mb-4"></div>
        <div className="text-gray-400 font-bold uppercase tracking-widest text-xs animate-pulse">Chargement de votre session...</div>
      </div>
    );
  }

  if (!userRole) {
    return <LoginView handleLogin={handleLogin} supabase={supabase} successMessage={resetSuccessMsg} onNeedsSetup={() => setNeedsSetup(true)} />;
  }

  // Vérification abonnement expiré — s'applique à tous les rôles de l'organisme (admin, formateur,
  // client) : un abonnement expiré doit bloquer tout le monde, pas seulement l'admin.
  const isSubscriptionExpired = orgSettings && (() => {
    const { subscription_status, trial_ends_at } = orgSettings;
    if (!subscription_status || subscription_status === 'active') return false;
    // 2026-10-19 : Stripe utilise aussi unpaid / paused pour un abonnement impayé ou suspendu.
    if (['canceled', 'past_due', 'unpaid', 'paused'].includes(subscription_status)) return true;
    if (subscription_status === 'trialing' && trial_ends_at) {
      return new Date(trial_ends_at) < new Date();
    }
    return false;
  })();

  return (
    <ComptesClientsContext.Provider value={comptesCtx}>
    <div className="flex h-dvh bg-gray-50 font-sans overflow-hidden">
      {/* Sidebar Mobile Overlay */}
      <div className={`fixed inset-0 bg-gray-900/50 z-40 transition-opacity md:hidden ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)}></div>

      {/* Navigation Sidebar (Dynamic par Rôle) */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 text-gray-300 transition-transform transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static md:flex-shrink-0 flex flex-col`} style={{background:'linear-gradient(180deg,#1C0D40 0%,#130922 30%,#0F0A1E 70%)'}}>
        <style>{`
          .nav-glow {
            background:
              linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 55%),
              linear-gradient(135deg, rgba(255,255,255,0.3) 0%, var(--brand-primary, #7C3AED) 50%, var(--brand-primary-dark, #5B21B6) 100%);
            box-shadow: 0 3px 16px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.22);
            color: white !important;
            font-weight: 500;
          }
          .nav-scroll::-webkit-scrollbar {
            width: 3px;
          }
          .nav-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .nav-scroll::-webkit-scrollbar-thumb {
            background: rgba(124, 58, 237, 0.4);
            border-radius: 999px;
          }
          .nav-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(124, 58, 237, 0.7);
          }
          .nav-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(124,58,237,0.4) transparent;
          }
        `}</style>
        <div className="flex items-center justify-between h-20 px-5 border-b border-white/10" style={{background:'#0C0619'}}>
          <div className="flex items-center gap-3 min-w-0">
            {brandSettings.logo_url ? (
              <img src={brandSettings.logo_url} alt="Logo" className="w-9 h-9 rounded-xl object-contain bg-white/10 p-1 flex-shrink-0" />
            ) : (
              <img src="/logo-mark.png" alt="SkorUp" className="w-9 h-9 object-contain flex-shrink-0" />
            )}
            <span className="text-white truncate" style={{fontSize:'15px',lineHeight:1,letterSpacing:'0.2px'}}>
              {brandSettings.org_name ? <span style={{fontWeight:700}}>{brandSettings.org_name}</span> : <><span style={{fontWeight:800}}>Skor</span><span style={{fontWeight:300,opacity:0.9}}>Up</span></>}
            </span>
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="md:hidden text-gray-400 hover:text-white flex-shrink-0">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto nav-scroll">
          {userRole === 'admin' && (
            <>
              <button onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'dashboard' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <LayoutDashboard className="w-5 h-5 mr-3" /> Tableau de bord
              </button>
              <p className="px-4 pt-4 pb-1 text-[11px] font-black uppercase tracking-widest text-slate-500">Suivi</p>
              <button onClick={() => { setActiveTab('clients'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'clients' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Users className="w-5 h-5 mr-3" /> Clients
              </button>
              <button onClick={() => { setActiveTab('groupes'); setSelectedGroupeId(null); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'groupes' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <UsersRound className="w-5 h-5 mr-3" /> Groupes
              </button>
              <button onClick={() => { setActiveTab('formateurs'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'formateurs' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Users className="w-5 h-5 mr-3" /> Formateurs
              </button>
              <button onClick={() => { setActiveTab('calendrier'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'calendrier' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <CalendarDays className="w-5 h-5 mr-3" /> Calendrier
              </button>
              <p className="px-4 pt-4 pb-1 text-[11px] font-black uppercase tracking-widest text-slate-500">Contenus</p>
              <button onClick={() => { setActiveTab('gestion_documents'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'gestion_documents' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <FileText className="w-5 h-5 mr-3" /> Documents
              </button>
              <button onClick={() => { setActiveTab('questionnaires'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'questionnaires' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <FileCheck className="w-5 h-5 mr-3" /> Questionnaires
              </button>
              <button onClick={() => { setActiveTab('modules'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'modules' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Layers className="w-5 h-5 mr-3" /> Modules
              </button>
              <button onClick={() => { setActiveTab('fiches_metiers'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'fiches_metiers' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Briefcase className="w-5 h-5 mr-3" /> Fiches Métiers
              </button>
              <p className="px-4 pt-4 pb-1 text-[11px] font-black uppercase tracking-widest text-slate-500">Commercial</p>
              <button onClick={() => { setActiveTab('relances'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'relances' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Bell className="w-5 h-5 mr-3" /> Relances
                {!hasFeatureAccess(orgSettings, 'pro') && <span className="ml-auto text-[11px] font-black bg-violet-500/90 text-white px-1.5 py-0.5 rounded-md">PRO</span>}
              </button>
              <button onClick={() => { setActiveTab('prospects'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'prospects' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Send className="w-5 h-5 mr-3" /> Prospects
              </button>
              <p className="px-4 pt-4 pb-1 text-[11px] font-black uppercase tracking-widest text-slate-500">Gestion</p>
              <button onClick={() => { setActiveTab('finances'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'finances' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Wallet className="w-5 h-5 mr-3" /> Finances
              </button>
              <button onClick={() => { setActiveTab('bpf'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'bpf' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <FileCheck className="w-5 h-5 mr-3" /> BPF
                {!hasFeatureAccess(orgSettings, 'pro') && <span className="ml-auto text-[11px] font-black bg-violet-500/90 text-white px-1.5 py-0.5 rounded-md">PRO</span>}
              </button>
              <button onClick={() => { setActiveTab('veille'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'veille' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <ClipboardList className="w-5 h-5 mr-3" /> Veille
                {!hasFeatureAccess(orgSettings, 'pro')
                  ? <span className="ml-auto text-[11px] font-black bg-violet-500/90 text-white px-1.5 py-0.5 rounded-md">PRO</span>
                  : (veilleNonLues > 0 && <span className="ml-auto text-[11px] font-black bg-white text-violet-700 px-1.5 py-0.5 rounded-md" aria-label={`${veilleNonLues} information(s) à traiter`}>{veilleNonLues}</span>)}
              </button>
              <button onClick={() => { setActiveTab('processus'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'processus' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Layout className="w-5 h-5 mr-3" /> Processus
              </button>
              <button onClick={() => { setActiveTab('messagerie'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'messagerie' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                Messagerie
                {unreadMessagesCount > 0 && (
                  <span className="ml-auto bg-rose-500 text-white text-xs font-black rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-tight">
                    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                  </span>
                )}
              </button>
              <button onClick={() => { setActiveTab('parametres_org'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'parametres_org' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Settings className="w-5 h-5 mr-3" /> Paramètres
              </button>
              <button
                onClick={() => window.open(window.location.origin + window.location.pathname + '?view=formateur', '_blank')}
                className="w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 text-violet-300 hover:bg-violet-900/30 hover:text-white font-medium mt-2 border border-violet-800/60"
                title="Ouvre votre espace formateur dans un nouvel onglet, sans vous déconnecter de l'espace admin"
              >
                <ExternalLink className="w-5 h-5 mr-3" /> Mon espace Formateur
              </button>
            </>
          )}

          {userRole === 'formateur' && (
            <>
              <button onClick={() => { setActiveTab('accueil_formateur'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'accueil_formateur' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <LayoutDashboard className="w-5 h-5 mr-3" /> Accueil
              </button>
              <button onClick={() => { setActiveTab('clients'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'clients' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Users className="w-5 h-5 mr-3" /> Mes Clients
              </button>
              <button onClick={() => { setActiveTab('calendrier'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'calendrier' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <CalendarDays className="w-5 h-5 mr-3" /> Calendrier
              </button>
              <button onClick={() => { setActiveTab('fiches_metiers'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'fiches_metiers' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Briefcase className="w-5 h-5 mr-3" /> Fiches Métiers
              </button>
              <button onClick={() => { setActiveTab('prospects'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'prospects' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Send className="w-5 h-5 mr-3" /> Prospects
              </button>
              <button onClick={() => { setActiveTab('processus'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'processus' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <Layout className="w-5 h-5 mr-3" /> Processus
              </button>
              <button onClick={() => { setActiveTab('messagerie'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'messagerie' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                Messagerie
                {unreadMessagesCount > 0 && (
                  <span className="ml-auto bg-rose-500 text-white text-xs font-black rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-tight">
                    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                  </span>
                )}
              </button>
              <button onClick={() => { setActiveTab('profil'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'profil' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <UserCircle className="w-5 h-5 mr-3" /> Mon profil
              </button>
            </>
          )}

          {userRole === 'client' && (
            <>
              <button onClick={() => { setActiveTab('accueil'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'accueil' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}><LayoutDashboard className="w-5 h-5 mr-3" /> Accueil</button>
              <button onClick={() => { setActiveTab('mes_seances'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'mes_seances' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}><FileText className="w-5 h-5 mr-3" /> Mes Séances</button>
              <button onClick={() => { setActiveTab('calendrier'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'calendrier' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}><CalendarDays className="w-5 h-5 mr-3" /> Calendrier</button>
              <button onClick={() => { setActiveTab('mes_documents'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'mes_documents' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}><FileText className="w-5 h-5 mr-3" /> Mes Documents</button>
              <button onClick={() => { setActiveTab('exercices'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'exercices' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}><ClipboardList className="w-5 h-5 mr-3" /> Exercices</button>
              <button onClick={() => { setActiveTab('fiches_metiers'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'fiches_metiers' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}><Briefcase className="w-5 h-5 mr-3" /> Fiches Métiers</button>
              <button onClick={() => { setActiveTab('processus'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'processus' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}><BookOpen className="w-5 h-5 mr-3" /> Ressources</button>
              <button onClick={() => { setActiveTab('messagerie'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'messagerie' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                Messagerie
                {unreadMessagesCount > 0 && (
                  <span className="ml-auto bg-rose-500 text-white text-xs font-black rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-tight">
                    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                  </span>
                )}
              </button>
              <button onClick={() => { setActiveTab('profil'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === 'profil' ? 'nav-glow text-white' : 'text-slate-300 hover:bg-violet-900/30 hover:text-white font-medium'}`}>
                <UserCircle className="w-5 h-5 mr-3" /> Mon profil
              </button>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-violet-900/40" style={{background:'#0C0619'}}>
          {/* Bannière essai / abonnement expiré */}
          {userRole === 'admin' && orgSettings?.subscription_status === 'trialing' && (
            <TrialBanner orgSettings={orgSettings} onUpgrade={() => { setActiveTab('parametres_org'); setMobileMenuOpen(false); }} />
          )}
          {/* Liens légaux */}
          <LiensLegaux className="flex flex-wrap justify-center gap-x-3 gap-y-1 mb-3 px-2"
            lienClassName="text-xs text-violet-400/60 hover:text-violet-300 transition-colors" />
          {isAdminActingAsFormateur && (
            <button
              onClick={() => { window.location.href = window.location.origin + window.location.pathname; }}
              className="w-full flex items-center px-4 py-2.5 rounded-xl transition-all duration-200 text-violet-300 hover:bg-violet-900/30 hover:text-white font-medium mb-2 border border-violet-800/60"
              title="Revenir à l'espace administrateur (dans cet onglet)"
            >
              <ExternalLink className="w-5 h-5 mr-3 rotate-180" /> Accéder à l'espace administrateur
            </button>
          )}
          <button onClick={handleLogout} className="w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 hover:bg-red-500/10 hover:text-red-400 text-gray-400 font-medium">
            <LogOut className="w-5 h-5 mr-3" /> Déconnexion
          </button>
        </div>
      </div>

      {/* CONFORMITÉ (2026-10-23) : acceptation des conditions en vigueur */}
      <PorteConditions userRole={userRole} currentOrgId={currentOrgId} onLogout={handleLogout} />
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {userRole === 'client' && (() => {
          const _parcoursFini = isModuleCompletedForClient(currentUserId, sessions, moduleDuClient(clients, currentUserId)); // lot 3
          const docsASigner = documents.filter(d => d.user_id === currentUserId && docAwaitsClientSignature(d) && !isBlockedBySigningOrder(d, 'client') && !docDeFinVerrouille(d, _parcoursFini)).length;
          const onglets = [
            { onglet: 'accueil', label: 'Accueil', Icone: LayoutDashboard, badge: 0 },
            { onglet: 'mes_seances', label: 'Séances', Icone: CalendarCheck, badge: 0 },
            { onglet: 'mes_documents', label: 'Documents', Icone: FileText, badge: docsASigner },
            { onglet: 'messagerie', label: 'Messages', Icone: MessageCircle, badge: unreadMessagesCount },
          ];
          return (
            <nav aria-label="Navigation principale" className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 grid grid-cols-5 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
              {onglets.map(({ onglet, label, Icone, badge }) => (
                <button key={onglet} onClick={() => { setActiveTab(onglet); setMobileMenuOpen(false); }}
                  aria-current={activeTab === onglet ? 'page' : undefined}
                  className={`relative flex flex-col items-center justify-center py-2 text-[11px] font-bold ${activeTab === onglet ? 'text-violet-700' : 'text-gray-500'}`}>
                  <Icone className="w-6 h-6 mb-0.5" />
                  {label}
                  {badge > 0 && <span className="absolute top-1 left-1/2 ml-2 bg-rose-500 text-white text-[10px] font-black rounded-full min-w-[18px] px-1 leading-[18px] text-center">{badge > 9 ? '9+' : badge}</span>}
                </button>
              ))}
              <button onClick={() => setMobileMenuOpen(true)} className="flex flex-col items-center justify-center py-2 text-[11px] font-bold text-gray-500">
                <Menu className="w-6 h-6 mb-0.5" />
                Plus
              </button>
            </nav>
          );
        })()}

        <header className="md:hidden bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between z-10 w-full shrink-0">
          <button onClick={() => setMobileMenuOpen(true)} className="text-gray-500 shrink-0" aria-label="Ouvrir le menu">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          {(() => {
            // Logo et nom affichés aux clients/formateurs = personnalisation du portail (offre Pro) :
            // on reprend exactement les réglages « Personnalisation », comme l'en-tête ordinateur.
            const logoEspace = userRole === 'admin' ? (orgSettings?.logo_url || null) : (brandSettings?.logo_url || null);
            const nomEspace = userRole === 'admin'
              ? (orgSettings?.nom || 'Espace Admin')
              : (brandSettings?.org_name || 'SkorUp');
            const nomPersonne = userRole === 'client'
              ? clients.find(c => c.id === currentUserId)?.nom
              : userRole === 'formateur' ? assignableFormateurs.find(f => f.id === currentUserId)?.nom : orgSettings?.nom;
            return (
              <>
                <div className="flex-1 min-w-0 flex items-center gap-2 mx-3">
                  {logoEspace && <img src={logoEspace} alt="" className="w-7 h-7 rounded-full object-contain bg-white border border-gray-100 shrink-0" />}
                  <span className="font-bold text-gray-900 truncate">{nomEspace}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <NotificationBell sessions={sessions} documents={documents} clients={clients} userRole={userRole} currentUserId={currentUserId} setActiveTab={setActiveTab} depotsNonLus={depotsPourMoi} piecesAFournir={piecesAFournirClient} onOpenClient={ouvrirRecusClient} />
                  <button onClick={() => setActiveTab('profil')} aria-label="Mon profil"
                    className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center">
                    {computeInitials(nomPersonne || '') || <UserCircle size={20} />}
                  </button>
                </div>
              </>
            );
          })()}
        </header>

        <header className="hidden md:flex bg-white px-10 py-5 border-b border-gray-100 shadow-sm z-10 justify-between items-center w-full shrink-0">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-gray-800">
              {userRole === 'admin' ? 'Espace Admin' : userRole === 'formateur' ? (brandSettings?.org_name || 'Espace Formateur') : (brandSettings?.org_name || 'Espace Client')}
            </h2>
            {chargementEnCours ? (
              <span className="bg-amber-50 text-amber-700 text-xs font-bold px-2 py-1 rounded-md border border-amber-200 inline-flex items-center gap-1.5">
                <span className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin"></span> Mise à jour…
              </span>
            ) : (
              <span className="bg-green-50 text-green-700 text-xs font-bold px-2 py-1 rounded-md border border-green-200">Connecté en ligne</span>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <NotificationBell
              sessions={sessions}
              documents={documents}
              clients={clients}
              userRole={userRole}
              currentUserId={currentUserId}
              setActiveTab={setActiveTab}
              depotsNonLus={depotsPourMoi}
              piecesAFournir={piecesAFournirClient}
              onOpenClient={ouvrirRecusClient}
            />
            {(() => {
              let displayName = "";
              let rawName = "";
              if (userRole === 'admin') {
                displayName = orgSettings?.nom || "Mon espace";
              } else if (userRole === 'formateur') {
                rawName = assignableFormateurs.find(f => f.id === currentUserId)?.nom;
                displayName = rawName || "Formateur";
              } else if (userRole === 'client') {
                rawName = clients.find(c => c.id === currentUserId)?.nom;
                displayName = rawName || "Bénéficiaire";
              }

              return (
                <>
                  <div className="text-right mr-2">
                    <p className="text-sm font-bold text-gray-800 leading-tight">
                      {displayName}
                    </p>
                  </div>
                  {userRole === 'admin' && orgSettings?.logo_url ? (
                    <img
                      src={orgSettings.logo_url}
                      alt="Logo"
                      onClick={() => setActiveTab('profil')}
                      className="w-10 h-10 rounded-full object-contain bg-white border-2 border-indigo-200 p-0.5 shadow-sm cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-all"
                    />
                  ) : (
                    <div
                      onClick={() => setActiveTab('profil')}
                      className="w-10 h-10 rounded-full bg-indigo-100 border-2 border-indigo-200 flex items-center justify-center font-bold text-sm text-indigo-700 shadow-sm cursor-pointer hover:bg-indigo-600 hover:text-white transition-all transform hover:scale-105"
                    >
                      {userRole === 'admin' ? "AD" : getInitials(rawName)}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </header>

        <ConventionsContext.Provider value={{ orgSettings, currentOrgId, documentTemplates, estPro, nomUtilisateur: nomUtilisateurConnecte, modules, formateurs: assignableFormateurs, groupes, groupeSeances, clients, sessions, userRole, rafraichirGroupes: fetchGroupes }}>
        <main className={`flex-1 overflow-y-auto bg-gray-50/50 p-6 md:p-10 w-full h-full relative ${userRole === 'client' ? 'pb-28 md:pb-10' : ''}`}>
          {isSubscriptionExpired && activeTab !== 'parametres_org' ? (
            // Abonnement expiré : on n'affiche QUE l'écran paywall, le contenu de l'onglet n'est même
            // pas monté — avant, ce n'était qu'une superposition visuelle par-dessus un contenu resté
            // actif (chargements/mutations de données en arrière-plan malgré le blocage à l'écran).
            <div className="w-full h-full flex items-center justify-center">
              <PaywallScreen onSubscribe={() => setActiveTab('parametres_org')} isAdmin={userRole === 'admin'} status={orgSettings?.subscription_status} />
            </div>
          ) : (
          <>
          {activeTab === 'profil' && <ProfileView
            currentUserId={currentUserId}
            supabase={supabase}
            fetchUtilisateurs={fetchUtilisateurs}
            formateurs={assignableFormateurs}
            clients={clients}
            userRole={userRole}
            orgSettings={orgSettings}
            currentOrgId={currentOrgId}
            onOrgSaved={(updated) => setOrgSettings(prev => ({ ...prev, ...updated }))}
          />}
          {activeTab === 'dashboard' && userRole === 'admin' && <DashboardAdminView
            clients={clients}
            sessions={sessions}
            documents={documents}
            formateurs={assignableFormateurs}
            setActiveTab={setActiveTab}
            modules={modules}
            orgSettings={orgSettings}
            currentOrgId={currentOrgId}
          />}
          {activeTab === 'calendrier' && <CalendrierView
            sessions={sessions}
            clients={clients}
            formateurs={assignableFormateurs}
            userRole={userRole}
            currentUserId={currentUserId}
            fetchSessions={fetchSessions}
            groupes={groupes}
            groupeSeances={groupeSeances}
            onOpenGroupe={userRole === 'admin' ? ouvrirGroupe : null}
          />}
          {activeTab === 'accueil_formateur' && userRole === 'formateur' && <FormateurAccueilView
            formateurs={assignableFormateurs}
            clients={clients}
            sessions={sessions}
            documents={documents}
            currentUserId={currentUserId}
            setActiveTab={setActiveTab}
            setSigningDocId={setSigningDocId}
            setViewingSession={setViewingSession}
            handleSaveCorrection={handleSaveCorrection}
          />}
          {activeTab === 'fiches_metiers' && <FichesMetiersView
            userRole={userRole}
            currentUserId={currentUserId}
            currentOrgId={currentOrgId}
            supabase={supabase}
            clients={clients}
            formateurs={assignableFormateurs}
          />}
          {activeTab === 'parametres_org' && userRole === 'admin' && (
            <OrganisationSettingsView
              supabase={supabase}
              currentOrgId={currentOrgId}
              orgId={currentOrgId}
              orgSettings={orgSettings}
              onSaved={(updated) => setOrgSettings(prev => ({ ...prev, ...updated }))}
              brandSettings={brandSettings}
              onBrandSaved={(updated) => setBrandSettings(prev => ({ ...prev, ...updated }))}
              onRefresh={fetchOrgSettings}
            />
          )}
          {activeTab === 'clients' && userRole === 'admin' && <AdminClientsView chargement={chargementEnCours}
            clientsArchives={clientsArchives} onRestaurerDossier={restaurerDossierClient}
            handleAddUser={handleAddUser}
            newUserName={newUserName} setNewUserName={setNewUserName}
            newUserEmail={newUserEmail} setNewUserEmail={setNewUserEmail}
            newUserRole={newUserRole} setNewUserRole={setNewUserRole}
            clientPhone={clientPhone} setClientPhone={setClientPhone}
            clientEmail={clientEmail} setClientEmail={setClientEmail}
            isAddingUser={isAddingUser}
            clients={clients}
            formateurs={assignableFormateurs}
            assignFormateur={assignFormateur}
            handleModuleChange={handleModuleChange}
            modules={modules}
            handleGenerateDocx={handleGenerateDocx}
            sessions={sessions}
            documentTemplates={documentTemplates}
            supabase={supabase}
            expandedClientId={expandedClientId}
            setExpandedClientId={setExpandedClientId}
            fetchUtilisateurs={fetchUtilisateurs}
            fetchDocuments={fetchDocuments}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setIsInviteModalOpen={setIsInviteModalOpen}
            setInviteDefaultRole={setInviteDefaultRole}
            pedagogicalResources={pedagogicalResources}
            fetchSessions={fetchSessions}
            documents={documents}
            handleDownloadResource={handleDownloadResource}
            handleUploadExerciseResponse={handleUploadExerciseResponse}
            generateSessions={generateSessions}
            handleDeleteClient={handleDeleteClient}
            setIsSessionItemModalOpen={setIsSessionItemModalOpen}
            setTargetSessionForAddition={setTargetSessionForAddition}
            setViewingSession={setViewingSession}
            handleDownloadPDF={handleDownloadPDF}
            updateSessionDate={updateSessionDate}
            updateSessionTime={updateSessionTime}
            onTimeChange={onTimeChange}
            onSaveTimes={onSaveTimes}
            editedTimes={editedTimes}
            lastModifiedSessionId={lastModifiedSessionId}
            setDocSettingsTarget={setDocSettingsTarget}
            setIsDocSettingsOpen={setIsDocSettingsOpen}
            setViewingDocId={setViewingDocId}
            handleMoveSessionItem={handleMoveSessionItem}
            handleSaveCorrection={handleSaveCorrection}
            handleAddAdminBloc={handleAddAdminBloc}
            handleRegenerateVisualDocs={handleRegenerateVisualDocs}
            instantiateDocument={instantiateDocument}
            currentOrgId={currentOrgId}
            groupes={groupes}
            onOpenGroupe={ouvrirGroupe}
            depotsNonLus={depotsNonLus}
            estPro={estPro}
            nomUtilisateur={nomUtilisateurConnecte}
            onDepotsVus={fetchDepotsNonLus}
            ongletInitial={ongletFicheDemande}
            onOngletInitialUtilise={() => setOngletFicheDemande(null)}
          />}
          {activeTab === 'groupes' && userRole === 'admin' && <GroupesView
            groupes={groupes}
            groupeSeances={groupeSeances}
            groupesDispo={groupesDispo}
            clients={clients}
            sessions={sessions}
            formateurs={assignableFormateurs}
            modules={modules}
            moduleSessionTemplates={moduleSessionTemplates}
            orgSettings={orgSettings}
            selectedGroupeId={selectedGroupeId}
            setSelectedGroupeId={setSelectedGroupeId}
            actions={actionsGroupes}
            ouvrirFicheClient={ouvrirFicheClientDepuisGroupe}
          />}
          {activeTab === 'formateurs' && userRole === 'admin' && <AdminFormateursView
            clients={clients}
            formateurs={assignableFormateurs}
            adminSelfId={adminSelfProfile?.id}
            documents={documents}
            expandedClientId={expandedClientId}
            setExpandedClientId={setExpandedClientId}
            supabase={supabase}
            fetchUtilisateurs={fetchUtilisateurs}
            fetchDocuments={fetchDocuments}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            modules={modules}
            sessions={sessions}
            handleDownloadResource={handleDownloadResource}
            handleDeleteFormateur={handleDeleteFormateur}
            documentTemplates={documentTemplates}
            handleGenerateDocx={handleGenerateDocx}
            setViewingDocId={setViewingDocId}
            handleUploadDocxTemplate={handleUploadDocxTemplate}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            currentOrgId={currentOrgId}
            setIsInviteModalOpen={setIsInviteModalOpen}
            setInviteDefaultRole={setInviteDefaultRole}
          />}
          {activeTab === 'relances' && userRole === 'admin' && !hasFeatureAccess(orgSettings, 'pro') && (
            <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100 text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-5">
                <Lock className="w-7 h-7 text-violet-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Fonctionnalité réservée aux plans Pro et Illimité</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                Les relances automatiques auprès de vos clients sont disponibles à partir du plan Pro.
              </p>
              <button onClick={() => setActiveTab('parametres_org')} className="bg-violet-600 hover:bg-violet-700 text-white font-bold px-6 py-3 rounded-xl transition-all">
                Voir les plans →
              </button>
            </div>
          )}
          {activeTab === 'relances' && userRole === 'admin' && hasFeatureAccess(orgSettings, 'pro') && <AutomationSettingsView
            supabase={supabase}
            currentOrgId={currentOrgId}
          />}
          {activeTab === 'prospects' && (userRole === 'admin' || userRole === 'formateur') && <ProspectsView
            supabase={supabase}
            currentOrgId={currentOrgId}
            userRole={userRole}
            currentUserId={currentUserId}
          />}
          {activeTab === 'finances' && userRole === 'admin' && <FinancesView
            supabase={supabase}
            currentOrgId={currentOrgId}
            clients={clients}
          />}
          {activeTab === 'bpf' && userRole === 'admin' && !hasFeatureAccess(orgSettings, 'pro') && (
            <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100 text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-5">
                <Lock className="w-7 h-7 text-violet-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Fonctionnalité réservée aux plans Pro et Illimité</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                Le bilan pédagogique et financier (BPF) préparé automatiquement à partir de vos dossiers est disponible à partir du plan Pro.
              </p>
              <button onClick={() => setActiveTab('parametres_org')} className="bg-violet-600 hover:bg-violet-700 text-white font-bold px-6 py-3 rounded-xl transition-all">
                Voir les plans →
              </button>
            </div>
          )}
          {activeTab === 'bpf' && userRole === 'admin' && hasFeatureAccess(orgSettings, 'pro') && (
            <BpfView currentOrgId={currentOrgId} orgSettings={orgSettings}
              onOuvrirClient={(id) => { setOngletFicheDemande('finances'); setExpandedClientId(id); setActiveTab('clients'); }} />
          )}
          {activeTab === 'veille' && userRole === 'admin' && !hasFeatureAccess(orgSettings, 'pro') && (
            <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100 text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-5">
                <Lock className="w-7 h-7 text-violet-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Fonctionnalité réservée aux plans Pro et Illimité</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                Chaque lundi, les nouveautés de la formation (Qualiopi, CPF, financement, réglementation) avec leur source, l'action menée notée en un clic et la preuve de veille prête pour votre audit Qualiopi.
              </p>
              <button onClick={() => setActiveTab('parametres_org')} className="bg-violet-600 hover:bg-violet-700 text-white font-bold px-6 py-3 rounded-xl transition-all">
                Voir les plans →
              </button>
            </div>
          )}
          {activeTab === 'veille' && userRole === 'admin' && hasFeatureAccess(orgSettings, 'pro') && (
            <VeilleView currentOrgId={currentOrgId} orgSettings={orgSettings} nomUtilisateur={nomUtilisateurConnecte} currentUserId={currentUserId} onNonLus={setVeilleNonLues} />
          )}
          {activeTab === 'processus' && (
            <SharedProcessesView
              supabase={supabase}
              userRole={userRole}
              currentUserId={currentUserId}
              currentOrgId={currentOrgId}
            />
          )}
          {activeTab === 'messagerie' && (
            <MessagesView
              supabase={supabase}
              userRole={userRole}
              currentUserId={currentUserId}
              clients={clients}
              formateurs={assignableFormateurs}
              currentOrgId={currentOrgId}
              orgName={orgSettings?.nom || ''}
            />
          )}
          {activeTab === 'modules' && userRole === 'admin' && <IngenierieView
            modules={modules}
            handleAddModule={handleAddModule}
            handleDeleteModule={handleDeleteModule}
            handleRenameModule={handleRenameModule}
            newModuleName={newModuleName}
            setNewModuleName={setNewModuleName}
            newModuleSeances={newModuleSeances}
            setNewModuleSeances={setNewModuleSeances}
            handleUploadDocxTemplate={handleUploadDocxTemplate}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            handleUploadResource={handleUploadResource}
            newResourceName={newResourceName}
            setNewResourceName={setNewResourceName}
            isUploadingResource={isUploadingResource}
            modelingModuleId={modelingModuleId}
            setModelingModuleId={setModelingModuleId}
            moduleSessionTemplates={moduleSessionTemplates}
            moduleStepResources={moduleStepResources}
            fetchModules={fetchModules}
            newStepTitle={newStepTitle}
            setNewStepTitle={setNewStepTitle}
            newStepActivity={newStepActivity}
            setNewStepActivity={setNewStepActivity}
            selectedResourceId={selectedResourceId}
            setSelectedResourceId={setSelectedResourceId}
            pedagogicalResources={pedagogicalResources}
            isAddingStep={isAddingStep}
            setIsAddingStep={setIsAddingStep}
            isAddingStepResource={isAddingStepResource}
            setIsAddingStepResource={setIsAddingStepResource}
            supabase={supabase}
            createSessionFolder={createSessionFolder}
            isResourceModalOpen={isResourceModalOpen}
            setIsResourceModalOpen={setIsResourceModalOpen}
            activeFolderId={activeFolderId}
            setActiveFolderId={setActiveFolderId}
            handleDeleteFolder={handleDeleteFolder}
            handleDeleteStepResource={handleDeleteStepResource}
            handleAddStepResource={handleAddStepResource}
            handleRenameFolder={handleRenameFolder}
            handleRenameResource={handleRenameResource}
            handleAddModuleMomentResource={handleAddModuleMomentResource}
            handleRedistributeModuleDocs={handleRedistributeModuleDocs}
            documentTemplates={documentTemplates}
            currentOrgId={currentOrgId}
            handleUpdateStepResourceSignatures={handleUpdateStepResourceSignatures}
          />}
          {activeTab === 'clients' && userRole === 'formateur' && <FormateurView chargement={chargementEnCours}
            clients={clients}
            formateurs={assignableFormateurs}
            currentOrgId={currentOrgId}
            sessions={sessions}
            generateSessions={generateSessions}
            updateSessionDate={updateSessionDate}
            signSession={signSession}
            modules={modules}
            userRole={userRole}
            currentUserId={currentUserId}
            expandedClientId={expandedClientId}
            setExpandedClientId={setExpandedClientId}
            handleAddSession={handleAddSession}
            handleDeleteSession={handleDeleteSession}
            updateSessionTime={updateSessionTime}
            handleGenerateDocx={handleGenerateDocx}
            documents={documents}
            fetchUtilisateurs={fetchUtilisateurs}
            documentTemplates={documentTemplates}
            pedagogicalResources={pedagogicalResources}
            handleDownloadResource={handleDownloadResource}
            handleUploadExerciseResponse={handleUploadExerciseResponse}
            setIsSessionItemModalOpen={setIsSessionItemModalOpen}
            setTargetSessionForAddition={setTargetSessionForAddition}
            onTimeChange={onTimeChange}
            onSaveTimes={onSaveTimes}
            setLastModifiedSessionId={setLastModifiedSessionId}
            lastModifiedSessionId={lastModifiedSessionId}
            setViewingSession={setViewingSession}
            handleSignDocument={handleSignDocument}
            setViewingDocId={setViewingDocId}
            supabase={supabase}
            fetchDocuments={fetchDocuments}
            handleMoveSessionItem={handleMoveSessionItem}
            handleSaveCorrection={handleSaveCorrection}
            handleAddCompletedExercise={handleAddCompletedExercise}
            depotsNonLus={depotsNonLus}
            estPro={estPro}
            nomUtilisateur={nomUtilisateurConnecte}
            onDepotsVus={fetchDepotsNonLus}
            ongletInitial={ongletFicheDemande}
            onOngletInitialUtilise={() => setOngletFicheDemande(null)}
          />}
          {activeTab === 'accueil' && (() => {
            const _client = clients.find(c => c.id === currentUserId);
            const _module = modules.find(m => String(m.id) === String(_client?.module_id));
            const _clientSessions = sessions.filter(s => String(s.client_id) === String(currentUserId));
            const _prog = progressionParcours(currentUserId, sessions, _client?.module_id); // LOT B
            const _signed = _prog.faites;
            const _total = _prog.total;
            const _progress = _prog.pct;
            const _today = jourLocalISO(new Date());
            const _nextSession = _clientSessions.filter(s => s.date >= _today).sort((a, b) => (a.date || '').localeCompare(b.date || ''))[0] || null;
            const _coach = assignableFormateurs.find(f => f.id === _client?.formateur_id);
            const _pendingDocs = documents.filter(d => d.user_id === currentUserId && docAwaitsClientSignature(d) && !isBlockedBySigningOrder(d, 'client') && !docDeFinVerrouille(d, isModuleCompletedForClient(currentUserId, sessions, moduleDuClient(clients, currentUserId)))).length;
            // UX (2026-10-12) : prochaine séance (regroupée, avec horaires et lien visio) + liste « À faire ».
            const _todayL = localTodayISO();
            const _metaDe = (m) => (typeof m === 'string') ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
            const _ng = regrouperParSeance(_clientSessions.filter(s => s.date && s.date >= _todayL && Number(s.numero_seance) >= 1))[0] || null;
            const _nextGroup = _ng ? {
              titre: titreSeance(_ng),
              date: _ng.date,
              horaire: horaireSeance(_ng),
              estAujourdhui: _ng.date === _todayL,
              lienVisio: _ng.items.map(i => _metaDe(i.metadata).lien_visio).find(u => typeof u === 'string' && /^https?:\/\//i.test(u.trim())) || null,
            } : null;
            const _emargements = _clientSessions.filter(s => s.type_activite === 'signature' && s.statut_client !== 'Signé'
              && _metaDe(s.metadata).requiresClientSignature !== false && s.date && s.date <= _todayL && Number(s.numero_seance) >= 1);
            const _exosARendre = _clientSessions.filter(s => (s.type_activite === 'exercice' || s.type_activite === 'Exercice') && !s.reponse_url && (!s.date || s.date <= _todayL));
            const _jjmm = (d) => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
            const _todos = [
              ..._emargements.map(s => ({ cle: 'em-' + s.id, urgent: true, label: s.date === _todayL ? `Signer ma présence — Séance ${s.numero_seance} (aujourd'hui)` : `Signer ma présence — Séance ${s.numero_seance} du ${_jjmm(s.date)}`, onglet: 'mes_seances', bouton: 'Signer' })),
              ...(_pendingDocs > 0 ? [{ cle: 'docs', urgent: true, label: `${_pendingDocs} document${_pendingDocs > 1 ? 's' : ''} à signer`, onglet: 'mes_documents', bouton: 'Voir' }] : []),
              ...(_exosARendre.length > 0 ? [{ cle: 'exos', label: `${_exosARendre.length} exercice${_exosARendre.length > 1 ? 's' : ''} à rendre`, onglet: 'exercices', bouton: 'Voir' }] : []),
              ...(unreadMessagesCount > 0 ? [{ cle: 'msg', label: `${unreadMessagesCount} nouveau${unreadMessagesCount > 1 ? 'x' : ''} message${unreadMessagesCount > 1 ? 's' : ''}`, onglet: 'messagerie', bouton: 'Lire' }] : []),
            ];
            return <AccueilView
              setActiveTab={setActiveTab}
              clientProgress={_progress}
              moduleName={_module?.nom || null}
              totalSessions={_total}
              signedSessions={_signed}
              nextSession={_nextSession}
              coachName={_coach?.nom || null}
              pendingDocsCount={_pendingDocs}
              brandSettings={brandSettings}
              clientName={_client?.nom_complet || ''}
              nextGroup={_nextGroup}
              todos={_todos}
            />;
          })()}
          {activeTab === 'mes_seances' && <SessionsView moduleActuelId={moduleDuClient(clients, currentUserId)} sessions={sessions} signSession={signSession} currentUserId={currentUserId} userRole={userRole} pedagogicalResources={pedagogicalResources} handleDownloadResource={handleDownloadResource} handleUploadExerciseResponse={handleUploadExerciseResponse} setViewingSession={setViewingSession} />}
          {activeTab === 'mes_documents' && <ClientDocumentsView supabase={supabase} currentUserId={currentUserId} clients={clients} documents={documents} fetchDocuments={fetchDocuments} formateurs={assignableFormateurs} orgSettings={orgSettings} sessions={sessions} fetchSessions={fetchSessions} />}
          {activeTab === 'exercices' && <ExercicesView setActiveTab={setActiveTab} sessions={sessions} currentUserId={currentUserId} handleUploadExerciseResponse={handleUploadExerciseResponse} />}
          {activeTab === 'questionnaires' && userRole === 'admin' && <QuestionnairesView supabase={supabase} currentOrgId={currentOrgId} clients={clients} formateurs={assignableFormateurs} modules={modules} orgName={orgSettings?.nom || ''} />}
          {activeTab === 'gestion_documents' && <DocumentsView
            onOpenQuestionnaires={userRole === 'admin' ? () => setActiveTab('questionnaires') : null}
            sessions={sessions}
            documents={documents}
            clients={clients}
            formateurs={assignableFormateurs}
            userRole={userRole}
            currentUserId={currentUserId}
            handleSignDocument={handleSignDocument}
            handleDownloadPDF={handleDownloadPDF}
            handleAddDocument={handleAddDocument}
            updateDateSeance={updateDateSeance}
            newDocName={newDocName} setNewDocName={setNewDocName}
            newDocType={newDocType} setNewDocType={setNewDocType}
            newDocUrl={newDocUrl} setNewDocUrl={setNewDocUrl}
            newDocFile={newDocFile} setNewDocFile={setNewDocFile}
            newDocClientId={newDocClientId} setNewDocClientId={setNewDocClientId}
            newDocVisClient={newDocVisClient} setNewDocVisClient={setNewDocVisClient}
            newDocVisFormateur={newDocVisFormateur} setNewDocVisFormateur={setNewDocVisFormateur}
            isAddingDoc={isAddingDoc}
            selectedClientForDocs={selectedClientForDocs}
            setSelectedClientForDocs={setSelectedClientForDocs}
            signingDocId={signingDocId}
            setSigningDocId={setSigningDocId}
            viewingDocId={viewingDocId}
            setViewingDocId={setViewingDocId}
            handleSignatureSave={handleSignatureSave}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            newTemplateDestination={newTemplateDestination}
            setNewTemplateDestination={setNewTemplateDestination}
            newTemplateClassification={newTemplateClassification}
            setNewTemplateClassification={setNewTemplateClassification}
            fetchDocuments={fetchDocuments}
            setIsDeleteModalOpen={setIsDeleteModalOpen}
            setTargetToDelete={setTargetToDelete}
            supabase={supabase}
            currentOrgId={currentOrgId}
            handleUploadDocxTemplate={handleUploadDocxTemplate}
            handleUploadVisualTemplate={handleUploadVisualTemplate}
            onUpdateTemplateDestination={handleUpdateTemplateDestination}
            documentTemplates={documentTemplates}
          />}
          {activeTab === 'ressources' && userRole === 'formateur' && <RessourcesView pedagogicalResources={pedagogicalResources} supabase={supabase} currentUserId={currentUserId} />}

          {activeTab === 'set-password' && <SetPasswordView supabase={supabase} onComplete={() => setActiveTab('accueil')} />}
          </>
          )}
        </main>
        </ConventionsContext.Provider>
      </div>

      {/* Modals Qualiopi */}
      {/* FIX (2026-09-07) : remplace la modale générique <SignatureModal> par le même
          <DocumentViewerModal mode="sign"> déjà utilisé côté "Documents à signer" du formateur
          (viewingSession, plus bas) — la saisie des champs texte (et le cochage des cases) se
          fait ainsi DIRECTEMENT sur le document affiché, à la position exacte de la balise,
          identique partout dans l'app plutôt qu'une liste générique séparée. */}
      <DocumentViewerModal
        isOpen={signingDocId !== null}
        url={(() => { const d = documents.find(d => d.id === signingDocId); return d?.signed_pdf_url || d?.url || null; })()}
        title={documents.find(d => d.id === signingDocId)?.nom}
        onClose={() => setSigningDocId(null)}
        supabase={supabase}
        mode="sign"
        requiredCheckboxes={(() => {
          const signingDoc = documents.find(d => d.id === signingDocId);
          if (!signingDoc) return [];
          const meta = (() => { try { return typeof signingDoc.metadata === 'string' ? JSON.parse(signingDoc.metadata) : (signingDoc.metadata || {}); } catch { return {}; } })();
          // Repli sur template_fields puis fields quand signature_fields est vide — les documents
          // envoyés via "Envoyer" (handleGenerateDocx) stockent leurs balises dans
          // metadata.template_fields, pas metadata.signature_fields (même repli déjà utilisé par
          // handleSignDocument pour graver la signature dans le PDF).
          const fieldsForSigning = (Array.isArray(meta.signature_fields) && meta.signature_fields.length > 0) ? meta.signature_fields
            : (Array.isArray(meta.template_fields) && meta.template_fields.length > 0) ? meta.template_fields
            : (Array.isArray(meta.fields) ? meta.fields : []);
          const signerRoleForCheckbox = userRole === 'client' ? 'client' : userRole === 'admin' ? 'organisme' : 'formateur';
          const checkboxTag = `checkbox_${signerRoleForCheckbox}`;
          return fieldsForSigning.filter(f => f.tag === checkboxTag);
        })()}
        requiredTextFields={(() => {
          const signingDoc = documents.find(d => d.id === signingDocId);
          if (!signingDoc) return [];
          const meta = (() => { try { return typeof signingDoc.metadata === 'string' ? JSON.parse(signingDoc.metadata) : (signingDoc.metadata || {}); } catch { return {}; } })();
          const fieldsForSigning = (Array.isArray(meta.signature_fields) && meta.signature_fields.length > 0) ? meta.signature_fields
            : (Array.isArray(meta.template_fields) && meta.template_fields.length > 0) ? meta.template_fields
            : (Array.isArray(meta.fields) ? meta.fields : []);
          const signerRoleForText = userRole === 'client' ? 'client' : userRole === 'admin' ? 'organisme' : 'formateur';
          const textTag = `texte_${signerRoleForText}`;
          return fieldsForSigning.filter(f => f.tag === textTag);
        })()}
        requiresSignature={(() => {
          const signingDoc = documents.find(d => d.id === signingDocId);
          if (!signingDoc) return true;
          const meta = (() => { try { return typeof signingDoc.metadata === 'string' ? JSON.parse(signingDoc.metadata) : (signingDoc.metadata || {}); } catch { return {}; } })();
          const fieldsForSigning = (Array.isArray(meta.signature_fields) && meta.signature_fields.length > 0) ? meta.signature_fields
            : (Array.isArray(meta.template_fields) && meta.template_fields.length > 0) ? meta.template_fields
            : (Array.isArray(meta.fields) ? meta.fields : []);
          const signerRoleForSig = userRole === 'client' ? 'client' : userRole === 'admin' ? 'organisme' : 'formateur';
          // Pas de métadonnées de balises du tout → document hors du nouveau système visuel, on
          // garde le comportement historique (signature toujours exigée). Sinon, on ne l'exige que
          // si une balise signature_<rôle> a effectivement été posée pour CE signataire.
          if (!Array.isArray(fieldsForSigning) || fieldsForSigning.length === 0) return true;
          return fieldsForSigning.some(f => f.tag === `signature_${signerRoleForSig}`);
        })()}
        onSave={(sigDataUrl, _choice, checkedIds, textValues) => handleSignatureSave(sigDataUrl, checkedIds, textValues)}
      />

      <EmargementModal
        isOpen={signingSessionId !== null}
        onClose={() => setSigningSessionId(null)}
        onSave={handleEmargementSave}
        sessionTitle={(() => {
          const s = sessions.find(s => s.id === signingSessionId);
          if (!s) return '';
          const morceaux = [
            Number(s.numero_seance) >= 1 ? `Séance ${s.numero_seance}` : (s.ressource_titre || s.nom),
            s.date ? new Date(s.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : null,
            s.heure_debut ? `${s.heure_debut}${s.heure_fin ? '–' + s.heure_fin : ''}` : null,
          ];
          return morceaux.filter(Boolean).join(' · ');
        })()}
        signerRole={userRole}
      />

      <ChangementModuleModal etat={changementModule} onAnnuler={() => { if (!changementModule?.enCours) setChangementModule(null); }} onConfirmer={confirmerChangementModule} />

      <SessionItemModal
        isOpen={isSessionItemModalOpen}
        onClose={() => setIsSessionItemModalOpen(false)}
        pedagogicalResources={pedagogicalResources}
        supabase={supabase}
        onSave={handleAddSessionItem}
        clientSessions={sessions.filter(s => s.client_id === targetSessionForAddition?.clientId && s.numero_seance !== null && s.numero_seance !== undefined)}
        preSelectedSessionId={targetSessionForAddition?.preSelectedSessionId || null}
        preSelectedLabel={targetSessionForAddition?.numero ? `SÉANCE ${targetSessionForAddition.numero}` : null}
      />

      {/* Visionneuse PDF pour docs de la modélothèque */}
      <DocumentViewerModal
        isOpen={viewingDocId !== null}
        url={(() => { const d = documents.find(doc => doc.id === viewingDocId); return d?.signed_pdf_url || d?.url || null; })()}
        title={documents.find(doc => doc.id === viewingDocId)?.nom}
        onClose={() => setViewingDocId(null)}
        supabase={supabase}
        mode="view"
      />

      {/* Visionneuse PDF pour sessions (lecture + signature obligatoire) */}
      <DocumentViewerModal
        isOpen={viewingSession !== null}
        url={resolveFileUrl(
          viewingSession?.session
            ? (viewingSession.session.signed_pdf_url || viewingSession.session.file_url || viewingSession.session.ressource_url || null)
            : null
        )}
        title={viewingSession?.session
          ? (viewingSession.session.ressource_titre || viewingSession.session.nom || 'Document')
          : ''
        }
        supabase={supabase}
        mode={viewingSession?.mode || 'view'}
        isInteractiveConsent={viewingSession?.session?.is_interactive_consent === true}
        requiredCheckboxes={(() => {
          const sess = viewingSession?.session;
          if (!sess || viewingSession?.mode !== 'sign') return [];
          const meta = (() => { try { return typeof sess.metadata === 'string' ? JSON.parse(sess.metadata) : (sess.metadata || {}); } catch { return {}; } })();
          // FIX (2026-09-03) : repli sur template_fields/fields quand signature_fields est vide —
          // voir commentaire détaillé plus bas sur requiresSignature.
          const sessFields = (Array.isArray(meta.signature_fields) && meta.signature_fields.length > 0) ? meta.signature_fields
            : (Array.isArray(meta.template_fields) ? meta.template_fields : (Array.isArray(meta.fields) ? meta.fields : []));
          return sessFields.filter(f => f.tag === (viewingSession?.source === 'session' ? 'checkbox_client' : 'checkbox_formateur'));
        })()}
        requiredTextFields={(() => {
          const sess = viewingSession?.session;
          if (!sess || viewingSession?.mode !== 'sign') return [];
          const meta = (() => { try { return typeof sess.metadata === 'string' ? JSON.parse(sess.metadata) : (sess.metadata || {}); } catch { return {}; } })();
          const sessFields = (Array.isArray(meta.signature_fields) && meta.signature_fields.length > 0) ? meta.signature_fields
            : (Array.isArray(meta.template_fields) ? meta.template_fields : (Array.isArray(meta.fields) ? meta.fields : []));
          return sessFields.filter(f => f.tag === (viewingSession?.source === 'session' ? 'texte_client' : 'texte_formateur'));
        })()}
        requiresSignature={(() => {
          const sess = viewingSession?.session;
          if (!sess || viewingSession?.mode !== 'sign') return true;
          const meta = (() => { try { return typeof sess.metadata === 'string' ? JSON.parse(sess.metadata) : (sess.metadata || {}); } catch { return {}; } })();
          // BUG URGENT (2026-09-03) : cette visionneuse (accueil formateur → "Documents à signer")
          // ne lisait QUE metadata.signature_fields — une clé propre à l'ancien flux
          // instantiateDocument ("Schema B"), jamais alimentée par les documents créés via le
          // bouton "Envoyer" (handleGenerateDocx) ni par l'envoi programmé, qui stockent leurs
          // balises dans metadata.template_fields à la place. Résultat : un document avec un champ
          // "texte formateur"/"case à cocher formateur" bien posé sur le modèle n'affichait ce
          // champ NULLE PART pour le formateur — seulement le PDF statique + la signature. Repli
          // ajouté sur template_fields puis fields, même logique que côté client (handleSignSave).
          const sigFields = (Array.isArray(meta.signature_fields) && meta.signature_fields.length > 0) ? meta.signature_fields
            : (Array.isArray(meta.template_fields) ? meta.template_fields : (Array.isArray(meta.fields) ? meta.fields : []));
          // Pas de métadonnées de balises du tout → document hors du nouveau système visuel,
          // on garde le comportement historique (signature toujours exigée). Sinon, on ne l'exige
          // que si une balise "signature_formateur" a effectivement été posée sur ce document.
          if (!Array.isArray(sigFields) || sigFields.length === 0) return true;
          return sigFields.some(f => f.tag === (viewingSession?.source === 'session' ? 'signature_client' : 'signature_formateur'));
        })()}
        onClose={() => setViewingSession(null)}
        onSave={async (sigDataUrl, _choice, checkedIds, textValues) => {
          const sess = viewingSession?.session;
          if (!sess?.id) { setViewingSession(null); return; }
          // SIGNATURES (2026-10-20) : un document placé dans une séance est enregistré sur la séance
          // (avant : cherché dans les documents → rien enregistré, message de succès quand même).
          if (viewingSession?.source === 'session') {
            const ok = await signerDocumentDeSeance(sess.id, 'client', sigDataUrl, checkedIds, textValues);
            if (ok) setViewingSession(null);
            return;
          }
          // Signature d'un document administratif par le formateur
          const ok = await handleSignDocument(sess.id, 'formateur', sigDataUrl, checkedIds, textValues);
          if (ok) { toast.success('Document signé avec succès !'); setViewingSession(null); }
        }}
      />

      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteUser}
        isAddingUser={isAddingUser}
        formateurs={assignableFormateurs}
        defaultRole={inviteDefaultRole}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer définitivement ?"
        itemName={targetToDelete?.type === 'template' ? `le modèle "${targetToDelete.id}"` : "cet élément"}
      />

      <DocumentSettingsModal
        isOpen={isDocSettingsOpen}
        session={docSettingsTarget}
        onClose={() => { setIsDocSettingsOpen(false); setDocSettingsTarget(null); }}
        onSave={handleSaveDocSettings}
      />

      <ConfirmModal
        isOpen={appConfirmState.open}
        title={appConfirmState.title}
        message={appConfirmState.message}
        onConfirm={appConfirmState.onConfirm}
        onCancel={hideAppConfirm}
      />

      <Toaster />
    </div>
    </ComptesClientsContext.Provider>
  );
}
